import React, { useState, useEffect, createContext, useContext, useCallback, useMemo, useRef, memo, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams, useSearchParams, Outlet, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Drawer as Vaul } from "vaul";
import { useIsMobile } from "./hooks/useIsMobile";
import { useWeddingPermissions } from "./hooks/useWeddingPermissions";
import { SkeletonPage, SkeletonStatsGrid, SkeletonList } from "./components/common/Skeletons";
import PermissionEditor from "./components/common/PermissionEditor";
import GetStartedModal from "./components/common/GetStartedModal";
import GuestStaffInfoCard from "./components/common/GuestStaffInfoCard";
import { ConfirmDialogRoot, confirmDialog } from "./components/common/ConfirmDialog";
import GetStarted from "./pages/GetStarted";
// AdminDashboard é lazy: contém recharts (~170kb gzip) e só carrega quando admin abre /admin
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
// Onda 2 — TasksPage isolado (3 views + comentários + anexos)
const LazyTasksPage = lazy(() => import("./pages/TasksPage"));
// Onda 3 — Contratos: aba dentro de Fornecedores
const LazyContractsPanel = lazy(() => import("./components/contracts/ContractsPanel"));
import PaymentTermsForm from "./components/payments/PaymentTermsForm";
// Onda 4 — Inspirações: galeria colaborativa
const LazyInspirationsPage = lazy(() => import("./pages/InspirationsPage"));
// Onda 6 — Relatórios Financeiros (dashboard + PDF premium)
const LazyReportsPage = lazy(() => import("./pages/ReportsPage"));
// Onda 7 — Mesas & Assentos Premium (Dashboard + Board Drag&Drop + PDF)
const LazySeatingPage = lazy(() => import("./pages/SeatingPage"));
// RevenueComposedChart é lazy: isola recharts da rota financeira
const LazyRevenueComposedChart = lazy(() => import("./components/charts/RevenueComposedChart"));
import WhatsAppSendFlow from "./components/common/WhatsAppSendFlow";
import SwipeableRow from "./components/common/SwipeableRow";
import { redirectToCheckout } from "./lib/stripeRedirect";
import { useAccessStatus } from "./hooks/useAccessStatus";
import { PremiumGateProvider, usePremiumGate } from "./components/common/PremiumGate";
import { PlanUpgradeProvider, usePlanUpgrade } from "./components/common/PlanUpgradeModal";
import StaffUpgradeGate from "./components/common/StaffUpgradeGate";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/PasswordResetPages";
import VipRedeemPage from "./pages/VipRedeemPage";
import StaffInvitePage from "./pages/StaffInvitePage";
import VipAccessAdminPage from "./pages/VipAccessAdminPage";
import EmailLogsPage from "./pages/EmailLogsPage";
import { PrivacidadePage, TermosPage } from "./pages/LegalPages";
import { ClassicTemplate, ModernTemplate, RomanticTemplate } from "./components/templates";
import { LiventyLogoMark, LiventyWordmark } from "./components/common/LiventyLogo";
// IMPORTS PESADOS MOVIDOS PARA LAZY LOADING - Carregados apenas quando necessário
// Html5QrcodeScanner, Html5Qrcode - importado dinamicamente em CheckinLivePage
// XLSX - importado dinamicamente em export functions
// jsPDF - importado dinamicamente em export functions
// DndContext - parcialmente importado para drag-and-drop
import { DndContext, closestCenter, useSensors, useSensor, PointerSensor, DragOverlay } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Home, Users, DollarSign, CreditCard, CheckSquare, Sparkles, Settings, LogOut,
  Menu, X, Plus, Trash2, Edit2, Check, ChevronRight, TrendingUp, TrendingDown,
  Calendar, Heart, Crown, AlertCircle, Phone, Mail, Instagram, Loader2, MessageSquare, MessageCircle,
  Star, Zap, Shield, ArrowRight, ArrowLeft, Play, Gift, ChevronLeft, ChevronDown, ChevronUp,
  FileText, Clock, Building, Cake, Camera, Music, Flower2, Car, Gem, Plane,
  Receipt, PieChart, ListChecks, CalendarDays, Eye, EyeOff, RefreshCw,
  Baby, UserCheck, UserX, User, UserPlus, Minus, MapPin, Link2, Copy, QrCode, Download,
  HelpCircle, Send, LayoutGrid, UserCog, GripVertical, Undo2, PartyPopper,
  Upload, ImagePlus, GripHorizontal, ToggleLeft, ToggleRight, ExternalLink, Bell, Briefcase,
  FolderOpen, Folder, Pencil, Search, Lock, ArrowUp, Wallet, Filter, UtensilsCrossed, FileBarChart,
  GraduationCap, Flame, School
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WebSocket URL
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// SITE DO CASAMENTO REMOVIDO - Constantes vazias para compatibilidade
const TEMPLATES = [];
const Image = () => null;

// ==================== PERFORMANCE: Skeleton Loading ====================
const PageSkeleton = memo(() => (
  <div className="animate-pulse">
    <div className="h-8 bg-slate-200 rounded w-1/3 mb-6"></div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div className="h-32 bg-slate-200 rounded-xl"></div>
      <div className="h-32 bg-slate-200 rounded-xl"></div>
      <div className="h-32 bg-slate-200 rounded-xl"></div>
    </div>
    <div className="h-64 bg-slate-200 rounded-xl"></div>
  </div>
));

// ==================== PERFORMANCE: Loading Fallback ====================
const PageLoadingFallback = memo(() => (
  <PageSkeleton />
));

// ==================== LAZY IMPORT HELPERS ====================
// Funções para importar bibliotecas pesadas apenas quando necessário
const loadXLSX = () => import('xlsx');
const loadJsPDF = () => import('jspdf').then(m => m.jsPDF);
const loadAutoTable = () => import('jspdf-autotable').then(m => m.default);
const loadQrScanner = () => import('html5-qrcode');

// ==================== HOOK: useCheckinRealtime ====================
/**
 * Hook customizado para check-in em TEMPO REAL.
 * Tenta WebSocket primeiro, fallback para SSE.
 * 
 * EVENTOS SUPORTADOS:
 * - checkin:success - Check-in realizado com sucesso (verde)
 * - checkin:already_checked - Já fez check-in (amarelo)
 * - checkin:not_allowed - Check-in bloqueado (vermelho)
 * - checkin:update_list - Lista atualizada
 */
function useCheckinWebSocket(weddingId, onCheckinEvent, onStatsUpdate) {
  const wsRef = useRef(null);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [connectionType, setConnectionType] = useState(null); // 'websocket' ou 'sse'
  const scannerIdRef = useRef(`scanner_${Math.random().toString(36).substr(2, 8)}`);

  // Processar mensagem recebida (comum para WS e SSE)
  const handleMessage = useCallback((eventType, data) => {
    switch (eventType) {
      case 'connected':
        if (data.stats) onStatsUpdate?.(data.stats);
        if (data.checked_in_list) onStatsUpdate?.({ checkin_list: data.checked_in_list });
        break;
      
      case 'checkin:success':
        onCheckinEvent?.({ type: 'success', ...data });
        onStatsUpdate?.({ 
          stats: { 
            total_checked_in: data.total_checked_in, 
            total_confirmed: data.total_confirmed,
            arrival_percentage: data.arrival_percentage 
          } 
        });
        break;
      
      case 'checkin:already_checked':
        onCheckinEvent?.({ type: 'already_checked', ...data });
        break;
      
      case 'checkin:not_allowed':
        onCheckinEvent?.({ type: 'not_allowed', ...data });
        break;
      
      case 'checkin:update_list':
        if (data.checked_in_list) {
          onStatsUpdate?.({ checkin_list: data.checked_in_list });
        }
        break;
      
      case 'stats:updated':
        onStatsUpdate?.({ stats: data });
        break;
      
      case 'ping':
        // Manter conexão
        break;
      
      // Compatibilidade com eventos antigos
      case 'guest.checked_in':
        onCheckinEvent?.({ type: 'success', guest_name: data.invite_name, ...data });
        if (data.stats) onStatsUpdate?.({ stats: data.stats, checkin_list: data.checkin_list });
        break;
      
      default:
    }
  }, [onCheckinEvent, onStatsUpdate]);

  // Conectar via WebSocket
  const connectWebSocket = useCallback(() => {
    if (!weddingId) return;
    
    try {
      const ws = new WebSocket(`${WS_URL}/ws/checkin/${weddingId}?scanner_id=${scannerIdRef.current}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setConnectionType('websocket');
        setConnectionError(null);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleMessage(message.event, message.data || {});
          
          // Responder ping com pong
          if (message.event === 'ping') {
            ws.send('pong');
          }
        } catch (e) {
          console.error('Erro ao processar mensagem WS:', e);
        }
      };

      ws.onerror = () => {
        console.warn('⚠️ WebSocket erro, tentando SSE...');
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;
        
        // Se fechou com erro, tentar SSE como fallback
        if (event.code !== 1000) {
          connectSSE();
        } else {
          // Reconectar WebSocket após 3s
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 3000);
        }
      };

    } catch (err) {
      console.error('Erro ao criar WebSocket:', err);
      connectSSE(); // Fallback para SSE
    }
  }, [weddingId, handleMessage]);

  // Conectar via SSE (fallback)
  const connectSSE = useCallback(() => {
    if (!weddingId) return;
    
    try {
      const eventSource = new EventSource(`${API}/sse/checkin/${weddingId}`);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
        setConnectionType('sse');
        setConnectionError(null);
      };

      // Eventos SSE
      ['connected', 'checkin:success', 'checkin:already_checked', 'checkin:not_allowed', 
       'checkin:update_list', 'stats:updated', 'ping', 'guest.checked_in'].forEach(eventName => {
        eventSource.addEventListener(eventName, (event) => {
          try {
            const data = JSON.parse(event.data);
            handleMessage(eventName, data);
          } catch (e) {
            console.error(`Erro ao processar evento ${eventName}:`, e);
          }
        });
      });

      eventSource.onerror = () => {
        setIsConnected(false);
        setConnectionError('Reconectando...');
        eventSource.close();
        eventSourceRef.current = null;
        
        // Reconectar após 3s
        reconnectTimeoutRef.current = setTimeout(() => {
          connectSSE();
        }, 3000);
      };

    } catch (err) {
      console.error('Erro ao criar SSE:', err);
      setConnectionError(err.message);
    }
  }, [weddingId, handleMessage]);

  // Enviar evento de scan (apenas via WebSocket)
  const sendCheckinScan = useCallback((inviteId) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        event: 'checkin:scan',
        data: {
          event_id: weddingId,
          invite_id: inviteId,
          scanner_id: scannerIdRef.current,
          timestamp: new Date().toISOString()
        }
      }));
      return true;
    }
    return false;
  }, [weddingId]);

  // Conectar quando weddingId mudar
  useEffect(() => {
    // Tentar WebSocket primeiro
    connectWebSocket();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [weddingId]);

  // Função para solicitar stats atualizados
  const requestStats = useCallback(async () => {
    // Via WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ event: 'get_stats' }));
      return;
    }
    // Via API
    try {
      const res = await fetch(`${API}/weddings/${weddingId}/checkin-live`);
      const data = await res.json();
      onStatsUpdate?.({ stats: data.stats, checkin_list: data.checkin_list });
    } catch (e) {
      console.error('Erro ao buscar stats:', e);
    }
  }, [weddingId, onStatsUpdate]);

  return { 
    isConnected, 
    connectionError, 
    connectionType,
    requestStats, 
    sendCheckinScan,
    scannerId: scannerIdRef.current
  };
}

// ==================== FUNÇÕES DE DIAS ÚTEIS ====================

/**
 * Verifica se uma data é dia útil (segunda a sexta)
 */
const isBusinessDay = (date) => {
  return date.getDay() !== 0 && date.getDay() !== 6; // 0 = domingo, 6 = sábado
};

/**
 * Conta dias úteis entre duas datas
 * Retorna positivo se endDate > startDate, negativo se passado, 0 se hoje
 */
const countBusinessDaysBetween = (startDate, endDate) => {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);
  
  if (start.getTime() === end.getTime()) return 0;
  
  let count = 0;
  const direction = end > start ? 1 : -1;
  const current = new Date(start);
  
  while (direction > 0 ? current < end : current > end) {
    if (direction > 0) {
      current.setDate(current.getDate() + 1);
      if (isBusinessDay(current)) count++;
    } else {
      current.setDate(current.getDate() - 1);
      if (isBusinessDay(current)) count--;
    }
  }
  
  return count;
};

/**
 * Retorna texto de alerta baseado em dias úteis
 */
const getAlertText = (dueDate, isPaid) => {
  if (isPaid) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + 'T12:00:00');
  
  const businessDays = countBusinessDaysBetween(today, due);
  
  if (businessDays < 0) {
    return { type: 'vencida', text: `Vencida há ${Math.abs(businessDays)} dia(s) útil(eis)`, color: 'red' };
  } else if (businessDays === 0) {
    return { type: 'vence_hoje', text: 'Vence hoje!', color: 'orange' };
  } else if (businessDays <= 2) {
    return { type: 'urgente', text: `Vence em ${businessDays} dia(s) útil(eis)`, color: 'orange' };
  } else if (businessDays <= 5) {
    return { type: 'proximo', text: `Vence em ${businessDays} dias úteis`, color: 'blue' };
  }
  return null; // Não precisa de alerta se > 5 dias úteis
};

// ==================== STATUS DE PAGAMENTO PADRONIZADO ====================
// REGRA DE OURO: Apenas 3 status possíveis
// - "paga": Parcela/despesa já foi paga (is_paid = true)
// - "a_vencer": Não paga e data de vencimento >= hoje
// - "vencida": Não paga e data de vencimento < hoje (apenas no dia SEGUINTE ao vencimento)

const PAYMENT_STATUS = {
  PAGA: "paga",
  A_VENCER: "a_vencer", 
  VENCIDA: "vencida"
};

const PAYMENT_STATUS_CONFIG = {
  paga: { 
    label: "Paga", 
    color: "bg-emerald-100 text-emerald-700",
    icon: "check"
  },
  a_vencer: { 
    label: "A Vencer", 
    color: "bg-blue-100 text-blue-700",
    icon: "clock"
  },
  vencida: { 
    label: "Vencida", 
    color: "bg-red-100 text-red-700",
    icon: "alert"
  }
};

/**
 * Calcula o status de pagamento de forma CONSISTENTE em todo o sistema
 * @param {boolean} isPaid - Se a parcela/despesa está paga
 * @param {string} dueDate - Data de vencimento no formato "YYYY-MM-DD"
 * @returns {Object} { status, label, color }
 * 
 * REGRAS:
 * 1. Se isPaid = true → SEMPRE "Paga"
 * 2. Se não paga E hoje <= data_vencimento → "A Vencer"
 * 3. Se não paga E hoje > data_vencimento → "Vencida"
 * 
 * IMPORTANTE: No dia do vencimento, status é "A Vencer", NÃO "Vencida"
 */
const getPaymentStatus = (isPaid, dueDate) => {
  // Regra 1: Paga tem precedência absoluta
  if (isPaid) {
    return {
      status: PAYMENT_STATUS.PAGA,
      label: PAYMENT_STATUS_CONFIG.paga.label,
      color: PAYMENT_STATUS_CONFIG.paga.color
    };
  }
  
  // Calcular datas para comparação
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const due = new Date(dueDate + 'T12:00:00');
  due.setHours(0, 0, 0, 0);
  
  // Regra 2 e 3: Comparar datas
  // No dia do vencimento (today === due) → "A Vencer"
  // Apenas no dia SEGUINTE (today > due) → "Vencida"
  if (today <= due) {
    return {
      status: PAYMENT_STATUS.A_VENCER,
      label: PAYMENT_STATUS_CONFIG.a_vencer.label,
      color: PAYMENT_STATUS_CONFIG.a_vencer.color
    };
  } else {
    return {
      status: PAYMENT_STATUS.VENCIDA,
      label: PAYMENT_STATUS_CONFIG.vencida.label,
      color: PAYMENT_STATUS_CONFIG.vencida.color
    };
  }
};

/**
 * Calcula o status de uma despesa baseado em suas parcelas
 * @param {Object} expense - Objeto da despesa com installments
 * @returns {Object} { status, label, color }
 */
const getExpenseStatus = (expense) => {
  // Se todas as parcelas estão pagas → Paga
  const allPaid = expense.installments?.every(i => i.is_paid) || 
                  (expense.total_paid >= expense.total_amount);
  
  if (allPaid) {
    return {
      status: PAYMENT_STATUS.PAGA,
      label: PAYMENT_STATUS_CONFIG.paga.label,
      color: PAYMENT_STATUS_CONFIG.paga.color
    };
  }
  
  // Encontrar próxima parcela não paga
  const nextInstallment = expense.installments?.find(i => !i.is_paid);
  
  if (nextInstallment) {
    return getPaymentStatus(false, nextInstallment.due_date);
  }
  
  // Fallback: A Vencer
  return {
    status: PAYMENT_STATUS.A_VENCER,
    label: PAYMENT_STATUS_CONFIG.a_vencer.label,
    color: PAYMENT_STATUS_CONFIG.a_vencer.color
  };
};

// ==================== CATEGORIAS ====================

// NOMES DOS MESES EM PORTUGUÊS (constante global)
const MONTH_NAMES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

// CATEGORIAS PADRÃO DO SISTEMA (Obrigatório para todo o sistema)
const STANDARD_CATEGORIES = [
  { value: "local_estrutura", label: "Local & Estrutura" },
  { value: "buffet_bebidas", label: "Buffet & Bebidas" },
  { value: "decoracao_ambientacao", label: "Decoração & Ambientação" },
  { value: "trajes_beleza", label: "Trajes & Beleza" },
  { value: "foto_video_memorias", label: "Foto, Vídeo & Memórias" },
  { value: "musica_entretenimento", label: "Música & Entretenimento" },
  { value: "cerimonia", label: "Cerimônia" },
  { value: "convites_papelaria", label: "Convites & Papelaria" },
  { value: "transporte_logistica", label: "Transporte & Logística" },
  { value: "lembrancinhas_experiencia", label: "Lembrancinhas & Experiência do Convidado" },
  { value: "lua_de_mel", label: "Lua de Mel" },
  { value: "outros", label: "Outras" }
];

// FORMAS DE PAGAMENTO PADRÃO
const PAYMENT_TYPES = [
  { value: "a_vista", label: "À Vista" },
  { value: "a_vista_30", label: "À Vista – 30 Dias" },
  { value: "parcelado_boleto", label: "Parcelado (Boleto)" },
  { value: "parcelado_cartao", label: "Parcelado (Cartão de Crédito)" },
  { value: "parcelado_cheque", label: "Parcelado (Cheque)" }
];

// ==================== FUNÇÃO HELPER PARA DATAS ====================
// NUNCA usar new Date() diretamente com datas do backend para evitar problemas de timezone
// Esta função formata a data exatamente como vem do backend (YYYY-MM-DD) para DD/MM/YYYY
const formatDateBR = (dateStr) => {
  if (!dateStr) return "";
  // Se já está no formato DD/MM/YYYY, retorna como está
  if (dateStr.includes('/')) return dateStr;
  // Formato YYYY-MM-DD do backend
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

// Calcular dias até uma data sem problemas de timezone
const calculateDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts;
  // Criar data usando componentes para evitar timezone
  const targetDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);
  const diffTime = targetDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

// Loading Screen Component
function LoadingScreen() {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center z-50" style={{backgroundColor:'#F8F7FF'}}>
      {/* Glow gradients sutis no fundo */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute" style={{top:'30%', left:'30%', width:'40vw', height:'40vw', borderRadius:'50%', background:'radial-gradient(50% 50% at 50% 50%, rgba(107,61,246,0.10), transparent 70%)', filter:'blur(60px)'}} />
        <div className="absolute" style={{top:'45%', right:'25%', width:'30vw', height:'30vw', borderRadius:'50%', background:'radial-gradient(50% 50% at 50% 50%, rgba(255,77,141,0.10), transparent 70%)', filter:'blur(60px)'}} />
      </div>
      <div className="relative loading-logo flex flex-col items-center gap-5">
        <span
          aria-label="Liventy"
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 96,
            height: 96,
            borderRadius: 22,
            background: "linear-gradient(160deg, #FFFFFF 0%, #FAF7FF 100%)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.9), 0 18px 40px -14px rgba(107,61,246,0.45), 0 8px 18px -6px rgba(255,77,141,0.25)",
            border: "1px solid rgba(107,61,246,0.10)",
            overflow: "hidden",
          }}
        >
          <img
            src="/brand/liventy-l-mark.webp"
            alt=""
            aria-hidden="true"
            style={{
              width: 62,
              height: 62,
              objectFit: "contain",
              display: "block",
              filter: "drop-shadow(0 6px 12px rgba(107,61,246,0.30))",
            }}
          />
        </span>
        <img
          src="/brand/liventy-wordmark-dark.webp"
          alt="liventy"
          style={{ height: 38, width: 'auto', display: 'block' }}
        />
      </div>
      <div className="relative loading-text mt-10 w-40">
        <div className="h-1 bg-slate-200/70 rounded-full overflow-hidden">
          <div className="loading-progress h-full rounded-full" style={{backgroundImage:'linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}}></div>
        </div>
      </div>
    </div>
  );
}

// Helper function to extract error message from API response
const getErrorMessage = (error, defaultMessage = "Ocorreu um erro") => {
  if (!error.response?.data?.detail) {
    return error.message || defaultMessage;
  }
  const detail = error.response.data.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
  }
  if (typeof detail === 'object') {
    // 402 trial_expired: silenciar (modal premium global já trata via interceptor)
    if (detail.code === 'trial_expired') {
      return null;
    }
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return defaultMessage;
};

// Versão segura de toast.error: aceita objeto ou string e nunca renderiza objeto cru
const safeToastError = (err, defaultMsg) => {
  const msg = typeof err === 'string' ? err : getErrorMessage(err, defaultMsg);
  if (msg) {
    // Lazy import para evitar circular: usar window.toast via sonner se disponível
    try { (require('sonner')).toast.error(msg); } catch { /* sonner indisponível */ }
  }
};

// Auth Context
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

// ─────────────────────────────────────────────────────────────────────────────
// Plan helpers — detecta admin via email (espelha lógica do AdminRoute)
// ─────────────────────────────────────────────────────────────────────────────
const ADMIN_EMAILS_LIST = (process.env.REACT_APP_ADMIN_EMAILS || "adm.liventy@gmail.com")
  .split(",").map(e => e.trim().toLowerCase()).filter(Boolean);

const isAdminUser = (user) => !!user?.email && ADMIN_EMAILS_LIST.includes(user.email.toLowerCase());

/**
 * PlanBadge — pill premium para exibir plano do usuário.
 * Variantes: ADMIN (gradient roxo→dourado, glow), PRO (amber), FREE (zinc neutro)
 */
function PlanBadge({ user, size = "sm", className = "" }) {
  const isAdmin = isAdminUser(user);
  const isPro = user?.subscription === "pro" || user?.plan === "pro";
  const sizeCls = size === "xs"
    ? "text-[9.5px] px-1.5 py-0.5 gap-0.5"
    : size === "md"
    ? "text-[11px] px-2.5 py-1 gap-1.5"
    : "text-[10px] px-2 py-0.5 gap-1";

  if (isAdmin) {
    return (
      <span
        className={`inline-flex items-center rounded-full font-extrabold uppercase tracking-widest text-white whitespace-nowrap ${sizeCls} ${className}`}
        style={{
          background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 40%, #F59E0B 100%)",
          boxShadow: "0 4px 14px -4px rgba(124,58,237,0.5), 0 0 0 1px rgba(245,158,11,0.3) inset",
          textShadow: "0 1px 2px rgba(0,0,0,0.15)",
        }}
        data-testid="plan-badge-admin"
      >
        <Sparkles size={size === "md" ? 11 : size === "xs" ? 8 : 9} className="text-amber-200" strokeWidth={2.5} />
        ADMIN
      </span>
    );
  }
  if (isPro) {
    return (
      <span
        className={`inline-flex items-center rounded-full font-bold uppercase tracking-wider whitespace-nowrap ${sizeCls} ${className}`}
        style={{
          background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
          color: "#92400e",
          boxShadow: "0 0 0 1px rgba(245,158,11,0.3) inset",
        }}
        data-testid="plan-badge-pro"
      >
        <Crown size={size === "md" ? 11 : size === "xs" ? 8 : 9} strokeWidth={2.4} />
        PRO
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center rounded-full font-bold uppercase tracking-wider whitespace-nowrap bg-zinc-100 text-zinc-500 ${sizeCls} ${className}`}
      data-testid="plan-badge-free"
    >
      FREE
    </span>
  );
}

function AuthProvider({ children }) {
  // ⚠ CRÍTICO: setamos o header de Authorization SINCRONAMENTE (antes do primeiro
  // render), pra evitar race condition onde componentes filhos disparam requests
  // ANTES do useEffect do AuthProvider rodar — causando 403 em /auth/onboarding-status
  // e endpoints autenticados logo após login/redeem/page-reload.
  if (typeof window !== "undefined") {
    const _initialToken = localStorage.getItem("token");
    if (_initialToken) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${_initialToken}`;
    }
  }

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState(null); // /api/me/capabilities — single source of truth dos feature gates

  // Interceptor global para 402 — trial expirado: abre modal premium (sem redirect)
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 402) {
          const detail = err?.response?.data?.detail;
          const code = typeof detail === "object" ? detail?.code : null;
          const message = typeof detail === "object" ? detail?.message : (typeof detail === "string" ? detail : null);
          if (code === "trial_expired") {
            window.dispatchEvent(new CustomEvent("liventy:premium-gate", { detail: { message } }));
          }
        }
        // Iter 75 — staff bloqueado por RBAC: dispara modal premium "Meu Espaço Pro"
        if (err?.response?.status === 403) {
          const detail = err?.response?.data?.detail;
          const msg = typeof detail === "string" ? detail : "";
          const code = typeof detail === "object" ? detail?.code : null;
          if (msg.toLowerCase().includes("colaborador")) {
            window.dispatchEvent(new CustomEvent("liventy:staff-upgrade-gate", { detail: { message: msg } }));
          } else if (code === "plan_feature_locked") {
            window.dispatchEvent(new CustomEvent("liventy:plan-lock", { detail: { message: detail?.message } }));
          } else if (code === "event_limit_reached") {
            window.dispatchEvent(new CustomEvent("liventy:event-limit", { detail: { message: detail?.message } }));
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (err) {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setLoading(false);
    }
  }, []);

  // Carrega capabilities (regras de produto centralizadas no backend).
  // Falha silenciosa → fallback `null` permite ao consumidor decidir
  // (recomendado: render condicional sem mostrar entry-points até carregar).
  const fetchCapabilities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me/capabilities`);
      setCapabilities(res.data || null);
    } catch {
      setCapabilities(null);
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
      fetchCapabilities();
    } else {
      setLoading(false);
      setCapabilities(null);
    }
  }, [token, fetchUser, fetchCapabilities]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.access_token);
    // Assina o header ANTES de fetchCapabilities para evitar 403 inicial.
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    setToken(res.data.access_token);
    setUser(res.data.user);
    fetchCapabilities();
    return res.data;
  };

  const register = async (name, email, password, phone) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password, phone, role: "personal", accepted_terms: true });
    localStorage.setItem("token", res.data.access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.access_token}`;
    setToken(res.data.access_token);
    setUser(res.data.user);
    fetchCapabilities();
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setCapabilities(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  const refreshUser = async () => {
    await fetchUser();
    await fetchCapabilities();
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser, capabilities, refreshCapabilities: fetchCapabilities }}>
      {loading ? <LoadingScreen /> : children}
    </AuthContext.Provider>
  );
}

// Hook ergonômico para consumir capabilities — sempre retorna um objeto seguro
// (defaults a `false`). Use no lugar de checagens manuais por subscription/profile_type.
// `_loaded` indica se a resposta do backend já chegou (útil para evitar bounce
// indevido em useEffect de proteção de rota).
const useCapabilities = () => {
  const { capabilities } = useAuth() || {};
  if (capabilities) {
    return { ...capabilities, _loaded: true };
  }
  return {
    can_create_events: false,
    can_see_revenues: false,
    can_access_inspirations: false,
    can_access_seating: false,
    can_access_checkin: false,
    can_manage_staff: false,
    can_access_whatsapp: false,
    can_access_pro_features: false,
    can_manage_subscription: false,
    can_access_admin: false,
    plan_tier: null,
    has_active_subscription: false,
    _context: {},
    _loaded: false,
  };
};

// Wedding Context
const WeddingContext = createContext(null);
const useWedding = () => useContext(WeddingContext);

function WeddingProvider({ children }) {
  const [wedding, setWedding] = useState(null);
  const [weddings, setWeddings] = useState(null); // null = loading, [] = no weddings
  const weddingsLoadedRef = useRef(false);
  const { token, user } = useAuth();

  // Fetch weddings apenas UMA VEZ - trava ANTES de qualquer coisa
  const fetchWeddings = useCallback(async () => {
    if (weddingsLoadedRef.current) return;
    weddingsLoadedRef.current = true;
    
    if (!token) {
      weddingsLoadedRef.current = false;
      return;
    }
    
    try {
      const res = await axios.get(`${API}/weddings`);
      const list = res.data;
      setWeddings(list);
      if (list.length > 0) {
        // Set active event based on user's active_event_id or first event
        const activeId = user?.active_event_id;
        const active = activeId ? list.find(w => w.id === activeId) : null;
        setWedding(prev => prev || active || list[0]);
      }
    } catch (err) {
      setWeddings([]);
    }
  }, [token, user?.active_event_id]);

  // Carrega weddings uma única vez quando token disponível
  useEffect(() => {
    if (token && !weddingsLoadedRef.current) {
      fetchWeddings();
    }
  }, [token, fetchWeddings]);

  // Reset quando token muda (logout/login)
  useEffect(() => {
    if (!token) {
      weddingsLoadedRef.current = false;
      setWeddings(null);
      setWedding(null);
    }
  }, [token]);

  // Criar evento simples (novo fluxo)
  const createEvent = async (data) => {
    const res = await axios.post(`${API}/events/create`, data);
    // Refetch weddings para pegar dados completos do evento
    weddingsLoadedRef.current = false;
    const weddingsRes = await axios.get(`${API}/weddings`);
    const list = weddingsRes.data;
    setWeddings(list);
    const newEvent = list.find(w => w.id === res.data.id);
    setWedding(newEvent || list[0]);
    return res.data;
  };

  // Cria evento + receita vinculada (custo do serviço com plano de pagamento) numa só transação.
  const createEventFromRevenue = async (payload) => {
    const res = await axios.post(`${API}/events-from-revenue`, payload);
    weddingsLoadedRef.current = false;
    const weddingsRes = await axios.get(`${API}/weddings`);
    const list = weddingsRes.data;
    setWeddings(list);
    const newId = res.data?.wedding?.id;
    const newEvent = list.find(w => w.id === newId);
    setWedding(newEvent || list[0]);
    return res.data;
  };

  // Set active event
  const setActiveEvent = async (eventId) => {
    try {
      await axios.put(`${API}/auth/active-event`, { event_id: eventId });
      const event = weddings?.find(w => w.id === eventId);
      if (event) setWedding(event);
    } catch (err) {
      toast.error("Erro ao trocar evento");
    }
  };

  const createWedding = async (data) => {
    const res = await axios.post(`${API}/weddings`, data);
    setWedding(res.data);
    setWeddings(prev => [...(prev || []), res.data]);
    return res.data;
  };

  const updateWedding = async (data) => {
    const res = await axios.put(`${API}/weddings/${wedding.id}`, data);
    setWedding(res.data);
    return res.data;
  };

  // Force refresh weddings list
  const refreshWeddings = useCallback(async () => {
    weddingsLoadedRef.current = false;
    await fetchWeddings();
  }, [fetchWeddings]);

  // Delete an event (cascade) and update local state
  const deleteEvent = async (eventId) => {
    await axios.delete(`${API}/weddings/${eventId}`);
    weddingsLoadedRef.current = false;
    const weddingsRes = await axios.get(`${API}/weddings`);
    const list = weddingsRes.data;
    setWeddings(list);
    if (wedding?.id === eventId) {
      setWedding(list.length > 0 ? list[0] : null);
    }
  };

  // Complete an event
  const completeEvent = async (eventId) => {
    await axios.put(`${API}/weddings/${eventId}/complete`);
    weddingsLoadedRef.current = false;
    const weddingsRes = await axios.get(`${API}/weddings`);
    const list = weddingsRes.data;
    setWeddings(list);
    if (wedding?.id === eventId) {
      const updated = list.find(w => w.id === eventId);
      setWedding(updated || (list.length > 0 ? list[0] : null));
    }
  };

  // Delete ALL events
  const deleteAllEvents = async () => {
    await axios.delete(`${API}/weddings/delete-all`);
    setWeddings([]);
    setWedding(null);
    weddingsLoadedRef.current = false;
  };

  // Event categories state
  const [eventCategories, setEventCategories] = useState([]);

  const fetchEventCategories = useCallback(async () => {
    if (!wedding?.id) { setEventCategories([]); return; }
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/event-categories`);
      setEventCategories(res.data);
    } catch { setEventCategories([]); }
  }, [wedding?.id]);

  useEffect(() => { fetchEventCategories(); }, [fetchEventCategories]);

  // Valor memoizado - SEM dashboard para evitar re-renders em cascata
  const value = useMemo(() => ({ 
    wedding, weddings,
    setWedding, fetchWeddings, createWedding, updateWedding,
    createEvent, createEventFromRevenue, setActiveEvent, deleteEvent, completeEvent, deleteAllEvents, refreshWeddings,
    eventCategories, fetchEventCategories
  }), [wedding, weddings, fetchWeddings, eventCategories, fetchEventCategories]);

  return (
    <WeddingContext.Provider value={value}>
      {children}
    </WeddingContext.Provider>
  );
}

// UI Components - Design System (Cinza, Branco, Azul)
function Button({ children, variant = "primary", size = "md", className = "", disabled = false, ...props }) {
  const baseClasses = "inline-flex items-center justify-center font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]";
  const variants = {
    primary: "bg-brand-deep hover:bg-brand text-white",
    secondary: "bg-white hover:bg-zinc-50 text-zinc-900 border border-zinc-200",
    outline: "border border-zinc-200 text-zinc-900 hover:bg-zinc-50",
    ghost: "hover:bg-zinc-100 text-zinc-700",
    danger: "bg-red-500 hover:bg-red-600 text-white",
    white: "bg-white hover:bg-zinc-50 text-zinc-900 border border-zinc-200",
    success: "bg-emerald-500 hover:bg-emerald-600 text-white"
  };
  const sizes = {
    sm: "px-3 py-1.5 text-[13px]",
    md: "px-4 py-2.5 text-[13px]",
    lg: "px-6 py-3 text-sm",
    xl: "px-8 py-4 text-base"
  };
  return (
    <button className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`} disabled={disabled} {...props}>
      {children}
    </button>
  );
}

function Input({ label, error, className = "", ...props }) {
  return (
    <div className={className}>
      {label && <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">{label}</label>}
      <input 
        className={`w-full px-3.5 py-2.5 rounded-xl border ${error ? 'border-red-400 focus:border-red-500' : 'border-zinc-200 focus:border-zinc-400'} focus:ring-4 focus:ring-zinc-900/5 outline-none transition-all duration-200 bg-white text-zinc-900 placeholder-zinc-400 text-[14px]`}
        {...props}
      />
      {error && <p className="mt-1.5 text-sm text-red-500">{error}</p>}
    </div>
  );
}

function Select({ label, options, className = "", ...props }) {
  return (
    <div className={className}>
      {label && <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">{label}</label>}
      <select className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 focus:border-zinc-400 focus:ring-4 focus:ring-zinc-900/5 outline-none transition-all duration-200 bg-white text-zinc-900 text-[14px]" {...props}>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

/**
 * Card — wrapper visual padronizado (estilo Check-in convidados).
 * Hover premium minimalista: brightness(0.97) — sutil, sem lift exagerado.
 * Active mobile: brightness(0.95) — feedback de toque tipo app nativo.
 * Auto-clicável quando recebe onClick; ou force com `hoverable`.
 */
function Card({ children, className = "", animate = false, onClick, hoverable, ...props }) {
  const isClickable = !!onClick || !!hoverable;
  const base = "bg-white rounded-2xl border border-zinc-200/60 shadow-[0_1px_3px_rgba(24,24,27,0.04)] transition-[filter,background-color,border-color] duration-[180ms] ease-out";
  const interactive = isClickable
    ? "cursor-pointer card-hover-brightness"
    : "";
  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={isClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(e); } } : undefined}
      className={`${base} ${interactive} ${animate ? 'card-animate opacity-0' : ''} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subvalue, color = "blue", onClick }) {
  const colorMap = {
    blue: "#3B82F6",
    sky: "#0EA5E9",
    indigo: "#6366F1",
    slate: "#71717A",
    emerald: "#10B981",
    orange: "#F59E0B",
    rose: "#F43F5E",
    purple: "#A855F7"
  };
  const accent = colorMap[color] || "#71717A";
  return (
    <Card className="p-5" onClick={onClick} animate>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{backgroundColor: `${accent}15`}}>
          <Icon size={14} style={{color: accent}} strokeWidth={2} />
        </div>
        <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-2xl font-semibold text-zinc-900 tracking-tight">{value}</p>
      {subvalue && <p className="text-[12px] text-zinc-500 mt-1">{subvalue}</p>}
    </Card>
  );
}

function Modal({ isOpen, onClose, title, children, fullScreen = false }) {
  const isMobile = useIsMobile();

  // ── Body lock + esconder bottom nav/FAB enquanto modal aberto (sensação app nativo) ──
  useEffect(() => {
    if (!isOpen || !isMobile) return undefined;
    const html = document.documentElement;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevTouch = body.style.touchAction;
    body.style.overflow = "hidden";
    body.style.touchAction = "none";
    html.classList.add("app-modal-open");
    // Haptic feedback leve ao abrir (PWA iOS/Android)
    try { navigator.vibrate?.(8); } catch {}
    return () => {
      body.style.overflow = prevOverflow;
      body.style.touchAction = prevTouch;
      html.classList.remove("app-modal-open");
    };
  }, [isOpen, isMobile]);

  if (!isOpen) return null;

  // ── Variante FULL-SCREEN mobile (sensação app nativo, 100dvh, safe-areas) ──
  if (isMobile && fullScreen) {
    return (
      <div
        className="fixed inset-0 z-[10005] flex flex-col bg-white animate-slide-up"
        style={{ height: "100dvh" }}
        data-testid="modal-fullscreen"
        role="dialog"
        aria-modal="true"
      >
        <div
          className="flex items-center justify-between px-5 pb-3 border-b border-zinc-100 shrink-0"
          style={{ paddingTop: "calc(max(env(safe-area-inset-top), 0px) + 12px)" }}
        >
          <h2 className="text-[15.5px] font-semibold text-zinc-900 tracking-tight truncate">{title || ""}</h2>
          <button
            onClick={onClose}
            className="p-2 -mr-2 rounded-lg active:bg-zinc-100 transition-colors"
            data-testid="modal-close-btn"
            aria-label="Fechar"
          >
            <X size={20} className="text-zinc-700" />
          </button>
        </div>
        <div
          className="flex-1 overflow-y-auto overscroll-contain px-5 py-4"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
        >
          {children}
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <Vaul.Root open={isOpen} onOpenChange={(o) => { if (!o) onClose?.(); }}>
        <Vaul.Portal>
          <Vaul.Overlay className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[2px]" />
          <Vaul.Content
            className="fixed inset-x-0 bottom-0 z-[61] flex flex-col bg-white rounded-t-3xl outline-none max-h-[92vh]"
            data-testid="modal-mobile-sheet"
          >
            <div className="mx-auto mt-2.5 mb-1 h-1.5 w-12 rounded-full bg-zinc-200 shrink-0" />
            <div className="flex items-center justify-between px-5 pt-2 pb-3 border-b border-zinc-100">
              <Vaul.Title className="text-[16px] font-semibold text-zinc-900 tracking-tight">{title}</Vaul.Title>
              <button onClick={onClose} className="p-1.5 -mr-1.5 rounded-lg active:bg-zinc-100 transition-colors" data-testid="modal-close-btn">
                <X size={18} className="text-zinc-500" />
              </button>
            </div>
            <Vaul.Description className="sr-only">{title}</Vaul.Description>
            <div className="px-5 py-4 overflow-y-auto pb-[max(env(safe-area-inset-bottom),16px)]">
              {children}
            </div>
          </Vaul.Content>
        </Vaul.Portal>
      </Vaul.Root>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-brand-deep/50 backdrop-blur-[3px] animate-in fade-in duration-200" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl shadow-2xl ring-1 ring-zinc-200/60 max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
          <h3 className="text-[17px] font-semibold tracking-tight text-zinc-900">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-500 hover:text-zinc-900 transition-all" data-testid="modal-close-btn">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// ==================== CATEGORY MANAGER MODAL (fullscreen, scroll, CRUD compartilhado entre Orçamentos e Despesas) ====================
function CategoryManagerModal({
  isOpen, onClose,
  categories = [],
  editingCatId, setEditingCatId,
  editingCatName, setEditingCatName,
  newCatName, setNewCatName,
  catLoading,
  onCreate, onRename, onDeleteRequest,
  contextLabel = "itens",
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Gerenciar categorias" fullScreen>
      <div className="space-y-5" data-testid="category-manager-modal">
        {/* Form criar — sticky no topo */}
        <div className="rounded-2xl border border-zinc-200/70 bg-zinc-50/50 p-3.5">
          <p className="text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-2">Nova categoria</p>
          <div className="flex items-center gap-2">
            <input
              value={newCatName}
              onChange={e => setNewCatName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newCatName.trim()) { e.preventDefault(); onCreate(); } }}
              placeholder="Ex: Decoração, Buffet, Música…"
              className="flex-1 text-[14px] px-3.5 py-2.5 border border-zinc-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-violet-100 focus:border-violet-300 bg-white transition-all"
              data-testid="cat-modal-new-input"
            />
            <button
              onClick={onCreate}
              disabled={catLoading || !newCatName.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-zinc-900 text-white rounded-xl hover:bg-zinc-800 text-[13px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              data-testid="cat-modal-add-btn"
            >
              <Plus size={14} strokeWidth={2.6} />
              Adicionar
            </button>
          </div>
        </div>

        {/* Lista de categorias */}
        <div>
          <p className="text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-2 px-1">
            Suas categorias · {categories.length}
          </p>
          {categories.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/40 p-8 text-center">
              <p className="text-[13px] text-zinc-500">Sem categorias ainda. Crie a primeira acima.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="cat-modal-list">
              {categories.map(cat => (
                <div
                  key={cat.id}
                  className="flex items-center gap-2 bg-white rounded-xl border border-zinc-200/70 px-3 py-2.5 hover:border-zinc-300 transition-colors"
                  data-testid={`cat-modal-item-${cat.slug}`}
                >
                  {editingCatId === cat.id ? (
                    <React.Fragment>
                      <input
                        value={editingCatName}
                        onChange={e => setEditingCatName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onRename(cat.id); } if (e.key === 'Escape') { setEditingCatId(null); setEditingCatName(''); } }}
                        className="flex-1 text-[13.5px] px-2.5 py-1.5 border border-violet-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-200 bg-white"
                        autoFocus
                        data-testid={`cat-modal-edit-input-${cat.slug}`}
                      />
                      <button onClick={() => onRename(cat.id)} className="text-emerald-600 hover:bg-emerald-50 p-1.5 rounded-lg transition-colors" aria-label="Salvar" data-testid={`cat-modal-save-${cat.slug}`}>
                        <Check size={16} strokeWidth={2.5} />
                      </button>
                      <button onClick={() => { setEditingCatId(null); setEditingCatName(''); }} className="text-zinc-400 hover:bg-zinc-50 p-1.5 rounded-lg transition-colors" aria-label="Cancelar">
                        <X size={16} />
                      </button>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <span className="flex-1 text-[13.5px] text-zinc-800 font-medium truncate">{cat.name}</span>
                      <span className="text-[11px] text-zinc-400 tabular-nums whitespace-nowrap mr-1">
                        {cat.expense_count || 0} {(cat.expense_count || 0) === 1 ? contextLabel.replace(/s$/, '') : contextLabel}
                      </span>
                      {cat.is_protected ? (
                        <span className="text-[9px] uppercase tracking-wider text-zinc-400 font-semibold px-1.5 py-0.5 rounded bg-zinc-100">Padrão</span>
                      ) : (
                        <React.Fragment>
                          <button
                            onClick={() => { setEditingCatId(cat.id); setEditingCatName(cat.name); }}
                            className="text-zinc-400 hover:text-violet-600 hover:bg-violet-50 p-1.5 rounded-lg transition-colors"
                            aria-label="Renomear"
                            data-testid={`cat-modal-rename-${cat.slug}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => onDeleteRequest(cat)}
                            className="text-zinc-400 hover:text-rose-600 hover:bg-rose-50 p-1.5 rounded-lg transition-colors"
                            aria-label="Excluir"
                            data-testid={`cat-modal-delete-${cat.slug}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-[11px] text-zinc-400 px-1">
          💡 Categorias <strong>Padrão</strong> não podem ser excluídas. Ao excluir uma categoria customizada com itens, você poderá movê-los para outra.
        </p>
      </div>
    </Modal>
  );
}



// ==================== CATEGORY COMBOBOX (Notion-style searchable + creatable + inline rename) ====================
function CategoryCombobox({ value, onChange, wedding, categories = [], onCategoriesChange, label = "Categoria", required = false, testId = "category-combobox" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const ref = useRef(null);

  // Recent categories (localStorage por wedding)
  const recentKey = wedding?.id ? `liventy.recent_cats.${wedding.id}` : null;
  const readRecent = () => {
    if (!recentKey) return [];
    try { return JSON.parse(localStorage.getItem(recentKey) || '[]'); } catch { return []; }
  };
  const pushRecent = (slug) => {
    if (!recentKey || !slug) return;
    try {
      const prev = readRecent().filter(s => s !== slug);
      const next = [slug, ...prev].slice(0, 5);
      localStorage.setItem(recentKey, JSON.stringify(next));
    } catch { /* ignore */ }
  };

  // Body lock + hide app nav quando picker fullscreen mobile estiver aberto
  useEffect(() => {
    if (!open) return undefined;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) return undefined;
    const html = document.documentElement;
    const prevOverflow = document.body.style.overflow;
    html.classList.add('app-modal-open');
    document.body.style.overflow = 'hidden';
    return () => {
      html.classList.remove('app-modal-open');
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setRenamingId(null);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const selected = categories.find(c => c.slug === value);
  const q = search.trim().toLowerCase();
  const filtered = q ? categories.filter(c => c.name.toLowerCase().includes(q)) : categories;
  const exactMatch = categories.find(c => c.name.toLowerCase() === q);
  const showCreate = q.length > 0 && !exactMatch;
  const recentSlugs = readRecent();
  const recentCats = recentSlugs
    .map(s => categories.find(c => c.slug === s))
    .filter(Boolean)
    .filter(c => !q || c.name.toLowerCase().includes(q));

  const handlePick = (slug) => {
    try { navigator.vibrate?.(8); } catch { /* noop */ }
    pushRecent(slug);
    onChange(slug);
    setOpen(false);
    setSearch("");
  };

  const handleCreate = async () => {
    const name = search.trim();
    if (!name || creating || !wedding?.id) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/weddings/${wedding.id}/event-categories`, { name });
      const newSlug = res?.data?.slug;
      if (onCategoriesChange) await onCategoriesChange();
      if (newSlug) { pushRecent(newSlug); onChange(newSlug); }
      setSearch("");
      setOpen(false);
      toast.success(`Categoria "${name}" criada`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar categoria");
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (cat) => {
    const name = renameValue.trim();
    if (!name || name === cat.name) { setRenamingId(null); return; }
    try {
      await axios.put(`${API}/weddings/${wedding.id}/event-categories/${cat.id}`, { name });
      if (onCategoriesChange) await onCategoriesChange();
      toast.success("Categoria renomeada");
      setRenamingId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao renomear");
    }
  };

  return (
    <div ref={ref} className="relative">
      {label && <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>}
      <button type="button" onClick={() => { try { navigator.vibrate?.(6); } catch {} setOpen(o => !o); }}
        className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 bg-white text-left text-[14px] flex items-center justify-between hover:border-zinc-300 transition-colors focus:outline-none focus:ring-4 focus:ring-zinc-900/5 tap-press"
        data-testid={`${testId}-trigger`}>
        <span className={selected ? "text-zinc-900" : "text-zinc-400"}>{selected ? selected.name : "Selecione ou crie uma categoria"}</span>
        <ChevronDown size={15} className={`text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* ═══ DESKTOP dropdown (md+) ═══ */}
      {open && (
        <div className="hidden md:block absolute z-50 left-0 right-0 mt-1.5 rounded-xl border border-zinc-200 bg-white shadow-xl overflow-hidden" style={{boxShadow:'0 12px 32px -8px rgba(0,0,0,0.12)'}} data-testid={`${testId}-dropdown`}>
          <div className="p-2 border-b border-zinc-100">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar ou criar categoria..."
                className="w-full pl-8 pr-2.5 py-1.5 text-[13px] bg-zinc-50 rounded-lg border border-transparent outline-none focus:bg-white focus:border-zinc-200"
                onKeyDown={e => { if (e.key === 'Enter' && showCreate) { e.preventDefault(); handleCreate(); } if (e.key === 'Escape') { setOpen(false); setSearch(""); } }}
                data-testid={`${testId}-search`} />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.map(cat => (
              <div key={cat.id} className="group flex items-center gap-1 px-2 py-0.5 hover:bg-zinc-50 rounded-md mx-1">
                {renamingId === cat.id ? (
                  <input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)}
                    onBlur={() => handleRename(cat)}
                    onKeyDown={e => { if (e.key === 'Enter') handleRename(cat); if (e.key === 'Escape') setRenamingId(null); }}
                    className="flex-1 px-2 py-1 text-[13px] bg-white border border-zinc-200 rounded-md outline-none focus:ring-2 focus:ring-zinc-900/10 my-1"
                    data-testid={`${testId}-rename-input-${cat.slug}`} />
                ) : (
                  <React.Fragment>
                    <button type="button" onClick={() => handlePick(cat.slug)}
                      className="flex-1 text-left text-[13px] text-zinc-700 hover:text-zinc-900 px-2 py-1.5 flex items-center gap-2 rounded"
                      data-testid={`${testId}-option-${cat.slug}`}>
                      <span className="flex-1">{cat.name}</span>
                      {cat.slug === value && <Check size={13} className="text-zinc-900" />}
                    </button>
                    {!cat.is_protected && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setRenamingId(cat.id); setRenameValue(cat.name); }}
                        className="opacity-0 group-hover:opacity-100 p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md transition-all"
                        title="Renomear"
                        data-testid={`${testId}-rename-btn-${cat.slug}`}>
                        <Pencil size={11} />
                      </button>
                    )}
                  </React.Fragment>
                )}
              </div>
            ))}
            {showCreate && (
              <button type="button" onClick={handleCreate} disabled={creating}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-zinc-700 hover:bg-zinc-50 border-t border-zinc-100 disabled:opacity-50"
                data-testid={`${testId}-create`}>
                <div className="w-5 h-5 rounded-md bg-zinc-100 flex items-center justify-center">
                  <Plus size={11} className="text-zinc-600" />
                </div>
                <span>Criar categoria</span>
                <span className="font-semibold text-zinc-900">"{search.trim()}"</span>
                {creating && <Loader2 size={12} className="animate-spin ml-auto text-zinc-400" />}
              </button>
            )}
            {filtered.length === 0 && !showCreate && (
              <p className="text-center text-[12px] text-zinc-400 py-6">Nenhuma categoria encontrada</p>
            )}
          </div>
        </div>
      )}

      {/* ═══ MOBILE picker fullscreen (< md) ═══ */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-[10020] flex flex-col bg-white"
          style={{ animation: 'cp-slide-in 220ms cubic-bezier(0.22,1,0.36,1) both' }}
          data-testid={`${testId}-picker-mobile`}
        >
          {/* Header sticky */}
          <div
            className="flex items-center gap-2 px-3 border-b border-zinc-100 bg-white"
            style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)', paddingBottom: 8 }}
          >
            <button
              type="button"
              onClick={() => { setOpen(false); setSearch(''); }}
              className="tap-press w-10 h-10 rounded-full flex items-center justify-center text-zinc-700 hover:bg-zinc-100"
              aria-label="Voltar"
              data-testid={`${testId}-picker-back`}
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className="text-[16px] font-semibold text-zinc-900 tracking-tight flex-1">Categorias</h2>
          </div>
          {/* Search */}
          <div className="px-4 py-3 border-b border-zinc-100">
            <div className="relative">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar ou criar categoria..."
                className="w-full pl-10 pr-3 py-3 text-[14.5px] bg-zinc-100 rounded-2xl border border-transparent outline-none focus:bg-white focus:border-zinc-200 focus:ring-4 focus:ring-zinc-900/5 transition-all"
                onKeyDown={e => { if (e.key === 'Enter' && showCreate) { e.preventDefault(); handleCreate(); } }}
                data-testid={`${testId}-picker-search`}
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
                  <X size={15} />
                </button>
              )}
            </div>
          </div>
          {/* Lista */}
          <div className="flex-1 overflow-y-auto overscroll-contain" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}>
            {/* Recentes */}
            {!q && recentCats.length > 0 && (
              <div className="pt-4 pb-2">
                <p className="text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] px-5 mb-1">Recentes</p>
                <ul>
                  {recentCats.map(cat => (
                    <li key={`r-${cat.id}`}>
                      <button
                        type="button"
                        onClick={() => handlePick(cat.slug)}
                        className="tap-press w-full flex items-center gap-3 px-5 py-3.5 text-left"
                        data-testid={`${testId}-picker-recent-${cat.slug}`}
                      >
                        <Clock size={15} className="text-zinc-400 flex-shrink-0" />
                        <span className="flex-1 text-[15px] text-zinc-900 truncate">{cat.name}</span>
                        {cat.slug === value && <Check size={16} className="text-zinc-900" />}
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="h-px bg-zinc-100 mx-5 mt-2" />
              </div>
            )}
            {/* Todas */}
            <div className={q || recentCats.length === 0 ? 'pt-4 pb-2' : 'pt-2 pb-2'}>
              {!q && <p className="text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] px-5 mb-1">Todas as categorias</p>}
              <ul>
                {filtered.map(cat => (
                  <li key={cat.id}>
                    <button
                      type="button"
                      onClick={() => handlePick(cat.slug)}
                      className="tap-press w-full flex items-center gap-3 px-5 py-3.5 text-left"
                      data-testid={`${testId}-picker-option-${cat.slug}`}
                    >
                      <span className="w-7 h-7 rounded-lg bg-zinc-100 text-zinc-500 flex items-center justify-center flex-shrink-0">
                        <Folder size={14} />
                      </span>
                      <span className="flex-1 text-[15px] text-zinc-900 truncate">{cat.name}</span>
                      {cat.slug === value && <Check size={16} className="text-zinc-900" />}
                    </button>
                  </li>
                ))}
              </ul>
              {filtered.length === 0 && !showCreate && (
                <p className="text-center text-[13.5px] text-zinc-400 py-10">Nenhuma categoria encontrada</p>
              )}
            </div>
            {/* Criar */}
            {showCreate && (
              <div className="px-5 pt-2 pb-6">
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating}
                  className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border-2 border-dashed border-zinc-300 hover:border-zinc-900 text-zinc-700 hover:text-zinc-900 transition-colors disabled:opacity-50"
                  data-testid={`${testId}-picker-create`}
                >
                  <span className="w-9 h-9 rounded-full bg-zinc-900 text-white flex items-center justify-center flex-shrink-0">
                    <Plus size={16} strokeWidth={2.5} />
                  </span>
                  <span className="flex-1 text-left">
                    <span className="block text-[13.5px] font-medium">Criar categoria</span>
                    <span className="block text-[12px] text-zinc-500 mt-0.5 truncate">"{search.trim()}"</span>
                  </span>
                  {creating && <Loader2 size={16} className="animate-spin text-zinc-400" />}
                </button>
              </div>
            )}
          </div>
          <style>{`
            @keyframes cp-slide-in {
              from { opacity: 0.4; transform: translateX(28px); }
              to   { opacity: 1; transform: translateX(0); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// OptionPicker — Picker reutilizável (mobile fullscreen / desktop
// dropdown nativo). Para listas curtas e fixas, sem criação inline.
// ────────────────────────────────────────────────────────────────
function OptionPicker({
  value,
  onChange,
  options = [],
  label,
  required = false,
  placeholder = "Selecione uma opção",
  title = "Selecione",
  testId = "option-picker",
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) return undefined;
    const html = document.documentElement;
    const prevOverflow = document.body.style.overflow;
    html.classList.add('app-modal-open');
    document.body.style.overflow = 'hidden';
    return () => {
      html.classList.remove('app-modal-open');
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const selected = options.find(o => o.value === value);
  const pick = (v) => {
    try { navigator.vibrate?.(8); } catch {}
    onChange(v);
    setOpen(false);
  };

  return (
    <div className="relative">
      {label && (
        <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <button
        type="button"
        onClick={() => { try { navigator.vibrate?.(6); } catch {} setOpen(true); }}
        className="tap-press w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 bg-white text-left text-[14px] flex items-center justify-between hover:border-zinc-300 transition-colors focus:outline-none focus:ring-4 focus:ring-zinc-900/5"
        data-testid={`${testId}-trigger`}
      >
        <span className={selected ? 'text-zinc-900' : 'text-zinc-400'}>{selected ? selected.label : placeholder}</span>
        <ChevronDown size={15} className={`text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Mobile fullscreen */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-[10020] flex flex-col bg-white"
          style={{ animation: 'cp-slide-in 220ms cubic-bezier(0.22,1,0.36,1) both' }}
          data-testid={`${testId}-picker-mobile`}
        >
          <div
            className="flex items-center gap-2 px-3 border-b border-zinc-100 bg-white"
            style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)', paddingBottom: 8 }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="tap-press w-10 h-10 rounded-full flex items-center justify-center text-zinc-700 hover:bg-zinc-100"
              aria-label="Voltar"
              data-testid={`${testId}-picker-back`}
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className="text-[16px] font-semibold text-zinc-900 tracking-tight flex-1">{title}</h2>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain pt-2 pb-6" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}>
            <ul>
              {options.map(opt => (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => pick(opt.value)}
                    className="tap-press w-full flex items-center gap-3 px-5 py-3.5 text-left"
                    data-testid={`${testId}-picker-option-${opt.value}`}
                  >
                    {opt.icon && (
                      <span className="w-7 h-7 rounded-lg bg-zinc-100 text-zinc-500 flex items-center justify-center flex-shrink-0">
                        {opt.icon}
                      </span>
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] text-zinc-900 truncate">{opt.label}</span>
                      {opt.description && <span className="block text-[12px] text-zinc-500 truncate mt-0.5">{opt.description}</span>}
                    </span>
                    {opt.value === value && <Check size={16} className="text-zinc-900" />}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Desktop dropdown */}
      {open && (
        <div className="hidden md:block absolute z-50 left-0 right-0 mt-1.5 rounded-xl border border-zinc-200 bg-white shadow-xl overflow-hidden" style={{boxShadow:'0 12px 32px -8px rgba(0,0,0,0.12)'}} data-testid={`${testId}-dropdown`}>
          <div className="max-h-72 overflow-y-auto py-1">
            {options.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => pick(opt.value)}
                className="w-full text-left text-[13px] text-zinc-700 hover:bg-zinc-50 px-3 py-2 flex items-center gap-2"
                data-testid={`${testId}-option-${opt.value}`}
              >
                <span className="flex-1">{opt.label}</span>
                {opt.value === value && <Check size={13} className="text-zinc-900" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== TOUR GUIADO ====================
const TourContext = createContext();

const TOUR_STEPS = [
  {
    id: 'comece-aqui',
    target: '[data-tour="comece-aqui"]',
    title: 'Assistente IA',
    content: 'Aqui está a IA que ajuda você a planejar o casamento, sugerindo próximos passos e prioridades.',
    icon: Sparkles,
    module: null, // Disponível para todos
    position: 'right'
  },
  {
    id: 'convidados',
    target: '[data-tour="convidados"]',
    title: 'Convidados e Check-in',
    content: 'Gerencie sua lista de convidados, confirmações de presença e o check-in no dia do evento.',
    icon: Users,
    module: 'convidados',
    position: 'right'
  },
  {
    id: 'orcamentos',
    target: '[data-tour="orcamentos"]',
    title: 'Orçamentos e Despesas',
    content: 'Acompanhe os custos do casamento, orçamentos aprovados e despesas pagas.',
    icon: DollarSign,
    module: 'orcamentos',
    position: 'right'
  },
  {
    id: 'agenda',
    target: '[data-tour="agenda"]',
    title: 'Agenda',
    content: 'Centralize prazos, reuniões, pagamentos e compromissos importantes.',
    icon: Calendar,
    module: 'agenda',
    position: 'right'
  },
  {
    id: 'mesas',
    target: '[data-tour="mesas"]',
    title: 'Organização de Mesas',
    content: 'Organize os convidados nas mesas. A IA sugere a melhor configuração, mas você pode ajustar manualmente.',
    icon: Users,
    module: 'mesas',
    position: 'right'
  },
  {
    id: 'configuracoes',
    target: '[data-tour="configuracoes"]',
    title: 'Configurações e Usuários',
    content: 'Convide outras pessoas para ajudar no planejamento e defina o que cada uma pode acessar.',
    icon: Settings,
    module: 'configuracoes',
    position: 'right'
  },
  {
    id: 'suporte',
    target: '[data-tour="suporte"]',
    title: 'Suporte',
    content: 'Use o suporte sempre que tiver dúvidas sobre o funcionamento do sistema.',
    icon: HelpCircle,
    module: null,
    position: 'right'
  }
];

function TourProvider({ children }) {
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourCompleted, setTourCompleted] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  
  // Filtrar passos baseado nas permissões do usuário
  const availableSteps = TOUR_STEPS.filter(step => {
    if (!step.module) return true; // Passos sem módulo são visíveis para todos
    if (!userPermissions) return true; // Se não tem permissões definidas, mostra tudo (admin)
    return userPermissions[step.module]?.view === true;
  });
  
  // Salvar status do tour no backend
  const saveTourStatusToBackend = async (completed) => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await axios.put(`${API}/auth/tour-status`, 
          { tour_completed: completed },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      } catch (err) {
        console.warn('Não foi possível salvar status do tour no servidor');
      }
    }
  };
  
  const startTour = (permissions = null) => {
    setUserPermissions(permissions);
    setShowWelcome(true);
  };
  
  const beginTourSteps = () => {
    setShowWelcome(false);
    setShowTour(true);
    setCurrentStep(0);
  };
  
  const nextStep = () => {
    if (currentStep < availableSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      completeTour();
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  const skipTour = () => {
    setShowWelcome(false);
    setShowTour(false);
    setTourCompleted(true);
    localStorage.setItem('nosly_tour_skipped', 'true');
    saveTourStatusToBackend(true);
  };
  
  const completeTour = () => {
    setShowTour(false);
    setTourCompleted(true);
    localStorage.setItem('nosly_tour_completed', 'true');
    saveTourStatusToBackend(true);
  };
  
  const resetTour = () => {
    setTourCompleted(false);
    setCurrentStep(0);
    localStorage.removeItem('nosly_tour_completed');
    localStorage.removeItem('nosly_tour_skipped');
    saveTourStatusToBackend(false);
  };
  
  // Inicializar status do tour baseado no usuário logado
  const initializeTourStatus = (userTourCompleted) => {
    if (userTourCompleted) {
      setTourCompleted(true);
      localStorage.setItem('nosly_tour_completed', 'true');
    } else {
      // Verificar localStorage como fallback
      const completed = localStorage.getItem('nosly_tour_completed');
      const skipped = localStorage.getItem('nosly_tour_skipped');
      if (completed || skipped) {
        setTourCompleted(true);
      }
    }
  };
  
  // Verificar localStorage na inicialização
  useEffect(() => {
    const completed = localStorage.getItem('nosly_tour_completed');
    const skipped = localStorage.getItem('nosly_tour_skipped');
    if (completed || skipped) {
      setTourCompleted(true);
    }
  }, []);
  
  return (
    <TourContext.Provider value={{
      showWelcome,
      showTour,
      currentStep,
      tourCompleted,
      availableSteps,
      startTour,
      beginTourSteps,
      nextStep,
      prevStep,
      skipTour,
      completeTour,
      resetTour,
      initializeTourStatus
    }}>
      {children}
      
      {/* Modal de Boas-vindas */}
      {showWelcome && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header com gradiente */}
            <div className="p-8 text-center" style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}}>
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                <LiventyLogoMark size={64} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.03em' }}>Bem-vindo ao liventy</h2>
              <p className="text-white/85 text-sm">Eventos que conectam · Memórias que ficam</p>
            </div>
            
            {/* Conteúdo */}
            <div className="p-6">
              <p className="text-slate-600 text-center mb-6 leading-relaxed">
                O liventy foi criado para facilitar a organização do seu evento.
                Vamos te mostrar, em poucos passos, onde está cada funcionalidade.
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={beginTourSteps}
                  className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-all shadow-lg"
                  data-testid="start-tour-btn"
                >
                  <Play size={18} />
                  Começar tour
                </button>
                <button
                  onClick={skipTour}
                  className="w-full py-3 px-4 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl font-medium transition-all"
                  data-testid="skip-tour-btn"
                >
                  Pular por enquanto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Tour Interativo */}
      {showTour && availableSteps[currentStep] && (
        <div className="fixed inset-0 z-[100]">
          {/* Overlay com destaque */}
          <div className="absolute inset-0 bg-black/50"></div>
          
          {/* Tooltip do passo atual */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    {React.createElement(availableSteps[currentStep].icon, { size: 20, className: "text-white" })}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{availableSteps[currentStep].title}</h3>
                    <p className="text-blue-100 text-xs">Passo {currentStep + 1} de {availableSteps.length}</p>
                  </div>
                </div>
              </div>
              
              {/* Conteúdo */}
              <div className="p-5">
                <p className="text-slate-600 text-sm leading-relaxed">
                  {availableSteps[currentStep].content}
                </p>
              </div>
              
              {/* Progress bar */}
              <div className="px-5 pb-2">
                <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300"
                    style={{ width: `${((currentStep + 1) / availableSteps.length) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Navegação */}
              <div className="p-4 border-t border-slate-100 flex items-center justify-between">
                <button
                  onClick={skipTour}
                  className="text-slate-400 hover:text-slate-600 text-sm font-medium transition-colors"
                >
                  Pular tour
                </button>
                <div className="flex gap-2">
                  {currentStep > 0 && (
                    <button
                      onClick={prevStep}
                      className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      <ChevronLeft size={16} />
                      Anterior
                    </button>
                  )}
                  <button
                    onClick={nextStep}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                  >
                    {currentStep === availableSteps.length - 1 ? 'Concluir' : 'Próximo'}
                    {currentStep < availableSteps.length - 1 && <ChevronRight size={16} />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast de conclusão */}
      {tourCompleted && showTour === false && showWelcome === false && localStorage.getItem('nosly_tour_just_completed') === 'true' && (
        <div className="fixed bottom-4 right-4 z-[100] bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-lg max-w-sm animate-fade-in">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Check size={16} className="text-emerald-600" />
            </div>
            <div>
              <p className="font-medium text-emerald-800">Pronto!</p>
              <p className="text-sm text-emerald-600 mt-1">
                Agora você já sabe por onde começar. O tour está sempre disponível na Ajuda.
              </p>
            </div>
          </div>
        </div>
      )}
    </TourContext.Provider>
  );
}

function useTour() {
  const context = React.useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within TourProvider');
  }
  return context;
}

// Botão de Ajuda que aparece no layout
function HelpButton() {
  // Replaced by AISupportAssistant which is the unified help/support entry.
  return null;
}

// ============================================================
// AI SUPPORT ASSISTANT — Floating contextual support
// ============================================================
function AISupportAssistant() {
  const { startTour } = useTour();
  const { user } = useAuth();
  const { wedding } = useWedding();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', text, suggestions?}
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  // Initial proactive welcome
  useEffect(() => {
    if (open && messages.length === 0) {
      const firstName = user?.name?.split(' ')[0] || '';
      const greet = firstName ? `Olá, ${firstName}! ` : 'Olá! ';
      setMessages([{
        role: 'assistant',
        text: `${greet}Sou o assistente do **liventy**. Posso te orientar passo a passo em qualquer parte do sistema. Como posso ajudar?`,
        suggestions: [
          'Como envio QR Code para confirmados?',
          'Como organizo as mesas?',
          'Por que meu WhatsApp desconectou?',
          'Como faço o check-in no evento?'
        ]
      }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;
    setMessages(prev => [...prev, { role: 'user', text: trimmed }]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/support`, {
        message: trimmed,
        session_id: sessionId,
        context: {
          path: location.pathname,
          wedding_id: wedding?.id || null,
        }
      });
      if (!sessionId) setSessionId(res.data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: res.data.response,
        suggestions: res.data.suggestions || []
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Desculpe, tive um problema momentâneo. Pode tentar novamente?',
        suggestions: []
      }]);
    } finally {
      setLoading(false);
    }
  };

  const newSession = () => {
    if (sessionId) {
      axios.delete(`${API}/ai/support/session/${sessionId}`).catch(() => {});
    }
    setSessionId(null);
    setMessages([]);
  };

  // Inline markdown bold (**text**) renderer
  const renderText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) {
        return <strong key={i} className="font-semibold text-zinc-900">{p.slice(2, -2)}</strong>;
      }
      // Convert single newlines to <br/>
      return p.split('\n').map((line, j) => (
        <React.Fragment key={`${i}-${j}`}>
          {j > 0 && <br />}
          {line}
        </React.Fragment>
      ));
    });
  };

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed right-4 z-[9998] w-12 h-12 text-white rounded-full shadow-[0_12px_28px_-8px_rgba(107,61,246,0.55),0_6px_14px_-4px_rgba(244,114,182,0.35)] flex items-center justify-center transition-all hover:scale-105 active:scale-95 bottom-[calc(max(env(safe-area-inset-bottom),0px)+20px)] lg:bottom-5 group"
        style={{ backgroundImage: 'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)' }}
        title="Assistente liventy"
        data-testid="ai-support-fab"
        aria-label="Abrir assistente"
      >
        <Sparkles size={18} className="transition-transform group-hover:rotate-12" />
        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-white rounded-full ring-2 ring-violet-400" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[10000]"
          style={{ fontFamily: "'Poppins', sans-serif" }}
          data-testid="ai-support-panel"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            style={{ animation: 'fadeIn 200ms ease-out' }}
          />
          {/* Panel — bottom sheet on mobile, right side on desktop */}
          <div
            className="absolute lg:right-4 lg:top-4 lg:bottom-4 lg:w-[420px] lg:rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden inset-x-0 bottom-0 rounded-t-3xl pb-safe"
            style={{ animation: 'slideUp 280ms cubic-bezier(.22,.9,.32,1)', maxHeight: 'calc(100vh - 16px)' }}
          >
            {/* Drag handle (mobile) */}
            <div className="lg:hidden sheet-handle" />

            {/* Header */}
            <div className="px-5 pt-3 lg:pt-5 pb-4 border-b border-zinc-100 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}}>
                  <Sparkles size={16} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-zinc-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.02em' }}>
                    Assistente liventy
                  </p>
                  <p className="text-[11px] text-zinc-500">Sempre online · contextual</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={newSession}
                  className="w-8 h-8 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-500 hover:text-zinc-900 transition-colors"
                  title="Nova conversa"
                  data-testid="ai-support-new-session"
                >
                  <RefreshCw size={14} />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="w-8 h-8 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-500 hover:text-zinc-900 transition-colors"
                  data-testid="ai-support-close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-[#FAFAFA]">
              {messages.map((m, i) => (
                <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                  <div className={`max-w-[85%] ${m.role === 'user' ? 'bg-brand-deep text-white rounded-2xl rounded-br-md' : 'bg-white border border-zinc-200/70 rounded-2xl rounded-bl-md text-zinc-800'} px-3.5 py-2.5 text-[13.5px] leading-[1.55] tracking-[-0.005em]`} data-testid={`ai-msg-${m.role}-${i}`}>
                    {renderText(m.text)}
                    {m.role === 'assistant' && m.suggestions && m.suggestions.length > 0 && (
                      <div className="mt-3 -mb-1 flex flex-col items-start gap-1.5">
                        {m.suggestions.map((s, j) => (
                          <button
                            key={j}
                            onClick={() => send(s)}
                            disabled={loading}
                            className="tap-scale text-[12px] text-left px-2.5 py-1.5 rounded-lg border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 hover:border-zinc-300 text-zinc-700 transition-all disabled:opacity-50"
                            data-testid={`ai-suggestion-${i}-${j}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-zinc-200/70 rounded-2xl rounded-bl-md px-3.5 py-3 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" style={{ animationDelay: '120ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" style={{ animationDelay: '240ms' }} />
                  </div>
                </div>
              )}
            </div>

            {/* Quick actions footer */}
            <div className="px-5 py-2.5 border-t border-zinc-100 bg-white flex-shrink-0">
              <div className="flex items-center gap-1.5 mb-2 overflow-x-auto no-scrollbar">
                <button
                  onClick={() => { setOpen(false); startTour(); }}
                  className="tap-scale flex-shrink-0 text-[11.5px] px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors"
                  data-testid="ai-quick-tour"
                >
                  Ver tour do sistema
                </button>
                <button
                  onClick={() => send('Faça um resumo do meu evento agora')}
                  disabled={loading || !wedding?.id}
                  className="tap-scale flex-shrink-0 text-[11.5px] px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors disabled:opacity-50"
                  data-testid="ai-quick-status"
                >
                  Resumo do evento
                </button>
              </div>
              <div className="flex items-end gap-2">
                <textarea
                  rows={1}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Pergunte qualquer coisa sobre o liventy..."
                  className="flex-1 resize-none px-3.5 py-2.5 rounded-xl border border-zinc-200 bg-zinc-50 text-[13.5px] text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white focus:ring-4 focus:ring-zinc-900/5 transition-all max-h-32"
                  data-testid="ai-support-input"
                  disabled={loading}
                />
                <button
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  className="tap-scale w-10 h-10 rounded-xl bg-brand-deep text-white hover:bg-brand active:scale-95 transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                  data-testid="ai-support-send"
                >
                  {loading ? <Loader2 size={15} className="animate-spin" /> : <ArrowUp size={16} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ==================== FIM TOUR GUIADO ====================

function ProgressBar({ value, max, color = "emerald" }) {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  const colors = {
    emerald: "bg-emerald-500",
    blue: "bg-blue-500",
    orange: "bg-orange-500",
    red: "bg-red-500"
  };
  return (
    <div className="w-full bg-slate-100 rounded-full h-2">
      <div 
        className={`h-2 rounded-full transition-all ${colors[color]}`} 
        style={{ width: `${Math.min(percentage, 100)}%` }}
      ></div>
    </div>
  );
}

// Componente de Input Monetário com máscara
function MoneyInput({ label, value, onChange, disabled = false, className = "" }) {
  // Formatar valor como moeda brasileira
  const formatCurrency = (val) => {
    if (val === null || val === undefined || val === "") return "R$ 0,00";
    const num = typeof val === 'string' ? parseFloat(val.replace(/[^\d]/g, '')) / 100 : val;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Parse valor da string para número
  const parseCurrency = (str) => {
    if (!str) return 0;
    const cleanStr = str.replace(/[^\d]/g, '');
    return parseFloat(cleanStr) / 100 || 0;
  };

  const [displayValue, setDisplayValue] = useState(formatCurrency(value));

  useEffect(() => {
    setDisplayValue(formatCurrency(value));
  }, [value]);

  const handleChange = (e) => {
    const rawValue = e.target.value.replace(/[^\d]/g, '');
    const numValue = parseFloat(rawValue) / 100 || 0;
    setDisplayValue(formatCurrency(numValue));
    onChange(numValue);
  };

  return (
    <div className={className}>
      {label && <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>}
      <input
        type="text"
        value={displayValue}
        onChange={handleChange}
        disabled={disabled}
        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
        placeholder="R$ 0,00"
      />
    </div>
  );
}

// Landing Page - Minimalista Premium


function LandingPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [vis, setVis] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual' — mensal por padrão
  const [gsModal, setGsModal] = useState({ open: false, plan: 'pro_yearly_personal', tier: 'premium', showPlanSelection: false });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const openCheckout = (tier = 'premium', cycle = billingCycle) => {
    const planMap = {
      premium: cycle === 'monthly' ? 'pro_monthly_personal' : 'pro_yearly_personal',
      pro: cycle === 'monthly' ? 'pro_monthly_professional' : 'pro_yearly_professional',
    };
    setGsModal({ open: true, plan: planMap[tier] || planMap.premium, tier, showPlanSelection: false });
  };

  const openSignupWithPlanChoice = () => {
    // CTA "Criar conta" — abre signup e depois deixa o usuário escolher o plano
    setGsModal({ open: true, plan: 'pro_yearly_personal', tier: 'premium', showPlanSelection: true });
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 72;
    const targetY = el.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top: targetY, behavior: 'smooth' });
  };

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) setVis(p => ({ ...p, [e.target.id]: true })); }),
      { threshold: 0.08 }
    );
    document.querySelectorAll('[data-animate]').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const ff = "'Poppins', system-ui, sans-serif";

  const a = (sid, i = 0) => ({
    opacity: vis[sid] ? 1 : 0,
    transform: vis[sid] ? 'translateY(0)' : 'translateY(28px)',
    transition: `all 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 0.08}s`,
  });

  return (
    <div className="min-h-screen" style={{ fontFamily: ff, backgroundColor: '#FAFAFA', color: '#09090B' }}>
      <style>{`
        @keyframes lp-fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes lp-pulse { 0%,100%{opacity:.5;transform:scale(1)} 50%{opacity:1;transform:scale(1.15)} }
        @keyframes lp-shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes lp-grid-fade { 0%,100%{opacity:0.4} 50%{opacity:0.7} }
        .lp-fade { animation: lp-fadeUp 0.9s cubic-bezier(0.16,1,0.3,1) forwards; opacity:0; }
        .lp-d1{animation-delay:.05s} .lp-d2{animation-delay:.15s} .lp-d3{animation-delay:.25s} .lp-d4{animation-delay:.4s} .lp-d5{animation-delay:.55s} .lp-d6{animation-delay:.7s}
        .lp-feat-card { transition: all 0.4s cubic-bezier(0.16,1,0.3,1); }
        .lp-feat-card:hover { transform: translateY(-3px); border-color: #A1A1AA !important; }
        .lp-feat-card:hover .lp-feat-ico { background-color: #18181B !important; color: white !important; }
        .lp-cta-prim { transition: all 0.25s cubic-bezier(0.16,1,0.3,1); }
        .lp-cta-prim:hover { transform: translateY(-1px); box-shadow: 0 8px 24px -8px rgba(0,0,0,0.25); }
        .lp-cta-prim:active { transform: translateY(0) scale(0.98); }
        .lp-cta-sec { transition: all 0.25s cubic-bezier(0.16,1,0.3,1); }
        .lp-cta-sec:hover { background-color: #F4F4F5; border-color: #A1A1AA; }
        .lp-step { transition: all 0.4s cubic-bezier(0.16,1,0.3,1); }
        .lp-step:hover { transform: translateY(-3px); border-color: rgba(255,255,255,0.14) !important; background-color: rgba(255,255,255,0.045) !important; }
        .lp-flow-arrow { color: #D4D4D8; }
        .lp-noise::after { content:''; position:absolute; inset:0; pointer-events:none; opacity:0.025; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E"); }
        .lp-bg-grid { background-image: linear-gradient(rgba(24,24,27,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(24,24,27,0.04) 1px, transparent 1px); background-size: 64px 64px; }
        .lp-mask-fade { mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%); -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%); }
        .lp-pricing-card { transition: all 0.4s cubic-bezier(0.16,1,0.3,1); }
        .lp-pricing-card:hover { transform: translateY(-4px); }
        .lp-no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
        .lp-no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ═══ HEADER ═══ */}
      <header className="fixed top-0 left-0 right-0 z-50" style={{backgroundColor:'rgba(250,250,250,0.8)', backdropFilter:'blur(20px) saturate(180%)', borderBottom:'1px solid rgba(0,0,0,0.06)'}}>
        <div className="max-w-7xl mx-auto px-6 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <LiventyLogoMark size={42} />
            <LiventyWordmark height={28} variant="dark" />
          </div>
          <nav className="hidden md:flex items-center gap-9">
            {[{l:'Funcionalidades',id:'funcionalidades'},{l:'Como funciona',id:'como-funciona'},{l:'Planos',id:'planos'}].map(n=>(
              <a key={n.id} href={'#'+n.id} onClick={e=>{e.preventDefault();scrollTo(n.id)}} className="relative text-[13.5px] font-medium text-zinc-600 hover:text-zinc-900 transition-all duration-200 after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 hover:after:w-full after:transition-all after:duration-300 after:rounded-full" style={{fontFamily:"'Poppins', sans-serif"}} data-testid={'nav-'+n.id}>{n.l}</a>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <button onClick={()=>navigate('/login')} className="px-4 py-2 text-[13.5px] font-medium text-zinc-700 hover:text-zinc-900 transition-colors" style={{fontFamily:"'Poppins', sans-serif"}} data-testid="nav-login-btn">Entrar</button>
            <button onClick={()=>navigate('/get-started')} className="lp-cta-prim relative px-5 py-2.5 text-[13.5px] font-semibold text-white rounded-full transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]" style={{fontFamily:"'Poppins', sans-serif", backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)', boxShadow:'0 8px 24px -8px rgba(107,61,246,0.50), 0 0 0 0 rgba(107,61,246,0)'}} onMouseEnter={(e)=>{e.currentTarget.style.boxShadow='0 12px 32px -8px rgba(107,61,246,0.70), 0 0 24px rgba(255,77,141,0.35)'}} onMouseLeave={(e)=>{e.currentTarget.style.boxShadow='0 8px 24px -8px rgba(107,61,246,0.50), 0 0 0 0 rgba(107,61,246,0)'}} data-testid="nav-signup-btn">Criar conta</button>
          </div>
          {/* Mobile hamburger */}
          <button
            onClick={()=>setMobileMenuOpen(true)}
            className="md:hidden w-10 h-10 rounded-full bg-white border border-zinc-200 flex items-center justify-center text-zinc-900 active:scale-95 transition-transform"
            aria-label="Abrir menu"
            data-testid="lp-mobile-menu-btn"
          >
            <Menu size={18} strokeWidth={2} />
          </button>
        </div>
      </header>

      {/* ═══ MOBILE DRAWER ═══ */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[10001]" data-testid="lp-mobile-drawer">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={()=>setMobileMenuOpen(false)}
            style={{ animation: 'fadeIn 200ms ease-out' }}
          />
          <div
            className="absolute top-0 right-0 bottom-0 w-[84%] max-w-sm bg-white shadow-2xl flex flex-col pt-safe"
            style={{ animation: 'lpSlideInRight 280ms cubic-bezier(.22,.9,.32,1)' }}
          >
            <div className="px-5 h-16 flex items-center justify-between border-b border-zinc-100">
              <div className="flex items-center gap-2.5">
                <LiventyLogoMark size={32} />
                <span className="text-lg font-bold tracking-tight text-zinc-900" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.04em' }}>liventy</span>
              </div>
              <button
                onClick={()=>setMobileMenuOpen(false)}
                className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 active:scale-95 transition-transform"
                aria-label="Fechar menu"
                data-testid="lp-mobile-close-btn"
              >
                <X size={16} />
              </button>
            </div>
            <nav className="flex-1 px-3 py-5 overflow-y-auto">
              {[
                {l:'Funcionalidades',id:'funcionalidades'},
                {l:'Como funciona',id:'como-funciona'},
                {l:'Planos',id:'planos'},
              ].map(n=>(
                <button
                  key={n.id}
                  onClick={()=>{ setMobileMenuOpen(false); setTimeout(()=>scrollTo(n.id), 280); }}
                  className="w-full flex items-center justify-between px-3 py-4 rounded-xl hover:bg-zinc-50 active:bg-zinc-100 transition-colors text-left"
                  data-testid={`lp-mobile-nav-${n.id}`}
                >
                  <span className="text-[15px] font-medium text-zinc-900">{n.l}</span>
                  <ChevronRight size={16} className="text-zinc-400" />
                </button>
              ))}
              <div className="h-px bg-zinc-100 my-3" />
              <button
                onClick={()=>{ setMobileMenuOpen(false); navigate('/login'); }}
                className="w-full flex items-center justify-between px-3 py-4 rounded-xl hover:bg-zinc-50 active:bg-zinc-100 transition-colors text-left"
                data-testid="lp-mobile-login-btn"
              >
                <span className="text-[15px] font-medium text-zinc-900">Entrar</span>
                <ChevronRight size={16} className="text-zinc-400" />
              </button>
            </nav>
            <div className="px-5 pb-safe pt-3 border-t border-zinc-100 bg-zinc-50/50">
              <button
                onClick={()=>{ setMobileMenuOpen(false); navigate('/get-started'); }}
                className="w-full py-3.5 text-[14px] font-medium text-white rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-brand"
                style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}}
                data-testid="lp-mobile-signup-btn"
              >
                Criar conta grátis
                <ArrowRight size={15} />
              </button>
              <p className="text-[11px] text-zinc-400 text-center mt-3 mb-1">7 dias grátis · Cancele quando quiser</p>
            </div>
          </div>
          <style>{`
            @keyframes lpSlideInRight { from { transform: translateX(100%) } to { transform: translateX(0) } }
            @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
          `}</style>
        </div>
      )}

      {/* ═══ HERO (Liventy premium — centralizada, compacta) ═══ */}
      <section className="relative pt-20 pb-12 sm:pt-24 sm:pb-20" data-testid="hero-section" style={{backgroundColor:'#F8F7FF'}}>
        {/* Glow gradients sutis — estilo Stripe/Linear */}
        <div className="absolute pointer-events-none overflow-hidden inset-0">
          <div className="absolute" style={{top:'10%', left:'-8%', width:'min(440px,50vw)', height:'min(440px,50vw)', borderRadius:'50%', background:'radial-gradient(50% 50% at 50% 50%, rgba(255,77,141,0.18), transparent 70%)', filter:'blur(60px)'}} />
          <div className="absolute" style={{top:'35%', left:'-12%', width:'min(320px,38vw)', height:'min(320px,38vw)', borderRadius:'50%', background:'radial-gradient(50% 50% at 50% 50%, rgba(107,61,246,0.16), transparent 70%)', filter:'blur(70px)'}} />
          <div className="absolute" style={{top:'14%', right:'-10%', width:'min(480px,56vw)', height:'min(480px,56vw)', borderRadius:'50%', background:'radial-gradient(50% 50% at 50% 50%, rgba(255,179,71,0.16), transparent 70%)', filter:'blur(70px)'}} />
          <div className="absolute" style={{top:'48%', right:'-8%', width:'min(360px,42vw)', height:'min(360px,42vw)', borderRadius:'50%', background:'radial-gradient(50% 50% at 50% 50%, rgba(107,61,246,0.12), transparent 70%)', filter:'blur(75px)'}} />
        </div>

        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center mx-auto">
            {/* Wordmark Liventy — imagem oficial da marca (gradient) */}
            <div className="mx-auto inline-block lp-fade lp-d1 select-none" data-testid="hero-wordmark">
              <img
                src="/brand/liventy-wordmark-gradient.webp"
                alt="liventy"
                draggable={false}
                style={{
                  display: 'block',
                  width: 'auto',
                  height: 'clamp(80px, 12vw, 170px)',
                  objectFit: 'contain',
                  margin: '0 auto',
                  filter: 'drop-shadow(0 28px 56px rgba(107,61,246,0.20))',
                }}
              />
            </div>

            {/* Título do hero */}
            <h2 className="mt-5 sm:mt-6 mx-auto max-w-3xl lp-fade lp-d2 text-center" style={{fontFamily:"'Open Sauce One', sans-serif", fontWeight:700, letterSpacing:'-0.025em', lineHeight:1.08, fontSize:'clamp(30px, 5vw, 56px)', color:'#141023'}}>
              Crie, gerencie e realize.
            </h2>

            {/* Texto institucional — Geist, max-w menor para leitura */}
            <p className="mt-5 sm:mt-6 mx-auto max-w-2xl text-[15px] sm:text-[17px] leading-[1.6] lp-fade lp-d3" style={{fontFamily:"'Poppins', sans-serif", fontWeight:400, color:'#52525B'}}>
              Chega de planilhas, grupos e anotações espalhadas. Organize todo o seu evento em um único lugar.
            </p>

            {/* CTAs — primário chamativo, secundário minimalista */}
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 lp-fade lp-d4">
              <button
                onClick={()=>navigate('/get-started')}
                className="group relative inline-flex items-center justify-center gap-2.5 px-8 py-4 text-[15px] font-semibold text-white rounded-full transition-all duration-300 active:scale-[0.98] hover:scale-[1.03]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  backgroundImage: 'linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)',
                  boxShadow: '0 18px 44px -12px rgba(107,61,246,0.55), 0 8px 20px -6px rgba(255,77,141,0.40), inset 0 1px 0 rgba(255,255,255,0.25)',
                }}
                onMouseEnter={(e)=>{e.currentTarget.style.boxShadow='0 24px 52px -10px rgba(107,61,246,0.70), 0 12px 28px -6px rgba(255,77,141,0.50), 0 0 30px rgba(255,179,71,0.30), inset 0 1px 0 rgba(255,255,255,0.3)'}}
                onMouseLeave={(e)=>{e.currentTarget.style.boxShadow='0 18px 44px -12px rgba(107,61,246,0.55), 0 8px 20px -6px rgba(255,77,141,0.40), inset 0 1px 0 rgba(255,255,255,0.25)'}}
                data-testid="hero-cta-primary"
              >
                Criar meu evento
                <ArrowRight size={18} strokeWidth={2.4} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </button>
              <button
                onClick={()=>scrollTo('como-funciona')}
                className="inline-flex items-center justify-center gap-2 px-6 py-4 text-[14.5px] font-medium rounded-full text-zinc-700 hover:text-zinc-900 transition-all duration-200 hover:bg-zinc-100/60"
                style={{fontFamily: "'Poppins', sans-serif"}}
                data-testid="hero-cta-secondary"
              >
                <Play size={13} className="text-zinc-500" strokeWidth={2} />
                Ver como funciona
              </button>
            </div>

            {/* Social proof elegante — sem números inventados */}
            <div className="mt-7 sm:mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 lp-fade lp-d5" data-testid="hero-social-proof">
              <span className="inline-flex items-center gap-1.5 text-[12.5px] text-zinc-500" style={{fontFamily:"'Poppins', sans-serif", fontWeight:500}}>
                <span className="inline-flex items-center gap-0.5 text-amber-500">
                  {[0,1,2,3,4].map(i => <Star key={i} size={13} fill="currentColor" strokeWidth={0} />)}
                </span>
                Experiência premium
              </span>
              <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-zinc-300" />
              <span className="inline-flex items-center gap-1.5 text-[12.5px] text-zinc-500" style={{fontFamily:"'Poppins', sans-serif", fontWeight:500}}>
                <Shield size={13} className="text-zinc-400" />
                Pagamento seguro Stripe
              </span>
              <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-zinc-300" />
              <span className="inline-flex items-center gap-1.5 text-[12.5px] text-zinc-500" style={{fontFamily:"'Poppins', sans-serif", fontWeight:500}}>
                <Sparkles size={13} className="text-zinc-400" />
                7 dias grátis
              </span>
            </div>

            {/* Jornada — Da ideia à realização (substitui os 4 atalhos) */}
            <div className="mt-14 sm:mt-20 max-w-5xl mx-auto lp-fade lp-d6">
              <h2 className="text-center text-[22px] sm:text-[30px] font-bold tracking-tight" style={{fontFamily:"'Open Sauce One', sans-serif", color:'#141023', letterSpacing:'-0.02em'}}>
                Da ideia à realização,{" "}
                <span style={{backgroundImage:'linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)', WebkitBackgroundClip:'text', backgroundClip:'text', color:'transparent'}}>a gente te acompanha!</span>
              </h2>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-9">
                {[
                  { ic: Sparkles,    t: 'Planeje do seu jeito',   d: 'Crie seu evento em minutos e comece sem complicação.' },
                  { ic: ListChecks,  t: 'Organize cada detalhe',  d: 'Tarefas, fornecedores e orçamento no mesmo lugar.' },
                  { ic: Send,        t: 'Convide e encante',      d: 'Convites em massa, organização de mesas e check-in com QR Code.' },
                  { ic: TrendingUp,  t: 'Acompanhe tudo',         d: 'Visão completa do progresso e do orçamento.' },
                  { ic: Users,       t: 'Colabore com a equipe',  d: 'Trabalhe junto com sua equipe e fornecedores.' },
                  { ic: PartyPopper, t: 'Realize sem estresse',   d: 'Chegou o grande dia! É só aproveitar.' },
                ].map((it, i) => (
                  <div key={i} className="flex flex-col items-center text-center px-1" data-testid={`journey-step-${i}`}>
                    <span
                      className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3.5"
                      style={{
                        backgroundImage:'linear-gradient(135deg, rgba(107,61,246,0.08), rgba(255,77,141,0.08))',
                        border:'1px solid #EEE8FF',
                      }}
                    >
                      <it.ic size={22} strokeWidth={1.9} className="text-zinc-700" />
                    </span>
                    <span className="text-[13.5px] font-bold tracking-tight leading-tight" style={{fontFamily:"'Poppins', sans-serif", color:'#141023'}}>{it.t}</span>
                    <span className="text-[11.5px] mt-1 leading-snug" style={{fontFamily:"'Poppins', sans-serif", fontWeight:400, color:'#6B7280'}}>{it.d}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Seja qual for o seu evento — 6 tipos com imagens */}
            <div className="mt-20 sm:mt-28 max-w-6xl mx-auto lp-fade lp-d6" data-testid="event-types-section">
              <h2 className="text-center text-[22px] sm:text-[30px] font-bold tracking-tight" style={{fontFamily:"'Open Sauce One', sans-serif", color:'#141023', letterSpacing:'-0.02em'}}>
                Seja qual for o seu evento,{" "}
                <span style={{backgroundImage:'linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)', WebkitBackgroundClip:'text', backgroundClip:'text', color:'transparent'}}>a liventy é pra você</span>
              </h2>
              <div className="mt-10 liventy-marquee-mask overflow-hidden" data-testid="event-types-carousel">
                <div className="liventy-marquee-track">
                  {(() => {
                    const items = [
                      { img: '/events/aniversarios.webp', t: 'Aniversários', ic: Cake },
                      { img: '/events/confraternizacoes.webp', t: 'Confraternizações', ic: Flame },
                      { img: '/events/festas.webp', t: 'Festas', ic: PartyPopper },
                      { img: '/events/casamentos.webp', t: 'Casamentos', ic: Gem },
                      { img: '/events/formaturas.webp', t: 'Formaturas', ic: GraduationCap },
                      { img: '/events/corporativos.webp', t: 'Eventos Corporativos', ic: Briefcase },
                      { img: '/events/escolares.webp', t: 'Eventos Escolares', ic: School },
                      { img: '/events/chabebe.webp', t: 'Chá de bebê', ic: Baby },
                      { more: true, t: 'E muito mais', ic: Sparkles },
                    ];
                    return [...items, ...items].map((it, i) => (
                      it.more ? (
                      <div
                        key={i}
                        className="relative rounded-2xl overflow-hidden w-40 sm:w-44 aspect-[3/4] shrink-0 mr-4 flex flex-col items-center justify-center text-center px-3"
                        style={{backgroundImage:'linear-gradient(150deg, #6B3DF6 0%, #FF4D8D 60%, #FFB347 100%)', boxShadow:'0 10px 24px -14px rgba(20,16,35,0.35)'}}
                        data-testid={`event-type-${i}`}
                        aria-hidden={i >= items.length ? 'true' : undefined}
                      >
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl mb-2.5" style={{backgroundColor:'rgba(255,255,255,0.18)', border:'1px solid rgba(255,255,255,0.3)'}}>
                          <it.ic size={17} strokeWidth={2.1} color="#ffffff" />
                        </span>
                        <span className="text-[13px] font-bold tracking-tight text-white leading-tight" style={{fontFamily:"'Poppins', sans-serif"}}>{it.t}</span>
                      </div>
                      ) : (
                      <div
                        key={i}
                        className="group relative rounded-2xl overflow-hidden w-40 sm:w-44 aspect-[3/4] shrink-0 mr-4"
                        style={{boxShadow:'0 10px 24px -14px rgba(20,16,35,0.35)'}}
                        data-testid={`event-type-${i}`}
                        aria-hidden={i >= items.length ? 'true' : undefined}
                      >
                        <img
                          src={it.img}
                          alt={it.t}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.08]"
                        />
                        <div className="absolute inset-0" style={{background:'linear-gradient(to top, rgba(15,11,28,0.85) 0%, rgba(15,11,28,0.25) 50%, rgba(15,11,28,0.04) 100%)'}} />
                        <div className="absolute bottom-0 left-0 right-0 p-2.5 flex flex-col items-start gap-1">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg" style={{backgroundColor:'rgba(255,255,255,0.16)', backdropFilter:'blur(8px)', border:'1px solid rgba(255,255,255,0.25)'}}>
                            <it.ic size={13} strokeWidth={2.1} color="#ffffff" />
                          </span>
                          <span className="text-[11.5px] font-bold tracking-tight text-white leading-tight" style={{fontFamily:"'Poppins', sans-serif"}}>{it.t}</span>
                        </div>
                      </div>
                      )
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Curva SVG inferior — transição suave do lilás para o branco da próxima seção */}
        <svg
          aria-hidden="true"
          className="absolute -bottom-px left-0 right-0 w-full block pointer-events-none"
          viewBox="0 0 1440 110"
          preserveAspectRatio="none"
          style={{ height: 'clamp(60px, 8vw, 110px)' }}
        >
          <path d="M0,0 C360,110 1080,110 1440,0 L1440,110 L0,110 Z" fill="#FFFFFF" />
        </svg>
      </section>

      {/* ═══ VEJA EM AÇÃO — Showcase com tabs interativas ═══ */}
      <ShowcaseInAction />

      {/* ═══ STATS / TRUST STRIP — elegante, sem números inventados ═══ */}
      <section className="py-12 border-y" style={{borderColor:'#F4F4F5', backgroundColor:'white'}}>
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-center text-[11px] font-medium uppercase tracking-[0.2em] text-zinc-400 mb-7" style={{fontFamily:"'Poppins', sans-serif"}}>Construído para eventos que importam</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6 max-w-5xl mx-auto">
            {[
              {ic: Shield,      l:'Pagamento Stripe',       s:'Seguro & criptografado'},
              {ic: MessageCircle, l:'WhatsApp integrado',  s:'Convites e RSVP em massa'},
              {ic: QrCode,      l:'Check-in em <5s',        s:'QR Code individual'},
              {ic: Sparkles,    l:'Experiência premium',    s:'Funcionalidades pensadas em cada detalhe'},
            ].map((s,i)=>(
              <div key={i} className="flex items-center gap-3 justify-self-center lg:justify-self-auto" data-testid={`trust-${i}`}>
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl" style={{backgroundImage:'linear-gradient(135deg, rgba(107,61,246,0.08), rgba(255,77,141,0.08))', border:'1px solid #EEE8FF'}}>
                  <s.ic size={17} strokeWidth={1.9} className="text-zinc-700" />
                </span>
                <div className="flex flex-col leading-tight">
                  <span className="text-[13.5px] font-semibold text-zinc-900" style={{fontFamily:"'Poppins', sans-serif"}}>{s.l}</span>
                  <span className="text-[11.5px] text-zinc-500" style={{fontFamily:"'Poppins', sans-serif"}}>{s.s}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FUNCIONALIDADES ═══ */}
      <section id="funcionalidades" data-animate className="py-24 sm:py-32" style={{backgroundColor:'#FAFAFA'}}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mb-16" style={a('funcionalidades',0)}>
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-500 mb-3">Funcionalidades</p>
            <h2 className="text-[34px] sm:text-[44px] font-semibold tracking-[-0.03em] leading-[1.05] mb-4 text-zinc-900" style={{fontFamily:"'Poppins', sans-serif"}}>Controle total.<br/>Zero complexidade.</h2>
            <p className="text-[15px] text-zinc-500 leading-relaxed max-w-lg">Ferramentas pensadas para tornar a gestão do seu evento simples, rápida e eficiente.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              {t:'Confirmação inteligente', d:'Link único por convidado. Confirmação em segundos.', ic:Check},
              {t:'WhatsApp integrado', d:'Conecte seu próprio número e envie em massa.', ic:MessageCircle},
              {t:'QR Code individual', d:'Cada convidado recebe seu acesso único.', ic:QrCode},
              {t:'Check-in rápido', d:'Câmera aponta, entrada liberada. <5s.', ic:UserCheck},
              {t:'Gestão de mesas', d:'Organize com drag-and-drop. Setores, mesas, lugares.', ic:LayoutGrid},
              {t:'Status em tempo real', d:'Acompanhe confirmações conforme acontecem.', ic:TrendingUp},
              {t:'Convites personalizados', d:'Mensagem padrão liventy ou customize a sua.', ic:Send},
              {t:'Painel premium', d:'Métricas e progresso a um olhar de distância.', ic:Home},
            ].map((f,i)=>(
              <div key={i} className="lp-feat-card rounded-2xl p-6 border bg-white" style={{borderColor:'#E4E4E7', ...a('funcionalidades', i+1)}}>
                <div className="lp-feat-ico w-9 h-9 rounded-lg flex items-center justify-center mb-12" style={{backgroundColor:'#F4F4F5', color:'#52525B', transition:'all 0.3s'}}>
                  <f.ic size={16} strokeWidth={2} />
                </div>
                <h3 className="text-[14px] font-semibold mb-1.5 text-zinc-900">{f.t}</h3>
                <p className="text-[13px] text-zinc-500 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ COMO FUNCIONA — 5 STEPS ═══ */}
      <section id="como-funciona" data-animate className="py-24 sm:py-32 relative overflow-hidden" style={{backgroundColor:'#09090B'}}>
        <div className="absolute inset-0 opacity-[0.04]" style={{backgroundImage:'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize:'48px 48px'}} />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[min(700px,90vw)] h-[400px] max-w-full rounded-full pointer-events-none" style={{background:'radial-gradient(50% 50% at 50% 50%, rgba(107,61,246,0.18), transparent 70%)'}} />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-14" style={a('como-funciona',0)}>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{fontFamily:"'Nunito', sans-serif", color:'#FF4D8D'}}>Como funciona</p>
            <h2 className="text-[34px] sm:text-[44px] tracking-[-0.025em] leading-[1.05] mb-4 text-white" style={{fontFamily:"'Inter', sans-serif", fontWeight:700}}>Crie, gerencie e realize.<br/>Da criação do evento ao primeiro check-in.</h2>
            <p className="text-[15px] leading-relaxed max-w-xl mx-auto" style={{fontFamily:"'Inter', sans-serif", fontWeight:400, color:'#A1A1AA'}}>Setup simples. Finanças claras. Convidados sincronizados. Check-in instantâneo.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            {[
              {n:'01', t:'Crie seu evento',       d:'Configure seu evento em minutos com uma experiência simples e intuitiva.', ic:CalendarDays, accent:'#3B82F6'},
              {n:'02', t:'Gestão financeira',     d:'Controle despesas, orçamentos e fluxo de caixa.',   ic:DollarSign,   accent:'#22C55E'},
              {n:'03', t:'Gestão de convidados',  d:'Envie convites e confirmações em massa via WhatsApp com links inteligentes.', ic:Users,       accent:'#A855F7'},
              {n:'04', t:'Check-in do evento',    d:'Faça check-in rápido com QR Code individual e acompanhe chegadas em tempo real.', ic:QrCode, accent:'#F59E0B'},
            ].map((s,i)=>(
              <div
                key={i}
                className="lp-step group relative rounded-2xl p-6 transition-all duration-500 ease-out"
                style={{
                  backgroundColor:'rgba(255,255,255,0.03)',
                  border:'1px solid rgba(255,255,255,0.07)',
                  backdropFilter:'blur(20px)',
                  ...a('como-funciona', i+1)
                }}
                data-testid={`step-${i+1}`}
              >
                {/* Glow on hover */}
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{background:`radial-gradient(80% 80% at 50% 0%, ${s.accent}20, transparent 65%)`}}
                />
                <div className="relative flex items-center justify-between mb-7">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-500"
                    style={{
                      backgroundColor:`${s.accent}14`,
                      border:`1px solid ${s.accent}28`,
                    }}
                  >
                    <s.ic size={18} style={{color:s.accent}} strokeWidth={1.8} />
                  </div>
                  <span className="text-[11px] font-mono tracking-wider" style={{color:'#3F3F46'}}>{s.n}</span>
                </div>
                <h3 className="relative text-[16px] font-semibold text-white mb-2 tracking-tight leading-tight">{s.t}</h3>
                <p className="relative text-[12.5px] leading-[1.6]" style={{color:'#71717A'}}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FLUXO RSVP VISUAL ═══ */}
      <section id="fluxo-rsvp" data-animate className="py-24 sm:py-32" style={{backgroundColor:'white'}}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16" style={a('fluxo-rsvp',0)}>
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-500 mb-3">Jornada de confirmação liventy</p>
            <h2 className="text-[34px] sm:text-[44px] font-semibold tracking-[-0.03em] leading-[1.05] mb-4 text-zinc-900">Tudo conectado.<br/>Do convite ao check-in.</h2>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-1">
            {[
              {ic:MessageCircle, label:'WhatsApp', sub:'Convite enviado', color:'#22C55E'},
              {ic:Link2, label:'Link único', sub:'/confirmar/abc123', color:'#3B82F6'},
              {ic:Check, label:'Confirmação', sub:'Status atualizado', color:'#10B981'},
              {ic:QrCode, label:'QR Code', sub:'Acesso individual', color:'#8B5CF6'},
              {ic:UserCheck, label:'Check-in', sub:'Entrada liberada', color:'#F59E0B'},
            ].map((f, i, arr) => (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center text-center" style={{...a('fluxo-rsvp', i+1)}}>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 border" style={{backgroundColor:'white', borderColor:'#E4E4E7', boxShadow:'0 4px 14px -4px rgba(0,0,0,0.08)'}}>
                    <f.ic size={20} style={{color:f.color}} strokeWidth={2} />
                  </div>
                  <p className="text-[13px] font-semibold text-zinc-900">{f.label}</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">{f.sub}</p>
                </div>
                {i < arr.length - 1 && (
                  <div className="lp-flow-arrow hidden md:flex items-center px-2" style={a('fluxo-rsvp', i+1)}>
                    <ArrowRight size={16} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ MOCKUP SHOWCASE — iPhone (RSVP) + Browser (Convidados) ═══ */}
      <section id="mockups" data-animate className="py-24 sm:py-32 relative overflow-hidden" style={{backgroundColor:'#FAFAFA'}}>
        <div className="absolute inset-0 lp-bg-grid lp-mask-fade opacity-50" />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="max-w-2xl mb-16" style={a('mockups',0)}>
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-500 mb-3">A experiência</p>
            <h2 className="text-[34px] sm:text-[44px] font-semibold tracking-[-0.03em] leading-[1.05] mb-4 text-zinc-900">Premium para você.<br/>Premium para seus convidados.</h2>
            <p className="text-[15px] text-zinc-500 leading-relaxed max-w-lg">Cada interface foi desenhada com atenção a cada detalhe — porque seu evento merece.</p>
          </div>
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-3" style={a('mockups',1)}>
              <BrowserFrame compact>
                <ConvidadosMockup />
              </BrowserFrame>
              <p className="text-[13px] text-zinc-500 mt-4 max-w-md">
                <span className="font-semibold text-zinc-900">Painel de Convidados</span> · Selecione, dispare em massa, acompanhe status em tempo real.
              </p>
            </div>
            <div className="lg:col-span-2 flex justify-center" style={a('mockups',2)}>
              <PhoneFrame>
                <RsvpMockup />
              </PhoneFrame>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PLANOS — Premium Pricing ═══ */}
      <section id="planos" data-animate className="py-24 sm:py-32" style={{backgroundColor:'white'}}>
        <div className="max-w-6xl mx-auto px-6">
          {/* Trial banner */}
          <div className="text-center mb-8" style={a('planos',0)}>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-deep text-white" data-testid="trial-banner">
              <Sparkles size={13} className="text-amber-300" />
              <span className="text-[12px] font-medium tracking-tight">7 dias grátis para testar tudo</span>
            </div>
          </div>

          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-10" style={a('planos',1)}>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{fontFamily:"'Nunito', sans-serif", color:'#6B3DF6'}}>Planos</p>
            <h2 className="text-[34px] sm:text-[44px] font-semibold tracking-[-0.03em] leading-[1.05] mb-4 text-zinc-900">De eventos íntimos<br/>a produções em escala.</h2>
            <p className="text-[14px] max-w-xl mx-auto" style={{fontFamily:"'Poppins', sans-serif", fontWeight:400, color:'#6B7280'}}>Seu evento. Sua necessidade. Qualidade em qualquer dimensão.</p>
          </div>

          {/* Toggle Mensal / Anual */}
          <div className="flex justify-center mb-10" style={a('planos',2)}>
            <div className="relative inline-flex items-center rounded-full p-1 bg-zinc-100" data-testid="billing-toggle">
              <span
                aria-hidden
                className="absolute top-1 bottom-1 rounded-full bg-white shadow-sm transition-all duration-300 ease-out"
                style={{
                  width: 'calc(50% - 4px)',
                  left: billingCycle === 'monthly' ? '4px' : 'calc(50% + 0px)'
                }}
              />
              <button
                type="button"
                onClick={() => setBillingCycle('monthly')}
                data-testid="billing-monthly"
                className={`relative z-10 px-5 py-2 text-[12.5px] font-medium tracking-tight rounded-full transition-colors ${billingCycle === 'monthly' ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
              >
                Mensal
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle('annual')}
                data-testid="billing-annual"
                className={`relative z-10 px-5 py-2 text-[12.5px] font-medium tracking-tight rounded-full transition-colors flex items-center gap-2 ${billingCycle === 'annual' ? 'text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
              >
                Anual
                <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-semibold tracking-tight ${billingCycle === 'annual' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-200 text-zinc-600'}`}>−10%</span>
              </button>
            </div>
          </div>

          {/* Cards */}
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {/* Essencial */}
            <div className="lp-pricing-card rounded-2xl p-7 border flex flex-col bg-white" style={{borderColor:'#E4E4E7', ...a('planos',3)}} data-testid="plan-card-essencial">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={14} className="text-zinc-500" />
                <p className="text-[12px] font-semibold uppercase tracking-wider text-zinc-700">Essencial</p>
              </div>
              <p className="text-[12.5px] text-zinc-500 mb-3">Para organizar o seu evento</p>
              <div className="inline-flex w-fit items-center gap-1.5 px-2.5 py-1 rounded-full text-zinc-900 font-bold text-[10px] uppercase tracking-wider mb-4" style={{backgroundImage:'linear-gradient(135deg, #FFD166, #FF8A5B)'}} data-testid="launch-badge-essencial">
                🏷️ Preço especial de lançamento
              </div>

              <div className="mb-1 transition-all duration-300">
                {billingCycle === 'monthly' ? (
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-4xl font-semibold tracking-tight text-zinc-900">R$ 39,90</span>
                    <span className="text-[13px] text-zinc-400">/mês</span>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-4xl font-semibold tracking-tight text-zinc-900">R$ 430,92</span>
                      <span className="text-[13px] text-zinc-400">/ano</span>
                    </div>
                    <p className="text-[11.5px] text-emerald-700 mt-1 font-medium">≈ R$ 35,91/mês · economize R$ 47,88/ano</p>
                  </div>
                )}
              </div>
              <p className="text-[11.5px] text-zinc-400 mb-6">Sem fidelidade · cancele quando quiser</p>

              <ul className="space-y-2.5 mb-7 flex-1">
                {[
                  '1 evento ativo por vez',
                  'Gestão de convidados',
                  'Disparos via WhatsApp',
                  'Controle financeiro básico',
                  'Fornecedores',
                  'Tarefas e agenda',
                  'Relatórios',
                  'Suporte por e-mail'
                ].map((it,i)=>(
                  <li key={i} className="flex items-start gap-2.5 text-[13px] text-zinc-700">
                    <Check size={14} className="text-zinc-900 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={()=>navigate('/get-started')}
                className="lp-cta-sec w-full py-3 text-[13px] font-medium rounded-xl border text-zinc-900 hover:bg-zinc-50 transition-colors"
                style={{borderColor:'#E4E4E7', backgroundColor:'white'}}
                data-testid="pricing-essencial-btn"
              >
                Começar grátis por 7 dias
              </button>
            </div>

            {/* Plus */}
            <div className="lp-pricing-card rounded-2xl p-7 flex flex-col relative overflow-hidden" style={{backgroundColor:'#09090B', color:'white', boxShadow:'0 10px 30px -10px rgba(0,0,0,0.25)', ...a('planos',4)}} data-testid="plan-card-plus">
              <div className="absolute top-4 right-4 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full bg-amber-300 text-zinc-900" data-testid="badge-popular">⭐ Mais popular</div>
              <div className="flex items-center gap-2 mb-1">
                <Briefcase size={14} style={{color:'#A1A1AA'}} />
                <p className="text-[12px] font-semibold uppercase tracking-wider" style={{color:'#A1A1AA'}}>Plus</p>
              </div>
              <p className="text-[12.5px] mb-3" style={{color:'#A1A1AA'}}>Tudo liberado · múltiplos eventos</p>
              <div className="inline-flex w-fit items-center gap-1.5 px-2.5 py-1 rounded-full text-zinc-900 font-bold text-[10px] uppercase tracking-wider mb-4" style={{backgroundImage:'linear-gradient(135deg, #FFD166, #FF8A5B)'}} data-testid="launch-badge-plus">
                🏷️ Preço especial de lançamento
              </div>

              <div className="mb-1 transition-all duration-300">
                {billingCycle === 'monthly' ? (
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-4xl font-semibold tracking-tight">R$ 99,90</span>
                    <span className="text-[13px]" style={{color:'#71717A'}}>/mês</span>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-4xl font-semibold tracking-tight">R$ 1.078,92</span>
                      <span className="text-[13px]" style={{color:'#71717A'}}>/ano</span>
                    </div>
                    <p className="text-[11.5px] text-emerald-300 mt-1 font-medium">≈ R$ 89,91/mês · economize R$ 119,88/ano</p>
                  </div>
                )}
              </div>
              <p className="text-[11.5px] mb-6" style={{color:'#71717A'}}>Sem fidelidade · cancele quando quiser</p>

              <ul className="space-y-2.5 mb-7 flex-1">
                {[
                  'Até 12 eventos simultâneos',
                  'Todas as funcionalidades da plataforma',
                  'Check-in com QR Code',
                  'Mesas & Assentos + Mapa do evento',
                  'Inspirações (galeria colaborativa)',
                  'Receitas e Despesas (financeiro completo)',
                  'Disparos WhatsApp + Assistente IA',
                  'Gestão completa de eventos',
                  'Equipe e colaboradores',
                  'Suporte prioritário'
                ].map((it,i)=>(
                  <li key={i} className="flex items-start gap-2.5 text-[13px]" style={{color:'#E4E4E7'}}>
                    <Check size={14} className="flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={()=>navigate('/get-started')}
                className="lp-cta-prim w-full py-3 text-[13px] font-semibold rounded-xl text-zinc-900 hover:bg-zinc-100 transition-colors"
                style={{backgroundColor:'white'}}
                data-testid="pricing-plus-btn"
              >
                Começar grátis por 7 dias
              </button>
            </div>
          </div>

          {/* Card disclaimer */}
          <p className="text-center text-[12px] text-zinc-500 mt-6 max-w-lg mx-auto" data-testid="card-disclaimer">
            🔥 Preço especial de lançamento por tempo limitado, válido para novas assinaturas durante a campanha. Comece o teste gratuito sem cartão — só pedimos o pagamento se você decidir continuar após os 7 dias.
          </p>

          {/* Trust indicators */}
          <div className="mt-10 pt-8 border-t border-zinc-100 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[11.5px] text-zinc-500" data-testid="trust-indicators">
            <span className="inline-flex items-center gap-1.5"><Shield size={13} className="text-zinc-400" />Pagamento seguro via Stripe</span>
            <span className="inline-flex items-center gap-1.5"><Lock size={13} className="text-zinc-400" />Checkout criptografado</span>
            <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-zinc-400" />Cancelamento simples</span>
            <span className="inline-flex items-center gap-1.5"><Sparkles size={13} className="text-zinc-400" />Dados protegidos</span>
          </div>
        </div>
      </section>

      {/* ═══ CTA FINAL ═══ */}
      <section className="py-24 sm:py-32 relative overflow-hidden" style={{backgroundColor:'#FAFAFA'}}>
        <div className="absolute inset-0 lp-bg-grid lp-mask-fade" />
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-[36px] sm:text-[48px] font-semibold tracking-[-0.03em] leading-[1.05] mb-5 text-zinc-900">Eventos que conectam.<br/>Memórias que ficam.</h2>
          <p className="text-[15px] leading-relaxed max-w-lg mx-auto mb-10 text-zinc-500">Comece gratuitamente por 7 dias. Sem compromisso durante o período de teste.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={()=>navigate('/get-started')} className="lp-cta-prim px-6 py-3 text-[14px] font-medium text-white rounded-xl flex items-center gap-2 shadow-brand" style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}} data-testid="final-cta-btn">
              Comece gratuitamente por 7 dias
              <ArrowRight size={15} />
            </button>
            <button onClick={()=>navigate('/login')} className="lp-cta-sec px-6 py-3 text-[14px] font-medium rounded-xl border text-zinc-900" style={{borderColor:'#E4E4E7', backgroundColor:'white'}}>
              Entrar
            </button>
          </div>
          <p className="text-[12px] mt-5 text-zinc-400">Pagamento seguro via Stripe</p>
        </div>
      </section>

      {/* ═══ FOOTER — clean minimalista premium ═══ */}
      <footer className="pt-16 pb-10 border-t" style={{backgroundColor:'white', borderColor:'#F4F4F5', fontFamily:"'Poppins', sans-serif"}} data-testid="lp-footer">
        <div className="max-w-6xl mx-auto px-6">
          {/* Top — branding + links */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 items-start">
            {/* Branding centralizado */}
            <div className="md:col-span-6 flex flex-col items-center md:items-start gap-4">
              <LiventyWordmark height={32} variant="dark" />
              <a
                href="mailto:contato@liventy.com.br"
                className="inline-flex items-center gap-2 text-[13.5px] font-medium text-zinc-700 hover:text-zinc-900 transition-colors group"
                data-testid="footer-contact-email"
              >
                <Mail size={14} className="text-zinc-400 group-hover:text-zinc-700 transition-colors" strokeWidth={1.8} />
                contato@liventy.com.br
              </a>
              <a
                href="https://instagram.com/liventy_oficial"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[13.5px] font-medium text-zinc-700 hover:text-zinc-900 transition-colors group"
                data-testid="footer-contact-instagram"
              >
                <Instagram size={14} className="text-zinc-400 group-hover:text-zinc-700 transition-colors" strokeWidth={1.8} />
                @liventy_oficial
              </a>
            </div>

            {/* Links - Produto */}
            <div className="md:col-span-3 flex flex-col items-center md:items-start gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Produto</p>
              <a href="#funcionalidades" onClick={e=>{e.preventDefault();scrollTo('funcionalidades')}} className="text-[13.5px] text-zinc-600 hover:text-zinc-900 transition-colors">Funcionalidades</a>
              <a href="#como-funciona" onClick={e=>{e.preventDefault();scrollTo('como-funciona')}} className="text-[13.5px] text-zinc-600 hover:text-zinc-900 transition-colors">Como funciona</a>
              <a href="#planos" onClick={e=>{e.preventDefault();scrollTo('planos')}} className="text-[13.5px] text-zinc-600 hover:text-zinc-900 transition-colors">Planos</a>
            </div>

            {/* Links - Suporte */}
            <div className="md:col-span-3 flex flex-col items-center md:items-start gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Suporte</p>
              <a href="mailto:contato@liventy.com.br" className="text-[13.5px] text-zinc-600 hover:text-zinc-900 transition-colors">Contato</a>
              <Link to="/termos" className="text-[13.5px] text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="footer-link-termos">Termos</Link>
              <Link to="/privacidade" className="text-[13.5px] text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="footer-link-privacidade">Privacidade</Link>
            </div>
          </div>

          {/* Bottom bar — centralizada */}
          <div className="mt-14 pt-6 text-center text-[12px] text-zinc-400" style={{borderTop:'1px solid #F4F4F5'}}>
            <p>© 2026 Liventy · Todos os direitos reservados</p>
          </div>
        </div>
      </footer>

      {/* ═══ MODAL DE CONTRATAÇÃO PREMIUM ═══ */}
      <GetStartedModal
        open={gsModal.open}
        onOpenChange={(o) => setGsModal((s) => ({ ...s, open: o }))}
        plan={gsModal.plan}
        tier={gsModal.tier}
        isAuthenticated={!!auth?.user}
      />
    </div>
  );
}

// ═══════════════════════════════════════════
// LANDING PAGE — MOCKUP SUBCOMPONENTS
// ═══════════════════════════════════════════

function BrowserFrame({ children, compact = false }) {
  return (
    <div className="rounded-2xl overflow-hidden border bg-white" style={{borderColor:'#E4E4E7', boxShadow:'0 24px 60px -12px rgba(0,0,0,0.18), 0 8px 16px -4px rgba(0,0,0,0.06)'}}>
      <div className="flex items-center gap-2 px-4 py-3 border-b" style={{borderColor:'#F4F4F5', backgroundColor:'#FAFAFA'}}>
        <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#FF5F57'}} />
        <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#FEBC2E'}} />
        <span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#28C840'}} />
        <div className="flex-1 mx-3 px-3 py-1 rounded-md text-[11px] font-mono text-zinc-400 truncate" style={{backgroundColor:'white', border:'1px solid #E4E4E7'}}>
          liventy.com.br{compact ? '/convidados' : '/dashboard'}
        </div>
      </div>
      <div className="bg-white">{children}</div>
    </div>
  );
}

function LiveDashboardMockup() {
  // 4-step storyline (~16s loop). Each tick advances state for "screen recording" feel.
  const [tick, setTick] = useState(0);
  const [event, setEvent] = useState(null); // { name, type } toast

  useEffect(() => {
    let cancelled = false;
    const STEPS = [
      { delay: 1800, fire: () => setEvent({ name: 'Ana Paula Costa', type: 'rsvp' }) },
      { delay: 3600, fire: () => setEvent({ name: 'Gabriel Lima', type: 'checkin' }) },
      { delay: 4000, fire: () => setEvent({ name: 'Maria Clara', type: 'rsvp' }) },
      { delay: 3200, fire: () => setEvent(null) },
    ];
    let i = 0;
    const next = () => {
      if (cancelled) return;
      const s = STEPS[i % STEPS.length];
      s.fire();
      setTick((t) => t + 1);
      setTimeout(next, s.delay);
      i++;
    };
    const handle = setTimeout(next, 800);
    return () => { cancelled = true; clearTimeout(handle); };
  }, []);

  // Computed values that "tick up" based on tick count
  const confirmed = Math.min(5 + Math.floor(tick / 2), 14);
  const arrivals  = Math.min(Math.floor(tick / 3), 4);
  const sentPct   = Math.min(62 + tick * 2, 88);
  const rsvpPct   = Math.min(45 + tick * 3, 82);

  return (
    <div className="relative flex" style={{minHeight:'460px'}}>
      {/* AO VIVO badge */}
      <div className="absolute top-3 right-4 z-20 flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-50 border border-red-100" data-testid="live-badge">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" style={{animation:'lp-pulse 1.4s ease-in-out infinite'}} />
        <span className="text-[9.5px] font-semibold uppercase tracking-wider text-red-700">Ao vivo</span>
      </div>

      {/* Floating notification */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 pointer-events-none" style={{minWidth: 260}}>
        <div
          key={event?.name + (event?.type || '')}
          className="rounded-xl bg-white border shadow-lg px-3 py-2 flex items-center gap-2.5 transition-all duration-500"
          style={{
            borderColor: '#E4E4E7',
            boxShadow: '0 12px 30px -10px rgba(0,0,0,0.18)',
            opacity: event ? 1 : 0,
            transform: event ? 'translateY(0)' : 'translateY(-12px)',
          }}
        >
          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
            event?.type === 'checkin' ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white'
          }`}>
            {event?.type === 'checkin' ? <UserCheck size={13} strokeWidth={2.5} /> : <Check size={13} strokeWidth={2.8} />}
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-zinc-900 truncate">{event?.name || '—'}</p>
            <p className="text-[9.5px] text-zinc-500">
              {event?.type === 'checkin' ? 'chegou ao evento · agora' : 'confirmou presença · agora'}
            </p>
          </div>
        </div>
      </div>

      <aside className="hidden sm:flex flex-col w-48 border-r p-3" style={{borderColor:'#F4F4F5'}}>
        <div className="flex items-center gap-2 mb-1 px-2 py-2">
          <LiventyLogoMark size={28} />
          <div className="flex-1">
            <LiventyWordmark height={14} variant="dark" />
          </div>
        </div>
        <div className="rounded-lg px-3 py-2.5 mb-2" style={{backgroundColor:'#F4F4F5'}}>
          <p className="text-[11px] font-semibold">Maria & João</p>
          <p className="text-[10px] text-zinc-500">15 jun · 14:00</p>
        </div>
        {[
          {l:'Painel', ic:Home, active:true},
          {l:'Convidados', ic:Users},
          {l:'WhatsApp', ic:MessageCircle},
          {l:'Check-in', ic:UserCheck},
          {l:'Agenda', ic:CalendarDays},
        ].map((item,i)=>(
          <div key={i} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] ${item.active ? 'bg-zinc-100 font-semibold text-zinc-900' : 'text-zinc-500'}`}>
            <item.ic size={13} />
            {item.l}
          </div>
        ))}
      </aside>

      <main className="flex-1 p-5 sm:p-7">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-[18px] font-semibold tracking-tight">Painel</h3>
            <p className="text-[11px] text-zinc-400">Maria & João</p>
          </div>
          <div className="text-[10px] px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">37 dias restantes</div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5">
          {[
            {l:'Convidados', v:'248', sub:`${confirmed} confirmados`, ic:Users, color:'#3B82F6', highlight: event?.type === 'rsvp'},
            {l:'Orçamento', v:'R$ 50k', sub:'R$ 0 pago', ic:DollarSign, color:'#22C55E'},
            {l:'Presentes', v:String(arrivals), sub:'check-in feito', ic:UserCheck, color:'#A855F7', highlight: event?.type === 'checkin'},
            {l:'Dias', v:'37', sub:'15/06/2026', ic:CalendarDays, color:'#F59E0B'},
          ].map((s,i)=>(
            <div
              key={i}
              className="rounded-xl p-3 border bg-white transition-all duration-500"
              style={{
                borderColor: s.highlight ? s.color : '#F4F4F5',
                boxShadow: s.highlight ? `0 0 0 3px ${s.color}15` : 'none',
                transform: s.highlight ? 'translateY(-1px)' : 'translateY(0)',
              }}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{backgroundColor:`${s.color}15`}}>
                  <s.ic size={10} style={{color:s.color}} />
                </div>
                <p className="text-[9.5px] text-zinc-500 font-medium">{s.l}</p>
              </div>
              <p className="text-[16px] font-semibold tracking-tight transition-all duration-300" style={{color: s.highlight ? s.color : '#18181B'}}>{s.v}</p>
              <p className="text-[9px] text-zinc-400 mt-0.5">{s.sub}</p>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-2.5">
          <div className="rounded-xl p-4 border bg-white" style={{borderColor:'#F4F4F5'}}>
            <p className="text-[11px] font-semibold mb-3">Confirmações</p>
            <div className="flex items-center gap-3">
              <div className="relative w-14 h-14">
                <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
                  <circle cx="18" cy="18" r="15" fill="none" stroke="#F4F4F5" strokeWidth="3.5" />
                  <circle cx="18" cy="18" r="15" fill="none" stroke="#22C55E" strokeWidth="3.5" strokeLinecap="round"
                    strokeDasharray={`${(rsvpPct/100)*94.25} 94.25`}
                    style={{transition:'stroke-dasharray 700ms cubic-bezier(0.16,1,0.3,1)'}} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-[11px] font-bold text-zinc-900 tabular-nums">{rsvpPct}%</p>
                </div>
              </div>
              <div className="flex-1 space-y-0.5">
                <div className="flex items-center justify-between text-[10px]"><span className="text-zinc-500">Confirmados</span><span className="font-semibold tabular-nums">{confirmed}</span></div>
                <div className="flex items-center justify-between text-[10px]"><span className="text-zinc-500">Pendentes</span><span className="font-semibold tabular-nums">{14 - Math.min(confirmed, 14) + 3}</span></div>
                <div className="flex items-center justify-between text-[10px]"><span className="text-zinc-500">Total</span><span className="font-semibold tabular-nums">{confirmed + (14 - Math.min(confirmed, 14) + 3)}</span></div>
              </div>
            </div>
          </div>
          <div className="rounded-xl p-4 border bg-white" style={{borderColor:'#F4F4F5'}}>
            <p className="text-[11px] font-semibold mb-3">Progresso</p>
            <div className="space-y-2">
              {[
                {l:'Convites enviados', v: sentPct, c:'#18181B'},
                {l:'Confirmações', v: rsvpPct, c:'#22C55E'},
                {l:'Mesas alocadas', v: Math.min(28 + tick, 48), c:'#A855F7'},
              ].map((p,i)=>(
                <div key={i}>
                  <div className="flex items-center justify-between text-[9.5px] mb-1"><span className="text-zinc-600">{p.l}</span><span className="font-semibold tabular-nums">{p.v}%</span></div>
                  <div className="w-full h-1.5 rounded-full overflow-hidden" style={{backgroundColor:'#F4F4F5'}}>
                    <div className="h-full rounded-full" style={{width:`${p.v}%`, backgroundColor:p.c, transition:'width 700ms cubic-bezier(0.16,1,0.3,1)'}} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function MacWindow({ children, address = "app.liventy.com", compact = false }) {
  return (
    <div className="rounded-2xl overflow-hidden bg-white border" style={{borderColor:'#E4E4E7', boxShadow:'0 30px 80px -20px rgba(24,24,27,0.18), 0 8px 16px -6px rgba(24,24,27,0.08)'}}>
      <div className="flex items-center gap-3 px-4 py-3 border-b" style={{borderColor:'#F4F4F5', backgroundColor:'#FAFAFA'}}>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{backgroundColor:'#FF5F57'}} />
          <span className="w-3 h-3 rounded-full" style={{backgroundColor:'#FEBC2E'}} />
          <span className="w-3 h-3 rounded-full" style={{backgroundColor:'#28C840'}} />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] text-zinc-500" style={{backgroundColor:'white', border:'1px solid #F4F4F5'}}>
            <Lock size={10} className="text-zinc-400" />
            <span>{address}</span>
          </div>
        </div>
        <div className="w-12" />
      </div>
      <div className={compact ? "" : ""}>{children}</div>
    </div>
  );
}

const SHOWCASE_TABS = [
  { id: 'dashboard',   label: 'Painel',      icon: Home },
  { id: 'convidados',  label: 'Convidados',  icon: Users },
  { id: 'whatsapp',    label: 'WhatsApp',    icon: MessageCircle },
  { id: 'checkin',     label: 'Check-in',    icon: UserCheck },
  { id: 'mesas',       label: 'Mesas',       icon: LayoutGrid },
  { id: 'financeiro',  label: 'Financeiro',  icon: DollarSign },
  { id: 'relatorios',  label: 'Relatórios',  icon: TrendingUp },
];

function ShowcaseInAction() {
  const [active, setActive] = useState('dashboard');
  const [fade, setFade] = useState(true);
  const switchTo = (id) => {
    if (id === active) return;
    setFade(false);
    setTimeout(() => { setActive(id); setFade(true); }, 140);
  };

  const renderTab = () => {
    switch (active) {
      case 'convidados':  return <ConvidadosMockup />;
      case 'whatsapp':    return <WhatsAppMockup />;
      case 'checkin':     return <CheckinMockup />;
      case 'mesas':       return <MesasMockup />;
      case 'financeiro':  return <FinanceiroMockup />;
      case 'relatorios':  return <RelatoriosMockup />;
      default:            return <LiveDashboardMockup />;
    }
  };

  return (
    <section id="veja-em-acao" data-animate className="py-24 sm:py-32 relative overflow-hidden" style={{backgroundColor:'white'}}>
      <div className="absolute inset-0 lp-bg-grid lp-mask-fade opacity-40" />
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-zinc-500 mb-3">Veja em ação</p>
          <h2 className="text-[34px] sm:text-[44px] font-semibold tracking-[-0.03em] leading-[1.05] mb-4 text-zinc-900">
            Tudo o que você precisa para organizar<br className="hidden sm:block" /> seus eventos em um só lugar.
          </h2>
          <p className="text-[15px] text-zinc-500 leading-relaxed max-w-xl mx-auto">
            Uma experiência moderna e completa para gerenciar convidados, confirmações, check-in, mesas, financeiro e muito mais.
          </p>
        </div>

        {/* Tabs scrollable on mobile */}
        <div className="flex justify-center mb-7 px-2 -mx-2">
          <div className="flex items-center gap-1 p-1 rounded-full border max-w-full overflow-x-auto lp-no-scrollbar" style={{borderColor:'#E4E4E7', backgroundColor:'#FAFAFA'}} data-testid="showcase-tabs">
            {SHOWCASE_TABS.map((t) => {
              const isActive = active === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => switchTo(t.id)}
                  data-testid={`showcase-tab-${t.id}`}
                  className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12.5px] font-medium tracking-tight transition-all duration-300 ${
                    isActive
                      ? 'bg-brand-deep text-white shadow-sm'
                      : 'text-zinc-600 hover:text-zinc-900 hover:bg-white'
                  }`}
                >
                  <t.icon size={13} strokeWidth={isActive ? 2.2 : 1.7} />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Mac window with active tab content */}
        <div className="relative max-w-5xl mx-auto">
          <div className="absolute -inset-x-16 -top-6 -bottom-6 opacity-30 pointer-events-none" style={{background:'radial-gradient(60% 50% at 50% 50%, rgba(107,61,246,0.18), transparent 70%)'}} />
          <div className={`relative transition-opacity duration-200 ${fade ? 'opacity-100' : 'opacity-0'}`} data-testid="showcase-window">
            <MacWindow address={`app.liventy.com/${active}`}>
              {renderTab()}
            </MacWindow>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatsAppMockup() {
  return (
    <div className="grid sm:grid-cols-2" style={{minHeight:'420px'}}>
      <div className="p-6 border-r flex flex-col items-center justify-center text-center" style={{borderColor:'#F4F4F5', backgroundColor:'#FAFAFA'}}>
        <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center mb-3">
          <MessageCircle size={16} className="text-emerald-600" />
        </div>
        <p className="text-[13px] font-semibold text-zinc-900 mb-1">Conecte seu WhatsApp</p>
        <p className="text-[11px] text-zinc-500 mb-4 max-w-[200px]">Escaneie o QR Code abaixo no app do WhatsApp</p>
        <div className="w-32 h-32 rounded-xl bg-white border p-2 grid grid-cols-8 grid-rows-8 gap-[1.5px]" style={{borderColor:'#E4E4E7'}}>
          {Array.from({length: 64}).map((_, i) => (
            <div key={i} className="rounded-[1px]" style={{backgroundColor: [0,1,2,3,4,5,6,7,8,15,16,23,24,31,32,39,40,47,48,55,56,57,58,59,60,61,62,63,10,12,14,19,21,26,28,33,35,42,44,49,51].includes(i) ? '#18181B' : (Math.random() > 0.55 ? '#18181B' : 'transparent')}} />
          ))}
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] text-emerald-700 font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" style={{animation:'lp-pulse 2s ease-in-out infinite'}} />
          Aguardando conexão
        </div>
      </div>
      <div className="p-5 sm:p-6 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[13px] font-semibold tracking-tight">Disparo em massa</p>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-medium">3 / 248</span>
        </div>
        <div className="rounded-xl border p-3 mb-3 bg-zinc-50" style={{borderColor:'#F4F4F5'}}>
          <p className="text-[10px] text-zinc-400 mb-1.5">Mensagem padrão</p>
          <p className="text-[11.5px] text-zinc-700 leading-relaxed">
            Olá <span className="bg-brand-deep text-white px-1 rounded">{`{nome}`}</span>! Você está convidado(a) para o casamento da Maria & João em 15/06. Confirme em: <span className="text-blue-600">liventy.com/c/abc</span>
          </p>
        </div>
        <div className="space-y-1.5 flex-1">
          {[
            {n:'Gabriel e Família', s:'sent', t:'agora'},
            {n:'Denis e Família', s:'delivered', t:'2 min'},
            {n:'Jerry e Família', s:'read', t:'5 min'},
            {n:'Maria Clara Silva', s:'sending', t:'enviando…'},
          ].map((m,i)=>(
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{backgroundColor: i === 3 ? '#FAFAFA' : 'transparent'}}>
              <div className="w-6 h-6 rounded-full bg-zinc-200 flex items-center justify-center text-[9px] font-semibold text-zinc-600">{m.n.charAt(0)}</div>
              <p className="flex-1 text-[11px] text-zinc-700 truncate">{m.n}</p>
              <span className="text-[9px] text-zinc-400">{m.t}</span>
              {m.s === 'read' && <Check size={11} className="text-emerald-600" strokeWidth={3} />}
              {m.s === 'delivered' && <Check size={11} className="text-zinc-400" strokeWidth={3} />}
              {m.s === 'sent' && <Check size={11} className="text-zinc-300" strokeWidth={3} />}
              {m.s === 'sending' && <Loader2 size={10} className="text-zinc-400 animate-spin" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CheckinMockup() {
  return (
    <div className="grid sm:grid-cols-5 gap-0" style={{minHeight:'420px'}}>
      <div className="sm:col-span-2 p-5 sm:p-6 border-r flex flex-col items-center justify-center text-center" style={{borderColor:'#F4F4F5', backgroundColor:'#0A0A0B'}}>
        <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-3 font-medium">Scanner ao vivo</p>
        <div className="relative w-40 h-40 rounded-2xl overflow-hidden mb-4" style={{backgroundColor:'#18181B'}}>
          <div className="absolute inset-2 border-2 rounded-xl" style={{borderColor:'rgba(255,255,255,0.06)'}} />
          {[0,1,2,3].map(c => (
            <div key={c} className="absolute w-4 h-4 border-emerald-400" style={{
              top: c < 2 ? 8 : 'auto', bottom: c >= 2 ? 8 : 'auto',
              left: c % 2 === 0 ? 8 : 'auto', right: c % 2 === 1 ? 8 : 'auto',
              borderTopWidth: c < 2 ? 2 : 0, borderBottomWidth: c >= 2 ? 2 : 0,
              borderLeftWidth: c % 2 === 0 ? 2 : 0, borderRightWidth: c % 2 === 1 ? 2 : 0,
            }} />
          ))}
          <div className="absolute inset-x-3 h-0.5 bg-emerald-400/80 shadow-[0_0_12px_rgba(52,211,153,0.6)]" style={{top:'50%', animation:'lp-pulse 1.6s ease-in-out infinite'}} />
        </div>
        <p className="text-[11px] text-zinc-300 font-medium">Aponte para o QR Code</p>
        <p className="text-[9.5px] text-zinc-500 mt-1">&lt; 5 segundos por convidado</p>
      </div>
      <div className="sm:col-span-3 p-5 sm:p-6 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[13px] font-semibold tracking-tight">Chegadas em tempo real</p>
            <p className="text-[10px] text-zinc-400">186 / 248 presentes</p>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" style={{animation:'lp-pulse 2s ease-in-out infinite'}} /> Ao vivo
          </span>
        </div>
        <div className="space-y-1.5 flex-1">
          {[
            {n:'Gabriel e Família', m:'Mesa 4', t:'agora', g:'+1'},
            {n:'Denis e Família', m:'Mesa 4', t:'1 min'},
            {n:'Maria Clara Silva', m:'Mesa 2', t:'3 min'},
            {n:'Pedro Henrique', m:'Mesa 7', t:'4 min'},
            {n:'Ana Paula Costa', m:'Mesa 1', t:'6 min'},
          ].map((g,i)=>(
            <div key={i} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg" style={{backgroundColor: i === 0 ? '#ECFDF5' : 'transparent'}}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${i === 0 ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-600'}`}>
                <Check size={12} strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11.5px] font-medium text-zinc-900 truncate">{g.n}</p>
                <p className="text-[10px] text-zinc-500">{g.m}</p>
              </div>
              <span className="text-[10px] text-zinc-400">{g.t}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MesasMockup() {
  const tables = [
    {id:1, n:'Mesa 1', g:['Família Silva','Família Costa'], lots:8, x:6, y:18},
    {id:2, n:'Mesa 2', g:['Família Souza'], lots:6, x:36, y:14},
    {id:3, n:'Mesa 3', g:['Amigos faculdade'], lots:8, x:66, y:18},
    {id:4, n:'Mesa 4', g:['Gabriel & família','Denis & família'], lots:10, x:18, y:50},
    {id:5, n:'Mesa 5', g:['Família noivo'], lots:8, x:50, y:55},
    {id:6, n:'Mesa 6', g:['Trabalho'], lots:6, x:78, y:48},
  ];
  return (
    <div className="grid sm:grid-cols-5" style={{minHeight:'420px'}}>
      <div className="sm:col-span-3 p-5 sm:p-6 relative" style={{backgroundColor:'#FAFAFA'}}>
        <p className="text-[13px] font-semibold tracking-tight mb-1">Planta do salão</p>
        <p className="text-[10px] text-zinc-400 mb-3">Arraste para organizar · 6 mesas configuradas</p>
        <div className="relative rounded-xl border bg-white" style={{borderColor:'#F4F4F5', height:'340px'}}>
          <div className="absolute top-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[9px] font-medium tracking-wider uppercase" style={{backgroundColor:'#18181B', color:'white'}}>Pista</div>
          {tables.map(t => (
            <div key={t.id} className="absolute -translate-x-1/2 -translate-y-1/2 group" style={{left:`${t.x + 8}%`, top:`${t.y + 12}%`}}>
              <div className="w-14 h-14 rounded-full border-2 flex items-center justify-center bg-white" style={{borderColor: t.id === 4 ? '#3B82F6' : '#E4E4E7', boxShadow: t.id === 4 ? '0 4px 14px -4px rgba(59,130,246,0.4)' : 'none'}}>
                <div className="text-center">
                  <p className="text-[9px] font-semibold text-zinc-900">{t.n}</p>
                  <p className="text-[8px] text-zinc-400">{t.lots} lug.</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="sm:col-span-2 p-5 sm:p-6 border-l" style={{borderColor:'#F4F4F5'}}>
        <p className="text-[13px] font-semibold tracking-tight mb-1">Mesa 4</p>
        <p className="text-[10px] text-zinc-400 mb-3">10 lugares · 9 ocupados</p>
        <div className="space-y-1.5">
          {['Gabriel Lima','Carolina Lima','Lucas Lima — 6 anos','Maria Lima — 2 anos','Denis Ferreira','Patrícia Ferreira','Helena Ferreira — 4 anos','André Sousa','Beatriz Sousa'].map((g,i)=>(
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{backgroundColor: i % 2 === 0 ? '#FAFAFA' : 'transparent'}}>
              <div className="w-5 h-5 rounded-full bg-zinc-200 flex items-center justify-center text-[8px] font-semibold text-zinc-600 flex-shrink-0">{g.charAt(0)}</div>
              <p className="text-[10.5px] text-zinc-700 truncate flex-1">{g}</p>
            </div>
          ))}
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg border-dashed border" style={{borderColor:'#E4E4E7'}}>
            <div className="w-5 h-5 rounded-full bg-zinc-50 flex items-center justify-center text-zinc-300">+</div>
            <p className="text-[10px] text-zinc-400">1 lugar livre</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FinanceiroMockup() {
  return (
    <div className="p-5 sm:p-6" style={{minHeight:'420px'}}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[14px] font-semibold tracking-tight">Financeiro</p>
          <p className="text-[10px] text-zinc-400">Visão geral · Junho 2026</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button className="px-2.5 py-1.5 rounded-lg border text-[10px] font-medium text-zinc-700" style={{borderColor:'#E4E4E7'}}>Exportar</button>
          <button className="px-2.5 py-1.5 rounded-lg text-[10px] font-medium text-white" style={{backgroundColor:'#18181B'}}>+ Despesa</button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          {l:'Orçamento total', v:'R$ 50.000', sub:'planejado', c:'#3B82F6'},
          {l:'Pago', v:'R$ 32.450', sub:'64,9%', c:'#22C55E'},
          {l:'A pagar', v:'R$ 17.550', sub:'13 itens', c:'#F59E0B'},
        ].map((s,i)=>(
          <div key={i} className="rounded-xl border p-3 bg-white" style={{borderColor:'#F4F4F5'}}>
            <p className="text-[9.5px] text-zinc-500 font-medium mb-1">{s.l}</p>
            <p className="text-[18px] font-semibold tracking-tight" style={{color:s.c}}>{s.v}</p>
            <p className="text-[9px] text-zinc-400 mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl border bg-white" style={{borderColor:'#F4F4F5'}}>
        <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[9px] font-medium text-zinc-400 uppercase tracking-wider border-b" style={{borderColor:'#F4F4F5', backgroundColor:'#FAFAFA'}}>
          <div className="col-span-5">Despesa</div>
          <div className="col-span-3">Categoria</div>
          <div className="col-span-2 text-right">Valor</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        {[
          {n:'Buffet — Casa Brasa', c:'Buffet', v:'R$ 18.000', s:'pago'},
          {n:'Decoração — Atelier Pétala', c:'Decoração', v:'R$ 8.500', s:'pago'},
          {n:'Fotógrafo — Studio Lente', c:'Fotografia', v:'R$ 5.950', s:'parcial'},
          {n:'Música — Trio Cordas', c:'Música', v:'R$ 3.200', s:'pendente'},
          {n:'Convites — Papel Fino', c:'Papelaria', v:'R$ 1.450', s:'pago'},
        ].map((row,i)=>(
          <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-b" style={{borderColor:'#F4F4F5'}}>
            <div className="col-span-5 text-[11px] text-zinc-900 truncate font-medium">{row.n}</div>
            <div className="col-span-3 text-[10px] text-zinc-500">{row.c}</div>
            <div className="col-span-2 text-[10.5px] font-semibold text-zinc-900 text-right tracking-tight">{row.v}</div>
            <div className="col-span-2 text-right">
              <span className={`inline-block text-[9px] font-medium px-1.5 py-0.5 rounded ${
                row.s === 'pago' ? 'bg-emerald-50 text-emerald-700' :
                row.s === 'parcial' ? 'bg-amber-50 text-amber-700' :
                'bg-zinc-100 text-zinc-600'
              }`}>{row.s}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RelatoriosMockup() {
  const bars = [42, 58, 71, 65, 82, 91, 88];
  return (
    <div className="p-5 sm:p-6" style={{minHeight:'420px'}}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[14px] font-semibold tracking-tight">Relatórios</p>
          <p className="text-[10px] text-zinc-400">Análise de confirmações nos últimos 7 dias</p>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
          <TrendingUp size={10} strokeWidth={2.5} /> +24%
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          {l:'Confirmados', v:'186', sub:'75% do total', c:'#22C55E'},
          {l:'Aguardando', v:'48', sub:'19,4%', c:'#F59E0B'},
          {l:'Recusaram', v:'10', sub:'4%', c:'#EF4444'},
          {l:'Sem resposta', v:'4', sub:'1,6%', c:'#A1A1AA'},
        ].map((s,i)=>(
          <div key={i} className="rounded-xl border p-3 bg-white" style={{borderColor:'#F4F4F5'}}>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{backgroundColor:s.c}} />
              <p className="text-[9.5px] text-zinc-500 font-medium">{s.l}</p>
            </div>
            <p className="text-[18px] font-semibold tracking-tight text-zinc-900">{s.v}</p>
            <p className="text-[9px] text-zinc-400 mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl border bg-white p-4" style={{borderColor:'#F4F4F5'}}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-semibold">Confirmações por dia</p>
          <p className="text-[9px] text-zinc-400">Últimos 7 dias</p>
        </div>
        <div className="flex items-end gap-2 h-32">
          {bars.map((h, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="w-full rounded-md transition-all duration-500" style={{height:`${h}%`, backgroundColor: i === bars.length - 1 ? '#18181B' : '#E4E4E7'}} />
              <span className="text-[9px] text-zinc-400 font-medium">{['Seg','Ter','Qua','Qui','Sex','Sáb','Dom'][i]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PhoneFrame({ children }) {
  return (
    <div className="relative rounded-[40px] p-2 max-w-[280px]" style={{backgroundColor:'#18181B', boxShadow:'0 30px 60px -12px rgba(0,0,0,0.25), 0 12px 20px -6px rgba(0,0,0,0.1)'}}>
      <div className="rounded-[32px] overflow-hidden bg-zinc-50" style={{aspectRatio:'9/19.5'}}>
        <div className="h-6 flex items-center justify-center" style={{backgroundColor:'#18181B'}}>
          <div className="w-20 h-4 rounded-full" style={{backgroundColor:'#000'}} />
        </div>
        <div className="h-full overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

function DashboardMockup() {
  return (
    <div className="flex" style={{minHeight:'460px'}}>
      <aside className="hidden sm:flex flex-col w-48 border-r p-3" style={{borderColor:'#F4F4F5'}}>
        <div className="flex items-center gap-2 mb-1 px-2 py-2">
          <LiventyLogoMark size={28} />
          <div className="flex-1">
            <p className="text-[12px] font-bold leading-tight" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.03em' }}>liventy</p>
            <p className="text-[10px] text-zinc-400 leading-tight">Organize · Celebre · Crie memórias</p>
          </div>
        </div>
        <div className="rounded-lg px-3 py-2.5 mb-2" style={{backgroundColor:'#F4F4F5'}}>
          <p className="text-[11px] font-semibold">Maria & João</p>
          <p className="text-[10px] text-zinc-500">15 jun · 14:00</p>
        </div>
        {[
          {l:'Painel', ic:Home, active:true},
          {l:'Convidados', ic:Users},
          {l:'WhatsApp', ic:MessageCircle},
          {l:'Check-in', ic:UserCheck},
          {l:'Agenda', ic:CalendarDays},
        ].map((item,i)=>(
          <div key={i} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] ${item.active ? 'bg-zinc-100 font-semibold text-zinc-900' : 'text-zinc-500'}`}>
            <item.ic size={13} />
            {item.l}
          </div>
        ))}
      </aside>
      <main className="flex-1 p-5 sm:p-7">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-[18px] font-semibold tracking-tight">Painel</h3>
            <p className="text-[11px] text-zinc-400">Maria & João</p>
          </div>
          <div className="text-[10px] px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">37 dias restantes</div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5">
          {[
            {l:'Convidados', v:'248', sub:'5 confirmados', ic:Users, color:'#3B82F6'},
            {l:'Orçamento', v:'R$ 50k', sub:'R$ 0 pago', ic:DollarSign, color:'#22C55E'},
            {l:'Tarefas', v:'0/11', sub:'0% concluído', ic:Check, color:'#A855F7'},
            {l:'Dias', v:'37', sub:'15/06/2026', ic:CalendarDays, color:'#F59E0B'},
          ].map((s,i)=>(
            <div key={i} className="rounded-xl p-3 border bg-white" style={{borderColor:'#F4F4F5'}}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{backgroundColor:`${s.color}15`}}>
                  <s.ic size={10} style={{color:s.color}} />
                </div>
                <p className="text-[9.5px] text-zinc-500 font-medium">{s.l}</p>
              </div>
              <p className="text-[16px] font-semibold tracking-tight">{s.v}</p>
              <p className="text-[9px] text-zinc-400 mt-0.5">{s.sub}</p>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-2.5">
          <div className="rounded-xl p-4 border bg-white" style={{borderColor:'#F4F4F5'}}>
            <p className="text-[11px] font-semibold mb-3">Confirmações</p>
            <div className="flex items-center gap-3">
              <svg width="60" height="60" viewBox="0 0 60 60" className="-rotate-90">
                <circle cx="30" cy="30" r="22" fill="none" stroke="#F4F4F5" strokeWidth="6" />
                <circle cx="30" cy="30" r="22" fill="none" stroke="#22C55E" strokeWidth="6" strokeDasharray="138" strokeDashoffset="55" strokeLinecap="round" />
              </svg>
              <div className="flex-1 space-y-1.5">
                <div className="flex justify-between text-[10px]"><span className="text-zinc-500">Confirmados</span><span className="font-semibold">5</span></div>
                <div className="flex justify-between text-[10px]"><span className="text-zinc-500">Pendentes</span><span className="font-semibold">3</span></div>
                <div className="flex justify-between text-[10px]"><span className="text-zinc-500">Total</span><span className="font-semibold">8</span></div>
              </div>
            </div>
          </div>
          <div className="rounded-xl p-4 border bg-white" style={{borderColor:'#F4F4F5'}}>
            <p className="text-[11px] font-semibold mb-3">Progresso</p>
            <div className="space-y-2.5">
              <div>
                <div className="flex justify-between text-[10px] mb-1"><span className="text-zinc-500">Convites enviados</span><span className="font-semibold">62%</span></div>
                <div className="h-1.5 rounded-full" style={{backgroundColor:'#F4F4F5'}}>
                  <div className="h-full rounded-full" style={{width:'62%', backgroundColor:'#18181B'}} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-[10px] mb-1"><span className="text-zinc-500">Confirmações</span><span className="font-semibold">45%</span></div>
                <div className="h-1.5 rounded-full" style={{backgroundColor:'#F4F4F5'}}>
                  <div className="h-full rounded-full" style={{width:'45%', backgroundColor:'#22C55E'}} />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-[10px] mb-1"><span className="text-zinc-500">Mesas alocadas</span><span className="font-semibold">28%</span></div>
                <div className="h-1.5 rounded-full" style={{backgroundColor:'#F4F4F5'}}>
                  <div className="h-full rounded-full" style={{width:'28%', backgroundColor:'#A855F7'}} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function ConvidadosMockup() {
  const guests = [
    {n:'Gabriel e Família', p:'+55 19 99936-2505', m:'Mesa 4', s:'confirmado'},
    {n:'Denis e Família', p:'+55 19 99765-4036', m:'Mesa 4', s:'confirmado'},
    {n:'Jerry e Família', p:'+55 19 99830-6513', m:'—', s:'pendente'},
    {n:'Maria Clara Silva', p:'+55 11 98765-4321', m:'Mesa 2', s:'confirmado'},
    {n:'Pedro Henrique', p:'—', m:'—', s:'sem-fone'},
    {n:'Ana Paula Costa', p:'+55 11 97654-3210', m:'Mesa 1', s:'pendente'},
  ];
  const statusBadge = (s) => {
    if (s === 'confirmado') return <span className="inline-flex items-center gap-1 text-[9px] font-medium"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>Confirmado</span>;
    if (s === 'pendente') return <span className="inline-flex items-center gap-1 text-[9px] font-medium"><span className="w-1.5 h-1.5 rounded-full bg-amber-500"/>A confirmar</span>;
    return <span className="inline-flex items-center gap-1 text-[9px] font-medium"><span className="w-1.5 h-1.5 rounded-full bg-zinc-300"/>Sem telefone</span>;
  };
  return (
    <div className="p-4 sm:p-5" style={{minHeight:'420px'}}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[15px] font-semibold tracking-tight">Convidados</h3>
          <p className="text-[10px] text-zinc-400">8 selecionados · 248 totais</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button className="px-2.5 py-1.5 rounded-lg border text-[10px] font-medium text-zinc-700" style={{borderColor:'#E4E4E7'}}>Filtrar</button>
          <button className="px-2.5 py-1.5 rounded-lg text-[10px] font-medium text-white flex items-center gap-1" style={{backgroundColor:'#18181B'}}><Send size={10} /> Enviar WhatsApp</button>
        </div>
      </div>
      <div className="rounded-xl border overflow-hidden" style={{borderColor:'#F4F4F5'}}>
        <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[9px] font-medium text-zinc-400 uppercase tracking-wider border-b" style={{borderColor:'#F4F4F5', backgroundColor:'#FAFAFA'}}>
          <div className="col-span-1"></div>
          <div className="col-span-4">Nome</div>
          <div className="col-span-3">Telefone</div>
          <div className="col-span-2">Mesa</div>
          <div className="col-span-2">Status</div>
        </div>
        {guests.map((g,i)=>(
          <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center border-b" style={{borderColor:'#F4F4F5'}}>
            <div className="col-span-1">
              <div className="w-3.5 h-3.5 rounded border-2 flex items-center justify-center" style={{borderColor: i < 2 ? '#18181B' : '#E4E4E7', backgroundColor: i < 2 ? '#18181B' : 'transparent'}}>
                {i < 2 && <Check size={8} className="text-white" strokeWidth={3} />}
              </div>
            </div>
            <div className="col-span-4 text-[11px] font-medium text-zinc-900 truncate">{g.n}</div>
            <div className="col-span-3 text-[10px] text-zinc-500 truncate">{g.p}</div>
            <div className="col-span-2 text-[10px] text-zinc-700">{g.m}</div>
            <div className="col-span-2">{statusBadge(g.s)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RsvpMockup() {
  return (
    <div className="bg-zinc-50 h-full p-4 flex flex-col">
      <div className="text-center mb-3">
        <LiventyLogoMark size={40} />
        <p className="text-[14px] font-semibold tracking-tight">Maria & João</p>
        <p className="text-[10px] text-zinc-500">Confirmação de Presença</p>
      </div>
      <div className="bg-white rounded-2xl border overflow-hidden flex-1" style={{borderColor:'#E4E4E7'}}>
        <div className="px-4 py-3 border-b" style={{borderColor:'#F4F4F5'}}>
          <p className="text-[8px] text-zinc-400 uppercase tracking-wider">Convidado</p>
          <p className="text-[12px] font-semibold mt-0.5">Gabriel e Família</p>
        </div>
        <div className="px-4 py-3 border-b space-y-2.5" style={{borderColor:'#F4F4F5'}}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{backgroundColor:'#F4F4F5'}}>
              <CalendarDays size={11} className="text-zinc-500" />
            </div>
            <div>
              <p className="text-[10px] font-medium">Sábado, 15 de Junho</p>
              <p className="text-[8px] text-zinc-400">às 14h00</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{backgroundColor:'#F4F4F5'}}>
              <MapPin size={11} className="text-zinc-500" />
            </div>
            <div>
              <p className="text-[10px] font-medium">Espaço Vista</p>
              <p className="text-[8px] text-zinc-400">Itu, SP</p>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 space-y-2">
          <div className="flex gap-1.5">
            <div className="flex-1 rounded-md px-2 py-1.5 text-center" style={{backgroundColor:'#FAFAFA'}}>
              <p className="text-[7px] text-zinc-400 uppercase">Adultos</p>
              <p className="text-[12px] font-semibold">2</p>
            </div>
            <div className="flex-1 rounded-md px-2 py-1.5 text-center" style={{backgroundColor:'#FAFAFA'}}>
              <p className="text-[7px] text-zinc-400 uppercase">Crianças</p>
              <p className="text-[12px] font-semibold">1</p>
            </div>
          </div>
          <div className="rounded-lg py-2 text-center text-white text-[10px] font-medium flex items-center justify-center gap-1" style={{backgroundColor:'#18181B'}}>
            <Check size={11} /> Confirmar presença
          </div>
        </div>
      </div>
      <p className="text-center text-[8px] text-zinc-400 mt-2">Powered by liventy</p>
    </div>
  );
}

// Login Page
function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  // 🔑 Iter 79 — Suporte a ?staff_invite=TOKEN&email=X — pre-preenche email e
  // dispara claim automático após login bem-sucedido.
  const [searchParams] = useSearchParams();
  const pendingStaffInvite = searchParams.get("staff_invite") || "";
  const prefillEmail = searchParams.get("email") || "";
  const [form, setForm] = useState({ email: prefillEmail, password: "" });
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const cleanEmail = (form.email || "").trim().toLowerCase();
      const res = await login(cleanEmail, form.password);
      const u = res.user || {};

      // 🎯 Se logou via /invite/staff/:token, reivindica o convite agora
      if (pendingStaffInvite) {
        try {
          await axios.post(`${API}/users/claim-staff-invite`, { token: pendingStaffInvite });
          toast.success("Acessos liberados! Bem-vindo à equipe.");
          window.location.assign("/dashboard");
          return;
        } catch (claimErr) {
          // Se falhar o claim, segue o fluxo normal (toast de aviso)
          toast.error(getErrorMessage(claimErr, "Erro ao ativar convite — verifique com o organizador"));
        }
      }

      const isPro = u.subscription === "pro" || u.plan === "pro";
      const isAdmin = u.profile_type === "admin" || isAdminUser(u);
      const completed = (
        u.onboarding_step === "completed"
        || u.onboarding_completed === true
        || isPro
        || isAdmin
      );
      if (!completed) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
      toast.success("Bem-vindo de volta!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao autenticar"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Nunito', sans-serif", backgroundColor: '#F8F7FF' }}>
      <style>{`
        @keyframes loginFadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .login-anim-1 { animation: loginFadeUp 0.5s cubic-bezier(.22,.9,.32,1) both; }
        .login-anim-2 { animation: loginFadeUp 0.5s cubic-bezier(.22,.9,.32,1) 0.06s both; }
        .login-anim-3 { animation: loginFadeUp 0.5s cubic-bezier(.22,.9,.32,1) 0.12s both; }
      `}</style>

      <header className="px-6 sm:px-10 py-5 flex items-center justify-between bg-white/60 backdrop-blur-sm" style={{borderBottom:'1px solid #E9E5FF'}}>
        <button onClick={() => navigate('/')} className="flex items-center gap-2.5 group" data-testid="login-brand-home">
          <LiventyLogoMark size={32} />
          <LiventyWordmark height={22} variant="dark" />
        </button>
        <button
          onClick={() => navigate('/get-started')}
          className="text-[13px] font-bold transition-colors"
          style={{fontFamily:"'Nunito', sans-serif", color:'#6B3DF6'}}
          data-testid="header-create-account"
        >
          Criar conta
        </button>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-10 sm:py-14 relative overflow-hidden">
        {/* Glows de brand suaves */}
        <div className="absolute pointer-events-none" style={{top:'15%', left:'-10%', width:'min(420px,50vw)', height:'min(420px,50vw)', borderRadius:'50%', background:'radial-gradient(50% 50% at 50% 50%, rgba(255,77,141,0.20), transparent 70%)', filter:'blur(50px)'}} />
        <div className="absolute pointer-events-none" style={{bottom:'10%', right:'-10%', width:'min(460px,50vw)', height:'min(460px,50vw)', borderRadius:'50%', background:'radial-gradient(50% 50% at 50% 50%, rgba(107,61,246,0.20), transparent 70%)', filter:'blur(55px)'}} />

        <div className="w-full max-w-[400px] relative z-10">
          <div className="login-anim-1 mb-8 text-center sm:text-left">
            <h1 className="text-[28px] sm:text-[32px] tracking-tight leading-tight" style={{ fontFamily:"'Nunito', sans-serif", fontWeight:700, letterSpacing:'-0.02em', color:'#141023' }}>
              Bem-vindo de volta
            </h1>
            <p className="text-[14px] mt-2" style={{fontFamily:"'Nunito', sans-serif", fontWeight:500, color:'#6B7280'}}>
              Entre para continuar planejando seu evento
            </p>
          </div>

          <div className="login-anim-2">
            <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="login-form">
              <div>
                <label className="block text-[12.5px] mb-1.5" style={{fontFamily:"'Nunito', sans-serif", fontWeight:600, color:'#141023'}}>Email</label>
                <input
                  type="email"
                  placeholder="seu@email.com"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  required
                  autoComplete="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  autoFocus
                  data-testid="login-email-input"
                  className="w-full h-12 px-4 rounded-xl bg-white text-[14px] placeholder-zinc-400 focus:outline-none transition-all"
                  style={{fontFamily:"'Nunito', sans-serif", border:'1.5px solid #E9E5FF', color:'#141023'}}
                  onFocus={(e)=>{e.target.style.borderColor='#6B3DF6'; e.target.style.boxShadow='0 0 0 4px rgba(107,61,246,0.10)'}}
                  onBlur={(e)=>{e.target.style.borderColor='#E9E5FF'; e.target.style.boxShadow='none'}}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[12.5px]" style={{fontFamily:"'Nunito', sans-serif", fontWeight:600, color:'#141023'}}>Senha</label>
                  <Link
                    to="/forgot-password"
                    className="text-[12px] transition-colors"
                    style={{fontFamily:"'Nunito', sans-serif", fontWeight:600, color:'#6B3DF6'}}
                    data-testid="forgot-password"
                  >
                    Esqueceu?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    required
                    autoComplete="current-password"
                    data-testid="login-password-input"
                    className="w-full h-12 pl-4 pr-12 rounded-xl bg-white text-[14px] placeholder-zinc-400 focus:outline-none transition-all"
                    style={{fontFamily:"'Nunito', sans-serif", border:'1.5px solid #E9E5FF', color:'#141023'}}
                    onFocus={(e)=>{e.target.style.borderColor='#6B3DF6'; e.target.style.boxShadow='0 0 0 4px rgba(107,61,246,0.10)'}}
                    onBlur={(e)=>{e.target.style.borderColor='#E9E5FF'; e.target.style.boxShadow='none'}}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(v => !v)}
                    aria-label={showPw ? "Ocultar senha" : "Visualizar senha"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md transition-colors hover:bg-zinc-100 active:bg-zinc-200"
                    style={{ color: showPw ? '#6B3DF6' : '#9CA3AF' }}
                    data-testid="login-pw-toggle"
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full h-12 rounded-full text-white text-[14px] font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed mt-3"
                style={{
                  fontFamily:"'Nunito', sans-serif",
                  backgroundImage:'linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)',
                  boxShadow:'0 10px 28px -8px rgba(107,61,246,0.50), 0 4px 14px -6px rgba(255,77,141,0.30)',
                }}
              >
                {loading && <Loader2 className="animate-spin" size={15} />}
                <span>Entrar</span>
              </button>
            </form>
          </div>

          <p className="login-anim-3 text-center text-[13.5px] mt-7" style={{fontFamily:"'Nunito', sans-serif", color:'#6B7280'}}>
            Não tem conta?{' '}
            <button
              onClick={() => navigate('/get-started')}
              className="hover:underline"
              style={{fontFamily:"'Nunito', sans-serif", fontWeight:700, color:'#6B3DF6'}}
              data-testid="link-create-account"
            >
              Criar conta
            </button>
          </p>
        </div>
      </main>

      <footer className="px-6 sm:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11.5px]" style={{borderTop:'1px solid #E9E5FF', color:'#9CA3AF', fontFamily:"'Nunito', sans-serif"}}>
        <p>© 2026 <span style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, letterSpacing: '-0.02em', color:'#141023' }}>liventy</span></p>
        <div className="flex items-center gap-5">
          <Link to="/termos" className="hover:text-zinc-900 transition-colors">Termos</Link>
          <Link to="/privacidade" className="hover:text-zinc-900 transition-colors">Privacidade</Link>
          <a href="mailto:contato@liventy.com.br" className="hover:text-zinc-900 transition-colors">Suporte</a>
        </div>
      </footer>
    </div>
  );
}

// ============================================================
// REGISTER WIZARD — Multi-step premium onboarding
// ============================================================
function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(0); // 0: dados, 1: perfil, 2: success
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [profileType, setProfileType] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const profiles = [
    { id: 'casal', label: 'Estou organizando o meu evento', desc: 'Casamento, aniversário, festa pessoal', icon: User },
    { id: 'professional', label: 'Sou um(a) profissional', desc: 'Cerimonialista, planejador, agência', icon: Briefcase },
  ];

  const validateStep0 = () => {
    if (!form.name.trim()) { toast.error("Informe seu nome"); return false; }
    if (!form.email.trim() || !form.email.includes('@')) { toast.error("Email inválido"); return false; }
    if (!form.phone.trim()) { toast.error("Informe seu telefone"); return false; }
    if (form.password.length < 6) { toast.error("Senha precisa ter ao menos 6 caracteres"); return false; }
    if (!acceptedTerms) { toast.error("Você precisa aceitar os Termos de Uso e a Política de Privacidade"); return false; }
    return true;
  };

  const next = async () => {
    if (step === 0) {
      if (!validateStep0()) return;
      setStep(1);
    } else if (step === 1) {
      if (!profileType) { toast.error("Selecione um perfil para continuar"); return; }
      setLoading(true);
      try {
        await register(form.name, form.email, form.password, form.phone);
        // Try to set profile_type silently (server may already do this in onboarding)
        setStep(2);
        // Auto-redirect to onboarding after 2.4s celebration
        setTimeout(() => navigate('/onboarding'), 2400);
      } catch (err) {
        toast.error(getErrorMessage(err, "Erro ao criar conta"));
      } finally {
        setLoading(false);
      }
    }
  };

  const back = () => { if (step > 0 && step < 2) setStep(step - 1); };

  const totalSteps = 3;

  return (
    <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Poppins', sans-serif", backgroundColor: '#FAFAFA' }}>
      <style>{`
        @keyframes regSlideIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes regBarFill { from { width: 0% } }
        @keyframes regBadgePop { 0% { opacity: 0; transform: scale(0.6); } 60% { transform: scale(1.08); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes confettiFall { 0% { opacity: 1; transform: translate3d(var(--cx,0),-20vh,0) rotate(0deg); } 100% { opacity: 0; transform: translate3d(var(--cx,0),120vh,0) rotate(720deg); } }
        .reg-step-anim { animation: regSlideIn 0.45s cubic-bezier(.22,.9,.32,1) both; }
        .reg-bar-anim { animation: regBarFill 0.5s cubic-bezier(.22,.9,.32,1) both; }
        .reg-badge-anim { animation: regBadgePop 0.6s cubic-bezier(.22,1.36,.32,1) both; }
        .confetti-piece { position: fixed; width: 8px; height: 12px; top: -10vh; pointer-events: none; z-index: 100; animation: confettiFall 2.6s linear forwards; border-radius: 2px; }
      `}</style>

      <header className="px-6 sm:px-10 py-5 flex items-center justify-between border-b border-zinc-200/60 bg-white/60 backdrop-blur-sm">
        <button onClick={() => navigate('/')} className="flex items-center gap-2.5 group">
          <LiventyLogoMark size={32} />
          <span className="text-lg font-bold tracking-tight text-zinc-900" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.04em' }}>liventy</span>
        </button>
        <button
          onClick={() => navigate('/login')}
          className="text-[13px] font-medium text-zinc-700 hover:text-zinc-900 transition-colors"
          data-testid="header-go-login"
        >
          Já tenho conta
        </button>
      </header>

      <main className="flex-1 flex items-start sm:items-center justify-center px-5 py-8 sm:py-14">
        <div className="w-full max-w-[460px]">
          {/* Progress indicator */}
          {step < 2 && (
            <div className="mb-7" data-testid="register-progress">
              <div className="flex items-center justify-between mb-2.5 text-[11.5px] text-zinc-500">
                <span className="font-medium tracking-wide uppercase">Passo {step + 1} de {totalSteps - 1}</span>
                <span>{Math.round(((step + 1) / (totalSteps - 1)) * 100)}%</span>
              </div>
              <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
                <div
                  key={step}
                  className="h-full bg-brand-deep rounded-full reg-bar-anim transition-all"
                  style={{ width: `${((step + 1) / (totalSteps - 1)) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* STEP 0 - Dados básicos */}
          {step === 0 && (
            <div className="reg-step-anim" data-testid="register-step-0">
              <div className="mb-7">
                <h1 className="text-[28px] sm:text-[30px] font-semibold text-zinc-900 tracking-tight leading-tight" style={{ letterSpacing: '-0.025em' }}>
                  Crie sua conta
                </h1>
                <p className="text-[13.5px] text-zinc-500 mt-1.5">
                  Comece a organizar seus eventos em minutos
                </p>
              </div>
              <div className="space-y-3.5">
                <div>
                  <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">Nome completo</label>
                  <input
                    type="text"
                    placeholder="Como podemos te chamar?"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    required
                    autoFocus
                    data-testid="register-name-input"
                    className="w-full h-11 px-3.5 rounded-lg border border-zinc-200 bg-white text-[14px] text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-zinc-900 focus:ring-4 focus:ring-zinc-900/5 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    placeholder="seu@email.com"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    required
                    data-testid="register-email-input"
                    className="w-full h-11 px-3.5 rounded-lg border border-zinc-200 bg-white text-[14px] text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-zinc-900 focus:ring-4 focus:ring-zinc-900/5 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">Telefone (WhatsApp)</label>
                  <input
                    type="tel"
                    placeholder="(11) 99999-9999"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    required
                    data-testid="register-phone-input"
                    className="w-full h-11 px-3.5 rounded-lg border border-zinc-200 bg-white text-[14px] text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-zinc-900 focus:ring-4 focus:ring-zinc-900/5 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">Senha</label>
                  <input
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    required
                    minLength={6}
                    data-testid="register-password-input"
                    className="w-full h-11 px-3.5 rounded-lg border border-zinc-200 bg-white text-[14px] text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-zinc-900 focus:ring-4 focus:ring-zinc-900/5 transition-all"
                  />
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1" data-testid="register-accept-terms-label">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={e => setAcceptedTerms(e.target.checked)}
                    data-testid="register-accept-terms"
                    className="mt-0.5 w-[18px] h-[18px] rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900/20 cursor-pointer flex-shrink-0"
                  />
                  <span className="text-[12px] text-zinc-500 leading-snug">
                    Li e aceito os{" "}
                    <a href="/termos" target="_blank" rel="noopener noreferrer" className="underline font-medium text-zinc-700 hover:text-zinc-900">Termos de Uso</a>{" "}
                    e a{" "}
                    <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="underline font-medium text-zinc-700 hover:text-zinc-900">Política de Privacidade</a>.
                  </span>
                </label>
                <button
                  onClick={next}
                  data-testid="register-next-button"
                  className="w-full h-11 rounded-lg bg-brand-deep text-white text-[14px] font-medium hover:bg-brand active:scale-[0.99] transition-all flex items-center justify-center gap-2 mt-2"
                >
                  Continuar
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 1 - Perfil */}
          {step === 1 && (
            <div className="reg-step-anim" data-testid="register-step-1">
              <div className="mb-7">
                <h1 className="text-[28px] sm:text-[30px] font-semibold text-zinc-900 tracking-tight leading-tight" style={{ letterSpacing: '-0.025em' }}>
                  Como você usa o liventy?
                </h1>
                <p className="text-[13.5px] text-zinc-500 mt-1.5">
                  Personalizamos sua experiência conforme seu perfil
                </p>
              </div>
              <div className="space-y-3">
                {profiles.map(p => {
                  const Ic = p.icon;
                  const active = profileType === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setProfileType(p.id)}
                      data-testid={`profile-${p.id}`}
                      className={`w-full text-left p-4 rounded-xl border transition-all flex items-start gap-3.5 ${active ? 'border-zinc-900 bg-zinc-50 ring-4 ring-zinc-900/5' : 'border-zinc-200 bg-white hover:border-zinc-300'}`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${active ? 'bg-brand-deep text-white' : 'bg-zinc-100 text-zinc-700'}`}>
                        <Ic size={17} strokeWidth={1.6} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-semibold text-zinc-900 tracking-tight">{p.label}</p>
                        <p className="text-[12px] text-zinc-500 mt-0.5">{p.desc}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${active ? 'border-zinc-900 bg-brand-deep' : 'border-zinc-300'}`}>
                        {active && <Check size={11} className="text-white" strokeWidth={3} />}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 mt-6">
                <button
                  onClick={back}
                  data-testid="register-back-button"
                  className="flex-shrink-0 h-11 px-4 rounded-lg bg-white border border-zinc-200 text-zinc-700 text-[13.5px] font-medium hover:bg-zinc-50 transition-all flex items-center gap-1.5"
                >
                  <ChevronLeft size={15} /> Voltar
                </button>
                <button
                  onClick={next}
                  disabled={loading || !profileType}
                  data-testid="register-finish-button"
                  className="flex-1 h-11 rounded-lg bg-brand-deep text-white text-[14px] font-medium hover:bg-brand active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading && <Loader2 className="animate-spin" size={15} />}
                  Criar conta
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 - Sucesso com confete */}
          {step === 2 && (
            <div className="reg-step-anim text-center" data-testid="register-success">
              {/* Confetti */}
              {Array.from({ length: 24 }).map((_, i) => {
                const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6'];
                const color = colors[i % colors.length];
                const left = Math.random() * 100;
                const delay = Math.random() * 0.4;
                const duration = 2.2 + Math.random() * 1.2;
                return (
                  <span
                    key={i}
                    className="confetti-piece"
                    style={{
                      left: `${left}vw`,
                      backgroundColor: color,
                      animationDelay: `${delay}s`,
                      animationDuration: `${duration}s`,
                    }}
                  />
                );
              })}
              <div className="reg-badge-anim w-20 h-20 rounded-2xl bg-emerald-500 flex items-center justify-center mx-auto mb-6 shadow-[0_20px_40px_-12px_rgba(16,185,129,0.4)]">
                <Check size={42} className="text-white" strokeWidth={3} />
              </div>
              <h1 className="text-[28px] sm:text-[32px] font-semibold text-zinc-900 tracking-tight leading-tight" style={{ letterSpacing: '-0.025em' }}>
                Bem-vindo, {form.name.split(' ')[0]}!
              </h1>
              <p className="text-[14px] text-zinc-500 mt-2 max-w-sm mx-auto">
                Sua conta foi criada com sucesso. Vamos configurar seu primeiro evento agora.
              </p>
              <div className="flex items-center justify-center gap-1.5 mt-6 text-[12px] text-zinc-400">
                <Loader2 size={13} className="animate-spin" />
                Preparando tudo...
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="px-6 sm:px-10 py-5 border-t border-zinc-200/60 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11.5px] text-zinc-400">
        <p>© 2026 <span className="font-semibold text-zinc-600" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.02em' }}>liventy</span></p>
        <div className="flex items-center gap-5">
          <Link to="/termos" className="hover:text-zinc-900 transition-colors">Termos</Link>
          <Link to="/privacidade" className="hover:text-zinc-900 transition-colors">Privacidade</Link>
          <a href="mailto:contato@liventy.com.br" className="hover:text-zinc-900 transition-colors">Suporte</a>
        </div>
      </footer>
    </div>
  );
}
// Onboarding Page — Fluxo: Verificação → Perfil → IA
function OnboardingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(null); // verification, profile_selection, event_creation, completed
  const [loading, setLoading] = useState(true);

  // === Verification State ===
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const inputRefs = useRef([]);

  // === Profile State ===
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showTransition, setShowTransition] = useState(false);

  // Load onboarding status
  useEffect(() => {
    const checkStatus = async (attempt = 0) => {
      try {
        // Garante que o header Authorization está setado (defensivo: cobre
        // race condition onde AuthProvider ainda não rodou seu useEffect)
        const tk = typeof window !== "undefined" ? localStorage.getItem("token") : null;
        if (tk && !axios.defaults.headers.common["Authorization"]) {
          axios.defaults.headers.common["Authorization"] = `Bearer ${tk}`;
        }
        const res = await axios.get(`${API}/auth/onboarding-status`);
        const step = res.data.onboarding_step;
        if (step === "completed") {
          navigate("/dashboard", { replace: true });
          return;
        }
        setCurrentStep(step);
      } catch (err) {
        const status = err?.response?.status;
        // 401/403: token ainda não pronto OU sessão inválida.
        // Tenta 1 retry após 300ms (cobre race condition); se persistir → /login
        if ((status === 401 || status === 403) && attempt === 0) {
          await new Promise(r => setTimeout(r, 300));
          return checkStatus(attempt + 1);
        }
        if (status === 401 || status === 403) {
          // Sessão inválida de verdade → manda pro login
          navigate("/login", { replace: true });
          return;
        }
        // Outro erro qualquer (network, 500): assume completed e manda pro dashboard
        // (mais seguro do que mostrar tela de SMS que o user nunca pediu)
        navigate("/dashboard", { replace: true });
        return;
      } finally {
        setLoading(false);
      }
    };
    checkStatus();
  }, [navigate]);

  // Auto-focus first verification input
  useEffect(() => {
    if (currentStep === "verification" && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [currentStep]);

  // === Verification Handlers ===
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setVerifyError("");
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pasted[i] || "";
      }
      setCode(newCode);
      const focusIdx = Math.min(pasted.length, 5);
      inputRefs.current[focusIdx]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join("");
    if (fullCode.length !== 6) {
      setVerifyError("Digite o código completo");
      return;
    }
    setVerifyLoading(true);
    setVerifyError("");
    try {
      await axios.post(`${API}/auth/verify-code`, {
        user_id: user.id,
        code: fullCode,
        type: "phone"
      });
      await refreshUser();
      setCurrentStep("profile_selection");
    } catch (err) {
      setVerifyError(err.response?.data?.detail || "Código inválido. Tente novamente.");
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await axios.post(`${API}/auth/resend-code`);
      toast.success("Código reenviado!");
    } catch {
      toast.error("Erro ao reenviar código");
    }
  };

  // === Profile Handlers ===
  const handleSelectProfile = async () => {
    if (!selectedProfile) return;
    setProfileLoading(true);
    setShowTransition(true);
    try {
      await axios.post(`${API}/auth/select-profile`, {
        profile_type: selectedProfile
      });
      await refreshUser();
      await new Promise(r => setTimeout(r, 1200));
      setShowTransition(false);
      navigate("/dashboard");
    } catch (err) {
      toast.error("Erro ao selecionar perfil");
      setShowTransition(false);
    } finally {
      setProfileLoading(false);
    }
  };

  // === Loading ===
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#4A4543]" size={32} />
      </div>
    );
  }

  // === Transition Screen ===
  if (showTransition) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <div className="text-center" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          <Loader2 className="animate-spin text-[#4A4543] mx-auto mb-4" size={32} />
          <p className="text-[#3D3A38] text-lg" style={{ fontWeight: 500 }}>Preparando sua experiência...</p>
        </div>
      </div>
    );
  }

  // === Steps indicator ===
  const steps = [
    { key: "verification", label: "Verificação" },
    { key: "profile_selection", label: "Perfil" }
  ];
  const currentStepIdx = steps.findIndex(s => s.key === currentStep);

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex flex-col items-center justify-center p-4 md:p-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <style>{`
        @font-face {
          font-family: 'Breathing';
          src: url('/breathing.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up { animation: fadeUp 0.6s ease-out forwards; }
        .animate-fade-up-delay { animation: fadeUp 0.6s ease-out 0.2s forwards; opacity: 0; }
        .animate-fade-up-delay2 { animation: fadeUp 0.6s ease-out 0.4s forwards; opacity: 0; }
      `}</style>

      {/* Logo */}
      <div className="mb-8 animate-fade-up">
        <span className="text-5xl text-[#3D3A38] block tracking-tight" style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, letterSpacing: '-0.04em' }}>liventy</span>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2 mb-8 animate-fade-up-delay" data-testid="onboarding-progress">
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all ${
              i <= currentStepIdx ? 'bg-[#4A4543] text-white' : 'bg-[#E5E2E0] text-[#8A8583]'
            }`} style={{ fontWeight: i <= currentStepIdx ? 500 : 400 }}>
              <span>{i + 1}</span>
              <span className="hidden sm:inline">{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`w-8 h-0.5 ${i < currentStepIdx ? 'bg-[#4A4543]' : 'bg-[#E5E2E0]'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* === STEP: VERIFICATION === */}
      {currentStep === "verification" && (
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-[#E5E2E0] p-8 animate-fade-up-delay2" data-testid="verification-step">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-[#F0EDEB] flex items-center justify-center mb-4">
              <Shield className="text-[#4A4543]" size={24} />
            </div>
            <h2 className="text-xl text-[#3D3A38] mb-2" style={{ fontWeight: 600 }}>Verificação</h2>
            <p className="text-sm text-[#8A8583]" style={{ fontWeight: 300 }}>
              Digite o código de 6 dígitos enviado para seu telefone
            </p>
          </div>

          <div className="flex justify-center gap-3 mb-6" data-testid="verification-code-inputs">
            {code.map((digit, i) => (
              <input
                key={i}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleCodeChange(i, e.target.value)}
                onKeyDown={e => handleCodeKeyDown(i, e)}
                onPaste={i === 0 ? handleCodePaste : undefined}
                className={`w-12 h-14 text-center text-xl rounded-xl border transition-all focus:outline-none focus:ring-2 focus:ring-[#4A4543]/20 ${
                  verifyError ? 'border-red-300 bg-red-50' : digit ? 'border-[#4A4543] bg-[#FAF9F7]' : 'border-[#E5E2E0] bg-white'
                }`}
                style={{ fontWeight: 600 }}
                data-testid={`verification-digit-${i}`}
              />
            ))}
          </div>

          {verifyError && (
            <p className="text-center text-sm text-red-500 mb-4" data-testid="verification-error">{verifyError}</p>
          )}

          <button
            onClick={handleVerify}
            disabled={verifyLoading || code.join("").length !== 6}
            className="w-full py-3.5 bg-[#4A4543] text-white rounded-full hover:bg-[#3D3A38] transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ fontWeight: 400 }}
            data-testid="verify-code-btn"
          >
            {verifyLoading && <Loader2 className="animate-spin" size={18} />}
            <span>Confirmar</span>
          </button>

          <button
            onClick={handleResend}
            className="w-full mt-4 text-sm text-[#8A8583] hover:text-[#4A4543] transition-colors"
            style={{ fontWeight: 300 }}
            data-testid="resend-code-btn"
          >
            Não recebeu? Reenviar código
          </button>
        </div>
      )}

      {/* === STEP: PROFILE SELECTION === */}
      {currentStep === "profile_selection" && (
        <div className="w-full max-w-lg animate-fade-up-delay2" data-testid="profile-selection-step">
          <div className="text-center mb-8">
            <h2 className="text-xl text-[#3D3A38] mb-2" style={{ fontWeight: 600 }}>Como você vai usar o liventy?</h2>
            <p className="text-sm text-[#8A8583]" style={{ fontWeight: 300 }}>
              Isso vai nos ajudar a personalizar sua experiência
            </p>
          </div>

          <div className="grid gap-4 mb-8">
            {/* Card Pessoal */}
            <button
              onClick={() => setSelectedProfile("personal")}
              className={`w-full text-left p-6 rounded-2xl border-2 transition-all duration-300 hover:shadow-md ${
                selectedProfile === "personal"
                  ? 'border-[#4A4543] bg-[#FAF9F7] shadow-md'
                  : 'border-[#E5E2E0] bg-white hover:border-[#B8A5A0]'
              }`}
              data-testid="profile-card-personal"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors ${
                  selectedProfile === "personal" ? 'bg-[#4A4543]' : 'bg-[#F0EDEB]'
                }`}>
                  <User className={selectedProfile === "personal" ? 'text-white' : 'text-[#4A4543]'} size={22} />
                </div>
                <div className="flex-1">
                  <h3 className="text-base text-[#3D3A38] mb-1" style={{ fontWeight: 600 }}>Organizando meu evento</h3>
                  <p className="text-sm text-[#8A8583] mb-3" style={{ fontWeight: 300 }}>
                    Para quem está planejando seu próprio evento (aniversário, casamento, festa, etc.)
                  </p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs text-[#5C5856]">
                      <Check size={14} className="text-[#4A4543]" /> <span>Planejamento simples</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#5C5856]">
                      <Check size={14} className="text-[#4A4543]" /> <span>Controle de convidados</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#5C5856]">
                      <Check size={14} className="text-[#4A4543]" /> <span>Checklist automático</span>
                    </div>
                  </div>
                </div>
                {selectedProfile === "personal" && (
                  <div className="w-6 h-6 rounded-full bg-[#4A4543] flex items-center justify-center flex-shrink-0">
                    <Check className="text-white" size={14} />
                  </div>
                )}
              </div>
            </button>

            {/* Card Profissional */}
            <button
              onClick={() => setSelectedProfile("professional")}
              className={`w-full text-left p-6 rounded-2xl border-2 transition-all duration-300 hover:shadow-md ${
                selectedProfile === "professional"
                  ? 'border-[#4A4543] bg-[#FAF9F7] shadow-md'
                  : 'border-[#E5E2E0] bg-white hover:border-[#B8A5A0]'
              }`}
              data-testid="profile-card-professional"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors ${
                  selectedProfile === "professional" ? 'bg-[#4A4543]' : 'bg-[#F0EDEB]'
                }`}>
                  <Briefcase className={selectedProfile === "professional" ? 'text-white' : 'text-[#4A4543]'} size={22} />
                </div>
                <div className="flex-1">
                  <h3 className="text-base text-[#3D3A38] mb-1" style={{ fontWeight: 600 }}>Sou organizador / cerimonialista</h3>
                  <p className="text-sm text-[#8A8583] mb-3" style={{ fontWeight: 300 }}>
                    Para quem gerencia eventos para clientes
                  </p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs text-[#5C5856]">
                      <Check size={14} className="text-[#4A4543]" /> <span>Gerencie múltiplos eventos</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#5C5856]">
                      <Check size={14} className="text-[#4A4543]" /> <span>Organização profissional</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#5C5856]">
                      <Check size={14} className="text-[#4A4543]" /> <span>Fluxo com clientes</span>
                    </div>
                  </div>
                </div>
                {selectedProfile === "professional" && (
                  <div className="w-6 h-6 rounded-full bg-[#4A4543] flex items-center justify-center flex-shrink-0">
                    <Check className="text-white" size={14} />
                  </div>
                )}
              </div>
            </button>
          </div>

          <button
            onClick={handleSelectProfile}
            disabled={!selectedProfile || profileLoading}
            className="w-full py-3.5 bg-[#4A4543] text-white rounded-full hover:bg-[#3D3A38] transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ fontWeight: 400 }}
            data-testid="profile-continue-btn"
          >
            {profileLoading && <Loader2 className="animate-spin" size={18} />}
            <span>Continuar</span>
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

// Sidebar Component
function Sidebar({ isOpen, setIsOpen }) {
  const { user, logout } = useAuth();
  const { wedding, weddings, setActiveEvent } = useWedding();
  const capabilities = useCapabilities();
  const { openPlanLock } = usePlanUpgrade();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOwner, can, loading: permsLoading } = useWeddingPermissions(wedding?.id);
  const [showSwitcher, setShowSwitcher] = useState(false);

  // 🔒 RBAC Iter 74: Staff só cria evento se for account_owner (Pro próprio)
  const isStaffAccount = user?.user_type === "colaborador" && !user?.account_owner;
  const canCreateEvent = user?.profile_type === "professional" && !isStaffAccount;

  // Fechar dropdown ao trocar de rota
  useEffect(() => { setShowSwitcher(false); }, [location.pathname]);

  // Menu lateral — ordem definida pelo produto. PRO-only via capabilities.
  const canPro = capabilities.can_access_pro_features;
  const menuItems = [
    { icon: Home,          label: "Painel",              path: "/dashboard",    tourId: "painel",        module: "painel",        color: "violet" },
    { icon: Wallet,        label: "Minhas Receitas",     path: "/revenue",      tourId: "receitas",      module: "receitas",      color: "green",  locked: !capabilities.can_see_revenues },
    { icon: FolderOpen,    label: "Meus Eventos",        path: "/my-events",    tourId: "meus-eventos",  module: null,            color: "amber",  locked: !capabilities.can_create_events },
    { icon: FileText,      label: "Fornecedores",        path: "/budgets",      tourId: "orcamentos",    module: "orcamentos",    color: "blue" },
    { icon: CreditCard,    label: "Despesas",            path: "/expenses",     tourId: "despesas",      module: "despesas",      color: "rose" },
    { icon: Receipt,       label: "Fluxo de caixa",      path: "/cashflow",     tourId: "fluxo-caixa",   module: "despesas",      color: "emerald" },
    ...(canPro ? [{ icon: FileBarChart, label: "Relatórios", path: "/reports", tourId: "relatorios", module: "despesas", color: "purple" }] : []),
    { icon: Sparkles,      label: "Inspirações",         path: "/inspirations", tourId: "inspiracoes",   module: null,            color: "violet", locked: !capabilities.can_access_inspirations },
    { icon: Calendar,      label: "Tarefas",             path: "/tasks",        tourId: "agenda",        module: "agenda",        color: "pink" },
    { icon: Users,         label: "Convidados",          path: "/guests",       tourId: "convidados",    module: "convidados",    color: "orange" },
    { icon: MessageCircle, label: "WhatsApp",            path: "/whatsapp",     tourId: "whatsapp",      module: "whatsapp",      color: "green" },
    { icon: LayoutGrid,    label: "Mesas & Assentos",    path: "/organizar-mesas", tourId: "mesas",      module: "mesas",         color: "amber",  locked: !capabilities.can_access_seating },
    { icon: UserCheck,     label: "Check-in convidados", path: "/checkin-live", tourId: "checkin",       module: "checkin",       color: "violet", locked: !capabilities.can_access_checkin },
    { icon: Shield,        label: "Retirada de crianças", path: "/child-checkout", tourId: "retirada",   module: "checkin",       color: "violet", locked: !capabilities.can_access_checkin },
    { icon: Settings,      label: "Configurações",       path: "/settings",     tourId: "configuracoes", module: "configuracoes", color: "zinc" },
    { icon: HelpCircle,    label: "Suporte",             path: "/support",      tourId: "suporte",       module: null,            color: "sky" },
  ];

  // Mapeamento estático Tailwind (precisa estar resolvido no build)
  const COLOR_MAP = {
    violet:  { icon: "text-violet-500",  bg: "bg-violet-50",  ring: "ring-violet-100" },
    blue:    { icon: "text-blue-500",    bg: "bg-blue-50",    ring: "ring-blue-100" },
    rose:    { icon: "text-rose-500",    bg: "bg-rose-50",    ring: "ring-rose-100" },
    emerald: { icon: "text-emerald-500", bg: "bg-emerald-50", ring: "ring-emerald-100" },
    orange:  { icon: "text-orange-500",  bg: "bg-orange-50",  ring: "ring-orange-100" },
    green:   { icon: "text-green-500",   bg: "bg-green-50",   ring: "ring-green-100" },
    pink:    { icon: "text-pink-500",    bg: "bg-pink-50",    ring: "ring-pink-100" },
    sky:     { icon: "text-sky-500",     bg: "bg-sky-50",     ring: "ring-sky-100" },
    zinc:    { icon: "text-zinc-700",    bg: "bg-zinc-100",   ring: "ring-zinc-200" },
    amber:   { icon: "text-amber-500",   bg: "bg-amber-50",   ring: "ring-amber-100" },
  };

  return (
    <React.Fragment>
      {isOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setIsOpen(false)}></div>
      )}
      
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-zinc-200/60 transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} style={{fontFamily:"'Poppins', sans-serif"}}>
        <div className="flex flex-col h-full">
          <div className="p-5 border-b border-zinc-200/60">
            <button type="button" className="flex items-center gap-3 cursor-pointer active:scale-95 transition-transform" onClick={() => navigate('/dashboard')} aria-label="Ir para o Painel" data-testid="sidebar-brand-home">
              <LiventyLogoMark size={40} />
              <LiventyWordmark height={22} variant="dark" />
            </button>
          </div>

          {wedding && (
            <div className="px-3 mt-4 relative" data-testid="sidebar-event-switcher-wrap">
              <button
                type="button"
                onClick={() => {
                  if (!weddings || weddings.length <= 1) return;
                  try { navigator.vibrate?.(6); } catch {}
                  setShowSwitcher(v => !v);
                }}
                disabled={!weddings || weddings.length <= 1}
                className={`group w-full flex items-center gap-2.5 p-2.5 rounded-2xl bg-white border border-zinc-200/80 shadow-[0_1px_2px_rgba(24,24,27,0.04)] transition-all text-left ${weddings && weddings.length > 1 ? 'hover:border-zinc-300 hover:shadow-[0_6px_18px_-10px_rgba(24,24,27,0.18)] cursor-pointer' : 'cursor-default'}`}
                data-testid="sidebar-event-switcher-trigger"
                aria-haspopup="menu"
                aria-expanded={showSwitcher}
              >
                {(() => {
                  const coverUrl = wedding.imagem_capa_url || wedding.cover_image_url || wedding.cover_url;
                  const coverSrc = coverUrl ? (coverUrl.startsWith("http") ? coverUrl : `${API.replace(/\/api$/, "")}${coverUrl}`) : null;
                  if (coverSrc) {
                    return <img src={coverSrc} alt="" className="w-9 h-9 rounded-xl object-cover flex-shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />;
                  }
                  const initials = (wedding.event_name || wedding.partner1_name || "E")
                    .split(/[\s&]+/).filter(Boolean).slice(0, 2)
                    .map(s => s.charAt(0).toUpperCase()).join("") || "E";
                  return (
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-white text-[11.5px] font-bold tracking-wide"
                      style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 60%, #FFB347 100%)" }}
                      aria-hidden="true"
                    >
                      {initials}
                    </div>
                  );
                })()}
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-[9px] uppercase tracking-[0.16em] font-semibold text-zinc-400 leading-none">
                    Evento ativo
                  </span>
                  <span className="text-[13px] font-semibold text-zinc-900 tracking-tight leading-tight truncate mt-1">
                    {wedding.event_name || wedding.partner1_name || "Meu Evento"}
                  </span>
                </div>
                {weddings && weddings.length > 1 && (
                  <ChevronDown
                    size={14}
                    className={`text-zinc-400 flex-shrink-0 transition-transform ${showSwitcher ? 'rotate-180' : ''}`}
                  />
                )}
              </button>

              {!isOwner && !permsLoading && (
                <div className="mt-2 ml-0.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 border border-blue-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  <span className="text-[10px] font-medium text-blue-700 tracking-wide">Membro equipe</span>
                </div>
              )}

              {showSwitcher && weddings && weddings.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Fechar"
                    className="fixed inset-0 z-40 cursor-default"
                    onClick={() => setShowSwitcher(false)}
                  />
                  <div
                    className="absolute left-3 right-3 top-full mt-2 bg-white rounded-2xl border border-zinc-200/70 shadow-[0_24px_60px_-20px_rgba(24,24,27,0.30)] z-50 overflow-hidden"
                    data-testid="sidebar-event-switcher-dropdown"
                    role="menu"
                  >
                    <div className="px-3 pt-3 pb-2 border-b border-zinc-100/80">
                      <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-zinc-400">
                        Meu espaço · {weddings.length} eventos
                      </p>
                    </div>
                    <div className="max-h-[300px] overflow-y-auto py-1">
                      {weddings.map(ev => {
                        const isActive = ev.id === wedding?.id;
                        const coverUrl = ev.imagem_capa_url || ev.cover_image_url || ev.cover_url;
                        const coverSrc = coverUrl ? (coverUrl.startsWith("http") ? coverUrl : `${API.replace(/\/api$/, "")}${coverUrl}`) : null;
                        const evLabel = EVENT_TYPE_LABELS[ev.event_type] || ev.event_type || "Evento";
                        const dateStr = ev.event_date ? (() => {
                          const iso = ev.event_date.includes('T') ? ev.event_date : `${ev.event_date}T12:00:00`;
                          return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
                        })() : null;
                        const initials = (ev.event_name || ev.partner1_name || "E")
                          .split(/[\s&]+/).filter(Boolean).slice(0, 2)
                          .map(s => s.charAt(0).toUpperCase()).join("") || "E";
                        return (
                          <button
                            key={ev.id}
                            onClick={async () => {
                              if (isActive) { setShowSwitcher(false); return; }
                              await setActiveEvent(ev.id);
                              setShowSwitcher(false);
                            }}
                            className={`group w-full text-left px-2 py-2 mx-1 rounded-xl flex items-center gap-2.5 transition-colors ${isActive ? 'bg-violet-50/60' : 'hover:bg-zinc-50'}`}
                            data-testid={`sidebar-switch-to-event-${ev.id}`}
                            role="menuitem"
                          >
                            {coverSrc ? (
                              <img src={coverSrc} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            ) : (
                              <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[10.5px] font-bold"
                                style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 60%, #FFB347 100%)" }}
                              >
                                {initials}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-[12.5px] truncate ${isActive ? 'font-semibold text-zinc-900' : 'font-medium text-zinc-800'}`}>
                                {ev.event_name || ev.partner1_name || 'Meu Evento'}
                              </p>
                              <p className="text-[10.5px] text-zinc-500 mt-0.5 truncate">
                                <span className="uppercase tracking-wider">{evLabel}</span>
                                {dateStr && <><span className="mx-1 text-zinc-300">·</span>{dateStr}</>}
                              </p>
                            </div>
                            {isActive && (
                              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-violet-600 text-white flex-shrink-0">
                                <Check size={10} strokeWidth={3} />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {canCreateEvent && (
                      <div className="px-2 py-2 border-t border-zinc-100/80 bg-zinc-50/30">
                        <button
                          type="button"
                          onClick={() => { setShowSwitcher(false); navigate('/my-events'); setIsOpen(false); }}
                          className="w-full inline-flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-zinc-700 hover:text-zinc-900 hover:bg-white transition-colors text-left"
                          data-testid="sidebar-switcher-new-event"
                        >
                          <span className="w-6 h-6 rounded-lg bg-zinc-900 text-white flex items-center justify-center flex-shrink-0">
                            <Plus size={12} strokeWidth={2.6} />
                          </span>
                          <span className="text-[12px] font-medium">Criar novo evento</span>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {menuItems.map(item => {
              const isActive = location.pathname === item.path;
              const blocked = (item.ownerOnly && wedding?.id && !permsLoading && !isOwner)
                || (item.module && wedding?.id && !permsLoading && !isOwner && !can(item.module, "view"));
              const c = COLOR_MAP[item.color] || COLOR_MAP.violet;
              if (blocked) {
                return (
                  <div
                    key={item.path}
                    data-testid={`sidebar-blocked-${item.module}`}
                    title="Sem acesso"
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-zinc-300 cursor-not-allowed select-none"
                  >
                    <div className="w-9 h-9 rounded-xl bg-zinc-50 flex items-center justify-center">
                      <item.icon size={17} strokeWidth={1.8} />
                    </div>
                    <span className="text-[13.5px] flex-1 truncate">{item.label}</span>
                    <span className="text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-400 font-medium">Sem acesso</span>
                  </div>
                );
              }
              if (item.locked) {
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => openPlanLock()}
                    data-testid={`sidebar-locked-${item.path.replace('/', '')}`}
                    title="Disponível no plano Plus"
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl opacity-60 hover:opacity-90 hover:bg-zinc-50 transition-all duration-150 active:scale-[0.98] group"
                  >
                    <div className={`w-9 h-9 rounded-xl ${c.bg} flex items-center justify-center relative`}>
                      <item.icon size={17} className={c.icon} strokeWidth={2} />
                    </div>
                    <span className="text-[13.5px] font-semibold flex-1 text-left text-zinc-500">{item.label}</span>
                    <Lock size={13} className="text-zinc-400" />
                  </button>
                );
              }
              if (isActive) {
                return (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setIsOpen(false); }}
                    data-tour={item.tourId}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-2xl text-white shadow-[0_8px_24px_-8px_rgba(107,61,246,0.5)] transition-all duration-150 active:scale-[0.98]"
                    style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)" }}
                    data-testid={`sidebar-active-${item.path.replace('/', '')}`}
                  >
                    <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                      <item.icon size={17} className="text-white" strokeWidth={2} />
                    </div>
                    <span className="text-[14px] font-bold tracking-tight flex-1 text-left">{item.label}</span>
                  </button>
                );
              }
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    if (item.soon) {
                      toast.info(`${item.label} em breve. Aguarde novidades!`);
                      return;
                    }
                    navigate(item.path);
                    setIsOpen(false);
                  }}
                  data-tour={item.tourId}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl ${item.soon ? 'text-zinc-500' : 'text-zinc-900 hover:bg-zinc-50'} transition-all duration-150 active:scale-[0.98] group`}
                  data-testid={`sidebar-item-${item.path.replace('/', '')}`}
                >
                  <div className={`w-9 h-9 rounded-xl ${c.bg} flex items-center justify-center transition-transform group-hover:scale-105 ${item.soon ? 'opacity-60' : ''}`}>
                    <item.icon size={17} className={c.icon} strokeWidth={2} />
                  </div>
                  <span className={`text-[13.5px] font-semibold flex-1 text-left ${item.soon ? 'text-zinc-500' : 'text-zinc-800'}`}>{item.label}</span>
                  {item.soon ? (
                    <span className="text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-violet-100 text-violet-700">
                      Em breve
                    </span>
                  ) : (
                    <ChevronRight size={14} className="text-zinc-300 group-hover:text-zinc-500 transition-colors" />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Assistente IA — card destacado no rodapé */}
          <div className="px-3 pt-1">
            <button
              onClick={() => { navigate('/ai-assistant'); setIsOpen(false); }}
              className="w-full flex items-center gap-3 p-3 rounded-2xl bg-violet-50/60 border border-violet-100 hover:bg-violet-50 transition-all group"
              data-testid="sidebar-ai-assistant"
            >
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                <Sparkles size={18} className="text-violet-500" strokeWidth={2.2} />
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="text-[13px] font-extrabold text-zinc-900 leading-tight">Assistente IA</p>
                <p className="text-[10.5px] text-zinc-500 mt-0.5 leading-tight truncate">Seu assistente para planejar eventos</p>
              </div>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-[0_4px_12px_-4px_rgba(107,61,246,0.6)] group-hover:scale-105 transition-transform" style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #8B5CF6 100%)" }}>
                <ArrowRight size={14} strokeWidth={2.5} />
              </div>
            </button>
          </div>

          {(user?.subscription === 'free' && !isAdminUser(user)) && (
            <div className="p-3 pt-2">
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-50 to-violet-50 border border-violet-100">
                <p className="font-bold text-[13px] text-zinc-900">Upgrade para Pro</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">Recursos ilimitados</p>
                <button 
                  className="mt-2.5 w-full px-3 py-2 rounded-lg bg-zinc-900 text-white text-[12.5px] font-bold hover:bg-zinc-800 transition-colors"
                  onClick={() => navigate('/upgrade')}
                  data-testid="sidebar-upgrade-btn"
                >
                  Ver planos
                </button>
              </div>
            </div>
          )}

          <div className="p-3 border-t border-zinc-200/60">
            <div className="flex items-center gap-3 p-2">
              <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center">
                <span className="text-zinc-700 font-bold text-sm">{user?.name?.charAt(0)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-900 truncate">{user?.name}</p>
                <div className="mt-0.5">
                  <PlanBadge user={user} size="xs" />
                </div>
              </div>
              <button onClick={logout} className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-400 hover:text-zinc-600 transition-colors" data-testid="sidebar-logout">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </React.Fragment>
  );
}

// Sidebar memoizado para evitar re-renders
const MemoizedSidebar = memo(Sidebar);

// ============================================================
// MOBILE NAVIGATION — Bottom Nav + "Mais" Drawer (Phase 1)
// ============================================================

function MobileBottomNav({ onOpenMore }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [createOpen, setCreateOpen] = useState(false);
  const tabs = [
    { icon: Home, label: "Painel", path: "/dashboard" },
    { icon: CreditCard, label: "Despesas", path: "/expenses" },
    { center: true },
    { icon: Users, label: "Convidados", path: "/guests" },
    { icon: Menu, label: "Mais", action: "more" },
  ];
  return (
    <>
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-[10000] bg-white border-t border-zinc-200/80 pb-safe shadow-[0_-2px_12px_-4px_rgba(24,24,27,0.08)]"
        data-bottom-nav
        style={{ fontFamily: "'Poppins', sans-serif" }}
        data-testid="mobile-bottom-nav"
      >
        <div className="grid grid-cols-5" style={{ height: 'var(--bottom-nav-height)' }}>
          {tabs.map((t) => {
            if (t.center) {
              return (
                <div key="center" className="flex items-center justify-center">
                  <button
                    onClick={() => setCreateOpen(true)}
                    className="relative -mt-7 w-14 h-14 rounded-full flex items-center justify-center shadow-[0_10px_26px_-6px_rgba(236,72,153,0.6)] active:scale-95 transition-transform"
                    style={{ backgroundImage: 'linear-gradient(160deg,#7C3AED 0%,#EC4899 55%,#FB7185 100%)' }}
                    aria-label="Criar novo"
                    data-testid="nav-create"
                  >
                    <Plus size={26} className="text-white" strokeWidth={2.4} />
                  </button>
                </div>
              );
            }
            const active = t.path && (location.pathname === t.path || (t.path === '/dashboard' && location.pathname === '/'));
            const onClick = () => {
              if (t.action === 'more') { onOpenMore(); return; }
              navigate(t.path);
            };
            return (
              <button
                key={t.label}
                onClick={onClick}
                className="tap-scale flex flex-col items-center justify-center gap-1 active:bg-zinc-50 transition-colors"
                data-testid={`nav-${t.label.toLowerCase()}`}
              >
                <div className={`relative flex items-center justify-center transition-all ${active ? 'text-zinc-900' : 'text-zinc-400'}`}>
                  <t.icon size={22} strokeWidth={active ? 2 : 1.6} />
                  {active && <span className="absolute -bottom-1.5 w-1 h-1 rounded-full bg-brand-deep" />}
                </div>
                <span className={`text-[10.5px] leading-none ${active ? 'text-zinc-900 font-medium' : 'text-zinc-400'}`}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
      <MobileQuickCreate open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}

function MobileQuickCreate({ open, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;
  const actions = [
    { icon: Users, label: "Convidado", desc: "Adicionar à lista", path: "/guests", grad: "linear-gradient(135deg,#F97316,#FB7185)" },
    { icon: CreditCard, label: "Despesa", desc: "Registrar gasto", path: "/expenses", grad: "linear-gradient(135deg,#0EA5E9,#6366F1)" },
    { icon: Calendar, label: "Tarefa", desc: "Nova tarefa", path: "/tasks", grad: "linear-gradient(135deg,#EC4899,#A855F7)" },
    { icon: FolderOpen, label: "Evento", desc: "Criar evento", path: "/my-events", grad: "linear-gradient(135deg,#7C3AED,#6D28D9)" },
  ];
  const go = (p) => { navigate(p); onClose(); };
  return (
    <div className="lg:hidden fixed inset-0 z-[10001]" style={{ fontFamily: "'Poppins', sans-serif" }} data-testid="mobile-quick-create">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} style={{ animation: 'fadeIn 200ms ease-out' }} />
      <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-2xl pb-safe" style={{ animation: 'slideUp 280ms cubic-bezier(.22,.9,.32,1)' }}>
        <div className="sheet-handle" />
        <div className="px-5 pt-1 pb-1">
          <p className="text-[16px] font-bold text-zinc-900 tracking-tight">Criar novo</p>
          <p className="text-[12px] text-zinc-500">O que você quer adicionar?</p>
        </div>
        <div className="px-4 pt-3 pb-5 grid grid-cols-2 gap-2.5">
          {actions.map((a) => (
            <button key={a.path} onClick={() => go(a.path)} className="flex items-center gap-3 p-3.5 rounded-2xl bg-zinc-50 active:scale-[0.97] transition-transform text-left" data-testid={`quick-create-${a.path.replace('/', '')}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundImage: a.grad }}>
                <a.icon size={18} className="text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-zinc-900 leading-tight">{a.label}</p>
                <p className="text-[11px] text-zinc-500 leading-tight">{a.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileMoreDrawer({ open, onClose }) {
  const { user, logout } = useAuth();
  const capabilities = useCapabilities();
  const navigate = useNavigate();
  const location = useLocation();
  const { openPlanLock } = usePlanUpgrade();
  if (!open) return null;
  // Navegação principal (antes na bottom nav) + secundária
  // Navegação em grid (mesma ordem do menu lateral) + lista inferior (config/suporte)
  const canPro = capabilities.can_access_pro_features;
  const navItems = [
    { icon: Home, label: "Painel", path: "/dashboard" },
    { icon: Wallet, label: "Minhas Receitas", path: "/revenue", locked: !capabilities.can_see_revenues },
    { icon: FolderOpen, label: "Meus Eventos", path: "/my-events", locked: !capabilities.can_create_events },
    { icon: FileText, label: "Fornecedores", path: "/budgets" },
    { icon: CreditCard, label: "Despesas", path: "/expenses" },
    { icon: Receipt, label: "Fluxo de caixa", path: "/cashflow" },
    ...(canPro ? [{ icon: FileBarChart, label: "Relatórios", path: "/reports" }] : []),
    { icon: Sparkles, label: "Inspirações", path: "/inspirations", locked: !capabilities.can_access_inspirations },
    { icon: Calendar, label: "Tarefas", path: "/tasks" },
    { icon: Users, label: "Convidados", path: "/guests" },
    { icon: MessageCircle, label: "WhatsApp", path: "/whatsapp" },
    { icon: LayoutGrid, label: "Mesas & Assentos", path: "/organizar-mesas", locked: !capabilities.can_access_seating },
    { icon: UserCheck, label: "Check-in", path: "/checkin-live", locked: !capabilities.can_access_checkin },
    { icon: Shield, label: "Retirada de crianças", path: "/child-checkout", locked: !capabilities.can_access_checkin },
    { icon: Sparkles, label: "Assistente IA", path: "/ai-assistant" },
  ];
  const listItems = [
    { icon: Settings, label: "Configurações", path: "/settings" },
    { icon: HelpCircle, label: "Suporte", path: "/support" },
  ];
  const go = (path) => { navigate(path); onClose(); };
  const isActive = (path) => location.pathname === path || (path === '/dashboard' && location.pathname === '/');
  return (
    <div className="lg:hidden fixed inset-0 z-[10001]" style={{ fontFamily: "'Poppins', sans-serif" }} data-testid="mobile-more-drawer">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in"
        onClick={onClose}
        style={{ animation: 'fadeIn 200ms ease-out' }}
      />
      <div
        className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-2xl pb-safe overflow-hidden"
        style={{ animation: 'slideUp 280ms cubic-bezier(.22,.9,.32,1)' }}
      >
        <div className="sheet-handle" />
        {/* Header — marca clicável (→ Painel) + fechar */}
        <div className="px-5 pt-1 pb-4 flex items-center justify-between">
          <button
            onClick={() => go('/dashboard')}
            className="flex items-center gap-2.5 active:scale-95 transition-transform"
            aria-label="Ir para o Painel"
            data-testid="drawer-brand-home"
          >
            <LiventyLogoMark size={30} />
            <LiventyWordmark height={19} variant="dark" />
          </button>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 active:scale-95 transition-transform"
            data-testid="close-more-drawer"
          >
            <X size={17} />
          </button>
        </div>
        <div className="px-4 pb-2 max-h-[72vh] overflow-y-auto">
          {/* Grid 2 colunas — cartões estilo app nativo */}
          <div className="grid grid-cols-2 gap-2.5">
            {navItems.map((it) => {
              const active = isActive(it.path);
              if (it.locked) {
                return (
                  <button
                    key={it.path}
                    onClick={() => { onClose?.(); openPlanLock(); }}
                    className="flex items-center gap-3 px-3.5 py-3.5 rounded-2xl text-left transition-all active:scale-[0.97] bg-zinc-50 opacity-60"
                    data-testid={`drawer-locked-${it.path.replace('/', '')}`}
                  >
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)] relative">
                      <it.icon size={17} strokeWidth={1.8} className="text-zinc-500" />
                    </div>
                    <span className="text-[12.5px] font-semibold leading-tight text-zinc-500 flex-1">{it.label}</span>
                    <Lock size={12} className="text-zinc-400" />
                  </button>
                );
              }
              return (
                <button
                  key={it.path}
                  onClick={() => go(it.path)}
                  className={`flex items-center gap-3 px-3.5 py-3.5 rounded-2xl text-left transition-all active:scale-[0.97] ${active ? 'bg-zinc-900 shadow-[0_8px_22px_-8px_rgba(0,0,0,0.45)]' : 'bg-zinc-50 hover:bg-zinc-100'}`}
                  data-testid={`drawer-nav-${it.path.replace('/', '')}`}
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${active ? 'bg-white/15' : 'bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)]'}`}>
                    <it.icon size={17} strokeWidth={1.8} className={active ? 'text-white' : 'text-zinc-700'} />
                  </div>
                  <span className={`text-[12.5px] font-semibold leading-tight ${active ? 'text-white' : 'text-zinc-800'}`}>{it.label}</span>
                </button>
              );
            })}
          </div>

          {/* Lista inferior — Configurações / Suporte */}
          <div className="mt-4 grid grid-cols-1 divide-y divide-zinc-100 border-t border-zinc-100">
            {listItems.map((it) => (
              <button
                key={it.path}
                onClick={() => go(it.path)}
                className="w-full flex items-center gap-3.5 px-2 py-3.5 active:bg-zinc-50 transition-colors text-left"
                data-testid={`drawer-${it.path.replace('/', '')}`}
              >
                <div className="w-9 h-9 rounded-xl bg-zinc-100 flex items-center justify-center flex-shrink-0">
                  <it.icon size={17} className="text-zinc-600" strokeWidth={1.7} />
                </div>
                <span className="flex-1 text-[14px] font-medium text-zinc-800">{it.label}</span>
                <ChevronRight size={15} className="text-zinc-300" />
              </button>
            ))}
          </div>
          {user?.subscription === 'free' && !isAdminUser(user) && (
            <button
              onClick={() => go('/upgrade')}
              className="mt-3 w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-brand-deep text-white tap-scale"
              data-testid="drawer-upgrade"
            >
              <Crown size={16} className="text-amber-300" />
              <span className="flex-1 text-[14px] font-medium text-left">Upgrade para Pro</span>
              <ChevronRight size={15} className="text-white/60" />
            </button>
          )}
          <button
            onClick={() => { logout(); onClose(); }}
            className="mt-3 w-full flex items-center gap-3 px-3 py-3.5 rounded-xl text-rose-600 hover:bg-rose-50 tap-scale"
            data-testid="drawer-logout"
          >
            <div className="w-9 h-9 rounded-xl bg-rose-50 flex items-center justify-center">
              <LogOut size={16} />
            </div>
            <span className="flex-1 text-[14px] font-medium text-left">Sair</span>
          </button>
          <div className="px-3 pt-4 pb-2 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center">
              <span className="text-[12px] font-medium text-zinc-700">{user?.name?.charAt(0) || 'N'}</span>
            </div>
            <div className="min-w-0">
              <p className="text-[12.5px] font-medium text-zinc-900 truncate">{user?.name || 'Usuário'}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <PlanBadge user={user} size="xs" />
                <span className="text-[10.5px] text-zinc-400 truncate">{user?.email}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
      `}</style>
    </div>
  );
}

// Dashboard Page
// Event type config
const EVENT_TYPES = [
  { value: "birthday", label: "Aniversário", icon: "gift" },
  { value: "wedding", label: "Casamento", icon: "heart" },
  { value: "corporate", label: "Corporativo", icon: "building" },
  { value: "graduation", label: "Formatura", icon: "award" },
  { value: "other", label: "Outro", icon: "sparkles" }
];

const EVENT_TYPE_LABELS = {
  birthday: "Aniversário",
  wedding: "Casamento",
  corporate: "Corporativo",
  graduation: "Formatura",
  other: "Outro",
  casamento: "Casamento",
  aniversario: "Aniversário",
  corporativo: "Corporativo",
  formatura: "Formatura"
};

// Create Event Modal
function CreateEventModal({ isOpen, onClose, onCreated }) {
  const [eventType, setEventType] = useState("");
  const [eventName, setEventName] = useState("");
  const [eventBudget, setEventBudget] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [creating, setCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { createEvent } = useWedding();

  const handleCreate = async () => {
    if (!eventType) {
      toast.error("Selecione o tipo de evento");
      return;
    }
    setCreating(true);
    try {
      const budget = parseFloat(eventBudget.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
      const result = await createEvent({
        name: eventName.trim() || null,
        type: eventType,
        budget,
        event_date: eventDate || null
      });
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        if (onCreated) onCreated(result);
        setEventType("");
        setEventName("");
        setEventBudget("");
        setEventDate("");
      }, 1500);
    } catch (err) {
      const msg = getErrorMessage(err, "Erro ao criar evento");
      if (msg) toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 text-center" style={{ fontFamily: "'Poppins', sans-serif" }}>
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
            <Check className="text-emerald-500" size={32} />
          </div>
          <h3 className="text-lg font-semibold tracking-tight text-zinc-900 mb-2">Evento criado com sucesso!</h3>
          <p className="text-sm text-zinc-500">Preparando seu painel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="create-event-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl ring-1 ring-zinc-200/60 overflow-hidden" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-200/60">
          <div>
            <h2 className="text-[17px] font-semibold tracking-tight text-zinc-900">Criar novo evento</h2>
            <p className="text-[12.5px] text-zinc-500 mt-0.5">Preencha as informações básicas para começar</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="close-create-modal">
            <X size={18} className="text-zinc-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Tipo de Evento */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-2.5">Tipo de evento</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {EVENT_TYPES.map(t => (
                <button
                  key={t.value}
                  onClick={() => setEventType(t.value)}
                  className={`px-3 py-2.5 rounded-xl border text-[13px] font-medium transition-all ${
                    eventType === t.value
                      ? 'border-zinc-900 bg-zinc-900 text-white'
                      : 'border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400'
                  }`}
                  data-testid={`event-type-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Nome do Evento */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-2">Nome do evento</label>
            <input
              type="text"
              value={eventName}
              onChange={e => setEventName(e.target.value)}
              placeholder={eventType ? `Ex: ${EVENT_TYPE_LABELS[eventType] || "Meu evento"} do João` : "Ex: Aniversário 30 anos"}
              className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 bg-white focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 text-[13.5px] transition-all"
              data-testid="event-name-input"
            />
            <p className="text-[11.5px] text-zinc-400 mt-1.5">Se vazio, usaremos um nome padrão</p>
          </div>

          {/* Data do Evento */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-2">Data do evento</label>
            <input
              type="date"
              value={eventDate}
              onChange={e => setEventDate(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 bg-white focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 text-[13.5px] transition-all"
              data-testid="event-date-input"
            />
          </div>

          {/* Orçamento */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-2">Orçamento previsto</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13.5px] text-zinc-500 pointer-events-none">R$</span>
              <input
                type="text"
                value={eventBudget}
                onChange={e => {
                  const val = e.target.value.replace(/[^\d.,]/g, '');
                  setEventBudget(val);
                }}
                placeholder="15.000"
                className="w-full pl-10 pr-3.5 py-2.5 rounded-xl border border-zinc-200 bg-white focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 text-[13.5px] transition-all"
                data-testid="event-budget-input"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-200/60 bg-zinc-50/50">
          <button
            onClick={handleCreate}
            disabled={!eventType || creating}
            className="w-full py-3 rounded-xl text-[14px] font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{backgroundImage: 'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)', boxShadow: '0 10px 30px -10px rgba(107,61,246,0.55)'}}
            data-testid="create-event-submit"
          >
            {creating ? (
              <>
                <Loader2 className="animate-spin" size={16} />
                <span>Criando...</span>
              </>
            ) : (
              <>
                <Plus size={16} />
                <span>Criar evento</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * QuickCreateEventModal — modal estilo Google Calendar/Teams.
 * Minimal por default (tipo, nome, data, horário, cliente) + "+ Detalhes" colapsável
 * (orçamento, observações). Cria via mesmo endpoint /events/create (createEvent).
 */
// Converte os `terms` do PaymentTermsForm no formato de parcelas esperado pela Receita
// ({ installments: [{ amount, due_date }] }). Entrada/sinal vira a 1ª parcela.
function termsToRevenueInstallments(terms, total, fallbackDate) {
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  if (!terms || terms.type === "avista") {
    return { installments: [{ amount: round2(total), due_date: (terms && terms.payment_date) || fallbackDate }] };
  }
  const list = [];
  if (terms.has_down_payment && terms.down_payment && Number(terms.down_payment.amount) > 0) {
    list.push({ amount: round2(terms.down_payment.amount), due_date: terms.down_payment.date || fallbackDate });
  }
  (terms.installments || []).forEach((inst) => {
    list.push({ amount: round2(inst.value || 0), due_date: inst.due_date || fallbackDate });
  });
  if (list.length === 0) list.push({ amount: round2(total), due_date: fallbackDate });
  return { installments: list };
}

function QuickCreateEventModal({ isOpen, onClose, prefillDate, onCreated }) {
  const { createEvent, createEventFromRevenue } = useWedding();
  const [type, setType] = useState("");
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [timeStart, setTimeStart] = useState("");
  const [timeEnd, setTimeEnd] = useState("");
  const [client, setClient] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [budget, setBudget] = useState("");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  // Custo do serviço de organização (vira Receita) + plano de pagamento
  const [serviceCost, setServiceCost] = useState(0);
  const [serviceTerms, setServiceTerms] = useState({ type: "avista", method: "pix" });

  useEffect(() => {
    if (isOpen) {
      setDate(prefillDate || "");
      setType("");
      setName("");
      setTimeStart("");
      setTimeEnd("");
      setClient("");
      setBudget("");
      setNotes("");
      setShowMore(false);
      setServiceCost(0);
      setServiceTerms({ type: "avista", method: "pix" });
    }
  }, [isOpen, prefillDate]);

  if (!isOpen) return null;

  const formatDateLabel = (iso) => {
    if (!iso) return "";
    try {
      const [y, m, d] = iso.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      return dt.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
    } catch { return iso; }
  };

  const handleCreate = async () => {
    if (!type) { toast.error("Selecione o tipo do evento"); return; }
    if (!date) { toast.error("Selecione a data"); return; }
    const cost = Number(serviceCost) || 0;
    if (cost > 0 && !client.trim()) {
      setShowMore(true);
      toast.error("Informe o nome do cliente para registrar a receita");
      return;
    }
    setCreating(true);
    try {
      const budgetNum = parseFloat(String(budget).replace(/[^\d.,]/g, "").replace(",", ".")) || 0;
      // Compõe um nome inteligente quando vazio: "Tipo · Cliente · HH:mm"
      const composedName = (name || "").trim() || [
        EVENT_TYPE_LABELS[type] || "Evento",
        client.trim(),
        timeStart,
      ].filter(Boolean).join(" · ");

      const eventData = {
        name: composedName,
        type,
        budget: budgetNum,
        event_date: date,
        event_time: timeStart || null,
        event_end_time: timeEnd || null,
        client_name: client.trim() || null,
        notes: notes.trim() || null,
      };

      if (cost > 0) {
        // Cria evento + receita (custo do serviço com plano de pagamento) numa só transação
        await createEventFromRevenue({
          event: eventData,
          revenue: {
            client_name: client.trim(),
            event_name: composedName,
            event_date: date,
            total_amount: cost,
            notes: "Custo do serviço de organização",
            ...termsToRevenueInstallments(serviceTerms, cost, date),
          },
          standalone: false,
        });
        toast.success("Evento criado e receita registrada!");
      } else {
        await createEvent(eventData);
        toast.success("Evento criado na agenda!");
      }
      onClose();
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao criar evento"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="quick-create-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-[26px] w-full max-w-md shadow-2xl ring-1 ring-zinc-200/60 overflow-hidden" style={{ fontFamily: "'Poppins', sans-serif", animation: "confirm-pop 240ms cubic-bezier(0.16,1,0.3,1)" }}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-200/60 flex items-start justify-between">
          <div>
            <h2 className="text-[16px] font-semibold tracking-tight text-zinc-900">Criar evento na agenda</h2>
            <p className="text-[12px] text-zinc-500 mt-0.5 capitalize">{formatDateLabel(date) || "Selecione a data"}</p>
          </div>
          <button onClick={onClose} className="p-1.5 -mr-1.5 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="quick-close-btn">
            <X size={17} className="text-zinc-500" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3.5 max-h-[70vh] overflow-y-auto">
          {/* Tipo */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-2">Tipo de evento</label>
            <div className="grid grid-cols-3 gap-1.5">
              {EVENT_TYPES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setType(t.value)}
                  className={`px-2 py-2 rounded-lg border text-[12px] font-medium transition-all ${
                    type === t.value ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"
                  }`}
                  data-testid={`quick-type-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Nome */}
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-1.5">Nome do evento</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Casamento João & Maria"
              className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 bg-white focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 text-[13.5px] transition-all"
              data-testid="quick-name-input"
            />
          </div>

          {/* Data + Horário lado-a-lado */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-1.5">Data</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 bg-white focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 text-[13.5px] transition-all"
                data-testid="quick-date-input"
              />
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-1.5">Início</label>
              <input
                type="time"
                value={timeStart}
                onChange={(e) => setTimeStart(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 bg-white focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 text-[13.5px] transition-all"
                data-testid="quick-timestart-input"
              />
            </div>
          </div>

          {/* Fim + Cliente */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-1.5">Término</label>
              <input
                type="time"
                value={timeEnd}
                onChange={(e) => setTimeEnd(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 bg-white focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 text-[13.5px] transition-all"
                data-testid="quick-timeend-input"
              />
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-1.5">Cliente</label>
              <input
                type="text"
                value={client}
                onChange={(e) => setClient(e.target.value)}
                placeholder="Nome do cliente"
                className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 bg-white focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 text-[13.5px] transition-all"
                data-testid="quick-client-input"
              />
            </div>
          </div>

          {/* + Detalhes (collapse) */}
          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-50 hover:bg-zinc-100 text-[12.5px] font-medium text-zinc-700 transition-colors"
            data-testid="quick-toggle-more"
          >
            <span>{showMore ? "− Menos opções" : "+ Detalhes"}</span>
            <ChevronDown size={14} className={`text-zinc-500 transition-transform ${showMore ? 'rotate-180' : ''}`} />
          </button>

          {showMore && (
            <div className="space-y-3 pt-1">
              <div>
                <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-1.5">Orçamento previsto</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13.5px] text-zinc-500 pointer-events-none">R$</span>
                  <input
                    type="text"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value.replace(/[^\d.,]/g, ""))}
                    placeholder="15.000"
                    className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-zinc-200 bg-white focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 text-[13.5px] transition-all"
                    data-testid="quick-budget-input"
                  />
                </div>
              </div>
              {/* Custo do serviço de organização → vira Receita automaticamente */}
              <div className="rounded-2xl bg-violet-50/40 ring-1 ring-violet-100 p-3 space-y-2.5" data-testid="quick-service-section">
                <div className="flex items-center gap-2">
                  <DollarSign size={14} className="text-violet-500" />
                  <span className="text-[12.5px] font-semibold text-zinc-800">Custo do serviço de organização</span>
                </div>
                <p className="text-[11px] leading-relaxed text-zinc-500">Quanto você cobra para organizar este evento. Vira automaticamente uma receita em <span className="font-semibold text-zinc-700">Minhas Receitas</span>, vinculada ao cliente e ao evento.</p>
                <MoneyInput label="Valor do serviço (R$)" value={serviceCost} onChange={setServiceCost} />
                {Number(serviceCost) > 0 && (
                  <div className="pt-1" data-testid="quick-service-terms">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">Plano de pagamento</p>
                    <PaymentTermsForm totalAmount={Number(serviceCost) || 0} value={serviceTerms} onChange={setServiceTerms} />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-1.5">Observações</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Endereço, contato, observações…"
                  className="w-full px-3 py-2 rounded-lg border border-zinc-200 bg-white focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 text-[13.5px] transition-all resize-none"
                  data-testid="quick-notes-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-200/60 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-medium text-zinc-600 hover:bg-zinc-100 transition-colors" data-testid="quick-cancel-btn">
            Cancelar
          </button>
          <button
            onClick={handleCreate}
            disabled={creating || !type || !date}
            className="px-4 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white text-[13px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5 transition-colors"
            data-testid="quick-create-btn"
          >
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Criar evento
          </button>
        </div>
      </div>
    </div>
  );
}


// =====================================================================
// 🟢 LiveToasts — pop-ups "ao vivo" no Dashboard
// Escuta o WebSocket /ws/checkin/{wedding_id} (já existente) e mostra
// toasts elegantes para 3 eventos: RSVP confirmado / RSVP recusado / Check-in.
// Apenas no Dashboard inicialmente — ver decisão de produto.
// =====================================================================
function LiveToasts({ weddingId }) {
  const [toasts, setToasts] = useState([]);
  const seenRef = useRef(new Set());

  const pushToast = useCallback((t) => {
    setToasts((arr) => [...arr.slice(-3), t]);
    setTimeout(() => {
      setToasts((arr) => arr.filter((x) => x.id !== t.id));
    }, 6000);
  }, []);

  // Polling leve a cada 8s para detectar mudanças de RSVP e check-ins recentes.
  // Estratégia escolhida em vez de WebSocket porque o ingress K8s só roteia /api/*
  // e nesta versão do produto a latência ~8s é totalmente aceitável para "ao vivo".
  useEffect(() => {
    if (!weddingId) return undefined;
    let cancelled = false;
    // baseline: na primeira chamada apenas registra o estado atual (sem disparar toast)
    const initRef = { done: false };
    const lastSnapRef = { current: new Map() }; // invite_id -> { status, last_checkin_at }

    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/weddings/${weddingId}/invites`);
        const data = r.data;
        const arr = Array.isArray(data) ? data : (data?.invites || []);
        const newSnap = new Map();
        const events = [];
        for (const inv of arr) {
          const id = inv.id;
          const status = inv.status; // 'confirmado' | 'recusado' | 'a_confirmar'
          const lastCheckin = inv.last_checkin_at || null;
          newSnap.set(id, { status, lastCheckin });
          if (initRef.done) {
            const prev = lastSnapRef.current.get(id);
            if (prev) {
              // mudança de status
              if (prev.status !== status) {
                if (status === "confirmado") {
                  events.push({ kind: "confirmed", name: inv.name || "Convidado", qty: inv.total_people || 1, key: `c-${id}-${Date.now()}` });
                } else if (status === "recusado") {
                  events.push({ kind: "declined", name: inv.name || "Convidado", qty: inv.total_people || 1, key: `d-${id}-${Date.now()}` });
                }
              }
              // novo check-in
              if (lastCheckin && prev.lastCheckin !== lastCheckin) {
                events.push({ kind: "checkin", name: inv.name || "Convidado", qty: inv.total_people || 1, key: `ck-${id}-${lastCheckin}` });
              }
            }
          }
        }
        lastSnapRef.current = newSnap;
        if (!initRef.done) {
          initRef.done = true;
        } else {
          // dispara toasts agrupados (máx 3 para não floodar)
          for (const ev of events.slice(0, 3)) {
            if (seenRef.current.has(ev.key)) continue;
            seenRef.current.add(ev.key);
            pushToast({ id: ev.key, kind: ev.kind, name: ev.name, qty: ev.qty });
          }
        }
      } catch { /* silencia */ }
    };
    // Primeiro tick imediato (baseline)
    tick();
    const interval = setInterval(tick, 8000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [weddingId, pushToast]);

  if (toasts.length === 0) return null;

  const META = {
    confirmed: { icon: Check,    bg: "bg-emerald-500", label: "confirmou presença" },
    declined:  { icon: X,        bg: "bg-rose-500",    label: "recusou o convite" },
    checkin:   { icon: UserCheck, bg: "bg-violet-500", label: "fez check-in" },
  };

  return (
    <div
      className="fixed z-[9999] flex flex-col gap-2 pointer-events-none bottom-20 right-4 md:bottom-6 md:right-6 max-w-[calc(100vw-2rem)] md:max-w-[360px]"
      data-testid="live-toasts"
      aria-live="polite"
    >
      {toasts.map((t) => {
        const m = META[t.kind] || META.confirmed;
        const Icon = m.icon;
        return (
          <div
            key={t.id}
            className="pointer-events-auto flex items-center gap-3 pl-3 pr-4 py-2.5 bg-white border border-zinc-200 rounded-2xl shadow-[0_12px_32px_-12px_rgba(24,24,27,0.18)] animate-in slide-in-from-right-4 fade-in duration-300"
            data-testid={`live-toast-${t.kind}`}
          >
            <div className={`w-8 h-8 rounded-full ${m.bg} text-white flex items-center justify-center shrink-0`}>
              <Icon size={16} strokeWidth={2.5} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-zinc-900 truncate">{t.name}</p>
              <p className="text-[11.5px] text-zinc-500 truncate">
                {m.label} · <span className="tabular-nums">{t.qty}</span> {t.qty === 1 ? "pessoa" : "pessoas"}
              </p>
            </div>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" title="ao vivo" />
          </div>
        );
      })}
    </div>
  );
}


// =====================================================================
// 📱 MobileDashboard — versão mobile premium do Painel
// Visual: hero gradient LIVENTY (claro/lilás) + glass cards leves + CTA grande.
// Inspirado em: Linear light, Stripe, Notion Calendar, Arc browser.
// NÃO afetar desktop — renderização condicional via isMobile.
// =====================================================================
function MobileDashboard({ user, wedding, weddings, invites, budget, tasks, daysUntil, onSwitchEvent, onCreate, onOpenEvent }) {
  const navigate = useNavigate();
  const firstName = (user?.name || "").split(" ")[0] || "você";
  const [alertData, setAlertData] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState(false);
  useEffect(() => {
    if (!wedding?.id) return undefined;
    let active = true;
    axios.get(`${API}/weddings/${wedding.id}/alerts`)
      .then((r) => { if (active) setAlertData(r.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [wedding?.id]);
  const alertCount = alertData?.total_alerts || 0;
  const avatarUrl = user?.avatar_url || user?.photo_url || user?.profile_photo || user?.picture || null;
  const initials = (user?.name || "U").split(" ").filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U";

  const fmtBRLShort = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1000) return `R$ ${(n / 1000).toFixed(1).replace(".", ",")}k`;
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
  };
  const fmtDateLong = (iso) => {
    if (!iso) return "Sem data";
    try {
      const d = new Date(iso.length <= 10 ? iso + "T12:00:00" : iso);
      return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return "Sem data"; }
  };

  const eventName = wedding?.event_name || wedding?.partner1_name || "Meu Evento";
  const hasMultiple = Array.isArray(weddings) && weddings.length > 1;

  // Encontra próximos eventos futuros, ordenados por event_date ASC + event_time ASC.
  // Limita a 3 no mobile (resto vai pra "Ver todos" em /my-events).
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const _timeToMin = (t) => {
    if (!t || typeof t !== "string") return 9999;
    const m = t.match(/^(\d{1,2}):(\d{2})/);
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : 9999;
  };
  const upcomingEventsAll = (Array.isArray(weddings) ? weddings : [])
    .filter(w => {
      if (!w.event_date) return false;
      try { return new Date(w.event_date + "T23:59:59") >= today; } catch { return false; }
    })
    .sort((a, b) => {
      const cmp = (a.event_date || "").localeCompare(b.event_date || "");
      if (cmp !== 0) return cmp;
      return _timeToMin(a.event_time) - _timeToMin(b.event_time);
    });
  const upcomingEvents = upcomingEventsAll.slice(0, 3);
  const nextEvent = upcomingEvents[0] || wedding;
  const hasMoreEvents = upcomingEventsAll.length > 3 || (Array.isArray(weddings) && weddings.length > upcomingEvents.length);

  // Badge elegante de status — baseado em proximidade real do evento
  const eventBadge = (ev) => {
    if (!ev?.event_date) return null;
    if (ev.status === "completed") return { label: "Finalizado", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200/60" };
    try {
      const d = new Date(ev.event_date.length <= 10 ? ev.event_date + "T12:00:00" : ev.event_date);
      const diff = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
      if (diff < 0) return { label: "Atrasado", cls: "bg-rose-50 text-rose-700 ring-rose-200/60" };
      if (diff === 0) return { label: "Hoje", cls: "bg-rose-100 text-rose-700 ring-rose-300/60" };
      if (diff <= 7) return { label: "Esta semana", cls: "bg-amber-50 text-amber-700 ring-amber-200/60" };
      if (diff <= 30) return { label: "Em breve", cls: "bg-violet-50 text-violet-700 ring-violet-200/60" };
      return { label: "Planejamento", cls: "bg-zinc-100 text-zinc-600 ring-zinc-200/60" };
    } catch { return null; }
  };

  const eventDaysLabel = (ev) => {
    if (!ev?.event_date) return null;
    try {
      const d = new Date(ev.event_date.length <= 10 ? ev.event_date + "T12:00:00" : ev.event_date);
      const diff = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
      if (diff < 0) return null;
      if (diff === 0) return "Hoje!";
      return `em ${diff} ${diff === 1 ? "dia" : "dias"}`;
    } catch { return null; }
  };
  const nextEventDays = (() => {
    if (!nextEvent?.event_date) return null;
    try {
      const d = new Date(nextEvent.event_date.length <= 10 ? nextEvent.event_date + "T12:00:00" : nextEvent.event_date);
      const diff = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
      return diff >= 0 ? diff : null;
    } catch { return null; }
  })();

  const StatCard = ({ icon: Icon, label, value, sub, tint, onClick, testid }) => (
    <button
      type="button"
      onClick={() => { try { navigator.vibrate?.(6); } catch {} onClick && onClick(); }}
      className="relative text-left p-4 rounded-3xl bg-white border border-zinc-100/80 shadow-[0_2px_14px_rgba(20,16,35,0.05)] active:scale-[0.98] transition-transform"
      data-testid={testid}
    >
      <div className={`w-10 h-10 rounded-2xl ${tint.bg} flex items-center justify-center mb-3`}>
        <Icon size={18} strokeWidth={2.2} className={tint.text} />
      </div>
      <p className="text-[26px] font-semibold text-zinc-900 tracking-tight leading-none tabular-nums">{value}</p>
      <p className="text-[12px] text-zinc-500 mt-1 font-medium">{label}</p>
      {sub && <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{sub}</p>}
    </button>
  );

  return (
    <div className="md:hidden -mx-4 -mt-4 mb-4" data-testid="mobile-dashboard">
      {/* Sheet de Alertas (sino) */}
      {alertsOpen && (
        <div className="md:hidden fixed inset-0 z-[10002]" style={{ fontFamily: "'Poppins', sans-serif" }} data-testid="mobile-alerts-sheet">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setAlertsOpen(false)} style={{ animation: 'fadeIn 200ms ease-out' }} />
          <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl shadow-2xl pb-safe max-h-[80vh] flex flex-col" style={{ animation: 'slideUp 280ms cubic-bezier(.22,.9,.32,1)' }}>
            <div className="sheet-handle" />
            <div className="px-5 pt-1 pb-3 flex items-center justify-between">
              <div>
                <p className="text-[16px] font-bold text-zinc-900 tracking-tight">Alertas</p>
                <p className="text-[12px] text-zinc-500">{alertCount > 0 ? `${alertCount} pendência(s)` : "Tudo em dia ✨"}</p>
              </div>
              <button onClick={() => setAlertsOpen(false)} className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 active:scale-95 transition-transform" data-testid="close-alerts-sheet"><X size={17} /></button>
            </div>
            <div className="px-4 pb-5 overflow-y-auto">
              {(!alertData || alertCount === 0) ? (
                <div className="py-10 text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-3"><Check size={26} className="text-emerald-500" /></div>
                  <p className="text-[13.5px] font-semibold text-zinc-800">Nenhum alerta no momento</p>
                  <p className="text-[12px] text-zinc-500 mt-1">Despesas e tarefas em dia.</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {alertData.alerts.map((al, i) => (
                    <button
                      key={al.id || i}
                      onClick={() => { setAlertsOpen(false); navigate(al.type === 'expense' ? '/cashflow' : '/tasks'); }}
                      className={`w-full text-left p-3.5 rounded-2xl flex items-start gap-3 ${al.alert_type === 'urgent' ? 'bg-rose-50 ring-1 ring-rose-100' : 'bg-amber-50 ring-1 ring-amber-100'}`}
                      data-testid={`alert-item-${i}`}
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${al.alert_type === 'urgent' ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'}`}>
                        {al.type === 'expense' ? <DollarSign size={17} /> : <Calendar size={17} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-zinc-900 truncate">{al.type === 'expense' ? al.vendor_name : al.task_name}</p>
                        {al.type === 'expense' && al.amount != null && (
                          <p className="text-[12.5px] font-bold text-zinc-700 mt-0.5">R$ {al.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                        )}
                        <span className={`inline-block mt-1.5 text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${al.alert_type === 'urgent' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>{al.days_text}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* HERO superior: gradient full-bleed deep purple, cobre safe-area + status bar */}
      <div
        className="relative overflow-hidden rounded-b-[44px] pb-14 px-5 pt-safe"
        style={{ backgroundImage: "linear-gradient(160deg, #1E0A3C 0%, #2E1065 25%, #4C1D95 55%, #6D28D9 88%, #8B5CF6 100%)" }}
      >
        {/* Radial glows orgânicos — luz indireta atrás do painel */}
        <span aria-hidden className="absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full opacity-50 blur-[80px]"
              style={{ backgroundImage: "radial-gradient(circle, rgba(167,139,250,0.55) 0%, rgba(76,29,149,0.30) 45%, transparent 75%)" }} />
        <span aria-hidden className="absolute -bottom-40 -right-24 w-[380px] h-[380px] rounded-full opacity-90 blur-[70px]"
              style={{ backgroundImage: "radial-gradient(circle, rgba(139,92,246,0.65) 0%, rgba(109,40,217,0.35) 40%, transparent 75%)" }} />
        <span aria-hidden className="absolute top-1/3 right-1/4 w-[160px] h-[160px] rounded-full opacity-40 blur-[60px]"
              style={{ backgroundImage: "radial-gradient(circle, rgba(244,114,182,0.40) 0%, transparent 70%)" }} />

        {/* Header: Logo "L" + wordmark + hamburger flutuante */}
        <div className="relative flex items-center justify-between pt-3 mb-6" data-testid="mobile-brand-wordmark">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 active:scale-95 transition-transform"
            aria-label="Ir para o Painel"
            data-testid="mobile-hero-brand-home"
          >
            <LiventyLogoMark size={32} bare />
            <LiventyWordmark height={22} variant="white" />
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/settings')}
              className="w-11 h-11 rounded-full overflow-hidden border border-white/25 bg-white/10 active:scale-95 transition-all shadow-[0_4px_16px_-4px_rgba(0,0,0,0.25)]"
              aria-label="Meu perfil"
              data-testid="mobile-hero-avatar"
            >
              {avatarUrl
                ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                : <span className="w-full h-full flex items-center justify-center text-white font-bold text-[15px]">{initials}</span>}
            </button>
            <button
              onClick={() => setAlertsOpen(true)}
              className="relative w-11 h-11 rounded-full bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/15 active:scale-95 transition-all shadow-[0_4px_16px_-4px_rgba(0,0,0,0.25)]"
              aria-label="Alertas"
              data-testid="mobile-hero-bell"
            >
              <Bell size={19} className="text-white" strokeWidth={2} />
              {alertCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#2E1065]">
                  {alertCount > 9 ? '9+' : alertCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Saudação */}
        <div className="relative">
          <h1 className="text-[32px] font-semibold tracking-tight text-white leading-[1.05]">
            Olá, {firstName}! <span className="inline-block">👋</span>
          </h1>
          <p className="text-[15px] text-violet-200/85 mt-1.5 font-light">Que bom ter você por aqui!</p>
        </div>

        {/* Botão Trocar Evento (mais leve, transparente) */}
        {wedding && (
          <button
            type="button"
            onClick={onSwitchEvent}
            className="mt-6 w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/[0.07] backdrop-blur-sm border border-white/[0.10] hover:bg-white/[0.10] active:scale-[0.99] transition-all"
            data-testid="mobile-event-switcher"
          >
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-[0_4px_12px_-4px_rgba(244,114,182,0.5)]" style={{ backgroundImage: "linear-gradient(135deg, #FB923C 0%, #F472B6 100%)" }}>
              <Heart size={15} className="text-white" fill="white" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-violet-200/70">Evento atual</p>
              <p className="text-[14.5px] font-semibold text-white truncate leading-tight">{eventName}</p>
              {daysUntil !== null && daysUntil >= 0 && (
                <p className="text-[11px] font-medium text-violet-200/80 mt-0.5">
                  {daysUntil === 0 ? "É hoje! 🎉" : daysUntil === 1 ? "Falta 1 dia" : `Faltam ${daysUntil} dias`}
                </p>
              )}
            </div>
            {hasMultiple && <ChevronDown size={16} className="text-white/50 flex-shrink-0" />}
          </button>
        )}
      </div>

      {/* GRID 2x2 — KPI cards iOS-style suaves */}
      <div className="px-4 -mt-7 grid grid-cols-2 gap-3 relative z-10">
        <StatCard
          icon={Users}
          label="Convidados"
          value={invites.total_people || 0}
          sub={`${invites.confirmed_people || 0} confirmados`}
          tint={{ bg: "bg-orange-50", text: "text-orange-500" }}
          onClick={() => navigate("/guests")}
          testid="mob-stat-guests"
        />
        <StatCard
          icon={DollarSign}
          label="Orçamento total"
          value={fmtBRLShort(budget.total_budget)}
          sub={`${fmtBRLShort(budget.total_paid)} pago`}
          tint={{ bg: "bg-emerald-50", text: "text-emerald-500" }}
          onClick={() => navigate("/expenses")}
          testid="mob-stat-budget-total"
        />
        <StatCard
          icon={Wallet}
          label="Disponível"
          value={fmtBRLShort(budget.remaining)}
          sub={(() => {
            const total = budget.total_budget || 0;
            const paid = budget.total_paid || 0;
            const pct = total > 0 ? Math.min(100, Math.round(((total - paid) / total) * 100)) : 100;
            return total > 0 ? `${pct}% restante` : "Sem orçamento";
          })()}
          tint={{ bg: "bg-blue-50", text: "text-blue-500" }}
          onClick={() => navigate("/expenses")}
          testid="mob-stat-budget"
        />
        <StatCard
          icon={CheckSquare}
          label="Tarefas"
          value={`${tasks.completed}/${tasks.total}`}
          sub={`${tasks.progress}% concluído`}
          tint={{ bg: "bg-pink-50", text: "text-pink-500" }}
          onClick={() => navigate("/tasks")}
          testid="mob-stat-tasks"
        />
      </div>

      {/* Planejamento do evento — barra de evolução (% concluído) */}
      <div className="px-4 mt-3">
        <div className="p-4 rounded-3xl bg-white border border-zinc-100/80 shadow-[0_2px_14px_rgba(20,16,35,0.05)]" data-testid="mob-event-progress">
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[13.5px] font-semibold text-zinc-900">Planejamento do evento</p>
            <span className="text-[15px] font-bold text-violet-600 tabular-nums">{tasks.progress || 0}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-zinc-100 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, tasks.progress || 0)}%`, backgroundImage: "linear-gradient(90deg,#7C3AED,#EC4899)" }} />
          </div>
          <p className="text-[11.5px] text-zinc-500 mt-2">{tasks.completed || 0} de {tasks.total || 0} tarefas concluídas</p>
        </div>
      </div>

      {/* Orçamento utilizado — barra orçamento x utilizado */}
      <div className="px-4 mt-3">
        <div className="p-4 rounded-3xl bg-white border border-zinc-100/80 shadow-[0_2px_14px_rgba(20,16,35,0.05)]" data-testid="mob-budget-used">
          {(() => {
            const total = budget.total_budget || 0;
            const used = Math.max(0, total - (budget.remaining || 0));
            const pct = total > 0 ? Math.round((used / total) * 100) : 0;
            const over = pct > 100;
            const near = pct > 75 && pct <= 100;
            const fill = over ? "#ef4444" : near ? "#f59e0b" : "#22c55e";
            const status = over ? "Acima do planejado" : near ? "Atenção ao limite" : "Dentro do planejado";
            const statusColor = over ? "text-rose-600" : near ? "text-amber-600" : "text-emerald-600";
            return (
              <>
                <div className="flex items-start justify-between mb-2.5">
                  <div>
                    <p className="text-[13.5px] font-semibold text-zinc-900">Orçamento utilizado</p>
                    <p className="text-[11.5px] text-zinc-500 mt-0.5">{fmtBRLShort(used)} usados de {fmtBRLShort(total)}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-[15px] font-bold tabular-nums ${statusColor}`}>{pct}%</p>
                    <p className={`text-[10.5px] font-medium ${statusColor}`}>{status}</p>
                  </div>
                </div>
                <div className="h-2.5 rounded-full bg-zinc-100 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: fill }} />
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* PRÓXIMOS EVENTOS — lista vertical (até 3) com badges premium */}
      {upcomingEvents.length > 0 && (
        <div className="mt-7 px-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[15px] font-semibold tracking-tight text-zinc-900" data-testid="mob-upcoming-events-title">
              Próximos Eventos
              {upcomingEvents.length > 1 && (
                <span className="ml-2 text-[11px] font-medium text-zinc-400 tabular-nums">{upcomingEvents.length}</span>
              )}
            </h2>
            {hasMoreEvents && (
              <button
                onClick={() => navigate("/my-events")}
                className="text-[12.5px] font-semibold text-violet-600 inline-flex items-center gap-0.5"
                data-testid="mob-upcoming-see-all"
              >
                Ver todos <ChevronRight size={14} strokeWidth={2.5} />
              </button>
            )}
          </div>
          <div className="space-y-2.5">
            {upcomingEvents.map((ev, idx) => {
              const badge = eventBadge(ev);
              const daysLbl = eventDaysLabel(ev);
              const isFirst = idx === 0;
              return (
                <button
                  type="button"
                  key={ev.id}
                  onClick={async () => { try { navigator.vibrate?.(6); } catch {} onOpenEvent && onOpenEvent(ev); }}
                  className={`block w-full text-left rounded-3xl overflow-hidden bg-white border ${isFirst ? "border-zinc-100 shadow-[0_10px_30px_-12px_rgba(24,24,27,0.10)]" : "border-zinc-100/80 shadow-[0_2px_10px_-4px_rgba(24,24,27,0.05)]"} active:scale-[0.99] transition-transform`}
                  data-testid={isFirst ? "mob-next-event-card" : `mob-upcoming-event-${ev.id}`}
                >
                  <div className="flex items-stretch">
                    <div className="relative w-[88px] flex-shrink-0">
                      <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 60%, #FFB347 100%)" }} />
                      {(ev.cover_url || ev.imagem_capa_url || ev.cover_image_url) && (
                        <img
                          src={(() => { const u = ev.cover_url || ev.imagem_capa_url || ev.cover_image_url; return u.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`; })()}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                    </div>
                    <div className="flex-1 p-4 min-w-0">
                      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                        {isFirst && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-[10.5px] font-semibold tracking-wide">
                            <Sparkles size={10} /> próximo
                          </span>
                        )}
                        {badge && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide ring-1 ${badge.cls}`}>
                            {badge.label}
                          </span>
                        )}
                      </div>
                      <p className="text-[15.5px] font-semibold text-zinc-900 leading-tight line-clamp-1">
                        {ev.event_name || ev.partner1_name || "Meu Evento"}
                      </p>
                      <p className="text-[11.5px] text-zinc-500 mt-1">
                        {fmtDateLong(ev.event_date)}
                        {ev.event_time && <> <span className="text-zinc-300">·</span> <span className="font-medium text-zinc-700 tabular-nums">{ev.event_time}h</span></>}
                        {daysLbl && (
                          <> · <span className="font-semibold text-emerald-600">{daysLbl}</span></>
                        )}
                      </p>
                      {ev.venue_name && (
                        <p className="text-[11px] text-zinc-400 mt-0.5 truncate flex items-center gap-1">
                          <MapPin size={10} strokeWidth={2} /> {ev.venue_name}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* CTA Novo Evento — gradient violet→pink→orange (oculto para staff/colaborador) */}
      {!(user?.user_type === "colaborador" && !user?.account_owner) && (
      <div className="mt-5 px-4 pb-2">
        <button
          type="button"
          onClick={() => { try { navigator.vibrate?.(6); } catch {} onCreate && onCreate(); }}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-2xl text-white text-[15px] font-semibold shadow-[0_14px_36px_-12px_rgba(168,85,247,0.55)] active:scale-[0.99] transition-transform"
          style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FF9248 100%)" }}
          data-testid="mob-create-event-cta"
        >
          <Plus size={18} strokeWidth={2.6} />
          Novo Evento
        </button>
      </div>
      )}
    </div>
  );
}



// =============================================================
// 💸 ExpenseRemindersBanner — Iter 79
// Banner leve e lazy de despesas vencendo/vencidas. Substitui o
// antigo "Insights Liventy" (que rodava ai/insights pesado).
//
// Performance:
//   • 1 fetch GET com axios cancelToken
//   • Disparado em requestIdleCallback (ou setTimeout 0) para
//     NÃO bloquear a renderização inicial do dashboard
//   • Filtragem local (sem analytics)
//   • Some sozinho quando vazio (returns null)
//   • Apenas para owner — staff vê null
// =============================================================
function ExpenseRemindersBanner({ weddingId, userIsOwner }) {
  const [buckets, setBuckets] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!weddingId || !userIsOwner) return;

    const controller = new AbortController();
    let cancelled = false;

    const run = async () => {
      try {
        const res = await axios.get(
          `${API}/weddings/${weddingId}/expenses`,
          { signal: controller.signal }
        );
        if (cancelled) return;
        const items = Array.isArray(res.data) ? res.data : [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const in3Days = new Date(today);
        in3Days.setDate(in3Days.getDate() + 3);

        const overdue = [], dueToday = [], dueSoon = [];
        for (const exp of items) {
          if (exp.paid || exp.status === "paid") continue;
          const dueRaw = exp.due_date || exp.dueDate;
          if (!dueRaw) continue;
          const iso = String(dueRaw).includes("T") ? dueRaw : `${dueRaw}T12:00:00`;
          const dt = new Date(iso);
          if (Number.isNaN(dt.getTime())) continue;
          dt.setHours(0, 0, 0, 0);
          if (dt < today) overdue.push(exp);
          else if (dt.getTime() === today.getTime()) dueToday.push(exp);
          else if (dt <= in3Days) dueSoon.push(exp);
        }
        setBuckets({ overdue, dueToday, dueSoon });
      } catch (err) {
        // silently — banner some
        if (!cancelled) setBuckets({ overdue: [], dueToday: [], dueSoon: [] });
      }
    };

    // ⏰ Lazy: dispara DEPOIS que o dashboard montou para não atrasar 1º paint
    const idleId = (typeof window !== "undefined" && "requestIdleCallback" in window)
      ? window.requestIdleCallback(run, { timeout: 1500 })
      : setTimeout(run, 250);

    return () => {
      cancelled = true;
      controller.abort();
      if (typeof window !== "undefined" && "cancelIdleCallback" in window && typeof idleId === "number") {
        try { window.cancelIdleCallback(idleId); } catch {}
      } else {
        try { clearTimeout(idleId); } catch {}
      }
    };
  }, [weddingId, userIsOwner]);

  // ⛔ Staff (guest mode) NÃO vê
  if (!userIsOwner) return null;
  if (!buckets) return null;
  const total = buckets.overdue.length + buckets.dueToday.length + buckets.dueSoon.length;
  if (total === 0) return null;

  // Prioridade visual: overdue → dueToday → dueSoon
  let icon, label, accent, bg, ring, count;
  if (buckets.overdue.length > 0) {
    icon = "🔴"; label = "vencida"; count = buckets.overdue.length;
    accent = "text-rose-900"; bg = "bg-rose-50"; ring = "ring-rose-200";
  } else if (buckets.dueToday.length > 0) {
    icon = "🟡"; label = "vencendo hoje"; count = buckets.dueToday.length;
    accent = "text-amber-900"; bg = "bg-amber-50"; ring = "ring-amber-200";
  } else {
    icon = "🔵"; label = "vencendo nos próximos 3 dias"; count = buckets.dueSoon.length;
    accent = "text-sky-900"; bg = "bg-sky-50"; ring = "ring-sky-200";
  }

  return (
    <button
      type="button"
      onClick={() => navigate("/expenses")}
      className={`group w-full ${bg} ring-1 ${ring} rounded-2xl px-4 py-3 flex items-center gap-3 text-left transition-transform active:scale-[0.995]`}
      data-testid="expense-reminders-banner"
    >
      <span aria-hidden="true" className="text-[18px] leading-none">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className={`text-[13.5px] font-semibold ${accent} truncate`}>
          {count} {count === 1 ? "despesa" : "despesas"} {label}
        </p>
        <p className={`text-[11.5px] ${accent}/70 mt-0.5`}>
          Toque para ver detalhes →
        </p>
      </div>
      <span className={`text-[11px] font-semibold ${accent} uppercase tracking-[0.12em] px-2 py-1 rounded-md bg-white/60`}>
        Ver despesas
      </span>
    </button>
  );
}



function DashboardPage() {
  const { wedding, weddings, createEvent, setActiveEvent } = useWedding();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEventSwitcher, setShowEventSwitcher] = useState(false);

  // 🪟 Workspace context (Fase 4 — Iter 77)
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(user?.active_workspace_id || 'personal');
  const [showWorkspaceSwitcher, setShowWorkspaceSwitcher] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/workspaces`);
        if (!cancel) setWorkspaces(Array.isArray(r.data) ? r.data : []);
      } catch { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    setActiveWorkspaceId(user?.active_workspace_id || 'personal');
  }, [user?.active_workspace_id]);

  const hasMultipleWorkspaces = workspaces.length >= 2;
  const activeWorkspace = workspaces.find(w => w.id === activeWorkspaceId) || workspaces.find(w => w.id === 'personal') || null;

  const handleSwitchWorkspace = async (wsId) => {
    if (wsId === activeWorkspaceId) { setShowWorkspaceSwitcher(false); return; }
    try {
      await axios.put(`${API}/auth/active-workspace`, { workspace_id: wsId });
      setActiveWorkspaceId(wsId);
      setShowWorkspaceSwitcher(false);
      try { await refreshUser?.(); } catch { /* ignore */ }
      // forçar refresh dos eventos
      try { window.location.reload(); } catch { /* ignore */ }
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao trocar workspace"));
    }
  };

  // Body lock + hide app chrome quando picker fullscreen mobile estiver aberto
  useEffect(() => {
    if (!showEventSwitcher) return undefined;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) return undefined;
    const html = document.documentElement;
    const prevOverflow = document.body.style.overflow;
    html.classList.add('app-modal-open');
    document.body.style.overflow = 'hidden';
    return () => {
      html.classList.remove('app-modal-open');
      document.body.style.overflow = prevOverflow;
    };
  }, [showEventSwitcher]);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [insights, setInsights] = useState([]); // 🗑 Iter 79 — vazio (mantido só p/ não quebrar referências; bloco removido do DOM)
  const [loadingInsights] = useState(false);
  const [showInsights] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 🔒 RBAC Iter 74: Staff (user_type=colaborador) só pode criar eventos se
  // for account_owner=true (Pro próprio). Backend é fonte da verdade — esta
  // checagem só esconde a UI redundante (defense-in-depth).
  const isStaffAccount = user?.user_type === "colaborador" && !user?.account_owner;
  const canCreateEvent = user?.profile_type === "professional" && !isStaffAccount;

  // Buscar dashboard LOCAL - não no context
  const fetchDashboard = useCallback(async () => {
    if (!wedding?.id) return;
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/dashboard`);
      setDashboard(res.data);
    } catch (err) {
      // silently handle
    }
  }, [wedding?.id]);

  // Buscar alertas de despesas e tarefas (lógica sem IA)
  const fetchAlerts = useCallback(async () => {
    if (!wedding?.id) return;
    setLoadingAlerts(true);
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/alerts`);
      setAlerts(res.data);
    } catch (err) {
      // silently handle
    } finally {
      setLoadingAlerts(false);
    }
  }, [wedding?.id]);
  
  // 🗑 Iter 79 — fetchInsights removido (substituído por ExpenseRemindersBanner)
  const fetchInsights = useCallback(() => { /* noop */ }, []);

  // Carregar dados do dashboard APENAS quando a página montar
  useEffect(() => {
    let mounted = true;
    
    const loadData = async () => {
      if (!wedding?.id) return;
      setIsLoading(true);
      try {
        await fetchDashboard();
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    
    loadData();
    return () => { mounted = false; };
  }, [wedding?.id, fetchDashboard]);

  // Carregar alertas (insights removidos — substituídos por ExpenseRemindersBanner leve)
  useEffect(() => {
    if (dashboard?.wedding?.id) {
      fetchAlerts();
    }
  }, [dashboard?.wedding?.id, fetchAlerts]);

  // Skeleton loading enquanto carrega dados
  if (weddings !== null && weddings.length === 0) {
    // 🛡 Staff que não é account_owner não pode criar evento — UX redirecionando
    // de volta ao workspace do owner se existir, caso contrário mensagem clara.
    const isStaffNoOwner = user?.user_type === "colaborador" && !user?.account_owner;
    const otherWorkspaces = workspaces.filter(w => w.id !== "personal");
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4" style={{ fontFamily: "'Poppins', sans-serif" }} data-testid="empty-state-dashboard">
        <div className="w-20 h-20 rounded-3xl bg-zinc-100 flex items-center justify-center mb-6 ring-1 ring-zinc-200/60">
          <Calendar className="text-zinc-700" size={32} strokeWidth={1.5} />
        </div>
        <h2 className="text-[22px] font-semibold tracking-tight text-zinc-900 mb-2 text-center">
          {isStaffNoOwner && otherWorkspaces.length > 0
            ? "Seu espaço pessoal está vazio"
            : "Você ainda não tem eventos criados"}
        </h2>
        <p className="text-[13.5px] text-zinc-500 mb-8 text-center max-w-md">
          {isStaffNoOwner && otherWorkspaces.length > 0
            ? "Você é membro de equipe em outro espaço. Volte para acessar os eventos atribuídos."
            : "Crie seu primeiro evento para começar a organizar tudo em um só lugar"}
        </p>

        {/* 🔄 Iter 79 — botões para voltar a qualquer workspace que o staff seja membro */}
        {otherWorkspaces.length > 0 && (
          <div className="w-full max-w-md mb-4 space-y-2" data-testid="empty-state-workspace-switcher">
            {otherWorkspaces.map(ws => (
              <button
                key={ws.id}
                type="button"
                onClick={() => handleSwitchWorkspace(ws.id)}
                className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white border border-zinc-200 hover:border-violet-300 hover:bg-violet-50/20 transition-colors text-left"
                data-testid={`empty-state-switch-to-${ws.id}`}
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                  style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 60%, #FFB347 100%)" }}
                >
                  {(ws.name || "?").substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-zinc-900 truncate">Voltar para {ws.name}</p>
                  <p className="text-[11px] text-zinc-500">{ws.event_count || 0} evento(s)</p>
                </div>
                <ArrowRight size={14} className="text-zinc-400" />
              </button>
            ))}
          </div>
        )}

        {!isStaffNoOwner && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-7 py-3.5 rounded-xl text-white text-[14px] font-semibold transition-all flex items-center gap-2 hover:scale-[1.02]"
            style={{backgroundImage: 'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)', boxShadow: '0 12px 32px -10px rgba(107,61,246,0.55)'}}
            data-testid="create-first-event-btn"
          >
            <Plus size={18} />
            <span>Criar Evento</span>
          </button>
        )}
        <CreateEventModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { toast.success("Evento criado!"); }}
        />
      </div>
    );
  }

  if (isLoading || !dashboard) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-32 mb-2"></div>
          <div className="h-5 bg-slate-200 rounded w-64"></div>
        </div>
        
        {/* Stats Grid Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-slate-200"></div>
                <div className="flex-1">
                  <div className="h-8 bg-slate-200 rounded w-16 mb-2"></div>
                  <div className="h-4 bg-slate-200 rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Content Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-40 mb-4"></div>
            <div className="h-48 bg-slate-200 rounded"></div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-40 mb-4"></div>
            <div className="h-48 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const { wedding: weddingData, invites, budget, tasks, limits } = dashboard;

  // Calculate days until wedding - usando função helper sem timezone
  const daysUntil = calculateDaysUntil(weddingData.event_date);

  // Budget breakdown for donut chart
  const budgetData = [
    { label: "Pago", value: budget.total_paid, color: "#22c55e" },
    { label: "Comprometido", value: budget.total_committed - budget.total_paid, color: "#f59e0b" },
    { label: "Disponível", value: budget.remaining, color: "#3b82f6" }
  ];

  // Guests breakdown for donut chart
  const guestData = [
    { label: "Adultos", value: invites.total_adults, color: "#3b82f6" },
    { label: "Crianças", value: invites.total_children, color: "#06b6d4" }
  ];

  // Expenses by category for donut chart — paleta premium (sem preto)
  const expensesPalette = ["#635BFF", "#7C3AED", "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#ec4899", "#0ea5e9", "#84cc16", "#6366f1"];
  const rawExpenses = Array.isArray(dashboard?.expenses_by_category) ? dashboard.expenses_by_category : [];
  const expensesData = rawExpenses.slice(0, 9).map((c, i) => ({ label: c.name, value: c.amount, color: expensesPalette[i % expensesPalette.length], count: c.count }));
  if (rawExpenses.length > 9) {
    const rest = rawExpenses.slice(9).reduce((acc, c) => ({ amount: acc.amount + (c.amount || 0), count: acc.count + (c.count || 0) }), { amount: 0, count: 0 });
    if (rest.amount > 0) expensesData.push({ label: "Outros", value: rest.amount, color: "#a1a1aa", count: rest.count });
  }
  const expensesTotal = expensesData.reduce((s, d) => s + d.value, 0);

  return (
    <div className="space-y-10">
      {/* 🟢 Pop-ups "ao vivo" — RSVP & check-in em tempo real (apenas Dashboard) */}
      <LiveToasts weddingId={wedding?.id} />

      {/* 🎟️ Onda 1.5 — Card Informativo para usuários convidados/staff (acesso derivado de outra assinatura) */}
      {isStaffAccount && <GuestStaffInfoCard user={user} navigate={navigate} />}

      {/* 📱 Mobile: novo Dashboard premium (hero gradient + glass cards) */}
      {isMobile && (
        <MobileDashboard
          user={user}
          wedding={wedding}
          weddings={weddings}
          invites={invites}
          budget={budget}
          tasks={tasks}
          daysUntil={daysUntil}
          onSwitchEvent={() => setShowEventSwitcher(true)}
          onCreate={() => setShowCreateModal(true)}
          onOpenEvent={async (ev) => { await setActiveEvent(ev.id); }}
        />
      )}

      {/* 📱 Mobile: CreateEventModal renderizado FORA do wrapper desktop "hidden"
          para evitar display:none herdado (mesmo padrão do EventSwitcher). */}
      {isMobile && (
        <CreateEventModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { toast.success("Evento criado!"); }}
          data-testid="create-event-modal-mobile"
        />
      )}

      {/* 📱 Mobile Event Switcher Picker — renderizado FORA do wrapper desktop "hidden"
          para funcionar quando isMobile=true (caso contrário, display:none herdado bloqueia o picker). */}
      {isMobile && showEventSwitcher && weddings && weddings.length > 1 && (
        <div
          className="fixed inset-0 z-[10020] flex flex-col bg-white"
          style={{ animation: 'cp-slide-in 220ms cubic-bezier(0.22,1,0.36,1) both' }}
          data-testid="event-switcher-picker-mobile"
        >
          <div
            className="flex items-center gap-2 px-3 border-b border-zinc-100 bg-white"
            style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)', paddingBottom: 8 }}
          >
            <button
              type="button"
              onClick={() => setShowEventSwitcher(false)}
              className="tap-press w-10 h-10 rounded-full flex items-center justify-center text-zinc-700 hover:bg-zinc-100"
              aria-label="Voltar"
              data-testid="event-switcher-back-m"
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className="text-[16px] font-semibold text-zinc-900 tracking-tight flex-1">Trocar evento</h2>
          </div>
          <div
            className="flex-1 overflow-y-auto overscroll-contain pt-3"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}
          >
            <p className="text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] px-5 mb-2">
              {weddings.length} {weddings.length === 1 ? 'evento' : 'eventos'} no workspace
            </p>
            <ul>
              {weddings.map(ev => {
                const isActive = ev.id === wedding?.id;
                const coverUrl = ev.imagem_capa_url || ev.cover_image_url;
                const coverSrc = coverUrl ? (coverUrl.startsWith("http") ? coverUrl : `${API.replace(/\/api$/, "")}${coverUrl}`) : null;
                const evLabel = EVENT_TYPE_LABELS[ev.event_type] || ev.event_type || "Evento";
                const dateStr = ev.event_date ? (() => {
                  const iso = ev.event_date.includes('T') ? ev.event_date : `${ev.event_date}T12:00:00`;
                  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
                })() : null;
                return (
                  <li key={ev.id}>
                    <button
                      type="button"
                      onClick={async () => {
                        try { navigator.vibrate?.(8); } catch {}
                        await setActiveEvent(ev.id);
                        setShowEventSwitcher(false);
                        setIsLoading(true);
                        setDashboard(null);
                      }}
                      className="tap-press w-full flex items-center gap-3 px-5 py-3 text-left"
                      data-testid={`switch-to-event-m-${ev.id}`}
                    >
                      {coverSrc ? (
                        <img src={coverSrc} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}}>
                          <Calendar size={18} className="text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-[15px] truncate ${isActive ? 'font-semibold text-zinc-900' : 'text-zinc-900'}`}>
                          {ev.event_name || ev.partner1_name || 'Meu Evento'}
                        </p>
                        <p className="text-[11.5px] text-zinc-500 mt-0.5 truncate">
                          <span className="uppercase tracking-wider">{evLabel}</span>
                          {dateStr && <><span className="mx-1.5 text-zinc-300">·</span>{dateStr}</>}
                        </p>
                      </div>
                      {isActive ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 px-2 py-1 rounded-full bg-emerald-50">
                          <Check size={11} strokeWidth={3} />
                          Atual
                        </span>
                      ) : (
                        <ChevronRight size={16} className="text-zinc-300 flex-shrink-0" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            {canCreateEvent && (
              <div className="px-5 pt-4 pb-2">
                <button
                  type="button"
                  onClick={() => { setShowEventSwitcher(false); setTimeout(() => setShowCreateModal(true), 220); }}
                  className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border-2 border-dashed border-zinc-300 hover:border-zinc-900 text-zinc-700 hover:text-zinc-900 transition-colors text-left"
                  data-testid="event-switcher-new-event-m"
                >
                  <span className="w-9 h-9 rounded-full bg-zinc-900 text-white flex items-center justify-center flex-shrink-0">
                    <Plus size={16} strokeWidth={2.5} />
                  </span>
                  <span className="flex-1 text-left">
                    <span className="block text-[14px] font-medium">Criar novo evento</span>
                    <span className="block text-[11.5px] text-zinc-500 mt-0.5">Adicionar ao seu espaço</span>
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 💻 Desktop: layout original (preservado) */}
      <div className={isMobile ? "hidden" : "flex flex-col space-y-6"}>
      {/* Header com Event Switcher — chip Notion-style no topo */}
      <div className="flex flex-col gap-4" style={{fontFamily:"'Poppins', sans-serif"}}>
        {/* 🪟 Workspace Switcher (Fase 4 — Iter 77) — visível APENAS se tiver 2+ workspaces (i.e., user é staff de outro owner) */}
        {hasMultipleWorkspaces && activeWorkspace && (
          <div className="relative inline-block self-start" data-testid="ws-switcher-wrap">
            <button
              type="button"
              onClick={() => setShowWorkspaceSwitcher(v => !v)}
              className="group inline-flex items-center gap-2 pl-2 pr-2.5 py-1 rounded-full bg-gradient-to-r from-violet-50 to-pink-50 border border-violet-200/70 hover:border-violet-300 transition-all text-[11px] font-semibold text-violet-900"
              data-testid="ws-switcher-trigger"
            >
              <span
                className="w-5 h-5 rounded-md flex items-center justify-center text-white text-[9px] font-bold"
                style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 60%, #FFB347 100%)" }}
              >
                {activeWorkspace.type === 'personal' ? 'ME' : (activeWorkspace.name || '?').charAt(0).toUpperCase()}
              </span>
              <span className="uppercase tracking-wider">
                {activeWorkspace.type === 'personal' ? 'Meu Espaço' : activeWorkspace.name}
              </span>
              <span className="text-violet-400">·</span>
              <span className="text-violet-700 tabular-nums">{activeWorkspace.event_count} ev.</span>
              <ChevronDown size={11} className={`text-violet-500 transition-transform ${showWorkspaceSwitcher ? 'rotate-180' : ''}`} />
            </button>
            {showWorkspaceSwitcher && (
              <>
                <button
                  type="button"
                  aria-label="Fechar"
                  className="fixed inset-0 z-40 cursor-default"
                  onClick={() => setShowWorkspaceSwitcher(false)}
                />
                <div
                  className="absolute left-0 top-full mt-2 w-[340px] bg-white rounded-2xl border border-zinc-200/70 shadow-[0_24px_60px_-20px_rgba(24,24,27,0.30)] z-50 overflow-hidden"
                  data-testid="ws-switcher-dropdown"
                  role="menu"
                >
                  <div className="px-4 pt-3.5 pb-2 border-b border-zinc-100/80">
                    <p className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-zinc-400">
                      Trocar de espaço · {workspaces.length} disponíveis
                    </p>
                  </div>
                  <div className="max-h-[320px] overflow-y-auto py-1">
                    {workspaces.map(ws => {
                      const isActive = ws.id === activeWorkspaceId;
                      const isPersonal = ws.type === 'personal';
                      const initials = isPersonal
                        ? 'ME'
                        : (ws.name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s.charAt(0).toUpperCase()).join('');
                      return (
                        <button
                          key={ws.id}
                          onClick={() => handleSwitchWorkspace(ws.id)}
                          className={`group w-full text-left px-3 py-2.5 mx-1 rounded-xl flex items-center gap-3 transition-colors ${isActive ? 'bg-violet-50/70' : 'hover:bg-zinc-50'}`}
                          data-testid={`ws-switch-to-${ws.id}`}
                          role="menuitem"
                        >
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-[11px] font-bold"
                            style={{ backgroundImage: isPersonal
                              ? "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 60%, #FFB347 100%)"
                              : "linear-gradient(135deg, #475569 0%, #1e293b 100%)" }}
                          >
                            {initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-[13.5px] truncate ${isActive ? 'font-semibold text-zinc-900' : 'font-medium text-zinc-800'}`}>
                              {isPersonal ? 'Meu Espaço' : ws.name}
                            </p>
                            <p className="text-[11px] text-zinc-500 mt-0.5">
                              <span className="uppercase tracking-wider">{isPersonal ? 'Owner' : ws.role}</span>
                              <span className="mx-1.5 text-zinc-300">·</span>
                              <span className="tabular-nums font-semibold text-zinc-700">{ws.event_count}</span> evento{ws.event_count === 1 ? '' : 's'}
                            </p>
                          </div>
                          {isActive && (
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-violet-600 text-white flex-shrink-0">
                              <Check size={12} strokeWidth={3} />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="px-4 py-2.5 bg-zinc-50/50 border-t border-zinc-100/80">
                    <p className="text-[10.5px] text-zinc-500 leading-snug">
                      💡 Workspaces isolam seus eventos próprios dos eventos onde você atua como equipe.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}


        {/* Linha do título + ações */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Painel</h1>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <p className="text-sm text-zinc-500">{weddingData.event_name || weddingData.partner1_name}</p>
              {daysUntil !== null && daysUntil >= 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-100" data-testid="days-remaining-chip">
                  <Calendar size={11} />
                  {daysUntil === 0 ? "É hoje! 🎉" : daysUntil === 1 ? "Falta 1 dia" : `Faltam ${daysUntil} dias`}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Trocar Evento (legacy mobile switcher dispara aqui) — apenas mobile */}
            {weddings && weddings.length > 1 && (
              <div className="relative md:hidden">
                <button
                  onClick={() => { try { navigator.vibrate?.(6); } catch {} setShowEventSwitcher(!showEventSwitcher); }}
                  className="tap-press flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm text-slate-700"
                  data-testid="switch-event-btn"
                >
                  <RefreshCw size={16} />
                  <span>Trocar evento</span>
                  <ChevronDown size={14} className={`transition-transform ${showEventSwitcher ? 'rotate-180' : ''}`} />
                </button>
                {/* MOBILE picker fullscreen (igual aos outros) */}
                {showEventSwitcher && (
                <div
                  className="md:hidden fixed inset-0 z-[10020] flex flex-col bg-white"
                  style={{ animation: 'cp-slide-in 220ms cubic-bezier(0.22,1,0.36,1) both' }}
                  data-testid="event-switcher-picker-mobile"
                >
                  <div
                    className="flex items-center gap-2 px-3 border-b border-zinc-100 bg-white"
                    style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)', paddingBottom: 8 }}
                  >
                    <button
                      type="button"
                      onClick={() => setShowEventSwitcher(false)}
                      className="tap-press w-10 h-10 rounded-full flex items-center justify-center text-zinc-700 hover:bg-zinc-100"
                      aria-label="Voltar"
                      data-testid="event-switcher-back"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <h2 className="text-[16px] font-semibold text-zinc-900 tracking-tight flex-1">Trocar evento</h2>
                  </div>
                  <div
                    className="flex-1 overflow-y-auto overscroll-contain pt-3"
                    style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}
                  >
                    <p className="text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] px-5 mb-2">
                      {weddings.length} {weddings.length === 1 ? 'evento' : 'eventos'} no workspace
                    </p>
                    <ul>
                      {weddings.map(ev => {
                        const isActive = ev.id === wedding?.id;
                        const coverUrl = ev.imagem_capa_url || ev.cover_image_url;
                        const coverSrc = coverUrl ? (coverUrl.startsWith("http") ? coverUrl : `${API.replace(/\/api$/, "")}${coverUrl}`) : null;
                        const evLabel = EVENT_TYPE_LABELS[ev.event_type] || ev.event_type || "Evento";
                        const dateStr = ev.event_date ? (() => {
                          const iso = ev.event_date.includes('T') ? ev.event_date : `${ev.event_date}T12:00:00`;
                          return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
                        })() : null;
                        return (
                          <li key={ev.id}>
                            <button
                              type="button"
                              onClick={async () => {
                                try { navigator.vibrate?.(8); } catch {}
                                await setActiveEvent(ev.id);
                                setShowEventSwitcher(false);
                                setIsLoading(true);
                                setDashboard(null);
                              }}
                              className="tap-press w-full flex items-center gap-3 px-5 py-3 text-left"
                              data-testid={`switch-to-event-${ev.id}`}
                            >
                              {/* Cover ou ícone gradient */}
                              {coverSrc ? (
                                <img src={coverSrc} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}}>
                                  <Calendar size={18} className="text-white" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className={`text-[15px] truncate ${isActive ? 'font-semibold text-zinc-900' : 'text-zinc-900'}`}>
                                  {ev.event_name || ev.partner1_name || 'Meu Evento'}
                                </p>
                                <p className="text-[11.5px] text-zinc-500 mt-0.5 truncate">
                                  <span className="uppercase tracking-wider">{evLabel}</span>
                                  {dateStr && <><span className="mx-1.5 text-zinc-300">·</span>{dateStr}</>}
                                </p>
                              </div>
                              {isActive ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 px-2 py-1 rounded-full bg-emerald-50">
                                  <Check size={11} strokeWidth={3} />
                                  Atual
                                </span>
                              ) : (
                                <ChevronRight size={16} className="text-zinc-300 flex-shrink-0" />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    {/* Criar novo evento (atalho dentro do picker) */}
                    {canCreateEvent && (
                      <div className="px-5 pt-4 pb-2">
                        <button
                          type="button"
                          onClick={() => { setShowEventSwitcher(false); setTimeout(() => setShowCreateModal(true), 220); }}
                          className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border-2 border-dashed border-zinc-300 hover:border-zinc-900 text-zinc-700 hover:text-zinc-900 transition-colors text-left"
                          data-testid="event-switcher-new-event"
                        >
                          <span className="w-9 h-9 rounded-full bg-zinc-900 text-white flex items-center justify-center flex-shrink-0">
                            <Plus size={16} strokeWidth={2.5} />
                          </span>
                          <span className="flex-1 text-left">
                            <span className="block text-[14px] font-medium">Criar novo evento</span>
                            <span className="block text-[11.5px] text-zinc-500 mt-0.5">Adicionar ao seu espaço</span>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Criar Evento (Professional) */}
          {canCreateEvent && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#4A4543] text-white rounded-xl hover:bg-[#3D3A38] transition-all text-sm"
              data-testid="add-new-event-btn"
            >
              <Plus size={16} />
              <span>Novo evento</span>
            </button>
          )}
        </div>
      </div>
      </div>{/* /header-with-switcher */}

      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => { toast.success("Evento criado!"); }}
      />

      {/* Stats Grid — cards clicáveis (atalhos rápidos) com hover e press feedback */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{fontFamily:"'Poppins', sans-serif"}}>
        <button
          type="button"
          onClick={() => { try { navigator.vibrate?.(6); } catch {} navigate('/guests'); }}
          className="text-left bg-white rounded-2xl p-5 border border-zinc-200/60 shadow-[0_1px_3px_rgba(24,24,27,0.04)] card-hover-brightness card-animate tap-press transition-shadow hover:shadow-[0_6px_18px_-8px_rgba(24,24,27,0.12)]"
          data-testid="stat-guests"
          aria-label="Ver convidados"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center">
              <Users size={14} className="text-orange-500" />
            </div>
            <p className="text-[11px] font-medium text-orange-600 uppercase tracking-wider">Convidados</p>
          </div>
          <p className="text-2xl font-semibold text-zinc-900 tracking-tight tabular-nums">{invites.total_people}</p>
          <p className="text-[11.5px] text-zinc-400 mt-1">{invites.confirmed_people} confirmados</p>
        </button>

        <button
          type="button"
          onClick={() => { try { navigator.vibrate?.(6); } catch {} navigate('/expenses'); }}
          className="text-left bg-white rounded-2xl p-5 border border-zinc-200/60 shadow-[0_1px_3px_rgba(24,24,27,0.04)] card-hover-brightness card-animate tap-press transition-shadow hover:shadow-[0_6px_18px_-8px_rgba(24,24,27,0.12)]"
          data-testid="stat-budget"
          aria-label="Ver orçamento total nas despesas"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
              <DollarSign size={14} className="text-emerald-500" />
            </div>
            <p className="text-[11px] font-medium text-emerald-600 uppercase tracking-wider">Orçamento total</p>
          </div>
          <p className="text-2xl font-semibold text-zinc-900 tracking-tight tabular-nums">{(budget.total_budget || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
          <p className="text-[11.5px] text-zinc-400 mt-1">{(budget.total_paid || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} pago</p>
        </button>

        <button
          type="button"
          onClick={() => { try { navigator.vibrate?.(6); } catch {} navigate('/expenses'); }}
          className="text-left bg-white rounded-2xl p-5 border border-zinc-200/60 shadow-[0_1px_3px_rgba(24,24,27,0.04)] card-hover-brightness card-animate tap-press transition-shadow hover:shadow-[0_6px_18px_-8px_rgba(24,24,27,0.12)]"
          data-testid="stat-budget-available"
          aria-label="Ver orçamento disponível nas despesas"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
              <Wallet size={14} className="text-blue-500" />
            </div>
            <p className="text-[11px] font-medium text-blue-600 uppercase tracking-wider">Disponível</p>
          </div>
          <p className={`text-2xl font-semibold tracking-tight tabular-nums ${(budget.remaining || 0) < 0 ? 'text-rose-600' : 'text-zinc-900'}`}>{(budget.remaining || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
          <p className="text-[11.5px] text-zinc-400 mt-1">de {(budget.total_budget || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
        </button>

        <button
          type="button"
          onClick={() => { try { navigator.vibrate?.(6); } catch {} navigate('/tasks'); }}
          className="text-left bg-white rounded-2xl p-5 border border-zinc-200/60 shadow-[0_1px_3px_rgba(24,24,27,0.04)] card-hover-brightness card-animate tap-press transition-shadow hover:shadow-[0_6px_18px_-8px_rgba(24,24,27,0.12)]"
          data-testid="stat-tasks"
          aria-label="Ver tarefas"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-pink-50 flex items-center justify-center">
              <CheckSquare size={14} className="text-pink-500" />
            </div>
            <p className="text-[11px] font-medium text-pink-600 uppercase tracking-wider">Tarefas</p>
          </div>
          <p className="text-2xl font-semibold text-zinc-900 tracking-tight tabular-nums">{tasks.completed}/{tasks.total}</p>
          <p className="text-[11.5px] text-zinc-400 mt-1">{tasks.progress}% concluído</p>
        </button>
      </div>

      {/* Charts Row — cards INFORMATIVOS (não clicáveis) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{fontFamily:"'Poppins', sans-serif"}}>
        {/* Budget Donut */}
        <div
          className="bg-white rounded-2xl p-6 border border-zinc-200/60 shadow-[0_1px_3px_rgba(24,24,27,0.04)]"
          data-testid="chart-budget-card"
        >
          <h3 className="text-sm font-medium text-zinc-900 mb-4 flex items-center gap-2">
            <PieChart size={16} className="text-zinc-400" />
            <span>Orçamento</span>
          </h3>
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <div className="relative w-36 h-36 sm:w-40 sm:h-40 flex-shrink-0">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                {budgetData.reduce((acc, item, i) => {
                  const total = budgetData.reduce((s, d) => s + d.value, 0) || 1;
                  const startAngle = acc.angle;
                  const angle = (item.value / total) * 360;
                  const endAngle = startAngle + angle;
                  const largeArc = angle > 180 ? 1 : 0;
                  
                  const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                  const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                  const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                  const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
                  
                  acc.paths.push(
                    <path
                      key={i}
                      d={angle >= 360 
                        ? `M 50 10 A 40 40 0 1 1 49.99 10` 
                        : `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                      fill={item.color}
                    />
                  );
                  acc.angle = endAngle;
                  return acc;
                }, { paths: [], angle: 0 }).paths}
                <circle cx="50" cy="50" r="25" fill="white" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xs text-slate-500">Total</span>
                <span className="text-[11px] font-bold text-slate-800">{(budget.total_budget || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
              </div>
            </div>
            <div className="flex-1 w-full space-y-2">
              {budgetData.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></div>
                    <span className="text-sm text-slate-600 truncate">{item.label}</span>
                  </div>
                  <span className="text-sm font-medium text-slate-800 flex-shrink-0 ml-2">{item.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Guests Donut */}
        <div
          className="bg-white rounded-2xl p-6 border border-zinc-200/60 shadow-[0_1px_3px_rgba(24,24,27,0.04)]"
          data-testid="chart-guests-card"
        >
          <h3 className="text-sm font-medium text-zinc-900 mb-4 flex items-center gap-2">
            <Users size={16} className="text-zinc-400" />
            <span>Convidados</span>
          </h3>
          <div className="flex items-center gap-6">
            <div className="relative w-40 h-40">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                {guestData.reduce((acc, item, i) => {
                  const total = guestData.reduce((s, d) => s + d.value, 0) || 1;
                  const startAngle = acc.angle;
                  const angle = (item.value / total) * 360;
                  const endAngle = startAngle + angle;
                  const largeArc = angle > 180 ? 1 : 0;
                  
                  const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                  const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                  const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                  const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
                  
                  if (item.value > 0) {
                    acc.paths.push(
                      <path
                        key={i}
                        d={angle >= 360 
                          ? `M 50 10 A 40 40 0 1 1 49.99 10` 
                          : `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                        fill={item.color}
                      />
                    );
                  }
                  acc.angle = endAngle;
                  return acc;
                }, { paths: [], angle: 0 }).paths}
                <circle cx="50" cy="50" r="25" fill="white" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xs text-slate-500">Total</span>
                <span className="text-lg font-bold text-slate-800">{invites.total_people}</span>
              </div>
            </div>
            <div className="flex-1 w-full space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0"></div>
                  <span className="text-sm text-slate-600 truncate">Adultos</span>
                </div>
                <span className="text-sm font-medium text-slate-800 flex-shrink-0 ml-2">{invites.total_adults}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-3 h-3 rounded-full bg-cyan-500 flex-shrink-0"></div>
                  <span className="text-sm text-slate-600 truncate">Crianças (3–10 anos)</span>
                </div>
                <span className="text-sm font-medium text-slate-800 flex-shrink-0 ml-2">{invites.total_children}</span>
              </div>
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between text-blue-600">
                  <span className="text-sm font-medium">Confirmados</span>
                  <span className="text-sm font-bold">{invites.confirmed_people}</span>
                </div>
                <div className="flex items-center justify-between text-orange-500">
                  <span className="text-sm font-medium">Pendentes</span>
                  <span className="text-sm font-bold">{invites.pending_people}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Despesas por categoria - Donut Premium */}
      <div className="bg-white rounded-2xl p-6 border border-zinc-200/60 shadow-[0_1px_3px_rgba(24,24,27,0.04)]" style={{fontFamily:"'Poppins', sans-serif"}} data-testid="expenses-by-category-card">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Receipt size={16} className="text-zinc-400" />
            <h3 className="text-sm font-medium text-zinc-900">Despesas por categoria</h3>
          </div>
          <button
            onClick={() => navigate('/expenses')}
            className="text-[11.5px] text-zinc-500 hover:text-zinc-900 transition-colors flex items-center gap-1"
            data-testid="view-all-expenses"
          >
            Ver todas <ChevronRight size={12} />
          </button>
        </div>
        {expensesTotal === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mb-3">
              <Receipt size={18} className="text-zinc-400" />
            </div>
            <p className="text-[13.5px] font-medium text-zinc-900 mb-1">Sem despesas registradas</p>
            <p className="text-[12px] text-zinc-500 mb-4">Adicione despesas para visualizar a distribuição por categoria.</p>
            <button
              onClick={() => navigate('/expenses')}
              className="px-4 py-2 rounded-full bg-brand-deep text-white text-[12.5px] font-medium hover:bg-brand transition-all"
              data-testid="add-first-expense"
            >
              Adicionar despesa
            </button>
          </div>
        ) : (
          <div>
            {/* Gráfico de barras — estilo financeiro premium (gradiente vertical) */}
            <div data-testid="expenses-bar-chart">
              <div className="relative h-44 sm:h-56">
                {/* Gridlines tracejadas horizontais */}
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none" aria-hidden>
                  <div className="border-t border-dashed border-zinc-200/80" />
                  <div className="border-t border-dashed border-zinc-200/80" />
                  <div className="border-t border-dashed border-zinc-200/80" />
                  <div className="border-t border-dashed border-zinc-200/80" />
                  <div />
                </div>
                <div className="relative h-full flex items-end justify-around gap-2 sm:gap-4 px-1">
                  {expensesData.map((item, i) => {
                    const maxVal = Math.max(...expensesData.map(d => d.value), 1);
                    const hPct = Math.max(3, (item.value / maxVal) * 100);
                    return (
                      <div key={i} className="flex flex-col items-center justify-end h-full flex-1 min-w-0 group" data-testid={`expense-bar-${i}`}>
                        <span className="text-[10px] sm:text-[10.5px] font-semibold text-zinc-600 tabular-nums mb-1.5 truncate max-w-full opacity-80 group-hover:opacity-100 transition-opacity">
                          {item.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </span>
                        <div
                          className="w-full max-w-[42px] sm:max-w-[50px] rounded-t-[10px] transition-[height] duration-500"
                          style={{ height: `${hPct}%`, background: `linear-gradient(to bottom, ${item.color}F2, ${item.color}26)` }}
                          title={`${item.label}: ${item.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Labels do eixo X */}
              <div className="flex justify-around gap-2 sm:gap-4 px-1 mt-2">
                {expensesData.map((item, i) => (
                  <span key={i} className="flex-1 min-w-0 text-center text-[10px] sm:text-[11px] text-zinc-500 truncate" title={item.label}>{item.label}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 💸 ExpenseRemindersBanner — leve, owner-only, sem analytics */}
      <ExpenseRemindersBanner weddingId={wedding?.id} userIsOwner={!isStaffAccount} />


      {/* Tasks Progress */}
      <div className="bg-white rounded-xl p-6 border border-zinc-200/60" style={{fontFamily:"'Poppins', sans-serif"}}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-2">
            <CheckSquare size={16} className="text-zinc-400" />
            Progresso da Agenda
          </h3>
          <span className="text-lg font-bold text-indigo-600">{tasks.progress}%</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full transition-all duration-500"
            style={{ width: `${tasks.progress}%` }}
          ></div>
        </div>
        <div className="flex justify-between mt-3 text-sm">
          <span className="text-slate-500">{tasks.completed} de {tasks.total} tarefas concluídas</span>
          <span className="text-orange-500 font-medium">{tasks.pending} pendentes</span>
        </div>
      </div>

      {/* Alertas */}
      {alerts && alerts.total_alerts > 0 && (
        <div className="bg-white rounded-xl p-5 border border-zinc-200/60 border-l-4 border-l-amber-400" style={{fontFamily:"'Poppins', sans-serif"}}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-2">
              <AlertCircle size={16} className="text-amber-500" />
              Alertas
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                {alerts.total_alerts}
              </span>
              {alerts.urgent_count > 0 && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full animate-pulse">
                  {alerts.urgent_count} urgente(s)
                </span>
              )}
            </h3>
            <button 
              onClick={fetchAlerts} 
              className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
              disabled={loadingAlerts}
            >
              <RefreshCw size={16} className={loadingAlerts ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="space-y-3">
            {alerts.alerts.slice(0, 5).map((alert, index) => (
              <div 
                key={alert.id || index} 
                className={`p-3 rounded-xl flex items-start gap-3 ${
                  alert.alert_type === 'urgent' ? 'bg-red-50 border border-red-100' :
                  'bg-orange-50 border border-orange-100'
                }`}
              >
                <div className={`mt-0.5 ${
                  alert.alert_type === 'urgent' ? 'text-red-500' : 'text-orange-500'
                }`}>
                  {alert.type === 'expense' ? <DollarSign size={18} /> : <Calendar size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 text-sm">
                    {alert.type === 'expense' ? alert.vendor_name : alert.task_name}
                  </p>
                  {alert.type === 'expense' && (
                    <p className="text-sm font-bold text-slate-700 mt-1">
                      R$ {alert.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      alert.alert_type === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                    }`}>
                      {alert.days_text}
                    </span>
                    <span className="text-xs text-slate-400">
                      {alert.type === 'expense' ? '💰 Despesa' : '📋 Tarefa'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {alerts.total_alerts > 5 && (
            <button 
              onClick={() => navigate('/cashflow')}
              className="w-full mt-3 text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Ver todos os {alerts.total_alerts} alertas →
            </button>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" style={{fontFamily:"'Poppins', sans-serif"}}>
        <button onClick={() => navigate('/ai-assistant')} className="p-5 bg-white hover:bg-zinc-50 rounded-xl border border-zinc-200/60 hover:border-zinc-300 transition-all text-left group">
          <Sparkles className="text-zinc-400 mb-3 group-hover:text-zinc-600 transition-colors" size={20} />
          <p className="text-sm font-medium text-zinc-900">Planner IA</p>
          <p className="text-xs text-zinc-400 mt-0.5">Converse com a IA</p>
        </button>
        <button onClick={() => navigate('/guests')} className="p-5 bg-white hover:bg-zinc-50 rounded-xl border border-zinc-200/60 hover:border-zinc-300 transition-all text-left group">
          <Users className="text-zinc-400 mb-3 group-hover:text-zinc-600 transition-colors" size={20} />
          <p className="text-sm font-medium text-zinc-900">Convidados</p>
          <p className="text-xs text-zinc-400 mt-0.5">Gerencie a lista</p>
        </button>
        <button onClick={() => navigate('/expenses')} className="p-5 bg-white hover:bg-zinc-50 rounded-xl border border-zinc-200/60 hover:border-zinc-300 transition-all text-left group">
          <Receipt className="text-zinc-400 mb-3 group-hover:text-zinc-600 transition-colors" size={20} />
          <p className="text-sm font-medium text-zinc-900">Despesas</p>
          <p className="text-xs text-zinc-400 mt-0.5">Controlar gastos</p>
        </button>
        <button onClick={() => navigate('/tasks')} className="p-5 bg-white hover:bg-zinc-50 rounded-xl border border-zinc-200/60 hover:border-zinc-300 transition-all text-left group">
          <CheckSquare className="text-zinc-400 mb-3 group-hover:text-zinc-600 transition-colors" size={20} />
          <p className="text-sm font-medium text-zinc-900">Checklist</p>
          <p className="text-xs text-zinc-400 mt-0.5">Ver tarefas</p>
        </button>
      </div>
      </div>{/* /desktop wrapper */}
    </div>
  );
}



// AI Assistant Page - Comece Por Aqui - Chat Conversacional liventy

// Meus Eventos Page (Profissional)
/**
 * MyEventsCalendar — agenda operacional inteligente.
 * Mostra status do dia (🟢 Livre / 🟡 Parcial / 🔴 Ocupado) baseado em maxPerDay.
 * Datas futuras clicáveis → onCellClick(dateISO) (modal quick-create).
 * Eventos clicáveis → onEventClick(ev) (vai pro dashboard do evento).
 */
function MyEventsCalendar({ events, onEventClick, onCellClick, onDayClick, maxPerDay = 3, canCreate = false }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const today = new Date(); today.setHours(0,0,0,0);
  const monthNames = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
  const dayNames = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

  const firstDay = new Date(cursor.year, cursor.month, 1);
  const lastDay = new Date(cursor.year, cursor.month + 1, 0);
  const startDayOfWeek = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const eventsByDay = {};
  events.forEach(({ ev, date }) => {
    if (date && date.getFullYear() === cursor.year && date.getMonth() === cursor.month) {
      const k = date.getDate();
      (eventsByDay[k] = eventsByDay[k] || []).push(ev);
    }
  });
  // Ordena eventos de cada dia por horário (event_time "HH:MM"); sem horário vai pro fim.
  const _calTimeToMinutes = (t) => {
    if (!t || typeof t !== 'string') return 9999;
    const m = t.match(/^(\d{1,2}):(\d{2})/);
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : 9999;
  };
  Object.keys(eventsByDay).forEach((k) => {
    eventsByDay[k].sort((a, b) => _calTimeToMinutes(a?.event_time) - _calTimeToMinutes(b?.event_time));
  });

  const cells = [];
  for (let i = 0; i < startDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prev = () => setCursor(c => c.month === 0 ? { year: c.year - 1, month: 11 } : { ...c, month: c.month - 1 });
  const next = () => setCursor(c => c.month === 11 ? { year: c.year + 1, month: 0 } : { ...c, month: c.month + 1 });
  const goToday = () => { const d = new Date(); setCursor({ year: d.getFullYear(), month: d.getMonth() }); };

  const dateToISO = (year, month, day) => {
    const mm = String(month + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    return `${year}-${mm}-${dd}`;
  };

  // Status visual: 0=livre / 1..max-1=parcial / >=max=ocupado
  const statusFor = (count) => {
    if (count === 0) return "free";
    if (count >= maxPerDay) return "busy";
    return "partial";
  };
  // Cores suaves estilo Notion/Linear (não-saturadas)
  const STATUS_STYLE = {
    free:    { bg: "bg-emerald-50/40", border: "border-emerald-100", dot: "bg-emerald-400",  label: "Livre",    labelCls: "text-emerald-600" },
    partial: { bg: "bg-amber-50/40",   border: "border-amber-200",  dot: "bg-amber-400",    label: "Parcial",  labelCls: "text-amber-600" },
    busy:    { bg: "bg-rose-50/50",    border: "border-rose-200",   dot: "bg-rose-400",     label: "Ocupado",  labelCls: "text-rose-600" },
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-[26px] sm:text-[28px] font-bold tracking-tight text-zinc-900 leading-none">{monthNames[cursor.month]}</h3>
          <p className="text-[14px] font-medium text-zinc-400 mt-1">{cursor.year}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={prev} className="w-9 h-9 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center text-zinc-500 transition-colors active:scale-95" data-testid="cal-prev"><ChevronLeft size={18} /></button>
          <button onClick={goToday} className="px-4 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 text-[13px] font-semibold text-zinc-700 transition-colors active:scale-95" data-testid="cal-today">Hoje</button>
          <button onClick={next} className="w-9 h-9 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center text-zinc-500 transition-colors active:scale-95" data-testid="cal-next"><ChevronRight size={18} /></button>
        </div>
      </div>

      {/* Legenda de status — visual minimalista */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 text-[11px] text-zinc-400">
        <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Livre</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />Parcial</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-rose-400" />Ocupado</span>
        <span className="ml-auto">Limite: {maxPerDay} evento(s)/dia</span>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayNames.map(d => <div key={d} className="text-center text-[11px] uppercase tracking-wider font-semibold text-zinc-300 py-1.5">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="min-h-[84px] sm:min-h-[104px]" />;
          const cellDate = new Date(cursor.year, cursor.month, d);
          cellDate.setHours(0,0,0,0);
          const isToday = cellDate.getTime() === today.getTime();
          const isPast = cellDate < today;
          const dayEvents = eventsByDay[d] || [];
          const status = statusFor(dayEvents.length);
          const st = STATUS_STYLE[status];
          const interactable = !isPast && canCreate;

          const handleCellClick = () => {
            if (isPast && dayEvents.length === 0) return;
            // Qualquer dia com evento(s) → abre o modal do dia (lista + adicionar evento)
            if (dayEvents.length >= 1 && onDayClick) {
              onDayClick(dayEvents, dateToISO(cursor.year, cursor.month, d));
              return;
            }
            // Dia livre/sem eventos → quick create (se permitido)
            if (canCreate) onCellClick?.(dateToISO(cursor.year, cursor.month, d));
          };

          const hasEvents = dayEvents.length > 0;
          // iOS: célula limpa, sem borda pesada; tinte suave só quando há eventos
          const cellCls = `relative min-h-[84px] sm:min-h-[104px] rounded-2xl p-2 flex flex-col text-[11px] transition-all duration-200 ${
            hasEvents ? `${st.bg} border ${st.border}` : 'border border-transparent hover:bg-zinc-50'
          } ${isPast ? 'opacity-50' : ''} ${(interactable || hasEvents) ? 'card-hover-brightness cursor-pointer' : 'cursor-default'}`;

          return (
            <button
              type="button"
              key={i}
              onClick={handleCellClick}
              disabled={isPast && dayEvents.length === 0}
              className={`${cellCls} text-left`}
              data-testid={`cal-cell-${cursor.year}-${cursor.month + 1}-${d}`}
              aria-label={`${d} de ${monthNames[cursor.month]} · ${st.label}`}
            >
              {/* Número do dia — estilo iOS (hoje = círculo preenchido) */}
              <div className="flex items-center justify-between mb-1">
                <span className={`inline-flex items-center justify-center text-[13px] font-semibold tabular-nums ${
                  isToday
                    ? 'w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 text-white shadow-[0_2px_8px_-2px_rgba(139,92,246,0.55)]'
                    : 'w-7 h-7 text-zinc-700'
                }`}>{d}</span>
                {!isPast && hasEvents && (
                  <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} aria-hidden />
                )}
              </div>
              {hasEvents ? (
                <div className="mt-auto flex flex-col gap-1">
                  {dayEvents.slice(0, 2).map((e, idx) => (
                    <span key={idx} className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9.5px] font-medium bg-white/80 backdrop-blur-sm text-zinc-700 border border-zinc-200/70 truncate">
                      <span className={`w-1 h-1 rounded-full flex-shrink-0 ${st.dot}`} aria-hidden />
                      <span className="truncate">{e.event_name || e.partner1_name || 'Evento'}</span>
                    </span>
                  ))}
                  {dayEvents.length > 2 && <span className="text-[9.5px] font-medium text-zinc-400 pl-1.5">+{dayEvents.length - 2} mais</span>}
                </div>
              ) : (
                !isPast && (
                  <span className={`mt-auto text-[9.5px] font-medium ${st.labelCls} opacity-80`}>{st.label}</span>
                )
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MyEventsPage() {
  const { weddings, setActiveEvent, deleteEvent, completeEvent, refreshWeddings } = useWedding();
  const { user, refreshUser } = useAuth();
  const capabilities = useCapabilities();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [eventStats, setEventStats] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [uploadingCoverId, setUploadingCoverId] = useState(null);
  const [eventDrawer, setEventDrawer] = useState(null); // {ev, render} ou null — drawer de pré-visualização do evento
  // Modal exibido quando o usuário clica num dia do calendário com 2+ eventos
  const [dayEventsModal, setDayEventsModal] = useState(null); // { events, dateISO } | null
  const coverFileRefs = useRef({});
  // Quick-create vindo do clique no calendário
  const [quickCreateDate, setQuickCreateDate] = useState(null);
  // Quick-create aberto pelo botão "Incluir evento" (sem data pré-preenchida)
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  // Limite de eventos/dia configurável (PRO)
  const [maxPerDay, setMaxPerDay] = useState(() => Number(user?.max_events_per_day) || 3);
  useEffect(() => {
    if (user?.max_events_per_day) setMaxPerDay(Number(user.max_events_per_day));
  }, [user?.max_events_per_day]);
  // Modo de visualização: agenda (default mobile) ou calendar (default desktop, persistido)
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'agenda';
    if (window.matchMedia('(max-width: 767px)').matches) return 'agenda';
    return localStorage.getItem('liventy_events_view') || 'calendar';
  });
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.matchMedia('(max-width: 767px)').matches) {
      localStorage.setItem('liventy_events_view', viewMode);
    }
  }, [viewMode]);

  // Fetch stats for all events
  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await axios.get(`${API}/weddings/events-stats`);
        setEventStats(res.data);
      } catch (err) {
        console.error("Erro ao carregar stats:", err);
      }
    };
    if (weddings && weddings.length > 0) loadStats();
  }, [weddings]);

  // Upload cover do evento específico
  const handleCoverUpload = async (eventId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem (JPG, PNG ou WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande. Limite 5MB');
      return;
    }
    setUploadingCoverId(eventId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/weddings/${eventId}/cover`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Capa atualizada!');
      if (typeof refreshWeddings === 'function') {
        await refreshWeddings();
      } else {
        window.location.reload();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar capa');
    } finally {
      setUploadingCoverId(null);
    }
  };

  // ✅ Iter 79 — Remover capa do evento
  const handleCoverDelete = async (eventId) => {
    if (!(await confirmDialog({
      title: "Remover capa do evento",
      message: "Tem certeza que deseja remover a imagem de capa deste evento?",
      confirmLabel: "Remover",
      variant: "danger",
    }))) return;
    setUploadingCoverId(eventId);
    try {
      await axios.delete(`${API}/weddings/${eventId}/cover`);
      toast.success("Capa removida");
      if (typeof refreshWeddings === 'function') {
        await refreshWeddings();
      } else {
        window.location.reload();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao remover capa");
    } finally {
      setUploadingCoverId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await deleteEvent(deleteTarget.id);
      toast.success("Evento excluído com sucesso!");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao excluir evento"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!completeTarget) return;
    setActionLoading(true);
    try {
      await completeEvent(completeTarget.id);
      toast.success("Evento concluído com sucesso!");
      setCompleteTarget(null);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao concluir evento"));
    } finally {
      setActionLoading(false);
    }
  };

  // ═══ Lógica de agenda profissional ═══
  const isPro = (
    user?.profile_type === "professional"
    || user?.profile_type === "admin"
    || user?.subscription === "pro"
    || user?.plan === "pro"
    || isAdminUser(user)
  );
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
  const today = startOfDay(new Date());
  const tomorrow = startOfDay(new Date(Date.now() + 86400000));

  const parsedEvents = (weddings || []).map(ev => {
    const d = ev.event_date ? startOfDay(new Date(ev.event_date + (ev.event_date.includes('T') ? '' : 'T12:00:00'))) : null;
    const isPast = d ? d < today : false;
    const isToday = d ? d.getTime() === today.getTime() : false;
    const isTomorrow = d ? d.getTime() === tomorrow.getTime() : false;
    return { ev, date: d, isPast, isToday, isTomorrow };
  });

  // Ordenação: hoje → futuros (asc) → passados (desc), e dentro do mesmo dia: por horário (asc)
  const _parseTimeMinutes = (t) => {
    // event_time esperado "HH:MM" ou "HH:MM:SS". Sem horário = vai pro fim do dia.
    if (!t || typeof t !== "string") return 9999;
    const m = t.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return 9999;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  };
  const sortedEvents = [...parsedEvents].sort((a, b) => {
    const aDate = a.date ? a.date.getTime() : Infinity;
    const bDate = b.date ? b.date.getTime() : Infinity;
    const aFuture = !a.isPast;
    const bFuture = !b.isPast;
    if (aFuture && !bFuture) return -1;
    if (!aFuture && bFuture) return 1;
    // Mesma data → ordena por horário (event_time) ascendente. Sem horário cai pro fim.
    if (aDate === bDate) {
      return _parseTimeMinutes(a.ev?.event_time) - _parseTimeMinutes(b.ev?.event_time);
    }
    if (aFuture && bFuture) return aDate - bDate;
    return bDate - aDate; // passados: mais recente primeiro
  });

  // Agrupamento por data (yyyy-mm-dd)
  const groups = [];
  const groupMap = new Map();
  for (const item of sortedEvents) {
    const key = item.date ? item.date.toISOString().slice(0,10) : 'sem-data';
    if (!groupMap.has(key)) {
      const group = { key, date: item.date, items: [] };
      groupMap.set(key, group);
      groups.push(group);
    }
    groupMap.get(key).items.push(item);
  }

  // KPIs operacionais (mês corrente, semana corrente, próximo fim de semana)
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const monthEvents = parsedEvents.filter(p => p.date && p.date.getMonth() === currentMonth && p.date.getFullYear() === currentYear && !p.isPast);
  const upcomingEvents = parsedEvents.filter(p => !p.isPast);
  const next7Days = new Date(today.getTime() + 7 * 86400000);
  const thisWeekEvents = parsedEvents.filter(p => p.date && p.date >= today && p.date < next7Days);
  // Próximo fim de semana (sat-sun mais próximo)
  const dow = today.getDay();
  const daysToSat = dow === 6 ? 0 : (6 - dow);
  const nextSat = new Date(today.getTime() + daysToSat * 86400000);
  const nextSun = new Date(nextSat.getTime() + 86400000);
  const weekendEvents = parsedEvents.filter(p => p.date && (p.date.getTime() === nextSat.getTime() || p.date.getTime() === nextSun.getTime()));

  // Formatadores
  const fmtDateHeader = (d) => {
    if (!d) return 'Data não definida';
    if (d.getTime() === today.getTime()) return `Hoje · ${d.getDate()} ${['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'][d.getMonth()]}`;
    if (d.getTime() === tomorrow.getTime()) return `Amanhã · ${d.getDate()} ${['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'][d.getMonth()]}`;
    const days = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
    return `${days[d.getDay()]} · ${d.getDate()} ${['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'][d.getMonth()]} ${d.getFullYear()}`;
  };

  const renderEventCard = (ev) => {
            const stats = eventStats[ev.id] || {};
            const isCompleted = ev.status === 'completed';
            const totalGuests = stats.total_guests !== undefined ? stats.total_guests : 0;
            const totalInvites = stats.total_invites !== undefined ? stats.total_invites : 0;
            const coverUrl = ev.imagem_capa_url || ev.cover_image_url;
            const coverSrc = coverUrl ? (coverUrl.startsWith("http") ? coverUrl : `${API.replace(/\/api$/, "")}${coverUrl}`) : null;
            const eventLabel = EVENT_TYPE_LABELS[ev.event_type] || ev.event_type || "Evento";

            let dateShort = '—';
            let daysLeft = null;
            if (ev.event_date) {
              // Forçar meio-dia local para evitar shift de fuso (GMT-3) que joga a data 1 dia atrás
              const isoLocal = ev.event_date.includes('T') ? ev.event_date : `${ev.event_date}T12:00:00`;
              const d = new Date(isoLocal);
              const day = String(d.getDate()).padStart(2, '0');
              const months = ['JAN','FEV','MAR','ABR','MAI','JUN','JUL','AGO','SET','OUT','NOV','DEZ'];
              dateShort = `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
              const startOfToday = new Date();
              startOfToday.setHours(0, 0, 0, 0);
              const startOfDate = new Date(d);
              startOfDate.setHours(0, 0, 0, 0);
              daysLeft = Math.round((startOfDate.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24));
            }
            return { isCompleted, totalGuests, totalInvites, coverSrc, eventLabel, dateShort, daysLeft, eventTime: ev.event_time || null };
  };

  return (
    <div className="space-y-8" data-testid="my-events-page">
      {/* Skeleton premium enquanto a lista de eventos ainda não carregou */}
      {weddings === null || weddings === undefined ? (
        <SkeletonPage stats={4} rows={4} />
      ) : (
      <>
      {/* Header premium */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400">Meu espaço</p>
          <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" data-testid="my-events-title">
            Meus eventos
          </h1>
          <p className="mt-1 text-[13px] text-zinc-500">
            {weddings?.length || 0} evento{(weddings?.length || 0) === 1 ? "" : "s"} no seu espaço
            {user?.subscription === "free" && " · plano gratuito"}
            {user?.profile_type === "professional" && " · plano profissional"}
          </p>
        </div>
        {isPro && !(user?.user_type === "colaborador" && !user?.account_owner) && (
          <button
            onClick={() => setQuickCreateOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-deep text-white rounded-xl hover:bg-brand transition-all text-[13px] font-semibold shadow-sm hover:shadow-md"
            data-testid="quick-include-event-btn"
          >
            <Plus size={15} />
            <span>Incluir evento</span>
          </button>
        )}
      </div>

      {(!weddings || weddings.length === 0) ? (
        <div className="rounded-3xl border border-zinc-200/70 bg-gradient-to-br from-white to-zinc-50/40 px-8 py-16 text-center" data-testid="empty-events-state">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-brand-deep text-white flex items-center justify-center mb-5 shadow-lg">
            <Calendar size={26} />
          </div>
          <h3 className="text-[17px] font-semibold tracking-tight text-zinc-900">Nenhum evento criado ainda</h3>
          <p className="text-[13px] text-zinc-500 mt-1.5 max-w-md mx-auto">
            Comece criando seu primeiro evento. Em poucos minutos seu espaço estará pronto para encantar convidados.
          </p>
          <button
            onClick={() => setQuickCreateOpen(true)}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-brand-deep text-white rounded-xl hover:bg-brand transition-all text-[13px] font-semibold"
            data-testid="create-event-empty-btn"
          >
            <Plus size={15} /> Criar evento
          </button>
        </div>
      ) : (
        <>
          {/* KPIs Operacionais — apenas para profissionais (cards com hover/press premium) */}
          {isPro && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="events-kpi-strip">
              <div className="rounded-2xl bg-white border border-zinc-200/70 p-4 card-hover-brightness card-animate tap-press transition-shadow hover:shadow-[0_6px_18px_-8px_rgba(24,24,27,0.10)]">
                <p className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-zinc-400">Este mês</p>
                <p className="text-[24px] font-semibold tracking-tight text-zinc-900 mt-1">{monthEvents.length}</p>
                <p className="text-[11.5px] text-zinc-500 mt-0.5">eventos ocupando agenda</p>
              </div>
              <div className="rounded-2xl bg-white border border-zinc-200/70 p-4 card-hover-brightness card-animate tap-press transition-shadow hover:shadow-[0_6px_18px_-8px_rgba(24,24,27,0.10)]">
                <p className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-zinc-400">Próximos 7 dias</p>
                <p className="text-[24px] font-semibold tracking-tight text-zinc-900 mt-1">{thisWeekEvents.length}</p>
                <p className="text-[11.5px] text-zinc-500 mt-0.5">demanda imediata</p>
              </div>
              <div className="rounded-2xl bg-white border border-zinc-200/70 p-4 card-hover-brightness card-animate tap-press transition-shadow hover:shadow-[0_6px_18px_-8px_rgba(24,24,27,0.10)]">
                <p className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-zinc-400">Final de semana</p>
                <p className="text-[24px] font-semibold tracking-tight text-zinc-900 mt-1">{weekendEvents.length}</p>
                <p className="text-[11.5px] text-zinc-500 mt-0.5">sáb · dom mais próximo</p>
              </div>
              <div className="rounded-2xl bg-white border border-zinc-200/70 p-4 card-hover-brightness card-animate tap-press transition-shadow hover:shadow-[0_6px_18px_-8px_rgba(24,24,27,0.10)]">
                <p className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-zinc-400">Futuros</p>
                <p className="text-[24px] font-semibold tracking-tight text-zinc-900 mt-1">{upcomingEvents.length}</p>
                <p className="text-[11.5px] text-zinc-500 mt-0.5">total em carteira</p>
              </div>
            </div>
          )}

          {/* Toggle Agenda / Calendário */}
          <div className="flex items-center justify-between gap-3">
            <div className="inline-flex p-1 rounded-full bg-zinc-100 ring-1 ring-zinc-200/60" data-testid="events-view-toggle">
              <button
                onClick={() => setViewMode('agenda')}
                className={`px-4 py-1.5 rounded-full text-[12.5px] font-medium transition-all inline-flex items-center gap-1.5 ${viewMode === 'agenda' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500'}`}
                data-testid="events-view-agenda"
              >
                <ListChecks size={13} /> Lista
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-4 py-1.5 rounded-full text-[12.5px] font-medium transition-all inline-flex items-center gap-1.5 ${viewMode === 'calendar' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500'}`}
                data-testid="events-view-calendar"
              >
                <CalendarDays size={13} /> Calendário
              </button>
            </div>
            <p className="hidden md:block text-[11.5px] text-zinc-400">
              Ordenado por data · hoje primeiro · passados ao final
            </p>
          </div>

        {viewMode === 'agenda' && (
          <div className="flex flex-col gap-6" data-testid="events-agenda">
            {groups.map(group => {
              const isPastGroup = group.date && group.date < today;
              const isTodayGroup = group.date && group.date.getTime() === today.getTime();
              const headerLabel = fmtDateHeader(group.date);
              return (
                <section key={group.key} className="space-y-3">
                  <div className="flex items-center gap-3">
                    <h2 className={`text-[11.5px] font-bold uppercase tracking-[0.18em] ${
                      isTodayGroup ? 'text-violet-600' : isPastGroup ? 'text-zinc-400' : 'text-zinc-700'
                    }`} data-testid={`agenda-group-header-${group.key}`}>
                      {headerLabel}
                    </h2>
                    <span className="flex-1 h-px bg-zinc-200/70" />
                    <span className="text-[10.5px] font-medium text-zinc-400">
                      {group.items.length} {group.items.length === 1 ? 'evento' : 'eventos'}
                    </span>
                  </div>
                  <div className="flex flex-col gap-3">
                    {group.items.map(({ ev }) => {
                      const r = renderEventCard(ev);
                      return (
              <div
                key={ev.id}
                className={`tap-press group relative rounded-3xl border bg-white overflow-hidden cursor-pointer transition-shadow ${
                  r.isCompleted ? "border-emerald-200/60" : "border-zinc-200/70"
                }`}
                style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -12px rgba(0,0,0,0.08)' }}
                onClick={async () => {
                  try { navigator.vibrate?.(6); } catch {}
                  // Desktop: abre drawer de pré-visualização (sem perder contexto). Mobile: navega direto.
                  if (!isMobile) {
                    setEventDrawer({ ev, render: r });
                  } else {
                    await setActiveEvent(ev.id);
                    navigate('/dashboard');
                  }
                }}
                data-testid={`event-card-${ev.id}`}
              >
                <div className="flex items-stretch gap-0">
                  <div
                    className="relative w-[110px] sm:w-[150px] flex-shrink-0 bg-gradient-to-br from-zinc-100 via-zinc-200/60 to-zinc-100 overflow-hidden m-3 rounded-2xl group/cover"
                    onClick={(e)=>{ e.stopPropagation(); if (coverFileRefs.current[ev.id]) coverFileRefs.current[ev.id].click(); }}
                    title="Clique para alterar a foto de capa"
                  >
                    {/* Placeholder gradiente sempre presente (fallback p/ capa quebrada/404) */}
                    <div className="absolute inset-0 flex items-center justify-center" style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}}>
                      <Calendar size={26} className="text-white/90" />
                    </div>
                    {r.coverSrc && (
                      <img
                        src={r.coverSrc}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover/cover:bg-black/50 transition-colors flex items-center justify-center opacity-0 group-hover/cover:opacity-100">
                      {uploadingCoverId === ev.id ? <Loader2 size={20} className="text-white animate-spin" /> : (
                        <div className="text-center"><Camera size={18} className="text-white mx-auto mb-1" /><span className="text-[10px] font-medium text-white">Alterar capa</span></div>
                      )}
                    </div>
                    {/* Badge sempre visível para mobile/touch (indica que o card é clicável p/ upload) */}
                    <div className="absolute bottom-1.5 right-1.5 sm:hidden w-7 h-7 rounded-full bg-white/95 backdrop-blur-sm shadow-md flex items-center justify-center pointer-events-none">
                      {uploadingCoverId === ev.id ? <Loader2 size={13} className="text-zinc-700 animate-spin" /> : <Camera size={13} className="text-zinc-700" />}
                    </div>
                    {/* ✅ Iter 79 — botão remover capa (só se houver capa) */}
                    {(ev.imagem_capa_url || ev.cover_image_url || ev.cover_url) && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleCoverDelete(ev.id); }}
                        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/95 backdrop-blur-sm shadow-md flex items-center justify-center hover:bg-rose-50 hover:text-rose-600 text-zinc-700 transition-colors z-10"
                        aria-label="Remover capa"
                        data-testid={`cover-delete-${ev.id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                    <input ref={el => { coverFileRefs.current[ev.id] = el; }} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                      onClick={(e)=>e.stopPropagation()}
                      onChange={(e)=>{ e.stopPropagation(); const f = e.target.files?.[0]; if (f) handleCoverUpload(ev.id, f); e.target.value=''; }}
                      data-testid={`cover-upload-input-${ev.id}`} />
                  </div>
                  <div className="flex-1 min-w-0 py-3.5 sm:py-5 pr-3 sm:pr-4 flex flex-col justify-center gap-1.5">
                    <h3 className="text-[16px] sm:text-[20px] font-semibold tracking-tight text-zinc-900 line-clamp-2" data-testid={`event-name-${ev.id}`}>
                      {ev.event_name || ev.partner1_name || "Meu Evento"}
                    </h3>
                    <div className="flex items-center gap-2 flex-wrap text-[11.5px] text-zinc-500">
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={11} className="text-zinc-400" /> {r.dateShort}
                      </span>
                      {r.eventTime && (
                        <>
                          <span className="text-zinc-300">·</span>
                          <span className="inline-flex items-center gap-1 font-medium text-zinc-700 tabular-nums" data-testid={`event-time-${ev.id}`}>
                            <Clock size={11} className="text-zinc-400" /> {r.eventTime}h
                          </span>
                        </>
                      )}
                      <span className="text-zinc-300">·</span>
                      <span className="uppercase tracking-wider text-[10.5px] font-medium text-zinc-500">{r.eventLabel}</span>
                      <span className="text-zinc-300">·</span>
                      <span>{r.totalGuests} {r.totalGuests === 1 ? 'convidado' : 'convidados'}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider uppercase ${
                        r.isCompleted ? "bg-emerald-100 text-emerald-700" : (isPastGroup ? "bg-zinc-100 text-zinc-500" : "bg-orange-100 text-orange-700")
                      }`} data-testid={`event-status-${ev.id}`}>
                        {r.isCompleted ? "Concluído" : (isPastGroup ? "Passado" : "Ativo")}
                      </span>
                      {r.daysLeft !== null && r.daysLeft >= 0 && !r.isCompleted && (
                        <span className="text-[10.5px] font-medium text-emerald-600">
                          {r.daysLeft === 0 ? "hoje" : `em ${r.daysLeft} ${r.daysLeft === 1 ? 'dia' : 'dias'}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!r.isCompleted && (
                    <button onClick={(e) => { e.stopPropagation(); setCompleteTarget(ev); }} className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors" data-testid={`complete-event-btn-${ev.id}`} title="Marcar como concluído"><Check size={14} /></button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(ev); }} className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors" data-testid={`delete-event-btn-${ev.id}`} title="Excluir"><Trash2 size={14} /></button>
                </div>
              </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {viewMode === 'calendar' && (
          <div className="rounded-3xl border border-zinc-200/70 bg-white p-4 sm:p-6" data-testid="events-calendar">
            {isPro && (
              <div className="flex items-center justify-end gap-2 mb-3 -mt-1">
                <label htmlFor="max-per-day" className="text-[11.5px] uppercase tracking-wider font-semibold text-zinc-500">Máx. eventos/dia</label>
                <input
                  id="max-per-day"
                  type="number"
                  min={1}
                  max={20}
                  value={maxPerDay}
                  onChange={(e) => setMaxPerDay(Math.max(1, Math.min(20, parseInt(e.target.value || "1", 10))))}
                  onBlur={async () => {
                    try {
                      await axios.put(`${API}/auth/agenda-config`, { max_events_per_day: maxPerDay });
                      toast.success("Limite diário salvo");
                      if (typeof refreshUser === 'function') refreshUser();
                    } catch (err) {
                      toast.error(getErrorMessage(err, "Erro ao salvar limite"));
                    }
                  }}
                  className="w-16 px-2 py-1.5 rounded-lg border border-zinc-200 bg-white text-center text-[13px] font-semibold text-zinc-900 focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10"
                  data-testid="max-per-day-input"
                />
              </div>
            )}
            <MyEventsCalendar
              events={parsedEvents}
              maxPerDay={maxPerDay}
              canCreate={isPro}
              onEventClick={async (ev) => {
                if (!isMobile) { setEventDrawer({ ev, render: renderEventCard(ev) }); return; }
                await setActiveEvent(ev.id); navigate('/dashboard');
              }}
              onDayClick={(events, dateISO) => setDayEventsModal({ events, dateISO })}
              onCellClick={async (dateISO) => {
                const [yy, mm, dd] = dateISO.split("-");
                const ok = await confirmDialog({
                  title: "Nenhum evento nesta data",
                  message: `Não há nenhum evento cadastrado para ${dd}/${mm}/${yy}. Deseja criar um agora?`,
                  confirmLabel: "Criar evento",
                  cancelLabel: "Agora não",
                  variant: "confirm",
                });
                if (ok) setQuickCreateDate(dateISO);
              }}
            />
          </div>
        )}
        </>
      )}

      {/* Modal Eventos do Dia — listagem completa quando há 2+ eventos no mesmo dia (já ordenados por horário) */}
      <Modal
        isOpen={!!dayEventsModal}
        onClose={() => setDayEventsModal(null)}
        title={
          dayEventsModal
            ? new Date(`${dayEventsModal.dateISO}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
            : 'Eventos do dia'
        }
      >
        <div className="space-y-2" data-testid="day-events-modal">
          <p className="text-[12.5px] text-zinc-500 mb-2">
            {dayEventsModal?.events?.length || 0} {dayEventsModal?.events?.length === 1 ? 'evento neste dia' : 'eventos neste dia'} · ordenados por horário
          </p>
          {dayEventsModal?.events?.map((ev) => {
            const r = renderEventCard(ev);
            return (
              <button
                key={ev.id}
                type="button"
                onClick={async () => {
                  setDayEventsModal(null);
                  if (!isMobile) {
                    setEventDrawer({ ev, render: r });
                  } else {
                    await setActiveEvent(ev.id);
                    navigate('/dashboard');
                  }
                }}
                className="w-full text-left rounded-2xl border border-zinc-200/70 bg-white p-3 flex items-center gap-3 hover:border-zinc-300 hover:shadow-[0_4px_14px_-8px_rgba(24,24,27,0.12)] transition-all tap-press"
                data-testid={`day-events-item-${ev.id}`}
              >
                <div className="w-14 h-14 rounded-xl flex-shrink-0 overflow-hidden bg-gradient-to-br from-violet-500 via-pink-500 to-amber-400 flex items-center justify-center">
                  {r.coverSrc ? (
                    <img src={r.coverSrc} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Calendar size={20} className="text-white/90" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-[14px] font-semibold text-zinc-900 truncate">
                    {ev.event_name || ev.partner1_name || 'Evento'}
                  </h4>
                  <div className="flex items-center gap-1.5 text-[11.5px] text-zinc-500 mt-0.5">
                    {r.eventTime ? (
                      <span className="inline-flex items-center gap-1 font-medium text-zinc-700 tabular-nums">
                        <Clock size={11} className="text-zinc-400" /> {r.eventTime}h
                      </span>
                    ) : (
                      <span className="italic text-zinc-400">sem horário</span>
                    )}
                    <span className="text-zinc-300">·</span>
                    <span className="uppercase tracking-wider text-[10px] font-medium">{r.eventLabel}</span>
                  </div>
                </div>
                <ChevronRight size={16} className="text-zinc-300 flex-shrink-0" />
              </button>
            );
          })}

          {/* Adicionar novo evento neste mesmo dia (estilo iOS) */}
          {isPro && dayEventsModal?.dateISO && (
            <button
              type="button"
              onClick={() => {
                const iso = dayEventsModal.dateISO;
                setDayEventsModal(null);
                setQuickCreateDate(iso);
              }}
              className="w-full mt-1 rounded-2xl border border-dashed border-violet-300 bg-violet-50/50 hover:bg-violet-50 p-3 flex items-center gap-3 transition-colors tap-press group"
              data-testid="day-events-add-btn"
            >
              <div className="w-14 h-14 rounded-xl flex-shrink-0 bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shadow-[0_4px_12px_-4px_rgba(139,92,246,0.5)] group-hover:scale-105 transition-transform">
                <Plus size={22} className="text-white" />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <h4 className="text-[14px] font-semibold text-violet-700">Adicionar evento</h4>
                <p className="text-[11.5px] text-violet-500/80 mt-0.5">Criar um novo evento nesta data</p>
              </div>
              <ChevronRight size={16} className="text-violet-300 flex-shrink-0" />
            </button>
          )}
        </div>
      </Modal>

      {/* Modal Confirmar Exclusão */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Excluir Evento">        <div className="space-y-4" data-testid="delete-event-modal">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 font-medium mb-2">
              Tem certeza que deseja excluir o evento "{deleteTarget?.event_name || deleteTarget?.partner1_name}"?
            </p>
            <p className="text-red-700 text-sm">
              Esta ação não pode ser desfeita. Todos os dados vinculados serão removidos:
            </p>
            <ul className="text-red-600 text-sm mt-2 space-y-1">
              <li>- Convidados e confirmações</li>
              <li>- Tarefas e checklist</li>
              <li>- Orçamentos e despesas</li>
              <li>- Histórico de IA</li>
            </ul>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setDeleteTarget(null)}
              className="flex-1 py-2.5 px-4 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 text-sm font-medium"
              data-testid="cancel-delete-btn"
            >
              Cancelar
            </button>
            <button
              onClick={handleDelete}
              disabled={actionLoading}
              className="flex-1 py-2.5 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
              data-testid="confirm-delete-btn"
            >
              {actionLoading ? <Loader2 className="animate-spin mx-auto" size={18} /> : 'Sim, Excluir'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Confirmar Conclusão */}
      <Modal isOpen={!!completeTarget} onClose={() => setCompleteTarget(null)} title="Concluir Evento">
        <div className="space-y-4" data-testid="complete-event-modal">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <p className="text-emerald-800 font-medium mb-2">
              Deseja concluir o evento "{completeTarget?.event_name || completeTarget?.partner1_name}"?
            </p>
            <p className="text-emerald-700 text-sm">
              Após a conclusão:
            </p>
            <ul className="text-emerald-600 text-sm mt-2 space-y-1">
              <li>- O evento será marcado como concluído</li>
              <li>- Você ainda poderá visualizar, editar e excluir</li>
              <li>- É apenas uma marcação de status para sua organização</li>
            </ul>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setCompleteTarget(null)}
              className="flex-1 py-2.5 px-4 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 text-sm font-medium"
              data-testid="cancel-complete-btn"
            >
              Cancelar
            </button>
            <button
              onClick={handleComplete}
              disabled={actionLoading}
              className="flex-1 py-2.5 px-4 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50"
              data-testid="confirm-complete-btn"
            >
              {actionLoading ? <Loader2 className="animate-spin mx-auto" size={18} /> : 'Sim, Concluir'}
            </button>
          </div>
        </div>
      </Modal>

      <QuickCreateEventModal
        isOpen={!!quickCreateDate || quickCreateOpen}
        prefillDate={quickCreateDate}
        onClose={() => { setQuickCreateDate(null); setQuickCreateOpen(false); }}
        onCreated={async () => {
          if (typeof refreshWeddings === 'function') await refreshWeddings();
        }}
      />

      {/* 📱 Mobile FAB — Incluir evento (md:hidden) */}
      <button
        onClick={() => { try { navigator.vibrate?.(8); } catch {} setQuickCreateOpen(true); }}
        className="md:hidden fixed right-4 z-[9999] w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-[0_8px_32px_rgba(139,92,246,0.45)] active:scale-95 transition-transform"
        style={{ bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' }}
        data-testid="mobile-fab-new-event"
        data-mobile-fab
        aria-label="Incluir evento"
        title="Incluir evento"
      >
        <Plus size={26} strokeWidth={2.5} />
      </button>

      {/* 🪟 Drawer lateral de pré-visualização do evento (desktop) — sensação app nativo */}
      <Vaul.Root
        open={!!eventDrawer}
        onOpenChange={(o) => { if (!o) setEventDrawer(null); }}
        dismissible
        direction={isMobile ? "bottom" : "right"}
      >
        <Vaul.Portal>
          <Vaul.Overlay className="fixed inset-0 z-[10010] bg-black/40 backdrop-blur-[3px]" />
          <Vaul.Content
            className={`fixed z-[10011] flex flex-col bg-white outline-none shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.18)] ${
              isMobile
                ? "inset-x-0 bottom-0 rounded-t-3xl"
                : "inset-y-0 right-0 w-full max-w-[460px] rounded-l-3xl border-l border-zinc-100"
            }`}
            style={isMobile ? { height: '78dvh' } : { height: '100dvh' }}
            data-testid="event-detail-drawer"
          >
            <Vaul.Title className="sr-only">{eventDrawer?.ev?.event_name || 'Evento'}</Vaul.Title>
            <Vaul.Description className="sr-only">Pré-visualização do evento</Vaul.Description>
            {isMobile && <div className="mx-auto mt-2.5 mb-1 h-1.5 w-12 rounded-full bg-zinc-200 shrink-0" />}

            {eventDrawer && (() => {
              const ev = eventDrawer.ev;
              const r = eventDrawer.render || {};
              const s = eventStats[ev.id] || {};
              const totalGuests = s.total_guests ?? r.totalGuests ?? 0;
              const confirmedGuests = s.confirmed_guests ?? 0;
              const totalSpent = s.total_spent ?? 0;
              const balance = s.balance ?? 0;
              const isPast = r.isPast;
              const isCompleted = r.isCompleted;
              const isProfessional = capabilities.can_see_revenues;

              const goTo = async (path) => {
                try { await setActiveEvent(ev.id); } catch {}
                setEventDrawer(null);
                navigate(path);
              };

              const QuickAction = ({ icon: Icon, label, color, path, testid }) => (
                <button
                  type="button"
                  onClick={() => goTo(path)}
                  className="flex items-center gap-3 p-3.5 rounded-2xl bg-white border border-zinc-200/80 hover:border-zinc-300 card-hover-brightness tap-press transition-all"
                  data-testid={testid}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                    <Icon size={17} strokeWidth={2.2} />
                  </div>
                  <span className="text-[13.5px] font-medium text-zinc-900">{label}</span>
                  <ChevronRight size={14} className="text-zinc-300 ml-auto" />
                </button>
              );

              return (
                <>
                  {/* Header com cover hero */}
                  <div className="relative shrink-0">
                    <div className="relative h-[160px] overflow-hidden">
                      {r.coverSrc ? (
                        <img src={r.coverSrc} alt="" className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0" style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}} />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                      <button
                        onClick={() => setEventDrawer(null)}
                        className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur-sm hover:bg-white flex items-center justify-center text-zinc-700 shadow-md transition-colors"
                        aria-label="Fechar"
                        data-testid="event-drawer-close"
                      >
                        <X size={16} />
                      </button>
                      <div className="absolute bottom-3 left-4 right-12 text-white">
                        <p className="text-[10.5px] uppercase tracking-[0.15em] font-semibold opacity-90">{r.eventLabel || 'Evento'}</p>
                        <h2 className="text-[20px] font-semibold tracking-tight leading-tight mt-0.5 line-clamp-2">{ev.event_name || ev.partner1_name || 'Meu Evento'}</h2>
                      </div>
                    </div>
                  </div>

                  {/* Conteúdo scrollável */}
                  <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
                    {/* Meta (data, dias, status) */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-zinc-100 text-zinc-700">
                        <Calendar size={11} /> {r.dateLong || r.dateShort || '—'}
                      </span>
                      {r.daysLeft !== null && r.daysLeft !== undefined && !isCompleted && r.daysLeft >= 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">
                          {r.daysLeft === 0 ? "Hoje!" : `em ${r.daysLeft} ${r.daysLeft === 1 ? 'dia' : 'dias'}`}
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                        isCompleted ? "bg-emerald-100 text-emerald-700" : isPast ? "bg-zinc-100 text-zinc-500" : "bg-orange-100 text-orange-700"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          isCompleted ? "bg-emerald-500" : isPast ? "bg-zinc-400" : "bg-orange-500"
                        }`} />
                        {isCompleted ? "Concluído" : isPast ? "Passado" : "Ativo"}
                      </span>
                    </div>

                    {/* KPIs deste evento */}
                    <div className="grid grid-cols-2 gap-2.5" data-testid="event-drawer-kpis">
                      <div className="p-3.5 rounded-2xl bg-zinc-50/70 border border-zinc-100">
                        <p className="text-[10.5px] uppercase tracking-wider font-semibold text-zinc-500">Convidados</p>
                        <p className="text-[20px] font-semibold tracking-tight text-zinc-900 mt-1 tabular-nums">{totalGuests}</p>
                        <p className="text-[11px] text-emerald-600 mt-0.5 font-medium">{confirmedGuests} confirmados</p>
                      </div>
                      <div className="p-3.5 rounded-2xl bg-zinc-50/70 border border-zinc-100">
                        <p className="text-[10.5px] uppercase tracking-wider font-semibold text-zinc-500">Gasto</p>
                        <p className="text-[20px] font-semibold tracking-tight text-zinc-900 mt-1 tabular-nums">
                          {(totalSpent || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
                        </p>
                        <p className={`text-[11px] mt-0.5 font-medium ${balance < 0 ? 'text-rose-600' : 'text-zinc-500'}`}>
                          {balance < 0 ? 'estouro' : 'saldo'}: {(balance || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>

                    {/* CTA principal */}
                    <button
                      type="button"
                      onClick={() => goTo('/dashboard')}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl text-white text-[14px] font-semibold shadow-[0_8px_24px_-8px_rgba(107,61,246,0.55)]"
                      style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)" }}
                      data-testid="event-drawer-open-dashboard"
                    >
                      Abrir painel completo
                      <ArrowRight size={16} strokeWidth={2.4} />
                    </button>

                    {/* Atalhos rápidos */}
                    <div>
                      <p className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-zinc-500 mb-2.5">Atalhos rápidos</p>
                      <div className="space-y-2">
                        <QuickAction icon={Users}      label="Convidados" color="bg-orange-50 text-orange-600" path="/guests"   testid="event-drawer-go-guests" />
                        <QuickAction icon={CreditCard} label="Despesas"   color="bg-rose-50 text-rose-600"     path="/expenses" testid="event-drawer-go-expenses" />
                        <QuickAction icon={FileText}   label="Orçamentos" color="bg-blue-50 text-blue-600"     path="/budgets"  testid="event-drawer-go-budgets" />
                        {isProfessional && (
                          <QuickAction icon={Wallet} label="Minhas Receitas" color="bg-amber-50 text-amber-600" path="/revenue" testid="event-drawer-go-revenues" />
                        )}
                        <QuickAction icon={Calendar}   label="Tarefas" color="bg-pink-50 text-pink-600" path="/tasks" testid="event-drawer-go-tasks" />
                      </div>
                    </div>

                    {/* Ações de gestão (sutis no rodapé) */}
                    <div className="pt-3 mt-3 border-t border-zinc-100 flex items-center gap-2">
                      {!isCompleted && (
                        <button
                          type="button"
                          onClick={() => { setEventDrawer(null); setCompleteTarget(ev); }}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-[12.5px] font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors"
                          data-testid="event-drawer-complete"
                        >
                          <Check size={14} /> Marcar concluído
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => { setEventDrawer(null); setDeleteTarget(ev); }}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-[12.5px] font-semibold bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors"
                        data-testid="event-drawer-delete"
                      >
                        <Trash2 size={14} /> Excluir
                      </button>
                    </div>
                  </div>
                </>
              );
            })()}
          </Vaul.Content>
        </Vaul.Portal>
      </Vaul.Root>
      </>
      )}
    </div>
  );
}


function AIAssistantPage() {
  const { wedding } = useWedding();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const messagesEndRef = React.useRef(null);

  // Carregar histórico de conversa
  useEffect(() => {
    const loadConversation = async () => {
      if (!wedding) return;
      try {
        const res = await axios.get(`${API}/ai/conversation/${wedding.id}`);
        if (res.data.conversation && res.data.conversation.length > 0) {
          setMessages(res.data.conversation);
        } else {
          // Mensagem inicial - Boas-vindas e pergunta sobre perfil
          setMessages([{
            role: "assistant",
            content: `Olá! 👋 Bem-vindo ao **liventy**, seu assistente para organizar eventos!

Antes de começar, me conta: você está organizando **seu próprio evento** ou é um **profissional** (planner/cerimonialista) que organiza eventos para clientes?

Selecione uma opção abaixo ou me diga o que você precisa:`
          }]);
        }
      } catch (err) {
        console.error("Erro ao carregar conversa:", err);
        setMessages([{
          role: "assistant",
          content: `Olá! 👋 Bem-vindo ao **liventy**!\n\nSou sua assistente para organizar eventos. Me conta: **qual tipo de evento** você quer organizar?`
        }]);
      } finally {
        setLoadingHistory(false);
      }
    };
    loadConversation();
  }, [wedding]);

  // Scroll para última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || !wedding || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: userMessage,
        wedding_id: wedding.id
      });

      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
      
      // Se detectou um perfil, pode atualizar UI se necessário
      if (res.data.profile) {
        // Dashboard será recarregado quando usuário navegar para lá
      }
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Desculpe, tive um problema para processar sua mensagem. Pode tentar novamente?" 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    if (!(await confirmDialog({
      title: "Limpar histórico",
      message: "Tem certeza que deseja limpar todo o histórico de conversa? Esta ação não poderá ser desfeita.",
      confirmLabel: "Limpar",
      variant: "danger",
    }))) return;
    try {
      await axios.delete(`${API}/ai/conversation/${wedding.id}`);
      setMessages([{
        role: "assistant",
        content: `Olá! 👋 Bem-vindo ao **liventy**, seu assistente para organizar eventos!

Antes de começar, me conta: você está organizando **seu próprio evento** ou é um **profissional** (planner/cerimonialista) que organiza eventos para clientes?

Selecione uma opção abaixo ou me diga o que você precisa:`
      }]);
      toast.success("Histórico limpo!");
    } catch (err) {
      toast.error("Erro ao limpar histórico");
    }
  };

  const quickActions = [
    { text: "Meu próprio evento", icon: Heart },
    { text: "Sou profissional/planner", icon: Briefcase },
    { text: "Organizar casamento", icon: Heart },
    { text: "Criar checklist", icon: CheckSquare }
  ];

  const profileBadge = {
    simple: { label: "Simple", color: "bg-blue-100 text-blue-700", icon: "💫" },
    classic: { label: "Classic", color: "bg-emerald-100 text-emerald-700", icon: "✨" },
    luxe: { label: "Luxe", color: "bg-amber-100 text-amber-700", icon: "👑" }
  };

  // Skeleton loading para melhor UX durante carregamento
  if (loadingHistory) {
    return (
      <div className="h-[calc(100vh-120px)] flex flex-col">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 animate-pulse">
            <div className="w-12 h-12 rounded-full bg-slate-200"></div>
            <div>
              <div className="h-6 bg-slate-200 rounded w-40 mb-2"></div>
              <div className="h-4 bg-slate-200 rounded w-32"></div>
            </div>
          </div>
          <div className="h-8 bg-slate-200 rounded w-24 animate-pulse"></div>
        </div>
        {/* Chat Skeleton */}
        <div className="flex-1 bg-white rounded-2xl p-4 shadow-sm animate-pulse">
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-200"></div>
              <div className="flex-1">
                <div className="h-20 bg-slate-200 rounded-xl"></div>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <div className="w-32 h-10 bg-slate-200 rounded-xl"></div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-slate-200"></div>
              <div className="flex-1">
                <div className="h-16 bg-slate-200 rounded-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-160px)] sm:h-[calc(100vh-180px)] flex flex-col" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}}>
            <Sparkles className="text-blue-400" size={18} />
          </div>
          <div className="min-w-0">
            <h1 className="text-[18px] sm:text-[20px] font-bold text-zinc-900 tracking-tight truncate" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.025em' }}>Assistente de Eventos</h1>
            <p className="text-[12px] text-zinc-500 truncate">Orientação inteligente para seu evento</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {wedding?.profile && profileBadge[wedding.profile] && (
            <span className={`hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-medium ${profileBadge[wedding.profile].color}`}>
              {profileBadge[wedding.profile].icon} {profileBadge[wedding.profile].label}
            </span>
          )}
          <button
            onClick={clearHistory}
            className="w-9 h-9 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-500 hover:text-zinc-900 transition-colors"
            title="Limpar histórico"
            data-testid="ai-page-clear-history"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {/* Chat Card */}
      <div className="flex-1 overflow-hidden flex flex-col bg-white rounded-2xl border border-zinc-200/70 shadow-[0_1px_2px_rgba(24,24,27,0.04)]">
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 sm:py-5 space-y-3.5 bg-[#FAFAFA]">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-3.5 py-2.5 text-[13.5px] leading-[1.55] tracking-[-0.005em] ${
                msg.role === "user"
                  ? "bg-brand-deep text-white rounded-2xl rounded-br-md"
                  : "bg-white border border-zinc-200/70 text-zinc-800 rounded-2xl rounded-bl-md"
              }`} data-testid={`ai-page-msg-${msg.role}-${idx}`}>
                <div className="whitespace-pre-wrap">
                  {msg.content.split('\n').map((line, i) => {
                    // Inline bold support: split on **...** while preserving order
                    const parts = line.startsWith('- ')
                      ? [{ type: 'bullet', text: line.substring(2) }]
                      : line.split(/(\*\*[^*]+\*\*)/g).map(p => (
                          p.startsWith('**') && p.endsWith('**')
                            ? { type: 'bold', text: p.slice(2, -2) }
                            : { type: 'text', text: p }
                        ));
                    return (
                      <React.Fragment key={i}>
                        {parts[0]?.type === 'bullet' ? (
                          <span className="block ml-2">• {parts[0].text}</span>
                        ) : (
                          parts.map((p, k) => (
                            p.type === 'bold'
                              ? <strong key={k} className={msg.role === 'user' ? 'font-semibold' : 'font-semibold text-zinc-900'}>{p.text}</strong>
                              : <React.Fragment key={k}>{p.text}</React.Fragment>
                          ))
                        )}
                        {i < msg.content.split('\n').length - 1 && <br />}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border border-zinc-200/70 rounded-2xl rounded-bl-md px-3.5 py-3 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" style={{ animationDelay: '120ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" style={{ animationDelay: '240ms' }} />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        {messages.length <= 2 && (
          <div className="px-4 sm:px-5 py-3 border-t border-zinc-100 bg-white">
            <p className="text-[10.5px] uppercase tracking-wider text-zinc-400 font-medium mb-2">Sugestões</p>
            <div className="flex flex-wrap gap-1.5">
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => { setInput(action.text); }}
                  className="tap-scale flex items-center gap-1.5 px-2.5 py-1.5 bg-zinc-50 hover:bg-zinc-100 border border-zinc-200/70 rounded-lg text-[12px] text-zinc-700 transition-all"
                  data-testid={`ai-page-quick-${idx}`}
                >
                  <action.icon size={12} className="text-zinc-500" />
                  <span>{action.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={sendMessage} className="p-3 sm:p-4 border-t border-zinc-100 bg-white">
          <div className="flex items-end gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Pergunte qualquer coisa sobre o liventy..."
              className="flex-1 h-11 px-3.5 rounded-xl border border-zinc-200 bg-zinc-50 text-[14px] text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white focus:ring-4 focus:ring-zinc-900/5 transition-all"
              disabled={loading}
              data-testid="ai-page-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="tap-scale w-11 h-11 rounded-xl bg-brand-deep text-white hover:bg-brand active:scale-95 transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              data-testid="ai-page-send"
            >
              {loading ? <Loader2 className="animate-spin" size={15} /> : <ArrowUp size={16} />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== CONVITE DIGITAL - TEMPLATES ANIMADOS ====================

// Template Clássico Elegante
const TemplateClassico = ({ wedding, guest, message, onConfirm, isConfirmed }) => (
  <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
    <div className="max-w-md w-full text-center animate-fadeIn">
      {/* Ornamento superior */}
      <div className="text-amber-300 mb-6 animate-slideDown">
        <svg className="w-24 h-8 mx-auto" viewBox="0 0 100 30" fill="currentColor">
          <path d="M50 0 C30 15, 10 15, 0 10 L0 20 C10 25, 30 25, 50 10 C70 25, 90 25, 100 20 L100 10 C90 15, 70 15, 50 0Z" opacity="0.3"/>
        </svg>
      </div>
      
      {/* Nomes dos noivos */}
      <h1 className="font-serif text-4xl md:text-5xl text-slate-800 mb-2 animate-fadeIn" style={{animationDelay: '0.2s'}}>
        {wedding?.partner1_name || 'Noiva'}
      </h1>
      <p className="text-2xl text-amber-600 font-serif mb-2">&</p>
      <h1 className="font-serif text-4xl md:text-5xl text-slate-800 mb-8 animate-fadeIn" style={{animationDelay: '0.4s'}}>
        {wedding?.partner2_name || 'Noivo'}
      </h1>
      
      {/* Mensagem */}
      <p className="text-slate-600 text-lg mb-8 leading-relaxed px-4 animate-fadeIn" style={{animationDelay: '0.6s'}}>
        {message}
      </p>
      
      {/* Data e Local */}
      <div className="bg-white/80 backdrop-blur rounded-2xl p-6 mb-8 shadow-sm animate-fadeIn" style={{animationDelay: '0.8s'}}>
        <p className="text-amber-700 font-medium mb-2">
          {wedding?.event_date ? new Date(wedding.event_date).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Data a confirmar'}
        </p>
        {wedding?.event_time && (
          <p className="text-slate-600 mb-2">às {wedding.event_time}</p>
        )}
        {wedding?.venue_name && (
          <p className="text-slate-500 text-sm">{wedding.venue_name}</p>
        )}
      </div>
      
      {/* Botão de confirmação */}
      {isConfirmed ? (
        <div className="bg-emerald-100 text-emerald-700 rounded-full px-8 py-4 inline-flex items-center gap-2 animate-fadeIn">
          <Check size={20} />
          <span className="font-medium">Presença confirmada!</span>
        </div>
      ) : (
        <button
          onClick={onConfirm}
          className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-4 font-medium transition-all hover:scale-105 shadow-lg animate-fadeIn"
          style={{animationDelay: '1s'}}
        >
          Confirmar presença
        </button>
      )}
      
      {/* Ornamento inferior */}
      <div className="text-amber-300 mt-8 animate-slideUp" style={{animationDelay: '1.2s'}}>
        <Heart className="w-6 h-6 mx-auto" />
      </div>
    </div>
  </div>
);

// Template Moderno Minimalista
const TemplateModerno = ({ wedding, guest, message, onConfirm, isConfirmed }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="max-w-lg w-full animate-fadeIn">
      {/* Header minimalista */}
      <div className="text-center mb-12 animate-slideDown">
        <div className="w-16 h-0.5 bg-slate-300 mx-auto mb-8"></div>
        <p className="text-slate-400 text-sm uppercase tracking-[0.3em] mb-4">Casamento</p>
      </div>
      
      {/* Nomes */}
      <div className="text-center mb-12">
        <h1 className="text-5xl md:text-6xl font-light text-slate-800 mb-4 animate-fadeIn" style={{animationDelay: '0.3s'}}>
          {wedding?.partner1_name || 'Noiva'}
        </h1>
        <p className="text-slate-300 text-3xl">&</p>
        <h1 className="text-5xl md:text-6xl font-light text-slate-800 mt-4 animate-fadeIn" style={{animationDelay: '0.5s'}}>
          {wedding?.partner2_name || 'Noivo'}
        </h1>
      </div>
      
      {/* Mensagem */}
      <p className="text-slate-500 text-center text-lg mb-12 leading-relaxed animate-fadeIn" style={{animationDelay: '0.7s'}}>
        {message}
      </p>
      
      {/* Data */}
      <div className="text-center mb-12 animate-fadeIn" style={{animationDelay: '0.9s'}}>
        <p className="text-3xl font-light text-slate-800 mb-2">
          {wedding?.event_date ? new Date(wedding.event_date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '--/--/----'}
        </p>
        {wedding?.event_time && (
          <p className="text-slate-400">{wedding.event_time}</p>
        )}
        {wedding?.venue_name && (
          <p className="text-slate-400 text-sm mt-2">{wedding.venue_name}</p>
        )}
      </div>
      
      {/* CTA */}
      <div className="text-center animate-fadeIn" style={{animationDelay: '1.1s'}}>
        {isConfirmed ? (
          <div className="border-2 border-emerald-500 text-emerald-600 rounded-none px-12 py-4 inline-flex items-center gap-3">
            <Check size={20} />
            <span className="uppercase tracking-wider text-sm">Confirmado</span>
          </div>
        ) : (
          <button
            onClick={onConfirm}
            className="border-2 border-slate-800 text-slate-800 hover:bg-slate-800 hover:text-white rounded-none px-12 py-4 uppercase tracking-wider text-sm transition-all"
          >
            Confirmar presença
          </button>
        )}
      </div>
      
      {/* Footer */}
      <div className="text-center mt-12 animate-slideUp" style={{animationDelay: '1.3s'}}>
        <div className="w-16 h-0.5 bg-slate-300 mx-auto"></div>
      </div>
    </div>
  </div>
);

// Template Romântico Suave
const TemplateRomantico = ({ wedding, guest, message, onConfirm, isConfirmed }) => (
  <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-rose-50 flex items-center justify-center p-4">
    <div className="max-w-md w-full text-center animate-fadeIn">
      {/* Flores decorativas */}
      <div className="text-rose-200 mb-6 animate-slideDown">
        <Flower2 className="w-12 h-12 mx-auto" />
      </div>
      
      {/* Título */}
      <p className="text-rose-400 text-sm uppercase tracking-wider mb-6 animate-fadeIn" style={{animationDelay: '0.2s'}}>
        Convidamos você para celebrar
      </p>
      
      {/* Nomes com estilo romântico */}
      <div className="relative mb-8">
        <h1 className="font-serif italic text-4xl md:text-5xl text-slate-700 mb-3 animate-fadeIn" style={{animationDelay: '0.4s'}}>
          {wedding?.partner1_name || 'Noiva'}
        </h1>
        <div className="flex items-center justify-center gap-4 my-4">
          <div className="w-12 h-px bg-rose-300"></div>
          <Heart className="w-5 h-5 text-rose-400 fill-rose-400" />
          <div className="w-12 h-px bg-rose-300"></div>
        </div>
        <h1 className="font-serif italic text-4xl md:text-5xl text-slate-700 animate-fadeIn" style={{animationDelay: '0.6s'}}>
          {wedding?.partner2_name || 'Noivo'}
        </h1>
      </div>
      
      {/* Mensagem */}
      <p className="text-slate-500 italic text-lg mb-10 leading-relaxed px-4 animate-fadeIn" style={{animationDelay: '0.8s'}}>
        "{message}"
      </p>
      
      {/* Card com data */}
      <div className="bg-white rounded-3xl p-8 mb-8 shadow-sm border border-rose-100 animate-fadeIn" style={{animationDelay: '1s'}}>
        <Calendar className="w-8 h-8 text-rose-400 mx-auto mb-4" />
        <p className="text-slate-700 font-medium text-lg mb-1">
          {wedding?.event_date ? new Date(wedding.event_date).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Data a confirmar'}
        </p>
        {wedding?.event_time && (
          <p className="text-rose-500">{wedding.event_time}</p>
        )}
        {wedding?.venue_name && (
          <p className="text-slate-400 text-sm mt-3 flex items-center justify-center gap-1">
            <MapPin size={14} />
            {wedding.venue_name}
          </p>
        )}
      </div>
      
      {/* Botão */}
      {isConfirmed ? (
        <div className="bg-rose-100 text-rose-600 rounded-full px-8 py-4 inline-flex items-center gap-2 animate-fadeIn">
          <Heart className="w-5 h-5 fill-rose-500" />
          <span className="font-medium">Presença confirmada!</span>
        </div>
      ) : (
        <button
          onClick={onConfirm}
          className="bg-gradient-to-r from-rose-400 to-pink-400 hover:from-rose-500 hover:to-pink-500 text-white rounded-full px-10 py-4 font-medium transition-all hover:scale-105 shadow-lg animate-fadeIn"
          style={{animationDelay: '1.2s'}}
        >
          <Heart className="w-5 h-5 inline mr-2" />
          Confirmar presença
        </button>
      )}
      
      {/* Rodapé */}
      <p className="text-rose-300 text-sm mt-10 animate-slideUp" style={{animationDelay: '1.4s'}}>
        Com amor, {wedding?.partner1_name} & {wedding?.partner2_name}
      </p>
    </div>
  </div>
);

// Mapa de templates
const INVITE_TEMPLATES = {
  classico: { name: 'Clássico Elegante', component: TemplateClassico, preview: 'bg-amber-100' },
  moderno: { name: 'Moderno Minimalista', component: TemplateModerno, preview: 'bg-slate-100' },
  romantico: { name: 'Romântico Suave', component: TemplateRomantico, preview: 'bg-rose-100' }
};

// Digital Invite Section Component
function DigitalInviteSection({ wedding, invites }) {
  const [loading, setLoading] = useState(true);
  const [digitalInvites, setDigitalInvites] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('classico');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [previewInvite, setPreviewInvite] = useState(null);
  const [tom, setTom] = useState('classico');
  
  // Buscar convites digitais
  useEffect(() => {
    let isMounted = true;
    if (!wedding?.id) return;
    
    const fetchDigitalInvites = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/weddings/${wedding.id}/digital-invites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (isMounted) setDigitalInvites(res.data || []);
      } catch (err) {
        console.error('Erro ao buscar convites digitais:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    fetchDigitalInvites();
    return () => { isMounted = false; };
  }, [wedding?.id]);

  // Gerar mensagem com IA
  const generateMessage = async () => {
    if (!selectedGuest) return;
    
    setLoadingMessage(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/ai/generate-invite-text`, {
        wedding_id: wedding.id,
        guest_id: selectedGuest.id,
        tom: tom
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedMessage(res.data.message);
    } catch (err) {
      toast.error('Erro ao gerar mensagem');
      // Fallback message
      setGeneratedMessage(`Com muito carinho, convidamos você para celebrar conosco o dia mais especial das nossas vidas.`);
    } finally {
      setLoadingMessage(false);
    }
  };

  // Criar convite digital
  const handleCreateInvite = async () => {
    if (!selectedGuest || !generatedMessage) {
      toast.error('Selecione um convidado e gere a mensagem');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/weddings/${wedding.id}/digital-invites`, {
        guest_id: selectedGuest.id,
        template: selectedTemplate,
        message: generatedMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDigitalInvites(prev => [...prev, res.data]);
      toast.success('Convite criado com sucesso.');
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar convite');
    }
  };

  // Copiar link
  const copyLink = (invite) => {
    const url = `${window.location.origin}/convite/${invite.token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado.');
  };

  // Reset form
  const resetForm = () => {
    setSelectedGuest(null);
    setSelectedTemplate('classico');
    setGeneratedMessage('');
    setTom('classico');
  };

  // Status labels
  const statusLabels = {
    sent: { label: 'Enviado', color: 'bg-amber-100 text-amber-700', icon: Clock },
    confirmed: { label: 'Presença confirmada', color: 'bg-emerald-100 text-emerald-700', icon: Check },
    declined: { label: 'Não confirmado', color: 'bg-red-100 text-red-700', icon: X }
  };

  // Convidados que ainda não têm convite digital
  const availableGuests = invites.filter(inv => 
    !digitalInvites.find(di => di.guest_id === inv.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="animate-spin text-pink-500" size={24} />
        <span className="ml-2 text-slate-500">Carregando convites...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800">Convite digital animado</h3>
          <p className="text-sm text-slate-500">
            Crie e compartilhe um convite moderno, elegante e fácil de confirmar presença.
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)} 
          className="bg-pink-600 hover:bg-pink-700"
          disabled={availableGuests.length === 0}
          data-testid="create-invite-btn"
        >
          <Sparkles size={18} className="mr-2" />
          Criar convite
        </Button>
      </div>

      {/* Lista de convites */}
      {digitalInvites.length === 0 ? (
        <Card className="p-8 text-center">
          <Mail size={48} className="mx-auto mb-3 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">Nenhum convite digital criado</h3>
          <p className="text-slate-500 text-sm mb-4 max-w-md mx-auto">
            Você ainda não criou nenhum convite digital. Crie seu primeiro convite e facilite a confirmação dos convidados.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Template</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Convidado</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                <th className="text-left p-4 text-sm font-medium text-slate-600">Enviado em</th>
                <th className="text-right p-4 text-sm font-medium text-slate-600">Ações</th>
              </tr>
            </thead>
            <tbody>
              {digitalInvites.map((invite) => {
                const status = statusLabels[invite.status] || statusLabels.sent;
                const StatusIcon = status.icon;
                const guest = invites.find(i => i.id === invite.guest_id);
                const template = INVITE_TEMPLATES[invite.template];
                
                return (
                  <tr key={invite.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded ${template?.preview || 'bg-slate-100'}`}></div>
                        <span className="text-sm text-slate-700">{template?.name || invite.template}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-800">{guest?.name || 'Convidado'}</span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${status.color}`}>
                        <StatusIcon size={12} />
                        {status.label}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-500">
                      {invite.sent_at ? new Date(invite.sent_at).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => { setPreviewInvite(invite); setShowPreviewModal(true); }}
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-pink-600"
                          title="Visualizar"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => copyLink(invite)}
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-blue-600"
                          title="Copiar link"
                        >
                          <Copy size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modal Criar Convite */}
      <Modal 
        isOpen={showCreateModal} 
        onClose={() => { setShowCreateModal(false); resetForm(); }} 
        title="Criar convite digital"
      >
        <div className="space-y-6">
          {/* Seleção de convidado */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Convidado</label>
            <select
              value={selectedGuest?.id || ''}
              onChange={(e) => {
                const guest = invites.find(i => i.id === e.target.value);
                setSelectedGuest(guest);
                setGeneratedMessage('');
              }}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            >
              <option value="">Selecione um convidado</option>
              {availableGuests.map(guest => (
                <option key={guest.id} value={guest.id}>{guest.name}</option>
              ))}
            </select>
          </div>

          {/* Seleção de template */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Template</label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(INVITE_TEMPLATES).map(([key, template]) => (
                <button
                  key={key}
                  onClick={() => setSelectedTemplate(key)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedTemplate === key 
                      ? 'border-pink-500 bg-pink-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-full h-16 rounded-lg ${template.preview} mb-2`}></div>
                  <p className="text-xs font-medium text-slate-700">{template.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Tom da mensagem */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Tom da mensagem</label>
            <div className="flex gap-2">
              {['classico', 'romantico', 'moderno'].map(t => (
                <button
                  key={t}
                  onClick={() => setTom(t)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    tom === t 
                      ? 'bg-pink-100 text-pink-700' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Gerar mensagem */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700">Mensagem</label>
              <Button
                variant="secondary"
                size="sm"
                onClick={generateMessage}
                disabled={!selectedGuest || loadingMessage}
              >
                {loadingMessage ? (
                  <Loader2 className="animate-spin" size={14} />
                ) : (
                  <Sparkles size={14} />
                )}
                <span className="ml-1">Gerar com IA</span>
              </Button>
            </div>
            <textarea
              value={generatedMessage}
              onChange={(e) => setGeneratedMessage(e.target.value)}
              placeholder="Clique em 'Gerar com IA' ou escreva sua mensagem..."
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
              rows={4}
            />
          </div>

          {/* Botões */}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setShowCreateModal(false); resetForm(); }} className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateInvite}
              disabled={!selectedGuest || !generatedMessage}
              className="flex-1 bg-pink-600 hover:bg-pink-700"
            >
              Criar convite
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Preview */}
      <Modal 
        isOpen={showPreviewModal} 
        onClose={() => { setShowPreviewModal(false); setPreviewInvite(null); }} 
        title="Visualizar convite"
        size="lg"
      >
        {previewInvite && (
          <div className="space-y-4">
            <div className="rounded-xl overflow-hidden border border-slate-200" style={{height: '500px', overflow: 'auto'}}>
              {React.createElement(INVITE_TEMPLATES[previewInvite.template]?.component || TemplateClassico, {
                wedding,
                guest: invites.find(i => i.id === previewInvite.guest_id),
                message: previewInvite.message,
                onConfirm: () => {},
                isConfirmed: previewInvite.status === 'confirmed'
              })}
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Compartilhe o convite por WhatsApp, email ou onde preferir.</p>
              <Button onClick={() => copyLink(previewInvite)} variant="secondary">
                <Copy size={16} className="mr-2" />
                Copiar link do convite
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ==================== SEÇÃO DE CONVITES PERSONALIZADOS (UPLOAD) ====================

function CustomInviteSection({ wedding }) {
  const [loading, setLoading] = useState(true);
  const [customInvites, setCustomInvites] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  
  // Form state
  const [inviteName, setInviteName] = useState('');
  const [inviteDescription, setInviteDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Buscar convites personalizados
  useEffect(() => {
    if (!wedding?.id) return;
    fetchCustomInvites();
  }, [wedding?.id]);
  
  const fetchCustomInvites = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/weddings/${wedding.id}/custom-invites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomInvites(res.data || []);
    } catch (err) {
      console.error('Erro ao buscar convites:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle file selection
  const handleFileSelect = (file) => {
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use PDF, PNG ou JPG.');
      return;
    }
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Arquivo muito grande. Limite: 10MB.');
      return;
    }
    
    setSelectedFile(file);
    
    // Generate preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };
  
  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  
  const handleDragLeave = () => {
    setDragOver(false);
  };
  
  // Upload invite
  const handleUpload = async () => {
    if (!selectedFile || !inviteName.trim()) {
      toast.error('Preencha o nome e selecione um arquivo.');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', inviteName.trim());
      formData.append('description', inviteDescription.trim());
      
      const res = await axios.post(
        `${API}/weddings/${wedding.id}/custom-invites`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );
      
      setCustomInvites(prev => [res.data, ...prev]);
      toast.success('Convite enviado com sucesso!');
      resetForm();
      setShowUploadModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar convite');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Delete invite
  const handleDelete = async (inviteId) => {
    if (!(await confirmDialog({
      title: "Excluir convite",
      message: "Tem certeza que deseja excluir este convite?",
      confirmLabel: "Excluir",
      variant: "danger",
    }))) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/weddings/${wedding.id}/custom-invites/${inviteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomInvites(prev => prev.filter(i => i.id !== inviteId));
      toast.success('Convite excluído.');
    } catch (err) {
      toast.error('Erro ao excluir convite');
    }
  };
  
  // Copy link
  const copyLink = (invite) => {
    const url = `${window.location.origin}/convite-personalizado/${invite.token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado!');
  };
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  // Reset form
  const resetForm = () => {
    setInviteName('');
    setInviteDescription('');
    setSelectedFile(null);
    setPreviewUrl(null);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-emerald-600" size={32} />
        <span className="ml-2 text-slate-500">Carregando convites...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Convites Personalizados</h2>
          <p className="text-sm text-slate-500 mt-1">
            Faça upload do seu convite criado externamente (PDF, PNG ou JPG)
          </p>
        </div>
        <Button onClick={() => setShowUploadModal(true)}>
          <Plus size={18} className="mr-2" />
          Fazer Upload
        </Button>
      </div>
      
      {/* Lista de convites */}
      {customInvites.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
          <FileText className="mx-auto text-slate-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-slate-700 mb-2">Nenhum convite enviado</h3>
          <p className="text-slate-500 mb-4">
            Faça upload de convites criados no Canva, Photoshop ou qualquer outro programa.
          </p>
          <Button onClick={() => setShowUploadModal(true)} variant="secondary">
            <Plus size={18} className="mr-2" />
            Enviar primeiro convite
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customInvites.map((invite) => (
            <Card key={invite.id} className="overflow-hidden">
              {/* Preview */}
              <div 
                className="h-48 bg-slate-100 flex items-center justify-center cursor-pointer relative group"
                onClick={() => { setSelectedInvite(invite); setShowPreviewModal(true); }}
              >
                {invite.preview_url ? (
                  <img 
                    src={invite.preview_url} 
                    alt={invite.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <FileText className="text-slate-400" size={64} />
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Eye className="text-white" size={32} />
                </div>
              </div>
              
              {/* Info */}
              <div className="p-4">
                <h4 className="font-medium text-slate-800 truncate">{invite.name}</h4>
                {invite.description && (
                  <p className="text-sm text-slate-500 truncate mt-1">{invite.description}</p>
                )}
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                  <span className="uppercase">{invite.file_type}</span>
                  <span>•</span>
                  <span>{formatFileSize(invite.file_size)}</span>
                  {invite.version > 1 && (
                    <>
                      <span>•</span>
                      <span>v{invite.version}</span>
                    </>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2 mt-4">
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => copyLink(invite)}
                  >
                    <Link2 size={14} className="mr-1" />
                    Copiar Link
                  </Button>
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => window.open(invite.download_url, '_blank')}
                  >
                    <Download size={14} />
                  </Button>
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => handleDelete(invite.id)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      
      {/* Modal de Upload */}
      <Modal isOpen={showUploadModal} onClose={() => { setShowUploadModal(false); resetForm(); }} title="Enviar Convite Personalizado">
        <div className="space-y-4">
          {/* Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
              ${dragOver ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 hover:border-emerald-400'}
              ${selectedFile ? 'bg-emerald-50 border-emerald-500' : ''}
            `}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => handleFileSelect(e.target.files[0])}
            />
            
            {selectedFile ? (
              <div className="space-y-3">
                {previewUrl ? (
                  <img src={previewUrl} alt="Preview" className="max-h-40 mx-auto rounded-lg" />
                ) : (
                  <FileText className="mx-auto text-emerald-600" size={48} />
                )}
                <p className="font-medium text-emerald-700">{selectedFile.name}</p>
                <p className="text-sm text-slate-500">{formatFileSize(selectedFile.size)}</p>
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setPreviewUrl(null); }}
                >
                  Trocar arquivo
                </Button>
              </div>
            ) : (
              <>
                <Download className="mx-auto text-slate-400 mb-3 rotate-180" size={48} />
                <p className="text-slate-600 font-medium">Arraste um arquivo aqui</p>
                <p className="text-sm text-slate-500 mt-1">ou clique para selecionar</p>
                <p className="text-xs text-slate-400 mt-2">PDF, PNG ou JPG até 10MB</p>
              </>
            )}
          </div>
          
          {/* Nome do convite */}
          <Input
            label="Nome do Convite *"
            placeholder="Ex: Convite Principal, Save the Date..."
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
          />
          
          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Descrição (opcional)
            </label>
            <textarea
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              rows={2}
              placeholder="Breve descrição do convite..."
              value={inviteDescription}
              onChange={(e) => setInviteDescription(e.target.value)}
            />
          </div>
          
          {/* Progress bar */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Enviando...</span>
                <span className="text-emerald-600">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div 
                  className="bg-emerald-600 h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
          
          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <Button 
              variant="secondary" 
              className="flex-1"
              onClick={() => { setShowUploadModal(false); resetForm(); }}
              disabled={uploading}
            >
              Cancelar
            </Button>
            <Button 
              className="flex-1"
              onClick={handleUpload}
              disabled={uploading || !selectedFile || !inviteName.trim()}
            >
              {uploading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Enviando...
                </>
              ) : (
                <>
                  <Plus size={18} className="mr-2" />
                  Enviar Convite
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
      
      {/* Modal de Preview */}
      <Modal 
        isOpen={showPreviewModal} 
        onClose={() => { setShowPreviewModal(false); setSelectedInvite(null); }} 
        title={selectedInvite?.name || 'Visualizar Convite'}
      >
        {selectedInvite && (
          <div className="space-y-4">
            {/* Preview Image */}
            <div className="bg-slate-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[300px]">
              {selectedInvite.preview_url ? (
                <img 
                  src={selectedInvite.preview_url} 
                  alt={selectedInvite.name}
                  className="max-w-full max-h-[500px] object-contain"
                />
              ) : (
                <div className="text-center py-12">
                  <FileText className="mx-auto text-slate-400 mb-3" size={64} />
                  <p className="text-slate-500">Preview não disponível para PDF</p>
                  <p className="text-sm text-slate-400 mt-1">Faça o download para visualizar</p>
                </div>
              )}
            </div>
            
            {/* Info */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Tipo:</span>
                  <span className="ml-2 font-medium uppercase">{selectedInvite.file_type}</span>
                </div>
                <div>
                  <span className="text-slate-500">Tamanho:</span>
                  <span className="ml-2 font-medium">{formatFileSize(selectedInvite.file_size)}</span>
                </div>
                {selectedInvite.version > 1 && (
                  <div>
                    <span className="text-slate-500">Versão:</span>
                    <span className="ml-2 font-medium">{selectedInvite.version}</span>
                  </div>
                )}
              </div>
              {selectedInvite.description && (
                <p className="text-slate-600 mt-3">{selectedInvite.description}</p>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex gap-3">
              <Button 
                variant="secondary" 
                className="flex-1"
                onClick={() => copyLink(selectedInvite)}
              >
                <Link2 size={18} className="mr-2" />
                Copiar Link Público
              </Button>
              <Button 
                className="flex-1"
                onClick={() => window.open(selectedInvite.download_url, '_blank')}
              >
                <Download size={18} className="mr-2" />
                Fazer Download
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ==================== SEÇÃO DE SITE DO CASAMENTO ====================

function WeddingSiteSection({ wedding }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weddingSite, setWeddingSite] = useState(null);
  const [siteType, setSiteType] = useState('external');
  const [externalUrl, setExternalUrl] = useState('');
  const [siteName, setSiteName] = useState('Nosso Site de Casamento');
  const [siteDescription, setSiteDescription] = useState('');
  const [showGiftList, setShowGiftList] = useState(true);
  
  useEffect(() => {
    if (!wedding?.id) return;
    fetchWeddingSite();
  }, [wedding?.id]);
  
  const fetchWeddingSite = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/weddings/${wedding.id}/wedding-site`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setWeddingSite(res.data);
        setSiteType(res.data.site_type || 'external');
        setExternalUrl(res.data.external_url || '');
        setSiteName(res.data.name || 'Nosso Site de Casamento');
        setSiteDescription(res.data.description || '');
        setShowGiftList(res.data.show_gift_list !== false);
      }
    } catch (err) {
      console.error('Erro ao buscar site:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    if (siteType === 'external' && !externalUrl.trim()) {
      toast.error('Insira a URL do site');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('site_type', siteType);
      formData.append('external_url', externalUrl.trim());
      formData.append('name', siteName.trim());
      formData.append('description', siteDescription.trim());
      formData.append('show_gift_list', showGiftList);
      
      const res = await axios.post(
        `${API}/weddings/${wedding.id}/wedding-site`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setWeddingSite(res.data);
      toast.success('Site salvo com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };
  
  const copyLink = () => {
    if (!weddingSite?.public_url) return;
    navigator.clipboard.writeText(weddingSite.public_url);
    toast.success('Link copiado!');
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-emerald-600" size={32} />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-slate-800">Site do Casamento</h2>
        <p className="text-sm text-slate-500 mt-1">
          Configure o site do seu casamento com link para a lista de presentes
        </p>
      </div>
      
      <Card className="p-6">
        <div className="space-y-6">
          {/* Tipo de site */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-3">
              Tipo de Site
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                className={`p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                  siteType === 'external' 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-slate-200 hover:border-emerald-300'
                }`}
                onClick={() => setSiteType('external')}
              >
                <div className="flex items-center gap-3">
                  <Link2 className={siteType === 'external' ? 'text-emerald-600' : 'text-slate-400'} size={24} />
                  <div>
                    <p className="font-medium text-slate-800">Link Externo</p>
                    <p className="text-sm text-slate-500">Canva, Wix, WordPress, etc.</p>
                  </div>
                </div>
              </div>
              
              <div
                className={`p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                  siteType === 'file' 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-slate-200 hover:border-emerald-300'
                }`}
                onClick={() => setSiteType('file')}
              >
                <div className="flex items-center gap-3">
                  <FileText className={siteType === 'file' ? 'text-emerald-600' : 'text-slate-400'} size={24} />
                  <div>
                    <p className="font-medium text-slate-800">Arquivo Estático</p>
                    <p className="text-sm text-slate-500">PDF ou imagem do site</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* URL Externa */}
          {siteType === 'external' && (
            <Input
              label="URL do Site"
              placeholder="https://seu-site.canva.site ou https://seusite.com"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
            />
          )}
          
          {/* Nome do site */}
          <Input
            label="Nome do Site"
            placeholder="Nosso Site de Casamento"
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
          />
          
          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Descrição (opcional)
            </label>
            <textarea
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
              rows={2}
              placeholder="Uma breve descrição do site..."
              value={siteDescription}
              onChange={(e) => setSiteDescription(e.target.value)}
            />
          </div>
          
          {/* Mostrar lista de presentes */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="show-gift-list"
              checked={showGiftList}
              onChange={(e) => setShowGiftList(e.target.checked)}
              className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
            />
            <label htmlFor="show-gift-list" className="text-sm text-slate-700">
              Mostrar lista de presentes no link público
            </label>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Salvando...
                </>
              ) : (
                <>
                  <Check size={18} className="mr-2" />
                  Salvar Site
                </>
              )}
            </Button>
            
            {weddingSite?.public_url && (
              <>
                <Button variant="secondary" onClick={copyLink}>
                  <Copy size={18} className="mr-2" />
                  Copiar Link
                </Button>
                <Button 
                  variant="secondary"
                  onClick={() => window.open(weddingSite.public_url, '_blank')}
                >
                  <Eye size={18} className="mr-2" />
                  Visualizar
                </Button>
              </>
            )}
          </div>
          
          {/* Link público */}
          {weddingSite?.public_url && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-sm text-emerald-700 font-medium mb-2">Link público do seu site:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-white px-3 py-2 rounded border text-sm text-slate-700 overflow-x-auto">
                  {weddingSite.public_url}
                </code>
                <Button variant="secondary" size="sm" onClick={copyLink}>
                  <Copy size={16} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ==================== PÁGINA PÚBLICA DO CONVITE PERSONALIZADO ====================

function PublicCustomInvitePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API}/custom-invite/${token}`);
        setInviteData(res.data);
      } catch (err) {
        console.error("Erro ao buscar convite:", err);
        setError("Convite não encontrado ou link inválido.");
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Loader2 className="animate-spin text-emerald-600" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h1 className="text-xl font-semibold text-slate-800">{error}</h1>
        </div>
      </div>
    );
  }

  const { invite, wedding } = inviteData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Heart className="mx-auto text-rose-500 mb-4" size={48} />
          <h1 className="text-3xl font-bold text-slate-800">
            {wedding.partner1_name} & {wedding.partner2_name}
          </h1>
          {wedding.event_date && (
            <p className="text-lg text-slate-600 mt-2">
              {new Date(wedding.event_date).toLocaleDateString('pt-BR', { 
                day: 'numeric', month: 'long', year: 'numeric' 
              })}
              {wedding.event_time && ` às ${wedding.event_time}`}
            </p>
          )}
          {wedding.venue_name && (
            <p className="text-slate-500 mt-1">{wedding.venue_name}</p>
          )}
        </div>

        {/* Convite */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-xl font-semibold text-slate-800">{invite.name}</h2>
            {invite.description && (
              <p className="text-slate-500 mt-1">{invite.description}</p>
            )}
          </div>
          
          {/* Preview/Imagem */}
          <div className="bg-slate-100 flex items-center justify-center min-h-[400px]">
            {invite.preview_url ? (
              <img 
                src={invite.preview_url} 
                alt={invite.name}
                className="max-w-full max-h-[600px] object-contain"
              />
            ) : (
              <div className="text-center py-12">
                <FileText className="mx-auto text-slate-400 mb-3" size={64} />
                <p className="text-slate-500">Faça o download para visualizar</p>
              </div>
            )}
          </div>
          
          {/* Download */}
          <div className="p-6 bg-slate-50">
            <Button 
              className="w-full"
              onClick={() => window.open(invite.download_url, '_blank')}
            >
              <Download size={18} className="mr-2" />
              Baixar Convite ({invite.file_type.toUpperCase()})
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-slate-400 text-sm">
          <p>Feito com <span className="font-semibold" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.02em' }}>liventy</span></p>
        </div>
      </div>
    </div>
  );
}

// ==================== PÁGINA PÚBLICA DO SITE DE CASAMENTO ====================

function PublicWeddingSitePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [siteData, setSiteData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSite = async () => {
      try {
        const res = await axios.get(`${API}/site/${token}`);
        setSiteData(res.data);
      } catch (err) {
        console.error("Erro ao buscar site:", err);
        setError("Site não encontrado ou link inválido.");
      } finally {
        setLoading(false);
      }
    };
    fetchSite();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Loader2 className="animate-spin text-emerald-600" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h1 className="text-xl font-semibold text-slate-800">{error}</h1>
        </div>
      </div>
    );
  }

  const { site, wedding, gift_list } = siteData;

  // Se for link externo, redirecionar
  if (site.site_type === 'external' && site.external_url) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Heart className="mx-auto text-rose-500 mb-4" size={48} />
            <h1 className="text-3xl font-bold text-slate-800">
              {wedding.partner1_name} & {wedding.partner2_name}
            </h1>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">{site.name}</h2>
            {site.description && (
              <p className="text-slate-500 mb-6">{site.description}</p>
            )}
            <Button 
              className="w-full"
              onClick={() => window.open(site.external_url, '_blank')}
            >
              <ArrowRight size={18} className="mr-2" />
              Acessar Site do Casamento
            </Button>
          </div>

          {/* Lista de Presentes */}
          {site.show_gift_list && gift_list && gift_list.items?.length > 0 && (
            <div className="mt-8 bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-6">
                <Gift className="mx-auto text-emerald-500 mb-2" size={32} />
                <h2 className="text-xl font-semibold text-slate-800">Lista de Presentes</h2>
                {gift_list.message && (
                  <p className="text-slate-500 mt-2">{gift_list.message}</p>
                )}
              </div>
              
              <div className="grid gap-4">
                {gift_list.items.map((item, index) => (
                  <div 
                    key={index} 
                    className={`p-4 border rounded-xl ${item.is_purchased ? 'bg-slate-50 opacity-60' : 'bg-white'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`font-medium ${item.is_purchased ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                          {item.name}
                        </h3>
                        {item.description && (
                          <p className="text-sm text-slate-500">{item.description}</p>
                        )}
                        {item.price && (
                          <p className="text-sm text-emerald-600 font-medium mt-1">
                            R$ {item.price.toFixed(2)}
                          </p>
                        )}
                      </div>
                      {item.link && !item.is_purchased && (
                        <Button 
                          variant="secondary" 
                          size="sm"
                          onClick={() => window.open(item.link, '_blank')}
                        >
                          Comprar
                        </Button>
                      )}
                      {item.is_purchased && (
                        <span className="text-sm text-slate-400 flex items-center gap-1">
                          <Check size={16} />
                          Comprado
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center mt-8 text-slate-400 text-sm">
            <p>Feito com <span className="font-semibold" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.02em' }}>liventy</span></p>
          </div>
        </div>
      </div>
    );
  }

  // Se for arquivo, mostrar preview/download
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Heart className="mx-auto text-rose-500 mb-4" size={48} />
          <h1 className="text-3xl font-bold text-slate-800">
            {wedding.partner1_name} & {wedding.partner2_name}
          </h1>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-xl font-semibold text-slate-800">{site.name}</h2>
          </div>
          
          {site.preview_url && (
            <div className="bg-slate-100 flex items-center justify-center min-h-[400px]">
              <img 
                src={site.preview_url} 
                alt={site.name}
                className="max-w-full max-h-[600px] object-contain"
              />
            </div>
          )}
          
          {site.file_download_url && (
            <div className="p-6 bg-slate-50">
              <Button 
                className="w-full"
                onClick={() => window.open(site.file_download_url, '_blank')}
              >
                <Download size={18} className="mr-2" />
                Baixar Site
              </Button>
            </div>
          )}
        </div>

        {/* Lista de Presentes */}
        {site.show_gift_list && gift_list && gift_list.items?.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <Gift className="mx-auto text-emerald-500 mb-2" size={32} />
              <h2 className="text-xl font-semibold text-slate-800">Lista de Presentes</h2>
            </div>
            
            <div className="grid gap-4">
              {gift_list.items.map((item, index) => (
                <div 
                  key={index} 
                  className={`p-4 border rounded-xl ${item.is_purchased ? 'bg-slate-50 opacity-60' : 'bg-white'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className={`font-medium ${item.is_purchased ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                        {item.name}
                      </h3>
                      {item.price && (
                        <p className="text-sm text-emerald-600 font-medium mt-1">
                          R$ {item.price.toFixed(2)}
                        </p>
                      )}
                    </div>
                    {item.link && !item.is_purchased && (
                      <Button 
                        variant="secondary" 
                        size="sm"
                        onClick={() => window.open(item.link, '_blank')}
                      >
                        Comprar
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-8 text-slate-400 text-sm">
          <p>Feito com <span className="font-semibold" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.02em' }}>liventy</span></p>
        </div>
      </div>
    </div>
  );
}

// ==================== PÁGINA PÚBLICA DO CASAL (SLUG) ====================
// Renderiza o site público do casal usando templates (Classic, Modern, Romantic)

function PublicCasalPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [casalData, setCasalData] = useState(null);
  const [presentes, setPresentes] = useState([]);
  const [error, setError] = useState(null);
  const [selectedPresente, setSelectedPresente] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ nome: '', mensagem: '' });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    const fetchCasalData = async () => {
      try {
        // Buscar dados do casal (nova estrutura com site_template e site_data)
        // Cache-bust com timestamp para sempre pegar a versão mais recente
        const ts = Date.now();
        const casalRes = await axios.get(`${API}/public/casal/${slug}?t=${ts}`, {
          headers: { 'Cache-Control': 'no-cache' }
        });
        setCasalData(casalRes.data.casal);
        
        // Buscar presentes
        const presentesRes = await axios.get(`${API}/public/casal/${slug}/presentes?t=${ts}`, {
          headers: { 'Cache-Control': 'no-cache' }
        });
        setPresentes(presentesRes.data.itens || []);
      } catch (err) {
        console.error("Erro ao buscar dados do casal:", err);
        setError("Página não encontrada ou link inválido.");
      } finally {
        setLoading(false);
      }
    };
    fetchCasalData();

    // Refetch quando a aba volta a ter foco (sincroniza com edições no painel)
    const handleFocus = () => {
      if (document.visibilityState === 'visible') {
        fetchCasalData();
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [slug]);

  // Atualiza dinamicamente as meta tags Open Graph para o card de compartilhamento
  // (funciona para crawlers que executam JS; WhatsApp/Facebook ainda usam o default)
  useEffect(() => {
    if (!casalData) return;
    const ogImageUrl = `${API}/public/og/casal/${slug}.png`;
    const title = casalData.nomes || casalData.event_name || "Você foi convidado";
    const description = casalData.local ? `${title} · ${casalData.local}` : `${title} · Liventy`;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const [_, key, val] = selector.match(/\[(.+?)="(.+?)"\]/) || [];
        if (key && val) el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:image"]', 'content', ogImageUrl);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', ogImageUrl);
    document.title = `${title} — Liventy`;
  }, [casalData, slug]);

  const handlePresentear = (presente) => {
    setSelectedPresente(presente);
    setShowPaymentModal(true);
    setPaymentResult(null);
    setPaymentForm({ nome: '', mensagem: '' });
  };

  const handlePayment = async () => {
    if (!paymentForm.nome.trim()) {
      toast.error('Por favor, informe seu nome');
      return;
    }

    setPaymentLoading(true);
    try {
      const res = await axios.post(`${API}/public/pagamentos`, {
        casal_slug: slug,
        presente_id: selectedPresente.id,
        nome_convidado: paymentForm.nome,
        mensagem: paymentForm.mensagem,
        metodo_pagamento: 'pix'
      });
      setPaymentResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar pagamento');
    } finally {
      setPaymentLoading(false);
    }
  };

  const copyPixCode = () => {
    if (paymentResult?.pix?.copia_e_cola) {
      navigator.clipboard.writeText(paymentResult.pix.copia_e_cola);
      toast.success('Código PIX copiado!');
    }
  };

  const formatCurrency = (centavos) => {
    return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-amber-50">
        <Loader2 className="animate-spin text-rose-500" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="text-center max-w-md px-6">
          <Heart className="mx-auto text-rose-300 mb-4" size={64} />
          <h1 className="text-2xl font-semibold text-slate-800 mb-2">Site não disponível</h1>
          <p className="text-slate-600 mb-6">
            Este site de casamento ainda não foi publicado ou o link está incorreto.
          </p>
          <p className="text-sm text-slate-500">
            Se você é o casal, acesse o painel administrativo para habilitar seu site.
          </p>
        </div>
      </div>
    );
  }

  // Renderizar o template baseado em site_template
  const renderTemplate = () => {
    const template = casalData?.site_template || 'classic';
    const siteData = casalData?.site_data || {};
    
    const templateProps = {
      siteData,
      presentes,
      onPresentear: handlePresentear,
      formatCurrency
    };

    switch (template) {
      case 'modern':
        return <ModernTemplate {...templateProps} />;
      case 'romantic':
        return <RomanticTemplate {...templateProps} />;
      case 'classic':
      default:
        return <ClassicTemplate {...templateProps} />;
    }
  };

  // Renderizar site com template selecionado
  return (
    <div className="min-h-screen">
      {/* Template do Site */}
      {renderTemplate()}

      {/* Modal de Pagamento PIX (comum a todos os templates) */}
      <Modal 
        isOpen={showPaymentModal} 
        onClose={() => { setShowPaymentModal(false); setPaymentResult(null); }}
        title={paymentResult ? "Pagamento PIX" : `Presentear: ${selectedPresente?.titulo}`}
      >
        {!paymentResult ? (
          <div className="space-y-4">
            <div className="text-center py-4">
              <Gift className="mx-auto text-amber-500 mb-2" size={40} />
              <p className="text-2xl font-bold text-emerald-600">
                {selectedPresente && formatCurrency(selectedPresente.valor)}
              </p>
            </div>
            <Input
              label="Seu nome *"
              placeholder="Como você quer ser identificado"
              value={paymentForm.nome}
              onChange={(e) => setPaymentForm({...paymentForm, nome: e.target.value})}
              data-testid="payment-name-input"
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Mensagem para os noivos (opcional)
              </label>
              <textarea
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 resize-none"
                rows={3}
                placeholder="Deixe uma mensagem carinhosa..."
                value={paymentForm.mensagem}
                onChange={(e) => setPaymentForm({...paymentForm, mensagem: e.target.value})}
                data-testid="payment-message-input"
              />
            </div>
            <Button 
              className="w-full bg-gradient-to-r from-rose-500 to-amber-500"
              onClick={handlePayment}
              disabled={paymentLoading}
              data-testid="generate-pix-btn"
            >
              {paymentLoading ? (
                <><Loader2 className="animate-spin mr-2" size={18} />Gerando PIX...</>
              ) : (
                <><QrCode size={18} className="mr-2" />Gerar QR Code PIX</>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-emerald-600 font-medium mb-2">PIX gerado com sucesso!</p>
              <p className="text-2xl font-bold text-slate-800">
                {selectedPresente && formatCurrency(selectedPresente.valor)}
              </p>
            </div>
            {paymentResult.pix?.qr_code_base64 && (
              <div className="flex flex-col items-center">
                <p className="text-sm font-medium text-slate-600 mb-3">Escaneie o QR Code</p>
                <div className="bg-white p-4 rounded-xl border-2 border-slate-200">
                  <img 
                    src={`data:image/png;base64,${paymentResult.pix.qr_code_base64}`}
                    alt="QR Code PIX"
                    className="w-48 h-48"
                  />
                </div>
              </div>
            )}
            {paymentResult.pix?.copia_e_cola && (
              <div>
                <p className="text-sm font-medium text-slate-600 mb-2">Ou copie o código</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={paymentResult.pix.copia_e_cola}
                    readOnly
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-xs bg-slate-50"
                  />
                  <Button variant="secondary" onClick={copyPixCode} data-testid="copy-pix-btn">
                    <Copy size={16} />
                  </Button>
                </div>
              </div>
            )}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-700">
                <strong>Importante:</strong> Após o pagamento, os noivos serão notificados automaticamente.
              </p>
            </div>
            <Button 
              variant="secondary" 
              className="w-full"
              onClick={() => { setShowPaymentModal(false); setPaymentResult(null); }}
            >
              Fechar
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ==================== PÁGINA PÚBLICA DO CONVITE DIGITAL ====================

function PublicInvitePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isDeclined, setIsDeclined] = useState(false);
  const [form, setForm] = useState({
    confirmed: true,
    adults_count: 1,
    children_count: 0,
    message: ""
  });

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API}/convite/${token}`);
        setInviteData(res.data);
        // Se já respondeu
        if (res.data.invite.status === "confirmed") {
          setIsConfirmed(true);
        } else if (res.data.invite.status === "declined") {
          setIsDeclined(true);
        }
      } catch (err) {
        console.error("Erro ao buscar convite:", err);
        setError("Convite não encontrado ou link inválido.");
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  const handleConfirm = () => {
    setForm(prev => ({ ...prev, confirmed: true }));
    setShowForm(true);
  };

  const handleDecline = () => {
    setForm(prev => ({ ...prev, confirmed: false }));
    setShowForm(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/convite/${token}/confirm`, form);
      if (form.confirmed) {
        setIsConfirmed(true);
      } else {
        setIsDeclined(true);
      }
      setShowForm(false);
    } catch (err) {
      console.error("Erro ao confirmar:", err);
      toast.error("Erro ao registrar resposta. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-rose-500 mx-auto mb-4" size={48} />
          <p className="text-slate-500">Carregando convite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="text-red-500" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Link Inválido</h1>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const { invite, wedding, guest } = inviteData;
  const TemplateComponent = INVITE_TEMPLATES[invite.template]?.component || TemplateClassico;

  // Se mostra o formulário de RSVP
  if (showForm) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full animate-fadeIn">
          <button 
            onClick={() => setShowForm(false)}
            className="mb-4 text-slate-400 hover:text-slate-600 flex items-center gap-1 text-sm"
          >
            <ArrowLeft size={16} />
            Voltar
          </button>
          
          {form.confirmed ? (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="text-emerald-600" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-slate-800">Confirmar presença</h2>
                <p className="text-slate-500 mt-2">Informe quantas pessoas estarão presentes</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Adultos</label>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setForm(prev => ({ ...prev, adults_count: Math.max(1, prev.adults_count - 1) }))}
                      className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="text-2xl font-bold text-slate-800 w-12 text-center">{form.adults_count}</span>
                    <button 
                      onClick={() => setForm(prev => ({ ...prev, adults_count: prev.adults_count + 1 }))}
                      className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Crianças</label>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setForm(prev => ({ ...prev, children_count: Math.max(0, prev.children_count - 1) }))}
                      className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="text-2xl font-bold text-slate-800 w-12 text-center">{form.children_count}</span>
                    <button 
                      onClick={() => setForm(prev => ({ ...prev, children_count: prev.children_count + 1 }))}
                      className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Mensagem (opcional)</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Deixe uma mensagem para os noivos..."
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>
              </div>
              
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full py-4 font-medium transition-all flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    <Check size={20} />
                    Confirmar presença
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="text-slate-500" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-slate-800">Não poderá comparecer?</h2>
                <p className="text-slate-500 mt-2">Sentiremos sua falta!</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Deixe uma mensagem (opcional)</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Deixe uma mensagem para os noivos..."
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none"
                  rows={3}
                />
              </div>
              
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full mt-6 bg-slate-600 hover:bg-slate-700 text-white rounded-full py-4 font-medium transition-all flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  "Enviar resposta"
                )}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Tela de confirmação após responder
  if (isConfirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="text-center animate-fadeIn">
          <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <PartyPopper className="text-emerald-600" size={48} />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Presença confirmada!</h1>
          <p className="text-slate-500 text-lg mb-6">Mal podemos esperar para celebrar com você!</p>
          <div className="bg-white rounded-2xl p-6 shadow-lg max-w-sm mx-auto">
            <p className="text-slate-700 mb-2">
              <span className="font-medium">{wedding?.partner1_name}</span> & <span className="font-medium">{wedding?.partner2_name}</span>
            </p>
            {wedding?.event_date && (
              <p className="text-emerald-600 font-medium">
                {new Date(wedding.event_date).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            )}
            {wedding?.venue_name && (
              <p className="text-slate-400 text-sm mt-2 flex items-center justify-center gap-1">
                <MapPin size={14} />
                {wedding.venue_name}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Tela de recusa
  if (isDeclined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
        <div className="text-center animate-fadeIn">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="text-slate-400" size={48} />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Resposta registrada</h1>
          <p className="text-slate-500 text-lg mb-6">Sentiremos sua falta!</p>
          <p className="text-slate-400 max-w-sm mx-auto">
            Se mudar de ideia, entre em contato com {wedding?.partner1_name} & {wedding?.partner2_name}.
          </p>
        </div>
      </div>
    );
  }

  // Renderizar template do convite
  return (
    <div className="relative">
      <TemplateComponent
        wedding={wedding}
        guest={guest}
        message={invite.message}
        onConfirm={handleConfirm}
        isConfirmed={false}
      />
      
      {/* Botão de recusar discreto */}
      <div className="fixed bottom-6 left-0 right-0 text-center animate-fadeIn" style={{animationDelay: '2s'}}>
        <button
          onClick={handleDecline}
          className="text-slate-400 hover:text-slate-600 text-sm underline transition-colors"
        >
          Não poderei comparecer
        </button>
      </div>
    </div>
  );
}

// ==================== DRAG AND DROP COMPONENTS ====================

// Componente Draggable para Convidado
function DraggableGuest({ guest, tableId }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `guest-${tableId}-${guest.nome || guest}`,
    data: { guest, sourceTableId: tableId }
  });
  
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  const nome = typeof guest === 'string' ? guest : guest.nome;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`
        flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-lg cursor-grab
        hover:border-blue-300 hover:shadow-sm transition-all
        ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-blue-400' : ''}
      `}
    >
      <GripVertical size={14} className="text-slate-400" />
      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
        <span className="text-blue-700 text-xs font-medium">{nome?.charAt(0)?.toUpperCase() || '?'}</span>
      </div>
      <span className="text-sm text-slate-700 truncate">{nome}</span>
    </div>
  );
}

// Componente Droppable para Mesa
function DroppableTable({ mesa, index, tablesConfig, onSelectTable, isOver, canDrop }) {
  const { setNodeRef } = useDroppable({
    id: `table-${mesa.numero || index}`,
    data: { tableId: mesa.numero || index, mesa }
  });

  const ocupacao = mesa.convidados?.length || 0;
  const capacidade = mesa.capacidade || tablesConfig.lugares_por_mesa;
  const isVip = mesa.isVip || mesa.nome?.toLowerCase().includes('padrinho');
  
  // Estado visual
  let bg = 'bg-slate-100';
  let border = 'border-slate-200';
  let text = 'text-slate-400';
  
  if (ocupacao > 0 && ocupacao < capacidade) {
    bg = 'bg-slate-200';
    border = 'border-slate-300';
    text = 'text-slate-600';
  } else if (ocupacao >= capacidade) {
    bg = 'bg-slate-400';
    border = 'border-slate-500';
    text = 'text-white';
  }

  return (
    <div
      ref={setNodeRef}
      onClick={() => onSelectTable(mesa)}
      className={`
        aspect-square rounded-full ${bg} ${border} border-2
        flex flex-col items-center justify-center cursor-pointer
        transition-all duration-200 hover:scale-105 hover:shadow-md
        ${isVip ? 'ring-2 ring-amber-400 ring-offset-1' : ''}
        ${isOver && canDrop ? 'ring-4 ring-blue-400 ring-offset-2 scale-110' : ''}
        ${isOver && !canDrop ? 'ring-4 ring-red-400 ring-offset-2' : ''}
      `}
      data-testid={`table-${mesa.numero || index}`}
    >
      <span className={`text-sm font-bold ${text}`}>
        {mesa.numero || index + 1}
      </span>
      <span className={`text-[10px] ${text} opacity-80`}>
        {ocupacao}/{capacidade}
      </span>
      {isVip && <Crown size={10} className="text-amber-500 mt-0.5" />}
    </div>
  );
}

// Tables Section Component - Seção de Mesas dentro de Convidados (COM DRAG-AND-DROP)
function TablesSection({ wedding, invites, confirmedCount }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showTableModal, setShowTableModal] = useState(false);
  const [showOrganizeModal, setShowOrganizeModal] = useState(false);
  const [tablesConfig, setTablesConfig] = useState({ num_mesas: 10, lugares_por_mesa: 8, lugares_mesa_padrinhos: 10 });
  const [loadingOrganize, setLoadingOrganize] = useState(false);
  
  // Drag-and-drop states
  const [activeGuest, setActiveGuest] = useState(null);
  const [overTableId, setOverTableId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Permissões - Editor e Admin podem editar
  const canEdit = user?.role !== 'viewer';

  // Sensores para drag
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    })
  );

  // Buscar mesas
  useEffect(() => {
    let isMounted = true;
    if (!wedding?.id) return;
    
    const fetchTables = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/weddings/${wedding.id}/tables`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        });
        if (isMounted) {
          setTables(res.data?.mesas || []);
        }
      } catch (err) {
        console.error('Erro ao buscar mesas:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    fetchTables();
    return () => { isMounted = false; };
  }, [wedding?.id]);

  // Salvar mesas automaticamente (debounced)
  const saveTables = useCallback(async (newTables) => {
    if (!wedding?.id || !canEdit) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/weddings/${wedding.id}/tables`, {
        mesas: newTables,
        justificativa: "Ajuste manual"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      toast.error("Erro ao salvar alterações");
      console.error('Erro ao salvar mesas:', err);
    } finally {
      setSaving(false);
    }
  }, [wedding?.id, canEdit]);

  // Handlers de drag-and-drop
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveGuest(active.data.current);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (over) {
      setOverTableId(over.id);
    } else {
      setOverTableId(null);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveGuest(null);
    setOverTableId(null);

    if (!over || !active.data.current) return;

    const { guest, sourceTableId } = active.data.current;
    const targetTableId = over.data.current?.tableId;

    if (!targetTableId || sourceTableId === targetTableId) return;

    // Encontrar mesas de origem e destino
    const sourceTable = tables.find(t => (t.numero || tables.indexOf(t) + 1) === sourceTableId);
    const targetTable = tables.find(t => (t.numero || tables.indexOf(t) + 1) === targetTableId);

    if (!sourceTable || !targetTable) return;

    // Validar capacidade
    const targetCapacity = targetTable.capacidade || tablesConfig.lugares_por_mesa;
    const targetOcupacao = targetTable.convidados?.length || 0;

    if (targetOcupacao >= targetCapacity) {
      toast.error("Esta mesa já atingiu o limite de lugares.");
      return;
    }

    // Salvar estado atual no histórico para Desfazer
    setHistory(prev => [...prev.slice(-9), JSON.parse(JSON.stringify(tables))]);

    // Mover convidado
    const newTables = tables.map(table => {
      const tableNum = table.numero || tables.indexOf(table) + 1;
      
      if (tableNum === sourceTableId) {
        // Remover da origem
        return {
          ...table,
          convidados: table.convidados.filter(g => {
            const gNome = typeof g === 'string' ? g : g.nome;
            const activeNome = typeof guest === 'string' ? guest : guest.nome;
            return gNome !== activeNome;
          })
        };
      }
      
      if (tableNum === targetTableId) {
        // Adicionar ao destino
        return {
          ...table,
          convidados: [...(table.convidados || []), guest]
        };
      }
      
      return table;
    });

    setTables(newTables);
    saveTables(newTables);
    toast.success(`Convidado movido para Mesa ${targetTableId}`);
  };

  const handleDragCancel = () => {
    setActiveGuest(null);
    setOverTableId(null);
  };

  // Desfazer última ação
  const handleUndo = () => {
    if (history.length === 0) return;
    
    const previousState = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    setTables(previousState);
    saveTables(previousState);
    toast.info("Ação desfeita");
  };

  // Verificar se pode soltar na mesa
  const canDropOnTable = (tableId) => {
    if (!activeGuest) return true;
    const table = tables.find(t => (t.numero || tables.indexOf(t) + 1) === tableId);
    if (!table) return false;
    const capacity = table.capacidade || tablesConfig.lugares_por_mesa;
    return (table.convidados?.length || 0) < capacity;
  };

  // Organizar mesas com IA
  const handleOrganizeTables = async () => {
    if (confirmedCount === 0) {
      toast.error("Nenhum convidado confirmado para organizar nas mesas");
      return;
    }
    
    setLoadingOrganize(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const res = await axios.post(`${API}/ai/organize-tables`, {
        wedding_id: wedding.id,
        num_mesas: tablesConfig.num_mesas,
        lugares_por_mesa: tablesConfig.lugares_por_mesa,
        lugares_mesa_padrinhos: tablesConfig.lugares_mesa_padrinhos
      }, { headers });
      
      if (res.data.mesas && res.data.mesas.length > 0) {
        await axios.post(`${API}/weddings/${wedding.id}/tables`, {
          mesas: res.data.mesas,
          justificativa: res.data.justificativa
        }, { headers });
        
        setTables(res.data.mesas);
        setHistory([]); // Limpar histórico
        toast.success(`Mesas organizadas! ${res.data.mesas.length} mesas criadas.`);
        setShowOrganizeModal(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao organizar mesas");
    } finally {
      setLoadingOrganize(false);
    }
  };

  // Estado visual da mesa
  const getTableState = (mesa) => {
    const ocupacao = mesa.convidados?.length || 0;
    const capacidade = mesa.capacidade || tablesConfig.lugares_por_mesa;
    
    if (ocupacao === 0) return { bg: 'bg-slate-100', border: 'border-slate-200', text: 'text-slate-400' };
    if (ocupacao < capacidade) return { bg: 'bg-slate-200', border: 'border-slate-300', text: 'text-slate-600' };
    return { bg: 'bg-slate-400', border: 'border-slate-500', text: 'text-white' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="animate-spin text-blue-500" size={24} />
        <span className="ml-2 text-slate-500">Carregando mesas...</span>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">
              {tables.length > 0 
                ? "Clique em uma mesa para ver detalhes. Arraste convidados entre mesas para ajustar."
                : "Organize seus convidados em mesas. Esta funcionalidade é opcional."
              }
            </p>
          </div>
          <div className="flex gap-2">
            {tables.length > 0 && canEdit && (
              <React.Fragment>
                <Button 
                  variant="secondary" 
                  onClick={handleUndo}
                  disabled={history.length === 0}
                  className="gap-1"
                >
                  <Undo2 size={16} />
                  Desfazer
                </Button>
                <Button 
                  variant={editMode ? "primary" : "secondary"}
                  onClick={() => setEditMode(!editMode)}
                  className="gap-1"
                >
                  <Edit2 size={16} />
                  {editMode ? "Concluir Edição" : "Editar Mesas"}
                </Button>
              </React.Fragment>
            )}
            {tables.length === 0 ? (
              <Button 
                onClick={() => setShowOrganizeModal(true)} 
                className="bg-blue-600 hover:bg-blue-700"
                disabled={confirmedCount === 0}
                data-testid="organize-tables-btn"
              >
                <Sparkles size={18} className="mr-2" />
                Organizar com IA
              </Button>
            ) : (
              <Button 
                onClick={() => setShowOrganizeModal(true)} 
                variant="secondary"
                data-testid="reorganize-tables-btn"
              >
                <RefreshCw size={16} className="mr-2" />
                Reorganizar
              </Button>
            )}
          </div>
        </div>

        {/* Status de salvamento */}
        {saving && (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <Loader2 className="animate-spin" size={14} />
            <span>Salvando...</span>
          </div>
        )}

        {/* Permissão negada */}
        {tables.length > 0 && !canEdit && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            <AlertCircle size={16} className="inline mr-2" />
            Você não tem permissão para editar mesas.
          </div>
        )}

        {/* Estado vazio */}
        {tables.length === 0 ? (
          <Card className="p-8 text-center">
            <LayoutGrid size={48} className="mx-auto mb-3 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Nenhuma mesa organizada</h3>
            <p className="text-slate-500 text-sm mb-4 max-w-md mx-auto">
              {confirmedCount === 0 
                ? "Você precisa ter convidados confirmados para organizar as mesas."
                : `Use a IA para organizar automaticamente os ${confirmedCount} convidados confirmados em mesas.`
              }
            </p>
          </Card>
        ) : (
          <React.Fragment>
            {/* Estatísticas */}
            <div className="grid grid-cols-4 gap-3">
              <Card className="p-3 text-center">
                <p className="text-xl font-bold text-slate-800">{tables.length}</p>
                <p className="text-xs text-slate-500">Mesas</p>
              </Card>
              <Card className="p-3 text-center">
                <p className="text-xl font-bold text-blue-600">{confirmedCount}</p>
                <p className="text-xs text-slate-500">Confirmados</p>
              </Card>
              <Card className="p-3 text-center">
                <p className="text-xl font-bold text-emerald-600">
                  {tables.reduce((acc, m) => acc + (m.convidados?.length || 0), 0)}
                </p>
                <p className="text-xs text-slate-500">Alocados</p>
              </Card>
              <Card className="p-3 text-center">
                <p className="text-xl font-bold text-amber-600">
                  {tables.filter(m => m.isVip || m.nome?.toLowerCase().includes('padrinho')).length}
                </p>
                <p className="text-xs text-slate-500">VIP</p>
              </Card>
            </div>

            {/* Legenda */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span className="font-medium">Legenda:</span>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-slate-100 border border-slate-200"></div>
                <span>Vazia</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-slate-200 border border-slate-300"></div>
                <span>Incompleta</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-slate-400 border border-slate-500"></div>
                <span>Cheia</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-slate-200 border-2 border-amber-400"></div>
                <span>Padrinhos</span>
              </div>
              {editMode && (
                <span className="ml-auto text-blue-600 font-medium">
                  ✏️ Modo de edição ativo - arraste para mover
                </span>
              )}
            </div>

            {/* Layout principal: Grid + Detalhe da mesa selecionada */}
            <div className={`grid gap-4 ${editMode ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
              {/* Grid de Mesas */}
              <Card className="p-4">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                  {tables.map((mesa, index) => (
                    <DroppableTable
                      key={mesa.numero || index}
                      mesa={mesa}
                      index={index}
                      tablesConfig={tablesConfig}
                      onSelectTable={(m) => { setSelectedTable(m); if (!editMode) setShowTableModal(true); else setSelectedTable(m); }}
                      isOver={overTableId === `table-${mesa.numero || index + 1}`}
                      canDrop={canDropOnTable(mesa.numero || index + 1)}
                    />
                  ))}
                </div>
              </Card>

              {/* Painel de edição da mesa selecionada */}
              {editMode && selectedTable && (
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-slate-800">
                      Mesa {selectedTable.numero} 
                      {(selectedTable.isVip || selectedTable.nome?.toLowerCase().includes('padrinho')) && (
                        <Crown size={14} className="inline ml-2 text-amber-500" />
                      )}
                    </h4>
                    <span className="text-sm text-slate-500">
                      {selectedTable.convidados?.length || 0} / {selectedTable.capacidade || tablesConfig.lugares_por_mesa} lugares
                    </span>
                  </div>
                  
                  <p className="text-xs text-slate-500 mb-3">
                    Arraste os convidados abaixo para outra mesa no grid:
                  </p>
                  
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedTable.convidados && selectedTable.convidados.length > 0 ? (
                      selectedTable.convidados.map((convidado, idx) => (
                        <DraggableGuest
                          key={idx}
                          guest={convidado}
                          tableId={selectedTable.numero}
                        />
                      ))
                    ) : (
                      <p className="text-slate-400 text-sm text-center py-4">Mesa vazia</p>
                    )}
                  </div>
                </Card>
              )}
            </div>
          </React.Fragment>
        )}

        {/* Drag Overlay */}
        <DragOverlay>
          {activeGuest && (
            <div className="flex items-center gap-2 p-2 bg-white border-2 border-blue-400 rounded-lg shadow-xl">
              <GripVertical size={14} className="text-blue-400" />
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-700 text-xs font-medium">
                  {(typeof activeGuest.guest === 'string' ? activeGuest.guest : activeGuest.guest?.nome)?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
              <span className="text-sm text-slate-700">
                {typeof activeGuest.guest === 'string' ? activeGuest.guest : activeGuest.guest?.nome}
              </span>
            </div>
          )}
        </DragOverlay>

        {/* Modal de Detalhes da Mesa (modo visualização) */}
        <Modal 
          isOpen={showTableModal && !editMode} 
          onClose={() => setShowTableModal(false)} 
          title={selectedTable?.nome || `Mesa ${selectedTable?.numero || ''}`}
        >
          {selectedTable && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getTableState(selectedTable).bg} ${getTableState(selectedTable).border} border-2`}>
                    <span className={`text-sm font-bold ${getTableState(selectedTable).text}`}>
                      {selectedTable.numero}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{selectedTable.nome || `Mesa ${selectedTable.numero}`}</p>
                    <p className="text-sm text-slate-500">
                      {selectedTable.convidados?.length || 0} / {selectedTable.capacidade || tablesConfig.lugares_por_mesa} lugares
                    </p>
                  </div>
                </div>
                {(selectedTable.isVip || selectedTable.nome?.toLowerCase().includes('padrinho')) && (
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1">
                    <Crown size={12} />
                    Padrinhos
                  </span>
                )}
              </div>

              <div>
                <h4 className="font-medium text-slate-700 mb-2">Convidados</h4>
                {selectedTable.convidados && selectedTable.convidados.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedTable.convidados.map((convidado, idx) => {
                      const nome = typeof convidado === 'string' ? convidado : convidado.nome;
                      return (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-white border border-slate-100 rounded-lg">
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-700 text-xs font-medium">{nome?.charAt(0)?.toUpperCase() || '?'}</span>
                          </div>
                          <span className="text-sm text-slate-700">{nome}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm text-center py-3">Nenhum convidado alocado.</p>
                )}
              </div>
              
              {canEdit && (
                <Button 
                  variant="secondary" 
                  onClick={() => { setShowTableModal(false); setEditMode(true); }}
                  className="w-full"
                >
                  <Edit2 size={16} className="mr-2" />
                  Editar convidados desta mesa
                </Button>
              )}
            </div>
          )}
        </Modal>

        {/* Modal de Organização */}
        <Modal 
          isOpen={showOrganizeModal} 
          onClose={() => setShowOrganizeModal(false)} 
          title="Organizar Mesas com IA"
        >
          <div className="space-y-4">
            <p className="text-slate-600 text-sm">
              A IA irá distribuir os {confirmedCount} convidados confirmados nas mesas, respeitando relacionamentos.
            </p>
            
            <div className="grid grid-cols-1 gap-3">
              <Input
                label="Número de mesas"
                type="number"
                value={tablesConfig.num_mesas}
                onChange={(e) => setTablesConfig({ ...tablesConfig, num_mesas: parseInt(e.target.value) || 10 })}
                min={1}
                max={50}
              />
              <Input
                label="Lugares por mesa"
                type="number"
                value={tablesConfig.lugares_por_mesa}
                onChange={(e) => setTablesConfig({ ...tablesConfig, lugares_por_mesa: parseInt(e.target.value) || 8 })}
                min={4}
                max={12}
              />
              <Input
                label="Lugares mesa padrinhos"
                type="number"
                value={tablesConfig.lugares_mesa_padrinhos}
                onChange={(e) => setTablesConfig({ ...tablesConfig, lugares_mesa_padrinhos: parseInt(e.target.value) || 10 })}
                min={4}
                max={20}
              />
            </div>

            {tables.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                <strong>Atenção:</strong> Reorganizar substituirá a organização atual.
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowOrganizeModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={handleOrganizeTables}
                disabled={loadingOrganize || confirmedCount === 0}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {loadingOrganize ? (
                  <React.Fragment>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Organizando...
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Sparkles size={16} className="mr-2" />
                    Organizar
                  </React.Fragment>
                )}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </DndContext>
  );
}

// Guests Page - Sistema de Convites (COM WEBSOCKET)
function BuffetForecastModal({ invites, onClose, wedding }) {
  const [exporting, setExporting] = useState(false);
  // Preço por prato — persistido localmente para conveniência do assessor.
  const [priceAdult, setPriceAdult] = useState(() => Number(localStorage.getItem('liventy_buffet_price_adult')) || 0);
  useEffect(() => { localStorage.setItem('liventy_buffet_price_adult', String(priceAdult || 0)); }, [priceAdult]);

  // Calcula a previsão a partir das confirmações individuais (com fallback ao status do convite).
  // Regra: Adulto=100% · Criança 6–10=50% (meia) · Criança 3–5=cortesia (grátis) · Bebê 0–2=grátis e sem assento.
  const f = useMemo(() => {
    const empty = () => ({ adults: 0, children_meia: 0, children_free: 0, babies: 0 });
    const acc = { confirmed: empty(), pending: empty(), declined: empty() };
    const classifyChild = (ind) => {
      const age = (ind.age != null && ind.age !== '') ? Number(ind.age) : null;
      const unit = ind.age_unit || 'years';
      // 3–5 anos = cortesia; 6–10 (ou idade desconhecida) = meia
      if (unit === 'years' && age != null && age >= 3 && age <= 5) return 'children_free';
      return 'children_meia';
    };
    (invites || []).forEach((inv) => {
      const inds = inv.individuals || [];
      const hasIndRsvp = inds.length > 0 && inds.some((i) => i.rsvp === 'confirmed' || i.rsvp === 'declined');
      if (hasIndRsvp) {
        inds.forEach((i) => {
          let key;
          if (i.type === 'baby') key = 'babies';
          else if (i.type === 'child') key = classifyChild(i);
          else key = 'adults';
          if (i.rsvp === 'confirmed') acc.confirmed[key] += 1;
          else if (i.rsvp === 'declined') acc.declined[key] += 1;
          else acc.pending[key] += 1;
        });
      } else {
        const a = inv.adults_count || 0, c = inv.children_count || 0, b = inv.babies_count || 0;
        const bucket = inv.status === 'confirmado' ? 'confirmed' : inv.status === 'nao_ira' ? 'declined' : 'pending';
        acc[bucket].adults += a; acc[bucket].children_meia += c; acc[bucket].babies += b; // fallback: crianças como meia
      }
    });
    return acc;
  }, [invites]);

  const childrenTotal = (g) => g.children_meia + g.children_free;
  const sum = (g) => g.adults + childrenTotal(g) + g.babies;
  const fmtBRL = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const priceMeia = (priceAdult || 0) * 0.5; // meia = 50% do adulto
  const costOf = (g) => (g.adults * (priceAdult || 0)) + (g.children_meia * priceMeia);
  // Projeção máxima = confirmados + pendentes (bebês e cortesias não contam prato pago).
  const projected = { adults: f.confirmed.adults + f.pending.adults, children_meia: f.confirmed.children_meia + f.pending.children_meia, children_free: f.confirmed.children_free + f.pending.children_free, babies: f.confirmed.babies + f.pending.babies };

  const Row = ({ label, g, accent }) => (
    <div className="flex items-center justify-between py-2.5 px-3.5 rounded-xl bg-white ring-1 ring-zinc-200/70">
      <div className="flex items-center gap-2.5">
        <span className={`w-1.5 h-1.5 rounded-full ${accent}`} />
        <span className="text-[13.5px] font-medium text-zinc-800">{label}</span>
      </div>
      <div className="text-right">
        <span className="text-[15px] font-bold text-zinc-900 tabular-nums">{sum(g)}</span>
        <span className="block text-[11px] text-zinc-400">{g.adults} ad · {childrenTotal(g)} cri · {g.babies} beb</span>
      </div>
    </div>
  );

  return (
    <Modal isOpen={true} onClose={onClose} title="Previsão de Buffet">
      <div className="space-y-5" data-testid="buffet-forecast-modal">
        <p className="text-[13px] text-zinc-500 -mt-1">
          Atualiza sozinho conforme as confirmações de presença chegam. Bebês não contam como prato.
        </p>

        {/* Confirmados — destaque */}
        <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-50 to-white ring-1 ring-emerald-100" data-testid="buffet-confirmed-card">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-600 mb-1">Convidados confirmados</p>
          <p className="text-[40px] leading-none font-bold tracking-tighter text-emerald-700 tabular-nums" data-testid="buffet-confirmed-total">
            {f.confirmed.adults + childrenTotal(f.confirmed)}
          </p>
          <p className="text-[12px] text-zinc-500 mt-1.5">
            {f.confirmed.adults} adulto(s) · {childrenTotal(f.confirmed)} criança(s) · {f.confirmed.babies} bebê(s)
          </p>
        </div>

        <div className="space-y-2">
          <Row label="Confirmados" g={f.confirmed} accent="bg-emerald-500" />
          <Row label="Aguardando confirmação" g={f.pending} accent="bg-amber-400" />
          <Row label="Não irão" g={f.declined} accent="bg-rose-400" />
        </div>

        {/* Estimador de custo */}
        <div className="rounded-2xl p-4 bg-zinc-50 ring-1 ring-zinc-200/70 space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Estimativa de custo</p>
          <label className="block">
            <span className="block text-[11px] text-zinc-500 mb-1">Preço por adulto (prato cheio)</span>
            <MoneyInput value={priceAdult} onChange={setPriceAdult} />
          </label>
          <div className="text-[11.5px] text-zinc-500 bg-white rounded-xl ring-1 ring-zinc-200/70 px-3 py-2 space-y-0.5">
            <p>• <b>Adulto (11+):</b> {fmtBRL(priceAdult)}</p>
            <p>• <b>Criança 6–10 (meia):</b> {fmtBRL(priceMeia)} <span className="text-zinc-400">(50% do adulto)</span></p>
            <p>• <b>Criança 3–5 (cortesia):</b> grátis</p>
            <p>• <b>Bebê 0–2:</b> grátis · não ocupa assento</p>
          </div>
          <div className="flex items-center justify-between pt-1 border-t border-zinc-200/70">
            <span className="text-[13px] text-zinc-600">Custo confirmado</span>
            <span className="text-[16px] font-bold text-emerald-700 tabular-nums" data-testid="buffet-cost-confirmed">{fmtBRL(costOf(f.confirmed))}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-zinc-600">Projeção máxima <span className="text-zinc-400">(confirmados + pendentes)</span></span>
            <span className="text-[15px] font-semibold text-zinc-800 tabular-nums" data-testid="buffet-cost-projected">{fmtBRL(costOf(projected))}</span>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={async () => {
              setExporting(true);
              try {
                const { generateBuffetForecastPdf } = await import("./lib/buffetForecastPdf");
                const evName = wedding?.event_name || `${wedding?.partner1_name || ""}${wedding?.partner2_name ? " & " + wedding.partner2_name : ""}`.trim();
                await generateBuffetForecastPdf({
                  eventName: evName || "Evento",
                  eventDate: wedding?.event_date || wedding?.wedding_date,
                  priceAdult: priceAdult || 0,
                  priceMeia,
                  groups: { confirmed: f.confirmed, pending: f.pending, declined: f.declined },
                  costConfirmed: costOf(f.confirmed),
                  costProjected: costOf(projected),
                });
              } catch (e) {
                toast.error("Não foi possível gerar o PDF.");
              } finally {
                setExporting(false);
              }
            }}
            disabled={exporting}
            className="flex-1 py-2.5 rounded-xl border border-zinc-200 bg-white text-zinc-800 text-sm font-medium hover:border-zinc-300 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="buffet-export-btn"
          >
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            Exportar p/ buffet
          </button>
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition-colors" data-testid="buffet-forecast-close">
            Fechar
          </button>
        </div>
      </div>
    </Modal>
  );
}

function GuestsPage() {
  const navigate = useNavigate();
  const { wedding } = useWedding();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [invites, setInvites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editInvite, setEditInvite] = useState(null);
  const [viewMode, setViewMode] = useState("invites"); // invites or people
  const [showBuffet, setShowBuffet] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeData, setQRCodeData] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);
  
  // Tables state (manual)
  const [tables, setTables] = useState([]);
  const [showTablesPanel, setShowTablesPanel] = useState(false);
  const [newTableName, setNewTableName] = useState('');
  const [editingTableId, setEditingTableId] = useState(null);
  const [editingTableName, setEditingTableName] = useState('');
  const [tableLoading, setTableLoading] = useState(false);
  
  // Estado para Organizar Mesas
  const [showTablesModal, setShowTablesModal] = useState(false);
  const [tablesConfig, setTablesConfig] = useState({ num_mesas: 10, lugares_por_mesa: 8, lugares_mesa_padrinhos: 10 });
  const [tablesResult, setTablesResult] = useState(null);
  const [loadingTables, setLoadingTables] = useState(false);
  
  // Estados para Upload de Convites e WhatsApp
  const [conviteFiles, setConviteFiles] = useState({ save_the_date_url: null, invitation_url: null });
  const [uploadingSaveTheDate, setUploadingSaveTheDate] = useState(false);
  const [uploadingInvitation, setUploadingInvitation] = useState(false);
  const [selectedInvites, setSelectedInvites] = useState([]);
  const [selectMode, setSelectMode] = useState(false); // modo de seleção (marcar um a um)
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [showWaSendFlow, setShowWaSendFlow] = useState(false); // novo flow premium
  // Drawer/sheet de ações de envio em massa via WhatsApp
  const [showMassActions, setShowMassActions] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  // Novo: Estados para ALERTA RSVP
  const [showRsvpAlertModal, setShowRsvpAlertModal] = useState(false);
  const [rsvpAlertPreview, setRsvpAlertPreview] = useState(null);
  const [loadingRsvpAlert, setLoadingRsvpAlert] = useState(false);
  // Modal de Organizar Mesas (premium)
  const [showOrganizeTablesModal, setShowOrganizeTablesModal] = useState(false);
  const [simpleTables, setSimpleTables] = useState([]);
  const [tableSearch, setTableSearch] = useState({}); // { inviteId: searchTerm }
  const [openTableSelect, setOpenTableSelect] = useState(null); // inviteId ou null
  
  // Fetch simple tables
  const fetchSimpleTables = async () => {
    if (!wedding?.id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/weddings/${wedding.id}/simple-tables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSimpleTables(res.data || []);
    } catch (err) {
      console.error('Erro ao carregar mesas:', err);
    }
  };
  useEffect(() => {
    if (showOrganizeTablesModal) fetchSimpleTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOrganizeTablesModal]);
  
  // Atribuir convidado a uma mesa (existente ou criar nova)
  const assignTable = async (inviteId, tableNameOrId, isNew = false) => {
    try {
      const token = localStorage.getItem('token');
      let tableId = tableNameOrId;
      if (isNew) {
        const created = await axios.post(`${API}/weddings/${wedding.id}/simple-tables`, { name: tableNameOrId }, { headers: { Authorization: `Bearer ${token}` } });
        tableId = created.data.id;
        await fetchSimpleTables();
      }
      await axios.put(`${API}/weddings/${wedding.id}/invites/${inviteId}`, { table_id: tableId }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Mesa atribuída");
      setOpenTableSelect(null);
      setTableSearch(prev => ({ ...prev, [inviteId]: '' }));
      fetchInvites();
      fetchSimpleTables();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atribuir mesa");
    }
  };
  
  const unassignTable = async (inviteId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/weddings/${wedding.id}/invites/${inviteId}`, { table_id: null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Mesa removida");
      fetchInvites();
      fetchSimpleTables();
    } catch (err) {
      toast.error("Erro ao remover mesa");
    }
  };
  
  // Timeline modal
  const [timelineModal, setTimelineModal] = useState({ open: false, invite: null, events: [], loading: false });

  // Bottom sheet de detalhes do convidado (mobile native experience — Sprint 2)
  const [guestSheet, setGuestSheet] = useState({ open: false, invite: null });
  const openGuestSheet = (invite) => {
    try { navigator.vibrate?.(8); } catch {}
    setGuestSheet({ open: true, invite });
  };
  const closeGuestSheet = () => setGuestSheet({ open: false, invite: null });

  // Importação CSV
  const [showImportCsv, setShowImportCsv] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const csvFileInputRef = useRef(null);

  const downloadCsvTemplate = () => {
    const header = ['Nome','Adultos','Crianças','Bebês','Telefone','Email','Mesa','Observações'];
    const sample = [
      ['Karen e Família','3','1','0','(11) 99999-9999','karen@email.com','Mesa 4','Sem restrição'],
      ['Gabriel e Família','2','0','1','(11) 98888-8888','','Mesa 7',''],
      ['João Silva','1','0','0','','joao@email.com','','Vegetariano'],
    ];
    const csv = [header, ...sample].map(row =>
      row.map(c => /[",;\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c).join(',')
    ).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'liventy-template-convidados.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Modelo CSV baixado');
  };

  const handleCsvImport = async () => {
    if (!csvFile || !wedding?.id) return;
    setCsvImporting(true);
    setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append('file', csvFile);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/weddings/${wedding.id}/invites/import-csv`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCsvResult(res.data);
      const { created, errors } = res.data || {};
      if (created > 0) toast.success(`${created} convidado(s) importado(s)!`);
      if (errors && errors.length > 0) toast.error(`${errors.length} linha(s) com erro`);
      try { navigator.vibrate?.(12); } catch {}
      await fetchInvites();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erro ao importar CSV'));
    } finally {
      setCsvImporting(false);
    }
  };
  
  const openTimelineFor = async (invite) => {
    setTimelineModal({ open: true, invite, events: [], loading: true });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/invites/${invite.id}/timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimelineModal({ open: true, invite, events: res.data.events || [], loading: false });
    } catch (err) {
      toast.error('Erro ao carregar histórico');
      setTimelineModal({ open: false, invite: null, events: [], loading: false });
    }
  };
  
  // Filtros rápidos da lista de convidados
  const [quickFilter, setQuickFilter] = useState('all'); // all, confirmed, pending, declined, qr_sent, qr_pending, reminder_sent
  // Modal de preview/elegibilidade para envio em massa (QR Code, Lembrete)
  const [eligibilityModal, setEligibilityModal] = useState({ open: false, type: null, stats: null, ids: [] });
  const saveTheDateInputRef = useRef(null);
  const invitationInputRef = useRef(null);
  
  const [form, setForm] = useState({
    name: "",
    total_guests: 1,
    children_count: 0,
    babies_count: 0,
    detailed_mode: true,
    individuals: [],
    adults: [{ name: "" }],
    children: [],
    contact_phone: "",
    contact_email: "",
    kids_pickup: false,
    responsible1_name: "",
    responsible1_phone: "",
    responsible2_name: "",
    responsible2_phone: "",
    status: "a_confirmar",
    notes: "",
    table_id: "",
    table_search: ""
  });

  const [kidsOnlyEvent, setKidsOnlyEvent] = useState(false);
  useEffect(() => {
    if (!wedding?.id) return;
    axios.get(`${API}/weddings/${wedding.id}/child-pickup-config`)
      .then(r => setKidsOnlyEvent(!!r.data?.kids_only_event))
      .catch(() => {});
  }, [wedding?.id]);

  const [checkoutQrModal, setCheckoutQrModal] = useState({ open: false, inviteName: "", loading: false, items: [] });
  const openCheckoutQr = async (invite) => {
    setCheckoutQrModal({ open: true, inviteName: invite.name, loading: true, items: [] });
    try {
      const iss = await axios.post(`${API}/weddings/${wedding.id}/child-checkout/issue`, { invite_id: invite.id });
      const co = iss.data;
      const label = co.children_label || (co.children || []).map(c => c.name).join(", ") || co.child_name || "Crianças";
      const items = [{ name: label, url: `${API}/child-checkout/${co.token}/qrcode`, status: co.status || "issued" }];
      setCheckoutQrModal({ open: true, inviteName: invite.name, loading: false, items });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível carregar o QR de saída");
      setCheckoutQrModal({ open: false, inviteName: "", loading: false, items: [] });
    }
  };

  const fetchInvites = useCallback(async () => {
    if (!wedding) {
      setLoading(false);
      return;
    }
    try {
      const [invitesRes, statsRes] = await Promise.all([
        axios.get(`${API}/weddings/${wedding.id}/invites`),
        axios.get(`${API}/weddings/${wedding.id}/invites/stats`)
      ]);
      setInvites(invitesRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [wedding]);

  // Carregar dados iniciais
  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  // Revalidar ao voltar para a aba (visibilitychange) — captura confirmações RSVP feitas em outra janela
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchInvites();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, [fetchInvites]);

  // Fetch tables
  const fetchTables = useCallback(async () => {
    if (!wedding?.id) return;
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/simple-tables`);
      setTables(res.data);
    } catch (err) { console.error(err); }
  }, [wedding?.id]);

  useEffect(() => { fetchTables(); }, [fetchTables]);

  const getTableName = (tableId) => {
    if (!tableId) return null;
    const t = tables.find(tb => tb.id === tableId);
    return t ? t.name : null;
  };

  // Table CRUD
  const handleCreateTable = async () => {
    if (!newTableName.trim()) return;
    setTableLoading(true);
    try {
      await axios.post(`${API}/weddings/${wedding.id}/simple-tables`, { name: newTableName.trim() });
      setNewTableName('');
      await fetchTables();
      toast.success("Mesa criada!");
    } catch (err) { toast.error(getErrorMessage(err, "Erro ao criar mesa")); }
    finally { setTableLoading(false); }
  };

  const handleRenameTable = async (tableId) => {
    if (!editingTableName.trim()) return;
    setTableLoading(true);
    try {
      await axios.put(`${API}/weddings/${wedding.id}/simple-tables/${tableId}`, { name: editingTableName.trim() });
      setEditingTableId(null);
      await fetchTables();
      toast.success("Mesa renomeada!");
    } catch (err) { toast.error(getErrorMessage(err, "Erro")); }
    finally { setTableLoading(false); }
  };

  const handleDeleteTable = async (tableId) => {
    setTableLoading(true);
    try {
      await axios.delete(`${API}/weddings/${wedding.id}/simple-tables/${tableId}`);
      await fetchTables();
      await fetchInvites();
      toast.success("Mesa excluída!");
    } catch (err) { toast.error(getErrorMessage(err, "Erro")); }
    finally { setTableLoading(false); }
  };

  const handleAssignTable = async (inviteId, tableId) => {
    try {
      await axios.put(`${API}/weddings/${wedding.id}/invites/${inviteId}`, { table_id: tableId || null });
      await fetchInvites();
    } catch (err) { toast.error("Erro ao atribuir mesa"); }
  };

  // Carregar arquivos de convite e créditos
  const fetchConvitesAndCredits = useCallback(async () => {
    if (!wedding?.id) return;
    try {
      const token = localStorage.getItem('token');
      const convitesRes = await axios.get(`${API}/app/convites`, { headers: { Authorization: `Bearer ${token}` } });
      setConviteFiles(convitesRes.data);
    } catch (err) {
      console.error('Erro ao carregar convites:', err);
    }
  }, [wedding?.id]);

  useEffect(() => {
    fetchConvitesAndCredits();
  }, [fetchConvitesAndCredits]);

  // Upload de arquivo de convite
  const handleUploadConvite = async (fileType, file) => {
    if (!file) return;
    
    const setUploading = fileType === 'save_the_date' ? setUploadingSaveTheDate : setUploadingInvitation;
    setUploading(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/app/convites/upload/${fileType}`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${fileType === 'save_the_date' ? 'Save the Date' : 'Convite'} enviado com sucesso!`);
      fetchConvitesAndCredits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar arquivo');
    } finally {
      setUploading(false);
    }
  };

  // Deletar arquivo de convite
  const handleDeleteConvite = async (fileType) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/app/convites/${fileType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Arquivo removido');
      fetchConvitesAndCredits();
    } catch (err) {
      toast.error('Erro ao remover arquivo');
    }
  };

  // Toggle seleção de convidado
  const toggleInviteSelection = (inviteId) => {
    setSelectedInvites(prev => 
      prev.includes(inviteId) 
        ? prev.filter(id => id !== inviteId)
        : [...prev, inviteId]
    );
  };

  // Selecionar todos
  const selectAllInvites = () => {
    if (selectedInvites.length === invites.length) {
      setSelectedInvites([]);
    } else {
      setSelectedInvites(invites.map(i => i.id));
    }
  };

  // Enviar WhatsApp em massa (save_the_date ou invitation - funciona COM ou SEM arquivo)
  const handleSendWhatsApp = async (type, inviteIds = null, skipPreview = false) => {
    const idsToSend = inviteIds || selectedInvites;
    
    if (idsToSend.length === 0) {
      toast.error('Selecione pelo menos um convidado');
      return;
    }
    
    // Para QR Code e Lembrete: mostrar preview de elegibilidade antes de enviar
    if (!skipPreview && (type === 'qrcode' || type === 'reminder')) {
      const previewToast = toast.loading(type === 'qrcode' ? 'Analisando convidados confirmados...' : 'Filtrando convidados pendentes...');
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API}/wa/send-preview`,
          { invite_ids: idsToSend, message_type: type },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.dismiss(previewToast);
        setEligibilityModal({ open: true, type, stats: res.data, ids: idsToSend });
        return;
      } catch (err) {
        toast.dismiss(previewToast);
        toast.error(err.response?.data?.detail || 'Erro ao verificar convidados');
        return;
      }
    }
    
    setSendingWhatsApp(true);
    const labels = { qrcode: 'QR Codes', reminder: 'lembretes', invitation: 'convites', save_the_date: 'mensagens' };
    const label = labels[type] || 'mensagens';
    const loadingToast = toast.loading(`Enviando ${label}...`);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/wa/send-bulk`, 
        { invite_ids: idsToSend, message_type: type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.dismiss(loadingToast);
      const mockLabel = res.data.mock ? ' (Simulado)' : '';
      const sent = res.data.sent || 0;
      const failed = res.data.failed || 0;

      if (sent > 0) {
        const successMsg = type === 'qrcode' 
          ? `QR Codes enviados com sucesso para ${sent} convidados confirmados.${mockLabel}`
          : type === 'reminder'
          ? `${sent} lembrete(s) enviado(s) para convidados pendentes.${mockLabel}`
          : `${sent} mensagem(ns) enviada(s) com sucesso!${mockLabel}`;
        toast.success(successMsg);
      }
      if (failed > 0) {
        toast.error(`${failed} envio(s) falharam. Verifique a conexão WhatsApp.`);
      }
      if (sent === 0 && failed === 0) {
        toast.info(res.data.message || 'Nenhuma mensagem enviada.');
      }

      setSelectedInvites([]);
      setEligibilityModal({ open: false, type: null, stats: null, ids: [] });
      fetchInvites();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Erro ao enviar. Verifique a conexão do WhatsApp.');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Enviar Alerta/Aviso customizado
  const handleSendAlert = async (inviteIds = null) => {
    const idsToSend = inviteIds || selectedInvites;
    
    if (idsToSend.length === 0) {
      toast.error('Selecione pelo menos um convidado');
      return;
    }
    
    if (!alertMessage || alertMessage.trim().length < 5) {
      toast.error('Digite uma mensagem com pelo menos 5 caracteres');
      return;
    }
    
    setSendingWhatsApp(true);
    const loadingToast = toast.loading(`Enviando aviso para ${idsToSend.length} convidado(s)...`);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/wa/send-bulk`, 
        { invite_ids: idsToSend, message_type: 'alert', custom_message: alertMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.dismiss(loadingToast);
      const sent = res.data.sent || 0;
      const failed = res.data.failed || 0;
      if (sent > 0) toast.success(`${sent} aviso(s) enviado(s)!${res.data.mock ? ' (Simulado)' : ''}`);
      if (failed > 0) toast.error(`${failed} envio(s) falharam.`);
      setSelectedInvites([]);
      setShowAlertModal(false);
      setAlertMessage('');
      fetchInvites();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Erro ao enviar avisos');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // NOVO: Buscar prévia do ALERTA RSVP (convidados aguardando)
  const fetchRsvpAlertPreview = async () => {
    setLoadingRsvpAlert(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/app/whatsapp/alert-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRsvpAlertPreview(res.data);
      setShowRsvpAlertModal(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao buscar informações do alerta');
    } finally {
      setLoadingRsvpAlert(false);
    }
  };

  // NOVO: Enviar ALERTA RSVP para todos com status "Aguardando"
  const handleSendRsvpAlert = async () => {
    if (!rsvpAlertPreview || rsvpAlertPreview.eligible_count === 0) {
      toast.error('Nenhum convidado elegível para receber o lembrete');
      return;
    }
    
    setSendingWhatsApp(true);
    const loadingToast = toast.loading(`Enviando lembrete para ${rsvpAlertPreview.eligible_count} convidado(s)...`);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/wa/send-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.dismiss(loadingToast);
      const sent = res.data.sent || 0;
      const failed = res.data.failed || 0;
      if (sent > 0) toast.success(`${sent} lembrete(s) enviado(s)!${res.data.mock ? ' (Simulado)' : ''}`);
      if (failed > 0) toast.error(`${failed} envio(s) falharam.`);
      setShowRsvpAlertModal(false);
      setRsvpAlertPreview(null);
      fetchInvites();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Erro ao enviar alertas');
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Enviar individual
  const handleSendIndividual = (invite, type) => {
    if (type === 'alert') {
      setSelectedInvites([invite.id]);
      setShowAlertModal(true);
    } else {
      handleSendWhatsApp(type, [invite.id]);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      total_guests: 1,
      children_count: 0,
      babies_count: 0,
      detailed_mode: true,
      individuals: [],
      adults: [{ name: "" }],
      children: [],
      contact_phone: "",
      contact_email: "",
      kids_pickup: kidsOnlyEvent,
      responsible1_name: "",
      responsible1_phone: "",
      responsible2_name: "",
      responsible2_phone: "",
      status: "a_confirmar",
      notes: "",
      table_id: "",
      table_search: ""
    });
    setEditInvite(null);
  };

  // Helpers para exportação detalhada (P1) — Nome + Idade individual
  const _formatIndividualsList = (invite, type) => {
    const list = (invite.individuals || []).filter(i => i?.type === type);
    if (list.length === 0) return "";
    if (type === 'adult') {
      // Adultos: apenas nomes
      return list.map(i => (i?.name || "Adulto").trim()).filter(Boolean).join(", ");
    }
    // Crianças/Bebês: Nome — X meses/anos
    return list.map(i => {
      const nome = (i?.name || (type === 'baby' ? 'Bebê' : 'Criança')).trim();
      const hasAge = i?.age !== undefined && i?.age !== null && i?.age !== "";
      let idade = "";
      if (hasAge) {
        const v = Number(i.age);
        const unit = i?.age_unit || 'years';
        idade = unit === 'months'
          ? `${v} ${v === 1 ? 'mês' : 'meses'}`
          : `${v} ano${v === 1 ? '' : 's'}`;
      }
      return idade ? `${nome} — ${idade}` : nome;
    }).join(", ");
  };

  const _getTableName = (invite) => {
    if (!invite.table_id) return "";
    const t = (tables || []).find(t => t.id === invite.table_id) || (simpleTables || []).find(t => t.id === invite.table_id);
    return t?.name || "";
  };

  // Função para exportar lista de convidados em Excel (formato hierárquico — igual painel do app)
  const exportToExcel = async () => {
    if (invites.length === 0) {
      toast.error("Não há convidados para exportar");
      return;
    }

    const XLSX = await loadXLSX();

    const statusRSVPMap = {
      "confirmado": "Confirmado",
      "nao_confirmado": "Não Confirmado",
      "a_confirmar": "Aguardando",
      "recusado": "Recusado"
    };

    const sortedInvites = [...invites].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", 'pt-BR')
    );

    // ─── ABA 1: Detalhado (hierárquico, igual painel) ───
    // Estrutura: linha do convite (bold) + linhas das pessoas indentadas
    const detailAOA = [];
    detailAOA.push(['Convite / Pessoa', 'Tipo / Composição', 'Idade', 'Mesa', 'Status RSVP']);

    const boldRows = [0]; // header sempre bold
    const inviteRows = []; // linhas que são de convite (para destaque)

    sortedInvites.forEach(invite => {
      let people = (invite.individuals || []).slice();
      if (people.length === 0) {
        const a = invite.adults_count || 0;
        const c = invite.children_count || 0;
        const b = invite.babies_count || 0;
        const total = invite.total_people || (a + c + b) || 1;
        for (let i = 0; i < a; i++) people.push({ name: '', type: 'adult' });
        for (let i = 0; i < c; i++) people.push({ name: '', type: 'child' });
        for (let i = 0; i < b; i++) people.push({ name: '', type: 'baby' });
        while (people.length < total) people.push({ name: '', type: 'adult' });
      }
      const adultsN = people.filter(p => p.type === 'adult').length;
      const childrenN = people.filter(p => p.type === 'child').length;
      const babiesN = people.filter(p => p.type === 'baby').length;
      const tableName = _getTableName(invite);
      const statusRSVP = statusRSVPMap[invite.status] || "Aguardando";

      const composition = [
        `${people.length} pessoa${people.length === 1 ? '' : 's'}`,
        adultsN > 0 ? `${adultsN} adulto${adultsN > 1 ? 's' : ''}` : null,
        childrenN > 0 ? `${childrenN} criança${childrenN > 1 ? 's' : ''}` : null,
        babiesN > 0 ? `${babiesN} bebê${babiesN > 1 ? 's' : ''}` : null,
      ].filter(Boolean).join(' · ');

      // Linha do convite
      inviteRows.push(detailAOA.length);
      boldRows.push(detailAOA.length);
      detailAOA.push([invite.name || '(sem nome)', composition, '', tableName || '', statusRSVP]);

      // Linhas das pessoas
      people.forEach((p, i) => {
        const isBaby = p.type === 'baby';
        const isChild = p.type === 'child';
        const rawName = (p.name && p.name.trim()) || (isBaby ? 'Bebê' : isChild ? 'Criança' : `Convidado ${i + 1}`);
        const tipo = isBaby ? 'Bebê' : isChild ? 'Criança' : 'Adulto';
        const ageStr = (p.age !== undefined && p.age !== null && p.age !== '')
          ? `${p.age} ano${Number(p.age) === 1 ? '' : 's'}`
          : '';
        detailAOA.push([`    ${rawName.toUpperCase()}`, tipo, ageStr, '', '']);
      });
      // linha em branco entre convites
      detailAOA.push(['', '', '', '', '']);
    });

    const wsDetail = XLSX.utils.aoa_to_sheet(detailAOA);
    wsDetail['!cols'] = [{ wch: 38 }, { wch: 28 }, { wch: 14 }, { wch: 18 }, { wch: 16 }];
    // Aplicar bold nas linhas designadas (best-effort; formato xlsx aceita s.font)
    try {
      boldRows.forEach(r => {
        for (let c = 0; c < 5; c++) {
          const cell = XLSX.utils.encode_cell({ r, c });
          if (wsDetail[cell]) {
            wsDetail[cell].s = { font: { bold: true } };
          }
        }
      });
    } catch { /* xlsx free build pode não suportar; ignora */ }

    // ─── ABA 2: Lista simples (uma linha por pessoa) — para buffet ───
    const peopleRows = [];
    sortedInvites.forEach(invite => {
      const inds = (invite.individuals || []).slice();
      const fallback = inds.length === 0;
      const list = fallback ? (() => {
        const a = invite.adults_count || 0;
        const c = invite.children_count || 0;
        const b = invite.babies_count || 0;
        const total = invite.total_people || (a + c + b) || 1;
        const arr = [];
        for (let i = 0; i < a; i++) arr.push({ name: '', type: 'adult' });
        for (let i = 0; i < c; i++) arr.push({ name: '', type: 'child' });
        for (let i = 0; i < b; i++) arr.push({ name: '', type: 'baby' });
        while (arr.length < total) arr.push({ name: '', type: 'adult' });
        return arr;
      })() : inds;
      list.forEach((ind, i) => {
        const isBaby = ind.type === 'baby';
        const isChild = ind.type === 'child';
        const tipo = isBaby ? 'Bebê' : isChild ? 'Criança' : 'Adulto';
        const nome = (ind?.name || '').trim() || (isBaby ? 'Bebê' : isChild ? 'Criança' : `Convidado ${i + 1}`);
        peopleRows.push({
          "Convite": invite.name || '',
          "Tipo": tipo,
          "Nome": nome.toUpperCase(),
          "Idade": (ind?.age !== undefined && ind?.age !== null && ind?.age !== '') ? `${ind.age} ano${Number(ind.age) === 1 ? '' : 's'}` : '',
          "Mesa": _getTableName(invite),
          "Status RSVP": statusRSVPMap[invite.status] || "Aguardando"
        });
      });
    });
    const wsPeople = XLSX.utils.json_to_sheet(peopleRows);
    wsPeople['!cols'] = [{ wch: 28 }, { wch: 10 }, { wch: 28 }, { wch: 12 }, { wch: 18 }, { wch: 16 }];

    // ─── ABA 3: Resumo (uma linha por convite, com listas detalhadas) ───
    const summary = sortedInvites.map(invite => {
      const a = (invite.individuals || []).filter(i => i?.type === 'adult').length || invite.adults_count || 0;
      const c = (invite.individuals || []).filter(i => i?.type === 'child').length || invite.children_count || 0;
      const b = (invite.individuals || []).filter(i => i?.type === 'baby').length || invite.babies_count || 0;
      return {
        "Convite": invite.name || "",
        "Categoria": (invite.vinculo || "convidado").replace(/^./, c => c.toUpperCase()),
        "Total Pessoas": invite.total_people || (a + c + b),
        "Adultos": a,
        "Nomes Adultos": _formatIndividualsList(invite, 'adult'),
        "Crianças": c,
        "Crianças (nome — idade)": _formatIndividualsList(invite, 'child'),
        "Bebês": b,
        "Bebês (nome — idade)": _formatIndividualsList(invite, 'baby'),
        "Mesa": _getTableName(invite),
        "Status RSVP": statusRSVPMap[invite.status] || "Aguardando",
        "Telefone": invite.contact_phone || "",
        "Email": invite.contact_email || "",
        "Observações": invite.notes || ""
      };
    });
    const wsSummary = XLSX.utils.json_to_sheet(summary);
    wsSummary['!cols'] = [
      { wch: 28 }, { wch: 18 }, { wch: 12 }, { wch: 8 }, { wch: 36 },
      { wch: 10 }, { wch: 36 }, { wch: 8 }, { wch: 36 }, { wch: 18 }, { wch: 14 }, { wch: 16 }, { wch: 26 }, { wch: 30 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsDetail, "Detalhado");
    XLSX.utils.book_append_sheet(wb, wsPeople, "Pessoas");
    XLSX.utils.book_append_sheet(wb, wsSummary, "Resumo");

    const fileName = `convidados_${wedding?.partner1_name || 'evento'}_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success("Lista exportada para Excel!");
  };

  // Função para exportar lista de convidados em PDF (layout vertical premium — igual ao painel do app)
  const exportToPDF = async () => {
    if (invites.length === 0) {
      toast.error("Não há convidados para exportar");
      return;
    }

    const jsPDF = await loadJsPDF();

    const statusRSVPMap = {
      "confirmado": "Confirmado",
      "nao_confirmado": "Não Confirmado",
      "a_confirmar": "Aguardando",
      "recusado": "Recusado"
    };

    const sortedInvites = [...invites].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", 'pt-BR')
    );

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const MARGIN_L = 36;
    const MARGIN_R = 36;
    const MARGIN_TOP = 50;
    const MARGIN_BOTTOM = 60;
    let y = MARGIN_TOP;

    // ── Header global ───────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(20, 20, 20);
    doc.text("Lista de Convidados", pageW / 2, y, { align: "center" });
    y += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(110, 110, 110);
    const eventTitle = wedding?.event_name || `${wedding?.partner1_name || ''}${wedding?.partner2_name ? ' & ' + wedding.partner2_name : ''}`;
    if (eventTitle.trim()) {
      doc.text(eventTitle, pageW / 2, y, { align: "center" });
      y += 14;
    }
    const totalPeople = sortedInvites.reduce((acc, inv) => acc + (inv.total_people || ((inv.individuals || []).length) || 0), 0);
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text(`${sortedInvites.length} convite${sortedInvites.length === 1 ? '' : 's'} · ${totalPeople} pessoa${totalPeople === 1 ? '' : 's'} · ${new Date().toLocaleDateString('pt-BR')}`, pageW / 2, y, { align: "center" });
    y += 18;

    // Linha divisória sutil
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.5);
    doc.line(MARGIN_L, y, pageW - MARGIN_R, y);
    y += 16;

    // Helper page break
    const ensureSpace = (needed) => {
      if (y + needed > pageH - MARGIN_BOTTOM) {
        doc.addPage();
        y = MARGIN_TOP;
      }
    };

    // Renderiza pílula colorida ao lado direito
    const drawPill = (x, py, text, bg, fg) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      const w = doc.getTextWidth(text) + 12;
      doc.setFillColor(...bg);
      doc.roundedRect(x - w, py - 9, w, 13, 6.5, 6.5, 'F');
      doc.setTextColor(...fg);
      doc.text(text, x - w / 2, py - 0.5, { align: 'center' });
    };

    // ── Render por convite ──────────────────────────────────────
    sortedInvites.forEach((invite, idx) => {
      const individuals = invite.individuals || [];
      // sintetizar fallback se vazio
      let people = individuals.slice();
      if (people.length === 0) {
        const a = invite.adults_count || 0;
        const c = invite.children_count || 0;
        const b = invite.babies_count || 0;
        const total = invite.total_people || (a + c + b) || 1;
        for (let i = 0; i < a; i++) people.push({ name: '', type: 'adult' });
        for (let i = 0; i < c; i++) people.push({ name: '', type: 'child' });
        for (let i = 0; i < b; i++) people.push({ name: '', type: 'baby' });
        while (people.length < total) people.push({ name: '', type: 'adult' });
      }

      const adultsN = people.filter(p => p.type === 'adult').length;
      const childrenN = people.filter(p => p.type === 'child').length;
      const babiesN = people.filter(p => p.type === 'baby').length;
      const totalN = people.length;
      const tableName = _getTableName(invite);
      const statusRSVP = statusRSVPMap[invite.status] || "Aguardando";

      const composition = [
        `${totalN} pessoa${totalN === 1 ? '' : 's'}`,
        adultsN > 0 ? `${adultsN} adulto${adultsN > 1 ? 's' : ''}` : null,
        childrenN > 0 ? `${childrenN} criança${childrenN > 1 ? 's' : ''}` : null,
        babiesN > 0 ? `${babiesN} bebê${babiesN > 1 ? 's' : ''}` : null,
      ].filter(Boolean).join(' · ');

      // Reserva espaço pra header do convite + ao menos 1 pessoa
      ensureSpace(34 + 18);

      // Card BG sutil
      const cardStart = y - 4;
      // Header do convite
      doc.setFillColor(245, 245, 245);
      doc.roundedRect(MARGIN_L, y - 4, 24, 24, 4, 4, 'F');
      // Ícone de grupo (desenhado vetorial simples)
      doc.setDrawColor(120, 120, 120);
      doc.setLineWidth(0.9);
      doc.circle(MARGIN_L + 9, y + 5, 2.5);
      doc.circle(MARGIN_L + 16, y + 5, 2.2);
      doc.line(MARGIN_L + 5, y + 14, MARGIN_L + 13, y + 14);
      doc.line(MARGIN_L + 12, y + 14, MARGIN_L + 19, y + 14);

      // Nome do convite
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(20, 20, 20);
      doc.text(invite.name || '(sem nome)', MARGIN_L + 32, y + 6);

      // Status pill à direita
      const statusBg = invite.status === 'confirmado' ? [220, 250, 232] : invite.status === 'recusado' ? [254, 226, 226] : [254, 243, 199];
      const statusFg = invite.status === 'confirmado' ? [22, 101, 52] : invite.status === 'recusado' ? [153, 27, 27] : [120, 82, 0];
      drawPill(pageW - MARGIN_R, y + 5, statusRSVP, statusBg, statusFg);

      // Linha de composição
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      const compoLine = tableName ? `${composition} · Mesa: ${tableName}` : composition;
      doc.text(compoLine, MARGIN_L + 32, y + 18);
      y += 32;

      // ── Indivíduos ──
      people.forEach((p, i) => {
        ensureSpace(18);
        const isChild = p.type === 'child';
        const isBaby = p.type === 'baby';
        const indent = MARGIN_L + 40;

        // Bullet circular cor
        if (isChild) doc.setFillColor(16, 185, 129); // green-500
        else if (isBaby) doc.setFillColor(236, 72, 153); // pink-500
        else doc.setFillColor(59, 130, 246); // blue-500
        doc.circle(indent, y + 1.5, 2.8, 'F');

        // Ícone person (vetorial)
        doc.setDrawColor(160, 160, 160);
        doc.setLineWidth(0.7);
        doc.circle(indent + 12, y - 2, 2.3);
        doc.line(indent + 9, y + 5, indent + 15, y + 5);

        // Nome (uppercase para destaque)
        const rawName = (p.name && p.name.trim()) || (isBaby ? 'Bebê' : isChild ? 'Criança' : `Convidado ${i + 1}`);
        const upName = rawName.toUpperCase();
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(30, 30, 30);
        doc.text(upName, indent + 20, y + 3);

        // Idade se for criança/bebê
        if ((isChild || isBaby) && p.age !== undefined && p.age !== null && p.age !== '') {
          doc.setTextColor(150, 150, 150);
          doc.setFontSize(10);
          doc.text(`— ${p.age} ano${Number(p.age) === 1 ? '' : 's'}`, indent + 20 + doc.getTextWidth(upName) + 6, y + 3);
        }

        // Badge tipo à direita
        const badgeText = isBaby ? 'Bebê' : isChild ? (p.age !== undefined && p.age !== null && p.age !== '' ? `Criança — ${p.age} ano${Number(p.age) === 1 ? '' : 's'}` : 'Criança') : 'Adulto';
        const badgeBg = isBaby ? [253, 232, 240] : isChild ? [219, 247, 232] : [219, 234, 254];
        const badgeFg = isBaby ? [157, 23, 77] : isChild ? [22, 101, 52] : [29, 78, 216];
        drawPill(pageW - MARGIN_R, y + 3, badgeText, badgeBg, badgeFg);

        y += 18;
      });

      // Card subtle border line bottom
      doc.setDrawColor(240, 240, 240);
      doc.setLineWidth(0.4);
      doc.line(MARGIN_L, y + 4, pageW - MARGIN_R, y + 4);
      y += 14;
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(170, 170, 170);
      doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')} · liventy`, pageW / 2, pageH - 28, { align: 'center' });
      doc.text(`${i} / ${pageCount}`, pageW - MARGIN_R, pageH - 28, { align: 'right' });
    }

    const fileName = `convidados_${wedding?.partner1_name || 'evento'}_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`;
    doc.save(fileName);
    toast.success("Lista exportada para PDF!");
  };

  // Função para organizar mesas com IA
  const organizarMesas = async () => {
    if (!wedding?.id) return;
    
    const confirmados = stats?.confirmed?.people || 0;
    if (confirmados === 0) {
      toast.error("Nenhum convidado confirmado para organizar nas mesas");
      return;
    }
    
    setLoadingTables(true);
    setTablesResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/ai/organize-tables`, {
        wedding_id: wedding.id,
        num_mesas: tablesConfig.num_mesas,
        lugares_por_mesa: tablesConfig.lugares_por_mesa,
        lugares_mesa_padrinhos: tablesConfig.lugares_mesa_padrinhos
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTablesResult(res.data);
      if (res.data.mesas && res.data.mesas.length > 0) {
        toast.success(`Mesas organizadas com sucesso! ${res.data.mesas.length} mesas criadas.`);
      }
    } catch (err) {
      console.error('Erro ao organizar mesas:', err);
      toast.error(err.response?.data?.detail || "Erro ao organizar mesas. Tente novamente.");
    } finally {
      setLoadingTables(false);
    }
  };

  // Exportar mapa de mesas para PDF
  const exportarMesasPDF = () => {
    if (!tablesResult?.mesas && !tablesResult?.mesaPadrinhos) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Título
    doc.setFontSize(18);
    doc.text("Mapa de Organização das Mesas", pageWidth / 2, 20, { align: "center" });
    
    // Subtítulo
    doc.setFontSize(12);
    doc.text(`${wedding?.partner1_name || ''} & ${wedding?.partner2_name || ''}`, pageWidth / 2, 28, { align: "center" });
    doc.text(`Total: ${tablesResult.total_convidados_input || tablesResult.total_convidados || 0} convidados`, pageWidth / 2, 35, { align: "center" });
    
    // Preparar dados para tabela
    const tableData = [];
    
    // Mesa de Padrinhos primeiro
    if (tablesResult.mesaPadrinhos?.convidados?.length > 0) {
      tablesResult.mesaPadrinhos.convidados.forEach((conv, idx) => {
        const nome = typeof conv === 'string' ? conv : conv.nome;
        const papel = typeof conv !== 'string' && conv.papel ? ` (${conv.papel})` : '';
        tableData.push([
          idx === 0 ? '🎀 Mesa de Padrinhos' : '',
          idx === 0 ? tablesResult.mesaPadrinhos.convidados.length : '',
          nome + papel
        ]);
      });
    }
    
    // Mesas comuns
    tablesResult.mesas?.forEach(mesa => {
      if (mesa.convidados && mesa.convidados.length > 0) {
        mesa.convidados.forEach((conv, idx) => {
          const nome = typeof conv === 'string' ? conv : conv.nome;
          tableData.push([
            idx === 0 ? (mesa.nome || `Mesa ${mesa.numero}`) : '',
            idx === 0 ? mesa.convidados.length : '',
            nome
          ]);
        });
      }
    });
    
    // Criar tabela
    doc.autoTable({
      startY: 45,
      head: [['Mesa', 'Pessoas', 'Convidado']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 10 }
    });
    
    // Salvar
    const fileName = `mapa_mesas_${wedding?.partner1_name || 'casamento'}.pdf`;
    doc.save(fileName);
    toast.success("Mapa de mesas exportado em PDF!");
  };

  // Exportar mapa de mesas para Excel
  const exportarMesasExcel = () => {
    if (!tablesResult?.mesas && !tablesResult?.mesaPadrinhos) return;
    
    const wb = XLSX.utils.book_new();
    
    // Criar planilha geral
    const allData = [];
    
    // Mesa de Padrinhos
    if (tablesResult.mesaPadrinhos?.convidados?.length > 0) {
      tablesResult.mesaPadrinhos.convidados.forEach(conv => {
        const nome = typeof conv === 'string' ? conv : conv.nome;
        const papel = typeof conv !== 'string' && conv.papel ? conv.papel : 'Padrinho/Madrinha';
        allData.push({
          "Mesa": "Mesa de Padrinhos",
          "Número": 0,
          "Convidado": nome,
          "Papel/Relacionamento": papel
        });
      });
    }
    
    // Mesas comuns
    tablesResult.mesas?.forEach(mesa => {
      const mesaNome = mesa.nome || `Mesa ${mesa.numero}`;
      if (mesa.convidados && mesa.convidados.length > 0) {
        mesa.convidados.forEach(conv => {
          const nome = typeof conv === 'string' ? conv : conv.nome;
          const rel = typeof conv !== 'string' && conv.relacionamento ? conv.relacionamento : '';
          allData.push({
            "Mesa": mesaNome,
            "Número": mesa.numero,
            "Convidado": nome,
            "Papel/Relacionamento": rel
          });
        });
      } else {
        allData.push({
          "Mesa": mesaNome,
          "Número": mesa.numero,
          "Convidado": "(Mesa reservada)",
          "Total na Mesa": 0
        });
      }
    });
    
    const ws = XLSX.utils.json_to_sheet(allData);
    XLSX.utils.book_append_sheet(wb, ws, "Mapa de Mesas");
    
    // Criar uma aba por mesa
    tablesResult.mesas.forEach(mesa => {
      if (mesa.convidados && mesa.convidados.length > 0) {
        const mesaData = mesa.convidados.map((c, i) => ({ "#": i + 1, "Convidado": c }));
        const mesaWs = XLSX.utils.json_to_sheet(mesaData);
        const sheetName = (mesa.nome || `Mesa ${mesa.numero}`).substring(0, 31);
        XLSX.utils.book_append_sheet(wb, mesaWs, sheetName);
      }
    });
    
    const fileName = `mapa_mesas_${wedding?.partner1_name || 'casamento'}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success("Mapa de mesas exportado em Excel!");
  };

  // Salvar organização das mesas no banco de dados
  const salvarOrganizacaoMesas = async () => {
    if (!tablesResult?.mesas || !wedding?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/weddings/${wedding.id}/tables`, {
        mesas: tablesResult.mesas,
        justificativa: tablesResult.justificativa
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Organização das mesas salva com sucesso!");
      setShowTablesModal(false);
      setTablesResult(null);
      fetchInvites(); // Recarregar para mostrar mesas atribuídas
    } catch (err) {
      console.error('Erro ao salvar mesas:', err);
      toast.error("Erro ao salvar organização das mesas");
    }
  };

  const addAdult = () => {
    setForm(prev => ({
      ...prev,
      adults: [...prev.adults, { name: "", guest_type: "convidado" }]
    }));
  };

  const removeAdult = (index) => {
    if (form.adults.length > 1) {
      setForm(prev => ({
        ...prev,
        adults: prev.adults.filter((_, i) => i !== index)
      }));
    }
  };

  const updateAdult = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      adults: prev.adults.map((adult, i) => i === index ? { ...adult, [field]: value } : adult)
    }));
  };

  const addChild = () => {
    setForm(prev => ({
      ...prev,
      children: [...prev.children, { name: "", age: "" }]
    }));
  };

  const removeChild = (index) => {
    setForm(prev => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index)
    }));
  };

  const updateChild = (index, field, value) => {
    if (field === "age") {
      const ageNum = parseInt(value);
      if (!isNaN(ageNum) && ageNum > 12) {
        toast.warning(`Criança com ${ageNum} anos será reclassificada como adulto automaticamente`);
      }
    }
    setForm(prev => ({
      ...prev,
      children: prev.children.map((child, i) => {
        if (i !== index) return child;
        if (field === "age") {
          // Permite limpar o campo (vazio) sem forçar 0
          const v = value === "" || value === null || value === undefined ? "" : (parseInt(value) || 0);
          return { ...child, age: v };
        }
        return { ...child, [field]: value };
      })
    }));
  };

  // === DETAILED MODE: Individual helpers ===
  const addIndividual = (type) => {
    setForm(prev => ({
      ...prev,
      individuals: [...prev.individuals, { name: "", type, age: type === 'child' ? "" : type === 'baby' ? "" : null }]
    }));
  };

  const removeIndividual = (index) => {
    setForm(prev => ({
      ...prev,
      individuals: prev.individuals.filter((_, i) => i !== index)
    }));
  };

  const updateIndividual = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      individuals: prev.individuals.map((ind, i) =>
        i === index ? { ...ind, [field]: field === 'age' ? (parseInt(value) || 0) : value } : ind
      )
    }));
  };

  const toggleDetailedMode = () => {
    setForm(prev => {
      if (!prev.detailed_mode) {
        // Simple -> Detailed: create adult entries from total
        const existingIndividuals = prev.individuals.length > 0 ? prev.individuals : [];
        if (existingIndividuals.length > 0) {
          return { ...prev, detailed_mode: true };
        }
        const numAdults = Math.max(1, (parseInt(prev.total_guests) || 1) - (parseInt(prev.children_count) || 0));
        const entries = Array.from({ length: numAdults }, () => ({ name: "", type: "adult", age: null }));
        return { ...prev, detailed_mode: true, individuals: entries };
      } else {
        // Detailed -> Simple: compute totals from individuals
        const total = prev.individuals.length || parseInt(prev.total_guests) || 1;
        const childrenCnt = prev.individuals.filter(i => i.type === 'child').length;
        const babiesCnt = prev.individuals.filter(i => i.type === 'baby').length;
        return { ...prev, detailed_mode: false, total_guests: Math.max(1, total), children_count: childrenCnt, babies_count: babiesCnt };
      }
    });
  };

  // Computed values for detailed mode
  const detailedAdults = form.individuals.map((ind, idx) => ({ ...ind, _idx: idx })).filter(i => i.type === 'adult');
  const detailedChildren = form.individuals.map((ind, idx) => ({ ...ind, _idx: idx })).filter(i => i.type === 'child');
  const detailedBabies = form.individuals.map((ind, idx) => ({ ...ind, _idx: idx })).filter(i => i.type === 'baby');
  const detailedTotal = form.individuals.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error("Nome do convite é obrigatório");
      return;
    }

    let totalGuests, childrenCount, babiesCount, individuals;

    if (form.detailed_mode) {
      if (form.individuals.length === 0) {
        toast.error("Adicione pelo menos um convidado na lista detalhada");
        return;
      }
      // Validate: adults must have names
      const adultsWithoutName = form.individuals.filter(i => i.type === 'adult' && !i.name.trim());
      if (adultsWithoutName.length > 0) {
        toast.error("Preencha o nome de todos os adultos");
        return;
      }
      // Validate: children & babies must have name AND age
      const minorsWithoutName = form.individuals.filter(i => (i.type === 'child' || i.type === 'baby') && !i.name.trim());
      if (minorsWithoutName.length > 0) {
        toast.error("Preencha o nome de todas as crianças e bebês");
        return;
      }
      const minorsWithoutAge = form.individuals.filter(i => (i.type === 'child' || i.type === 'baby') && (i.age === null || i.age === undefined || i.age === '' || isNaN(Number(i.age))));
      if (minorsWithoutAge.length > 0) {
        toast.error("Informe a idade de todas as crianças e bebês");
        return;
      }
      // Auto-classify by age — Regra: Adulto 11+, Criança 3–10, Bebê 0–2 (meses sempre bebê)
      form.individuals.forEach(i => {
        if (i.type === 'child' || i.type === 'baby') {
          const a = Number(i.age);
          const unit = i.age_unit || 'years';
          if (!isNaN(a)) {
            // Idade em meses → sempre bebê. Em anos: 0-2=bebê, 3-10=criança, 11+=adulto
            if (unit === 'months') i.type = 'baby';
            else if (a <= 2) i.type = 'baby';
            else if (a <= 10) i.type = 'child';
            else i.type = 'adult';
          }
        }
      });
      // Auto-calculate from individuals
      const childrenInd = form.individuals.filter(i => i.type === 'child');
      const babiesInd = form.individuals.filter(i => i.type === 'baby');
      totalGuests = form.individuals.length;
      childrenCount = childrenInd.length;
      babiesCount = babiesInd.length;
      individuals = form.individuals.map(ind => ({
        name: ind.name.trim(),
        type: ind.type,
        ...(ind.age != null && ind.age !== '' ? { age: Number(ind.age), age_unit: ind.age_unit || 'months' } : {})
      }));
    } else {
      totalGuests = parseInt(form.total_guests) || 1;
      childrenCount = parseInt(form.children_count) || 0;
      babiesCount = parseInt(form.babies_count) || 0;
      individuals = null;
    }
    
    if (totalGuests < 1) {
      toast.error("Total de convidados deve ser pelo menos 1");
      return;
    }
    
    try {
      const inviteName = form.name.trim();
      const adultsCount = totalGuests - childrenCount - babiesCount;
      const adults = adultsCount > 0 ? [{ name: inviteName, guest_type: "convidado" }] : [];
      const children = childrenCount > 0 ? Array.from({ length: childrenCount }, (_, i) => ({ name: `Criança ${i + 1}`, age: 0 })) : [];
      
      const payload = {
        name: inviteName,
        adults,
        children,
        individuals,
        contact_phone: form.contact_phone ? '55' + form.contact_phone.replace(/\D/g, '') : null,
        contact_email: form.contact_email || null,
        kids_pickup: !!form.kids_pickup,
        responsible1_name: form.kids_pickup ? (form.responsible1_name || null) : null,
        responsible1_phone: form.kids_pickup && form.responsible1_phone ? '55' + form.responsible1_phone.replace(/\D/g, '') : null,
        responsible2_name: form.kids_pickup ? (form.responsible2_name || null) : null,
        responsible2_phone: form.kids_pickup && form.responsible2_phone ? '55' + form.responsible2_phone.replace(/\D/g, '') : null,
        status: form.status,
        notes: form.notes || null,
        table_id: form.table_id || null,
        total_guests: totalGuests,
        children_count: childrenCount,
        babies_count: babiesCount,
        detailed_mode: form.detailed_mode
      };
      
      if (editInvite) {
        await axios.put(`${API}/weddings/${wedding.id}/invites/${editInvite.id}`, payload);
        toast.success("Salvo com sucesso!");
      } else {
        await axios.post(`${API}/weddings/${wedding.id}/invites`, payload);
        toast.success("Salvo com sucesso!");
      }
      setShowModal(false);
      resetForm();
      fetchInvites();
    } catch (err) {
      toast.error(getErrorMessage(err, "Ocorreu um erro. Tente novamente."));
    }
  };

  const handleEdit = (invite) => {
    setEditInvite(invite);
    setForm({
      name: invite.name,
      total_guests: invite.total_guests || invite.total_people || 1,
      children_count: invite.children_count || (invite.children?.length || 0),
      babies_count: invite.babies_count || 0,
      detailed_mode: true,
      individuals: invite.individuals || [],
      adults: invite.adults?.length > 0 ? invite.adults.map(a => ({ name: a.name, guest_type: a.guest_type || "convidado" })) : [{ name: "" }],
      children: invite.children || [],
      contact_phone: (() => {
        let p = (invite.contact_phone || '').replace(/\D/g, '');
        if (p.startsWith('55') && p.length > 11) p = p.slice(2);
        if (p.length === 11) return `(${p.slice(0,2)}) ${p.slice(2,7)}-${p.slice(7)}`;
        if (p.length === 10) return `(${p.slice(0,2)}) ${p.slice(2,6)}-${p.slice(6)}`;
        return p;
      })(),
      contact_email: invite.contact_email || "",
      kids_pickup: !!invite.kids_pickup,
      responsible1_name: invite.responsible1_name || "",
      responsible1_phone: (() => { let p=(invite.responsible1_phone||'').replace(/\D/g,''); if(p.startsWith('55')&&p.length>11)p=p.slice(2); if(p.length===11)return `(${p.slice(0,2)}) ${p.slice(2,7)}-${p.slice(7)}`; if(p.length===10)return `(${p.slice(0,2)}) ${p.slice(2,6)}-${p.slice(6)}`; return p; })(),
      responsible2_name: invite.responsible2_name || "",
      responsible2_phone: (() => { let p=(invite.responsible2_phone||'').replace(/\D/g,''); if(p.startsWith('55')&&p.length>11)p=p.slice(2); if(p.length===11)return `(${p.slice(0,2)}) ${p.slice(2,7)}-${p.slice(7)}`; if(p.length===10)return `(${p.slice(0,2)}) ${p.slice(2,6)}-${p.slice(6)}`; return p; })(),
      status: invite.status,
      notes: invite.notes || "",
      table_id: invite.table_id || "",
      table_search: ""
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    // Encontrar o convite para verificar status
    const invite = invites.find(i => i.id === id);
    
    // Se já fez check-in, bloquear exclusão
    if (invite?.presence_status === "presente") {
      toast.error("❌ Não é possível excluir. Este convidado já realizou check-in no evento.");
      return;
    }
    
    // Se confirmou presença, pedir confirmação especial
    if (invite?.status === "confirmado") {
      const confirmed = await confirmDialog({
        title: "Convidado com presença confirmada",
        message: "Este convidado já confirmou presença no evento. Deseja realmente excluir este convite?\n\nEsta ação não poderá ser desfeita.",
        confirmLabel: "Excluir mesmo assim",
        variant: "danger",
      });
      if (!confirmed) return;
    } else {
      // Confirmação simples para convidados não confirmados
      if (!(await confirmDialog({
        title: "Remover convite",
        message: "Tem certeza que deseja remover este convite e todos os convidados vinculados?",
        confirmLabel: "Remover",
        variant: "danger",
      }))) return;
    }
    
    try {
      await axios.delete(`${API}/weddings/${wedding.id}/invites/${id}`);
      toast.success("Ação realizada com sucesso.");
      fetchInvites();
    } catch (err) {
      toast.error("Erro ao remover");
    }
  };

  // V1 RSVP por participante: o link só pode ser enviado quando os participantes
  // estão nomeados (decisão do anfitrião: nomear antes de enviar).
  const participantsNamed = (invite) => {
    const inds = invite?.individuals || [];
    return inds.length > 0 && inds.every(i => (i?.name || '').trim());
  };

  const requireNamedParticipants = (invite) => {
    if (participantsNamed(invite)) return true;
    toast.error("Detalhe e nomeie os participantes deste convite antes de enviar o link de confirmação.");
    closeGuestSheet?.();
    setTimeout(() => handleEdit(invite), 220);
    return false;
  };

  // Função para obter QR Code
  const handleShowQRCode = async (invite) => {
    if (!invite.rsvp_token) {
      toast.error("Este convite não possui token RSVP");
      return;
    }
    if (!requireNamedParticipants(invite)) return;
    setLoadingQR(true);
    setShowQRModal(true);
    try {
      const res = await axios.get(`${API}/rsvp/${invite.rsvp_token}/qrcode-base64`);
      setQRCodeData(res.data);
    } catch (err) {
      toast.error("Erro ao gerar QR Code");
      setShowQRModal(false);
    } finally {
      setLoadingQR(false);
    }
  };

  // Função para download do QR Code
  const handleDownloadQR = () => {
    if (!qrCodeData?.qrcode) return;
    const link = document.createElement('a');
    link.download = `qrcode-${qrCodeData.invite_name || 'convite'}.png`;
    link.href = qrCodeData.qrcode;
    link.click();
  };

  const guestTypeLabels = {
    convidado: "Convidado",
    padrinho_noivo: "Padrinho do Noivo",
    padrinho_noiva: "Padrinho da Noiva",
    madrinha_noivo: "Madrinha do Noivo",
    madrinha_noiva: "Madrinha da Noiva"
  };

  const guestTypeColors = {
    convidado: "bg-slate-100 text-slate-700",
    padrinho_noivo: "bg-blue-100 text-blue-700",
    padrinho_noiva: "bg-blue-100 text-blue-700",
    madrinha_noivo: "bg-pink-100 text-pink-700",
    madrinha_noiva: "bg-pink-100 text-pink-700"
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmado: "bg-emerald-500",
      a_confirmar: "bg-amber-400",
      nao_ira: "bg-red-400",
      recusado: "bg-red-400",
      confirmed: "bg-emerald-500",
      pending: "bg-amber-400",
      declined: "bg-red-400"
    };
    return colors[status] || colors.a_confirmar;
  };

  const getStatusLabel = (status) => {
    const labels = { 
      confirmado: "Confirmado", 
      a_confirmar: "Pendente", 
      nao_ira: "Recusado",
      recusado: "Recusado",
      confirmed: "Confirmado", 
      pending: "Pendente", 
      declined: "Recusado" 
    };
    return labels[status] || "Pendente";
  };

  const groupLabels = {
    familia_noiva: "Família da Noiva",
    familia_noivo: "Família do Noivo",
    amigos: "Amigos",
    trabalho: "Trabalho"
  };

  // Função para gerar link WhatsApp com convite RSVP
  const getWhatsAppInviteLink = (invite) => {
    const rsvpUrl = `${window.location.origin}/confirmar/${invite.rsvp_token}`;
    
    // Nomes dos noivos
    const noivos = wedding?.partner1_name && wedding?.partner2_name 
      ? `${wedding.partner1_name} e ${wedding.partner2_name}` 
      : "Os noivos";
    
    // Formatar data (DD/MM/YYYY)
    const eventDate = formatDateBR(wedding?.event_date) || "Data a definir";
    
    // Formatar horário (19:30 -> 19h30)
    const formatTime = (timeStr) => {
      if (!timeStr) return "19h30";
      return timeStr.replace(':', 'h');
    };
    const eventTime = formatTime(wedding?.event_time);
    
    // Local e endereço
    const localName = wedding?.venue_name || "Local a definir";
    let endereco = "";
    if (wedding?.venue_address) {
      endereco = wedding.venue_address;
      if (wedding?.venue_city && wedding?.venue_state) {
        endereco += ` - ${wedding.venue_city}/${wedding.venue_state}`;
      } else if (wedding?.venue_city) {
        endereco += ` - ${wedding.venue_city}`;
      }
    }
    
    // MENSAGEM PADRONIZADA liventy
    const message = `Olá ${invite.name} 😊

Você foi convidado para:

🎉 *${noivos}*
📅 ${eventDate}${eventTime ? ` às ${eventTime}` : ''}
${localName !== 'Local a definir' ? `📍 ${localName}${endereco ? ` - ${endereco}` : ''}\n` : ''}
Confirme sua presença no link abaixo:

👉 ${rsvpUrl}

Esperamos você 💛`;
    
    const phone = invite.contact_phone?.replace(/\D/g, '') || '';
    return `https://wa.me/55${phone}?text=${encodeURIComponent(message)}`;
  };

  // Função para gerar link WhatsApp com QR Code (após confirmação)
  const getWhatsAppQRCodeLink = (invite) => {
    const qrcodeUrl = `${API}/rsvp/${invite.rsvp_token}/qrcode`;
    const noivos = wedding?.partner1_name && wedding?.partner2_name 
      ? `${wedding.partner1_name} e ${wedding.partner2_name}` 
      : "Os noivos";
    const eventDate = formatDateBR(wedding?.event_date) || "";
    
    // Montar local para lembrete
    let localInfo = "";
    if (wedding?.venue_name) {
      localInfo = `📍 ${wedding.venue_name}`;
      if (wedding?.venue_address) {
        localInfo += `\n📌 ${wedding.venue_address}`;
      }
      if (wedding?.venue_city && wedding?.venue_state) {
        localInfo += ` - ${wedding.venue_city}/${wedding.venue_state}`;
      }
      if (wedding?.venue_maps_link) {
        localInfo += `\n🗺️ ${wedding.venue_maps_link}`;
      }
    }
    
    const message = `Seu acesso ao evento está pronto ✅

🎉 *${noivos}*${eventDate ? `\n📅 ${eventDate}` : ''}

🎟️ QR Code de entrada:
${qrcodeUrl}

${localInfo ? `${localInfo}\n\n` : ''}Recomendamos salvar o QR Code no celular para facilitar seu acesso.

Nos vemos lá 😊`;
    
    const phone = invite.contact_phone?.replace(/\D/g, '') || '';
    return `https://wa.me/55${phone}?text=${encodeURIComponent(message)}`;
  };

  // Função legada para WhatsApp simples
  const getWhatsAppLink = (invite) => {
    // Se não confirmou RSVP, envia convite
    if (invite.status !== "confirmado") {
      return getWhatsAppInviteLink(invite);
    }
    // Se já confirmou, envia QR Code
    return getWhatsAppQRCodeLink(invite);
  };

  // Get all people as flat list
  const getAllPeople = () => {
    const people = [];
    invites.forEach(invite => {
      invite.adults?.forEach(adult => {
        people.push({
          name: adult.name,
          type: "adult",
          status: invite.status,
          inviteName: invite.name,
          inviteId: invite.id,
          reclassified: adult.reclassified
        });
      });
      invite.children?.forEach((child, index) => {
        people.push({
          name: child.name ? `${child.name} (${child.age} anos)` : `Criança ${index + 1} (${child.age} anos)`,
          type: "child",
          age: child.age,
          status: invite.status,
          inviteName: invite.name,
          inviteId: invite.id
        });
      });
    });
    return people;
  };

  // Skeleton loading para melhor UX durante carregamento
  if (loading) {
    return <SkeletonPage stats={4} rows={6} />;
  }

  return (
    <div className="space-y-6 pb-24 md:pb-0" style={{fontFamily:"'Poppins', sans-serif"}}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Convidados</h1>
          <p className="text-sm text-zinc-500">Organize sua lista e acompanhe as confirmações</p>
        </div>
        <div className="hidden md:flex gap-2">
          <button
            onClick={() => navigate('/organizar-mesas')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-all"
            data-testid="organize-tables-btn"
            title="Organizar mesas dos convidados confirmados"
          >
            <LayoutGrid size={15} />
            Organizar Mesas
          </button>
          <button onClick={exportToPDF} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-all" data-testid="export-pdf-btn" title="Exportar lista em PDF">
            <Download size={15} />
            PDF
          </button>
          <button onClick={exportToExcel} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-all" data-testid="export-excel-btn" title="Exportar lista em Excel">
            <Download size={15} />
            Excel
          </button>
          <button onClick={() => setShowImportCsv(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-all" data-testid="import-csv-btn" title="Importar convidados via CSV">
            <Upload size={15} />
            Importar CSV
          </button>
          <button onClick={() => { resetForm(); setShowModal(true); }} className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-[13.5px] font-semibold shadow-[0_8px_24px_-8px_rgba(107,61,246,0.5)] active:scale-[0.98] transition-transform" style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)" }} data-testid="add-invite-btn">
            <Plus size={16} strokeWidth={2.4} />
            Adicionar convidado
          </button>
        </div>
      </div>

      {/* Stats Cards Mobile — Carousel horizontal premium (md:hidden) */}
      <div className="md:hidden -mx-4 px-4" data-testid="mobile-guest-stats-carousel">
        <div className="grid grid-cols-2 gap-2.5">
          {/* Total */}
          <button type="button" onClick={() => { setQuickFilter('all'); document.getElementById('guests-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="text-left snap-start shrink-0 w-[150px] bg-white rounded-2xl p-3.5 border border-violet-100/70 shadow-[0_2px_10px_rgba(139,92,246,0.04)] card-hover-brightness" data-testid="mobile-stat-card-total">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Users size={13} className="text-white" />
              </div>
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Total</p>
            </div>
            <p className="text-[26px] font-bold tracking-tighter text-zinc-900 tabular-nums leading-none">{stats?.total_people || 0}</p>
            <p className="text-[10.5px] text-zinc-400 mt-1">{stats?.total_invites || 0} convite{stats?.total_invites === 1 ? '' : 's'}</p>
          </button>
          {/* Confirmados */}
          <button type="button" onClick={() => { setQuickFilter(quickFilter === 'confirmed' ? 'all' : 'confirmed'); document.getElementById('guests-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="text-left bg-white rounded-2xl p-3 border border-emerald-100/70 shadow-[0_2px_10px_rgba(16,185,129,0.04)] card-hover-brightness" data-testid="mobile-stat-card-confirmed">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Check size={14} className="text-emerald-600" strokeWidth={2.5} />
              </div>
              <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">Confirmados</p>
            </div>
            <p className="text-[26px] font-bold tracking-tighter text-emerald-600 tabular-nums leading-none">{stats?.confirmed?.people || 0}</p>
            <p className="text-[10.5px] text-zinc-400 mt-1">
              {stats?.total_people > 0 ? `${Math.round(((stats.confirmed?.people || 0) / stats.total_people) * 100)}% do total` : '—'}
            </p>
          </button>
          {/* Pendentes */}
          <button type="button" onClick={() => { setQuickFilter(quickFilter === 'pending' ? 'all' : 'pending'); document.getElementById('guests-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="text-left bg-white rounded-2xl p-3 border border-amber-100/70 shadow-[0_2px_10px_rgba(245,158,11,0.04)] card-hover-brightness" data-testid="mobile-stat-card-pending">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                <Clock size={13} className="text-amber-600" />
              </div>
              <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Aguardando</p>
            </div>
            <p className="text-[26px] font-bold tracking-tighter text-amber-600 tabular-nums leading-none">{stats?.pending?.people || 0}</p>
            <p className="text-[10.5px] text-zinc-400 mt-1">{stats?.pending?.invites || 0} pendente{stats?.pending?.invites === 1 ? '' : 's'}</p>
          </button>
          {/* Recusaram */}
          <button type="button" onClick={() => { setQuickFilter(quickFilter === 'declined' ? 'all' : 'declined'); document.getElementById('guests-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="text-left bg-white rounded-2xl p-3 border border-rose-100/70 shadow-[0_2px_10px_rgba(244,63,94,0.04)] card-hover-brightness" data-testid="mobile-stat-card-declined">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-7 h-7 rounded-lg bg-rose-50 flex items-center justify-center">
                <X size={13} className="text-rose-500" strokeWidth={2.5} />
              </div>
              <p className="text-[10px] font-semibold text-rose-500 uppercase tracking-wider">Não irão</p>
            </div>
            <p className="text-[26px] font-bold tracking-tighter text-rose-500 tabular-nums leading-none">{stats?.declined?.people || 0}</p>
            <p className="text-[10.5px] text-zinc-400 mt-1">{stats?.declined?.invites || 0} convite{stats?.declined?.invites === 1 ? '' : 's'}</p>
          </button>
          {/* Spacer trailing */}
          <div className="shrink-0 w-1" />
        </div>
      </div>

      {/* Stats Cards Desktop — Total destacado + 3 status (hidden md:grid) — clicáveis filtram a lista */}
      <div className="hidden md:grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="guests-stats">
        {/* TOTAL DE CONVIDADOS — mesmo padrão dos demais cards */}
        <button type="button" onClick={() => { setQuickFilter('all'); document.getElementById('guests-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className={`col-span-2 md:col-span-1 text-left bg-white rounded-xl p-4 border ${quickFilter === 'all' ? 'border-zinc-400 ring-1 ring-zinc-200' : 'border-zinc-200/60'} card-hover-brightness card-animate`} data-testid="stat-total-people">
          <div className="flex items-center gap-2 mb-1">
            <Users size={12} className="text-zinc-500" />
            <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Total convidados</p>
          </div>
          <div className="flex items-baseline gap-1.5">
            <p className="text-[24px] md:text-[26px] font-semibold text-zinc-900 tabular-nums leading-tight" style={{letterSpacing:'-0.02em'}}>{stats?.total_people || 0}</p>
            <p className="text-[12px] text-zinc-400">{stats?.total_people === 1 ? 'pessoa' : 'pessoas'}</p>
          </div>
          <p className="text-[11px] text-zinc-400 mt-1.5 truncate">
            <span className="text-zinc-700 font-medium tabular-nums">{stats?.total_adults || 0}</span> adultos
            <span className="mx-1 text-zinc-300">·</span>
            <span className="text-zinc-700 font-medium tabular-nums">{stats?.total_children || 0}</span> crianças
            <span className="mx-1 text-zinc-300">·</span>
            <span className="text-zinc-700 font-medium tabular-nums">{stats?.total_babies || 0}</span> bebês
          </p>
          <p className="text-[10.5px] text-zinc-300 mt-0.5">{stats?.total_invites || 0} convite{stats?.total_invites === 1 ? '' : 's'}</p>
        </button>

        {/* CONFIRMADOS */}
        <button type="button" onClick={() => { setQuickFilter(quickFilter === 'confirmed' ? 'all' : 'confirmed'); document.getElementById('guests-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className={`text-left bg-white rounded-xl p-4 border ${quickFilter === 'confirmed' ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-zinc-200/60'} card-hover-brightness card-animate`} data-testid="stat-confirmed">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Confirmados</p>
          </div>
          <p className="text-[24px] md:text-[26px] font-semibold text-emerald-600 tabular-nums leading-tight" style={{letterSpacing:'-0.02em'}}>{stats?.confirmed?.people || 0}</p>
          <p className="text-[11px] text-zinc-400 mt-1.5">
            <span className="tabular-nums">{stats?.confirmed?.invites || 0}</span> convite{stats?.confirmed?.invites === 1 ? '' : 's'}
            {stats?.total_people > 0 && (
              <span className="ml-1.5 text-zinc-300">· {Math.round(((stats.confirmed.people || 0) / stats.total_people) * 100)}%</span>
            )}
          </p>
        </button>

        {/* AGUARDANDO */}
        <button type="button" onClick={() => { setQuickFilter(quickFilter === 'pending' ? 'all' : 'pending'); document.getElementById('guests-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className={`text-left bg-white rounded-xl p-4 border ${quickFilter === 'pending' ? 'border-amber-300 ring-1 ring-amber-200' : 'border-zinc-200/60'} card-hover-brightness card-animate`} data-testid="stat-pending">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Aguardando</p>
          </div>
          <p className="text-[24px] md:text-[26px] font-semibold text-amber-600 tabular-nums leading-tight" style={{letterSpacing:'-0.02em'}}>{stats?.pending?.people || 0}</p>
          <p className="text-[11px] text-zinc-400 mt-1.5">
            <span className="tabular-nums">{stats?.pending?.invites || 0}</span> convite{stats?.pending?.invites === 1 ? '' : 's'} pendente{stats?.pending?.invites === 1 ? '' : 's'}
          </p>
        </button>

        {/* RECUSADOS */}
        <button type="button" onClick={() => { setQuickFilter(quickFilter === 'declined' ? 'all' : 'declined'); document.getElementById('guests-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className={`text-left bg-white rounded-xl p-4 border ${quickFilter === 'declined' ? 'border-rose-300 ring-1 ring-rose-200' : 'border-zinc-200/60'} card-hover-brightness card-animate`} data-testid="stat-declined">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Não irão</p>
          </div>
          <p className="text-[24px] md:text-[26px] font-semibold text-red-500 tabular-nums leading-tight" style={{letterSpacing:'-0.02em'}}>{stats?.declined?.people || 0}</p>
          <p className="text-[11px] text-zinc-400 mt-1.5">
            <span className="tabular-nums">{stats?.declined?.invites || 0}</span> convite{stats?.declined?.invites === 1 ? '' : 's'}
          </p>
        </button>
      </div>

      {/* Bloco de Convites Personalizados e Créditos WhatsApp */}
      {/* Tables Management Panel */}
      {showTablesPanel && (
        <Card className="p-5 border-slate-200" data-testid="tables-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-slate-800">Mesas do Evento</h3>
            <button onClick={() => setShowTablesPanel(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          {tables.length === 0 ? (
            <p className="text-sm text-slate-400 mb-4">Nenhuma mesa criada ainda.</p>
          ) : (
            <div className="space-y-2 mb-4" data-testid="tables-list">
              {tables.map(t => (
                <div key={t.id} className="flex items-center gap-2 group bg-slate-50 rounded-lg px-3 py-2.5">
                  {editingTableId === t.id ? (
                    <React.Fragment>
                      <input value={editingTableName} onChange={e => setEditingTableName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleRenameTable(t.id)} className="flex-1 text-sm px-2 py-1 border border-slate-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400" autoFocus />
                      <button onClick={() => handleRenameTable(t.id)} className="text-emerald-600 hover:text-emerald-700 p-1"><Check size={16} /></button>
                      <button onClick={() => setEditingTableId(null)} className="text-slate-400 hover:text-slate-600 p-1"><X size={16} /></button>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <span className="flex-1 text-sm text-slate-700 font-medium">{t.name}</span>
                      <span className="text-xs text-slate-400 mr-2">{t.guest_count} convite(s)</span>
                      <button onClick={() => { setEditingTableId(t.id); setEditingTableName(t.name); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 p-1 transition-opacity"><Pencil size={14} /></button>
                      <button onClick={() => handleDeleteTable(t.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 p-1 transition-opacity"><Trash2 size={14} /></button>
                    </React.Fragment>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 pt-3 border-t border-slate-200">
            <input value={newTableName} onChange={e => setNewTableName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateTable()} placeholder="Nome da mesa (ex: Mesa 1, Mesa VIP...)" className="flex-1 text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400" data-testid="new-table-input" />
            <button onClick={handleCreateTable} disabled={tableLoading || !newTableName.trim()} className="flex items-center gap-1 px-3 py-2 bg-[#4A4543] text-white rounded-lg hover:bg-[#3D3A38] text-sm disabled:opacity-50 transition-colors" data-testid="add-table-btn">
              <Plus size={14} /> Nova mesa
            </button>
          </div>
        </Card>
      )}

      {/* Mobile mass-actions trigger — sempre acima do toggle (mobile-first) */}
      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setShowMassActions(true)}
          className="w-full inline-flex items-center justify-between px-4 py-3 rounded-2xl bg-zinc-900 text-white font-semibold text-[13.5px] shadow-[0_4px_14px_-4px_rgba(24,24,27,0.4)] active:opacity-90"
          data-testid="btn-mass-actions-mobile"
        >
          <span className="inline-flex items-center gap-2"><MessageCircle size={16} /> Enviar via WhatsApp</span>
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Desktop mass-actions trigger — compacto, alinhado à direita */}
      <div className="hidden md:flex items-center justify-end">
        <button
          type="button"
          onClick={() => setShowMassActions(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-zinc-900 text-white font-semibold text-[13px] hover:bg-zinc-800 transition-colors"
          data-testid="btn-mass-actions"
        >
          <MessageCircle size={15} />
          Enviar via WhatsApp
          <ChevronDown size={14} className="opacity-70" />
        </button>
      </div>

      {/* View Toggle */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode("invites")}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${viewMode === "invites" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="view-mode-invites"
          >
            Por Convite
          </button>
          <button
            onClick={() => setViewMode("people")}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${viewMode === "people" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="view-mode-people"
          >
            Lista Completa
          </button>
        </div>
        <button
          onClick={() => setShowBuffet(true)}
          className="px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200/70"
          data-testid="buffet-forecast-btn"
          title="Previsão de buffet com base nas confirmações"
        >
          <UtensilsCrossed size={16} />
          Previsão Buffet
        </button>
      </div>

      {showBuffet && <BuffetForecastModal invites={invites} wedding={wedding} onClose={() => setShowBuffet(false)} />}

      {/* Invites View */}
      {viewMode === "invites" && (
        <Card id="guests-list-section" data-testid="guests-list-section" className="overflow-hidden scroll-mt-6">
          {invites.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="mx-auto text-slate-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-slate-800 mb-2">Nenhum convidado cadastrado ainda.</h3>
              <p className="text-slate-500 mb-4">Adicione convidados para começar a organizar sua lista.</p>
              <button onClick={() => { resetForm(); setShowModal(true); }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-[13.5px] font-semibold shadow-[0_8px_24px_-8px_rgba(107,61,246,0.5)] active:scale-[0.98] transition-transform" style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)" }} data-testid="add-invite-empty-btn">
                <Plus size={16} strokeWidth={2.4} />
                <span>Adicionar convidado</span>
              </button>
            </div>
          ) : (() => {
            const counts = {
              all: invites.length,
              confirmed: invites.filter(i => i.status === 'confirmado' || i.status === 'confirmed').length,
              pending: invites.filter(i => !['confirmado','confirmed','nao_ira','recusado','declined'].includes(i.status)).length,
              declined: invites.filter(i => ['nao_ira','recusado','declined'].includes(i.status)).length,
              qr_sent: invites.filter(i => !!i.qrcode_sent_at || !!i.checked_in).length,
              qr_pending: invites.filter(i => (i.status === 'confirmado' || i.status === 'confirmed') && !i.qrcode_sent_at && !i.checked_in).length,
              reminder_sent: invites.filter(i => !!i.reminder_sent_at).length,
            };
            const displayedInvites = invites.filter(i => {
              if (quickFilter === 'all') return true;
              if (quickFilter === 'confirmed') return i.status === 'confirmado' || i.status === 'confirmed';
              if (quickFilter === 'pending') return !['confirmado','confirmed','nao_ira','recusado','declined'].includes(i.status);
              if (quickFilter === 'declined') return ['nao_ira','recusado','declined'].includes(i.status);
              if (quickFilter === 'qr_sent') return !!i.qrcode_sent_at || !!i.checked_in;
              if (quickFilter === 'qr_pending') return (i.status === 'confirmado' || i.status === 'confirmed') && !i.qrcode_sent_at && !i.checked_in;
              if (quickFilter === 'reminder_sent') return !!i.reminder_sent_at;
              return true;
            });
            const filterPills = [
              { key: 'all', label: 'Todos', count: counts.all, dot: 'bg-zinc-300' },
              { key: 'confirmed', label: 'Confirmados', count: counts.confirmed, dot: 'bg-emerald-500' },
              { key: 'pending', label: 'Pendentes', count: counts.pending, dot: 'bg-amber-500' },
              { key: 'declined', label: 'Recusados', count: counts.declined, dot: 'bg-red-500' },
              { key: 'qr_pending', label: 'QR não enviado', count: counts.qr_pending, dot: 'bg-orange-500' },
              { key: 'qr_sent', label: 'QR enviado', count: counts.qr_sent, dot: 'bg-purple-500' },
              { key: 'reminder_sent', label: 'Lembrete enviado', count: counts.reminder_sent, dot: 'bg-blue-500' },
            ];
            return (
            <div className="divide-y">
              {/* Filtros rápidos */}
              <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-1.5 overflow-x-auto" data-testid="quick-filters">
                {filterPills.map(p => (
                  <button key={p.key} onClick={() => setQuickFilter(p.key)} data-testid={`filter-${p.key}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-medium whitespace-nowrap transition-all ${quickFilter === p.key ? 'bg-brand-deep text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />
                    {p.label}
                    <span className={`text-[11px] ${quickFilter === p.key ? 'text-zinc-300' : 'text-zinc-400'}`}>{p.count}</span>
                  </button>
                ))}
              </div>
              {/* Header de seleção: botão "Selecionar" (marcar um a um) + selecionar todos */}
              <div className="p-3 bg-zinc-50/50 flex items-center gap-3">
                {(selectMode || selectedInvites.length > 0) ? (
                  <>
                    <input 
                      type="checkbox"
                      checked={displayedInvites.length > 0 && displayedInvites.every(i => selectedInvites.includes(i.id))}
                      onChange={() => {
                        const allSelected = displayedInvites.every(i => selectedInvites.includes(i.id));
                        if (allSelected) {
                          setSelectedInvites(prev => prev.filter(id => !displayedInvites.some(i => i.id === id)));
                        } else {
                          setSelectedInvites(prev => [...new Set([...prev, ...displayedInvites.map(i => i.id)])]);
                        }
                      }}
                      className="w-4 h-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-500/20"
                      data-testid="select-all-checkbox"
                    />
                    <span className="text-[12.5px] text-zinc-600">
                      {selectedInvites.length > 0 ? `${selectedInvites.length} selecionado(s)` : `Selecionar todos${quickFilter !== 'all' ? ' (filtrados)' : ''}`}
                    </span>
                    <button
                      onClick={() => { setSelectMode(false); setSelectedInvites([]); }}
                      className="ml-auto text-[12px] font-medium text-zinc-500 hover:text-zinc-800 px-2 py-1 rounded-md hover:bg-zinc-100 transition-colors"
                      data-testid="exit-select-mode"
                    >
                      Concluir
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setSelectMode(true)}
                      className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-zinc-700 border border-zinc-200 bg-white px-3 py-1.5 rounded-full hover:border-zinc-300 transition-colors"
                      data-testid="enter-select-mode"
                    >
                      <CheckSquare size={14} /> Selecionar
                    </button>
                    <span className="text-[11.5px] text-zinc-400">marque um a um ou todos</span>
                    {displayedInvites.length !== invites.length && (
                      <span className="text-[11px] text-zinc-400 ml-auto">{displayedInvites.length} de {invites.length}</span>
                    )}
                  </>
                )}
              </div>
              {displayedInvites.length === 0 ? (
                <div className="p-10 text-center">
                  <p className="text-[13px] text-zinc-400">Nenhum convidado nesse filtro.</p>
                </div>
              ) : displayedInvites.map(invite => {
                const isConfirmed = invite.status === 'confirmado' || invite.status === 'confirmed';
                const isDeclined = ['nao_ira','recusado','declined'].includes(invite.status);
                const statusBadge = isConfirmed
                  ? 'bg-emerald-50 text-emerald-600'
                  : isDeclined
                  ? 'bg-red-50 text-red-500'
                  : 'bg-amber-50 text-amber-600';
                const tableName = invite.table_id ? getTableName(invite.table_id) : null;
                const peopleCount = invite.total_guests || invite.total_people || 1;
                return (
                <SwipeableRow
                  key={invite.id}
                  enabled={isMobile}
                  onDelete={() => handleDelete(invite.id)}
                  data-testid={`invite-row-${invite.id}`}
                >
                {(() => {
                  const palettes = [
                    'bg-violet-100 text-violet-700',
                    'bg-emerald-100 text-emerald-700',
                    'bg-sky-100 text-sky-700',
                    'bg-amber-100 text-amber-700',
                    'bg-rose-100 text-rose-700',
                    'bg-indigo-100 text-indigo-700',
                    'bg-teal-100 text-teal-700',
                    'bg-fuchsia-100 text-fuchsia-700',
                  ];
                  const hash = [...((invite.name || '?'))].reduce((a, c) => a + c.charCodeAt(0), 0);
                  const avatarBg = palettes[hash % palettes.length];
                  const inSelectMode = selectMode || selectedInvites.length > 0;
                  const statusLabel = isConfirmed ? 'Confirmado' : isDeclined ? 'Recusado' : 'Pendente';
                  const statusClass = isConfirmed ? 'text-emerald-600' : isDeclined ? 'text-rose-500' : 'text-amber-600';
                  return (
                    <>
                    <div className="px-4 py-3 md:py-3 border-b border-zinc-100 last:border-b-0 transition-colors">
                      {/* DESKTOP (md+) — clean clickable row: avatar + nome + pessoas · status (idêntico ao mobile com hover desktop) */}
                      <div className="hidden md:flex items-center gap-3">
                        {inSelectMode && (
                          <input type="checkbox" checked={selectedInvites.includes(invite.id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); toggleInviteSelection(invite.id); }}
                            className="w-4 h-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-500/20"
                            data-testid={`desktop-select-${invite.id}`}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (inSelectMode) { toggleInviteSelection(invite.id); return; }
                            openGuestSheet(invite);
                          }}
                          className="flex-1 flex items-center gap-3.5 text-left tap-press card-hover-brightness rounded-lg -mx-2 px-2 py-1.5"
                          data-testid={`invite-card-desktop-${invite.id}`}
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-[14px] tracking-tight ${avatarBg}`}>
                            {(invite.name || '?').trim().charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-[14.5px] font-medium text-zinc-900 truncate leading-snug">{invite.name}</h3>
                            <p className="text-[12px] text-zinc-500 mt-0.5 truncate">
                              <span className="tabular-nums">{peopleCount}</span> {peopleCount === 1 ? 'pessoa' : 'pessoas'}
                              <span className="mx-1.5 text-zinc-300">·</span>
                              <span className={statusClass}>{statusLabel}</span>
                            </p>
                          </div>
                          <ChevronRight size={14} className="text-zinc-300 flex-shrink-0" />
                        </button>
                        {invite.kids_pickup && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openCheckoutQr(invite); }}
                            title="Ver QR de saída"
                            data-testid={`qr-saida-${invite.id}`}
                            className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-violet-200 text-violet-700 text-[11.5px] font-semibold hover:bg-violet-50"
                          >
                            <QrCode size={13} /> QR saída
                          </button>
                        )}
                      </div>

                      {/* MOBILE card — ultra clean (avatar + nome + "X pessoas · status") */}
                      <button
                        type="button"
                        onClick={() => {
                          if (inSelectMode) { toggleInviteSelection(invite.id); return; }
                          openGuestSheet(invite);
                        }}
                        className="md:hidden w-full text-left tap-press flex items-center gap-3.5 py-1"
                        data-testid={`invite-card-mobile-${invite.id}`}
                      >
                        {inSelectMode && (
                          <input
                            type="checkbox"
                            checked={selectedInvites.includes(invite.id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => { e.stopPropagation(); toggleInviteSelection(invite.id); }}
                            className="w-5 h-5 rounded-full border-zinc-300 text-zinc-900 focus:ring-zinc-500/20 flex-shrink-0"
                            data-testid={`mobile-select-${invite.id}`}
                          />
                        )}
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-[15px] tracking-tight ${avatarBg}`}>
                          {(invite.name || '?').trim().charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[15.5px] font-medium text-zinc-900 truncate leading-snug">{invite.name}</h3>
                          <p className="text-[12.5px] text-zinc-500 mt-0.5 truncate">
                            <span className="tabular-nums">{peopleCount}</span> {peopleCount === 1 ? 'pessoa' : 'pessoas'}
                            <span className="mx-1.5 text-zinc-300">·</span>
                            <span className={statusClass}>{statusLabel}</span>
                          </p>
                        </div>
                        <ChevronRight size={16} className="text-zinc-300 flex-shrink-0" />
                      </button>
                    </div>
                    </>
                  );
                })()}
                </SwipeableRow>
              ); })}
            </div>
            ); })()}
        </Card>
      )}

      {/* People View */}
      {viewMode === "people" && (
        <div className="bg-white rounded-xl border border-zinc-200/60 overflow-hidden" data-testid="lista-completa-view">
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
            <div>
              <p className="text-[13.5px] font-semibold text-zinc-900 tracking-tight">Lista Completa</p>
              <p className="text-[11.5px] text-zinc-500 mt-0.5">Todos os convidados detalhados por convite com nome e idade</p>
            </div>
            <div className="hidden sm:flex items-center gap-3 text-[11.5px] text-zinc-500">
              <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />Adultos</span>
              <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Crianças (3–10)</span>
              <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-pink-500" />Bebês (0–2)</span>
            </div>
          </div>
          {invites.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="mx-auto text-zinc-300 mb-3" size={36} />
              <p className="text-[13.5px] font-semibold text-zinc-900 mb-1">Nenhum convidado cadastrado ainda.</p>
              <p className="text-[12.5px] text-zinc-500">Adicione convidados para começar a organizar sua lista.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {invites.map(invite => {
                // Build people list for this invite — prefer individuals array, fallback adults/children
                const people = [];
                const ind = Array.isArray(invite.individuals) ? invite.individuals : null;
                if (ind && ind.length > 0) {
                  ind.forEach((p, i) => {
                    const age = typeof p.age === 'number' ? p.age : null;
                    let kind = p.type;
                    // Re-classify by age if available (0-2=baby, 3-10=child, else adult)
                    if (age !== null) {
                      if (age <= 2) kind = 'baby';
                      else if (age <= 10) kind = 'child';
                      else kind = 'adult';
                    } else if (kind === 'crianca') kind = 'child';
                    else if (kind === 'bebe') kind = 'baby';
                    else if (kind === 'adulto') kind = 'adult';
                    people.push({ name: p.name || '', kind: kind || 'adult', age });
                  });
                } else {
                  (invite.adults || []).forEach(a => people.push({ name: a.name || '', kind: 'adult', age: null }));
                  (invite.children || []).forEach((c, i) => {
                    const age = typeof c.age === 'number' ? c.age : null;
                    const kind = age !== null && age <= 2 ? 'baby' : 'child';
                    people.push({ name: c.name || '', kind, age, fallbackIndex: i + 1 });
                  });
                }
                const adultsCount = people.filter(p => p.kind === 'adult').length;
                const childCount = people.filter(p => p.kind === 'child').length;
                const babyCount = people.filter(p => p.kind === 'baby').length;
                const total = people.length;
                const statusBadge = invite.status === 'confirmado' || invite.status === 'confirmed'
                  ? 'bg-emerald-50 text-emerald-700' : ['nao_ira','recusado','declined'].includes(invite.status)
                  ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700';
                const tableName = invite.table_id ? getTableName(invite.table_id) : null;
                return (
                  <div key={invite.id} className="px-4 py-4" data-testid={`lista-invite-${invite.id}`}>
                    {/* Invite header */}
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
                          <Users size={14} className="text-zinc-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[14px] font-semibold text-zinc-900 tracking-tight truncate">{invite.name}</p>
                          <p className="text-[11px] text-zinc-500 mt-0.5 truncate">
                            {total} {total === 1 ? 'pessoa' : 'pessoas'}
                            {adultsCount > 0 && <span className="text-zinc-400"> · {adultsCount} adulto{adultsCount === 1 ? '' : 's'}</span>}
                            {childCount > 0 && <span className="text-zinc-400"> · {childCount} criança{childCount === 1 ? '' : 's'}</span>}
                            {babyCount > 0 && <span className="text-zinc-400"> · {babyCount} bebê{babyCount === 1 ? '' : 's'}</span>}
                            {tableName && <span className="text-zinc-400"> · Mesa {tableName}</span>}
                          </p>
                        </div>
                      </div>
                      <span className={`flex-shrink-0 text-[10.5px] font-medium px-2 py-0.5 rounded-full ${statusBadge}`}>{getStatusLabel(invite.status)}</span>
                    </div>
                    {/* Individuals tree */}
                    <div className="ml-3 pl-4 border-l border-zinc-200/70 space-y-1.5">
                      {people.length === 0 ? (
                        <p className="text-[12px] text-zinc-400 italic py-1">Nenhuma pessoa detalhada.</p>
                      ) : people.map((p, i) => {
                        const isAdult = p.kind === 'adult';
                        const isBaby = p.kind === 'baby';
                        const ageInfo = p.age !== null && p.age !== undefined ? ` — ${p.age} ${p.age === 1 ? 'ano' : 'anos'}` : '';
                        const labelType = isAdult ? 'Adulto' : isBaby ? 'Bebê' : 'Criança';
                        const colorBg = isAdult ? 'bg-blue-50 text-blue-700' : isBaby ? 'bg-pink-50 text-pink-700' : 'bg-emerald-50 text-emerald-700';
                        const dotColor = isAdult ? 'bg-blue-500' : isBaby ? 'bg-pink-500' : 'bg-emerald-500';
                        const Icon = isAdult ? User : isBaby ? Baby : UserPlus;
                        const displayName = p.name && p.name.trim()
                          ? p.name
                          : isAdult ? 'Adulto sem nome' : `${labelType} ${p.fallbackIndex || (i + 1)}`;
                        // Visual warning if missing critical info
                        const missingAge = !isAdult && (p.age === null || p.age === undefined);
                        const missingName = !p.name || !p.name.trim();
                        return (
                          <div key={i} className="flex items-center gap-2.5 group" data-testid={`lista-person-${invite.id}-${i}`}>
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
                            <Icon size={13} className="text-zinc-400 flex-shrink-0" />
                            <span className={`text-[13px] truncate ${missingName ? 'text-zinc-400 italic' : 'text-zinc-800'}`}>
                              {displayName}
                              {!isAdult && p.age !== null && p.age !== undefined && (
                                <span className="text-zinc-400 font-normal"> — {p.age} {p.age === 1 ? 'ano' : 'anos'}</span>
                              )}
                            </span>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${colorBg}`}>
                              {labelType}{ageInfo}
                            </span>
                            {(missingAge || (missingName && !isAdult)) && (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200" title="Complete os dados">
                                <AlertCircle size={9} className="inline mr-0.5" />
                                {missingAge && missingName ? 'Nome e idade pendentes' : missingAge ? 'Sem idade' : 'Sem nome'}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Mesas View */}
      {viewMode === "mesas" && (
        <TablesSection 
          wedding={wedding} 
          invites={invites} 
          confirmedCount={stats?.confirmed?.people || 0}
        />
      )}

      {/* Invite Modal — premium signup-like style */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); resetForm(); }} title={editInvite ? "Editar convidado" : "Adicionar convidado"} fullScreen>
        <form onSubmit={handleSubmit} style={{fontFamily:"'Poppins', sans-serif"}}>
          {/* Hero header tipo "Crie sua conta" — só aparece no desktop, no mobile o header do modal já mostra título */}
          <div className="hidden md:block px-1 pb-4 mb-1">
            <h2 className="text-[28px] sm:text-[34px] font-semibold tracking-tight text-zinc-900 leading-tight">
              {editInvite ? "Editar convidado" : "Adicionar convidado"}
            </h2>
            <p className="text-[13.5px] text-zinc-500 mt-1.5">
              {editInvite ? "Atualize os dados do convite" : "Preencha os dados para criar o convite"}
            </p>
          </div>

          {/* Nome do Convite — input premium grande */}
          <div className="mb-5">
            <label className="block text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-2">Nome do convite</label>
            <div className="relative">
              <Users size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" strokeWidth={1.6} />
              <input
                type="text"
                placeholder="Ex: Família Silva, João +1, Empresa XPTO"
                value={form.name}
                onChange={e => setForm({...form, name: e.target.value})}
                required
                className="w-full pl-11 pr-4 py-3.5 bg-white border border-zinc-200 rounded-xl text-[14.5px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300 transition-all"
                data-testid="invite-name-input"
              />
            </div>
          </div>

          {/* Festa exclusiva para crianças — DESTAQUE NO TOPO */}
          <div className="mb-5 rounded-xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-white p-4" data-testid="kids-pickup-block">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0"><Shield size={18} className="text-violet-600" /></div>
                <div>
                  <p className="text-[14px] font-bold text-zinc-900">Festa exclusiva para crianças</p>
                  <p className="text-[11.5px] text-zinc-500">Gera QR Code de retirada e envia ao(s) responsável(is).</p>
                </div>
              </div>
              <button type="button" onClick={() => setForm({ ...form, kids_pickup: !form.kids_pickup })} data-testid="kids-pickup-toggle"
                className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${form.kids_pickup ? "bg-violet-600" : "bg-zinc-300"}`}>
                <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${form.kids_pickup ? "translate-x-5" : ""}`} />
              </button>
            </div>
            {form.kids_pickup && (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-1.5">Responsável 1 · Nome</label>
                    <input type="text" placeholder="Nome do responsável" value={form.responsible1_name} onChange={e => setForm({ ...form, responsible1_name: e.target.value })}
                      className="w-full px-3.5 py-3 bg-white border border-zinc-200 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-violet-400/40" data-testid="resp1-name" />
                  </div>
                  <div>
                    <label className="block text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-1.5">Responsável 1 · WhatsApp</label>
                    <input type="text" placeholder="(11) 98765-4321" value={form.responsible1_phone} onChange={e => {
                        let v = e.target.value.replace(/\D/g, '').slice(0, 11);
                        if (v.length > 6) v = `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}`;
                        else if (v.length > 2) v = `(${v.slice(0,2)}) ${v.slice(2)}`;
                        else if (v.length > 0) v = `(${v}`;
                        setForm({ ...form, responsible1_phone: v });
                      }}
                      className="w-full px-3.5 py-3 bg-white border border-zinc-200 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-violet-400/40" data-testid="resp1-phone" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-1.5">Responsável 2 · Nome <span className="text-zinc-300 normal-case">(opcional)</span></label>
                    <input type="text" placeholder="Nome do responsável" value={form.responsible2_name} onChange={e => setForm({ ...form, responsible2_name: e.target.value })}
                      className="w-full px-3.5 py-3 bg-white border border-zinc-200 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-violet-400/40" data-testid="resp2-name" />
                  </div>
                  <div>
                    <label className="block text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-1.5">Responsável 2 · WhatsApp <span className="text-zinc-300 normal-case">(opcional)</span></label>
                    <input type="text" placeholder="(11) 98765-4321" value={form.responsible2_phone} onChange={e => {
                        let v = e.target.value.replace(/\D/g, '').slice(0, 11);
                        if (v.length > 6) v = `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}`;
                        else if (v.length > 2) v = `(${v.slice(0,2)}) ${v.slice(2)}`;
                        else if (v.length > 0) v = `(${v}`;
                        setForm({ ...form, responsible2_phone: v });
                      }}
                      className="w-full px-3.5 py-3 bg-white border border-zinc-200 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-violet-400/40" data-testid="resp2-phone" />
                  </div>
                </div>
                <p className="text-[11.5px] text-violet-700/80">O QR Code de retirada será enviado por WhatsApp ao Responsável 1{form.responsible2_phone ? " e ao Responsável 2" : ""}.</p>
              </div>
            )}
          </div>

          {/* Convidados Detalhados — Sempre ativo */}
          <div className="space-y-3" data-testid="detailed-mode-section">
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-700 text-xs font-medium border border-zinc-200" data-testid="detailed-total-badge">
                <Users size={12} /> {detailedTotal} total
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zinc-50 text-zinc-600 text-xs">
                <User size={12} /> {detailedAdults.length} adultos
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zinc-50 text-zinc-600 text-xs">
                <UserPlus size={12} /> {detailedChildren.length} crianças
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zinc-50 text-zinc-600 text-xs">
                <Baby size={12} /> {detailedBabies.length} bebês
              </span>
            </div>

            {/* Adultos */}
            <div className="border border-zinc-200 rounded-lg p-3" data-testid="detailed-adults-section">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                  <User size={13} className="text-zinc-400" /> Adultos <span className="font-normal lowercase text-zinc-400">(11+)</span>
                </h4>
                <button type="button" onClick={() => addIndividual('adult')} className="text-xs text-zinc-500 hover:text-zinc-900 font-medium flex items-center gap-0.5 transition-colors" data-testid="add-adult-btn">
                  <Plus size={14} /> Adicionar
                </button>
              </div>
              {detailedAdults.length === 0 && <p className="text-xs text-zinc-400 py-2">Nenhum adulto adicionado</p>}
              <div className="space-y-1.5">
                {detailedAdults.map((ind) => (
                  <div key={ind._idx} className="flex items-center gap-2">
                    <input type="text" placeholder="Nome" value={ind.name} onChange={e => updateIndividual(ind._idx, 'name', e.target.value)}
                      className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300 transition-all" data-testid={`adult-name-${ind._idx}`} />
                    <button type="button" onClick={() => removeIndividual(ind._idx)} className="p-1.5 text-zinc-300 hover:text-red-500 rounded-lg transition-colors" data-testid={`remove-adult-${ind._idx}`}><X size={15} /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Crianças */}
            <div className="border border-zinc-200 rounded-lg p-3" data-testid="detailed-children-section">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                  <UserPlus size={13} className="text-zinc-400" /> Crianças <span className="font-normal lowercase text-zinc-400">(3–10 anos)</span>
                </h4>
                <button type="button" onClick={() => addIndividual('child')} className="text-xs text-zinc-500 hover:text-zinc-900 font-medium flex items-center gap-0.5 transition-colors" data-testid="add-child-btn">
                  <Plus size={14} /> Adicionar
                </button>
              </div>
              {detailedChildren.length === 0 && <p className="text-xs text-zinc-400 py-2">Nenhuma criança adicionada</p>}
              <div className="space-y-1.5">
                {detailedChildren.map((ind) => {
                  const ageNum = ind.age !== '' && ind.age !== null && ind.age !== undefined ? Number(ind.age) : null;
                  const willBecomeBaby = ageNum !== null && ageNum <= 2;
                  const willBecomeAdult = ageNum !== null && ageNum > 10;
                  return (
                    <div key={ind._idx}>
                      <div className="flex items-center gap-2">
                        <input type="text" placeholder="Nome *" value={ind.name} onChange={e => updateIndividual(ind._idx, 'name', e.target.value)}
                          required
                          className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300 transition-all" data-testid={`child-name-${ind._idx}`} />
                        <input type="number" placeholder="Idade *" min="0" max="17" value={ind.age || ''} onChange={e => updateIndividual(ind._idx, 'age', e.target.value)}
                          required
                          className="w-20 px-2 py-2 bg-white border border-zinc-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-zinc-900/10 transition-all" data-testid={`child-age-${ind._idx}`} />
                        <button type="button" onClick={() => removeIndividual(ind._idx)} className="p-1.5 text-zinc-300 hover:text-red-500 rounded-lg transition-colors" data-testid={`remove-child-${ind._idx}`}><X size={15} /></button>
                      </div>
                      {willBecomeBaby && (
                        <p className="text-[10.5px] text-pink-600 mt-1 ml-1 flex items-center gap-1"><Baby size={10} /> Será classificado como bebê (0–2 anos) ao salvar</p>
                      )}
                      {willBecomeAdult && (
                        <p className="text-[10.5px] text-blue-600 mt-1 ml-1 flex items-center gap-1"><User size={10} /> Será classificado como adulto (11+ anos) ao salvar</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bebês */}
            <div className="border border-zinc-200 rounded-lg p-3" data-testid="detailed-babies-section">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Baby size={13} className="text-zinc-400" /> Bebês <span className="font-normal lowercase text-zinc-400">(0–2 anos)</span>
                </h4>
                <button type="button" onClick={() => addIndividual('baby')} className="text-xs text-zinc-500 hover:text-zinc-900 font-medium flex items-center gap-0.5 transition-colors" data-testid="add-baby-btn">
                  <Plus size={14} /> Adicionar
                </button>
              </div>
              {detailedBabies.length === 0 && <p className="text-xs text-zinc-400 py-2">Nenhum bebê adicionado</p>}
              <div className="space-y-1.5">
                {detailedBabies.map((ind) => {
                  const ageNum = ind.age !== '' && ind.age !== null && ind.age !== undefined ? Number(ind.age) : null;
                  const unit = ind.age_unit || 'months';
                  const willBecomeChild = unit === 'years' && ageNum !== null && ageNum > 2;
                  return (
                    <div key={ind._idx}>
                      <div className="flex items-center gap-2">
                        <input type="text" placeholder="Nome *" value={ind.name} onChange={e => updateIndividual(ind._idx, 'name', e.target.value)}
                          required
                          className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300 transition-all" data-testid={`baby-name-${ind._idx}`} />
                        <input type="number" placeholder="Idade *" min="0" max={unit === 'months' ? 24 : 2}
                          value={ind.age === 0 ? 0 : (ind.age || '')}
                          onChange={e => updateIndividual(ind._idx, 'age', e.target.value)}
                          required
                          className="w-16 px-2 py-2 bg-white border border-zinc-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-zinc-900/10 transition-all" data-testid={`baby-age-${ind._idx}`} />
                        <select value={unit} onChange={e => updateIndividual(ind._idx, 'age_unit', e.target.value)}
                          className="px-2 py-2 bg-white border border-zinc-200 rounded-lg text-xs text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 transition-all" data-testid={`baby-age-unit-${ind._idx}`}>
                          <option value="months">meses</option>
                          <option value="years">anos</option>
                        </select>
                        <button type="button" onClick={() => removeIndividual(ind._idx)} className="p-1.5 text-zinc-300 hover:text-red-500 rounded-lg transition-colors" data-testid={`remove-baby-${ind._idx}`}><X size={15} /></button>
                      </div>
                      {willBecomeChild && (
                        <p className="text-[10.5px] text-emerald-600 mt-1 ml-1 flex items-center gap-1"><UserPlus size={10} /> Será classificado como criança (3–10 anos) ao salvar</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Contato */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-2">WhatsApp</label>
              <div className="relative">
                <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" strokeWidth={1.6} />
                <span className="absolute left-11 top-1/2 -translate-y-1/2 text-[12px] text-zinc-400 font-medium select-none">+55</span>
                <input type="text" placeholder="(11) 98765-4321" value={form.contact_phone}
                  onChange={e => {
                    let v = e.target.value.replace(/\D/g, '').slice(0, 11);
                    if (v.length > 6) v = `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}`;
                    else if (v.length > 2) v = `(${v.slice(0,2)}) ${v.slice(2)}`;
                    else if (v.length > 0) v = `(${v}`;
                    setForm({...form, contact_phone: v});
                  }}
                  className="w-full pl-[68px] pr-4 py-3.5 bg-white border border-zinc-200 rounded-xl text-[14.5px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300 transition-all" data-testid="invite-phone-input" />
              </div>
            </div>
            <div>
              <label className="block text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-2">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" strokeWidth={1.6} />
                <input type="email" placeholder="email@exemplo.com" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})}
                  className="w-full pl-11 pr-4 py-3.5 bg-white border border-zinc-200 rounded-xl text-[14.5px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300 transition-all" data-testid="invite-email-input" />
              </div>
            </div>
          </div>

          {/* Alerta telefone vazio */}
          {!form.contact_phone && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50/60 border border-amber-200/40" data-testid="phone-warning">
              <AlertCircle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-600 leading-relaxed">Sem telefone informado. Nao recebera convites e avisos automaticos.</p>
            </div>
          )}

          {/* Mesa / Setor — READ-ONLY (gerenciado em "Organizar Mesas") */}
          <div>
            <label className="block text-xs font-medium text-zinc-500 uppercase tracking-wider mb-1.5">Mesa / Setor</label>
            <div className="flex items-center gap-2 px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-lg" data-testid="invite-table-readonly">
              {form.table_id && tables.find(t => t.id === form.table_id) ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0" />
                  <span className="text-sm text-zinc-900 font-medium flex-1 truncate">{tables.find(t => t.id === form.table_id)?.name}</span>
                  <Lock size={12} className="text-zinc-300" />
                </>
              ) : (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-zinc-300 flex-shrink-0" />
                  <span className="text-sm text-zinc-400 italic flex-1">Sem mesa definida</span>
                  <Lock size={12} className="text-zinc-300" />
                </>
              )}
            </div>
            <p className="text-xs text-zinc-400 mt-1.5 flex items-center gap-1">
              Atribua mesa em <button type="button" onClick={() => { setShowModal(false); resetForm(); navigate('/organizar-mesas'); }} className="text-zinc-700 hover:text-zinc-900 underline underline-offset-2 font-medium">Organizar Mesas</button> (após confirmação RSVP).
            </p>
          </div>

          {/* Status */}
          <div className="mb-5">
            <label className="block text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-2">Status</label>
            <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
              className="w-full px-4 py-3.5 bg-white border border-zinc-200 rounded-xl text-[14.5px] text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300 transition-all" data-testid="invite-status-select">
              <option value="a_confirmar">Aguardando resposta</option>
              <option value="confirmado">Confirmado</option>
              <option value="nao_ira">Não irá</option>
            </select>
          </div>
          
          {/* Observações */}
          <div className="mb-6">
            <label className="block text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-2">Observações</label>
            <input type="text" placeholder="Ex: vegetariano, cadeirante..." value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
              className="w-full px-4 py-3.5 bg-white border border-zinc-200 rounded-xl text-[14.5px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300 transition-all" data-testid="invite-notes-input" />
          </div>
          
          {/* CTA — sticky bottom dentro do modal full-screen, respeita safe-area-bottom */}
          <div
            className="flex flex-col gap-2 pt-3 sticky bottom-0 bg-white -mx-1 px-1 mt-4"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 8px)" }}
          >
            <button
              type="submit"
              onClick={() => { try { navigator.vibrate?.(10); } catch {} }}
              className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full text-white text-[14px] font-semibold tracking-tight active:scale-[0.98] transition-transform"
              style={{
                backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)",
                boxShadow: "0 12px 32px -10px rgba(107,61,246,0.45)",
              }}
              data-testid="save-invite-btn"
            >
              {editInvite ? "Salvar alterações" : "Salvar convidado"}
              <ArrowRight size={15} />
            </button>
            <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="w-full h-11 inline-flex items-center justify-center text-[13.5px] font-medium text-zinc-500 active:text-zinc-900 transition-colors" data-testid="cancel-invite-btn">Cancelar</button>
          </div>
        </form>
      </Modal>

      {/* QR Code Modal */}
      <Modal isOpen={showQRModal} onClose={() => { setShowQRModal(false); setQRCodeData(null); }} title="QR Code - Convite">
        <div className="flex flex-col items-center space-y-4">
          {loadingQR ? (
            <div className="p-8">
              <Loader2 className="animate-spin text-blue-500" size={40} />
              <p className="text-slate-500 mt-2">Gerando QR Code...</p>
            </div>
          ) : qrCodeData ? (
            <>
              <div className="bg-white p-4 rounded-xl shadow-lg border">
                <img src={qrCodeData.qrcode} alt="QR Code" className="w-64 h-64" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-slate-800">{qrCodeData.invite_name}</p>
                <p className="text-sm text-slate-500 mt-1">Token: {qrCodeData.token}</p>
              </div>
              <div className="w-full space-y-2">
                <div className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                  <span className="text-xs text-slate-600 truncate flex-1 mr-2">{qrCodeData.url}</span>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(qrCodeData.url);
                      toast.success("Link copiado!");
                    }}
                    className="p-2 hover:bg-slate-200 rounded text-slate-600"
                    data-testid="copy-qr-link"
                  >
                    <Copy size={16} />
                  </button>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="secondary" 
                    className="flex-1" 
                    onClick={handleDownloadQR}
                    data-testid="download-qrcode"
                  >
                    <Download size={16} className="mr-2" />
                    Baixar QR Code
                  </Button>
                  <Button 
                    variant="primary" 
                    className="flex-1" 
                    onClick={() => {
                      navigator.clipboard.writeText(qrCodeData.url);
                      toast.success("Link copiado!");
                    }}
                    data-testid="copy-link-button"
                  >
                    <Link2 size={16} className="mr-2" />
                    Copiar Link
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-400 text-center">
                Convidados podem escanear este QR Code para confirmar presença ou fazer check-in no evento.
              </p>
            </>
          ) : null}
        </div>
      </Modal>


      {/* Premium WhatsApp Send Flow */}
      <WhatsAppSendFlow
        open={showWaSendFlow}
        onClose={() => setShowWaSendFlow(false)}
        selectedInvites={selectedInvites}
        wedding={wedding}
        onSent={() => { setSelectedInvites([]); fetchInvites(); }}
      />

      {/* Modal QR de saída (check-out) — visualização rápida */}
      <Modal isOpen={checkoutQrModal.open} onClose={() => setCheckoutQrModal({ open: false, inviteName: "", loading: false, items: [] })} title={`QR de saída · ${checkoutQrModal.inviteName}`}>
        <div className="space-y-4" data-testid="checkout-qr-modal">
          {checkoutQrModal.loading ? (
            <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-violet-500" size={26} /></div>
          ) : checkoutQrModal.items.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-6">Nenhuma criança neste convite.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {checkoutQrModal.items.map((it, i) => (
                <div key={i} className="rounded-xl border border-zinc-200 p-3 text-center" data-testid={`checkout-qr-item-${i}`}>
                  <p className="text-sm font-semibold text-zinc-800 mb-2 flex items-center justify-center gap-1"><Baby size={14} className="text-violet-500" />{it.name}</p>
                  <img src={it.url} alt={`QR saída ${it.name}`} className="w-40 h-40 mx-auto rounded-lg border border-zinc-100" />
                  <p className={`text-[11px] mt-2 font-semibold ${it.status === 'used' ? 'text-green-600' : 'text-blue-600'}`}>{it.status === 'used' ? 'Retirada realizada' : 'QR emitido'}</p>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11.5px] text-zinc-400 text-center">QR único do convite, de uso único, apresentado pela equipe na retirada das crianças.</p>
        </div>
      </Modal>


      {/* Modal de Envio de Alerta/Aviso */}
      <Modal isOpen={showAlertModal} onClose={() => { setShowAlertModal(false); setAlertMessage(''); }} title="Enviar Aviso via WhatsApp">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Envie um aviso personalizado via WhatsApp para <strong>{selectedInvites.length}</strong> convidado(s).
          </p>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Mensagem do aviso</label>
            <textarea
              value={alertMessage}
              onChange={(e) => setAlertMessage(e.target.value)}
              placeholder="Ex: Confirmem presença até dia 10/12. Informamos que o local foi alterado para..."
              className="w-full p-3 border border-slate-200 rounded-lg text-sm min-h-[120px] focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              maxLength={500}
            />
            <p className="text-xs text-slate-400 mt-1 text-right">{alertMessage.length}/500 caracteres</p>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => { setShowAlertModal(false); setAlertMessage(''); }}>
              Cancelar
            </Button>
            <Button 
              onClick={() => handleSendAlert()}
              disabled={sendingWhatsApp || alertMessage.trim().length < 5}
              data-testid="btn-confirm-alert"
            >
              {sendingWhatsApp ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
              Enviar Aviso
            </Button>
          </div>
        </div>
      </Modal>

      {/* NOVO: Modal de ALERTA RSVP */}
      <Modal 
        isOpen={showRsvpAlertModal} 
        onClose={() => { setShowRsvpAlertModal(false); setRsvpAlertPreview(null); }} 
        title="ALERTA - Lembrete de Confirmação"
      >
        <div className="space-y-4" data-testid="modal-alerta-rsvp">
          {/* Cabeçalho informativo */}
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <Bell className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-medium text-amber-800">Este alerta será enviado automaticamente para:</p>
                <p className="text-sm text-amber-700 mt-1">
                  Todos os convidados com status <strong>"Aguardando"</strong> que possuem telefone cadastrado.
                </p>
              </div>
            </div>
          </div>

          {/* Estatísticas */}
          {rsvpAlertPreview && (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{rsvpAlertPreview.eligible_count}</p>
                <p className="text-xs text-slate-500">Receberão o lembrete</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-600">{rsvpAlertPreview.without_phone}</p>
                <p className="text-xs text-slate-500">Sem contato</p>
              </div>
            </div>
          )}

          {/* Prévia da mensagem (NÃO EDITÁVEL) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Mensagem padrão <span className="text-slate-400 font-normal">(não editável)</span>
            </label>
            <div className="bg-slate-100 p-4 rounded-lg text-sm text-slate-700 whitespace-pre-line border-2 border-slate-200">
              {rsvpAlertPreview?.message_preview || 'Carregando...'}
            </div>
          </div>

          {/* Nenhum convidado elegível */}
          {rsvpAlertPreview && rsvpAlertPreview.eligible_count === 0 && (
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600">
                Não há convidados com status "Aguardando" e telefone cadastrado para receber o alerta.
              </p>
            </div>
          )}

          {/* Botões de ação */}
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="secondary" onClick={() => { setShowRsvpAlertModal(false); setRsvpAlertPreview(null); }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSendRsvpAlert}
              disabled={
                sendingWhatsApp || 
                !rsvpAlertPreview || 
                rsvpAlertPreview.eligible_count === 0
              }
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="btn-confirmar-alerta-rsvp"
            >
              {sendingWhatsApp ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : (
                <Send size={16} className="mr-2" />
              )}
              Enviar Lembrete
            </Button>
          </div>
        </div>
      </Modal>

      {/* MODAL DE ELEGIBILIDADE — preview antes do envio de QR / Lembrete */}
      <Modal
        isOpen={eligibilityModal.open}
        onClose={() => setEligibilityModal({ open: false, type: null, stats: null, ids: [] })}
        title={eligibilityModal.type === 'qrcode' ? 'Enviar QR Code de Acesso' : 'Enviar Lembrete RSVP'}
      >
        <div className="space-y-5" data-testid="eligibility-modal">
          {/* Header com regra clara */}
          <div className="rounded-xl p-4" style={{ backgroundColor: '#FAFAFA', border: '1px solid #E4E4E7' }}>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-brand-deep flex items-center justify-center flex-shrink-0">
                {eligibilityModal.type === 'qrcode' 
                  ? <QrCode className="text-white" size={15} />
                  : <Bell className="text-white" size={15} />
                }
              </div>
              <p className="text-[13px] text-zinc-700 leading-relaxed">
                {eligibilityModal.type === 'qrcode'
                  ? <>Somente convidados com <span className="font-semibold text-zinc-900">presença confirmada</span> receberão o QR Code de acesso.</>
                  : <>Os lembretes serão enviados apenas para convidados com <span className="font-semibold text-zinc-900">confirmação pendente</span>.</>
                }
              </p>
            </div>
          </div>

          {/* Estatísticas elegantes */}
          {eligibilityModal.stats && (
            <div className="space-y-2">
              {/* Elegíveis - destaque */}
              <div className="flex items-center justify-between rounded-xl p-4 bg-emerald-50/60 border border-emerald-200/50">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <div>
                    <p className="text-[12.5px] font-semibold text-zinc-900">
                      {eligibilityModal.type === 'qrcode' ? 'Convidados elegíveis' : 'Receberão o lembrete'}
                    </p>
                    <p className="text-[11px] text-zinc-500">
                      {eligibilityModal.type === 'qrcode' ? 'Confirmados com telefone' : 'Pendentes com telefone'}
                    </p>
                  </div>
                </div>
                <p className="text-2xl font-semibold tracking-tight text-emerald-700">{eligibilityModal.stats.eligible_count}</p>
              </div>

              {/* Ignorados */}
              {eligibilityModal.type === 'qrcode' && eligibilityModal.stats.skipped_pending > 0 && (
                <div className="flex items-center justify-between rounded-xl p-3.5 border border-zinc-200/60">
                  <div className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    <div>
                      <p className="text-[12.5px] text-zinc-700">Pendentes ignorados</p>
                      <p className="text-[11px] text-zinc-400">Ainda não confirmaram</p>
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-zinc-500">{eligibilityModal.stats.skipped_pending}</p>
                </div>
              )}

              {eligibilityModal.type === 'reminder' && eligibilityModal.stats.skipped_confirmed > 0 && (
                <div className="flex items-center justify-between rounded-xl p-3.5 border border-zinc-200/60">
                  <div className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <div>
                      <p className="text-[12.5px] text-zinc-700">Confirmados ignorados</p>
                      <p className="text-[11px] text-zinc-400">Já confirmaram presença</p>
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-zinc-500">{eligibilityModal.stats.skipped_confirmed}</p>
                </div>
              )}

              {eligibilityModal.stats.skipped_declined > 0 && (
                <div className="flex items-center justify-between rounded-xl p-3.5 border border-zinc-200/60">
                  <div className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <div>
                      <p className="text-[12.5px] text-zinc-700">Recusados ignorados</p>
                      <p className="text-[11px] text-zinc-400">Não comparecerão</p>
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-zinc-500">{eligibilityModal.stats.skipped_declined}</p>
                </div>
              )}

              {eligibilityModal.stats.skipped_no_phone > 0 && (
                <div className="flex items-center justify-between rounded-xl p-3.5 border border-zinc-200/60">
                  <div className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-zinc-300" />
                    <div>
                      <p className="text-[12.5px] text-zinc-700">Sem telefone</p>
                      <p className="text-[11px] text-zinc-400">Não podem ser contatados</p>
                    </div>
                  </div>
                  <p className="text-lg font-semibold text-zinc-500">{eligibilityModal.stats.skipped_no_phone}</p>
                </div>
              )}
            </div>
          )}

          {/* Empty state quando ninguém é elegível */}
          {eligibilityModal.stats?.eligible_count === 0 && (
            <div className="rounded-xl p-4 bg-amber-50/60 border border-amber-200/50">
              <p className="text-[13px] text-amber-800">
                {eligibilityModal.type === 'qrcode'
                  ? 'Nenhum convidado da seleção tem presença confirmada. Envie convites RSVP primeiro.'
                  : 'Nenhum convidado da seleção está pendente. Todos já responderam.'
                }
              </p>
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={() => setEligibilityModal({ open: false, type: null, stats: null, ids: [] })}
              className="px-5 py-2.5 rounded-xl border border-zinc-200 text-[13px] font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
              data-testid="eligibility-cancel"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => handleSendWhatsApp(eligibilityModal.type, eligibilityModal.ids, true)}
              disabled={sendingWhatsApp || !eligibilityModal.stats?.eligible_count}
              className="px-5 py-2.5 rounded-xl bg-brand-deep text-white text-[13px] font-medium hover:bg-brand transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="eligibility-confirm"
            >
              {sendingWhatsApp ? <><Loader2 className="animate-spin" size={14} /> Enviando...</> : (
                <><Send size={14} /> Enviar para {eligibilityModal.stats?.eligible_count || 0} convidado(s)</>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* TIMELINE MODAL — histórico de eventos do convidado */}
      <Modal
        isOpen={timelineModal.open}
        onClose={() => setTimelineModal({ open: false, invite: null, events: [], loading: false })}
        title={timelineModal.invite ? `Histórico — ${timelineModal.invite.name}` : 'Histórico'}
      >
        <div className="space-y-1" data-testid="timeline-modal">
          {timelineModal.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-zinc-400" size={20} />
            </div>
          ) : timelineModal.events.length === 0 ? (
            <div className="text-center py-10">
              <Clock className="mx-auto text-zinc-300 mb-3" size={32} />
              <p className="text-[13px] text-zinc-500">Nenhum evento registrado ainda.</p>
            </div>
          ) : (
            <div className="relative pl-7">
              {/* Linha vertical */}
              <div className="absolute left-[10px] top-2 bottom-2 w-px bg-zinc-200" />
              {timelineModal.events.map((ev, idx) => {
                const iconMap = {
                  'user-plus': UserPlus, 'calendar': Calendar, 'send': Send, 'check': Check,
                  'x': X, 'bell': Bell, 'qr-code': QrCode, 'user-check': UserCheck,
                  'message-circle': MessageCircle, 'alert-circle': AlertCircle,
                };
                const dotColors = {
                  rsvp_confirmed: 'bg-emerald-500', rsvp_declined: 'bg-red-500',
                  qrcode: 'bg-purple-500', reminder: 'bg-blue-500',
                  invitation: 'bg-zinc-700', save_the_date: 'bg-zinc-500',
                  checkin: 'bg-emerald-600', auto_reply: 'bg-blue-400',
                  alert: 'bg-amber-500', created: 'bg-zinc-300',
                };
                const Ic = iconMap[ev.icon] || Clock;
                const dotColor = dotColors[ev.type] || 'bg-zinc-400';
                const date = ev.at ? new Date(ev.at) : null;
                return (
                  <div key={idx} className="relative pb-4 last:pb-0">
                    <div className={`absolute -left-[24px] top-1 w-5 h-5 rounded-full flex items-center justify-center ${dotColor}`}>
                      <Ic size={10} className="text-white" strokeWidth={2.5} />
                    </div>
                    <div className="pt-0.5">
                      <p className="text-[13px] font-medium text-zinc-900">{ev.label}</p>
                      {date && (
                        <p className="text-[11.5px] text-zinc-400 mt-0.5">
                          {date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })} · {date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>

      {/* BOTTOM SHEET DE DETALHES DO CONVIDADO — Sprint 2 UX (mobile native) */}
      {(() => {
        const inv = guestSheet.invite;
        if (!inv) return null;
        const isConfirmed = inv.status === 'confirmado' || inv.status === 'confirmed';
        const isDeclined = ['nao_ira','recusado','declined'].includes(inv.status);
        const peopleCount = inv.total_guests || inv.total_people || 1;
        const tableName = inv.table_id ? getTableName(inv.table_id) : null;
        const palettes = [
          'bg-violet-100 text-violet-700',
          'bg-emerald-100 text-emerald-700',
          'bg-sky-100 text-sky-700',
          'bg-amber-100 text-amber-700',
          'bg-rose-100 text-rose-700',
          'bg-indigo-100 text-indigo-700',
          'bg-teal-100 text-teal-700',
          'bg-fuchsia-100 text-fuchsia-700',
        ];
        const hash = [...(inv.name || '?')].reduce((a, c) => a + c.charCodeAt(0), 0);
        const avatarBg = palettes[hash % palettes.length];
        const statusOptions = [
          { key: 'confirmado', label: 'Confirmado', dot: 'bg-emerald-500', active: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
          { key: 'a_confirmar', label: 'Pendente', dot: 'bg-amber-500', active: 'bg-amber-50 text-amber-700 border-amber-200' },
          { key: 'nao_ira', label: 'Recusado', dot: 'bg-rose-500', active: 'bg-rose-50 text-rose-700 border-rose-200' },
        ];
        const currentStatusKey = isConfirmed ? 'confirmado' : isDeclined ? 'nao_ira' : 'a_confirmar';
        const updateStatus = async (newKey) => {
          if (newKey === currentStatusKey) return;
          try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/weddings/${wedding.id}/invites/${inv.id}`, { status: newKey }, { headers: { Authorization: `Bearer ${token}` } });
            try { navigator.vibrate?.(8); } catch {}
            toast.success('Status atualizado');
            fetchInvites();
            setGuestSheet(prev => prev.invite ? { ...prev, invite: { ...prev.invite, status: newKey } } : prev);
          } catch (err) {
            toast.error(getErrorMessage(err, 'Erro ao atualizar status'));
          }
        };
        const copyLink = () => {
          if (!inv.rsvp_token) { toast.error('Convite sem link RSVP'); return; }
          if (!requireNamedParticipants(inv)) return;
          navigator.clipboard.writeText(`${window.location.origin}/confirmar/${inv.rsvp_token}`);
          toast.success('Link RSVP copiado');
          try { navigator.vibrate?.(8); } catch {}
        };
        return (
          <Vaul.Root open={guestSheet.open} onOpenChange={(o) => { if (!o) closeGuestSheet(); }} dismissible direction={isMobile ? "bottom" : "right"}>
            <Vaul.Portal>
              <Vaul.Overlay className="fixed inset-0 z-[10010] bg-black/40 backdrop-blur-[3px]" />
              <Vaul.Content
                className={`fixed z-[10011] flex flex-col bg-white outline-none shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.18)] ${
                  isMobile
                    ? "inset-x-0 bottom-0 rounded-t-3xl"
                    : "inset-y-0 right-0 w-full max-w-[440px] rounded-l-3xl border-l border-zinc-100"
                }`}
                style={isMobile ? { height: '75dvh' } : { height: '100dvh' }}
                data-testid="guest-detail-sheet"
              >
                <Vaul.Title className="sr-only">{inv.name}</Vaul.Title>
                <Vaul.Description className="sr-only">Detalhes do convidado {inv.name}</Vaul.Description>
                {/* Drag handle só no mobile */}
                {isMobile && <div className="mx-auto mt-2.5 mb-1 h-1.5 w-12 rounded-full bg-zinc-200 shrink-0" />}
                {/* Header */}
                <div className="px-5 pt-3 pb-4 flex items-center gap-3.5 border-b border-zinc-100">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-[20px] tracking-tight ${avatarBg}`}>
                    {(inv.name || '?').trim().charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[18px] font-semibold text-zinc-900 leading-tight tracking-tight truncate">{inv.name}</h3>
                    <p className="text-[13px] text-zinc-500 mt-0.5">
                      <span className="tabular-nums">{peopleCount}</span> {peopleCount === 1 ? 'convidado' : 'convidados'}
                      {tableName && <><span className="mx-1.5 text-zinc-300">·</span><span>{tableName}</span></>}
                    </p>
                  </div>
                  <button
                    onClick={closeGuestSheet}
                    className="w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 active:bg-zinc-300 flex items-center justify-center text-zinc-600 transition-colors"
                    aria-label="Fechar"
                    data-testid="guest-sheet-close"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Conteúdo rolável */}
                <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 space-y-6"
                     style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
                  {/* Status chips */}
                  <div>
                    <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2.5">Status RSVP</p>
                    <div className="flex items-center gap-2">
                      {statusOptions.map(opt => {
                        const isActive = opt.key === currentStatusKey;
                        return (
                          <button
                            key={opt.key}
                            onClick={() => updateStatus(opt.key)}
                            className={`tap-press flex items-center gap-2 px-3.5 py-2 rounded-full border text-[13px] font-medium transition-all ${isActive ? opt.active : 'bg-white text-zinc-600 border-zinc-200'}`}
                            data-testid={`guest-sheet-status-${opt.key}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${opt.dot}`} />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Pessoas — lista detalhada por convidado individual */}
                  {(() => {
                    // 1) Preferir individuals nomeados
                    let individuals = (inv.individuals && inv.individuals.length > 0)
                      ? inv.individuals.map(i => ({ name: i.name, type: i.type || 'adult', rsvp: i.rsvp || 'pending' }))
                      : [];
                    // 2) Fallback para arrays adults[]/children[] (modelo legado)
                    if (individuals.length === 0) {
                      individuals = [
                        ...((inv.adults || []).map(a => ({ name: a.name, type: 'adult', rsvp: 'pending' }))),
                        ...((inv.children || []).map(c => ({ name: c.name, type: 'child', rsvp: 'pending' }))),
                      ];
                    }
                    // 3) Fallback final: sintetizar a partir dos contadores
                    if (individuals.length === 0) {
                      const a = inv.adults_count || 0;
                      const c = inv.children_count || 0;
                      const b = inv.babies_count || 0;
                      const total = inv.total_guests || inv.total_people || (a + c + b) || 1;
                      // Se sabemos os counts, distribuir; caso contrário, todo mundo adulto
                      if (a + c + b > 0) {
                        for (let i = 0; i < a; i++) individuals.push({ name: null, type: 'adult' });
                        for (let i = 0; i < c; i++) individuals.push({ name: null, type: 'child' });
                        for (let i = 0; i < b; i++) individuals.push({ name: null, type: 'baby' });
                      }
                      // Completar com adultos extras se total > soma dos counts
                      while (individuals.length < total) individuals.push({ name: null, type: 'adult' });
                    }
                    const adultsN = individuals.filter(i => i.type === 'adult').length;
                    const childrenN = individuals.filter(i => i.type === 'child').length;
                    const babiesN = individuals.filter(i => i.type === 'baby').length;
                    if (individuals.length === 0) return null;
                    const composition = [
                      adultsN > 0 ? `${adultsN} adulto${adultsN > 1 ? 's' : ''}` : null,
                      childrenN > 0 ? `${childrenN} criança${childrenN > 1 ? 's' : ''}` : null,
                      babiesN > 0 ? `${babiesN} bebê${babiesN > 1 ? 's' : ''}` : null,
                    ].filter(Boolean).join(' · ');
                    const typeBadge = (t) => {
                      if (t === 'child') return { label: 'Criança', cls: 'bg-amber-50 text-amber-700' };
                      if (t === 'baby') return { label: 'Bebê', cls: 'bg-pink-50 text-pink-600' };
                      return { label: 'Adulto', cls: 'bg-blue-50 text-blue-600' };
                    };
                    const rsvpBadge = (r) => {
                      if (r === 'confirmed') return { label: 'Confirmou', cls: 'bg-emerald-50 text-emerald-600', dot: 'bg-emerald-500' };
                      if (r === 'declined') return { label: 'Não vai', cls: 'bg-rose-50 text-rose-600', dot: 'bg-rose-400' };
                      return { label: 'Pendente', cls: 'bg-zinc-100 text-zinc-500', dot: 'bg-amber-400' };
                    };
                    const confirmedN = individuals.filter(i => i.rsvp === 'confirmed').length;
                    const declinedN = individuals.filter(i => i.rsvp === 'declined').length;
                    const hasRsvp = confirmedN > 0 || declinedN > 0;
                    return (
                      <div data-testid="guest-sheet-people">
                        <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2.5">Pessoas</p>
                        <div className="rounded-2xl border border-zinc-200/70 bg-white overflow-hidden">
                          <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-100 bg-zinc-50/50">
                            <div className="w-9 h-9 rounded-lg bg-white ring-1 ring-zinc-200/80 flex items-center justify-center text-zinc-500">
                              <Users size={15} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[13.5px] font-semibold text-zinc-900 truncate leading-tight">{inv.name}</p>
                              <p className="text-[11.5px] text-zinc-500 mt-0.5">{individuals.length} {individuals.length === 1 ? 'pessoa' : 'pessoas'}{composition ? ` · ${composition}` : ''}</p>
                            </div>
                            {hasRsvp && (
                              <div className="text-right shrink-0" data-testid="guest-sheet-rsvp-summary">
                                <span className="block text-[12px] font-semibold text-emerald-600 tabular-nums">{confirmedN} confirmado{confirmedN === 1 ? '' : 's'}</span>
                                {declinedN > 0 && <span className="block text-[11px] text-rose-500 tabular-nums">{declinedN} não vai{declinedN === 1 ? '' : 'em'}</span>}
                              </div>
                            )}
                          </div>
                          <ul className="divide-y divide-zinc-100">
                            {individuals.map((ind, i) => {
                              const b = typeBadge(ind.type);
                              const rb = rsvpBadge(ind.rsvp);
                              const displayName = (ind.name && ind.name.trim()) || `Convidado ${i + 1}`;
                              return (
                                <li key={i} className="flex items-center gap-3 px-4 py-2.5">
                                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${rb.dot}`} />
                                  <User size={14} className="text-zinc-400 flex-shrink-0" />
                                  <span className="text-[13.5px] text-zinc-800 truncate flex-1">{displayName}</span>
                                  <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded ${rb.cls}`}>{rb.label}</span>
                                  <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded ${b.cls}`}>{b.label}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Info contato (somente se existir) */}
                  {(inv.contact_phone || inv.contact_email) && (
                    <div>
                      <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2.5">Contato</p>
                      <div className="space-y-1.5">
                        {inv.contact_phone && (
                          <div className="flex items-center gap-2.5 text-[13.5px] text-zinc-700">
                            <Phone size={14} className="text-zinc-400" />
                            <span>{inv.contact_phone}</span>
                          </div>
                        )}
                        {inv.contact_email && (
                          <div className="flex items-center gap-2.5 text-[13.5px] text-zinc-700">
                            <Mail size={14} className="text-zinc-400" />
                            <span className="truncate">{inv.contact_email}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Ações */}
                  <div>
                    <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2.5">Ações</p>
                    <div className="space-y-2">
                      <button
                        onClick={() => { closeGuestSheet(); setTimeout(() => handleEdit(inv), 220); }}
                        className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-zinc-50 hover:bg-zinc-100 border border-zinc-100 text-left"
                        data-testid="guest-sheet-edit"
                      >
                        <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-zinc-700 ring-1 ring-zinc-200/70">
                          <Pencil size={15} />
                        </div>
                        <span className="text-[14.5px] font-medium text-zinc-900">Editar convidado</span>
                        <ChevronRight size={16} className="text-zinc-300 ml-auto" />
                      </button>

                      {inv.contact_phone && (
                        <a
                          href={getWhatsAppInviteLink(inv)}
                          target="_blank" rel="noopener noreferrer"
                          onClick={(e) => { if (!requireNamedParticipants(inv)) { e.preventDefault(); return; } closeGuestSheet(); }}
                          className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-emerald-50/70 hover:bg-emerald-50 border border-emerald-100/80 text-left"
                          data-testid="guest-sheet-whatsapp"
                        >
                          <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                            <MessageCircle size={15} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="block text-[14.5px] font-medium text-zinc-900">Abrir no WhatsApp</span>
                            <span className="block text-[11.5px] text-zinc-500 truncate">Enviar convite ou conversar</span>
                          </div>
                          <ChevronRight size={16} className="text-zinc-300 ml-auto" />
                        </a>
                      )}

                      {inv.rsvp_token && (
                        <button
                          onClick={copyLink}
                          className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-zinc-50 hover:bg-zinc-100 border border-zinc-100 text-left"
                          data-testid="guest-sheet-copy-link"
                        >
                          <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-zinc-700 ring-1 ring-zinc-200/70">
                            <Link2 size={15} />
                          </div>
                          <span className="text-[14.5px] font-medium text-zinc-900">Copiar link RSVP</span>
                          <ChevronRight size={16} className="text-zinc-300 ml-auto" />
                        </button>
                      )}

                      <button
                        onClick={() => { closeGuestSheet(); setTimeout(() => openTimelineFor(inv), 220); }}
                        className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-zinc-50 hover:bg-zinc-100 border border-zinc-100 text-left"
                        data-testid="guest-sheet-history"
                      >
                        <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-zinc-700 ring-1 ring-zinc-200/70">
                          <Clock size={15} />
                        </div>
                        <span className="text-[14.5px] font-medium text-zinc-900">Histórico</span>
                        <ChevronRight size={16} className="text-zinc-300 ml-auto" />
                      </button>

                      <button
                        onClick={() => { const id = inv.id; closeGuestSheet(); setTimeout(() => handleDelete(id), 220); }}
                        className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-rose-50/70 hover:bg-rose-50 border border-rose-100/80 text-left"
                        data-testid="guest-sheet-delete"
                      >
                        <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-rose-600 ring-1 ring-rose-200/80">
                          <Trash2 size={15} />
                        </div>
                        <span className="text-[14.5px] font-medium text-rose-600">Excluir convidado</span>
                      </button>
                    </div>
                  </div>
                </div>
              </Vaul.Content>
            </Vaul.Portal>
          </Vaul.Root>
        );
      })()}

      {/* MODAL IMPORTAR CSV — Fullscreen mobile + centrado desktop */}
      <Modal
        isOpen={showImportCsv}
        onClose={() => { setShowImportCsv(false); setCsvFile(null); setCsvResult(null); }}
        title="Importar convidados via CSV"
        fullScreen
      >
        <div className="space-y-5" data-testid="import-csv-modal">
          {/* Instruções */}
          <div className="rounded-2xl border border-zinc-200/70 bg-zinc-50/40 px-4 py-4">
            <p className="text-[13.5px] text-zinc-700 leading-relaxed">
              Importe sua lista de convidados em CSV. As colunas aceitas são:
            </p>
            <ul className="mt-2 text-[12.5px] text-zinc-600 space-y-0.5 leading-snug">
              <li>• <b>Nome</b> (obrigatório)</li>
              <li>• Adultos, Crianças, Bebês</li>
              <li>• Telefone, Email</li>
              <li>• Mesa (se já existir na sua lista de mesas)</li>
              <li>• Observações</li>
            </ul>
          </div>

          {/* Baixar modelo */}
          <button
            type="button"
            onClick={downloadCsvTemplate}
            className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-zinc-50 hover:bg-zinc-100 border border-zinc-100 text-left"
            data-testid="csv-template-download"
          >
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-zinc-700 ring-1 ring-zinc-200/70">
              <Download size={15} />
            </div>
            <div className="flex-1 min-w-0">
              <span className="block text-[14.5px] font-medium text-zinc-900">Baixar modelo CSV</span>
              <span className="block text-[11.5px] text-zinc-500">Excel/Numbers · com 3 linhas de exemplo</span>
            </div>
            <ChevronRight size={16} className="text-zinc-300" />
          </button>

          {/* Selecionar arquivo */}
          <div>
            <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2">Arquivo CSV</p>
            <input
              ref={csvFileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { const f = e.target.files?.[0]; setCsvFile(f || null); setCsvResult(null); }}
              className="hidden"
              data-testid="csv-file-input"
            />
            <button
              type="button"
              onClick={() => csvFileInputRef.current?.click()}
              className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border-2 border-dashed border-zinc-300 hover:border-zinc-900 text-zinc-700 hover:text-zinc-900 transition-colors text-left"
              data-testid="csv-file-pick"
            >
              <div className="w-9 h-9 rounded-full bg-zinc-900 text-white flex items-center justify-center flex-shrink-0">
                <Upload size={15} />
              </div>
              <div className="flex-1 min-w-0">
                {csvFile ? (
                  <>
                    <span className="block text-[14.5px] font-medium text-zinc-900 truncate">{csvFile.name}</span>
                    <span className="block text-[11.5px] text-zinc-500">{(csvFile.size/1024).toFixed(1)} KB · trocar arquivo</span>
                  </>
                ) : (
                  <>
                    <span className="block text-[14.5px] font-medium">Escolher arquivo CSV</span>
                    <span className="block text-[11.5px] text-zinc-500">Tap para selecionar do seu dispositivo</span>
                  </>
                )}
              </div>
            </button>
          </div>

          {/* Resultado */}
          {csvResult && (
            <div className="rounded-2xl border border-zinc-200/70 bg-white px-4 py-4 space-y-3" data-testid="csv-result">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${csvResult.created > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {csvResult.created > 0 ? <Check size={18} strokeWidth={2.5} /> : <X size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14.5px] font-semibold text-zinc-900">
                    {csvResult.created} convidado{csvResult.created === 1 ? '' : 's'} importado{csvResult.created === 1 ? '' : 's'}
                  </p>
                  {csvResult.errors && csvResult.errors.length > 0 && (
                    <p className="text-[12px] text-rose-600">{csvResult.errors.length} linha(s) com erro</p>
                  )}
                </div>
              </div>
              {csvResult.errors && csvResult.errors.length > 0 && (
                <ul className="text-[12px] text-rose-600 space-y-0.5 pl-2">
                  {csvResult.errors.slice(0, 5).map((er, i) => (
                    <li key={i}>· Linha {er.line}: {er.error}</li>
                  ))}
                  {csvResult.errors.length > 5 && <li className="text-zinc-400">+ {csvResult.errors.length - 5} outros</li>}
                </ul>
              )}
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => { setShowImportCsv(false); setCsvFile(null); setCsvResult(null); }} className="flex-1">
              {csvResult ? 'Fechar' : 'Cancelar'}
            </Button>
            <Button
              type="button"
              onClick={handleCsvImport}
              disabled={!csvFile || csvImporting}
              className="flex-1 bg-zinc-900"
              data-testid="csv-import-submit"
            >
              {csvImporting ? (
                <span className="inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin" />Importando...</span>
              ) : (
                <span className="inline-flex items-center gap-2"><Upload size={15} />Importar</span>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* MODAL DE ORGANIZAR MESAS — premium, mostra apenas confirmados */}
      <Modal
        isOpen={showOrganizeTablesModal}
        onClose={() => { setShowOrganizeTablesModal(false); setOpenTableSelect(null); }}
        title="Organizar Mesas"
      >
        <div className="space-y-4" data-testid="organize-tables-modal">
          {(() => {
            const confirmedInvites = invites.filter(i => i.status === 'confirmado' || i.status === 'confirmed');
            if (confirmedInvites.length === 0) {
              return (
                <div className="text-center py-12">
                  <LayoutGrid className="mx-auto text-zinc-300 mb-3" size={36} />
                  <p className="text-[14px] font-semibold text-zinc-900 mb-1">Nenhum convidado confirmado</p>
                  <p className="text-[12.5px] text-zinc-500">A organização de mesas começa após as confirmações RSVP.</p>
                </div>
              );
            }
            const tablesAssigned = new Set(confirmedInvites.filter(i => i.table_id).map(i => i.id));
            return (
              <>
                {/* Header de stats */}
                <div className="flex items-center justify-between p-3 rounded-xl border border-zinc-200/60 bg-zinc-50/50">
                  <div className="flex items-center gap-4 text-[12px]">
                    <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/><span className="text-zinc-600">Confirmados:</span><span className="font-semibold text-zinc-900">{confirmedInvites.length}</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-purple-500"/><span className="text-zinc-600">Com mesa:</span><span className="font-semibold text-zinc-900">{tablesAssigned.size}</span></div>
                    <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-zinc-300"/><span className="text-zinc-600">Sem mesa:</span><span className="font-semibold text-zinc-900">{confirmedInvites.length - tablesAssigned.size}</span></div>
                  </div>
                  <p className="text-[11px] text-zinc-400">{simpleTables.length} mesa(s) criada(s)</p>
                </div>
                
                {/* Lista de convidados confirmados */}
                <div className="rounded-xl border border-zinc-200/60 overflow-hidden divide-y divide-zinc-100 max-h-[480px] overflow-y-auto">
                  {confirmedInvites.map(invite => {
                    const adults = invite.adults_count || 0;
                    const children = invite.children_count || 0;
                    const babies = invite.babies_count || 0;
                    const total = invite.total_people || (adults + children + babies);
                    const peopleLabel = total === 1 ? '1 pessoa' : `${total} pessoas`;
                    const breakdown = [];
                    if (adults > 0) breakdown.push(`${adults} ${adults === 1 ? 'adulto' : 'adultos'}`);
                    if (children > 0) breakdown.push(`${children} ${children === 1 ? 'criança' : 'crianças'}`);
                    if (babies > 0) breakdown.push(`${babies} ${babies === 1 ? 'bebê' : 'bebês'}`);
                    const breakdownStr = breakdown.length === 1 ? breakdown[0] : breakdown.length === 2 ? breakdown.join(' e ') : breakdown.length === 3 ? `${breakdown[0]}, ${breakdown[1]} e ${breakdown[2]}` : '';
                    const currentTable = simpleTables.find(t => t.id === invite.table_id);
                    const search = tableSearch[invite.id] || '';
                    const filtered = simpleTables.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));
                    const exactMatch = simpleTables.find(t => t.name.toLowerCase() === search.toLowerCase());
                    const showCreate = search.trim().length > 0 && !exactMatch;
                    const isOpen = openTableSelect === invite.id;
                    return (
                      <div key={invite.id} className="p-4 hover:bg-zinc-50/50 transition-colors">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="text-[13.5px] font-semibold text-zinc-900 truncate">{invite.name}</p>
                            <p className="text-[11.5px] text-zinc-500 mt-0.5">{peopleLabel}{breakdownStr && <span className="text-zinc-400"> · ({breakdownStr})</span>}</p>
                          </div>
                          <div className="relative w-56 flex-shrink-0">
                            <button type="button" onClick={() => setOpenTableSelect(isOpen ? null : invite.id)}
                              className={`w-full px-3 py-2 rounded-lg border text-left text-[12.5px] flex items-center justify-between transition-colors ${currentTable ? 'border-zinc-200 bg-white' : 'border-dashed border-zinc-300 bg-white hover:border-zinc-400'}`}
                              data-testid={`table-select-${invite.id}`}>
                              <span className={currentTable ? 'text-zinc-900 font-medium truncate' : 'text-zinc-400'}>{currentTable?.name || 'Sem mesa definida'}</span>
                              <ChevronDown size={13} className={`text-zinc-400 transition-transform flex-shrink-0 ml-1 ${isOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isOpen && (
                              <div className="absolute z-50 left-0 right-0 mt-1.5 rounded-xl border border-zinc-200 bg-white overflow-hidden" style={{boxShadow:'0 12px 32px -8px rgba(0,0,0,0.14)'}}>
                                <div className="p-2 border-b border-zinc-100">
                                  <div className="relative">
                                    <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                                    <input autoFocus value={search} onChange={e => setTableSearch(prev => ({ ...prev, [invite.id]: e.target.value }))}
                                      placeholder="Buscar ou criar mesa..."
                                      onKeyDown={e => { if (e.key === 'Enter' && showCreate) { e.preventDefault(); assignTable(invite.id, search.trim(), true); } if (e.key === 'Escape') { setOpenTableSelect(null); } }}
                                      className="w-full pl-7 pr-2 py-1.5 text-[12px] bg-zinc-50 rounded-md border border-transparent outline-none focus:bg-white focus:border-zinc-200" />
                                  </div>
                                </div>
                                <div className="max-h-48 overflow-y-auto py-1">
                                  {currentTable && (
                                    <button type="button" onClick={() => unassignTable(invite.id)} className="w-full text-left px-3 py-1.5 text-[12px] text-red-600 hover:bg-red-50 transition-colors">
                                      <X size={11} className="inline mr-1" /> Remover mesa
                                    </button>
                                  )}
                                  {filtered.map(t => (
                                    <button key={t.id} type="button" onClick={() => assignTable(invite.id, t.id)}
                                      className="w-full text-left px-3 py-1.5 text-[12px] text-zinc-700 hover:bg-zinc-50 transition-colors flex items-center justify-between">
                                      <span>{t.name}</span>
                                      <span className="text-[10px] text-zinc-400">{t.guest_count} convite(s)</span>
                                    </button>
                                  ))}
                                  {showCreate && (
                                    <button type="button" onClick={() => assignTable(invite.id, search.trim(), true)}
                                      className="w-full text-left px-3 py-2 text-[12px] text-zinc-700 hover:bg-zinc-50 border-t border-zinc-100 flex items-center gap-2">
                                      <div className="w-4 h-4 rounded bg-zinc-100 flex items-center justify-center"><Plus size={10}/></div>
                                      <span>Criar mesa</span><span className="font-semibold text-zinc-900">"{search.trim()}"</span>
                                    </button>
                                  )}
                                  {filtered.length === 0 && !showCreate && (
                                    <p className="text-center text-[11px] text-zinc-400 py-3">Nenhuma mesa</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Resumo das mesas */}
                {simpleTables.length > 0 && (
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500 mb-2">Mesas criadas</p>
                    <div className="flex flex-wrap gap-1.5">
                      {simpleTables.map(t => (
                        <span key={t.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-100 text-[11.5px] font-medium text-zinc-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                          {t.name}
                          <span className="text-[10px] text-zinc-500">{t.guest_count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </Modal>

      {/* Modal de Organizar Mesas com IA — legado */}
      <Modal isOpen={showTablesModal} onClose={() => { setShowTablesModal(false); setTablesResult(null); }} title="Organizar Mesas com IA">
        <div className="space-y-4">
          {!tablesResult ? (
            <>
              <p className="text-sm text-slate-600">
                Configure o número de mesas e lugares para que a IA organize seus convidados de forma inteligente.
              </p>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Convidados confirmados:</strong> {stats?.confirmed?.people || 0} pessoas
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <Input
                  label="Mesas comuns"
                  type="number"
                  min="1"
                  max="50"
                  value={tablesConfig.num_mesas}
                  onChange={(e) => setTablesConfig(prev => ({ ...prev, num_mesas: parseInt(e.target.value) || 1 }))}
                />
                <Input
                  label="Lugares/mesa"
                  type="number"
                  min="4"
                  max="20"
                  value={tablesConfig.lugares_por_mesa}
                  onChange={(e) => setTablesConfig(prev => ({ ...prev, lugares_por_mesa: parseInt(e.target.value) || 8 }))}
                />
                <Input
                  label="Mesa Padrinhos"
                  type="number"
                  min="4"
                  max="20"
                  value={tablesConfig.lugares_mesa_padrinhos}
                  onChange={(e) => setTablesConfig(prev => ({ ...prev, lugares_mesa_padrinhos: parseInt(e.target.value) || 10 }))}
                />
              </div>

              <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-600">
                <p><strong>Mesas comuns:</strong> {tablesConfig.num_mesas} x {tablesConfig.lugares_por_mesa} = {tablesConfig.num_mesas * tablesConfig.lugares_por_mesa} lugares</p>
                <p><strong>Mesa de Padrinhos:</strong> {tablesConfig.lugares_mesa_padrinhos} lugares</p>
                <p><strong>Total geral:</strong> {(tablesConfig.num_mesas * tablesConfig.lugares_por_mesa) + tablesConfig.lugares_mesa_padrinhos} lugares</p>
                <p className={(stats?.confirmed?.people || 0) > (tablesConfig.num_mesas * tablesConfig.lugares_por_mesa) + tablesConfig.lugares_mesa_padrinhos ? "text-red-600" : "text-emerald-600"}>
                  {(stats?.confirmed?.people || 0) <= (tablesConfig.num_mesas * tablesConfig.lugares_por_mesa) + tablesConfig.lugares_mesa_padrinhos 
                    ? "✓ Lugares suficientes" 
                    : `⚠ Faltam ${(stats?.confirmed?.people || 0) - ((tablesConfig.num_mesas * tablesConfig.lugares_por_mesa) + tablesConfig.lugares_mesa_padrinhos)} lugares`}
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="secondary" onClick={() => setShowTablesModal(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button 
                  onClick={organizarMesas} 
                  disabled={loadingTables || (stats?.confirmed?.people || 0) === 0}
                  className="flex-1"
                >
                  {loadingTables ? (
                    <>
                      <Loader2 size={18} className="animate-spin mr-2" />
                      Organizando...
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} className="mr-2" />
                      Organizar com IA
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              {tablesResult.mensagem && !tablesResult.mesas?.length ? (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-yellow-800">{tablesResult.mensagem}</p>
                </div>
              ) : (
                <>
                  <div className="bg-emerald-50 p-4 rounded-lg mb-4">
                    <p className="text-emerald-800 font-medium">
                      ✓ {(tablesResult.mesas?.length || 0) + (tablesResult.mesaPadrinhos ? 1 : 0)} mesas organizadas com {tablesResult.total_convidados_input || tablesResult.total_convidados || 0} convidados
                    </p>
                    {tablesResult.justificativa && (
                      <p className="text-sm text-emerald-700 mt-2">{tablesResult.justificativa}</p>
                    )}
                  </div>

                  <div className="max-h-96 overflow-y-auto space-y-3">
                    {/* Mesa de Padrinhos */}
                    {tablesResult.mesaPadrinhos && tablesResult.mesaPadrinhos.convidados?.length > 0 && (
                      <Card className="p-3 border-2 border-purple-200 bg-purple-50">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-purple-800">
                            🎀 Mesa de Padrinhos
                          </h4>
                          <span className="text-xs bg-purple-200 text-purple-700 px-2 py-1 rounded-full">
                            {tablesResult.mesaPadrinhos.convidados?.length || 0} pessoas
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {tablesResult.mesaPadrinhos.convidados?.map((conv, i) => (
                            <span key={i} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                              {typeof conv === 'string' ? conv : conv.nome} {typeof conv !== 'string' && conv.papel && `(${conv.papel})`}
                            </span>
                          ))}
                        </div>
                      </Card>
                    )}

                    {/* Mesas Comuns */}
                    {tablesResult.mesas?.map((mesa, idx) => (
                      <Card key={idx} className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-slate-800">
                            {mesa.nome || `Mesa ${mesa.numero}`}
                          </h4>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                            {mesa.lugaresTotais ? `${mesa.convidados?.length || 0}/${mesa.lugaresTotais}` : `${mesa.convidados?.length || 0} pessoas`}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {mesa.convidados?.map((conv, i) => (
                            <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                              {typeof conv === 'string' ? conv : conv.nome}
                            </span>
                          ))}
                        </div>
                      </Card>
                    ))}
                  </div>

                  {/* Botões de Exportação */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button variant="secondary" onClick={exportarMesasPDF} className="flex-1">
                      <Download size={16} className="mr-2" />
                      Exportar PDF
                    </Button>
                    <Button variant="secondary" onClick={exportarMesasExcel} className="flex-1">
                      <Download size={16} className="mr-2" />
                      Exportar Excel
                    </Button>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button variant="secondary" onClick={() => setTablesResult(null)} className="flex-1">
                      Reorganizar
                    </Button>
                    <Button onClick={salvarOrganizacaoMesas} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                      <Check size={16} className="mr-2" />
                      Salvar Organização
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Modal>

      {/* 📱 Mobile FAB — Adicionar convidado (md:hidden) */}
      <button
        onClick={() => { resetForm(); try { navigator.vibrate?.(8); } catch {} setShowModal(true); }}
        className="md:hidden fixed right-4 z-[9999] w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-[0_8px_32px_rgba(139,92,246,0.45)] active:scale-95 transition-transform"
        style={{ bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' }}
        data-testid="mobile-fab-add-guest"
        data-mobile-fab
        aria-label="Adicionar convidado"
        title="Adicionar convidado"
      >
        <Plus size={26} strokeWidth={2.5} />
      </button>

      {/* Drawer/Sheet de ações de envio em massa via WhatsApp */}
      {showMassActions && (
        <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="mass-actions-sheet" onClick={(e) => { if (e.target === e.currentTarget) setShowMassActions(false); }}>
          <div className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl shadow-2xl ring-1 ring-zinc-200/60 overflow-hidden animate-slide-up" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {/* Handle visual mobile */}
            <div className="md:hidden flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-zinc-300 rounded-full" />
            </div>
            <div className="px-5 pt-2 pb-3 flex items-start justify-between">
              <div>
                <h3 className="text-[16px] font-semibold tracking-tight text-zinc-900">Enviar via WhatsApp</h3>
                <p className="text-[12px] text-zinc-500 mt-0.5">Escolha o tipo de envio</p>
              </div>
              <button onClick={() => setShowMassActions(false)} className="p-1.5 -mr-1.5 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="mass-actions-close">
                <X size={17} className="text-zinc-500" />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-2">
              {/* Opção 1: Enviar em massa para selecionados */}
              <button
                type="button"
                onClick={() => { setShowMassActions(false); setShowWaSendFlow(true); }}
                disabled={selectedInvites.length === 0}
                className="w-full text-left flex items-center gap-3 p-3.5 rounded-2xl border border-zinc-200/70 disabled:opacity-50 disabled:cursor-not-allowed card-hover-brightness"
                data-testid="btn-send-mass"
              >
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Send size={18} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-zinc-900">Enviar em massa</p>
                  <p className="text-[11.5px] text-zinc-500 mt-0.5">
                    {selectedInvites.length > 0
                      ? `${selectedInvites.length} convidado(s) selecionado(s)`
                      : "Selecione convidados na lista primeiro"}
                  </p>
                </div>
                <ChevronRight size={16} className="text-zinc-400" />
              </button>

              {/* Opção 2: Lembrete para não confirmados */}
              <button
                type="button"
                onClick={() => { setShowMassActions(false); handleSendWhatsApp('reminder', invites.map(i => i.id)); }}
                disabled={sendingWhatsApp}
                className="w-full text-left flex items-center gap-3 p-3.5 rounded-2xl border border-zinc-200/70 disabled:opacity-50 disabled:cursor-not-allowed card-hover-brightness"
                data-testid="btn-alerta-rsvp"
              >
                <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                  {sendingWhatsApp ? <Loader2 className="animate-spin text-amber-600" size={18} /> : <Bell size={18} className="text-amber-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-zinc-900">Lembrete RSVP</p>
                  <p className="text-[11.5px] text-zinc-500 mt-0.5">Para todos os {stats?.pending?.invites || 0} pendentes</p>
                </div>
                <ChevronRight size={16} className="text-zinc-400" />
              </button>

              {/* Insight: total convites */}
              <div className="pt-3 mt-1 border-t border-zinc-100 flex items-center justify-between text-[11.5px] text-zinc-500">
                <span>Total de convites</span>
                <span className="font-semibold text-zinc-700 tabular-nums">{stats?.total_invites || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Check-in Ao Vivo - Painel de Acompanhamento em Tempo Real (COM WEBSOCKET)


// ==================== ORGANIZAR MESAS (página dedicada) ====================

// Check-in Ao Vivo - Painel de Acompanhamento em Tempo Real (COM WEBSOCKET)
// ==================== CHECK-OUT SEGURO DE CRIANÇAS (Fase 1) ====================

function ChildCheckoutStatusBadge({ status }) {
  const map = {
    issued: { t: "QR emitido", c: "bg-blue-50 text-blue-700 border-blue-200" },
    used: { t: "Retirada realizada", c: "bg-green-50 text-green-700 border-green-200" },
    cancelled: { t: "Invalidado", c: "bg-zinc-100 text-zinc-500 border-zinc-200" },
  };
  const m = map[status] || map.issued;
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${m.c}`} data-testid="checkout-status-badge">{m.t}</span>;
}

function ChildRetiradaCard({ data, onConfirm, confirming }) {
  const co = data?.checkout || {};
  const invalid = data && !data.valid && !data.already_used;
  const used = data?.already_used || co.status === "used";
  const fmt = (t) => t ? new Date(t).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : "—";
  const children = (co.children && co.children.length) ? co.children : [{ name: co.child_name, age: co.child_age }];
  const label = co.children_label || children.map(c => c.name).join(", ");
  const multi = children.length > 1;
  if (used) {
    return (
      <div className="rounded-2xl border-2 border-green-200 bg-green-50/60 p-5 text-center" data-testid="retirada-card">
        <p className="text-2xl mb-1">💜</p>
        <h3 className="text-lg font-bold text-green-700">Retirada concluída</h3>
        <p className="text-sm text-zinc-700 mt-1"><span className="font-semibold">{label}</span> {multi ? "foram retiradas" : "foi retirada"} com sucesso.</p>
        <p className="text-sm text-zinc-600 mt-2">Retirada realizada por: <span className="font-semibold">{co.responsible_name || "—"}</span></p>
        <p className="text-sm text-zinc-600">Horário: <span className="font-semibold">{fmt(co.checkout_at)}</span></p>
      </div>
    );
  }
  if (invalid) {
    return (
      <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-5 flex items-start gap-2" data-testid="retirada-invalid">
        <X className="text-red-600 mt-0.5" size={18} />
        <div>
          <p className="font-semibold text-red-700">{data.message}</p>
          <p className="text-sm text-red-600 mt-1">Operação bloqueada. Não libere a criança sem um QR válido.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border-2 border-green-200 bg-green-50/50 p-5" data-testid="retirada-card">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">🟢</span>
        <div>
          <h3 className="text-base font-bold text-green-700">QR Code validado · Retirada autorizada</h3>
          <p className="text-[12px] text-zinc-500">{multi ? "A retirada destas crianças está autorizada." : "A retirada desta criança está autorizada."}</p>
        </div>
      </div>
      <div className="space-y-2.5 bg-white rounded-xl p-4 border border-green-100">
        <div className="space-y-1.5" data-testid="retirada-children-list">
          {children.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <span>👧</span>
              <span className="text-base font-bold text-zinc-900" data-testid={`retirada-child-name-${i}`}>{c.name}{c.age ? ` — ${c.age} anos` : ""}</span>
            </div>
          ))}
        </div>
        <p className="text-sm text-zinc-700 pt-1 border-t border-zinc-100">Responsável: <span className="font-semibold" data-testid="retirada-responsible">{co.responsible_name || "—"}</span></p>
        <p className="text-sm text-zinc-600 flex items-center gap-1"><Clock size={13} /> Check-in: <span className="font-medium">{fmt(co.checkin_at)}</span></p>
      </div>
      {multi && <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">Ao confirmar, {label} serão retiradas juntas.</p>}
      <button
        onClick={() => onConfirm(co.token)}
        disabled={confirming}
        data-testid="confirm-retirada-btn"
        className="mt-4 w-full py-3.5 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold text-base flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
      >
        {confirming ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
        {multi ? "Confirmar retirada de todas" : "Confirmar retirada"}
      </button>
    </div>
  );
}

function ChildCheckoutPage() {
  const { wedding } = useWedding();
  const [config, setConfig] = useState({ enabled: false, require_qr: true, kids_only_event: false });
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [checkouts, setCheckouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tokenInput, setTokenInput] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const wid = wedding?.id;

  const fetchAll = useCallback(async () => {
    if (!wid) { setLoading(false); return; }
    try {
      const [cfg, st, pend, list] = await Promise.all([
        axios.get(`${API}/weddings/${wid}/child-pickup-config`),
        axios.get(`${API}/weddings/${wid}/child-checkout-stats`),
        axios.get(`${API}/weddings/${wid}/child-checkout/pending-issue`),
        axios.get(`${API}/weddings/${wid}/child-checkout`),
      ]);
      setConfig(cfg.data);
      setStats(st.data);
      setPending(pend.data.items || []);
      setCheckouts(list.data.items || []);
    } catch (e) {
      console.error("child-checkout fetch", e);
    } finally { setLoading(false); }
  }, [wid]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveConfig = async (patch) => {
    const next = { ...config, ...patch };
    setConfig(next);
    try { await axios.put(`${API}/weddings/${wid}/child-pickup-config`, next); }
    catch (e) { toast.error("Não foi possível salvar a configuração"); }
  };

  const issueAndSend = async (inviteId, send) => {
    const key = inviteId;
    setBusyKey(key);
    try {
      const res = await axios.post(`${API}/weddings/${wid}/child-checkout/issue`, { invite_id: inviteId });
      const tk = res.data.token;
      if (send) {
        try {
          const sres = await axios.post(`${API}/weddings/${wid}/child-checkout/${tk}/send`);
          toast.success(sres.data.mock ? "QR gerado (WhatsApp simulado)" : "QR de retirada enviado ao(s) responsável(is)!");
        } catch (se) {
          toast.error(`QR emitido, mas o envio falhou: ${se.response?.data?.detail || "verifique a conexão do WhatsApp"}`);
        }
      } else {
        toast.success("QR de retirada emitido!");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao emitir QR");
    } finally {
      setBusyKey(null);
      await fetchAll();
    }
  };

  const sendExisting = async (tk) => {
    setBusyKey(tk);
    try {
      const sres = await axios.post(`${API}/weddings/${wid}/child-checkout/${tk}/send`);
      toast.success(sres.data.mock ? "WhatsApp simulado (preview)" : "QR reenviado ao responsável!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar QR"); }
    finally { setBusyKey(null); }
  };

  const [bulkSending, setBulkSending] = useState(false);
  const [childFilter, setChildFilter] = useState('a_retirar'); // a_retirar | retiradas | todas
  const bulkSend = async () => {
    setBulkSending(true);
    try {
      const res = await axios.post(`${API}/weddings/${wid}/child-checkout/bulk-send`, {});
      const d = res.data;
      if (d.sent > 0) toast.success(`${d.sent} QR Code(s) de retirada enviado(s)!${d.mock ? " (simulado)" : ""}`);
      else if (d.total_children === 0) toast.info("Nenhuma criança com check-in ainda.");
      else toast.error(d.message || "Nenhum QR enviado. Verifique a conexão do WhatsApp.");
      if (d.skipped_no_phone > 0) toast.warning(`${d.skipped_no_phone} sem telefone do responsável (QR emitido, não enviado).`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no envio em massa");
    } finally { setBulkSending(false); }
  };

  const extractToken = (raw) => {
    let v = (raw || "").trim();
    if (!v) return "";
    if (v.includes("/retirada/")) {
      const parts = v.split("/").filter(Boolean);
      return parts[parts.length - 1].split("?")[0];
    }
    if (v.startsWith("http")) return v.split("/").filter(Boolean).pop().split("?")[0];
    return v;
  };

  const doScan = async (rawValue) => {
    const tk = extractToken(rawValue != null ? rawValue : tokenInput);
    if (!tk) return;
    setTokenInput(tk);
    try {
      const res = await axios.get(`${API}/weddings/${wid}/child-checkout/scan/${encodeURIComponent(tk)}`);
      setScanResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "QR Code não encontrado neste evento");
      setScanResult(null);
    }
  };

  const [cameraOn, setCameraOn] = useState(false);
  const scannerRef = useRef(null);
  const stopCamera = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch (_) {}
      scannerRef.current = null;
    }
    setCameraOn(false);
  }, []);
  const startCamera = async () => {
    setCameraOn(true);
    setScanResult(null);
    try {
      const { Html5Qrcode } = await loadQrScanner();
      await new Promise(r => setTimeout(r, 100));
      const scanner = new Html5Qrcode("checkout-qr-reader");
      scannerRef.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
        async (decoded) => { await stopCamera(); await doScan(decoded); },
        () => {}
      );
    } catch (e) {
      toast.error("Não foi possível acessar a câmera. Verifique a permissão.");
      setCameraOn(false);
    }
  };
  useEffect(() => () => { stopCamera(); }, [stopCamera]);

  const confirmRetirada = async (tk) => {
    setConfirming(true);
    try {
      await axios.post(`${API}/weddings/${wid}/child-checkout/${tk}/confirm`, {});
      toast.success("Retirada confirmada com sucesso! 💜");
      await doScan(tk);
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível confirmar a retirada"); }
    finally { setConfirming(false); }
  };

  if (!wid) return <div className="p-8 text-center text-zinc-500">Selecione um evento para gerenciar a retirada de crianças.</div>;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="child-checkout-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-violet-100 flex items-center justify-center"><Shield className="text-violet-600" size={22} /></div>
        <div>
          <h1 className="text-xl font-bold text-zinc-900">Retirada Segura de Crianças</h1>
          <p className="text-sm text-zinc-500">Check-out com QR Code individual e confirmação da equipe.</p>
        </div>
      </div>

      {/* Configuração */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="child-pickup-config">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-zinc-900">Controle de retirada de crianças</p>
            <p className="text-sm text-zinc-500">Ative para gerar QR Codes de check-out para crianças.</p>
          </div>
          <button
            onClick={() => saveConfig({ enabled: !config.enabled })}
            data-testid="toggle-pickup-control"
            className={`relative w-12 h-7 rounded-full transition-colors ${config.enabled ? "bg-violet-600" : "bg-zinc-300"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${config.enabled ? "translate-x-5" : ""}`} />
          </button>
        </div>
        {config.enabled && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-100">
            <p className="text-sm font-medium text-zinc-700">Exigir QR Code para retirada</p>
            <button
              onClick={() => saveConfig({ require_qr: !config.require_qr })}
              data-testid="toggle-require-qr"
              className={`relative w-12 h-7 rounded-full transition-colors ${config.require_qr ? "bg-violet-600" : "bg-zinc-300"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${config.require_qr ? "translate-x-5" : ""}`} />
            </button>
          </div>
        )}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-100">
          <div>
            <p className="text-sm font-medium text-zinc-700">Festa exclusiva para crianças (todo o evento)</p>
            <p className="text-xs text-zinc-400">Já mostra os campos de responsável em todo convite novo.</p>
          </div>
          <button
            onClick={() => saveConfig({ kids_only_event: !config.kids_only_event })}
            data-testid="toggle-kids-only-event"
            className={`relative w-12 h-7 rounded-full transition-colors ${config.kids_only_event ? "bg-violet-600" : "bg-zinc-300"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${config.kids_only_event ? "translate-x-5" : ""}`} />
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="child-checkout-stats">
          <div className="rounded-2xl border border-zinc-200 bg-white p-4"><p className="text-2xl font-bold text-zinc-900" data-testid="stat-children">{stats.children_present}</p><p className="text-xs text-zinc-500">Crianças no evento</p></div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-4"><p className="text-2xl font-bold text-green-600">{stats.accompanied}</p><p className="text-xs text-zinc-500">Acompanhadas</p></div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-4"><p className="text-2xl font-bold text-amber-600" data-testid="stat-unaccompanied">{stats.unaccompanied}</p><p className="text-xs text-zinc-500">Desacompanhadas</p></div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-4"><p className="text-2xl font-bold text-violet-600" data-testid="stat-retrieved">{stats.retrieved} / {stats.qr_issued}</p><p className="text-xs text-zinc-500">Retiradas</p></div>
        </div>
      )}

      {/* Scanner / Confirmação */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-5 space-y-3" data-testid="checkout-scanner">
        <div className="flex items-center gap-2"><QrCode size={18} className="text-violet-600" /><h2 className="font-bold text-zinc-900">Check-out / Retirada</h2></div>
        <p className="text-sm text-zinc-500">Aponte a câmera para o QR Code da criança — a leitura já autoriza a retirada. Não é preciso digitar código.</p>
        {cameraOn ? (
          <div>
            <div id="checkout-qr-reader" className="w-full max-w-sm mx-auto rounded-xl overflow-hidden" data-testid="checkout-qr-reader" />
            <button onClick={stopCamera} data-testid="stop-camera-btn" className="mt-3 w-full py-2.5 rounded-xl border border-zinc-300 text-zinc-600 font-semibold text-sm">Parar câmera</button>
          </div>
        ) : (
          <button onClick={startCamera} data-testid="start-camera-btn" className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-bold text-sm flex items-center justify-center gap-2">
            <QrCode size={16} /> Escanear QR Code com a câmera
          </button>
        )}
        <details className="text-xs text-zinc-400">
          <summary className="cursor-pointer">Informar código manualmente</summary>
          <div className="flex gap-2 mt-2">
            <input value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} placeholder="Código do QR de retirada" data-testid="token-input"
              className="flex-1 rounded-xl border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" />
            <button onClick={() => doScan()} data-testid="scan-btn" className="px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold text-sm">Buscar</button>
          </div>
        </details>
        {scanResult && <ChildRetiradaCard data={scanResult} onConfirm={confirmRetirada} confirming={confirming} />}
      </div>

      {/* Emitir QR — crianças com check-in */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="pending-issue-list">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-zinc-900">Crianças presentes</h2>
          <div className="flex items-center gap-2">
            <button onClick={bulkSend} disabled={bulkSending || pending.length === 0} data-testid="bulk-send-qr-btn"
              className="text-xs px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold flex items-center gap-1.5 disabled:opacity-50">
              {bulkSending ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} Enviar QR a todos
            </button>
            <button onClick={fetchAll} className="text-zinc-400 hover:text-zinc-600"><RefreshCw size={16} /></button>
          </div>
        </div>
        {loading ? <p className="text-sm text-zinc-400">Carregando…</p> : pending.length === 0 ? (
          <p className="text-sm text-zinc-400">Nenhuma criança com check-in ainda.</p>
        ) : (() => {
          const inviteUsed = (inv) => inv.checkout?.status === "used";
          const totalKids = pending.reduce((n, inv) => n + inv.children.length, 0);
          const cRetiradas = pending.filter(inviteUsed).reduce((n, inv) => n + inv.children.length, 0);
          const cARetirar = totalKids - cRetiradas;
          const matchFilter = (inv) => childFilter === 'todas' ? true : childFilter === 'retiradas' ? inviteUsed(inv) : !inviteUsed(inv);
          const visible = pending.filter(matchFilter);
          const tabs = [
            { id: 'a_retirar', label: 'A retirar', n: cARetirar, c: 'text-amber-600' },
            { id: 'retiradas', label: 'Já retiradas', n: cRetiradas, c: 'text-green-600' },
            { id: 'todas', label: 'Todas', n: totalKids, c: 'text-zinc-700' },
          ];
          return (
          <>
          <div className="flex items-center gap-2 mb-3" data-testid="child-filter-tabs">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setChildFilter(t.id)} data-testid={`child-filter-${t.id}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${childFilter === t.id ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-zinc-600 border-zinc-200 hover:border-violet-300'}`}>
                {t.label} <span className={childFilter === t.id ? 'text-white/90' : t.c}>({t.n})</span>
              </button>
            ))}
          </div>
          {visible.length === 0 ? (
            <p className="text-sm text-zinc-400">{childFilter === 'retiradas' ? 'Nenhuma criança retirada ainda.' : childFilter === 'a_retirar' ? 'Todas as crianças já foram retiradas! 🎉' : 'Nenhuma criança.'}</p>
          ) : (
          <div className="space-y-3">
            {visible.map((inv) => {
              const co = inv.checkout;
              const isUsed = co?.status === "used";
              return (
              <div key={inv.invite_id} className={`rounded-xl border p-3 ${inv.unaccompanied ? "border-amber-200 bg-amber-50/40" : "border-zinc-100"}`} data-testid={`pending-invite-${inv.invite_id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-sm text-zinc-800">{inv.invite_name}</p>
                  {inv.unaccompanied
                    ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">Sem responsável presente</span>
                    : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">Acompanhada</span>}
                </div>
                <p className="text-xs text-zinc-500 mt-0.5">Responsável: {inv.responsible?.name}{inv.responsible?.phone ? ` · ${inv.responsible.phone}` : " · sem telefone"}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {inv.children.map((c) => (
                    <span key={c.child_index} className="text-sm text-zinc-700 flex items-center gap-1 bg-white border border-zinc-100 rounded-lg px-2 py-1">
                      <Baby size={13} className={isUsed ? "text-green-500" : "text-violet-500"} />{c.name}{c.age ? ` (${c.age})` : ""}
                    </span>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap border-t border-zinc-100 pt-2">
                  <span className="text-[11px] text-zinc-500">
                    {isUsed && co?.checkout_at
                      ? <span className="text-green-600 font-medium">Retirada às {new Date(co.checkout_at).toLocaleTimeString('pt-BR', {hour:'2-digit',minute:'2-digit'})} por {co.checkout_by || "equipe"}</span>
                      : `QR único para ${inv.children.length > 1 ? `as ${inv.children.length} crianças` : "a criança"} deste convite`}
                  </span>
                  <div className="flex items-center gap-2">
                    {co ? (
                      <>
                        <ChildCheckoutStatusBadge status={co.status} />
                        {co.status === "issued" && (
                          <button disabled={busyKey === co.token} onClick={() => sendExisting(co.token)} data-testid={`send-qr-${inv.invite_id}`}
                            className="text-xs px-2.5 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold flex items-center gap-1 disabled:opacity-60">
                            {busyKey === co.token ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />} WhatsApp
                          </button>
                        )}
                        <a href={`${API}/child-checkout/${co.token}/qrcode`} target="_blank" rel="noreferrer" className="text-xs px-2.5 py-1.5 rounded-lg border border-zinc-200 text-zinc-600 font-semibold flex items-center gap-1"><QrCode size={12} /> Ver QR</a>
                      </>
                    ) : (
                      <button disabled={busyKey === inv.invite_id} onClick={() => issueAndSend(inv.invite_id, true)} data-testid={`issue-qr-${inv.invite_id}`}
                        className="text-xs px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold flex items-center gap-1 disabled:opacity-60">
                        {busyKey === inv.invite_id ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />} Emitir + enviar QR
                      </button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
          )}
          </>
          );
        })()}
      </div>
    </div>
  );
}

function ChildRetiradaPage() {
  const { weddingId, token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [justConfirmed, setJustConfirmed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/child-checkout/public/${weddingId}/${encodeURIComponent(token)}`);
      setData(res.data);
    } catch (e) {
      setData({ valid: false, message: e.response?.data?.detail || "QR inválido" });
    } finally { setLoading(false); }
  }, [weddingId, token]);

  useEffect(() => { load(); }, [load]);

  const confirmRetirada = async (tk) => {
    setConfirming(true);
    try {
      await axios.post(`${API}/child-checkout/public/${weddingId}/${encodeURIComponent(tk)}/confirm`, {});
      toast.success("Retirada confirmada com sucesso! ✅");
      setJustConfirmed(true);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível confirmar"); await load(); }
    finally { setConfirming(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 p-4 flex flex-col items-center justify-center" data-testid="child-retirada-page">
      <div className="w-full max-w-md">
        <button onClick={() => navigate("/checkin-live")} className="text-sm text-zinc-500 flex items-center gap-1 mb-4"><ArrowLeft size={15} /> Voltar</button>
        {loading ? (
          <div className="text-center py-16"><Loader2 className="animate-spin mx-auto text-violet-500" size={28} /></div>
        ) : authError ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-6 text-center">
            <Shield className="mx-auto text-zinc-400 mb-2" size={28} />
            <p className="font-semibold text-zinc-800">Faça login para validar a retirada</p>
            <p className="text-sm text-zinc-500 mt-1">Somente a equipe do evento pode confirmar retiradas.</p>
            <button onClick={() => navigate("/login")} className="mt-4 px-5 py-2.5 rounded-xl bg-violet-600 text-white font-semibold text-sm">Entrar</button>
          </div>
        ) : (
          <ChildRetiradaCard data={data} onConfirm={confirmRetirada} confirming={confirming} />
        )}
        {!loading && data?.already_used && !justConfirmed && (
          <p className="text-center text-sm text-red-600 mt-4 font-medium" data-testid="already-used-alert">⚠️ Este QR Code já foi utilizado anteriormente.</p>
        )}
      </div>
    </div>
  );
}


function CheckinLivePage() {
  const { wedding } = useWedding();
  const [stats, setStats] = useState(null);
  const [checkinList, setCheckinList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [lastCheckinEvent, setLastCheckinEvent] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'confirmed' | 'present' | 'waiting'
  const [manualCheckin, setManualCheckin] = useState(null); // { invite } modal aberto para check-in manual
  const [submittingCheckin, setSubmittingCheckin] = useState(false);
  const [report, setReport] = useState(null); // relatório (cartão ausentes + PDF)
  const [pdfLoading, setPdfLoading] = useState(false);

  // Buscar dados via API
  const fetchCheckinData = useCallback(async () => {
    if (!wedding?.id) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/checkin-live`);
      setStats(res.data.stats);
      setCheckinList(res.data.checkin_list || []);
      setLastUpdate(new Date());
      try {
        const rep = await axios.get(`${API}/weddings/${wedding.id}/checkin/report`);
        setReport(rep.data);
      } catch (e) { /* relatório é opcional */ }
    } catch (err) {
      console.error("Erro ao buscar dados de check-in:", err);
    } finally {
      setLoading(false);
    }
  }, [wedding?.id]);

  // Callback quando receber evento de check-in via SSE/WebSocket
  const handleCheckinEvent = useCallback((data) => {
    setLastCheckinEvent(data);
    setLastUpdate(new Date());
    
    // Buscar dados atualizados imediatamente
    fetchCheckinData();
    
    // Mostrar toast de notificação
    const guestName = data.guest_name || data.invite_name || "Convidado";
    toast.success(`✅ ${guestName} fez check-in!`, {
      description: `Check-in realizado com sucesso`,
      duration: 4000
    });
  }, [fetchCheckinData]);

  // Callback quando receber atualização de stats via SSE/WebSocket
  const handleStatsUpdate = useCallback((data) => {
    if (data.stats) {
      setStats(data.stats);
    }
    if (data.checkin_list) {
      setCheckinList(data.checkin_list);
    }
    setLastUpdate(new Date());
    setLoading(false);
  }, []);

  // Conectar SSE/WebSocket
  const { isConnected, connectionError, requestStats } = useCheckinWebSocket(
    wedding?.id,
    handleCheckinEvent,
    handleStatsUpdate
  );

  // Carregar dados iniciais
  useEffect(() => {
    fetchCheckinData();
  }, [fetchCheckinData]);

  // ⚡ POLLING AUTOMÁTICO: Atualizar a cada 3 segundos para garantir tempo real
  useEffect(() => {
    if (!wedding?.id) return;
    
    const interval = setInterval(() => {
      fetchCheckinData();
    }, 3000); // 3 segundos
    
    return () => clearInterval(interval);
  }, [wedding?.id, fetchCheckinData]);

  // Função para obter cor do status de presença (STATUS CORRETOS)
  const getPresenceConfig = (presenceStatus, rsvpStatus) => {
    if (presenceStatus === "presente") {
      return { label: "Presente", color: "bg-emerald-100 text-emerald-700", icon: Check };
    }
    if (rsvpStatus === "confirmado") {
      return { label: "Aguardando", color: "bg-orange-100 text-orange-700", icon: Clock };
    }
    if (rsvpStatus === "nao_ira") {
      return { label: "Não virá", color: "bg-slate-100 text-slate-500", icon: UserX };
    }
    return { label: "Não confirmado", color: "bg-blue-100 text-blue-700", icon: User };
  };

  // Função para formatar tipo de convidado
  const formatGuestType = (type) => {
    const types = {
      padrinho_noivo: "Padrinho do Noivo",
      madrinha_noivo: "Madrinha do Noivo",
      padrinho_noiva: "Padrinho da Noiva",
      madrinha_noiva: "Madrinha da Noiva",
      familia_noivo: "Família do Noivo",
      familia_noiva: "Família da Noiva",
      amigos_casal: "Amigos do Casal",
      amigos_noivo: "Amigos do Noivo",
      amigos_noiva: "Amigos da Noiva",
      trabalho_noivo: "Trabalho do Noivo",
      trabalho_noiva: "Trabalho da Noiva",
      amigos_infancia: "Infância",
      amigos_faculdade: "Faculdade",
      vizinhos: "Vizinhos",
      conhecidos: "Conhecidos",
      convidado: "Convidado"
    };
    return types[type] || type || "Convidado";
  };

  // Filtrar lista por status ativo
  const filteredList = useMemo(() => {
    if (filterStatus === 'all') return checkinList;
    if (filterStatus === 'present') return checkinList.filter(i => i.presence_status === 'presente');
    if (filterStatus === 'waiting') return checkinList.filter(i => i.presence_status !== 'presente' && i.rsvp_status === 'confirmado');
    if (filterStatus === 'confirmed') return checkinList.filter(i => i.rsvp_status === 'confirmado');
    return checkinList;
  }, [checkinList, filterStatus]);

  // Submeter check-in manual via token
  const submitManualCheckin = async (invite, peopleEntering) => {
    if (!invite?.rsvp_token) {
      toast.error('Convite sem token de check-in disponível');
      return;
    }
    setSubmittingCheckin(true);
    try {
      const res = await axios.post(`${API}/rsvp/${invite.rsvp_token}/checkin`, {
        people_entering: peopleEntering
      });
      const data = res.data;
      if (data?.success || data?.status === 'LIBERADO') {
        toast.success(`✅ ${invite.name} — check-in manual realizado`);
        setManualCheckin(null);
        fetchCheckinData();
      } else {
        toast.error(data?.message || 'Não foi possível realizar o check-in');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Erro ao fazer check-in');
    } finally {
      setSubmittingCheckin(false);
    }
  };

  // Baixar relatório de check-in em PDF (Confirmados x Presentes x Ausentes)
  const handleDownloadCheckinPdf = async () => {
    if (!report) { toast.error("Relatório ainda carregando, tente novamente."); return; }
    setPdfLoading(true);
    try {
      const { generateCheckinReportPdf } = await import("./lib/checkinReportPdf");
      await generateCheckinReportPdf(report, { responsavel: wedding?.partner1_name ? `${wedding.partner1_name}${wedding.partner2_name ? " & " + wedding.partner2_name : ""}` : "" });
    } catch (e) {
      toast.error("Não foi possível gerar o PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  // Skeleton loading para melhor UX durante carregamento
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="animate-pulse">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-slate-200"></div>
              <div className="h-8 bg-slate-200 rounded w-56"></div>
            </div>
            <div className="h-5 bg-slate-200 rounded w-72"></div>
          </div>
          <div className="flex items-center gap-3 animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-28"></div>
            <div className="h-10 bg-slate-200 rounded w-10"></div>
          </div>
        </div>
        {/* Progress Card Skeleton */}
        <div className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
          <div className="flex items-center justify-between mb-3">
            <div className="h-6 bg-slate-200 rounded w-40"></div>
            <div className="h-8 bg-slate-200 rounded w-16"></div>
          </div>
          <div className="h-6 bg-slate-200 rounded-full mb-6"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="text-center">
                <div className="h-8 bg-slate-200 rounded w-12 mx-auto mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-16 mx-auto"></div>
              </div>
            ))}
          </div>
        </div>
        {/* Content Skeleton */}
        <div className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
          <div className="h-6 bg-slate-200 rounded w-48 mb-4"></div>
          <div className="space-y-3">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-16 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Check-in em tempo real</h1>
          <p className="text-sm text-zinc-500 mt-1">Acompanhe a chegada dos convidados em tempo real</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Indicador de conexão */}
          <div className="flex items-center gap-2 text-[12px]">
            <div className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`}></div>
            <span className={isConnected ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
              {isConnected ? 'Tempo real' : 'Reconectando...'}
            </span>
          </div>
          <button 
            onClick={fetchCheckinData}
            className="p-2 bg-white border border-zinc-200 text-zinc-700 rounded-lg hover:bg-zinc-50 transition-colors"
            title="Atualizar agora"
            data-testid="refresh-checkin"
          >
            <RefreshCw size={15} />
          </button>
          <button
            onClick={handleDownloadCheckinPdf}
            disabled={pdfLoading}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 text-white text-[12.5px] font-medium rounded-lg transition-colors disabled:opacity-50"
            title="Baixar relatório de check-in em PDF"
            data-testid="checkin-pdf-btn"
          >
            {pdfLoading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            Relatório PDF
          </button>
        </div>
      </div>

      {/* ==================== BARRA SUPERIOR DE PROGRESSO ==================== */}
      <Card className="p-6">
        {/* Barra de Progresso */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Progresso de chegada</p>
              <p className="text-[12px] text-zinc-400 mt-0.5">{stats?.total_checked_in || 0} de {stats?.total_confirmed || 0} convidados</p>
            </div>
            <span className="text-3xl font-semibold tracking-tight text-zinc-900">{stats?.arrival_percentage || 0}%</span>
          </div>
          <div className="w-full bg-zinc-100 rounded-full h-2 overflow-hidden">
            <div 
              className="h-2 rounded-full bg-emerald-500 transition-all duration-700"
              style={{ width: `${stats?.arrival_percentage || 0}%` }}
            />
          </div>
        </div>

        {/* Cards de Estatísticas - PESSOAS, não convites — clicáveis para filtrar */}
        <div className="grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={()=>setFilterStatus(filterStatus === 'confirmed' ? 'all' : 'confirmed')}
            className={`text-left rounded-xl p-4 border card-hover-brightness ${filterStatus === 'confirmed' ? 'border-blue-500 ring-2 ring-blue-100 bg-blue-50/40' : 'border-zinc-200/60'}`}
            data-testid="filter-card-confirmados"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Confirmados</p>
              </div>
              {filterStatus === 'confirmed' && <span className="text-[10px] font-medium text-blue-600">Filtrando</span>}
            </div>
            <p className="text-3xl font-semibold tracking-tight text-zinc-900">{stats?.total_confirmed || 0}</p>
            <p className="text-[11px] text-zinc-400 mt-1">{stats?.invites_confirmed || 0} convite(s) · {stats?.total_confirmed || 0} pessoa(s)</p>
          </button>
          <button
            type="button"
            onClick={()=>setFilterStatus(filterStatus === 'present' ? 'all' : 'present')}
            className={`text-left rounded-xl p-4 border card-hover-brightness ${filterStatus === 'present' ? 'border-emerald-500 ring-2 ring-emerald-100 bg-emerald-50/40' : 'border-zinc-200/60'}`}
            data-testid="filter-card-presentes"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Presentes</p>
              </div>
              {filterStatus === 'present' && <span className="text-[10px] font-medium text-emerald-600">Filtrando</span>}
            </div>
            <p className="text-3xl font-semibold tracking-tight text-zinc-900">{stats?.total_checked_in || 0}</p>
            <p className="text-[11px] text-zinc-400 mt-1">{stats?.invites_present || 0} convite(s) · {stats?.total_checked_in || 0} pessoa(s)</p>
          </button>
          <button
            type="button"
            onClick={()=>setFilterStatus(filterStatus === 'waiting' ? 'all' : 'waiting')}
            className={`text-left rounded-xl p-4 border card-hover-brightness ${filterStatus === 'waiting' ? 'border-amber-500 ring-2 ring-amber-100 bg-amber-50/40' : 'border-zinc-200/60'}`}
            data-testid="filter-card-aguardando"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Aguardando</p>
              </div>
              {filterStatus === 'waiting' && <span className="text-[10px] font-medium text-amber-600">Filtrando</span>}
            </div>
            <p className="text-3xl font-semibold tracking-tight text-zinc-900">{stats?.total_waiting || 0}</p>
            <p className="text-[11px] text-zinc-400 mt-1">{stats?.invites_waiting || 0} convite(s) · {stats?.total_waiting || 0} pessoa(s)</p>
          </button>
        </div>
      </Card>

      {/* ==================== CARTÃO: CONFIRMADOS AUSENTES (tempo real) ==================== */}
      {report?.absent_participants?.length > 0 && (
        <Card className="overflow-hidden border-rose-200" data-testid="absent-card">
          <div className="p-4 border-b border-rose-100 bg-rose-50/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserX size={16} className="text-rose-500" />
              <h3 className="text-[13px] font-semibold text-rose-700">Confirmados Ausentes</h3>
              <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-semibold" data-testid="absent-count">
                {report.summary?.people_absent || report.absent_participants.length} · {report.summary?.absent_pct || 0}%
              </span>
            </div>
            <span className="text-[11px] text-rose-400">Lugares que podem ser remanejados</span>
          </div>
          <div className="divide-y divide-zinc-50 max-h-72 overflow-y-auto">
            {report.absent_participants.map((a, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`absent-row-${i}`}>
                <div className="min-w-0">
                  <p className="text-[13.5px] font-semibold text-zinc-800 truncate">{a.name || "—"}</p>
                  <p className="text-[11.5px] text-zinc-400 truncate">{a.invite_name}{a.invite_name && a.table ? " · " : ""}{a.table ? `Mesa: ${a.table}` : ""}</p>
                </div>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-50 text-rose-600 text-[11px] font-medium flex-shrink-0">
                  <UserX size={12} /> Ausente
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Lista de Check-in */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-[13px] font-semibold text-zinc-900">Lista de Check-in</h3>
            {filterStatus !== 'all' && (
              <button
                onClick={()=>setFilterStatus('all')}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-medium bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors"
                data-testid="clear-filter-btn"
              >
                <X size={10} /> Limpar filtro
              </button>
            )}
          </div>
          {lastUpdate && (
            <span className="text-[11px] text-zinc-400">
              Atualizado: {lastUpdate.toLocaleTimeString('pt-BR')}
            </span>
          )}
        </div>

        {filteredList.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="mx-auto text-zinc-300 mb-4" size={36} />
            <h3 className="text-[14px] font-semibold text-zinc-900 mb-1">
              {filterStatus === 'present' ? 'Nenhum convidado fez check-in ainda' :
               filterStatus === 'waiting' ? 'Todos os confirmados já chegaram' :
               filterStatus === 'confirmed' ? 'Nenhum convidado confirmado' :
               'Nenhum convidado confirmado'}
            </h3>
            <p className="text-[13px] text-zinc-500">
              {filterStatus !== 'all' ? 'Tente outro filtro ou limpe o filtro atual' : 'Aguardando confirmações de presença'}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table (md+) */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-100">
                    <th className="text-left p-4 text-[10px] font-medium text-zinc-400 uppercase tracking-wider">Convidado</th>
                    <th className="text-left p-4 text-[10px] font-medium text-zinc-400 uppercase tracking-wider">Pessoas</th>
                    <th className="text-left p-4 text-[10px] font-medium text-zinc-400 uppercase tracking-wider">Mesa</th>
                    <th className="text-left p-4 text-[10px] font-medium text-zinc-400 uppercase tracking-wider">Status</th>
                    <th className="text-left p-4 text-[10px] font-medium text-zinc-400 uppercase tracking-wider">Hora</th>
                    <th className="text-right p-4 text-[10px] font-medium text-zinc-400 uppercase tracking-wider">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map((invite) => {
                    const isPresent = invite.presence_status === "presente";
                    const isConfirmed = invite.rsvp_status === "confirmado";
                    const statusDot = isPresent ? 'bg-emerald-500' : 'bg-amber-500';
                    const statusLabel = isPresent ? 'Presente' : 'Aguardando chegada';
                    const adults = invite.adults_count || 0;
                    const children = invite.children_count || 0;
                    const babies = invite.babies_count || 0;
                    const total = invite.total_people || (adults + children + babies);
                    const peopleLabel = total === 1 ? '1 pessoa' : `${total} pessoas`;
                    const breakdown = [];
                    if (adults > 0) breakdown.push(`${adults} ${adults === 1 ? 'adulto' : 'adultos'}`);
                    if (children > 0) breakdown.push(`${children} ${children === 1 ? 'criança' : 'crianças'}`);
                    if (babies > 0) breakdown.push(`${babies} ${babies === 1 ? 'bebê' : 'bebês'}`);
                    let breakdownStr = '';
                    if (breakdown.length === 1) breakdownStr = breakdown[0];
                    else if (breakdown.length === 2) breakdownStr = breakdown.join(' e ');
                    else if (breakdown.length === 3) breakdownStr = `${breakdown[0]}, ${breakdown[1]} e ${breakdown[2]}`;
                    const peopleEntered = invite.people_entered || 0;
                    const remaining = total - peopleEntered;
                    const canCheckin = isConfirmed && remaining > 0 && invite.rsvp_token;
                    return (
                      <tr
                        key={invite.id}
                        className={`border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50 transition-colors ${isPresent ? 'bg-emerald-50/30' : ''}`}
                      >
                        <td className="p-4"><p className="text-[13.5px] font-medium text-zinc-900">{invite.name}</p></td>
                        <td className="p-4">
                          <p className="text-[13px] font-medium text-zinc-900">{peopleLabel}</p>
                          {breakdownStr && <p className="text-[11px] text-zinc-400 mt-0.5">({breakdownStr})</p>}
                        </td>
                        <td className="p-4">
                          {invite.mesa ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11.5px] font-medium text-zinc-700 bg-zinc-100">{invite.mesa}</span>
                          ) : (
                            <span className="text-[11.5px] text-zinc-300 italic">Sem mesa definida</span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-zinc-700">
                            <span className={`w-1.5 h-1.5 rounded-full ${statusDot}`} />
                            {statusLabel}
                          </span>
                          {peopleEntered > 0 && peopleEntered < total && (
                            <p className="text-[10.5px] text-zinc-400 mt-0.5">{peopleEntered}/{total} entraram</p>
                          )}
                        </td>
                        <td className="p-4">
                          {invite.checkin_at ? (
                            <span className="text-[12.5px] font-medium text-emerald-600 font-mono">
                              {new Date(invite.checkin_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          ) : (
                            <span className="text-[12.5px] text-zinc-300">—</span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          {canCheckin ? (
                            <button
                              onClick={()=>setManualCheckin({ invite, peopleEntering: Math.min(remaining, 1) })}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-zinc-900 text-white hover:bg-zinc-800 active:scale-95 transition-all"
                              data-testid={`manual-checkin-btn-${invite.id}`}
                              title="Fazer check-in manual (caso QR Code falhe)"
                            >
                              <UserCheck size={13} />
                              Check-in
                            </button>
                          ) : isPresent && remaining === 0 ? (
                            <span className="text-[11px] text-zinc-400">Concluído</span>
                          ) : (
                            <span className="text-[11px] text-zinc-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-zinc-100">
              {filteredList.map((invite) => {
                const isPresent = invite.presence_status === "presente";
                const isConfirmed = invite.rsvp_status === "confirmado";
                const statusDot = isPresent ? 'bg-emerald-500' : 'bg-amber-500';
                const statusLabel = isPresent ? 'Presente' : 'Aguardando';
                const adults = invite.adults_count || 0;
                const children = invite.children_count || 0;
                const babies = invite.babies_count || 0;
                const total = invite.total_people || (adults + children + babies);
                const peopleLabel = total === 1 ? '1 pessoa' : `${total} pessoas`;
                const checkinTime = invite.checkin_at ? new Date(invite.checkin_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : null;
                const peopleEntered = invite.people_entered || 0;
                const remaining = total - peopleEntered;
                const canCheckin = isConfirmed && remaining > 0 && invite.rsvp_token;
                return (
                  <div
                    key={invite.id}
                    role={canCheckin ? "button" : undefined}
                    tabIndex={canCheckin ? 0 : undefined}
                    onClick={canCheckin ? () => setManualCheckin({ invite, peopleEntering: Math.min(remaining, 1) }) : undefined}
                    onKeyDown={canCheckin ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setManualCheckin({ invite, peopleEntering: Math.min(remaining, 1) }); } } : undefined}
                    className={`px-4 py-3.5 transition-colors ${isPresent ? 'bg-emerald-50/40' : ''} ${canCheckin ? 'cursor-pointer active:bg-zinc-50' : ''}`}
                    data-testid={`checkin-row-mobile-${invite.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-[13px] font-bold text-white ${isPresent ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                        {(invite.name || '?').slice(0, 1).toUpperCase()}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14.5px] font-semibold text-zinc-900 leading-tight break-words">{invite.name}</p>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${isPresent ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${statusDot}`} />
                            {statusLabel}
                          </span>
                          <span className="text-[11.5px] text-zinc-500">{peopleLabel}</span>
                          {invite.mesa && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10.5px] font-medium text-zinc-700 bg-zinc-100">Mesa {invite.mesa}</span>
                          )}
                        </div>
                        {checkinTime && (
                          <p className="text-[11.5px] text-emerald-600 font-mono mt-1">Chegou às {checkinTime}</p>
                        )}
                        {peopleEntered > 0 && peopleEntered < total && (
                          <p className="text-[11.5px] text-zinc-500 mt-0.5">{peopleEntered} de {total} já entraram</p>
                        )}
                      </div>
                      {/* Ação compacta à direita */}
                      {canCheckin ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); setManualCheckin({ invite, peopleEntering: Math.min(remaining, 1) }); }}
                          className="flex-shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-zinc-900 text-white text-[12.5px] font-semibold active:scale-[0.97] transition-transform"
                          data-testid={`manual-checkin-btn-mobile-${invite.id}`}
                          title="Fazer check-in manual"
                        >
                          <UserCheck size={14} />
                          Check-in
                        </button>
                      ) : isPresent ? (
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 inline-flex items-center justify-center" title="Presente">
                          <Check size={16} />
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Card>

      {/* ═══ MODAL DE CHECK-IN MANUAL (fallback para falha de QR Code) ═══ */}
      {manualCheckin && (
        <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4" onClick={()=>!submittingCheckin && setManualCheckin(null)}>
          <div
            className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
            onClick={e=>e.stopPropagation()}
            data-testid="manual-checkin-modal"
          >
            <div className="p-5 sm:p-6 border-b border-zinc-100">
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600">
                  <UserCheck size={20} />
                </span>
                <div>
                  <h3 className="text-[16px] font-semibold text-zinc-900">Check-in manual</h3>
                  <p className="text-[12px] text-zinc-500">Use caso o QR Code falhe ou o convidado não tenha</p>
                </div>
              </div>
            </div>
            <div className="p-5 sm:p-6 space-y-4">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-400 mb-1">Convidado</p>
                <p className="text-[15px] font-semibold text-zinc-900">{manualCheckin.invite.name}</p>
                {manualCheckin.invite.mesa && (
                  <p className="text-[12px] text-zinc-500 mt-0.5">Mesa: {manualCheckin.invite.mesa}</p>
                )}
              </div>
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-400 mb-2">Quantas pessoas estão entrando agora?</p>
                {(() => {
                  const total = manualCheckin.invite.total_people || 1;
                  const entered = manualCheckin.invite.people_entered || 0;
                  const remaining = total - entered;
                  return (
                    <>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={()=>setManualCheckin(s=>({ ...s, peopleEntering: Math.max(1, s.peopleEntering - 1) }))}
                          disabled={manualCheckin.peopleEntering <= 1 || submittingCheckin}
                          className="w-10 h-10 rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-bold"
                          data-testid="manual-checkin-decrement"
                        >
                          −
                        </button>
                        <div className="flex-1 text-center py-2 rounded-lg border border-zinc-200 bg-zinc-50">
                          <span className="text-[20px] font-bold text-zinc-900">{manualCheckin.peopleEntering}</span>
                          <span className="text-[12px] text-zinc-500 ml-1">/ {remaining} restante{remaining !== 1 ? 's' : ''}</span>
                        </div>
                        <button
                          onClick={()=>setManualCheckin(s=>({ ...s, peopleEntering: Math.min(remaining, s.peopleEntering + 1) }))}
                          disabled={manualCheckin.peopleEntering >= remaining || submittingCheckin}
                          className="w-10 h-10 rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-bold"
                          data-testid="manual-checkin-increment"
                        >
                          +
                        </button>
                      </div>
                      <p className="text-[11px] text-zinc-400 mt-2">Convite permite até {total} pessoa{total !== 1 ? 's' : ''} · {entered} já entrou{entered === 1 ? '' : 'aram'}</p>
                    </>
                  );
                })()}
              </div>
            </div>
            <div className="p-5 sm:p-6 pt-0 flex gap-2">
              <button
                onClick={()=>setManualCheckin(null)}
                disabled={submittingCheckin}
                className="flex-1 py-3 rounded-xl text-[14px] font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 disabled:opacity-50 transition-colors"
                data-testid="manual-checkin-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={()=>submitManualCheckin(manualCheckin.invite, manualCheckin.peopleEntering)}
                disabled={submittingCheckin}
                className="flex-1 py-3 rounded-xl text-[14px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                data-testid="manual-checkin-confirm"
              >
                {submittingCheckin ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// Check-in Reception Page - Interface para Recepcionista com Scanner QR (COM WEBSOCKET)
function CheckinReceptionPage() {
  const { wedding } = useWedding();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [lastCheckin, setLastCheckin] = useState(null);
  const [feedbackType, setFeedbackType] = useState(null); // 'success' | 'warning' | 'error'
  const [stats, setStats] = useState(null);
  const [checkinList, setCheckinList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  // Check-in inteligente por participante (modal de confirmação)
  const [previewInvite, setPreviewInvite] = useState(null);
  const [previewPresences, setPreviewPresences] = useState({});
  const [confirming, setConfirming] = useState(false);
  const searchInputRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);
  const feedbackTimeoutRef = useRef(null);

  // Callback quando receber evento de check-in via WebSocket/SSE
  const handleCheckinEvent = useCallback((data) => {
    // Limpar timeout anterior
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }

    // Definir feedback baseado no tipo de evento
    if (data.type === 'success') {
      setLastCheckin({
        status: "LIBERADO",
        success: true,
        message: `${data.guest_name} - CHECK-IN REALIZADO ✅`,
        invite_name: data.guest_name,
        total_people: data.total_people,
        checkin_time: data.checked_at
      });
      setFeedbackType('success');
      
      // Som de sucesso
      playBeep(800, 0.15);
      
    } else if (data.type === 'already_checked') {
      setLastCheckin({
        status: "JÁ REGISTRADO",
        success: false,
        message: `${data.guest_name} já fez check-in anteriormente`,
        invite_name: data.guest_name,
        checkin_time: data.checked_at
      });
      setFeedbackType('warning');
      
      // Som de aviso
      playBeep(400, 0.3);
      
    } else if (data.type === 'not_allowed') {
      setLastCheckin({
        status: "BLOQUEADO",
        success: false,
        message: data.message || `Entrada não permitida: ${data.reason}`,
        reason: data.reason
      });
      setFeedbackType('error');
      
      // Som de erro
      playBeep(200, 0.5);
    }

    // Limpar feedback após 5 segundos
    feedbackTimeoutRef.current = setTimeout(() => {
      setFeedbackType(null);
    }, 5000);
  }, []);

  // Callback quando receber atualização de stats via WebSocket/SSE
  const handleStatsUpdate = useCallback((data) => {
    if (data.stats) {
      setStats(data.stats);
    }
    if (data.checkin_list) {
      setCheckinList(data.checkin_list);
    }
  }, []);

  // Conectar WebSocket/SSE para atualizações em tempo real
  const { isConnected, connectionType, sendCheckinScan } = useCheckinWebSocket(
    wedding?.id,
    handleCheckinEvent,
    handleStatsUpdate
  );

  // Função para tocar beep
  const playBeep = (frequency, duration) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.connect(audioContext.destination);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + duration);
    } catch (e) {}
  };

  // Buscar estatísticas ao carregar
  const fetchStats = useCallback(async () => {
    if (!wedding?.id) return;
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/checkin-live`);
      setStats(res.data.stats);
      setCheckinList(res.data.checkin_list || []);
    } catch (err) {
      console.error("Erro ao buscar stats:", err);
    }
  }, [wedding?.id]);

  // Carregar dados iniciais
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ⚡ POLLING AUTOMÁTICO: Atualizar a cada 3 segundos
  useEffect(() => {
    if (!wedding?.id) return;
    
    const interval = setInterval(() => {
      fetchStats();
    }, 3000);
    
    return () => clearInterval(interval);
  }, [wedding?.id, fetchStats]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
      }
    };
  }, []);

  // Extrai o token de uma URL de QR (/checkin/TOKEN, /rsvp/TOKEN ou token puro)
  const extractToken = (raw) => {
    let t = (raw || "").trim();
    if (t.includes("/")) t = t.split("?")[0].replace(/\/+$/, "").split("/").pop();
    return t.toUpperCase();
  };

  // Ler QR → CONSULTA dinâmica (lookup) e abre modal de confirmação por participante.
  const handleCheckinByToken = async (raw) => {
    if (!wedding?.id) return;
    const cleanToken = extractToken(raw);
    if (!cleanToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/checkin/lookup/${cleanToken}`);
      const data = res.data;
      if (data.status === "BLOQUEADO") {
        playBeep(200, 0.5);
        setLastCheckin({ status: "BLOQUEADO", success: false, message: data.message, invite_name: data.invite_name });
        setFeedbackType("error");
        setSearchTerm(""); setSearchResults([]);
        if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
        feedbackTimeoutRef.current = setTimeout(() => setFeedbackType(null), 5000);
        return;
      }
      const initial = {};
      (data.participants || []).forEach((p) => { initial[p.index] = p.present; });
      setPreviewPresences(initial);
      setPreviewInvite(data);
      setSearchTerm(""); setSearchResults([]);
      playBeep(600, 0.12);
    } catch (err) {
      playBeep(200, 0.5);
      setLastCheckin({ status: "BLOQUEADO", success: false, message: err.response?.data?.detail || "Convite não encontrado neste evento." });
      setFeedbackType("error");
      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => setFeedbackType(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const togglePresence = (index) => {
    setPreviewPresences((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  // Confirma o check-in com a lista de presentes/ausentes selecionada
  const confirmPreviewCheckin = async () => {
    if (!previewInvite || !wedding?.id) return;
    setConfirming(true);
    try {
      const presences = (previewInvite.participants || []).map((p) => ({
        index: p.index,
        present: !!previewPresences[p.index],
      }));
      const res = await axios.post(`${API}/weddings/${wedding.id}/checkin/confirm`, {
        token: previewInvite.rsvp_token,
        presences,
        operator_name: "Recepção",
      });
      const data = res.data;
      if (data.status === "LIBERADO") {
        playBeep(800, 0.15);
        const absentTxt = (data.absent_names || []).length ? ` · ${data.absent_names.length} ausente(s)` : "";
        setLastCheckin({
          status: "LIBERADO", success: true,
          message: `${data.invite_name} — ${data.present_count} presente(s)${absentTxt}`,
          invite_name: data.invite_name, mesa: data.mesa,
        });
        setFeedbackType("success");
      } else {
        playBeep(400, 0.3);
        setLastCheckin({ status: "JÁ REGISTRADO", success: false, message: data.message, invite_name: data.invite_name });
        setFeedbackType("warning");
      }
      setPreviewInvite(null);
      setPreviewPresences({});
      fetchStats();
    } catch (err) {
      playBeep(200, 0.5);
      toast.error(err.response?.data?.detail || "Erro ao confirmar check-in");
    } finally {
      setConfirming(false);
      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => setFeedbackType(null), 5000);
    }
  };

  // Iniciar scanner de câmera
  const startCamera = async () => {
    setCameraError(null);
    setCameraActive(true);
    
    // Aguardar o container ser renderizado
    setTimeout(async () => {
      try {
        const html5QrCode = new Html5Qrcode("qr-reader");
        html5QrCodeRef.current = html5QrCode;
        
        await html5QrCode.start(
          { facingMode: "environment" }, // Câmera traseira
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1
          },
          (decodedText) => {
            // QR Code lido com sucesso
            handleCheckinByToken(decodedText);
          },
          (errorMessage) => {
            // Erro de leitura (ignorar, continua tentando)
          }
        );
      } catch (err) {
        console.error("Erro ao iniciar câmera:", err);
        setCameraError("Não foi possível acessar a câmera. Verifique as permissões.");
        setCameraActive(false);
      }
    }, 100);
  };

  // Parar scanner de câmera
  const stopCamera = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (e) {}
    }
    setCameraActive(false);
  };

  // Buscar por nome
  const handleSearch = async () => {
    if (!searchTerm.trim() || !wedding?.id) return;
    
    // Verificar se é um token QR Code (8 caracteres alfanuméricos)
    if (/^[A-Z0-9]{8}$/i.test(searchTerm.trim())) {
      handleCheckinByToken(searchTerm.trim());
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/weddings/${wedding.id}/checkin-by-name?name=${encodeURIComponent(searchTerm)}`);
      
      if (res.data.status === "LIBERADO") {
        setLastCheckin(res.data);
        fetchStats();
        setSearchTerm("");
        setSearchResults([]);
      } else if (res.data.matches && res.data.matches.length > 0) {
        setSearchResults(res.data.matches);
        setLastCheckin(null);
      } else {
        setLastCheckin(res.data);
        setSearchResults([]);
      }
    } catch (err) {
      setLastCheckin({
        status: "BLOQUEADO",
        success: false,
        message: err.response?.data?.detail || "Erro ao buscar"
      });
    } finally {
      setLoading(false);
    }
  };

  // Selecionar um resultado e fazer check-in
  const handleSelectResult = (result) => {
    if (result.token && result.can_checkin) {
      handleCheckinByToken(result.token);
    }
  };

  // Determinar cor do status (VERDE/AMARELO/VERMELHO conforme spec)
  const getStatusColor = (status) => {
    switch (status) {
      case "LIBERADO": return "bg-emerald-500";       // ✅ Verde - Sucesso
      case "JÁ REGISTRADO": return "bg-yellow-500";   // ⚠️ Amarelo - Já fez check-in
      case "BLOQUEADO": return "bg-red-500";          // ❌ Vermelho - Não permitido
      case "ALERTA": return "bg-orange-500";
      default: return "bg-slate-500";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "LIBERADO": return <Check size={48} className="text-white" />;
      case "JÁ REGISTRADO": return <AlertCircle size={48} className="text-white" />;
      case "BLOQUEADO": return <X size={48} className="text-white" />;
      case "ALERTA": return <AlertCircle size={48} className="text-white" />;
      default: return <User size={48} className="text-white" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-4">
      {/* Feedback visual grande (verde/amarelo/vermelho) quando há check-in */}
      {feedbackType && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
          feedbackType === 'success' ? 'bg-emerald-500/95' :
          feedbackType === 'warning' ? 'bg-yellow-500/95' :
          'bg-red-500/95'
        }`} onClick={() => setFeedbackType(null)}>
          <div className="text-center p-8 max-w-lg">
            <div className="mb-6">
              {feedbackType === 'success' && <Check size={120} className="mx-auto animate-bounce" />}
              {feedbackType === 'warning' && <AlertCircle size={120} className="mx-auto animate-pulse" />}
              {feedbackType === 'error' && <X size={120} className="mx-auto animate-shake" />}
            </div>
            <h1 className="text-4xl font-bold mb-4">
              {lastCheckin?.status || (feedbackType === 'success' ? 'LIBERADO' : 'BLOQUEADO')}
            </h1>
            <p className="text-2xl mb-4">{lastCheckin?.invite_name || lastCheckin?.message}</p>
            {lastCheckin?.total_people && (
              <p className="text-xl opacity-80">{lastCheckin.total_people} pessoa(s)</p>
            )}
            <p className="mt-6 text-sm opacity-60">Toque para fechar</p>
          </div>
        </div>
      )}

      {/* Header com estatísticas - STATUS CORRETOS */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">Check-in em tempo real</h1>
            <p className="text-slate-400">Controle de entrada do evento</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Indicador WebSocket/SSE */}
            <div className={`px-3 py-1 rounded-full text-xs flex items-center gap-2 ${isConnected ? 'bg-emerald-500/30 text-emerald-400' : 'bg-red-500/30 text-red-400'}`}>
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`}></div>
              {isConnected ? `⚡ Tempo Real${connectionType === 'websocket' ? ' (WS)' : ' (SSE)'}` : 'Reconectando...'}
            </div>
            {/* Presentes - já fizeram check-in */}
            <div className="text-center px-4 py-2 bg-emerald-500/20 rounded-lg">
              <p className="text-2xl font-bold text-emerald-400">{stats?.total_checked_in || 0}</p>
              <p className="text-xs text-emerald-300">✅ Presentes</p>
            </div>
            {/* Aguardando - confirmados que ainda não chegaram */}
            <div className="text-center px-4 py-2 bg-orange-500/20 rounded-lg">
              <p className="text-2xl font-bold text-orange-400">{stats?.total_waiting || 0}</p>
              <p className="text-xs text-orange-300">⏳ Aguardando chegada</p>
            </div>
            {/* Confirmados - total macro */}
            <div className="text-center px-4 py-2 bg-blue-500/20 rounded-lg">
              <p className="text-2xl font-bold text-blue-400">{stats?.total_confirmed || 0}</p>
              <p className="text-xs text-blue-300">👥 Confirmados</p>
            </div>
          </div>
        </div>

        {/* Barra de progresso */}
        <div className="w-full bg-slate-700 rounded-full h-3 mb-2">
          <div 
            className="bg-emerald-500 h-3 rounded-full transition-all duration-500"
            style={{ width: `${stats?.arrival_percentage || 0}%` }}
          />
        </div>
        <p className="text-center text-slate-400 text-sm">
          ✅ {stats?.total_checked_in || 0} Presentes | ⏳ {stats?.total_waiting || 0} Aguardando chegada | 👥 {stats?.total_confirmed || 0} Confirmados
        </p>
      </div>

      {/* Botão Abrir Câmera */}
      <div className="max-w-4xl mx-auto mb-6">
        {!cameraActive ? (
          <button
            onClick={startCamera}
            className="w-full py-6 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-2xl font-bold text-xl flex items-center justify-center gap-3 transition-all shadow-lg"
            data-testid="open-camera-btn"
          >
            <Camera size={32} />
            📷 Abrir Câmera
          </button>
        ) : (
          <div className="space-y-4">
            <div className="relative bg-black rounded-2xl overflow-hidden">
              <div id="qr-reader" className="w-full" style={{ minHeight: '300px' }}></div>
              <button
                onClick={stopCamera}
                className="absolute top-4 right-4 p-2 bg-red-500 hover:bg-red-600 rounded-full"
              >
                <X size={24} />
              </button>
            </div>
            <p className="text-center text-slate-400 text-sm">
              Aponte a câmera para o QR Code do convidado
            </p>
          </div>
        )}
        
        {cameraError && (
          <div className="mt-4 p-4 bg-red-500/20 border border-red-500 rounded-xl text-red-400 text-center">
            {cameraError}
          </div>
        )}
      </div>

      {/* Campo de busca (backup) */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="relative">
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Ou digite o nome / código..."
            className="w-full px-6 py-4 text-xl bg-slate-800 border-2 border-slate-600 rounded-2xl focus:border-blue-500 focus:outline-none text-white placeholder-slate-500"
            data-testid="checkin-search-input"
          />
          <button
            onClick={handleSearch}
            disabled={loading}
            className="absolute right-3 top-1/2 -translate-y-1/2 px-6 py-2 bg-blue-500 hover:bg-blue-600 rounded-xl font-semibold disabled:opacity-50"
            data-testid="checkin-search-button"
          >
            {loading ? <Loader2 className="animate-spin" size={24} /> : "Buscar"}
          </button>
        </div>
      </div>

      {/* Resultado do último check-in */}
      {lastCheckin && (
        <div className="max-w-4xl mx-auto mb-6">
          <div className={`p-8 rounded-2xl ${getStatusColor(lastCheckin.status)}`}>
            <div className="flex justify-center mb-4">
              {getStatusIcon(lastCheckin.status)}
            </div>
            <h2 className="text-3xl font-bold mb-2 text-center">{lastCheckin.message || lastCheckin.status}</h2>
            
            {/* Informações do Convite - FORMATO CORRETO */}
            {lastCheckin.status === "LIBERADO" && (
              <div className="mt-6 bg-black/20 rounded-xl p-6 text-left">
                <p className="text-xl font-bold mb-2">Convite: {lastCheckin.invite_name}</p>
                <p className="text-lg mb-4">Quantidade: {lastCheckin.people_allowed} pessoa(s)</p>
                
                {/* Lista de Convidados */}
                {lastCheckin.guests_list && lastCheckin.guests_list.length > 0 && (
                  <div className="mt-4">
                    <p className="font-semibold mb-2">Convidados:</p>
                    <ul className="space-y-1">
                      {lastCheckin.guests_list.map((guest, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <Check size={16} />
                          <span>{guest}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Mesa */}
                {lastCheckin.mesa && (
                  <div className="mt-4 p-3 bg-white/10 rounded-lg">
                    <p className="font-semibold">🪑 Mesa: {lastCheckin.mesa}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Mensagem de bloqueio */}
            {lastCheckin.status === "BLOQUEADO" && lastCheckin.invite_name && (
              <div className="mt-4 text-center">
                <p className="text-lg">Convite: {lastCheckin.invite_name}</p>
                {lastCheckin.reason === "ALREADY_COMPLETE" && (
                  <p className="mt-2 text-sm opacity-80">Este convite já entrou no evento.</p>
                )}
              </div>
            )}
            
            {/* Alerta */}
            {lastCheckin.status === "ALERTA" && (
              <div className="mt-4 p-4 bg-black/30 rounded-xl text-center">
                <p className="font-semibold">⚠️ {lastCheckin.action_required}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lista de resultados da busca */}
      {searchResults.length > 0 && (
        <div className="max-w-4xl mx-auto">
          <h3 className="text-lg font-semibold mb-3 text-slate-300">Selecione o convidado:</h3>
          <div className="space-y-2">
            {searchResults.map((result) => (
              <button
                key={result.id}
                onClick={() => handleSelectResult(result)}
                disabled={!result.can_checkin}
                className={`w-full p-4 rounded-xl text-left flex items-center justify-between ${
                  result.can_checkin 
                    ? 'bg-slate-800 hover:bg-slate-700 cursor-pointer' 
                    : 'bg-slate-800/50 opacity-50 cursor-not-allowed'
                }`}
                data-testid={`search-result-${result.id}`}
              >
                <div>
                  <p className="font-semibold text-lg">{result.name}</p>
                  <p className="text-sm text-slate-400">
                    {result.people_entered}/{result.people_allowed} pessoas • 
                    {result.can_checkin ? " Disponível para check-in" : " Check-in não disponível"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {result.presence_status === "presente" ? (
                    <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
                      ✓ Presente
                    </span>
                  ) : result.status === "confirmado" ? (
                    <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">
                      Confirmado
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-slate-500/20 text-slate-400 rounded-full text-sm">
                      {result.status}
                    </span>
                  )}
                  {result.can_checkin && <ChevronRight size={20} />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Atalhos */}
      <div className="max-w-4xl mx-auto mt-8">
        <div className="flex justify-center gap-4">
          <button
            onClick={() => {
              setSearchTerm("");
              setSearchResults([]);
              setLastCheckin(null);
            }}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl"
          >
            Limpar
          </button>
          <button
            onClick={fetchStats}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl flex items-center gap-2"
          >
            <RefreshCw size={18} /> Atualizar
          </button>
        </div>
      </div>

      {/* MODAL — Check-in inteligente por participante */}
      {previewInvite && (
        <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" data-testid="checkin-confirm-modal">
          <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
            <div className="px-5 pt-5 pb-4 border-b border-zinc-100">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-violet-500">Convite</p>
                  <h3 className="text-xl font-bold text-zinc-900 leading-tight" data-testid="checkin-invite-name">{previewInvite.invite_name}</h3>
                </div>
                <button onClick={() => { setPreviewInvite(null); setPreviewPresences({}); }} className="p-2 rounded-full hover:bg-zinc-100 text-zinc-400" data-testid="checkin-modal-close">
                  <X size={20} />
                </button>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-violet-50 text-violet-700 text-[12.5px] font-semibold" data-testid="checkin-table-badge">
                  <MapPin size={13} /> {previewInvite.table_name ? `Mesa: ${previewInvite.table_name}` : "Sem mesa"}
                </span>
                <span className="inline-flex items-center gap-1.5 text-[12.5px] text-zinc-500">
                  <Users size={13} /> {previewInvite.participants?.length || 0} confirmado(s)
                </span>
              </div>
            </div>

            {previewInvite.already_checked_in && (
              <div className="mx-5 mt-4 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-[12.5px] text-amber-700 font-medium" data-testid="checkin-already-banner">
                Check-in já realizado. Veja abaixo quem foi registrado.
              </div>
            )}

            <div className="px-5 py-4 max-h-[40vh] overflow-y-auto space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-1">Quem chegou? (desmarque os ausentes)</p>
              {(previewInvite.participants || []).map((p) => {
                const present = !!previewPresences[p.index];
                return (
                  <button
                    key={p.index}
                    disabled={previewInvite.already_checked_in}
                    onClick={() => togglePresence(p.index)}
                    data-testid={`checkin-participant-${p.index}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl border transition-colors text-left ${present ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-zinc-50"} ${previewInvite.already_checked_in ? "opacity-90 cursor-default" : "hover:border-violet-300"}`}
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${present ? "bg-emerald-500 text-white" : "bg-zinc-300 text-white"}`}>
                      {present ? <Check size={15} /> : <X size={14} />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[14px] font-semibold text-zinc-800 truncate">{p.name}</span>
                      <span className={`block text-[11.5px] font-medium ${present ? "text-emerald-600" : "text-zinc-400"}`}>
                        {present ? "Presente" : "Ausente"}{p.type !== "adult" ? ` · ${p.type === "child" ? "criança" : "bebê"}` : ""}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="px-5 py-4 border-t border-zinc-100">
              {previewInvite.already_checked_in ? (
                <button onClick={() => { setPreviewInvite(null); setPreviewPresences({}); }} className="w-full py-3.5 rounded-2xl bg-zinc-900 text-white font-semibold" data-testid="checkin-close-btn">Fechar</button>
              ) : (
                <button onClick={confirmPreviewCheckin} disabled={confirming} className="w-full py-3.5 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="checkin-confirm-btn">
                  {confirming ? <Loader2 className="animate-spin" size={18} /> : <UserCheck size={18} />}
                  Confirmar check-in ({(previewInvite.participants || []).filter((p) => previewPresences[p.index]).length})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


// ==================== PÁGINA SITE DO CASAMENTO ====================
// Página de administração do site público com sistema de templates

function WeddingSitePage() {
  const { wedding, weddings, refreshWeddings } = useWedding();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [siteData, setSiteData] = useState(null);
  const [activeTab, setActiveTab] = useState('configuracao'); // 'configuracao' | 'presentes'
  const [presentes, setPresentes] = useState([]);
  const [uploadingCapa, setUploadingCapa] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    site_enabled: false,
    site_template: 'classic',
    couple_names: '',
    wedding_date: '',
    wedding_location: '',
    story: '',
    message: '',
    cover_image_url: '',
    gallery_images: []
  });
  
  // Gift management
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [editingGift, setEditingGift] = useState(null);
  const [giftForm, setGiftForm] = useState({ titulo: '', valor: '', descricao: '' });

  // File input refs
  const capaInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    if (wedding?.id) {
      fetchSiteData();
      fetchPresentes();
    }
  }, [wedding?.id]);

  // Se não há casamento após carregar, parar o loading
  useEffect(() => {
    if (weddings !== null && weddings.length === 0) {
      setLoading(false);
    }
  }, [weddings]);

  // Timeout de segurança - se após 5 segundos ainda estiver loading, forçar parada
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading && !wedding?.id) {
        setLoading(false);
      }
    }, 5000);
    return () => clearTimeout(timeout);
  }, [loading, wedding?.id]);

  const fetchSiteData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/app/site`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setSiteData(res.data);
        // Preencher formulário com dados existentes
        setFormData({
          site_enabled: res.data.site_enabled || false,
          site_template: res.data.site_template || 'classic',
          couple_names: res.data.site_data?.couple_names || '',
          wedding_date: res.data.site_data?.wedding_date || '',
          wedding_location: res.data.site_data?.wedding_location || '',
          story: res.data.site_data?.story || '',
          message: res.data.site_data?.message || '',
          cover_image_url: res.data.site_data?.cover_image_url || '',
          gallery_images: res.data.site_data?.gallery_images || []
        });
      }
    } catch (err) {
      console.error('Erro ao buscar dados do site:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPresentes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/app/presentes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPresentes(res.data.itens || []);
    } catch (err) {
      console.error('Erro ao buscar presentes:', err);
    }
  };

  // Salvar configurações do site
  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/app/site`, {
        site_enabled: formData.site_enabled,
        site_template: formData.site_template,
        site_data: {
          couple_names: formData.couple_names,
          wedding_date: formData.wedding_date,
          wedding_location: formData.wedding_location,
          story: formData.story,
          message: formData.message,
          cover_image_url: formData.cover_image_url,
          gallery_images: formData.gallery_images
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Site salvo com sucesso!');
      fetchSiteData();
    } catch (err) {
      console.error('Erro ao salvar:', err);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  // Upload imagem de capa
  const handleCapaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingCapa(true);
    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      form.append('file', file);
      
      const res = await axios.post(`${API}/app/site/capa`, form, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, cover_image_url: res.data.imagem_capa_url }));
      // 🔄 Iter 79: Atualiza contexto global p/ refletir capa no sidebar chip + cards
      if (typeof refreshWeddings === 'function') {
        try { await refreshWeddings(); } catch {}
      }
      toast.success('Imagem de capa atualizada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao fazer upload');
    } finally {
      setUploadingCapa(false);
    }
  };

  // Upload imagem para galeria
  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingGallery(true);
    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      form.append('file', file);
      
      const res = await axios.post(`${API}/app/site/gallery`, form, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({ ...prev, gallery_images: res.data.gallery }));
      toast.success('Imagem adicionada à galeria!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao fazer upload');
    } finally {
      setUploadingGallery(false);
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  // Remover imagem da galeria
  const handleRemoveGalleryImage = async (imageUrl) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API}/app/site/gallery`, {
        params: { image_url: imageUrl },
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({ ...prev, gallery_images: res.data.gallery }));
      toast.success('Imagem removida!');
    } catch (err) {
      toast.error('Erro ao remover imagem');
    }
  };

  const copyLink = () => {
    if (siteData?.slug) {
      const url = `${window.location.origin}/casal/${siteData.slug}`;
      navigator.clipboard.writeText(url);
      toast.success('Link copiado!');
    }
  };

  const openSite = () => {
    // Se o site está habilitado, abre a URL pública
    // Se não está habilitado, mostra a aba de preview
    if (formData.site_enabled && siteData?.slug) {
      window.open(`/casal/${siteData.slug}`, '_blank');
    } else {
      setActiveTab('preview');
      toast.info('Ative o site para acessar a página pública');
    }
  };

  const openPreview = () => {
    // Abrir página pública se o site estiver ativo
    if (formData.site_enabled && siteData?.slug) {
      window.open(`/casal/${siteData.slug}`, '_blank');
    } else {
      setActiveTab('preview');
    }
  };

  // Gift CRUD
  const handleSaveGift = async () => {
    if (!giftForm.titulo || !giftForm.valor) {
      toast.error('Preencha título e valor');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const valorCentavos = Math.round(parseFloat(giftForm.valor) * 100);

      if (editingGift) {
        await axios.put(`${API}/app/presentes/${editingGift.id}`, {
          titulo: giftForm.titulo,
          valor: valorCentavos,
          descricao: giftForm.descricao
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Presente atualizado!');
      } else {
        await axios.post(`${API}/app/presentes`, {
          titulo: giftForm.titulo,
          valor: valorCentavos,
          descricao: giftForm.descricao
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Presente adicionado!');
      }

      setShowGiftModal(false);
      setEditingGift(null);
      setGiftForm({ titulo: '', valor: '', descricao: '' });
      fetchPresentes();
    } catch (err) {
      toast.error('Erro ao salvar presente');
    }
  };

  const handleDeleteGift = async (id) => {
    if (!(await confirmDialog({
      title: "Excluir presente",
      message: "Tem certeza que deseja excluir este presente? Esta ação não poderá ser desfeita.",
      confirmLabel: "Excluir",
      variant: "danger",
    }))) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/app/presentes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Presente excluído');
      fetchPresentes();
    } catch (err) {
      toast.error('Erro ao excluir');
    }
  };

  const formatCurrency = (centavos) => {
    return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Loader2 className="animate-spin text-blue-600" size={32} />
        <span className="ml-2 text-slate-500">Carregando...</span>
      </div>
    );
  }

  // Se não há casamento, mostrar mensagem
  if (!wedding?.id) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-[60vh]">
        <Globe className="text-slate-300 mb-4" size={64} />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">Nenhum casamento encontrado</h2>
        <p className="text-slate-500 mb-4">Você precisa criar um casamento primeiro para configurar o site.</p>
        <Button onClick={() => window.location.href = '/onboarding'}>
          Criar Casamento
        </Button>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-5 md:space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center flex-shrink-0">
            <Globe className="text-white" size={22} />
          </div>
          <div className="min-w-0">
            <h1 className="text-[20px] md:text-2xl font-bold text-slate-800 tracking-tight">Site do Casamento</h1>
            <p className="text-[12.5px] md:text-sm text-slate-500 truncate">Personalize o site público do seu casamento</p>
          </div>
        </div>
        
        {/* Link público e botões */}
        <div className="flex flex-col md:flex-row md:items-center gap-2 md:flex-wrap">
          {siteData?.slug && (
            <code className="bg-slate-100 px-3 py-1.5 rounded text-[12px] md:text-sm text-slate-600 truncate max-w-full">
              /casal/{siteData.slug}
            </code>
          )}
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={copyLink} data-testid="copy-link-btn" className="flex-1 md:flex-none">
              <Copy size={16} className="mr-1" /> Copiar
            </Button>
            <Button variant="secondary" size="sm" onClick={openPreview} data-testid="preview-btn" className="flex-1 md:flex-none">
              <Eye size={16} className="mr-1" /> Prévia
            </Button>
            {formData.site_enabled && (
              <Button size="sm" onClick={openSite} data-testid="view-site-btn" className="flex-1 md:flex-none">
                <ExternalLink size={16} className="mr-1" /> Ver Site
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="overflow-x-auto no-scrollbar -mx-3 md:mx-0 px-3 md:px-0">
        <div className="flex gap-2 bg-slate-100 p-1 rounded-lg w-fit min-w-full md:min-w-0">
          <button
            onClick={() => setActiveTab('configuracao')}
            className={`flex-1 md:flex-none px-3 md:px-4 py-2 rounded-md text-[12.5px] md:text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'configuracao' 
                ? 'bg-white shadow text-rose-600' 
                : 'text-slate-600 hover:text-slate-800'
            }`}
            data-testid="tab-config"
          >
            <Settings size={15} className="inline mr-1.5" />
            Configuração
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`flex-1 md:flex-none px-3 md:px-4 py-2 rounded-md text-[12.5px] md:text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'preview' 
                ? 'bg-white shadow text-rose-600' 
                : 'text-slate-600 hover:text-slate-800'
            }`}
            data-testid="tab-preview"
          >
            <Eye size={15} className="inline mr-1.5" />
            Prévia
          </button>
          <button
            onClick={() => setActiveTab('presentes')}
            className={`flex-1 md:flex-none px-3 md:px-4 py-2 rounded-md text-[12.5px] md:text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === 'presentes' 
                ? 'bg-white shadow text-rose-600' 
                : 'text-slate-600 hover:text-slate-800'
            }`}
            data-testid="tab-gifts"
          >
            <Gift size={15} className="inline mr-1.5" />
            Presentes
          </button>
        </div>
      </div>

      {/* Conteúdo da Tab Preview - Mostra o template em tempo real */}
      {activeTab === 'preview' && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye size={18} className="text-amber-600" />
              <span className="text-amber-800 font-medium">Prévia do Template: {TEMPLATES.find(t => t.id === formData.site_template)?.name}</span>
            </div>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Check size={16} className="mr-2" />}
              Salvar Alterações
            </Button>
          </div>
          
          <div className="border border-slate-200 rounded-xl overflow-hidden shadow-lg" style={{ height: '70vh' }}>
            {(() => {
              const templateProps = {
                siteData: {
                  couple_names: formData.couple_names,
                  wedding_date: formData.wedding_date,
                  wedding_location: formData.wedding_location,
                  story: formData.story,
                  message: formData.message,
                  cover_image_url: formData.cover_image_url,
                  gallery_images: formData.gallery_images
                },
                presentes: presentes.filter(p => p.ativo !== false),
                onPresentear: () => toast.info('Pagamentos desabilitados na prévia'),
                formatCurrency
              };

              switch (formData.site_template) {
                case 'modern':
                  return <div className="h-full overflow-auto"><ModernTemplate {...templateProps} /></div>;
                case 'romantic':
                  return <div className="h-full overflow-auto"><RomanticTemplate {...templateProps} /></div>;
                case 'classic':
                default:
                  return <div className="h-full overflow-auto"><ClassicTemplate {...templateProps} /></div>;
              }
            })()}
          </div>
        </div>
      )}

      {/* Conteúdo da Tab Configuração */}
      {activeTab === 'configuracao' && (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Coluna Principal - Formulário */}
          <div className="lg:col-span-2 space-y-6">
            {/* Toggle Site Ativo */}
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800">Site Público</h3>
                  <p className="text-sm text-slate-500">Ative para tornar seu site visível para todos</p>
                </div>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, site_enabled: !prev.site_enabled }))}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                    formData.site_enabled ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                  data-testid="toggle-site-enabled"
                >
                  <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow ${
                    formData.site_enabled ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              {formData.site_enabled ? (
                <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm text-emerald-700 flex items-center gap-2">
                    <Check size={16} />
                    Seu site está ativo e pode ser acessado em: <strong>/casal/{siteData?.slug}</strong>
                  </p>
                </div>
              ) : (
                <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <AlertCircle size={16} />
                    Seu site está <strong>desativado</strong>. Ative o toggle acima para publicá-lo.
                  </p>
                </div>
              )}
            </Card>

            {/* Seletor de Template */}
            <Card className="p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Escolha o Template</h3>
              <div className="grid gap-4 md:grid-cols-3">
                {TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setFormData(prev => ({ ...prev, site_template: template.id }))}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      formData.site_template === template.id 
                        ? 'border-rose-500 bg-rose-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`template-${template.id}`}
                  >
                    <div className="flex gap-1 mb-3">
                      {template.colors.map((color, i) => (
                        <div key={i} className="w-5 h-5 rounded-full border border-slate-200" style={{ backgroundColor: color }} />
                      ))}
                    </div>
                    <h4 className="font-medium text-slate-800">{template.name}</h4>
                    <p className="text-xs text-slate-500 mt-1">{template.description}</p>
                  </button>
                ))}
              </div>
            </Card>

            {/* Dados do Casal */}
            <Card className="p-6 space-y-4">
              <h3 className="font-semibold text-slate-800">Informações do Casal</h3>
              
              <Input
                label="Nomes do Casal"
                placeholder="Maria & João"
                value={formData.couple_names}
                onChange={(e) => setFormData(prev => ({ ...prev, couple_names: e.target.value }))}
                data-testid="input-couple-names"
              />
              
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="Data do Casamento"
                  type="date"
                  value={formData.wedding_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, wedding_date: e.target.value }))}
                  data-testid="input-wedding-date"
                />
                <Input
                  label="Local"
                  placeholder="São Paulo, SP"
                  value={formData.wedding_location}
                  onChange={(e) => setFormData(prev => ({ ...prev, wedding_location: e.target.value }))}
                  data-testid="input-wedding-location"
                />
              </div>
            </Card>

            {/* Mensagem e História */}
            <Card className="p-6 space-y-4">
              <h3 className="font-semibold text-slate-800">Textos do Site</h3>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Mensagem aos Convidados
                </label>
                <textarea
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 resize-none"
                  rows={3}
                  placeholder="Uma mensagem especial para seus convidados..."
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  data-testid="input-message"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nossa História
                </label>
                <textarea
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 resize-none"
                  rows={5}
                  placeholder="Conte um pouco da história de vocês..."
                  value={formData.story}
                  onChange={(e) => setFormData(prev => ({ ...prev, story: e.target.value }))}
                  data-testid="input-story"
                />
              </div>
            </Card>

            {/* Galeria de Fotos */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-800">Galeria de Fotos</h3>
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={uploadingGallery}
                  data-testid="add-gallery-btn"
                >
                  {uploadingGallery ? (
                    <Loader2 className="animate-spin mr-2" size={16} />
                  ) : (
                    <ImagePlus size={16} className="mr-2" />
                  )}
                  Adicionar Foto
                </Button>
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleGalleryUpload}
                />
              </div>
              
              {formData.gallery_images.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
                  <Image className="mx-auto text-slate-400 mb-3" size={48} />
                  <p className="text-slate-500 font-medium">Nenhuma foto na galeria</p>
                  <p className="text-sm text-slate-400 mt-1">Adicione fotos para criar uma galeria bonita</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {formData.gallery_images.map((img, index) => (
                    <div key={index} className="relative group aspect-square rounded-lg overflow-hidden">
                      <img src={img} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => handleRemoveGalleryImage(img)}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-gallery-${index}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Botão Salvar */}
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full md:w-auto bg-gradient-to-r from-rose-500 to-pink-600"
              data-testid="save-site-btn"
            >
              {saving ? (
                <><Loader2 className="animate-spin mr-2" size={18} />Salvando...</>
              ) : (
                <><Check size={18} className="mr-2" />Salvar Alterações</>
              )}
            </Button>
          </div>

          {/* Coluna Lateral - Imagem de Capa */}
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="font-semibold text-slate-800 mb-4">Imagem de Capa</h3>
              
              <div 
                className="relative aspect-[4/3] rounded-lg overflow-hidden bg-gradient-to-br from-rose-100 to-pink-100 border-2 border-dashed border-rose-300 cursor-pointer hover:border-rose-400 transition-colors"
                onClick={() => capaInputRef.current?.click()}
              >
                {formData.cover_image_url ? (
                  <>
                    <img 
                      src={formData.cover_image_url} 
                      alt="Capa" 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                      <p className="text-white font-medium">Clique para trocar</p>
                    </div>
                  </>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {uploadingCapa ? (
                      <Loader2 className="animate-spin text-rose-500 mb-2" size={32} />
                    ) : (
                      <Upload className="text-rose-400 mb-2" size={32} />
                    )}
                    <p className="text-sm text-rose-500 font-medium">
                      {uploadingCapa ? 'Enviando...' : 'Clique para adicionar'}
                    </p>
                  </div>
                )}
              </div>
              <input
                ref={capaInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleCapaUpload}
              />
              <p className="text-xs text-slate-500 mt-2">
                Recomendado: 1920x1080px (JPG, PNG ou WebP)
              </p>
            </Card>

            {/* Preview rápido */}
            <Card className="p-6">
              <h3 className="font-semibold text-slate-800 mb-3">Prévia Rápida</h3>
              <div className="text-sm text-slate-600 space-y-2">
                <p><strong>Template:</strong> {TEMPLATES.find(t => t.id === formData.site_template)?.name}</p>
                <p><strong>Nomes:</strong> {formData.couple_names || '(não definido)'}</p>
                <p><strong>Data:</strong> {formData.wedding_date ? new Date(formData.wedding_date).toLocaleDateString('pt-BR') : '(não definida)'}</p>
                <p><strong>Fotos:</strong> {formData.gallery_images.length} na galeria</p>
                <p><strong>Presentes:</strong> {presentes.filter(p => p.ativo !== false).length} cadastrados</p>
              </div>
              <Button 
                variant="secondary" 
                className="w-full mt-4" 
                onClick={openPreview}
              >
                <Eye size={16} className="mr-2" />
                Ver Prévia do Site
              </Button>
            </Card>
          </div>
        </div>
      )}

      {/* Conteúdo da Tab Presentes */}
      {activeTab === 'presentes' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Gift className="text-amber-600" size={20} />
              </div>
              <div>
                <h2 className="font-semibold text-slate-800">Lista de Presentes</h2>
                <p className="text-sm text-slate-500">Presentes que aparecem no site público</p>
              </div>
            </div>
            <Button onClick={() => { setEditingGift(null); setGiftForm({ titulo: '', valor: '', descricao: '' }); setShowGiftModal(true); }} data-testid="add-gift-btn">
              <Plus size={18} className="mr-2" />
              Adicionar
            </Button>
          </div>

          {presentes.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
              <Gift className="mx-auto text-slate-400 mb-3" size={48} />
              <p className="text-slate-500 font-medium">Nenhum presente cadastrado</p>
              <p className="text-sm text-slate-400 mt-1">Os presentes aparecerão na seção de lista de presentes do site</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {presentes.filter(p => p.ativo !== false).map((presente) => (
                <div 
                  key={presente.id}
                  className="p-4 bg-gradient-to-br from-white to-slate-50 rounded-xl border border-slate-200 hover:shadow-md transition-shadow"
                  data-testid={`gift-item-${presente.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-slate-800">{presente.titulo}</h4>
                      {presente.descricao && (
                        <p className="text-sm text-slate-500 mt-1">{presente.descricao}</p>
                      )}
                      <p className="text-xl font-bold text-emerald-600 mt-2">
                        {formatCurrency(presente.valor)}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button 
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        onClick={() => {
                          setEditingGift(presente);
                          setGiftForm({
                            titulo: presente.titulo,
                            valor: (presente.valor / 100).toString(),
                            descricao: presente.descricao || ''
                          });
                          setShowGiftModal(true);
                        }}
                        data-testid={`edit-gift-${presente.id}`}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        onClick={() => handleDeleteGift(presente.id)}
                        data-testid={`delete-gift-${presente.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Modal de Presente */}
      <Modal 
        isOpen={showGiftModal} 
        onClose={() => { setShowGiftModal(false); setEditingGift(null); }}
        title={editingGift ? "Editar Presente" : "Adicionar Presente"}
      >
        <div className="space-y-4">
          <Input
            label="Título do Presente *"
            placeholder="Ex: Jogo de Panelas, Lua de Mel..."
            value={giftForm.titulo}
            onChange={(e) => setGiftForm({...giftForm, titulo: e.target.value})}
            data-testid="gift-title-input"
          />

          <MoneyInput
            label="Valor *"
            value={parseFloat(giftForm.valor) || 0}
            onChange={(val) => setGiftForm({...giftForm, valor: val})}
          />

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Descrição (opcional)
            </label>
            <textarea
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              rows={2}
              placeholder="Uma breve descrição..."
              value={giftForm.descricao}
              onChange={(e) => setGiftForm({...giftForm, descricao: e.target.value})}
              data-testid="gift-description-input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button 
              variant="secondary" 
              className="flex-1"
              onClick={() => { setShowGiftModal(false); setEditingGift(null); }}
            >
              Cancelar
            </Button>
            <Button className="flex-1" onClick={handleSaveGift} data-testid="save-gift-btn">
              {editingGift ? 'Atualizar' : 'Adicionar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}


// Budgets Page - Orçamentos de Fornecedores
function BudgetsPage() {
  const { wedding, eventCategories, fetchEventCategories } = useWedding();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [editQuote, setEditQuote] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [activeTab, setActiveTab] = useState("orcamentos"); // Onda 3 — orcamentos | contratos
  const [form, setForm] = useState({ 
    category: "buffet", 
    vendor_name: "", 
    description: "",
    total_amount: 0, 
    contact_phone: "",
    contact_email: "",
    notes: "" 
  });
  const [closeForm, setCloseForm] = useState({
    generate_expenses: true,
    payment_type: "a_vista",
    installments_count: 1,
    first_due_date: new Date().toISOString().split('T')[0],
    installments: []
  });

  // Category management state
  const [showCatManager, setShowCatManager] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [deleteCatTarget, setDeleteCatTarget] = useState(null);
  const [moveToCatSlug, setMoveToCatSlug] = useState('outros');
  const [catLoading, setCatLoading] = useState(false);

  // Bottom sheet de detalhes do orçamento (Full Pattern — igual Convidados)
  const [detailQuote, setDetailQuote] = useState(null);

  const handleCreateCategory = async () => {
    if (!newCatName.trim()) return;
    setCatLoading(true);
    try {
      await axios.post(`${API}/weddings/${wedding.id}/event-categories`, { name: newCatName.trim() });
      setNewCatName('');
      await fetchEventCategories();
      toast.success("Categoria criada!");
    } catch (err) { toast.error(getErrorMessage(err, "Erro ao criar categoria")); }
    finally { setCatLoading(false); }
  };

  const handleRenameCategory = async (catId) => {
    if (!editingCatName.trim()) return;
    setCatLoading(true);
    try {
      await axios.put(`${API}/weddings/${wedding.id}/event-categories/${catId}`, { name: editingCatName.trim() });
      setEditingCatId(null);
      await fetchEventCategories();
      toast.success("Categoria renomeada!");
    } catch (err) { toast.error(getErrorMessage(err, "Erro")); }
    finally { setCatLoading(false); }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCatTarget) return;
    setCatLoading(true);
    try {
      const hasData = deleteCatTarget.expense_count > 0;
      const url = `${API}/weddings/${wedding.id}/event-categories/${deleteCatTarget.id}${hasData ? `?move_to_slug=${moveToCatSlug}` : ''}`;
      await axios.delete(url);
      setDeleteCatTarget(null);
      await fetchEventCategories();
      toast.success("Categoria excluída!");
    } catch (err) { toast.error(getErrorMessage(err, "Erro ao excluir")); }
    finally { setCatLoading(false); }
  };

  // Usar categorias dinâmicas do evento
  const categories = eventCategories.length > 0 
    ? eventCategories.map(c => ({ value: c.slug, label: c.name }))
    : STANDARD_CATEGORIES;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const fetchQuotes = useCallback(async () => {
    if (!wedding) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/quotes`);
      setQuotes(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [wedding]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editQuote) {
        await axios.put(`${API}/weddings/${wedding.id}/quotes/${editQuote.id}`, form);
        toast.success("Salvo com sucesso!");
      } else {
        await axios.post(`${API}/weddings/${wedding.id}/quotes`, form);
        toast.success("Salvo com sucesso!");
      }
      setShowModal(false);
      setEditQuote(null);
      setForm({ category: "buffet", vendor_name: "", description: "", total_amount: 0, contact_phone: "", contact_email: "", notes: "" });
      fetchQuotes();
    } catch (err) {
      toast.error(getErrorMessage(err, "Ocorreu um erro. Tente novamente."));
    }
  };

  const handleDelete = async (id) => {
    // Encontrar o orçamento para verificar status
    const quote = quotes.find(q => q.id === id);
    
    // Se o contrato está fechado, pedir confirmação especial
    if (quote?.status === "fechado") {
      const confirmed = await confirmDialog({
        title: "Excluir orçamento fechado",
        message: "Este orçamento possui vínculo com despesas e histórico financeiro. Ao excluir, todas as informações relacionadas serão removidas.\n\nDeseja continuar?",
        confirmLabel: "Excluir",
        variant: "danger",
      });
      if (!confirmed) return;
    } else {
      if (!(await confirmDialog({
        title: "Remover orçamento",
        message: "Tem certeza que deseja remover este orçamento?",
        confirmLabel: "Remover",
        variant: "danger",
      }))) return;
    }
    
    try {
      await axios.delete(`${API}/weddings/${wedding.id}/quotes/${id}`);
      setQuotes(prev => prev.filter(q => q.id !== id));
      toast.success("Orçamento removido!");
    } catch (err) {
      toast.error("Erro ao remover");
    }
  };

  const openCloseModal = (quote) => {
    setSelectedQuote(quote);
    setCloseForm({
      generate_expenses: true,
      payment_terms: { type: "avista", method: "pix" },
    });
    setShowCloseModal(true);
  };

  const updateInstallments = (count, firstDate, total) => {
    const installments = [];
    const installmentAmount = total / count;
    for (let i = 0; i < count; i++) {
      const date = new Date(firstDate + 'T12:00:00');
      date.setMonth(date.getMonth() + i);
      installments.push({
        amount: installmentAmount,
        due_date: date.toISOString().split('T')[0]
      });
    }
    return installments;
  };

  const handleCloseFormChange = (field, value) => {
    setCloseForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'installments_count' || field === 'first_due_date') {
        const count = field === 'installments_count' ? value : prev.installments_count;
        const date = field === 'first_due_date' ? value : prev.first_due_date;
        if (selectedQuote) {
          updated.installments = updateInstallments(count, date, selectedQuote.total_amount);
        }
      }
      return updated;
    });
  };

  const handleInstallmentChange = (index, field, value) => {
    setCloseForm(prev => {
      const newInstallments = [...prev.installments];
      newInstallments[index] = { ...newInstallments[index], [field]: value };
      return { ...prev, installments: newInstallments };
    });
  };

  const handleCloseQuote = async () => {
    if (!selectedQuote) return;
    try {
      await axios.post(`${API}/weddings/${wedding.id}/quotes/${selectedQuote.id}/close`, {
        generate_expenses: closeForm.generate_expenses,
        payment_terms: closeForm.payment_terms,
      });
      toast.success("Contrato fechado com sucesso!");
      setShowCloseModal(false);
      setSelectedQuote(null);
      fetchQuotes();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao fechar contrato"));
    }
  };

  // Função para cancelar contrato fechado
  const handleCancelContract = async (quoteId) => {
    if (!(await confirmDialog({
      title: "Cancelar contrato",
      message: "Tem certeza que deseja cancelar este contrato? As despesas vinculadas serão mantidas no histórico.",
      confirmLabel: "Cancelar contrato",
      cancelLabel: "Voltar",
      variant: "danger",
    }))) return;
    try {
      // Atualização otimista - atualiza estado local primeiro
      setQuotes(prevQuotes => 
        prevQuotes.map(q => 
          q.id === quoteId ? { ...q, status: "cancelado" } : q
        )
      );
      
      await axios.put(`${API}/weddings/${wedding.id}/quotes/${quoteId}`, {
        status: "cancelado"
      });
      toast.success("Contrato cancelado com sucesso!");
    } catch (err) {
      // Em caso de erro, recarrega os dados do servidor
      fetchQuotes();
      toast.error(getErrorMessage(err, "Erro ao cancelar contrato"));
    }
  };

  const getCategoryLabel = (value) => categories.find(c => c.value === value)?.label || value;
  const getCategoryIcon = (value) => categories.find(c => c.value === value)?.icon || FileText;

  // Memoizar agrupamento por categoria para evitar recálculos desnecessários
  const quotesByCategory = useMemo(() => {
    return quotes.reduce((acc, quote) => {
      if (!acc[quote.category]) acc[quote.category] = [];
      acc[quote.category].push(quote);
      return acc;
    }, {});
  }, [quotes]);

  const totalQuotes = quotes.filter(q => q.status !== "fechado" && q.status !== "cancelado").reduce((sum, q) => sum + q.total_amount, 0);
  const acceptedTotal = quotes.filter(q => q.status === "fechado").reduce((sum, q) => sum + q.total_amount, 0);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-40 mb-2"></div>
            <div className="h-5 bg-slate-200 rounded w-72"></div>
          </div>
          <div className="flex gap-2">
            <div className="h-10 bg-slate-200 rounded-xl w-36 animate-pulse"></div>
            <div className="h-10 bg-slate-200 rounded-xl w-44 animate-pulse"></div>
          </div>
        </div>
        
        {/* Stats Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="bg-white rounded-2xl p-4 shadow-sm animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-200"></div>
                <div>
                  <div className="h-4 bg-slate-200 rounded w-16 mb-2"></div>
                  <div className="h-6 bg-slate-200 rounded w-10"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Content Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl p-5 shadow-sm animate-pulse h-48"></div>
          <div className="bg-white rounded-2xl p-5 shadow-sm animate-pulse h-48"></div>
        </div>
        
        <div className="bg-white rounded-2xl p-5 shadow-sm animate-pulse h-64"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Fornecedores</h1>
          <p className="text-slate-500">Centralize orçamentos e contratos do seu evento</p>
        </div>
      </div>

      {/* Onda 3 — Tabs: Orçamentos | Contratos */}
      <div className="inline-flex p-1 rounded-xl bg-zinc-100 ring-1 ring-zinc-200/70" role="tablist">
        <button
          type="button"
          onClick={() => setActiveTab("orcamentos")}
          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold transition-all ${
            activeTab === "orcamentos" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"
          }`}
          data-testid="budgets-tab-orcamentos"
        >
          <FileText size={13} /> Fornecedores
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("contratos")}
          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold transition-all ${
            activeTab === "contratos" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"
          }`}
          data-testid="budgets-tab-contratos"
        >
          <Briefcase size={13} /> Contratos
        </button>
      </div>

      {activeTab === "contratos" ? (
        <Suspense fallback={<PageLoadingFallback />}>
          <LazyContractsPanel wedding={wedding} />
        </Suspense>
      ) : (
      <>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Fornecedores</h1>
          <p className="text-slate-500">Gerencie e acompanhe todas as propostas do seu evento</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCatManager(!showCatManager)}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${showCatManager ? 'bg-slate-200 text-slate-800' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="budget-toggle-cat-manager-btn"
          >
            <Settings size={16} />
            Categorias
          </button>
          <Button onClick={() => { 
            setEditQuote(null); 
            setForm({ category: "buffet", vendor_name: "", description: "", total_amount: 0, contact_phone: "", contact_email: "", notes: "" }); 
            setShowModal(true); 
          }}>
            <Plus size={18} className="mr-2" />
            <span>Novo Fornecedor</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={FileText} label="Em negociação" value={formatCurrency(totalQuotes)} subvalue={`${quotes.filter(q => q.status === "em_negociacao").length} orçamentos`} color="blue" onClick={() => document.getElementById('budgets-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
        <StatCard icon={Check} label="Contratos fechados" value={formatCurrency(acceptedTotal)} subvalue={`${quotes.filter(q => q.status === "fechado").length} contratos`} color="emerald" onClick={() => document.getElementById('budgets-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
        <StatCard icon={DollarSign} label="Orçamento disponível" value={formatCurrency(wedding?.total_budget || 0)} color="purple" onClick={() => document.getElementById('budgets-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
        <StatCard icon={TrendingDown} label="Saldo restante" value={formatCurrency((wedding?.total_budget || 0) - acceptedTotal)} color={acceptedTotal > (wedding?.total_budget || 0) ? "rose" : "orange"} onClick={() => document.getElementById('budgets-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
      </div>

      {/* Category Manager Modal — fullscreen com scroll, CRUD completo */}
      <CategoryManagerModal
        isOpen={showCatManager}
        onClose={() => setShowCatManager(false)}
        categories={eventCategories}
        editingCatId={editingCatId}
        setEditingCatId={setEditingCatId}
        editingCatName={editingCatName}
        setEditingCatName={setEditingCatName}
        newCatName={newCatName}
        setNewCatName={setNewCatName}
        catLoading={catLoading}
        onCreate={handleCreateCategory}
        onRename={handleRenameCategory}
        onDeleteRequest={(cat) => { setDeleteCatTarget(cat); setMoveToCatSlug('outros'); }}
        contextLabel="orçamentos"
      />

      {/* Delete Category Modal */}
      <Modal isOpen={!!deleteCatTarget} onClose={() => setDeleteCatTarget(null)} title="Excluir Categoria">
        <div className="space-y-4" data-testid="budget-delete-category-modal">
          {deleteCatTarget?.expense_count > 0 ? (
            <React.Fragment>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 font-medium mb-2">A categoria "{deleteCatTarget?.name}" possui {deleteCatTarget?.expense_count} despesa(s).</p>
                <p className="text-amber-700 text-sm">Escolha para qual categoria deseja mover:</p>
              </div>
              <select value={moveToCatSlug} onChange={e => setMoveToCatSlug(e.target.value)} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm">
                {eventCategories.filter(c => c.id !== deleteCatTarget?.id).map(c => (<option key={c.slug} value={c.slug}>{c.name}</option>))}
              </select>
            </React.Fragment>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Excluir a categoria "{deleteCatTarget?.name}"?</p>
            </div>
          )}
          <div className="flex gap-3">
            <button onClick={() => setDeleteCatTarget(null)} className="flex-1 py-2.5 px-4 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 text-sm font-medium">Cancelar</button>
            <button onClick={handleDeleteCategory} disabled={catLoading} className="flex-1 py-2.5 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50">Excluir</button>
          </div>
        </div>
      </Modal>

      {Object.keys(quotesByCategory).length > 0 ? (
        <div id="budgets-list-section" className="space-y-6 scroll-mt-6">
          {Object.entries(quotesByCategory).map(([category, categoryQuotes]) => {
            const CategoryIcon = getCategoryIcon(category);
            return (
              <Card key={category} className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <CategoryIcon size={20} className="text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{getCategoryLabel(category)}</h3>
                    <p className="text-sm text-slate-500">{categoryQuotes.length} orçamento(s)</p>
                  </div>
                </div>

                {/* ═══ MOBILE: Card ultra clean (Full Pattern — igual Convidados) ═══ */}
                <div className="md:hidden divide-y divide-zinc-100 -mx-3">
                  {categoryQuotes.map(quote => {
                    const palettes = [
                      'bg-violet-100 text-violet-700',
                      'bg-emerald-100 text-emerald-700',
                      'bg-sky-100 text-sky-700',
                      'bg-amber-100 text-amber-700',
                      'bg-rose-100 text-rose-700',
                      'bg-indigo-100 text-indigo-700',
                      'bg-teal-100 text-teal-700',
                      'bg-fuchsia-100 text-fuchsia-700',
                    ];
                    const hash = [...((quote.vendor_name || '?'))].reduce((a, c) => a + c.charCodeAt(0), 0);
                    const avatarBg = palettes[hash % palettes.length];
                    const isClosed = quote.status === 'fechado';
                    const isCanceled = quote.status === 'recusado' || quote.status === 'cancelado';
                    const statusText = isClosed ? 'Contrato fechado' : isCanceled ? 'Cancelado' : 'Em negociação';
                    const statusColor = isClosed ? 'text-emerald-600' : isCanceled ? 'text-zinc-500' : 'text-orange-600';
                    return (
                      <button
                        type="button"
                        key={quote.id}
                        onClick={() => { try { navigator.vibrate?.(8); } catch {} setDetailQuote(quote); }}
                        className="tap-press w-full text-left flex items-center gap-3.5 px-3 py-3.5"
                        data-testid={`quote-card-mobile-${quote.id}`}
                      >
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-[15px] tracking-tight ${avatarBg}`}>
                          {(quote.vendor_name || '?').trim().charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-[15.5px] font-medium text-zinc-900 truncate leading-snug">{quote.vendor_name}</h3>
                          <p className="text-[12.5px] text-zinc-500 mt-0.5 truncate">
                            <span className="font-medium text-zinc-700 tabular-nums">{formatCurrency(quote.total_amount)}</span>
                            <span className="mx-1.5 text-zinc-300">·</span>
                            <span className={statusColor}>{statusText}</span>
                          </p>
                        </div>
                        <ChevronRight size={16} className="text-zinc-300 flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>

                {/* ═══ DESKTOP: Tabela ═══ */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-sm font-medium text-slate-600">Fornecedor</th>
                        <th className="text-left py-2 text-sm font-medium text-slate-600">Descrição</th>
                        <th className="text-right py-2 text-sm font-medium text-slate-600">Valor</th>
                        <th className="text-center py-2 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-right py-2 text-sm font-medium text-slate-600">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryQuotes.map(quote => (
                        <tr key={quote.id} className={`border-b last:border-0 ${quote.is_accepted ? 'bg-emerald-50' : ''}`}>
                          <td className="py-3">
                            <div className="font-medium text-slate-800">{quote.vendor_name}</div>
                            {quote.contact_phone && (
                              <div className="text-xs text-slate-500 flex items-center gap-1">
                                <Phone size={12} /> {quote.contact_phone}
                              </div>
                            )}
                          </td>
                          <td className="py-3 text-sm text-slate-600">{quote.description || '-'}</td>
                          <td className="py-3 text-right">
                            <span className="font-bold text-lg text-slate-800">{formatCurrency(quote.total_amount)}</span>
                          </td>
                          <td className="py-3 text-center">
                            {quote.status === "fechado" ? (
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium">
                                <Check size={14} /> Contrato Fechado
                              </span>
                            ) : quote.status === "recusado" ? (
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-700 text-sm font-medium">
                                <X size={14} /> Cancelado
                              </span>
                            ) : quote.status === "cancelado" ? (
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                                <X size={14} /> Cancelado
                              </span>
                            ) : (
                              <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-600 text-sm">
                                Em negociação
                              </span>
                            )}
                          </td>
                          <td className="py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {/* Botão Fechar - apenas para orçamentos em negociação */}
                              {quote.status === "em_negociacao" && (
                                <Button size="sm" onClick={() => openCloseModal(quote)}>
                                  <Check size={14} className="mr-1" />
                                  <span>Fechar contrato</span>
                                </Button>
                              )}
                              
                              {/* Botão Cancelar Contrato - apenas para contratos fechados */}
                              {quote.status === "fechado" && (
                                <Button size="sm" variant="secondary" onClick={() => handleCancelContract(quote.id)}>
                                  <X size={14} className="mr-1" />
                                  <span>Cancelar</span>
                                </Button>
                              )}
                              
                              {/* Botão Editar - sempre disponível exceto para cancelados */}
                              {quote.status !== "cancelado" && (
                                <button onClick={() => { setEditQuote(quote); setForm(quote); setShowModal(true); }} 
                                  className="p-2 hover:bg-slate-100 rounded-lg text-slate-600" title="Editar orçamento">
                                  <Edit2 size={16} />
                                </button>
                              )}
                              
                              {/* Botão Excluir - sempre disponível com confirmação */}
                              <button onClick={() => handleDelete(quote.id)} 
                                className="p-2 hover:bg-red-50 rounded-lg text-red-500" title="Excluir orçamento">
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <FileText size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Nenhum orçamento cadastrado ainda.</h3>
          <p className="text-slate-500 mb-4">Crie um novo orçamento para começar.</p>
          <Button onClick={() => setShowModal(true)}>
            <Plus size={18} className="mr-2" />
            <span>Novo Fornecedor</span>
          </Button>
        </Card>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editQuote ? "Editar orçamento" : "Novo orçamento"} fullScreen>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select label="Categoria" value={form.category} onChange={e => setForm({...form, category: e.target.value})} options={eventCategories.length > 0 ? eventCategories.map(c => ({value: c.slug, label: c.name})) : STANDARD_CATEGORIES} />
          <Input 
            label="Nome do Fornecedor" 
            value={form.vendor_name} 
            onChange={e => setForm({...form, vendor_name: e.target.value})} 
            placeholder="Ex: Buffet Delícias"
            required 
          />
          <Input 
            label="Descrição do Serviço" 
            value={form.description || ""} 
            onChange={e => setForm({...form, description: e.target.value})} 
            placeholder="Ex: Buffet completo para 150 pessoas"
          />
          <MoneyInput
            label="Valor Total"
            value={form.total_amount || 0}
            onChange={(val) => setForm({...form, total_amount: val})}
          />
          
          <div className="grid grid-cols-2 gap-4">
            <Input 
              label="Telefone" 
              value={form.contact_phone || ""} 
              onChange={e => setForm({...form, contact_phone: e.target.value})} 
              placeholder="(11) 99999-9999"
            />
            <Input 
              label="Email" 
              type="email"
              value={form.contact_email || ""} 
              onChange={e => setForm({...form, contact_email: e.target.value})} 
              placeholder="contato@fornecedor.com"
            />
          </div>
          
          <Input 
            label="Observações" 
            value={form.notes || ""} 
            onChange={e => setForm({...form, notes: e.target.value})} 
            placeholder="Observações sobre o orçamento"
          />
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button type="submit" className="flex-1">{editQuote ? "Salvar" : "Adicionar"}</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showCloseModal} onClose={() => setShowCloseModal(false)} title="Fechar contrato">
        {selectedQuote && (
          <div className="space-y-6">
            <Card className="p-4 bg-emerald-50 border-emerald-200">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold text-emerald-800">{selectedQuote.vendor_name}</h4>
                  <p className="text-sm text-blue-500">{getCategoryLabel(selectedQuote.category)}</p>
                </div>
                <span className="text-xl font-bold text-emerald-800">{formatCurrency(selectedQuote.total_amount)}</span>
              </div>
            </Card>

            <div className="p-4 bg-slate-50 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={closeForm.generate_expenses}
                  onChange={e => setCloseForm({...closeForm, generate_expenses: e.target.checked})}
                  className="w-5 h-5 text-blue-500 rounded"
                />
                <span className="text-slate-700">Deseja gerar as despesas a partir deste contrato?</span>
              </label>
            </div>

            {closeForm.generate_expenses && (
              <div className="space-y-4" data-testid="close-quote-payment-terms">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Condições de Pagamento *</p>
                <PaymentTermsForm
                  totalAmount={selectedQuote.total_amount || 0}
                  value={closeForm.payment_terms}
                  onChange={(t) => setCloseForm(prev => ({ ...prev, payment_terms: t }))}
                />
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t">
              <Button type="button" variant="secondary" onClick={() => setShowCloseModal(false)}>Cancelar</Button>
              <Button onClick={handleCloseQuote} className="flex-1 bg-emerald-600">
                <Check size={18} className="mr-2" />
                <span>Fechar contrato</span>
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* BOTTOM SHEET DE DETALHES DO ORÇAMENTO — Full Pattern (75dvh, igual Convidados) */}
      {detailQuote && (
        <Vaul.Root open={!!detailQuote} onOpenChange={(o) => { if (!o) setDetailQuote(null); }} dismissible>
          <Vaul.Portal>
            <Vaul.Overlay className="fixed inset-0 z-[10010] bg-black/40 backdrop-blur-[3px]" />
            <Vaul.Content
              className="fixed inset-x-0 bottom-0 z-[10011] flex flex-col bg-white rounded-t-3xl outline-none shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.18)]"
              style={{ height: '75dvh' }}
              data-testid="quote-detail-sheet"
            >
              <Vaul.Title className="sr-only">Detalhes do orçamento</Vaul.Title>
              <Vaul.Description className="sr-only">Ações do orçamento e contato</Vaul.Description>
              <div className="mx-auto mt-2.5 mb-1 h-1.5 w-12 rounded-full bg-zinc-200 shrink-0" />
              {(() => {
                const q = detailQuote;
                const palettes = [
                  'bg-violet-100 text-violet-700',
                  'bg-emerald-100 text-emerald-700',
                  'bg-sky-100 text-sky-700',
                  'bg-amber-100 text-amber-700',
                  'bg-rose-100 text-rose-700',
                  'bg-indigo-100 text-indigo-700',
                  'bg-teal-100 text-teal-700',
                  'bg-fuchsia-100 text-fuchsia-700',
                ];
                const hash = [...((q.vendor_name || '?'))].reduce((a, c) => a + c.charCodeAt(0), 0);
                const avatarBg = palettes[hash % palettes.length];
                const isClosed = q.status === 'fechado';
                const isCanceled = q.status === 'recusado' || q.status === 'cancelado';
                const statusBadge = isClosed
                  ? 'bg-emerald-100 text-emerald-700'
                  : isCanceled
                  ? 'bg-zinc-100 text-zinc-700'
                  : 'bg-orange-100 text-orange-700';
                const statusLabel = isClosed ? 'Contrato fechado' : isCanceled ? 'Cancelado' : 'Em negociação';
                return (
                  <>
                    {/* Header */}
                    <div className="px-5 pt-3 pb-4 flex items-center gap-3.5 border-b border-zinc-100 shrink-0">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-[20px] tracking-tight ${avatarBg}`}>
                        {(q.vendor_name || '?').trim().charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10.5px] uppercase tracking-[0.14em] text-zinc-400 font-medium">{getCategoryLabel(q.category)}</p>
                        <h3 className="text-[17px] font-semibold text-zinc-900 leading-tight tracking-tight truncate mt-0.5">{q.vendor_name}</h3>
                      </div>
                      <button
                        onClick={() => setDetailQuote(null)}
                        className="w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 active:bg-zinc-300 flex items-center justify-center text-zinc-600 transition-colors flex-shrink-0"
                        aria-label="Fechar"
                        data-testid="quote-sheet-close"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    {/* Conteúdo */}
                    <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 space-y-6"
                         style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
                      {/* Valor + Status */}
                      <div>
                        <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2">Valor proposto</p>
                        <div className="flex items-baseline gap-2.5 flex-wrap">
                          <span className="text-[30px] font-semibold text-zinc-900 tabular-nums leading-none tracking-tight">
                            {formatCurrency(q.total_amount)}
                          </span>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${statusBadge}`}>{statusLabel}</span>
                        </div>
                      </div>

                      {/* Descrição */}
                      {q.description && (
                        <div>
                          <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2">Descrição</p>
                          <p className="text-[13.5px] text-zinc-700 whitespace-pre-wrap">{q.description}</p>
                        </div>
                      )}

                      {/* Contato */}
                      {(q.contact_phone || q.contact_email) && (
                        <div>
                          <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2.5">Contato</p>
                          <div className="space-y-1.5">
                            {q.contact_phone && (
                              <div className="flex items-center gap-2.5 text-[13.5px] text-zinc-700">
                                <Phone size={14} className="text-zinc-400" />
                                <span>{q.contact_phone}</span>
                              </div>
                            )}
                            {q.contact_email && (
                              <div className="flex items-center gap-2.5 text-[13.5px] text-zinc-700">
                                <Mail size={14} className="text-zinc-400" />
                                <span className="truncate">{q.contact_email}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Notas */}
                      {q.notes && (
                        <div>
                          <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2">Notas</p>
                          <p className="text-[13px] text-zinc-600 whitespace-pre-wrap">{q.notes}</p>
                        </div>
                      )}

                      {/* Ações */}
                      <div>
                        <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2.5">Ações</p>
                        <div className="space-y-2">
                          {q.status === 'em_negociacao' && (
                            <button
                              onClick={() => { setDetailQuote(null); setTimeout(() => openCloseModal(q), 220); }}
                              className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-emerald-50/70 hover:bg-emerald-50 border border-emerald-100/80 text-left"
                              data-testid="quote-sheet-close-contract"
                            >
                              <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                                <Check size={15} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="block text-[14.5px] font-medium text-zinc-900">Fechar contrato</span>
                                <span className="block text-[11.5px] text-zinc-500">Aceitar e gerar despesa</span>
                              </div>
                              <ChevronRight size={16} className="text-zinc-300 ml-auto" />
                            </button>
                          )}

                          {q.contact_phone && (
                            <a
                              href={`https://wa.me/${q.contact_phone.replace(/\D/g, '')}`}
                              target="_blank" rel="noopener noreferrer"
                              onClick={() => setDetailQuote(null)}
                              className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-emerald-50/40 hover:bg-emerald-50 border border-emerald-100/60 text-left"
                              data-testid="quote-sheet-whatsapp"
                            >
                              <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                                <MessageCircle size={15} />
                              </div>
                              <span className="text-[14.5px] font-medium text-zinc-900">Abrir no WhatsApp</span>
                              <ChevronRight size={16} className="text-zinc-300 ml-auto" />
                            </a>
                          )}

                          {q.status !== 'cancelado' && (
                            <button
                              onClick={() => { setEditQuote(q); setForm(q); setDetailQuote(null); setTimeout(() => setShowModal(true), 220); }}
                              className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-zinc-50 hover:bg-zinc-100 border border-zinc-100 text-left"
                              data-testid="quote-sheet-edit"
                            >
                              <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-zinc-700 ring-1 ring-zinc-200/70">
                                <Edit2 size={15} />
                              </div>
                              <span className="text-[14.5px] font-medium text-zinc-900">Editar orçamento</span>
                              <ChevronRight size={16} className="text-zinc-300 ml-auto" />
                            </button>
                          )}

                          {q.status === 'fechado' && (
                            <button
                              onClick={() => { const id = q.id; setDetailQuote(null); setTimeout(() => handleCancelContract(id), 220); }}
                              className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-zinc-50 hover:bg-zinc-100 border border-zinc-100 text-left"
                              data-testid="quote-sheet-cancel-contract"
                            >
                              <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-zinc-700 ring-1 ring-zinc-200/70">
                                <X size={15} />
                              </div>
                              <span className="text-[14.5px] font-medium text-zinc-900">Cancelar contrato</span>
                              <ChevronRight size={16} className="text-zinc-300 ml-auto" />
                            </button>
                          )}

                          <button
                            onClick={() => { const id = q.id; setDetailQuote(null); setTimeout(() => handleDelete(id), 220); }}
                            className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-rose-50/70 hover:bg-rose-50 border border-rose-100/80 text-left"
                            data-testid="quote-sheet-delete"
                          >
                            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-rose-600 ring-1 ring-rose-200/80">
                              <Trash2 size={15} />
                            </div>
                            <span className="text-[14.5px] font-medium text-rose-600">Excluir orçamento</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </Vaul.Content>
          </Vaul.Portal>
        </Vaul.Root>
      )}
      </>
      )}

    </div>
  );
}


// Cashflow Page - Fluxo de Caixa
function CashflowPage() {
  const { wedding, eventCategories } = useWedding();
  const [expenses, setExpenses] = useState([]);
  const [installments, setInstallments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
      return 'list'; // Mobile: agenda/lista é melhor que grid de calendário
    }
    return 'calendar';
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // Novo filtro de status

  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  // Usar categorias dinâmicas do evento com opção "Todas"
  const dynCats = eventCategories.length > 0 
    ? eventCategories.map(c => ({ value: c.slug, label: c.name }))
    : STANDARD_CATEGORIES;
  const categories = [
    { value: "all", label: "Todas Categorias" },
    ...dynCats
  ];
  
  // Opções de filtro de status
  const statusFilters = [
    { value: "all", label: "Todos" },
    { value: "paid", label: "Pagos no Mês" },
    { value: "pending", label: "A Vencer no Mês" },
    { value: "overdue", label: "Vencidos" }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const fetchExpensesData = useCallback(async () => {
    if (!wedding) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/expenses`);
      setExpenses(res.data);
      
      const allInstallments = res.data.flatMap(exp => 
        exp.installments.map(inst => ({
          ...inst,
          expense: exp
        }))
      );
      setInstallments(allInstallments);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [wedding]);

  useEffect(() => {
    fetchExpensesData();
  }, [fetchExpensesData]);

  const handlePayInstallment = async (installmentId) => {
    try {
      await axios.patch(`${API}/weddings/${wedding.id}/installments/${installmentId}/pay`);
      toast.success("Parcela marcada como paga!");
      fetchExpensesData();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao marcar como paga"));
    }
  };

  const handleUnpayInstallment = async (installmentId) => {
    try {
      await axios.patch(`${API}/weddings/${wedding.id}/installments/${installmentId}/unpay`);
      toast.success("Parcela desmarcada!");
      fetchExpensesData();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao desmarcar"));
    }
  };

  const goToPreviousMonth = () => {
    setSelectedMonth(prev => {
      const newMonth = prev.month === 0 
        ? { year: prev.year - 1, month: 11 }
        : { year: prev.year, month: prev.month - 1 };
      return newMonth;
    });
  };

  const goToNextMonth = () => {
    setSelectedMonth(prev => {
      const newMonth = prev.month === 11 
        ? { year: prev.year + 1, month: 0 }
        : { year: prev.year, month: prev.month + 1 };
      return newMonth;
    });
  };

  const goToCurrentMonth = () => {
    const d = new Date();
    setSelectedMonth({ year: d.getFullYear(), month: d.getMonth() });
  };

  const getDaysInMonth = useMemo(() => {
    const { year, month } = selectedMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  }, [selectedMonth]);

  const getInstallmentsForDate = useCallback((date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return installments.filter(inst => inst.due_date === dateStr);
  }, [installments]);

  // REMOVIDO - getInstallmentsForMonth não usado diretamente

  const filteredInstallments = useMemo(() => {
    let filtered = installments;
    
    // Filtro por categoria
    if (filterCategory !== 'all') {
      filtered = filtered.filter(inst => inst.expense.category === filterCategory);
    }
    
    return filtered;
  }, [installments, filterCategory]);

  // Parcelas do mês selecionado (para totais do mês)
  const monthInstallments = useMemo(() => {
    const { year, month } = selectedMonth;
    return installments.filter(inst => {
      const dueDate = new Date(inst.due_date + 'T12:00:00');
      return dueDate.getFullYear() === year && dueDate.getMonth() === month;
    });
  }, [installments, selectedMonth]);

  // Parcelas PAGAS no mês selecionado (baseado na data de pagamento)
  const paidInMonth = useMemo(() => {
    const { year, month } = selectedMonth;
    return installments.filter(inst => {
      if (!inst.is_paid || !inst.payment_date) return false;
      const payDate = new Date(inst.payment_date + 'T12:00:00');
      return payDate.getFullYear() === year && payDate.getMonth() === month;
    });
  }, [installments, selectedMonth]);

  // Parcelas A VENCER no mês selecionado
  const pendingInMonth = useMemo(() => {
    const { year, month } = selectedMonth;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return installments.filter(inst => {
      if (inst.is_paid) return false;
      const dueDate = new Date(inst.due_date + 'T12:00:00');
      // Deve estar no mês selecionado E não estar vencida
      return dueDate.getFullYear() === year && 
             dueDate.getMonth() === month && 
             dueDate >= today;
    });
  }, [installments, selectedMonth]);

  // Parcelas filtradas para exibição na lista (com filtro de status)
  const displayInstallments = useMemo(() => {
    const { year, month } = selectedMonth;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let filtered = filteredInstallments;
    
    if (filterStatus === 'paid') {
      // Pagos no mês: parcelas pagas com data de pagamento no mês selecionado
      filtered = filtered.filter(inst => {
        if (!inst.is_paid || !inst.payment_date) return false;
        const payDate = new Date(inst.payment_date + 'T12:00:00');
        return payDate.getFullYear() === year && payDate.getMonth() === month;
      });
    } else if (filterStatus === 'pending') {
      // A vencer no mês: parcelas não pagas com vencimento no mês E não vencidas
      filtered = filtered.filter(inst => {
        if (inst.is_paid) return false;
        const dueDate = new Date(inst.due_date + 'T12:00:00');
        return dueDate.getFullYear() === year && 
               dueDate.getMonth() === month && 
               dueDate >= today;
      });
    } else if (filterStatus === 'overdue') {
      // Vencidos: parcelas não pagas com data de vencimento passada
      filtered = filtered.filter(inst => {
        if (inst.is_paid) return false;
        const dueDate = new Date(inst.due_date + 'T12:00:00');
        return dueDate < today;
      });
    } else {
      // Todos: mostrar apenas do mês selecionado + vencidos
      filtered = filtered.filter(inst => {
        const dueDate = new Date(inst.due_date + 'T12:00:00');
        const isInMonth = dueDate.getFullYear() === year && dueDate.getMonth() === month;
        const isOverdue = !inst.is_paid && dueDate < today;
        return isInMonth || isOverdue;
      });
    }
    
    return filtered;
  }, [filteredInstallments, filterStatus, selectedMonth]);

  const totalPaid = useMemo(() => 
    installments.filter(i => i.is_paid).reduce((sum, i) => sum + i.amount, 0),
    [installments]
  );
  const totalPending = useMemo(() => 
    installments.filter(i => !i.is_paid).reduce((sum, i) => sum + i.amount, 0),
    [installments]
  );
  const budgetRemaining = useMemo(() => 
    (wedding?.total_budget || 0) - totalPaid - totalPending,
    [wedding?.total_budget, totalPaid, totalPending]
  );
  const overspent = budgetRemaining < 0;

  // Data de hoje para comparação (memoizada para evitar recálculo)
  const todayTimestamp = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);
  
  const overdueInstallments = useMemo(() => 
    installments.filter(i => !i.is_paid && new Date(i.due_date + 'T12:00:00').getTime() < todayTimestamp),
    [installments, todayTimestamp]
  );

  // Totais do mês selecionado
  const monthTotal = useMemo(() => monthInstallments.reduce((sum, i) => sum + i.amount, 0), [monthInstallments]);
  const monthPaidCount = useMemo(() => paidInMonth.length, [paidInMonth]);
  const monthPaidAmount = useMemo(() => paidInMonth.reduce((sum, i) => sum + i.amount, 0), [paidInMonth]);
  const monthPendingCount = useMemo(() => pendingInMonth.length, [pendingInMonth]);
  const monthPendingAmount = useMemo(() => pendingInMonth.reduce((sum, i) => sum + i.amount, 0), [pendingInMonth]);

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-40 mb-2"></div>
            <div className="h-5 bg-slate-200 rounded w-64"></div>
          </div>
          <div className="flex items-center gap-2 animate-pulse">
            <div className="h-10 bg-slate-200 rounded w-28"></div>
            <div className="h-10 bg-slate-200 rounded w-24"></div>
          </div>
        </div>
        {/* Stats Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-2xl p-4 shadow-sm animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-200"></div>
                <div className="flex-1">
                  <div className="h-4 bg-slate-200 rounded w-20 mb-2"></div>
                  <div className="h-6 bg-slate-200 rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Content Skeleton */}
        <div className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Data de hoje para comparações no render
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Fluxo de caixa</h1>
          <p className="text-sm text-zinc-500 mt-1">Acompanhe seus pagamentos e vencimentos</p>
        </div>
        <div className="inline-flex items-center p-1 rounded-xl border border-zinc-200 bg-white">
          <button 
            type="button"
            onClick={() => setViewMode('calendar')}
            className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-[12.5px] font-medium transition-all ${
              viewMode === 'calendar' ? 'bg-brand-deep text-white' : 'text-zinc-600 hover:text-zinc-900'
            }`}
            data-testid="view-calendar-btn"
          >
            <CalendarDays size={14} />
            Calendário
          </button>
          <button 
            type="button"
            onClick={() => setViewMode('list')}
            className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-[12.5px] font-medium transition-all ${
              viewMode === 'list' ? 'bg-brand-deep text-white' : 'text-zinc-600 hover:text-zinc-900'
            }`}
            data-testid="view-list-btn"
          >
            <ListChecks size={14} />
            Lista
          </button>
        </div>
      </div>

      {/* STAT CARDS — Apple-style + barra de progresso visual — clicáveis: scroll para lista */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Orçamento total" value={formatCurrency(wedding?.total_budget || 0)} color="emerald" onClick={() => document.getElementById('cashflow-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
        <StatCard icon={Check} label="Total Pago" value={formatCurrency(totalPaid)} subvalue={wedding?.total_budget > 0 ? `${Math.round((totalPaid / wedding.total_budget) * 100)}% do orçamento` : null} color="blue" onClick={() => document.getElementById('cashflow-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
        <StatCard icon={Clock} label="À pagar" value={formatCurrency(totalPending)} color="orange" onClick={() => document.getElementById('cashflow-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
        <StatCard icon={overspent ? AlertCircle : TrendingUp} label={overspent ? 'Orçamento Estourado' : 'Orçamento Livre'} value={formatCurrency(budgetRemaining)} color={overspent ? 'rose' : 'purple'} onClick={() => document.getElementById('cashflow-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
      </div>

      {/* GRÁFICO DE PROGRESSO — barra estilo Apple Health */}
      {(wedding?.total_budget || 0) > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Visão geral do orçamento</p>
              <p className={`text-[12px] mt-0.5 font-medium ${(totalPaid + totalPending) > (wedding?.total_budget || 0) ? 'text-rose-500' : 'text-emerald-600'}`}>
                {(totalPaid + totalPending) > (wedding?.total_budget || 0)
                  ? `Você está ${formatCurrency((totalPaid + totalPending) - (wedding?.total_budget || 0))} acima do orçamento`
                  : `Dentro do orçamento · saldo de ${formatCurrency((wedding?.total_budget || 0) - (totalPaid + totalPending))}`}
              </p>
            </div>
            <span className="text-2xl font-semibold tracking-tight text-zinc-900">
              {Math.round(((totalPaid + totalPending) / (wedding?.total_budget || 1)) * 100)}%
            </span>
          </div>
          <div className="relative w-full h-3 rounded-full overflow-hidden bg-zinc-100">
            <div className="absolute inset-y-0 left-0 bg-emerald-500 transition-all duration-700" style={{width: `${Math.min(100, (totalPaid / (wedding?.total_budget || 1)) * 100)}%`}} />
            <div className="absolute inset-y-0 bg-amber-400 transition-all duration-700" style={{left: `${Math.min(100, (totalPaid / (wedding?.total_budget || 1)) * 100)}%`, width: `${Math.min(100 - (totalPaid / (wedding?.total_budget || 1)) * 100, (totalPending / (wedding?.total_budget || 1)) * 100)}%`}} />
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-[12px]">
            <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-zinc-600">Pago</span><span className="text-zinc-900 font-medium">{formatCurrency(totalPaid)}</span></div>
            <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /><span className="text-zinc-600">Pendente</span><span className="text-zinc-900 font-medium">{formatCurrency(totalPending)}</span></div>
            <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-zinc-200" /><span className="text-zinc-600">Disponível</span><span className="text-zinc-900 font-medium">{formatCurrency(Math.max(0, (wedding?.total_budget || 0) - totalPaid - totalPending))}</span></div>
          </div>
        </Card>
      )}

      {overdueInstallments.length > 0 && (
        <Card className="p-4 bg-red-50/50 border-red-200/60">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="text-red-500" size={16} />
            </div>
            <div className="flex-1">
              <p className="text-[13.5px] font-semibold text-red-800">
                {overdueInstallments.length} parcela(s) vencida(s)
              </p>
              <p className="text-[12px] text-red-600 mt-0.5">
                Total em atraso: {formatCurrency(overdueInstallments.reduce((sum, i) => sum + i.amount, 0))}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* CALENDÁRIO MOBILE — visual premium estilo Agenda                 */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="md:hidden" data-testid="mobile-cashflow-calendar">
        <Card className="overflow-hidden p-0">
          {/* Header navegação mensal */}
          <div className="px-4 pt-4 pb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={goToPreviousMonth}
              className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center active:scale-95 transition-transform"
              data-testid="mob-prev-month-btn"
              aria-label="Mês anterior"
            >
              <ChevronLeft size={18} className="text-zinc-700" />
            </button>
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-semibold">Mês</p>
              <h3 className="text-[16px] font-bold text-zinc-900 tracking-tight" data-testid="mob-current-month">
                {monthNames[selectedMonth.month]} {selectedMonth.year}
              </h3>
            </div>
            <button
              type="button"
              onClick={goToNextMonth}
              className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center active:scale-95 transition-transform"
              data-testid="mob-next-month-btn"
              aria-label="Próximo mês"
            >
              <ChevronRight size={18} className="text-zinc-700" />
            </button>
          </div>

          {/* Resumo do mês compacto */}
          <div className="grid grid-cols-3 gap-2 px-4 pb-3 text-center">
            <div className="rounded-xl bg-amber-50 border border-amber-100 py-2">
              <p className="text-[9.5px] uppercase tracking-wider font-semibold text-amber-700">A vencer</p>
              <p className="text-[13px] font-bold text-amber-900 tabular-nums mt-0.5">{formatCurrency(monthPendingAmount)}</p>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 py-2">
              <p className="text-[9.5px] uppercase tracking-wider font-semibold text-emerald-700">Pago</p>
              <p className="text-[13px] font-bold text-emerald-900 tabular-nums mt-0.5">{formatCurrency(monthPaidAmount)}</p>
            </div>
            <div className={`rounded-xl py-2 border ${overdueInstallments.length > 0 ? 'bg-rose-50 border-rose-100' : 'bg-zinc-50 border-zinc-200'}`}>
              <p className={`text-[9.5px] uppercase tracking-wider font-semibold ${overdueInstallments.length > 0 ? 'text-rose-700' : 'text-zinc-500'}`}>Vencidas</p>
              <p className={`text-[13px] font-bold tabular-nums mt-0.5 ${overdueInstallments.length > 0 ? 'text-rose-900' : 'text-zinc-900'}`}>{overdueInstallments.length}</p>
            </div>
          </div>

          {/* Grid de dias */}
          <div className="px-2 pb-3">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((day, i) => (
                <div key={i} className="text-center text-[10px] font-bold uppercase tracking-wider text-zinc-400 py-1">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth.map((day, index) => {
                if (!day) return <div key={`empty-${index}`} className="aspect-square" />;
                const dayInst = getInstallmentsForDate(day);
                const dayUnpaid = dayInst.filter(i => !i.is_paid);
                const dayPaid = dayInst.filter(i => i.is_paid);
                const isToday = day.toDateString() === new Date().toDateString();
                const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
                const hasOverdue = dayUnpaid.length > 0 && day < today;
                const hasPending = dayUnpaid.length > 0 && !hasOverdue;
                const isAllPaid = dayPaid.length > 0 && dayUnpaid.length === 0;

                // Color logic: red > yellow > paid-green > today > normal
                let cellClasses = "relative aspect-square rounded-xl flex flex-col items-center justify-center active:scale-95 transition-all border";
                let dayNumClasses = "text-[14px] font-bold tabular-nums leading-none";
                let dotColor = "";

                if (isSelected) {
                  cellClasses += " text-white shadow-md";
                  cellClasses += hasOverdue ? " bg-rose-500 border-rose-600" : hasPending ? " bg-amber-500 border-amber-600" : isAllPaid ? " bg-emerald-500 border-emerald-600" : " border-violet-600";
                  if (!hasOverdue && !hasPending && !isAllPaid) cellClasses += " bg-gradient-to-br from-violet-500 to-fuchsia-500";
                  dayNumClasses += " text-white";
                } else if (hasOverdue) {
                  cellClasses += " bg-rose-50 border-rose-200 hover:bg-rose-100";
                  dayNumClasses += " text-rose-900";
                  dotColor = "bg-rose-500";
                } else if (hasPending) {
                  cellClasses += " bg-amber-50 border-amber-200 hover:bg-amber-100";
                  dayNumClasses += " text-amber-900";
                  dotColor = "bg-amber-500";
                } else if (isAllPaid) {
                  cellClasses += " bg-emerald-50 border-emerald-200 hover:bg-emerald-100";
                  dayNumClasses += " text-emerald-900";
                  dotColor = "bg-emerald-500";
                } else if (isToday) {
                  cellClasses += " bg-white border-violet-300 ring-1 ring-violet-200";
                  dayNumClasses += " text-violet-700";
                } else {
                  cellClasses += " bg-white border-zinc-100 hover:border-zinc-200";
                  dayNumClasses += " text-zinc-600";
                }

                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    onClick={() => setSelectedDate(isSelected ? null : day)}
                    className={cellClasses}
                    data-testid={`mob-day-${day.getDate()}`}
                    aria-label={`${day.getDate()} ${monthNames[selectedMonth.month]}${dayInst.length > 0 ? `, ${dayInst.length} parcela(s)` : ''}`}
                  >
                    <span className={dayNumClasses}>{day.getDate()}</span>
                    {dotColor && !isSelected && (
                      <span className={`absolute bottom-1.5 w-1.5 h-1.5 rounded-full ${dotColor}`} />
                    )}
                    {isSelected && dayInst.length > 0 && (
                      <span className="absolute bottom-1 text-[9px] font-bold text-white/90">{dayInst.length}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Legenda */}
          <div className="px-4 pb-4 pt-1 flex items-center justify-center gap-4 text-[10.5px] text-zinc-500 border-t border-zinc-100 mt-1">
            <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500" /> Vencidas</div>
            <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> A pagar</div>
            <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Pagas</div>
          </div>
        </Card>

        {/* Lista do dia selecionado (mobile) */}
        {selectedDate && (
          <Card className="mt-3 overflow-hidden p-0" data-testid="mob-day-detail">
            <div className="px-4 py-3 bg-gradient-to-r from-violet-50 via-fuchsia-50 to-amber-50 border-b border-zinc-100 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">Detalhes do dia</p>
                <h4 className="text-[15px] font-bold text-zinc-900 tracking-tight">
                  {selectedDate.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
                </h4>
              </div>
              <button type="button" onClick={() => setSelectedDate(null)} className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center active:scale-95 transition-transform" data-testid="mob-close-day">
                <X size={14} className="text-zinc-600" />
              </button>
            </div>
            {(() => {
              const dayInst = getInstallmentsForDate(selectedDate);
              if (dayInst.length === 0) {
                return (
                  <div className="px-4 py-10 text-center">
                    <div className="w-12 h-12 rounded-full bg-zinc-100 mx-auto flex items-center justify-center mb-3">
                      <CalendarDays size={20} className="text-zinc-400" />
                    </div>
                    <p className="text-[13px] text-zinc-600 font-medium">Sem movimentação nesse dia</p>
                    <p className="text-[11.5px] text-zinc-400 mt-1">Nenhuma parcela com vencimento.</p>
                  </div>
                );
              }
              const dayTotal = dayInst.reduce((s, i) => s + i.amount, 0);
              const dayPending = dayInst.filter(i => !i.is_paid).reduce((s, i) => s + i.amount, 0);
              return (
                <>
                  <div className="px-4 py-3 flex items-center justify-between border-b border-zinc-100">
                    <span className="text-[11.5px] text-zinc-500"><strong className="text-zinc-900">{dayInst.length}</strong> parcela(s)</span>
                    <span className="text-[12.5px] font-bold text-zinc-900 tabular-nums">{formatCurrency(dayPending > 0 ? dayPending : dayTotal)}</span>
                  </div>
                  <div className="divide-y divide-zinc-100">
                    {dayInst.map((inst) => {
                      const isOverdue = !inst.is_paid && new Date(inst.due_date + 'T12:00:00') < today;
                      return (
                        <div key={inst.id} className="px-4 py-3 flex items-center gap-3" data-testid={`mob-installment-${inst.id}`}>
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                            inst.is_paid ? 'bg-emerald-100' : isOverdue ? 'bg-rose-100' : 'bg-amber-100'
                          }`}>
                            {inst.is_paid ? <Check size={16} className="text-emerald-600" /> :
                             isOverdue ? <AlertCircle size={16} className="text-rose-600" /> :
                             <Clock size={16} className="text-amber-600" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13.5px] font-semibold text-zinc-900 truncate">{inst.expense.vendor_name}</p>
                            <p className="text-[11px] text-zinc-500 truncate capitalize">
                              {(inst.expense.category || '').replace('_', ' ')} · parc. {inst.number}/{inst.expense.installments_count}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-[13.5px] font-bold text-zinc-900 tabular-nums">{formatCurrency(inst.amount)}</p>
                            {inst.is_paid ? (
                              <button type="button" onClick={() => handleUnpayInstallment(inst.id)} className="tap-press text-[10.5px] text-emerald-600 font-semibold mt-0.5" data-testid={`mob-unpay-${inst.id}`}>
                                Paga ✓
                              </button>
                            ) : (
                              <button type="button" onClick={() => { try { navigator.vibrate?.(8); } catch {} handlePayInstallment(inst.id); }} className={`tap-press text-[10.5px] font-bold mt-0.5 px-2.5 py-1 rounded-full ${isOverdue ? 'bg-rose-600 text-white' : 'bg-amber-500 text-white'}`} data-testid={`mob-pay-${inst.id}`}>
                                Pagar
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </Card>
        )}
      </div>

      {viewMode === 'calendar' && (
        <Card className="hidden md:block p-6" key={`calendar-${selectedMonth.year}-${selectedMonth.month}`}>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-[26px] sm:text-[28px] font-bold tracking-tight text-zinc-900 leading-none" data-testid="current-month-header">{monthNames[selectedMonth.month]}</h3>
              <p className="text-[14px] font-medium text-zinc-400 mt-1">{selectedMonth.year}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button type="button" data-testid="prev-month-btn" onClick={goToPreviousMonth} className="w-9 h-9 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center text-zinc-500 transition-colors active:scale-95">
                <ChevronLeft size={18} />
              </button>
              <button type="button" data-testid="today-month-btn" onClick={goToCurrentMonth} className="px-4 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 text-[13px] font-semibold text-zinc-700 transition-colors active:scale-95">Hoje</button>
              <button type="button" data-testid="next-month-btn" onClick={goToNextMonth} className="w-9 h-9 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center text-zinc-500 transition-colors active:scale-95">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" key={`stats-${selectedMonth.year}-${selectedMonth.month}`}>
            <div className="rounded-xl p-4 border border-zinc-200/60">
              <div className="flex items-center gap-1.5 mb-2"><span className="w-1.5 h-1.5 rounded-full bg-zinc-400" /><p className="text-[10.5px] font-medium uppercase tracking-wider text-zinc-500">Parcelas</p></div>
              <p className="text-xl font-semibold tracking-tight text-zinc-900">{formatCurrency(monthTotal)}</p>
            </div>
            <div className="rounded-xl p-4 border border-zinc-200/60">
              <div className="flex items-center gap-1.5 mb-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /><p className="text-[10.5px] font-medium uppercase tracking-wider text-zinc-500">Pagos</p></div>
              <p className="text-xl font-semibold tracking-tight text-zinc-900">{formatCurrency(monthPaidAmount)}</p>
            </div>
            <div className="rounded-xl p-4 border border-zinc-200/60">
              <div className="flex items-center gap-1.5 mb-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /><p className="text-[10.5px] font-medium uppercase tracking-wider text-zinc-500">A vencer</p></div>
              <p className="text-xl font-semibold tracking-tight text-zinc-900">{formatCurrency(monthPendingAmount)}</p>
            </div>
            <div className="rounded-xl p-4 border border-zinc-200/60">
              <div className="flex items-center gap-1.5 mb-2"><span className="w-1.5 h-1.5 rounded-full bg-rose-500" /><p className="text-[10.5px] font-medium uppercase tracking-wider text-zinc-500">Em atraso</p></div>
              <p className="text-xl font-semibold tracking-tight text-zinc-900">{formatCurrency(overdueInstallments.reduce((sum, i) => sum + i.amount, 0))}</p>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
              <div key={day} className="text-center text-[11px] font-semibold uppercase tracking-wider text-zinc-300 py-1.5">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
            {getDaysInMonth.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="min-h-[84px] sm:min-h-[104px]" />;
              }
              const dayInstallments = getInstallmentsForDate(day);
              const isToday = day.toDateString() === new Date().toDateString();
              const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
              const hasPending = dayInstallments.some(i => !i.is_paid);
              const isOverdue = hasPending && day < today;
              const isAllPaid = dayInstallments.length > 0 && !hasPending;
              const hasItems = dayInstallments.length > 0;
              const dot = isOverdue ? 'bg-rose-400' : hasPending ? 'bg-amber-400' : isAllPaid ? 'bg-emerald-400' : '';
              const tint = isOverdue ? 'bg-rose-50/50 border-rose-200' : hasPending ? 'bg-amber-50/40 border-amber-200' : isAllPaid ? 'bg-emerald-50/40 border-emerald-100' : 'border-transparent hover:bg-zinc-50';

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  onClick={() => setSelectedDate(isSelected ? null : day)}
                  className={`relative min-h-[84px] sm:min-h-[104px] rounded-2xl p-2 flex flex-col text-left transition-all duration-200 card-hover-brightness cursor-pointer ${
                    isSelected ? 'border-2 border-violet-400 bg-violet-50/50' : `border ${tint}`
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`inline-flex items-center justify-center w-7 h-7 text-[13px] font-semibold tabular-nums ${
                      isToday ? 'rounded-full bg-gradient-to-br from-violet-500 to-pink-500 text-white shadow-[0_2px_8px_-2px_rgba(139,92,246,0.55)]' : 'text-zinc-700'
                    }`}>
                      {day.getDate()}
                    </span>
                    {hasItems && dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} aria-hidden />}
                  </div>
                  {hasItems && (
                    <div className="mt-auto flex flex-col gap-1">
                      {dayInstallments.slice(0, 2).map((inst, i) => (
                        <span key={i} className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded-md truncate tabular-nums ${
                          inst.is_paid ? 'bg-emerald-100 text-emerald-700' :
                          isOverdue ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {formatCurrency(inst.amount)}
                        </span>
                      ))}
                      {dayInstallments.length > 2 && (
                        <span className="text-[9.5px] font-medium text-zinc-400 pl-1.5">+{dayInstallments.length - 2} mais</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {selectedDate && (
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-semibold text-slate-800 mb-3">
                Parcelas em {selectedDate.toLocaleDateString('pt-BR')}
              </h4>
              {getInstallmentsForDate(selectedDate).length > 0 ? (
                <div className="space-y-2">
                  {getInstallmentsForDate(selectedDate).map((inst, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                      <div>
                        <p className="font-medium text-slate-800">{inst.expense.vendor_name}</p>
                        <p className="text-sm text-slate-500 capitalize">{inst.expense.category.replace('_', ' ')}</p>
                        <p className="text-xs text-slate-400">Parcela {inst.number}/{inst.expense.installments_count}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-800">{formatCurrency(inst.amount)}</p>
                        {inst.is_paid ? (
                          <button type="button" onClick={() => handleUnpayInstallment(inst.id)}
                            className="text-xs text-blue-500 flex items-center gap-1 mt-1">
                            <Check size={12} /> Pago
                          </button>
                        ) : (
                          <Button size="sm" onClick={() => handlePayInstallment(inst.id)}>
                            Pagar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">Nenhuma parcela nesta data</p>
              )}
            </div>
          )}
        </Card>
      )}

      {(viewMode === 'list' || viewMode === 'calendar') && (
        <Card id="cashflow-list-section" className={`${viewMode === 'list' ? '' : 'hidden '}p-6 scroll-mt-6`} key={`list-${selectedMonth.year}-${selectedMonth.month}`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h3 className="font-semibold text-slate-800">Lista de Pagamentos</h3>
              <p className="text-sm text-slate-500">{monthNames[selectedMonth.month]} {selectedMonth.year}</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Navegação de meses */}
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                <button 
                  type="button"
                  onClick={goToPreviousMonth}
                  className="p-2 hover:bg-white rounded-lg transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="px-3 text-sm font-medium text-slate-700 min-w-[120px] text-center">
                  {monthNames[selectedMonth.month].substring(0, 3)} {selectedMonth.year}
                </span>
                <button 
                  type="button"
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-white rounded-lg transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
              
              {/* Filtro de Status */}
              <select 
                value={filterStatus} 
                onChange={e => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
              >
                {statusFilters.map(sf => (
                  <option key={sf.value} value={sf.value}>{sf.label}</option>
                ))}
              </select>
              
              {/* Filtro de Categoria */}
              <select 
                value={filterCategory} 
                onChange={e => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Resumo do filtro atual */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 p-4 bg-slate-50 rounded-lg">
            <div className="text-center">
              <p className="text-xs text-slate-500">Exibindo</p>
              <p className="text-lg font-bold text-slate-800">{displayInstallments.length}</p>
              <p className="text-xs text-slate-400">parcela(s)</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-emerald-600">Pagos no mês</p>
              <p className="text-lg font-bold text-emerald-700">{monthPaidCount}</p>
              <p className="text-xs text-emerald-500">{formatCurrency(monthPaidAmount)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-orange-600">À vencer no mês</p>
              <p className="text-lg font-bold text-orange-700">{monthPendingCount}</p>
              <p className="text-xs text-orange-500">{formatCurrency(monthPendingAmount)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-red-600">Em atraso</p>
              <p className="text-lg font-bold text-red-700">{overdueInstallments.length}</p>
              <p className="text-xs text-red-500">{formatCurrency(overdueInstallments.reduce((sum, i) => sum + i.amount, 0))}</p>
            </div>
          </div>

          {displayInstallments.length > 0 ? (
            <div className="space-y-3">
              {displayInstallments
                .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
                .map((inst, i) => {
                  // Usar função padronizada de status
                  const paymentStatus = getPaymentStatus(inst.is_paid, inst.due_date);
                  const isOverdue = paymentStatus.status === PAYMENT_STATUS.VENCIDA;
                  const alert = getAlertText(inst.due_date, inst.is_paid);
                  
                  return (
                    <div key={inst.id || i} className={`flex items-center justify-between p-4 rounded-lg border ${
                      inst.is_paid ? 'bg-emerald-50 border-emerald-200' :
                      isOverdue ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'
                    }`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          inst.is_paid ? 'bg-emerald-500' : isOverdue ? 'bg-red-500' : 'bg-blue-500'
                        }`}>
                          {inst.is_paid ? <Check className="text-white" size={18} /> : 
                           isOverdue ? <AlertCircle className="text-white" size={18} /> :
                           <Clock className="text-white" size={18} />}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{inst.expense.vendor_name}</p>
                          <p className="text-sm text-slate-500 capitalize">{inst.expense.category.replace('_', ' ')}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className="text-xs text-slate-400">
                              Parcela {inst.number}/{inst.expense.installments_count}
                            </span>
                            <span className="text-xs text-slate-400">•</span>
                            <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
                              {new Date(inst.due_date + 'T12:00:00').toLocaleDateString('pt-BR')}
                            </span>
                            {/* Badge de Status Padronizado */}
                            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${paymentStatus.color}`}>
                              {paymentStatus.label}
                            </span>
                            {alert && !inst.is_paid && (
                              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                alert.color === 'red' ? 'bg-red-100 text-red-700' :
                                alert.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {alert.text}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-lg text-slate-800">{formatCurrency(inst.amount)}</p>
                          {inst.is_paid && inst.payment_date && (
                            <p className="text-xs text-blue-500">
                              Pago em {new Date(inst.payment_date + 'T12:00:00').toLocaleDateString('pt-BR')}
                            </p>
                          )}
                        </div>
                        {inst.is_paid ? (
                          <button type="button" onClick={() => handleUnpayInstallment(inst.id)}
                            className="px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm hover:bg-emerald-200">
                            <Check size={16} className="inline mr-1" /> Pago
                          </button>
                        ) : (
                          <Button size="sm" onClick={() => handlePayInstallment(inst.id)}>
                            Marcar como Pago
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Receipt size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">
                {filterStatus === 'paid' 
                  ? `Nenhum pagamento registrado em ${monthNames[selectedMonth.month]}`
                  : filterStatus === 'pending'
                  ? `Nenhuma parcela a vencer em ${monthNames[selectedMonth.month]}`
                  : filterStatus === 'overdue'
                  ? 'Nenhuma parcela vencida'
                  : 'Nenhuma parcela encontrada para este período'}
              </p>
              <p className="text-sm text-slate-400 mt-1">
                {filterStatus === 'all' && 'Feche orçamentos ou crie despesas para gerar parcelas'}
              </p>
            </div>
          )}
        </Card>
      )}

      <Card className="p-6">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <PieChart size={18} className="text-blue-500" />
          <span>Resumo Financeiro</span>
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg text-center">
            <p className="text-sm text-slate-500">Total Contratado</p>
            <p className="text-lg font-bold text-slate-800">{formatCurrency(installments.reduce((sum, i) => sum + i.amount, 0))}</p>
          </div>
          <div className="p-4 bg-emerald-50 rounded-lg text-center">
            <p className="text-sm text-blue-500">Total Pago</p>
            <p className="text-lg font-bold text-emerald-700">{formatCurrency(totalPaid)}</p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg text-center">
            <p className="text-sm text-orange-600">À pagar</p>
            <p className="text-lg font-bold text-orange-700">{formatCurrency(totalPending)}</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg text-center">
            <p className="text-sm text-red-600">Em Atraso</p>
            <p className="text-lg font-bold text-red-700">
              {formatCurrency(overdueInstallments.reduce((sum, i) => sum + i.amount, 0))}
            </p>
          </div>
          <div className={`p-4 rounded-lg text-center ${overspent ? 'bg-red-50' : 'bg-purple-50'}`}>
            <p className={`text-sm ${overspent ? 'text-red-600' : 'text-purple-600'}`}>
              {overspent ? 'Orçamento Estourado' : 'Orçamento Livre'}
            </p>
            <p className={`text-lg font-bold ${overspent ? 'text-red-700' : 'text-purple-700'}`}>
              {formatCurrency(budgetRemaining)}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Expenses Page (redirects to Cashflow)
// ExpensesPage - Página de Despesas com Parcelamento Dinâmico
function ExpensesPage() {
  const { wedding, eventCategories, fetchEventCategories, updateWedding } = useWedding();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [detailExpense, setDetailExpense] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState({ type: "avista", method: "pix" });
  const [refreshKey, setRefreshKey] = useState(0); // Para forçar re-renderização
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState(0);
  const [savingBudget, setSavingBudget] = useState(false);
  const [form, setForm] = useState({
    category: "outros",
    vendor_name: "",
    description: "",
    total_amount: 0,
    payment_type: "a_vista",
    status: "a_vencer",
    due_date_30: "" // Data de vencimento para À Vista 30 dias
  });

  // Onda 5b — Sincroniza condições de pagamento ao abrir o modal (edição/novo)
  useEffect(() => {
    if (!showModal) return;
    if (editExpense && editExpense.payment_terms) {
      setPaymentTerms(editExpense.payment_terms);
    } else {
      setPaymentTerms({ type: "avista", method: "pix" });
    }
  }, [showModal, editExpense]);

  const fetchExpenses = useCallback(async () => {
    if (!wedding) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/expenses`);
      setExpenses([...res.data]); // Cria novo array para forçar React detectar mudança
      setRefreshKey(prev => prev + 1); // Incrementa chave para forçar re-render
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [wedding]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // Atualiza parcelas quando muda o tipo de pagamento ou valor
  useEffect(() => {
    if (form.payment_type === "a_vista" || form.payment_type === "a_vista_30") {
      setInstallments([]);
      // Para À Vista 30 dias, preencher data padrão
      if (form.payment_type === "a_vista_30" && !form.due_date_30) {
        const defaultDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        setForm(prev => ({ ...prev, due_date_30: defaultDate }));
      }
    }
  }, [form.payment_type]);

  const generateInstallments = (count) => {
    const total = form.total_amount || 0;
    const value = Math.round((total / count) * 100) / 100;
    const today = new Date();
    
    const newInstallments = Array.from({ length: count }, (_, i) => {
      const dueDate = new Date(today);
      dueDate.setMonth(dueDate.getMonth() + i + 1);
      return {
        number: i + 1,
        amount: i === count - 1 ? total - (value * (count - 1)) : value,
        due_date: dueDate.toISOString().split('T')[0]
      };
    });
    setInstallments(newInstallments);
  };

  const updateInstallment = (index, field, value) => {
    const updated = [...installments];
    updated[index] = { ...updated[index], [field]: field === 'amount' ? parseFloat(value) || 0 : value };
    setInstallments(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vendor_name || !form.vendor_name.trim()) {
      toast.error("Informe a descrição da despesa");
      return;
    }
    if (!form.total_amount || form.total_amount <= 0) {
      toast.error("Informe um valor total maior que zero");
      return;
    }
    try {
      const payload = {
        category: form.category,
        vendor_name: form.vendor_name,
        description: form.description || "",
        total_amount: form.total_amount,
        payment_terms: paymentTerms,
      };

      if (editExpense) {
        await axios.put(`${API}/weddings/${wedding.id}/expenses/${editExpense.id}`, payload);
        toast.success("Salvo com sucesso!");
      } else {
        await axios.post(`${API}/weddings/${wedding.id}/expenses`, payload);
        toast.success("Salvo com sucesso!");
      }
      setShowModal(false);
      setEditExpense(null);
      setInstallments([]);
      setPaymentTerms({ type: "avista", method: "pix" });
      setForm({
        category: "outros",
        vendor_name: "",
        description: "",
        total_amount: 0,
        payment_type: "a_vista",
        status: "a_vencer",
        due_date_30: ""
      });
      // Atualiza estado imediatamente buscando novos dados
      const res = await axios.get(`${API}/weddings/${wedding.id}/expenses`);
      setExpenses([...res.data]); // Cria novo array para garantir re-render
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao salvar despesa"));
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog({
      title: "Remover despesa",
      message: "Tem certeza que deseja remover esta despesa?",
      confirmLabel: "Remover",
      variant: "danger",
    }))) return;
    try {
      // Atualização otimista - remove imediatamente do estado local
      const newExpenses = expenses.filter(exp => exp.id !== id);
      setExpenses([...newExpenses]); // Cria novo array
      setRefreshKey(prev => prev + 1);
      
      await axios.delete(`${API}/weddings/${wedding.id}/expenses/${id}`);
      toast.success("Despesa removida!");
    } catch (err) {
      // Em caso de erro, recarrega os dados do servidor
      const res = await axios.get(`${API}/weddings/${wedding.id}/expenses`);
      setExpenses([...res.data]);
      setRefreshKey(prev => prev + 1);
      toast.error("Erro ao remover");
    }
  };

  // Usar categorias dinâmicas do evento e formas de pagamento padrão do sistema
  const categories = eventCategories.length > 0 
    ? eventCategories.map(c => ({ value: c.slug, label: c.name }))
    : STANDARD_CATEGORIES;
  const paymentTypes = PAYMENT_TYPES;

  const getCategoryName = (slug) => {
    const cat = eventCategories.find(c => c.slug === slug);
    return cat ? cat.name : (STANDARD_CATEGORIES.find(c => c.value === slug)?.label || slug);
  };

  // Category management state
  const [showCatManager, setShowCatManager] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [deleteCatTarget, setDeleteCatTarget] = useState(null);
  const [moveToCatSlug, setMoveToCatSlug] = useState('outros');
  const [catLoading, setCatLoading] = useState(false);

  const handleCreateCategory = async () => {
    if (!newCatName.trim()) return;
    setCatLoading(true);
    try {
      await axios.post(`${API}/weddings/${wedding.id}/event-categories`, { name: newCatName.trim() });
      setNewCatName('');
      await fetchEventCategories();
      toast.success("Categoria criada!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao criar categoria"));
    } finally { setCatLoading(false); }
  };

  const handleRenameCategory = async (catId) => {
    if (!editingCatName.trim()) return;
    setCatLoading(true);
    try {
      await axios.put(`${API}/weddings/${wedding.id}/event-categories/${catId}`, { name: editingCatName.trim() });
      setEditingCatId(null);
      setEditingCatName('');
      await fetchEventCategories();
      toast.success("Categoria renomeada!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao renomear"));
    } finally { setCatLoading(false); }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCatTarget) return;
    setCatLoading(true);
    try {
      const hasData = deleteCatTarget.expense_count > 0;
      const url = `${API}/weddings/${wedding.id}/event-categories/${deleteCatTarget.id}${hasData ? `?move_to_slug=${moveToCatSlug}` : ''}`;
      await axios.delete(url);
      setDeleteCatTarget(null);
      await fetchEventCategories();
      await fetchExpenses();
      toast.success("Categoria excluída!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao excluir categoria"));
    } finally { setCatLoading(false); }
  };

  // STATUS PADRONIZADOS GLOBAIS: "a_vencer", "paga", "vencida"
  const statusColors = {
    a_vencer: "bg-blue-100 text-blue-700",
    paga: "bg-emerald-100 text-emerald-700",
    vencida: "bg-red-100 text-red-700"
  };

  const statusLabels = {
    a_vencer: "A vencer",
    paga: "Paga",
    vencida: "Em atraso"
  };

  // Usar useMemo para garantir atualização imediata dos totais
  const totalExpenses = useMemo(() => 
    expenses.reduce((sum, exp) => sum + (exp.total_amount || 0), 0), 
    [expenses]
  );
  const totalPaid = useMemo(() => 
    expenses.reduce((sum, exp) => sum + (exp.total_paid || 0), 0), 
    [expenses]
  );
  const totalPending = useMemo(() => totalExpenses - totalPaid, [totalExpenses, totalPaid]);

  // Skeleton loading para melhor UX durante carregamento
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-32 mb-2"></div>
            <div className="h-5 bg-slate-200 rounded w-64"></div>
          </div>
          <div className="h-10 bg-slate-200 rounded w-36 animate-pulse"></div>
        </div>
        {/* Stats Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-slate-200"></div>
                <div className="flex-1">
                  <div className="h-4 bg-slate-200 rounded w-24 mb-2"></div>
                  <div className="h-7 bg-slate-200 rounded w-32"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Table Skeleton */}
        <div className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
          <div className="h-6 bg-slate-200 rounded w-40 mb-4"></div>
          <div className="space-y-3">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-14 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Despesas</h1>
          <p className="text-slate-500">Controle todos os pagamentos do seu evento</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => { setBudgetDraft(wedding?.total_budget || 0); setShowBudgetModal(true); }}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[13px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-100 transition-all"
            data-testid="expenses-edit-budget-btn"
          >
            <DollarSign size={15} />
            Alterar orçamento
          </button>
          <button 
            onClick={() => setShowCatManager(!showCatManager)} 
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${showCatManager ? 'bg-slate-200 text-slate-800' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid="toggle-cat-manager-btn"
          >
            <Settings size={16} />
            Categorias
          </button>
          <Button onClick={() => { setEditExpense(null); setInstallments([]); setShowModal(true); }}>
            <Plus size={18} className="mr-2" />
            <span>Nova despesa</span>
          </Button>
        </div>
      </div>

      {/* Modal: Alterar Orçamento Total — espelha em Configurações */}
      <Modal isOpen={showBudgetModal} onClose={() => !savingBudget && setShowBudgetModal(false)} title="Alterar orçamento total">
        <div className="space-y-4" data-testid="edit-budget-modal">
          <p className="text-[13px] text-slate-500">
            Este valor é o orçamento total do evento. Ele aparece no Painel, nas Despesas e em Configurações.
          </p>
          <MoneyInput
            label="Orçamento total"
            value={budgetDraft || 0}
            onChange={(v) => setBudgetDraft(v)}
            disabled={savingBudget}
          />
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowBudgetModal(false)}
              disabled={savingBudget}
              className="flex-1 py-2.5 px-4 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 text-[13px] font-medium disabled:opacity-50"
              data-testid="cancel-budget-btn"
            >
              Cancelar
            </button>
            <button
              onClick={async () => {
                if (!wedding?.id) return;
                setSavingBudget(true);
                try {
                  await updateWedding({ total_budget: Number(budgetDraft) || 0 });
                  toast.success("Orçamento atualizado!");
                  setShowBudgetModal(false);
                } catch (err) {
                  toast.error(getErrorMessage(err, "Erro ao atualizar orçamento"));
                } finally {
                  setSavingBudget(false);
                }
              }}
              disabled={savingBudget}
              className="flex-1 py-2.5 px-4 rounded-xl text-white text-[13px] font-semibold disabled:opacity-60"
              style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)" }}
              data-testid="save-budget-btn"
            >
              {savingBudget ? <Loader2 className="animate-spin mx-auto" size={16} /> : "Salvar orçamento"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Stats Cards - Apple/Linear premium — clicáveis: scroll para lista */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" key={`stats-${refreshKey}`}>
        <StatCard icon={Receipt} label="Total de Despesas" value={totalExpenses.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} color="blue" onClick={() => document.getElementById('expenses-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
        <StatCard icon={Check} label="Total Pago" value={totalPaid.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} color="emerald" onClick={() => document.getElementById('expenses-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
        <StatCard icon={Clock} label="Total à pagar" value={totalPending.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} color="orange" onClick={() => document.getElementById('expenses-list-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
      </div>

      {/* Category Manager Modal — fullscreen com scroll, CRUD completo */}
      <CategoryManagerModal
        isOpen={showCatManager}
        onClose={() => setShowCatManager(false)}
        categories={eventCategories}
        editingCatId={editingCatId}
        setEditingCatId={setEditingCatId}
        editingCatName={editingCatName}
        setEditingCatName={setEditingCatName}
        newCatName={newCatName}
        setNewCatName={setNewCatName}
        catLoading={catLoading}
        onCreate={handleCreateCategory}
        onRename={handleRenameCategory}
        onDeleteRequest={(cat) => { setDeleteCatTarget(cat); setMoveToCatSlug('outros'); }}
        contextLabel="despesas"
      />

      {/* Delete Category Modal */}
      <Modal isOpen={!!deleteCatTarget} onClose={() => setDeleteCatTarget(null)} title="Excluir Categoria">
        <div className="space-y-4" data-testid="delete-category-modal">
          {deleteCatTarget?.expense_count > 0 ? (
            <React.Fragment>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 font-medium mb-2">
                  A categoria "{deleteCatTarget?.name}" possui {deleteCatTarget?.expense_count} despesa(s).
                </p>
                <p className="text-amber-700 text-sm">
                  Escolha para qual categoria deseja mover as despesas antes de excluir:
                </p>
              </div>
              <select
                value={moveToCatSlug}
                onChange={e => setMoveToCatSlug(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                data-testid="move-category-select"
              >
                {eventCategories.filter(c => c.id !== deleteCatTarget?.id).map(c => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </React.Fragment>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">
                Excluir a categoria "{deleteCatTarget?.name}"?
              </p>
              <p className="text-red-700 text-sm mt-1">Esta categoria não possui despesas.</p>
            </div>
          )}
          <div className="flex gap-3">
            <button onClick={() => setDeleteCatTarget(null)} className="flex-1 py-2.5 px-4 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 text-sm font-medium" data-testid="cancel-delete-cat-btn">
              Cancelar
            </button>
            <button onClick={handleDeleteCategory} disabled={catLoading} className="flex-1 py-2.5 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50" data-testid="confirm-delete-cat-btn">
              {catLoading ? <Loader2 className="animate-spin mx-auto" size={18} /> : 'Excluir categoria'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Expenses List */}
      <Card id="expenses-list-section" className="overflow-hidden scroll-mt-6">
        {expenses.length === 0 ? (
          <div className="p-12 text-center">
            <Receipt className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-lg font-medium text-slate-800 mb-2">Nenhuma despesa cadastrada ainda.</h3>
            <p className="text-slate-500 mb-4">Adicione uma despesa para acompanhar seus pagamentos.</p>
            <Button onClick={() => setShowModal(true)}>Nova despesa</Button>
          </div>
        ) : (
          <>
          {/* ═══ MOBILE: Card ultra clean (Full Pattern — igual Convidados) ═══ */}
          <div className="md:hidden divide-y divide-zinc-100">
            {expenses.map(expense => {
              const expenseStatus = getExpenseStatus(expense);
              const allPaid = expenseStatus.status === PAYMENT_STATUS.PAGA;
              const isOverdue = expenseStatus.status === PAYMENT_STATUS.VENCIDA;
              // Avatar deterministic colors derivado do vendor_name
              const palettes = [
                'bg-violet-100 text-violet-700',
                'bg-emerald-100 text-emerald-700',
                'bg-sky-100 text-sky-700',
                'bg-amber-100 text-amber-700',
                'bg-rose-100 text-rose-700',
                'bg-indigo-100 text-indigo-700',
                'bg-teal-100 text-teal-700',
                'bg-fuchsia-100 text-fuchsia-700',
              ];
              const hash = [...((expense.vendor_name || '?'))].reduce((a, c) => a + c.charCodeAt(0), 0);
              const avatarBg = palettes[hash % palettes.length];
              const statusTextColor = allPaid
                ? 'text-emerald-600'
                : isOverdue
                ? 'text-rose-500'
                : 'text-blue-600';
              return (
                <button
                  type="button"
                  key={expense.id}
                  onClick={() => { try { navigator.vibrate?.(8); } catch {} setDetailExpense(expense); }}
                  className="tap-press w-full text-left flex items-center gap-3.5 px-4 py-3.5"
                  data-testid={`expense-card-mobile-${expense.id}`}
                >
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-[15px] tracking-tight ${avatarBg}`}>
                    {(expense.vendor_name || '?').trim().charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[15.5px] font-medium text-zinc-900 truncate leading-snug">{expense.vendor_name}</h3>
                    <p className="text-[12.5px] text-zinc-500 mt-0.5 truncate">
                      <span className="font-medium text-zinc-700 tabular-nums">
                        {(expense.total_amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </span>
                      <span className="mx-1.5 text-zinc-300">·</span>
                      <span className={statusTextColor}>{expenseStatus.label}</span>
                    </p>
                  </div>
                  <ChevronRight size={16} className="text-zinc-300 flex-shrink-0" />
                </button>
              );
            })}
          </div>

          {/* ═══ DESKTOP: Tabela (>= md) ═══ */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-semibold text-slate-600">Despesa</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-600">Categoria</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-600">Valor</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-600">Vencimento</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-600">Status</th>
                  <th className="text-left p-4 text-sm font-semibold text-slate-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(expense => {
                  // Usar função padronizada para calcular status
                  const expenseStatus = getExpenseStatus(expense);
                  const nextInstallment = expense.installments?.find(i => !i.is_paid);
                  const nextDueDate = nextInstallment?.due_date;
                  const allPaid = expenseStatus.status === PAYMENT_STATUS.PAGA;
                  
                  return (
                    <tr key={expense.id} onClick={() => setDetailExpense(expense)} className={`border-b last:border-0 hover:bg-violet-50/40 cursor-pointer transition-colors ${allPaid ? "bg-emerald-50/30" : ""}`} data-testid={`expense-row-${expense.id}`} title="Ver detalhes e parcelas">
                      <td className="p-4">
                        <p className="font-semibold text-slate-800">{expense.vendor_name}</p>
                        <p className="text-sm text-slate-500">{expense.description}</p>
                      </td>
                      <td className="p-4">
                        <span className="px-3 py-1.5 bg-slate-100 rounded-lg text-sm text-slate-700 font-medium">
                          {getCategoryName(expense.category)}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="font-bold text-slate-800">{expense.total_amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                        <p className="text-xs text-emerald-600 font-medium">{(expense.total_paid || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} pago</p>
                      </td>
                      <td className="p-4">
                        {allPaid ? (
                          <div className="flex items-center gap-2 text-emerald-600">
                            <Check size={16} />
                            <span className="text-sm font-medium">Paga</span>
                          </div>
                        ) : nextDueDate ? (
                          (() => {
                            const alert = getAlertText(nextDueDate, false);
                            const isOverdue = expenseStatus.status === PAYMENT_STATUS.VENCIDA;
                            return (
                              <div className="space-y-1">
                                <div className={`flex items-center gap-2 ${isOverdue ? 'text-red-600' : 'text-slate-600'}`}>
                                  <Calendar size={16} />
                                  <span className="text-sm font-medium">
                                    {new Date(nextDueDate + 'T12:00:00').toLocaleDateString('pt-BR')}
                                  </span>
                                </div>
                                {alert && (
                                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${
                                    alert.color === 'red' ? 'bg-red-100 text-red-700' :
                                    alert.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}>
                                    {alert.text}
                                  </span>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${expenseStatus.color}`}>
                          {expenseStatus.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <button onClick={(e) => { e.stopPropagation(); setDetailExpense(expense); }} className="p-2 hover:bg-violet-100 rounded-xl text-violet-600 transition-colors" title="Ver detalhes e parcelas" data-testid={`expense-detail-btn-${expense.id}`}>
                            <Eye size={18} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setEditExpense(expense); setForm(expense); setShowModal(true); }} className="p-2 hover:bg-slate-100 rounded-xl text-slate-600 transition-colors" title="Editar">
                            <Edit2 size={18} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(expense.id); }} className="p-2 hover:bg-red-50 rounded-xl text-red-500 transition-colors" title="Excluir">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Card>

      {/* Modal de Nova Despesa com Parcelamento Dinâmico */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setEditExpense(null); setInstallments([]); }} title={editExpense ? "Editar despesa" : "Nova despesa"} fullScreen>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* CATEGORIA - Primeiro campo obrigatório */}
          <Select label="Categoria" required value={form.category} onChange={e => setForm({...form, category: e.target.value})} options={eventCategories.length > 0 ? eventCategories.map(c => ({value: c.slug, label: c.name})) : STANDARD_CATEGORIES} />
          
          <Input 
            label="Descrição da Despesa *" 
            value={form.vendor_name} 
            onChange={e => setForm({...form, vendor_name: e.target.value})} 
            placeholder="Ex: Buffet do João, Decoração Flores..."
            required 
          />
          
          <Input 
            label="Detalhes (opcional)" 
            value={form.description} 
            onChange={e => setForm({...form, description: e.target.value})} 
            placeholder="Informações adicionais..."
          />
          
          <MoneyInput 
            label="Valor Total (R$) *" 
            value={form.total_amount || 0} 
            onChange={(val) => setForm({...form, total_amount: val})} 
          />

          {/* Onda 5b — Condições de Pagamento unificadas (mesmo motor dos Contratos) */}
          <div className="pt-1" data-testid="expense-payment-terms">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 mb-2">Condições de Pagamento *</p>
            <PaymentTermsForm
              totalAmount={form.total_amount || 0}
              value={paymentTerms}
              onChange={setPaymentTerms}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => { setShowModal(false); setEditExpense(null); setInstallments([]); }} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1">
              {editExpense ? "Salvar" : "Salvar"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Bottom sheet de detalhes — Full Pattern (estilo Convidados, 75dvh) */}
      {detailExpense && (
        <Vaul.Root open={!!detailExpense} onOpenChange={(o) => { if (!o) setDetailExpense(null); }} dismissible>
          <Vaul.Portal>
            <Vaul.Overlay className="fixed inset-0 z-[10010] bg-black/40 backdrop-blur-[3px]" />
            <Vaul.Content
              className="fixed inset-x-0 bottom-0 z-[10011] flex flex-col bg-white rounded-t-3xl outline-none shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.18)]"
              style={{ height: '75dvh' }}
              data-testid="expense-detail-sheet"
            >
              <Vaul.Title className="sr-only">Detalhes da despesa</Vaul.Title>
              <Vaul.Description className="sr-only">Informações detalhadas, parcelamento e ações</Vaul.Description>
              <div className="mx-auto mt-2.5 mb-1 h-1.5 w-12 rounded-full bg-zinc-200 shrink-0" />
              {(() => {
                const exp = detailExpense;
                const expStatus = getExpenseStatus(exp);
                const insts = exp.installments || [];
                const paidAmt = exp.total_paid || 0;
                const remaining = (exp.total_amount || 0) - paidAmt;
                const palettes = [
                  'bg-violet-100 text-violet-700',
                  'bg-emerald-100 text-emerald-700',
                  'bg-sky-100 text-sky-700',
                  'bg-amber-100 text-amber-700',
                  'bg-rose-100 text-rose-700',
                  'bg-indigo-100 text-indigo-700',
                  'bg-teal-100 text-teal-700',
                  'bg-fuchsia-100 text-fuchsia-700',
                ];
                const hash = [...((exp.vendor_name || '?'))].reduce((a, c) => a + c.charCodeAt(0), 0);
                const avatarBg = palettes[hash % palettes.length];
                const pendingCount = insts.filter(i => !i.is_paid).length;
                const togglePaid = async (inst) => {
                  try {
                    const token = localStorage.getItem('token');
                    const action = inst.is_paid ? 'unpay' : 'pay';
                    await axios.patch(
                      `${API}/weddings/${wedding.id}/installments/${inst.id}/${action}`,
                      {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    try { navigator.vibrate?.(8); } catch {}
                    toast.success(inst.is_paid ? 'Parcela marcada como não paga' : 'Parcela marcada como paga');
                    const res = await axios.get(`${API}/weddings/${wedding.id}/expenses`);
                    setExpenses([...res.data]);
                    const fresh = res.data.find(e => e.id === exp.id);
                    if (fresh) setDetailExpense(fresh);
                  } catch (err) {
                    toast.error(getErrorMessage(err, 'Erro ao atualizar parcela'));
                  }
                };
                const markAllPaid = async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const pending = insts.filter(i => !i.is_paid);
                    await Promise.all(pending.map(i =>
                      axios.patch(
                        `${API}/weddings/${wedding.id}/installments/${i.id}/pay`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                      )
                    ));
                    try { navigator.vibrate?.(12); } catch {}
                    toast.success('Despesa quitada!');
                    const res = await axios.get(`${API}/weddings/${wedding.id}/expenses`);
                    setExpenses([...res.data]);
                    const fresh = res.data.find(e => e.id === exp.id);
                    if (fresh) setDetailExpense(fresh);
                  } catch (err) {
                    toast.error(getErrorMessage(err, 'Erro ao quitar despesa'));
                  }
                };
                return (
                  <>
                    {/* Header */}
                    <div className="px-5 pt-3 pb-4 flex items-center gap-3.5 border-b border-zinc-100 shrink-0">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-[20px] tracking-tight ${avatarBg}`}>
                        {(exp.vendor_name || '?').trim().charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10.5px] uppercase tracking-[0.14em] text-zinc-400 font-medium">{getCategoryName(exp.category)}</p>
                        <h3 className="text-[17px] font-semibold text-zinc-900 leading-tight tracking-tight truncate mt-0.5">{exp.vendor_name}</h3>
                        {exp.description && <p className="text-[12.5px] text-zinc-500 mt-0.5 truncate">{exp.description}</p>}
                      </div>
                      <button
                        onClick={() => setDetailExpense(null)}
                        className="w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 active:bg-zinc-300 flex items-center justify-center text-zinc-600 transition-colors flex-shrink-0"
                        aria-label="Fechar"
                        data-testid="expense-sheet-close"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    {/* Conteúdo rolável */}
                    <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 space-y-6"
                         style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' }}>
                      {/* Valor + Resumo */}
                      <div>
                        <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2">Valor total</p>
                        <div className="flex items-baseline gap-2.5">
                          <span className="text-[30px] font-semibold text-zinc-900 tabular-nums leading-none tracking-tight">
                            {(exp.total_amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </span>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${expStatus.color}`}>{expStatus.label}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2.5 mt-4">
                          <div className="rounded-2xl bg-emerald-50/70 border border-emerald-100 px-4 py-3">
                            <p className="text-[10.5px] uppercase tracking-wider text-emerald-700 font-medium">Pago</p>
                            <p className="text-[16px] font-semibold text-emerald-900 tabular-nums mt-0.5">
                              {paidAmt.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </p>
                          </div>
                          <div className="rounded-2xl bg-amber-50/70 border border-amber-100 px-4 py-3">
                            <p className="text-[10.5px] uppercase tracking-wider text-amber-700 font-medium">A pagar</p>
                            <p className="text-[16px] font-semibold text-amber-900 tabular-nums mt-0.5">
                              {Math.max(0, remaining).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Parcelas (tap pra alternar pago) */}
                      <div>
                        <div className="flex items-center justify-between mb-2.5">
                          <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider">Parcelas</p>
                          <span className="text-[11px] text-zinc-500">{insts.filter(i => i.is_paid).length}/{insts.length} pagas</span>
                        </div>
                        {insts.length === 0 ? (
                          <div className="text-[13px] text-zinc-500 py-4 text-center bg-zinc-50 rounded-2xl">Sem parcelas registradas.</div>
                        ) : (
                          <div className="space-y-2">
                            {insts.map((inst, idx) => (
                              <button
                                type="button"
                                key={inst.id || idx}
                                onClick={() => togglePaid(inst)}
                                className="tap-press w-full flex items-center gap-3 p-3 rounded-2xl border border-zinc-200/70 bg-white text-left"
                                data-testid={`expense-installment-${idx}`}
                              >
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-semibold flex-shrink-0 ${inst.is_paid ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-700'}`}>
                                  {inst.is_paid ? <Check size={16} /> : (inst.number || idx + 1)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[13.5px] font-semibold text-zinc-900 tabular-nums">
                                    {(inst.amount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                  </p>
                                  <p className="text-[11.5px] text-zinc-500">
                                    {inst.is_paid
                                      ? `Paga em ${(inst.payment_date || inst.due_date) ? new Date((inst.payment_date || inst.due_date) + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}`
                                      : `Vence em ${inst.due_date ? new Date(inst.due_date + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}`}
                                  </p>
                                </div>
                                <span className={`text-[10.5px] font-medium ${inst.is_paid ? 'text-emerald-600' : 'text-zinc-400'}`}>
                                  {inst.is_paid ? 'Desmarcar' : 'Marcar paga'}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Ações */}
                      <div>
                        <p className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider mb-2.5">Ações</p>
                        <div className="space-y-2">
                          {pendingCount > 0 && (
                            <button
                              onClick={markAllPaid}
                              className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-emerald-50/70 hover:bg-emerald-50 border border-emerald-100/80 text-left"
                              data-testid="expense-sheet-mark-all-paid"
                            >
                              <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                                <Check size={15} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="block text-[14.5px] font-medium text-zinc-900">Marcar todas como pagas</span>
                                <span className="block text-[11.5px] text-zinc-500">Quita as {pendingCount} parcela(s) pendente(s)</span>
                              </div>
                              <ChevronRight size={16} className="text-zinc-300 ml-auto" />
                            </button>
                          )}

                          <button
                            onClick={() => { setEditExpense(exp); setForm(exp); setDetailExpense(null); setTimeout(() => setShowModal(true), 220); }}
                            className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-zinc-50 hover:bg-zinc-100 border border-zinc-100 text-left"
                            data-testid="expense-sheet-edit"
                          >
                            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-zinc-700 ring-1 ring-zinc-200/70">
                              <Edit2 size={15} />
                            </div>
                            <span className="text-[14.5px] font-medium text-zinc-900">Editar despesa</span>
                            <ChevronRight size={16} className="text-zinc-300 ml-auto" />
                          </button>

                          <button
                            onClick={() => { const id = exp.id; setDetailExpense(null); setTimeout(() => handleDelete(id), 220); }}
                            className="tap-press w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-rose-50/70 hover:bg-rose-50 border border-rose-100/80 text-left"
                            data-testid="expense-sheet-delete"
                          >
                            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-rose-600 ring-1 ring-rose-200/80">
                              <Trash2 size={15} />
                            </div>
                            <span className="text-[14.5px] font-medium text-rose-600">Excluir despesa</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </Vaul.Content>
          </Vaul.Portal>
        </Vaul.Root>
      )}
    </div>
  );
}

// Tasks Page
function TasksPage() {
  // Onda 2 — componente movido para /app/frontend/src/pages/TasksPage.jsx (split do monolítico).
  // Renderiza um wrapper fino que injeta os contexts.
  const { wedding } = useWedding();
  const isMobile = useIsMobile();
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <LazyTasksPage wedding={wedding} isMobile={isMobile} />
    </Suspense>
  );
}

function InspirationsRoute() {
  // Onda 4 — wrapper de InspirationsPage (lazy). Injeta wedding, user, isMobile.
  const { wedding } = useWedding();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <LazyInspirationsPage wedding={wedding} user={user} isMobile={isMobile} />
    </Suspense>
  );
}
function ReportsRoute() {
  // Onda 6 — wrapper de ReportsPage (lazy). Injeta wedding, user.
  const { wedding } = useWedding();
  const { user } = useAuth();
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <LazyReportsPage wedding={wedding} user={user} />
    </Suspense>
  );
}
function SeatingRoute() {
  // Onda 7 — wrapper de SeatingPage (lazy). Injeta wedding do contexto do App.
  const { wedding } = useWedding();
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <LazySeatingPage wedding={wedding} />
    </Suspense>
  );
}
// Tables Page - Layout Visual das Mesas
function TablesPage() {
  const { wedding } = useWedding();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showTableModal, setShowTableModal] = useState(false);
  const [showOrganizeModal, setShowOrganizeModal] = useState(false);
  const [tablesConfig, setTablesConfig] = useState({ num_mesas: 10, lugares_por_mesa: 8, lugares_mesa_padrinhos: 10 });
  const [loadingOrganize, setLoadingOrganize] = useState(false);
  const [invites, setInvites] = useState([]);
  const [confirmedCount, setConfirmedCount] = useState(0);

  // Buscar organização das mesas quando wedding estiver disponível
  useEffect(() => {
    let isMounted = true;
    
    // Aguardar wedding estar disponível
    if (!wedding?.id) {
      return;
    }
    
    const fetchTables = async () => {
      setLoading(true);
      
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        
        const [tablesRes, invitesRes] = await Promise.all([
          axios.get(`${API}/weddings/${wedding.id}/tables`, { headers, timeout: 15000 }),
          axios.get(`${API}/weddings/${wedding.id}/invites`, { headers, timeout: 15000 })
        ]);
        
        if (isMounted) {
          const tablesData = tablesRes.data?.mesas || [];
          setTables(tablesData);
          setInvites(invitesRes.data || []);
          
          const confirmed = (invitesRes.data || []).filter(i => i.status === 'confirmado')
            .reduce((acc, i) => acc + (i.adults_count || 0) + (i.children_count || 0), 0);
          setConfirmedCount(confirmed);
        }
      } catch (err) {
        console.error('Erro ao buscar mesas:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchTables();
    
    return () => { isMounted = false; };
  }, [wedding?.id]);

  // Organizar mesas com IA
  const handleOrganizeTables = async () => {
    if (confirmedCount === 0) {
      toast.error("Nenhum convidado confirmado para organizar nas mesas");
      return;
    }
    
    setLoadingOrganize(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/ai/organize-tables`, {
        wedding_id: wedding.id,
        num_mesas: tablesConfig.num_mesas,
        lugares_por_mesa: tablesConfig.lugares_por_mesa,
        lugares_mesa_padrinhos: tablesConfig.lugares_mesa_padrinhos
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.mesas && res.data.mesas.length > 0) {
        // Salvar organização
        await axios.post(`${API}/weddings/${wedding.id}/tables`, {
          mesas: res.data.mesas,
          justificativa: res.data.justificativa
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Atualizar estado local
        setTables(res.data.mesas);
        toast.success(`Mesas organizadas! ${res.data.mesas.length} mesas criadas.`);
        setShowOrganizeModal(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao organizar mesas");
    } finally {
      setLoadingOrganize(false);
    }
  };

  // Determinar estado visual da mesa
  const getTableState = (mesa) => {
    const ocupacao = mesa.convidados?.length || 0;
    const capacidade = mesa.capacidade || tablesConfig.lugares_por_mesa;
    const isVip = mesa.isVip || mesa.nome?.toLowerCase().includes('padrinho');
    
    if (ocupacao === 0) return { state: 'empty', label: 'Vazia', bg: 'bg-slate-100', border: 'border-slate-200', text: 'text-slate-400' };
    if (ocupacao < capacidade) return { state: 'partial', label: 'Incompleta', bg: 'bg-slate-200', border: 'border-slate-300', text: 'text-slate-600' };
    return { state: 'full', label: 'Cheia', bg: 'bg-slate-400', border: 'border-slate-500', text: 'text-white' };
  };

  // Abrir modal com detalhes da mesa
  const openTableDetails = (mesa) => {
    setSelectedTable(mesa);
    setShowTableModal(true);
  };

  // Mostrar loading enquanto wedding não estiver disponível ou dados carregando
  if (!wedding) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }
  
  // Skeleton loading para melhor UX durante carregamento
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-52 mb-2"></div>
            <div className="h-5 bg-slate-200 rounded w-80"></div>
          </div>
          <div className="h-10 bg-slate-200 rounded w-40 animate-pulse"></div>
        </div>
        {/* Content Skeleton */}
        <div className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[1,2,3,4].map(i => (
              <div key={i} className="text-center">
                <div className="h-8 bg-slate-200 rounded w-12 mx-auto mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-16 mx-auto"></div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Organização de Mesas</h1>
          <p className="text-slate-500 mt-1">Visualize a distribuição dos convidados nas mesas do evento.</p>
        </div>
        {tables.length === 0 && (
          <Button 
            onClick={() => setShowOrganizeModal(true)} 
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="organize-tables-btn"
          >
            <Sparkles size={18} className="mr-2" />
            Organizar com IA
          </Button>
        )}
      </div>

      {/* Estado vazio */}
      {tables.length === 0 ? (
        <Card className="p-12 text-center">
          <LayoutGrid size={64} className="mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhuma mesa organizada</h3>
          <p className="text-slate-500 mb-6 max-w-md mx-auto">
            Use a IA para organizar automaticamente seus convidados nas mesas. 
            Você precisará ter pelo menos 1 convidado confirmado.
          </p>
          <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <Users size={16} />
              {confirmedCount} confirmados
            </span>
          </div>
        </Card>
      ) : (
        <React.Fragment>
          {/* Estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-800">{tables.length}</p>
              <p className="text-sm text-slate-500">Mesas</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{confirmedCount}</p>
              <p className="text-sm text-slate-500">Confirmados</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">
                {tables.reduce((acc, m) => acc + (m.convidados?.length || 0), 0)}
              </p>
              <p className="text-sm text-slate-500">Alocados</p>
            </Card>
            <Card className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">
                {tables.filter(m => m.isVip || m.nome?.toLowerCase().includes('padrinho')).length}
              </p>
              <p className="text-sm text-slate-500">Mesa VIP</p>
            </Card>
          </div>

          {/* Legenda */}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="text-slate-600 font-medium">Legenda:</span>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-slate-100 border border-slate-200"></div>
              <span className="text-slate-500">Vazia</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-slate-200 border border-slate-300"></div>
              <span className="text-slate-500">Incompleta</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-slate-400 border border-slate-500"></div>
              <span className="text-slate-500">Cheia</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-slate-200 border-2 border-amber-400"></div>
              <span className="text-slate-500">Padrinhos</span>
            </div>
          </div>

          {/* Grid de Mesas */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {tables.map((mesa, index) => {
              const { state, bg, border, text } = getTableState(mesa);
              const isVip = mesa.isVip || mesa.nome?.toLowerCase().includes('padrinho');
              const ocupacao = mesa.convidados?.length || 0;
              const capacidade = mesa.capacidade || tablesConfig.lugares_por_mesa;
              
              return (
                <button
                  key={mesa.numero || index}
                  onClick={() => openTableDetails(mesa)}
                  className={`
                    relative aspect-square rounded-full ${bg} ${border} border-2
                    flex flex-col items-center justify-center
                    transition-all duration-200 hover:scale-105 hover:shadow-lg
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                    ${isVip ? 'ring-2 ring-amber-400 ring-offset-2' : ''}
                  `}
                  data-testid={`table-${mesa.numero || index}`}
                >
                  {/* Número da mesa */}
                  <span className={`text-lg font-bold ${text}`}>
                    {mesa.numero || index + 1}
                  </span>
                  
                  {/* Capacidade */}
                  <span className={`text-xs ${text} opacity-80`}>
                    {ocupacao} / {capacidade}
                  </span>
                  
                  {/* Badge VIP */}
                  {isVip && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                      <Crown size={12} className="text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Ações */}
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Clique em uma mesa para ver os convidados alocados.
              </p>
              <Button 
                onClick={() => setShowOrganizeModal(true)} 
                variant="secondary"
                data-testid="reorganize-tables-btn"
              >
                <RefreshCw size={16} className="mr-2" />
                Reorganizar
              </Button>
            </div>
          </Card>
        </React.Fragment>
      )}

      {/* Modal de Detalhes da Mesa */}
      <Modal 
        isOpen={showTableModal} 
        onClose={() => setShowTableModal(false)} 
        title={
          selectedTable?.nome || 
          `Mesa ${selectedTable?.numero || ''}`
        }
      >
        {selectedTable && (
          <div className="space-y-4">
            {/* Info da mesa */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center
                  ${getTableState(selectedTable).bg} ${getTableState(selectedTable).border} border-2
                `}>
                  <span className={`text-lg font-bold ${getTableState(selectedTable).text}`}>
                    {selectedTable.numero}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-slate-800">
                    {selectedTable.nome || `Mesa ${selectedTable.numero}`}
                  </p>
                  <p className="text-sm text-slate-500">
                    {selectedTable.convidados?.length || 0} / {selectedTable.capacidade || tablesConfig.lugares_por_mesa} lugares
                  </p>
                </div>
              </div>
              {(selectedTable.isVip || selectedTable.nome?.toLowerCase().includes('padrinho')) && (
                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium flex items-center gap-1">
                  <Crown size={12} />
                  Padrinhos
                </span>
              )}
            </div>

            {/* Lista de convidados */}
            <div>
              <h4 className="font-medium text-slate-700 mb-2">Convidados</h4>
              {selectedTable.convidados && selectedTable.convidados.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedTable.convidados.map((convidado, idx) => {
                    const nome = typeof convidado === 'string' ? convidado : convidado.nome;
                    const papel = typeof convidado !== 'string' && convidado.papel ? convidado.papel : null;
                    
                    return (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-2 bg-white border border-slate-100 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-700 text-sm font-medium">
                              {nome?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <span className="text-slate-700">{nome}</span>
                        </div>
                        {papel && (
                          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                            {papel}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-4">
                  Nenhum convidado alocado nesta mesa.
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Organização */}
      <Modal 
        isOpen={showOrganizeModal} 
        onClose={() => setShowOrganizeModal(false)} 
        title="Organizar Mesas com IA"
      >
        <div className="space-y-4">
          <p className="text-slate-600 text-sm">
            A IA irá distribuir os {confirmedCount} convidados confirmados nas mesas, 
            respeitando relacionamentos e preferências.
          </p>
          
          <div className="grid grid-cols-1 gap-4">
            <Input
              label="Número de mesas"
              type="number"
              value={tablesConfig.num_mesas}
              onChange={(e) => setTablesConfig({ ...tablesConfig, num_mesas: parseInt(e.target.value) || 10 })}
              min={1}
              max={50}
            />
            <Input
              label="Lugares por mesa"
              type="number"
              value={tablesConfig.lugares_por_mesa}
              onChange={(e) => setTablesConfig({ ...tablesConfig, lugares_por_mesa: parseInt(e.target.value) || 8 })}
              min={4}
              max={12}
            />
            <Input
              label="Lugares mesa padrinhos"
              type="number"
              value={tablesConfig.lugares_mesa_padrinhos}
              onChange={(e) => setTablesConfig({ ...tablesConfig, lugares_mesa_padrinhos: parseInt(e.target.value) || 10 })}
              min={4}
              max={20}
            />
          </div>

          {tables.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              <strong>Atenção:</strong> Reorganizar irá substituir a organização atual das mesas.
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button 
              variant="secondary" 
              onClick={() => setShowOrganizeModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleOrganizeTables}
              disabled={loadingOrganize || confirmedCount === 0}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {loadingOrganize ? (
                <React.Fragment>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Organizando...
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Sparkles size={18} className="mr-2" />
                  Organizar Mesas
                </React.Fragment>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// LGPD — Seção de Privacidade (Exportar dados + Excluir conta)
// ════════════════════════════════════════════════════════════════════
function PrivacySection() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/me/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `liventy-meus-dados-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Seus dados foram exportados (LGPD).");
    } catch (err) {
      toast.error(getErrorMessage(err, "Não foi possível exportar seus dados."));
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== "DELETAR MINHA CONTA") {
      setError("Digite exatamente: DELETAR MINHA CONTA");
      return;
    }
    if (!password) {
      setError("Informe sua senha.");
      return;
    }
    setDeleting(true);
    setError("");
    try {
      await axios.request({
        url: `${API}/me/account`,
        method: "DELETE",
        data: { password, confirmation: confirmText },
      });
      // Limpar TODOS os tokens/sessions locais
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch {}
      toast.success("Conta excluída. Você será redirecionado.");
      setTimeout(() => {
        logout();
        navigate("/", { replace: true });
        window.location.href = "/";
      }, 1200);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setError("Senha incorreta.");
      else if (status === 429) setError("Muitas tentativas. Tente novamente em 1 hora.");
      else setError(getErrorMessage(err, "Não foi possível excluir a conta."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="privacy-section">
      <Card className="p-6">
        <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
          <Shield size={20} className="text-violet-600" />
          Seus dados (LGPD)
        </h3>
        <p className="text-sm text-slate-500 mb-5">
          Aqui você controla seus dados pessoais conforme a <strong>Lei Geral de Proteção de Dados</strong>. Consulte nossa <Link to="/privacidade" className="text-violet-700 underline" data-testid="privacy-link-policy">Política de Privacidade</Link>.
        </p>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* Exportar dados */}
          <div className="rounded-2xl border border-slate-200 p-5 bg-gradient-to-br from-violet-50/40 to-white">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shrink-0">
                <Download size={18} className="text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 text-[15px]">Exportar meus dados</h4>
                <p className="text-[12.5px] text-slate-500 mt-0.5">Baixe um arquivo JSON com tudo o que mantemos sobre você (direito de portabilidade).</p>
              </div>
            </div>
            <Button
              onClick={handleExport}
              disabled={exporting}
              variant="secondary"
              className="w-full"
              data-testid="privacy-export-btn"
            >
              {exporting ? <><Loader2 className="animate-spin mr-2" size={15} />Gerando arquivo…</> : <><Download size={15} className="mr-2" />Baixar JSON</>}
            </Button>
          </div>

          {/* Excluir conta */}
          <div className="rounded-2xl border border-rose-200 p-5 bg-gradient-to-br from-rose-50/60 to-white">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shrink-0">
                <Trash2 size={18} className="text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 text-[15px]">Excluir minha conta</h4>
                <p className="text-[12.5px] text-slate-500 mt-0.5">Apaga permanentemente seus dados. Assinatura ativa é cancelada automaticamente.</p>
              </div>
            </div>
            <Button
              onClick={() => { setDeleteOpen(true); setConfirmText(""); setPassword(""); setError(""); }}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="privacy-delete-btn"
            >
              <Trash2 size={15} className="mr-2" />
              Excluir conta
            </Button>
          </div>
        </div>

        <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-4">
          <p className="text-[12px] text-slate-600 leading-relaxed">
            <strong>O que apagamos:</strong> seu perfil, eventos, convidados, despesas, mensagens, fotos e qualquer dado vinculado à sua conta.
            Assinaturas ativas na Stripe são canceladas imediatamente. Backups podem reter os dados por até 30 dias antes da remoção completa.
          </p>
        </div>
      </Card>

      {/* Bottom-sheet style modal (mobile-first premium) */}
      <Modal
        isOpen={deleteOpen}
        onClose={() => { if (!deleting) setDeleteOpen(false); }}
        title="Excluir conta — confirmação"
      >
        <div className="space-y-4" data-testid="privacy-delete-modal">
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-4">
            <p className="text-[13px] text-rose-900 font-semibold flex items-start gap-2">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              Essa ação é permanente e não pode ser desfeita.
            </p>
            <ul className="text-[12.5px] text-rose-800 mt-2 pl-6 list-disc space-y-1">
              <li>Todos os seus eventos, convidados e despesas serão apagados.</li>
              <li>Sua assinatura no Stripe será cancelada agora.</li>
              <li>Você não conseguirá recuperar esses dados depois.</li>
            </ul>
          </div>

          <div>
            <label className="block text-[12.5px] font-semibold text-slate-800 mb-1.5">
              Para confirmar, digite: <span className="text-rose-700 font-mono">DELETAR MINHA CONTA</span>
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => { setConfirmText(e.target.value.toUpperCase()); setError(""); }}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              className="w-full h-12 px-4 rounded-xl bg-white border-[1.5px] border-slate-200 text-[14px] focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100 transition-all"
              placeholder="DELETAR MINHA CONTA"
              data-testid="privacy-delete-confirm-input"
            />
          </div>

          <div>
            <label className="block text-[12.5px] font-semibold text-slate-800 mb-1.5">Sua senha</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(""); }}
                autoComplete="current-password"
                className="w-full h-12 pl-4 pr-16 rounded-xl bg-white border-[1.5px] border-slate-200 text-[14px] focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100 transition-all"
                placeholder="Sua senha atual"
                data-testid="privacy-delete-password"
              />
              <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-1 rounded-md hover:bg-slate-100 text-[11px] text-slate-500">
                {showPw ? "ocultar" : "mostrar"}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-[12.5px] text-rose-700 font-medium" data-testid="privacy-delete-error">{error}</p>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="secondary"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
              className="flex-1"
              data-testid="privacy-delete-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting || confirmText !== "DELETAR MINHA CONTA" || !password}
              className="flex-1 bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
              data-testid="privacy-delete-confirm-btn"
            >
              {deleting ? <><Loader2 className="animate-spin mr-2" size={15} />Excluindo…</> : "Excluir minha conta"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// Settings Page
function SubscriptionSection() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState('annual'); // 'monthly' | 'annual'
  const [selectedTier, setSelectedTier] = useState(null); // 'personal' | 'professional' — set on open
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/me/subscription`);
      setInfo(res.data);
    } catch (err) {
      toast.error("Não foi possível carregar dados da assinatura");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/payments/portal`, {
        return_url: window.location.href
      });
      if (res.data?.url) {
        redirectToCheckout(res.data.url);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível abrir o portal de gerenciamento");
    } finally {
      setPortalLoading(false);
    }
  };

  const cancelSubscription = async () => {
    if (!(await confirmDialog({
      title: "Cancelar assinatura",
      message: "Tem certeza que deseja cancelar sua assinatura?\n\nVocê manterá acesso premium até o fim do período já pago. Após isso, sua conta voltará para o plano gratuito. Você pode reativar a qualquer momento antes do vencimento.",
      confirmLabel: "Cancelar assinatura",
      cancelLabel: "Manter plano",
      variant: "danger",
    }))) {
      return;
    }
    setPortalLoading(true);
    try {
      await axios.post(`${API}/payments/cancel-subscription`);
      toast.success("Cancelamento programado. Você manterá acesso até o fim do período pago.");
      await fetchInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível cancelar a assinatura");
    } finally {
      setPortalLoading(false);
    }
  };

  const resumeSubscription = async () => {
    setPortalLoading(true);
    try {
      await axios.post(`${API}/payments/resume-subscription`);
      toast.success("Assinatura reativada com sucesso!");
      await fetchInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível reativar a assinatura");
    } finally {
      setPortalLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
    } catch { return iso; }
  };

  if (loading) {
    return (
      <Card className="p-8">
        <div className="flex items-center gap-3 text-zinc-500">
          <Loader2 size={18} className="animate-spin" /> Carregando informações da assinatura…
        </div>
      </Card>
    );
  }

  const status = info?.status || "none";
  const hasSub = info?.has_active_subscription;
  const canceled = info?.cancel_at_period_end;
  const trialActive = info?.trial_active;

  const statusConfig = {
    active:        { label: "Assinatura ativa",       color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
    trialing:      { label: "Em período de teste",    color: "bg-violet-100 text-violet-700",   dot: "bg-violet-500" },
    past_due:      { label: "Pagamento pendente",     color: "bg-amber-100 text-amber-700",     dot: "bg-amber-500" },
    canceled:      { label: "Cancelada",              color: "bg-zinc-100 text-zinc-600",       dot: "bg-zinc-400" },
    unpaid:        { label: "Não paga",               color: "bg-rose-100 text-rose-700",       dot: "bg-rose-500" },
    incomplete:    { label: "Pagamento incompleto",   color: "bg-amber-100 text-amber-700",     dot: "bg-amber-500" },
    none:          { label: "Sem assinatura",         color: "bg-zinc-100 text-zinc-600",       dot: "bg-zinc-400" },
  };
  const cfg = statusConfig[status] || statusConfig.none;

  return (
    <div className="space-y-5" data-testid="subscription-section">
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.18em] font-medium text-zinc-400 mb-1">Sua assinatura</p>
            <h2 className="text-[22px] font-semibold tracking-tight text-zinc-900">
              {info?.plan_label || (hasSub ? "Plano Premium" : "Nenhum plano ativo")}
            </h2>
            {info?.tier && (
              <p className="text-[12.5px] text-zinc-500 mt-1 capitalize">Tier: {info.tier}</p>
            )}
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11.5px] font-semibold ${cfg.color}`} data-testid="subscription-status">
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </span>
        </div>

        {/* Avisos contextuais */}
        {hasSub && canceled && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4 flex items-start gap-3" data-testid="canceled-notice">
            <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-amber-900">Cancelamento programado</p>
              <p className="text-[12.5px] text-amber-800 mt-0.5">
                Sua assinatura permanecerá ativa até <strong>{formatDate(info?.current_period_end)}</strong>.
                Depois dessa data o acesso premium será encerrado. Você pode reativar a qualquer momento antes disso.
              </p>
            </div>
          </div>
        )}

        {hasSub && !canceled && trialActive && (
          <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 mb-4 flex items-start gap-3" data-testid="trial-notice">
            <Sparkles size={18} className="text-violet-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-violet-900">Você está no período de teste gratuito</p>
              <p className="text-[12.5px] text-violet-800 mt-0.5">
                Acesso total liberado. Sua primeira cobrança será em <strong>{formatDate(info?.next_billing_at || info?.current_period_end)}</strong>.
              </p>
            </div>
          </div>
        )}

        {/* Detalhes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {info?.next_billing_at && hasSub && !canceled && (
            <div className="rounded-xl border border-zinc-200/70 p-4">
              <p className="text-[10.5px] uppercase tracking-wider font-medium text-zinc-400">Próxima cobrança</p>
              <p className="text-[15px] font-semibold text-zinc-900 mt-1">{formatDate(info.next_billing_at)}</p>
            </div>
          )}
          {info?.current_period_end && hasSub && canceled && (
            <div className="rounded-xl border border-zinc-200/70 p-4">
              <p className="text-[10.5px] uppercase tracking-wider font-medium text-zinc-400">Acesso ativo até</p>
              <p className="text-[15px] font-semibold text-zinc-900 mt-1">{formatDate(info.current_period_end)}</p>
            </div>
          )}
          {info?.expires_at && !info?.current_period_end && (
            <div className="rounded-xl border border-zinc-200/70 p-4">
              <p className="text-[10.5px] uppercase tracking-wider font-medium text-zinc-400">Validade local</p>
              <p className="text-[15px] font-semibold text-zinc-900 mt-1">{formatDate(info.expires_at)}</p>
            </div>
          )}
        </div>

        {/* Ações */}
        <div className="mt-6 flex flex-wrap gap-3">
          {info?.portal_available ? (
            <button
              onClick={openPortal}
              disabled={portalLoading}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-[13.5px] font-semibold text-white transition-all disabled:opacity-60"
              style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)', boxShadow:'0 8px 24px -8px rgba(107,61,246,0.45)'}}
              data-testid="open-billing-portal-btn"
            >
              {portalLoading ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />}
              Gerenciar assinatura
            </button>
          ) : (
            <button
              onClick={() => {
                const defaultTier = user?.profile_type === 'professional' ? 'professional' : 'personal';
                setSelectedTier(defaultTier);
                setSelectedCycle('annual');
                setPlanModalOpen(true);
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-[13.5px] font-semibold text-white transition-all"
              style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)', boxShadow:'0 8px 24px -8px rgba(107,61,246,0.45)'}}
              data-testid="start-subscription-btn"
            >
              <Sparkles size={15} />
              {hasSub ? "Ativar assinatura" : "Assinar agora — 7 dias grátis"}
            </button>
          )}
          {info?.portal_available && (
            <button
              onClick={fetchInfo}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13.5px] font-medium text-zinc-700 border border-zinc-200 hover:bg-zinc-50 transition-colors"
              data-testid="refresh-subscription-btn"
            >
              <RefreshCw size={14} />
              Atualizar
            </button>
          )}
          {hasSub && !canceled && (
            <button
              onClick={cancelSubscription}
              disabled={portalLoading}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13.5px] font-medium text-rose-600 border border-rose-200 hover:bg-rose-50 transition-colors disabled:opacity-60"
              data-testid="cancel-subscription-btn"
            >
              <X size={14} />
              Cancelar assinatura
            </button>
          )}
          {hasSub && canceled && (
            <button
              onClick={resumeSubscription}
              disabled={portalLoading}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13.5px] font-medium text-emerald-700 border border-emerald-200 hover:bg-emerald-50 transition-colors disabled:opacity-60"
              data-testid="resume-subscription-btn"
            >
              <RefreshCw size={14} />
              Reativar assinatura
            </button>
          )}
        </div>

        <p className="text-[11.5px] text-zinc-400 mt-5 leading-relaxed">
          Pagamentos processados com segurança pela Stripe. O cancelamento mantém seu acesso até o fim do período pago — você pode reativar a qualquer momento antes disso.
        </p>
      </Card>

      {/* ═══ MODAL DE SELEÇÃO DE PLANO ═══ */}
      {planModalOpen && (() => {
        const cycleSuffix = selectedCycle === 'annual' ? 'yearly' : 'monthly';
        const tierSuffix = selectedTier || 'personal';
        const planKey = `pro_${cycleSuffix}_${tierSuffix}`;
        const tableData = {
          personal: {
            monthly: { price: 29.90,  name: 'Premium', tag: null },
            annual:  { price: 358.80, name: 'Premium Anual', monthly_equiv: 29.90, tag: null },
          },
          professional: {
            monthly: { price: 79.90,  name: 'Pro', tag: null },
            annual:  { price: 767.04, name: 'Pro Anual', monthly_equiv: 63.92, tag: '20% OFF Lançamento' },
          },
        };
        const tier = tableData[tierSuffix][selectedCycle];

        const startCheckout = async () => {
          if (checkoutLoading) return;
          setCheckoutLoading(true);
          try {
            const res = await axios.post(`${API}/payments/checkout/session`, {
              plan: planKey,
              origin_url: window.location.origin,
            });
            if (res.data?.url) {
              redirectToCheckout(res.data.url);
              return;
            }
            toast.error("Não foi possível iniciar o checkout");
          } catch (err) {
            toast.error(err.response?.data?.detail || "Não foi possível iniciar o checkout");
          } finally {
            setCheckoutLoading(false);
          }
        };

        return (
          <div
            className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
            onClick={() => !checkoutLoading && setPlanModalOpen(false)}
            data-testid="plan-modal-backdrop"
          >
            <div
              className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="plan-modal"
            >
              <div className="p-5 sm:p-6 border-b border-zinc-100 flex items-center justify-between">
                <div>
                  <h3 className="text-[18px] font-semibold tracking-tight text-zinc-900" style={{fontFamily:"'Inter', sans-serif"}}>Escolha seu plano</h3>
                  <p className="text-[12.5px] text-zinc-500 mt-0.5">7 dias grátis, cancele quando quiser</p>
                </div>
                <button
                  onClick={() => !checkoutLoading && setPlanModalOpen(false)}
                  className="text-zinc-400 hover:text-zinc-700 transition-colors"
                  data-testid="plan-modal-close"
                  aria-label="Fechar"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-5 sm:p-6 space-y-5">
                {/* Toggle Personal / Professional */}
                <div>
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-zinc-400 mb-2">Tipo de uso</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setSelectedTier('personal')}
                      className={`px-4 py-3 rounded-xl text-[13px] font-medium transition-all ${
                        tierSuffix === 'personal'
                          ? 'bg-zinc-900 text-white shadow-md'
                          : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100'
                      }`}
                      data-testid="plan-tier-personal"
                    >
                      <span className="block">Pessoal</span>
                      <span className={`block text-[10.5px] mt-0.5 font-normal ${tierSuffix === 'personal' ? 'text-white/70' : 'text-zinc-500'}`}>1 evento + recursos completos</span>
                    </button>
                    <button
                      onClick={() => setSelectedTier('professional')}
                      className={`px-4 py-3 rounded-xl text-[13px] font-medium transition-all ${
                        tierSuffix === 'professional'
                          ? 'bg-zinc-900 text-white shadow-md'
                          : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100'
                      }`}
                      data-testid="plan-tier-professional"
                    >
                      <span className="block">Profissional</span>
                      <span className={`block text-[10.5px] mt-0.5 font-normal ${tierSuffix === 'professional' ? 'text-white/70' : 'text-zinc-500'}`}>Multi-eventos + workspace</span>
                    </button>
                  </div>
                </div>

                {/* Toggle Mensal / Anual */}
                <div>
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-zinc-400 mb-2">Ciclo de cobrança</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setSelectedCycle('monthly')}
                      className={`px-4 py-3 rounded-xl text-[13px] font-medium transition-all ${
                        selectedCycle === 'monthly'
                          ? 'bg-zinc-900 text-white shadow-md'
                          : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100'
                      }`}
                      data-testid="plan-cycle-monthly"
                    >
                      Mensal
                    </button>
                    <button
                      onClick={() => setSelectedCycle('annual')}
                      className={`relative px-4 py-3 rounded-xl text-[13px] font-medium transition-all ${
                        selectedCycle === 'annual'
                          ? 'text-white shadow-md'
                          : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100'
                      }`}
                      style={selectedCycle === 'annual' ? {backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)'} : {}}
                      data-testid="plan-cycle-annual"
                    >
                      Anual
                      <span className={`absolute -top-2 -right-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                        selectedCycle === 'annual' ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700'
                      }`}>−20%</span>
                    </button>
                  </div>
                </div>

                {/* Resumo */}
                <div className="rounded-2xl border border-zinc-200/70 p-4 bg-gradient-to-br from-violet-50/40 via-white to-orange-50/30">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Seu plano</p>
                      <p className="text-[16px] font-semibold tracking-tight text-zinc-900 mt-0.5" data-testid="plan-summary-name">{tier.name}</p>
                    </div>
                    {tier.tag && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-emerald-100 text-emerald-700">
                        {tier.tag}
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[28px] font-semibold tracking-tight text-zinc-900" data-testid="plan-summary-price">
                      R$ {tier.price.toFixed(2).replace('.', ',')}
                    </span>
                    <span className="text-[12.5px] text-zinc-500">
                      / {selectedCycle === 'annual' ? 'ano' : 'mês'}
                    </span>
                  </div>
                  {selectedCycle === 'annual' && tier.monthly_equiv && (
                    <p className="text-[11.5px] text-zinc-500 mt-1">
                      Equivale a <strong className="text-zinc-700">R$ {tier.monthly_equiv.toFixed(2).replace('.', ',')}/mês</strong> · cobrança única anual
                    </p>
                  )}
                  <ul className="mt-3 space-y-1.5">
                    {[
                      'Acesso completo à plataforma',
                      'WhatsApp + RSVP + Check-in',
                      'Suporte prioritário',
                      '7 dias grátis (sem cobrança no trial)',
                    ].map((it, i) => (
                      <li key={i} className="flex items-center gap-2 text-[12.5px] text-zinc-700">
                        <Check size={13} className="text-emerald-600 flex-shrink-0" strokeWidth={3} />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="p-5 sm:p-6 pt-0 flex flex-col-reverse sm:flex-row gap-2">
                <button
                  onClick={() => !checkoutLoading && setPlanModalOpen(false)}
                  disabled={checkoutLoading}
                  className="flex-1 py-3 rounded-xl text-[14px] font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 disabled:opacity-50 transition-colors"
                  data-testid="plan-modal-cancel"
                >
                  Cancelar
                </button>
                <button
                  onClick={startCheckout}
                  disabled={checkoutLoading}
                  className="flex-1 py-3 rounded-xl text-[14px] font-semibold text-white transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                  style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)', boxShadow:'0 8px 24px -8px rgba(107,61,246,0.45)'}}
                  data-testid="plan-modal-confirm"
                >
                  {checkoutLoading ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                  Continuar com {tier.name}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function SettingsPage() {
  const { wedding, updateWedding, deleteEvent, deleteAllEvents, refreshWeddings } = useWedding();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetMode, setResetMode] = useState(null); // 'reset_current' | 'delete_current' | 'delete_all'
  const [form, setForm] = useState({});
  
  // Estado para Gestão de Usuários (Iter 75 — Fase 3 UX)
  const [activeTab, setActiveTab] = useState('geral');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', description: '', role: 'editor', permissions: null });
  const [invitePresetId, setInvitePresetId] = useState('editor');
  // Onda 1 — perfil visual no convite (anfitriao | convidado | staff). NÃO altera role no backend.
  const [inviteProfile, setInviteProfile] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showInviteLink, setShowInviteLink] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState({});
  // Cache local do meta (presets/modules) para aplicar perfis sem refetch.
  const [permMeta, setPermMeta] = useState(null);

  useEffect(() => {
    if (permMeta) return;
    let cancelled = false;
    axios.get(`${API}/me/permissions-meta`).then(r => { if (!cancelled) setPermMeta(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [permMeta]);

  const applyInviteProfile = (profileId) => {
    setInviteProfile(profileId);
    if (!permMeta?.presets) return;
    // Onda 1.5 — Apenas 2 perfis (convidado e staff). receitas é exclusivo do proprietário e nunca é compartilhado.
    const presetMap = { convidado: 'viewer', staff: 'editor' };
    const baseId = presetMap[profileId] || 'editor';
    const base = JSON.parse(JSON.stringify(permMeta.presets[baseId] || {}));
    // Sanity check: garante que receitas fica desligado mesmo se algum preset venha com flags.
    if (base.receitas) {
      Object.keys(base.receitas).forEach((k) => { base.receitas[k] = false; });
    }
    setInviteForm((f) => ({ ...f, permissions: base }));
    setInvitePresetId(baseId);
  };

  // Iter 75 — workspace-wide collab manager (dedup por email, multi-evento)
  const [collabList, setCollabList] = useState([]);
  const [collabListLoading, setCollabListLoading] = useState(false);
  const [selectedCollab, setSelectedCollab] = useState(null); // entry from collabList
  const [collabDrawerTab, setCollabDrawerTab] = useState('dados'); // dados | eventos | permissoes | convite
  const [inviteWeddingIds, setInviteWeddingIds] = useState([]);
  const [drawerEventsDirty, setDrawerEventsDirty] = useState(false);
  const [drawerSelectedEvents, setDrawerSelectedEvents] = useState(new Set());

  useEffect(() => { 
    if (wedding) setForm(wedding); 
  }, [wedding]);
  
  // Carregar lista de usuários quando a aba muda
  useEffect(() => {
    if (activeTab === 'usuarios' && wedding?.id) {
      fetchUsers();
    }
  }, [activeTab, wedding?.id]);
  
  const fetchUsers = async () => {
    if (!wedding?.id) return;
    setUsersLoading(true);
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao carregar usuários"));
    } finally {
      setUsersLoading(false);
    }
  };
  
  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) {
      toast.error("Digite o email do convidado");
      return;
    }
    setInviteLoading(true);
    try {
      const res = await axios.post(`${API}/weddings/${wedding.id}/users/invite`, {
        email: inviteForm.email,
        name: inviteForm.name,
        role: invitePresetId === 'custom' ? 'custom' : invitePresetId,
        permissions: inviteForm.permissions
      });
      toast.success("Usuário convidado com sucesso!");
      setShowInviteLink(res.data.invite_url);
      setInviteForm({ email: '', name: '', role: 'editor', permissions: null });
      setInvitePresetId('editor');
      fetchUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Você não tem permissão para convidar usuários."));
    } finally {
      setInviteLoading(false);
    }
  };
  
  const handleResendInvite = async (userId) => {
    try {
      const res = await axios.post(`${API}/weddings/${wedding.id}/users/${userId}/resend-invite`);
      toast.success(res.data.message);
      setShowInviteLink(res.data.invite_url);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao reenviar convite"));
    }
  };
  
  const handleRevokeAccess = async (userId) => {
    if (!(await confirmDialog({
      title: "Revogar acesso",
      message: "Tem certeza que deseja revogar o acesso deste usuário?",
      confirmLabel: "Revogar",
      variant: "danger",
    }))) return;
    try {
      await axios.delete(`${API}/weddings/${wedding.id}/users/${userId}`);
      toast.success("Acesso revogado com sucesso.");
      fetchUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Você não tem acesso a esta funcionalidade."));
    }
  };
  
  const handleUpdatePermissions = async () => {
    if (!showPermissionsModal) return;
    try {
      // ✅ Iter 79 — usa endpoint by-invite quando ainda pendente (sem user_id)
      if (showPermissionsModal.user_id) {
        await axios.put(`${API}/weddings/${wedding.id}/users/${showPermissionsModal.id}/permissions`, {
          permissions: editingPermissions
        });
      } else if (showPermissionsModal.invite_token) {
        await axios.put(`${API}/collab/by-invite/${showPermissionsModal.invite_token}/permissions`, {
          permissions: editingPermissions
        });
      } else {
        toast.error("Sem token — peça ao organizador para regenerar.");
        return;
      }
      toast.success("Permissões atualizadas.");
      setShowPermissionsModal(null);
      fetchUsers();
      // Atualiza também o drawer aberto, se for o mesmo usuário
      if (selectedCollab && (
          (showPermissionsModal.user_id && selectedCollab.user_id === showPermissionsModal.user_id) ||
          (showPermissionsModal.invite_token && selectedCollab.invite_token === showPermissionsModal.invite_token)
      )) {
        await fetchCollabList();
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Você não tem permissão para alterar permissões."));
    }
  };
  
  const openPermissionsModal = (userItem) => {
    setEditingPermissions(userItem.permissions || {});
    setShowPermissionsModal(userItem);
  };
  
  const togglePermission = (module, action) => {
    setEditingPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: !prev[module]?.[action]
      }
    }));
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copiado!");
  };

  // ===== Iter 75 — Workspace-wide collab manager =====
  const { weddings } = useWedding();
  const fetchCollabList = useCallback(async () => {
    setCollabListLoading(true);
    try {
      const res = await axios.get(`${API}/collab/list`);
      setCollabList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao carregar usuários"));
    } finally {
      setCollabListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'usuarios') fetchCollabList();
  }, [activeTab, fetchCollabList]);

  // Helper resiliente para copiar texto (modern clipboard API + execCommand fallback)
  const robustCopy = async (text) => {
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        copied = true;
      }
    } catch { /* fall through */ }
    if (!copied) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        copied = true;
      } catch { /* nothing */ }
    }
    return copied;
  };

  const handleInviteMulti = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) { toast.error("Digite o email do convidado"); return; }
    if (inviteWeddingIds.length === 0) { toast.error("Selecione pelo menos 1 evento"); return; }
    setInviteLoading(true);
    try {
      const res = await axios.post(`${API}/collab/invite`, {
        email: inviteForm.email,
        name: inviteForm.name,
        description: inviteForm.description || '',
        role: invitePresetId === 'custom' ? 'custom' : invitePresetId,
        wedding_ids: inviteWeddingIds,
        permissions: inviteForm.permissions,
      });
      toast.success(res.data.message || "Convite enviado!");
      setShowInviteLink(res.data.invite_url);
      // ✨ Iter 75 — Copy-link instantâneo (UX premium recomendada pelo usuário)
      if (res.data.invite_url) {
        const ok = await robustCopy(res.data.invite_url);
        if (ok) toast.success("✓ Link copiado");
      }
      setInviteForm({ email: '', name: '', description: '', role: 'editor', permissions: null });
      setInviteWeddingIds([]);
      setInvitePresetId('editor');
      setInviteProfile(null);
      await fetchCollabList();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao enviar convite"));
    } finally {
      setInviteLoading(false);
    }
  };

  const openCollabDrawer = (collab) => {
    setSelectedCollab(collab);
    setCollabDrawerTab('dados');
    setDrawerSelectedEvents(new Set(collab.wedding_ids || []));
    setDrawerEventsDirty(false);
  };

  const closeCollabDrawer = () => {
    setSelectedCollab(null);
    setDrawerSelectedEvents(new Set());
    setDrawerEventsDirty(false);
  };

  const toggleDrawerEvent = (wid) => {
    setDrawerSelectedEvents(prev => {
      const next = new Set(prev);
      if (next.has(wid)) next.delete(wid); else next.add(wid);
      return next;
    });
    setDrawerEventsDirty(true);
  };

  const saveDrawerEvents = async () => {
    const currentIds = new Set(selectedCollab.wedding_ids || []);
    const newIds = drawerSelectedEvents;
    const add = [...newIds].filter(id => !currentIds.has(id));
    const remove = [...currentIds].filter(id => !newIds.has(id));
    if (add.length === 0 && remove.length === 0) {
      setDrawerEventsDirty(false);
      return;
    }
    try {
      // ✅ Iter 79 — funciona antes e depois da ativação
      if (selectedCollab?.user_id) {
        // Já ativou — usa endpoint clássico por user_id
        await axios.post(`${API}/collab/${selectedCollab.user_id}/assign`, {
          add_wedding_ids: add,
          remove_wedding_ids: remove,
        });
      } else if (selectedCollab?.invite_token) {
        // Ainda pendente — usa endpoint por invite_token
        await axios.post(`${API}/collab/by-invite/${selectedCollab.invite_token}/assign`, {
          add_wedding_ids: add,
          remove_wedding_ids: remove,
        });
      } else {
        toast.error("Convite sem token — peça para o organizador regenerar.");
        return;
      }
      toast.success("Eventos atualizados.");
      setDrawerEventsDirty(false);
      await fetchCollabList();
      setSelectedCollab(prev => prev ? { ...prev, wedding_ids: [...newIds] } : prev);
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao atualizar eventos"));
    }
  };

  const handleCopyInviteLink = async (collab) => {
    if (!collab.invite_token) {
      toast.error("Este usuário já aceitou o convite");
      return;
    }
    const url = `${window.location.origin}/invite/staff/${collab.invite_token}`;
    const ok = await robustCopy(url);
    if (ok) {
      toast.success("✓ Link de convite copiado");
    } else {
      toast.error("Não foi possível copiar — copie manualmente");
    }
  };

  const collabStatusInfo = (status) => {
    if (status === 'active') return { dot: 'bg-emerald-500', label: 'Ativo' };
    if (status === 'pending') return { dot: 'bg-amber-500', label: 'Convite pendente' };
    if (status === 'revoked') return { dot: 'bg-zinc-300', label: 'Sem acesso' };
    return { dot: 'bg-zinc-300', label: status || '-' };
  };

  const formatRelative = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const diffMs = Date.now() - d.getTime();
      const min = Math.round(diffMs / 60000);
      if (min < 1) return 'agora';
      if (min < 60) return `há ${min} min`;
      const h = Math.round(min / 60);
      if (h < 24) return `há ${h}h`;
      const dDays = Math.round(h / 24);
      if (dDays < 30) return `há ${dDays}d`;
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
    } catch {
      return '—';
    }
  };
  
  // Estado para Histórico de Atividades
  const [activityLogs, setActivityLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsHasMore, setLogsHasMore] = useState(false);
  const [logsModuleFilter, setLogsModuleFilter] = useState('');
  const [logModules, setLogModules] = useState([]);
  
  // Carregar logs quando a aba muda
  useEffect(() => {
    if (activeTab === 'historico' && wedding?.id) {
      fetchActivityLogs();
      fetchLogModules();
    }
  }, [activeTab, wedding?.id, logsPage, logsModuleFilter]);
  
  const fetchLogModules = async () => {
    try {
      const res = await axios.get(`${API}/weddings/${wedding.id}/action-logs/modules`);
      setLogModules(res.data);
    } catch (err) {
      console.error('Erro ao carregar módulos:', err);
    }
  };
  
  const fetchActivityLogs = async () => {
    if (!wedding?.id) return;
    setLogsLoading(true);
    try {
      let url = `${API}/weddings/${wedding.id}/action-logs?page=${logsPage}&page_size=15`;
      if (logsModuleFilter) {
        url += `&module=${logsModuleFilter}`;
      }
      const res = await axios.get(url);
      setActivityLogs(res.data.logs);
      setLogsTotal(res.data.total);
      setLogsHasMore(res.data.has_more);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Apenas administradores podem visualizar o histórico");
      } else {
        toast.error(getErrorMessage(err, "Erro ao carregar histórico"));
      }
    } finally {
      setLogsLoading(false);
    }
  };
  
  const formatLogDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getModuleIcon = (module) => {
    const icons = {
      usuarios: Users,
      convidados: Users,
      checkin: UserCheck,
      mesas: LayoutGrid,
      orcamentos: FileText,
      despesas: CreditCard,
      agenda: Calendar,
      configuracoes: Settings
    };
    return icons[module] || FileText;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { 
      await updateWedding(form); 
      toast.success("Configurações salvas com sucesso."); 
    } catch (err) { 
      toast.error("Erro ao salvar"); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleUpgradeToPro = async () => {
    setUpgradeLoading(true);
    try {
      await axios.post(`${API}/auth/upgrade`);
      await refreshUser();
      toast.success("🎉 Você agora é um usuário PRO! Aproveite todos os recursos!");
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao fazer upgrade"));
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleReset = async () => {
    setResetLoading(true);
    try {
      if (resetMode === 'reset_current') {
        // Keep the event but clear all linked data
        await axios.post(`${API}/weddings/${wedding.id}/reset`);
        toast.success("Dados do evento apagados! Evento mantido.");
        setShowResetConfirm(false);
        setResetMode(null);
        navigate("/dashboard");
      } else if (resetMode === 'delete_current' || user?.profile_type === 'personal') {
        // Delete only the current/active event
        if (wedding?.id) {
          await deleteEvent(wedding.id);
        }
        toast.success("Evento excluído com sucesso!");
        setShowResetConfirm(false);
        setResetMode(null);
        navigate("/dashboard");
      } else if (resetMode === 'delete_all') {
        // Delete ALL events
        await deleteAllEvents();
        toast.success("Todos os eventos foram excluídos!");
        setShowResetConfirm(false);
        setResetMode(null);
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao executar ação"));
    } finally {
      setResetLoading(false);
    }
  };

  if (!wedding) return null;
  
  // Módulos disponíveis para permissões
  const permissionModules = [
    { key: 'convidados', label: 'Convidados', icon: Users },
    { key: 'checkin', label: 'Check-in', icon: UserCheck },
    { key: 'orcamentos', label: 'Fornecedores', icon: FileText },
    { key: 'despesas', label: 'Despesas', icon: CreditCard },
    { key: 'mesas', label: 'Mesas', icon: LayoutGrid },
    { key: 'agenda', label: 'Tarefas', icon: Calendar },
    { key: 'configuracoes', label: 'Configurações', icon: Settings },
    { key: 'usuarios', label: 'Usuários', icon: UserCog }
  ];
  
  const roleLabels = {
    admin: { label: 'Administrador', color: 'bg-purple-100 text-purple-700' },
    editor: { label: 'Editor', color: 'bg-blue-100 text-blue-700' },
    viewer: { label: 'Visualizador', color: 'bg-slate-100 text-slate-700' }
  };
  
  const statusLabels = {
    active: { label: 'Acesso ativo', color: 'bg-emerald-100 text-emerald-700' },
    pending: { label: 'Convite enviado', color: 'bg-amber-100 text-amber-700' },
    revoked: { label: 'Acesso revogado', color: 'bg-red-100 text-red-700' },
    expired: { label: 'Convite expirado', color: 'bg-slate-100 text-slate-500' }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Configurações do Evento</h1>
        <p className="text-slate-500 mt-1">Gerencie os dados do seu evento, usuários e preferências do sistema.</p>
      </div>
      
      {/* Tabs de navegação — scroll horizontal nativo no mobile, com snap pra sensação de app */}
      <div
        className="flex gap-2 border-b border-slate-200 pb-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden snap-x snap-mandatory"
        style={{scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch'}}
      >
        <button
          onClick={() => setActiveTab('geral')}
          className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            activeTab === 'geral' 
              ? 'bg-blue-100 text-blue-700' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="settings-tab-geral"
        >
          <Settings size={16} className="inline mr-2" />
          Dados do Evento
        </button>
        <button
          onClick={() => setActiveTab('usuarios')}
          className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            activeTab === 'usuarios' 
              ? 'bg-blue-100 text-blue-700' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="settings-tab-usuarios"
        >
          <Users size={16} className="inline mr-2" />
          Usuários
        </button>
        <button
          onClick={() => setActiveTab('historico')}
          className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            activeTab === 'historico' 
              ? 'bg-blue-100 text-blue-700' 
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="settings-tab-historico"
        >
          <Clock size={16} className="inline mr-2" />
          Histórico
        </button>
        <button
          onClick={() => setActiveTab('assinatura')}
          className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            activeTab === 'assinatura'
              ? 'bg-blue-100 text-blue-700'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="settings-tab-assinatura"
        >
          <CreditCard size={16} className="inline mr-2" />
          Assinatura
        </button>
        <button
          onClick={() => setActiveTab('privacidade')}
          className={`flex-shrink-0 snap-start px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            activeTab === 'privacidade'
              ? 'bg-blue-100 text-blue-700'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="settings-tab-privacidade"
        >
          <Shield size={16} className="inline mr-2" />
          Privacidade
        </button>
      </div>
      
      {activeTab === 'assinatura' && <SubscriptionSection />}
      {activeTab === 'privacidade' && <PrivacySection />}
      
      {/* Conteúdo da aba Geral */}
      {activeTab === 'geral' && (
        <React.Fragment>
          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <Heart size={20} className="text-pink-500" />
              Dados do Evento
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Essas informações ajudam a organizar prazos, orçamento e convidados.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Nome do evento" value={form.event_name ?? form.partner1_name ?? ""} onChange={e => setForm({...form, event_name: e.target.value, partner1_name: e.target.value})} data-testid="settings-event-name-input" />
                <Input label="Organizador" value={form.partner2_name || ""} onChange={e => setForm({...form, partner2_name: e.target.value})} data-testid="settings-organizer-input" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input label="Data do evento" type="date" value={form.event_date || ""} onChange={e => setForm({...form, event_date: e.target.value})} />
                <Input label="Horário do evento" type="time" value={form.event_time || ""} onChange={e => setForm({...form, event_time: e.target.value})} placeholder="19:30" />
                <Input label="Número de convidados" type="number" value={form.guest_count || 0} onChange={e => setForm({...form, guest_count: parseInt(e.target.value) || 0})} />
              </div>
              <MoneyInput
                label="Orçamento total"
                value={form.total_budget || 0}
                onChange={(val) => setForm({...form, total_budget: val})}
              />
              <p className="text-xs text-slate-400">Formato: R$ 0,00 (separador de milhar: ponto / decimal: vírgula)</p>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="animate-spin mr-2" size={18} />}
                <span>Salvar alterações</span>
              </Button>
            </form>
          </Card>

          {/* Seção de Informações do Evento - LOCAL DA FESTA */}
          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <MapPin size={20} className="text-blue-500" />
              Informações do Evento
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Essas informações aparecerão no convite enviado pelo WhatsApp
            </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input 
            label="Nome do Local" 
            value={form.venue_name || ""} 
            onChange={e => setForm({...form, venue_name: e.target.value})} 
            placeholder="Ex: Espaço Jardim das Oliveiras"
          />
          <Input 
            label="Endereço Completo" 
            value={form.venue_address || ""} 
            onChange={e => setForm({...form, venue_address: e.target.value})} 
            placeholder="Ex: Rua das Flores, 123 - Centro"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input 
              label="Cidade" 
              value={form.venue_city || ""} 
              onChange={e => setForm({...form, venue_city: e.target.value})} 
              placeholder="Ex: São Paulo"
            />
            <Input 
              label="Estado" 
              value={form.venue_state || ""} 
              onChange={e => setForm({...form, venue_state: e.target.value})} 
              placeholder="Ex: SP"
            />
          </div>
          <Input 
            label="Link do Google Maps (opcional)" 
            value={form.venue_maps_link || ""} 
            onChange={e => setForm({...form, venue_maps_link: e.target.value})} 
            placeholder="https://maps.google.com/..."
          />
          <Button type="submit" disabled={loading}>
            {loading && <Loader2 className="animate-spin mr-2" size={18} />}
            <span>Salvar Local</span>
          </Button>
        </form>
      </Card>

      <Card className="p-6">
        <h3 className="font-semibold text-slate-800 mb-4">Sua Conta</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-start gap-3 py-2 border-b">
            <span className="text-slate-600 flex-shrink-0">Nome</span>
            <span className="font-medium text-right truncate">{user?.name}</span>
          </div>
          <div className="flex justify-between items-start gap-3 py-2 border-b">
            <span className="text-slate-600 flex-shrink-0">Email</span>
            <span className="font-medium text-right break-all min-w-0">{user?.email}</span>
          </div>
          <div className="flex justify-between items-start gap-3 py-2 border-b">
            <span className="text-slate-600 flex-shrink-0">Plano</span>
            <span className={`font-medium text-right ${user?.subscription === 'pro' ? 'text-blue-500' : ''}`}>
              {user?.subscription === 'pro' ? '✨ Pro' : 'Gratuito'}
            </span>
          </div>
          {user?.subscription !== 'pro' && (
            <div className="pt-4">
              <Button onClick={handleUpgradeToPro} disabled={upgradeLoading} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                {upgradeLoading ? (
                  <React.Fragment>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    <span>Ativando...</span>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Crown size={18} className="mr-2" />
                    <span>Ativar PRO (Grátis para Testes)</span>
                  </React.Fragment>
                )}
              </Button>
              <p className="text-xs text-slate-500 text-center mt-2">Clique para ativar todos os recursos PRO gratuitamente</p>
            </div>
          )}
        </div>
      </Card>

      {/* Seção - Começar do Zero */}
      <Card className="p-6 border-slate-200 bg-slate-50">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <RefreshCw size={20} className="text-slate-600" />
          Começar do zero
        </h3>
        {user?.profile_type === 'professional' ? (
          <div className="space-y-3">
            <p className="text-slate-600 text-sm mb-4">
              Escolha como deseja recomeçar. Sua conta e plano serão mantidos.
            </p>
            <button
              onClick={() => { setResetMode('reset_current'); setShowResetConfirm(true); }}
              className="w-full flex items-center gap-3 px-4 py-3 border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors text-left"
              data-testid="reset-current-event-btn"
            >
              <RefreshCw size={18} className="text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Recomeçar evento atual</p>
                <p className="text-xs text-amber-600">Limpa convidados, tarefas e despesas do evento ativo. Mantém o evento.</p>
              </div>
            </button>
            <button
              onClick={() => { setResetMode('delete_current'); setShowResetConfirm(true); }}
              className="w-full flex items-center gap-3 px-4 py-3 border border-orange-200 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors text-left"
              data-testid="delete-current-event-btn"
            >
              <Trash2 size={18} className="text-orange-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-800">Excluir evento atual</p>
                <p className="text-xs text-orange-600">Exclui o evento ativo e todos os dados vinculados.</p>
              </div>
            </button>
            <button
              onClick={() => { setResetMode('delete_all'); setShowResetConfirm(true); }}
              className="w-full flex items-center gap-3 px-4 py-3 border border-red-200 bg-red-50 rounded-lg hover:bg-red-100 transition-colors text-left"
              data-testid="delete-all-events-btn"
            >
              <AlertCircle size={18} className="text-red-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Apagar todos os eventos</p>
                <p className="text-xs text-red-600">Remove absolutamente tudo da conta. Volta ao estado inicial.</p>
              </div>
            </button>
          </div>
        ) : (
          <div>
            <p className="text-slate-600 text-sm mb-4">
              Excluir o evento atual e voltar ao estado inicial.
              <br /><span className="text-slate-500">Sua conta e plano serão mantidos.</span>
            </p>
            <button
              onClick={() => { setResetMode('delete_current'); setShowResetConfirm(true); }}
              className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              data-testid="personal-reset-btn"
            >
              Começar do zero
            </button>
          </div>
        )}
      </Card>

      {/* Modal de confirmação de reset */}
      <Modal isOpen={showResetConfirm} onClose={() => { setShowResetConfirm(false); setResetMode(null); }} title="Confirmação">
        <div className="space-y-4" data-testid="reset-confirm-modal">
          <div className={`border rounded-lg p-4 ${
            resetMode === 'reset_current' ? 'bg-amber-50 border-amber-200' :
            resetMode === 'delete_all' ? 'bg-red-50 border-red-200' :
            'bg-orange-50 border-orange-200'
          }`}>
            {resetMode === 'reset_current' && (
              <React.Fragment>
                <p className="text-amber-800 font-medium mb-2">Recomeçar evento atual</p>
                <p className="text-amber-700 text-sm">
                  Os seguintes dados do evento "{wedding?.event_name || wedding?.partner1_name}" serão apagados:
                </p>
                <ul className="text-amber-600 text-sm mt-2 space-y-1">
                  <li>- Convidados e convites</li>
                  <li>- Tarefas e checklist</li>
                  <li>- Orçamentos e despesas</li>
                </ul>
                <p className="text-amber-800 font-bold mt-3">O evento será mantido.</p>
              </React.Fragment>
            )}
            {resetMode === 'delete_current' && (
              <React.Fragment>
                <p className="text-orange-800 font-medium mb-2">Excluir evento atual</p>
                <p className="text-orange-700 text-sm">
                  O evento "{wedding?.event_name || wedding?.partner1_name}" e todos os dados vinculados serão excluídos permanentemente.
                </p>
                <p className="text-orange-800 font-bold mt-3">Esta ação não pode ser desfeita.</p>
              </React.Fragment>
            )}
            {resetMode === 'delete_all' && (
              <React.Fragment>
                <p className="text-red-800 font-medium mb-2">Apagar TODOS os eventos</p>
                <p className="text-red-700 text-sm">
                  Todos os seus eventos e dados vinculados serão excluídos permanentemente. Sua conta voltará ao estado inicial.
                </p>
                <p className="text-red-800 font-bold mt-3">Esta ação não pode ser desfeita.</p>
              </React.Fragment>
            )}
          </div>

          <p className="text-center text-lg font-medium text-slate-700">Deseja continuar?</p>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => { setShowResetConfirm(false); setResetMode(null); }}
              className="flex-1 py-2.5 px-4 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 text-sm font-medium"
              data-testid="cancel-reset-btn"
            >
              Cancelar
            </button>
            <button
              onClick={handleReset}
              disabled={resetLoading}
              className={`flex-1 py-2.5 px-4 text-white rounded-lg text-sm font-bold disabled:opacity-50 ${
                resetMode === 'delete_all' ? 'bg-red-600 hover:bg-red-700' :
                resetMode === 'reset_current' ? 'bg-amber-600 hover:bg-amber-700' :
                'bg-orange-600 hover:bg-orange-700'
              }`}
              data-testid="confirm-reset-btn"
            >
              {resetLoading ? (
                <Loader2 className="animate-spin mx-auto" size={18} />
              ) : (
                resetMode === 'delete_all' ? 'Sim, Excluir Tudo' :
                resetMode === 'reset_current' ? 'Sim, Limpar Dados' :
                'Sim, Excluir Evento'
              )}
            </button>
          </div>
        </div>
      </Modal>
        </React.Fragment>
      )}
      
      {/* Conteúdo da aba Usuários */}
      {activeTab === 'usuarios' && (
        <React.Fragment>
          {/* CARD: lista de usuários (workspace-wide, multi-evento) */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-semibold text-zinc-900 flex items-center gap-2 text-[15px]">
                  <Users size={18} className="text-violet-600" />
                  Usuários
                </h3>
                <p className="text-[12.5px] text-zinc-500 mt-1 max-w-md">
                  Convide usuários e atribua um ou mais eventos do seu espaço. Cada pessoa recebe um link VIP por email.
                </p>
              </div>
              <Button
                onClick={() => {
                  setInviteWeddingIds([]);
                  setShowInviteModal(true);
                }}
                data-testid="invite-user-btn"
                className="bg-zinc-900 hover:bg-zinc-800 text-white text-[13px]"
              >
                <UserPlus size={16} className="mr-2" />
                Adicionar Usuário
              </Button>
            </div>

            {collabListLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="collab-list-skeleton">
                {[0, 1].map(i => (
                  <div
                    key={i}
                    className="h-[100px] rounded-2xl border border-zinc-100 p-4 relative overflow-hidden"
                    style={{ backgroundImage: 'linear-gradient(110deg, #fafafa 0%, #f4f4f5 50%, #fafafa 100%)', backgroundSize: '200% 100%', animation: 'liventy-shimmer 1.4s ease-in-out infinite' }}
                  />
                ))}
              </div>
            ) : collabList.length === 0 ? (
              <div className="text-center py-10 rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/40">
                <Users size={28} className="mx-auto text-zinc-300 mb-2" />
                <p className="text-[13px] text-zinc-600">Nenhum usuário ainda.</p>
                <p className="text-[12px] text-zinc-400 mt-1">Clique em "Adicionar Usuário" para começar.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="collab-list">
                {collabList.map(collab => {
                  const status = collabStatusInfo(collab.status);
                  const initials = (collab.name || collab.email || '?').split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(s => s.charAt(0).toUpperCase()).join('') || '?';
                  const count = (collab.wedding_ids || []).length;
                  return (
                    <button
                      key={collab.email}
                      type="button"
                      onClick={() => openCollabDrawer(collab)}
                      className="text-left p-4 rounded-2xl border border-zinc-200/70 bg-white hover:border-zinc-300 hover:shadow-[0_8px_24px_-12px_rgba(24,24,27,0.16)] transition-all"
                      data-testid={`collab-card-${collab.email}`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-white text-[12px] font-bold tracking-wide"
                          style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 60%, #FFB347 100%)" }}
                        >
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-[13.5px] font-semibold text-zinc-900 truncate">
                              {collab.name || collab.email}
                            </p>
                            <span className="inline-flex items-center px-1.5 py-[1px] rounded-full bg-zinc-100 text-zinc-600 text-[9.5px] font-semibold tracking-wide">
                              Membro equipe
                            </span>
                          </div>
                          <p className="text-[11.5px] text-zinc-500 truncate mt-0.5">{collab.email}</p>
                          {collab.description && (
                            <p
                              className="text-[11.5px] text-zinc-700 truncate mt-1 italic"
                              data-testid={`collab-description-${collab.user_id || collab.email}`}
                            >
                              {collab.description}
                            </p>
                          )}
                          <div className="mt-2.5 flex items-center gap-3 text-[11px] text-zinc-500 flex-wrap">
                            <span className="inline-flex items-center gap-1.5">
                              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                              <span className="font-medium text-zinc-700">{status.label}</span>
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <Calendar size={11} className="text-zinc-400" />
                              <span className="tabular-nums font-semibold text-zinc-700">{count}</span> evento{count === 1 ? '' : 's'}
                            </span>
                            <span className="text-zinc-400">
                              {collab.accepted_at ? `Aceito ${formatRelative(collab.accepted_at)}` : `Convidado ${formatRelative(collab.invited_at)}`}
                            </span>
                          </div>
                        </div>
                        <ChevronRight size={16} className="text-zinc-300 flex-shrink-0 mt-1" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Onda 1.5 — Apenas 2 perfis: Usuário Convidado e Staff. */}
          <Card className="p-6">
            <h4 className="font-semibold text-zinc-900 mb-1 text-[14px]">Perfis de acesso</h4>
            <p className="text-[12.5px] text-zinc-500 mb-4">
              A Liventy usa dois perfis simples. Permissões detalhadas são definidas por você ao adicionar cada usuário.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-4 rounded-xl bg-sky-50 ring-1 ring-sky-200/70">
                <p className="font-semibold text-sky-900 text-[13px] mb-1.5 flex items-center gap-2">
                  <Users size={14} /> Usuário Convidado
                </p>
                <p className="text-[11.5px] text-sky-800 leading-relaxed">Pessoa principal ou acompanhante do evento. Ex.: noivo, aniversariante, pais, padrinhos, familiares.</p>
              </div>
              <div className="p-4 rounded-xl bg-violet-50/60 ring-1 ring-violet-100">
                <p className="font-semibold text-violet-900 text-[13px] mb-1.5 flex items-center gap-2">
                  <Briefcase size={14} /> Staff
                </p>
                <p className="text-[11.5px] text-violet-800/80 leading-relaxed">Equipe operacional. Ex.: cerimonialista, fotógrafo, decorador, buffet, assessoria.</p>
              </div>
            </div>
            <p className="text-[11px] text-zinc-500 mt-3 flex items-start gap-1.5">
              <Lock size={11} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <span>
                <strong className="text-zinc-700">Minhas Receitas</strong> é exclusivo do proprietário da assinatura e não é compartilhado com outros usuários.
              </span>
            </p>
          </Card>
        </React.Fragment>
      )}
      
      {/* Conteúdo da aba Histórico de Atividades */}
      {activeTab === 'historico' && (
        <React.Fragment>
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Clock size={20} className="text-blue-500" />
                  Histórico de atividades
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Acompanhe tudo o que foi feito no sistema e saiba quem realizou cada ação.
                </p>
              </div>
              
              {/* Filtro por módulo */}
              <select
                value={logsModuleFilter}
                onChange={(e) => { setLogsModuleFilter(e.target.value); setLogsPage(1); }}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="logs-module-filter"
              >
                <option value="">Todos os módulos</option>
                {logModules.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            
            {/* Lista de atividades */}
            {logsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-blue-500" size={32} />
              </div>
            ) : activityLogs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Clock size={48} className="mx-auto mb-3 opacity-30" />
                <p>Nenhuma atividade registrada até o momento.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {activityLogs.map((log) => {
                  const ModuleIcon = getModuleIcon(log.module);
                  return (
                    <div 
                      key={log.id} 
                      className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                      data-testid={`activity-log-${log.id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <ModuleIcon size={18} className="text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-800">
                          <span className="font-medium">{log.user_name}</span>
                          {' '}
                          <span className="text-slate-600">{log.description}</span>
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full">
                            {log.module_label}
                          </span>
                          <span className="text-xs text-slate-400">
                            {formatLogDate(log.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Paginação */}
            {logsTotal > 15 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  Mostrando {Math.min((logsPage - 1) * 15 + 1, logsTotal)} - {Math.min(logsPage * 15, logsTotal)} de {logsTotal}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setLogsPage(p => Math.max(1, p - 1))}
                    disabled={logsPage === 1}
                  >
                    <ChevronLeft size={16} />
                    Anterior
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setLogsPage(p => p + 1)}
                    disabled={!logsHasMore}
                  >
                    Próximo
                    <ChevronRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </React.Fragment>
      )}
      
      {/* Modal de Adicionar Usuário (renomeado em Onda 1 — antes: "Gerar Link de Acesso") */}
      <Modal isOpen={showInviteModal} onClose={() => { setShowInviteModal(false); setShowInviteLink(null); setInviteProfile(null); }} title="Adicionar Usuário">
        {showInviteLink ? (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-emerald-700 font-medium flex items-center gap-2">
                <Check size={20} />
                Link de acesso gerado!
              </p>
            </div>
            <p className="text-sm text-slate-600">
              Compartilhe este link com o usuário por WhatsApp, Telegram, email ou qualquer canal:
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={showInviteLink}
                readOnly
                className="flex-1 px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-sm font-mono"
              />
              <Button onClick={() => copyToClipboard(showInviteLink)} variant="secondary">
                <Copy size={16} />
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              O convite expira em 7 dias.
            </p>
            <Button onClick={() => { setShowInviteModal(false); setShowInviteLink(null); }} className="w-full">
              Fechar
            </Button>
          </div>
        ) : (
          <form onSubmit={handleInviteMulti} className="space-y-4">
            <p className="text-[12.5px] text-zinc-500 -mt-1">
              Adicione pessoas ao evento por <strong className="text-zinc-700">link</strong>, <strong className="text-zinc-700">e-mail</strong> ou <strong className="text-zinc-700">WhatsApp</strong>.
            </p>

            {/* Seletor de Perfil — Onda 1 */}
            <div>
              <label className="block text-[12px] font-medium text-zinc-700 mb-2 uppercase tracking-wider">Tipo de usuário</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="invite-profile-selector">
                {[
                  { id: 'convidado', label: 'Usuário Convidado', icon: Users,    color: 'sky',     desc: 'Pessoa principal do evento ou acompanhante', examples: 'Noivo, aniversariante, pais, padrinhos, familiares' },
                  { id: 'staff',     label: 'Staff',             icon: Briefcase, color: 'violet', desc: 'Equipe operacional do evento',                examples: 'Cerimonialista, fotógrafo, decorador, buffet' },
                ].map((p) => {
                  const active = inviteProfile === p.id;
                  const Icon = p.icon;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => applyInviteProfile(p.id)}
                      className={`text-left p-3 rounded-2xl border transition-all active:scale-[0.98] ${
                        active
                          ? 'border-zinc-900 bg-zinc-900 text-white shadow-[0_8px_24px_-12px_rgba(24,24,27,0.4)]'
                          : 'border-zinc-200/70 bg-white hover:border-zinc-300'
                      }`}
                      data-testid={`invite-profile-${p.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${active ? 'bg-white/15' : `bg-${p.color}-50`}`}>
                          <Icon size={14} className={active ? 'text-white' : `text-${p.color}-500`} strokeWidth={2} />
                        </div>
                        <span className="text-[13px] font-bold tracking-tight">{p.label}</span>
                        {active && <Check size={14} className="ml-auto" />}
                      </div>
                      <p className={`text-[11px] mt-1 leading-snug ${active ? 'text-zinc-300' : 'text-zinc-600'}`}>{p.desc}</p>
                      <p className={`text-[10px] mt-0.5 italic ${active ? 'text-zinc-400' : 'text-zinc-400'}`}>Ex.: {p.examples}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <Input
              label="Email do usuário"
              type="email"
              value={inviteForm.email}
              onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
              placeholder="email@exemplo.com"
              required
              data-testid="invite-email-input"
            />
            <Input
              label="Nome do usuário"
              value={inviteForm.name}
              onChange={e => setInviteForm({ ...inviteForm, name: e.target.value })}
              placeholder="João da Silva"
              required
              data-testid="invite-name-input"
            />
            <Input
              label="Descrição"
              value={inviteForm.description || ''}
              onChange={e => setInviteForm({ ...inviteForm, description: e.target.value })}
              placeholder="Ex.: Noivo, Mãe da Noiva, Cerimonialista, Fotógrafo"
              data-testid="invite-description-input"
            />

            {/* MULTI-EVENT PICKER (Iter 75) */}
            <div>
              <label className="block text-[12px] font-medium text-zinc-700 mb-2 uppercase tracking-wider">
                Eventos atribuídos · <span className="text-violet-600 font-bold">{inviteWeddingIds.length}</span> selecionado{inviteWeddingIds.length === 1 ? '' : 's'}
              </label>
              <div className="space-y-1.5 max-h-[220px] overflow-y-auto rounded-xl border border-zinc-200/70 p-2 bg-zinc-50/40" data-testid="invite-events-picker">
                {(weddings || []).length === 0 ? (
                  <p className="text-[12px] text-zinc-500 text-center py-4">Sem eventos no seu espaço.</p>
                ) : (weddings || []).map(ev => {
                  const checked = inviteWeddingIds.includes(ev.id);
                  return (
                    <label
                      key={ev.id}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors ${checked ? 'bg-violet-50 ring-1 ring-violet-200' : 'hover:bg-white'}`}
                      data-testid={`invite-event-toggle-${ev.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setInviteWeddingIds(prev =>
                            prev.includes(ev.id) ? prev.filter(x => x !== ev.id) : [...prev, ev.id]
                          );
                        }}
                        className="w-4 h-4 rounded border-zinc-300 text-violet-600 focus:ring-violet-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-zinc-900 truncate">
                          {ev.event_name || ev.partner1_name || 'Evento'}
                        </p>
                        <p className="text-[10.5px] text-zinc-500">
                          {EVENT_TYPE_LABELS?.[ev.event_type] || ev.event_type || 'Evento'}
                          {ev.event_date && ` · ${formatDateBR(ev.event_date)}`}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-medium text-zinc-700 mb-2 uppercase tracking-wider">Permissões de acesso</label>
              <PermissionEditor
                value={inviteForm.permissions}
                onChange={(perms) => setInviteForm(f => ({ ...f, permissions: perms }))}
                selectedPresetId={invitePresetId}
                onPresetChange={setInvitePresetId}
                hideModules={["receitas"]}
              />
              <p className="text-[11px] text-zinc-500 mt-2 flex items-start gap-1.5">
                <Lock size={11} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="text-zinc-700">Minhas Receitas</strong> é uma área exclusiva do proprietário da assinatura e <strong>não pode ser compartilhada</strong> com outros usuários.
                </span>
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="secondary" onClick={() => setShowInviteModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" disabled={inviteLoading || inviteWeddingIds.length === 0} className="flex-1" data-testid="send-invite-btn">
                {inviteLoading ? (
                  <React.Fragment>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Enviando...
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Send size={18} className="mr-2" />
                    Enviar Convite
                  </React.Fragment>
                )}
              </Button>
            </div>
          </form>
        )}
      </Modal>
      
      {/* Modal de Permissões */}
      <Modal 
        isOpen={!!showPermissionsModal} 
        onClose={() => setShowPermissionsModal(null)} 
        title={`Permissões de ${showPermissionsModal?.name || showPermissionsModal?.email}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-zinc-500">
            Defina o que este usuário pode ver e fazer em cada módulo.
          </p>

          <PermissionEditor
            value={editingPermissions}
            onChange={setEditingPermissions}
            hideModules={["receitas"]}
          />

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowPermissionsModal(null)} className="flex-1">
              Cancelar
            </Button>
            <Button onClick={handleUpdatePermissions} className="flex-1" data-testid="save-permissions-btn">
              Salvar Permissões
            </Button>
          </div>
        </div>
      </Modal>

      {/* ===== Iter 75 — Drawer lateral premium do colaborador ===== */}
      {selectedCollab && (
        <>
          <div
            className="fixed inset-0 z-[70] bg-zinc-900/40 backdrop-blur-sm transition-opacity"
            onClick={closeCollabDrawer}
            aria-hidden
          />
          <aside
            className="fixed top-0 right-0 bottom-0 z-[71] w-full sm:w-[460px] bg-white shadow-[0_30px_80px_-20px_rgba(24,24,27,0.40)] flex flex-col"
            data-testid="collab-drawer"
            role="dialog"
            aria-label="Detalhes do usuário"
          >
            {/* Header */}
            <div className="px-5 pt-5 pb-4 border-b border-zinc-100/80">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 text-white text-[14px] font-bold tracking-wide"
                    style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 60%, #FFB347 100%)" }}
                  >
                    {(selectedCollab.name || selectedCollab.email || '?').split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(s => s.charAt(0).toUpperCase()).join('') || '?'}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-[16px] font-semibold text-zinc-900 truncate">
                        {selectedCollab.name || selectedCollab.email}
                      </h3>
                      <span className="inline-flex items-center px-1.5 py-[1px] rounded-full bg-zinc-100 text-zinc-600 text-[9.5px] font-semibold tracking-wide">
                        Membro equipe
                      </span>
                    </div>
                    <p className="text-[12px] text-zinc-500 truncate mt-0.5">{selectedCollab.email}</p>
                    <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px]">
                      <span className={`w-1.5 h-1.5 rounded-full ${collabStatusInfo(selectedCollab.status).dot}`} />
                      <span className="font-medium text-zinc-700">{collabStatusInfo(selectedCollab.status).label}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={closeCollabDrawer}
                  className="p-2 -mr-2 rounded-xl hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors"
                  data-testid="collab-drawer-close"
                  aria-label="Fechar"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Tabs */}
              <div className="mt-4 flex items-center gap-1 bg-zinc-100/60 p-1 rounded-xl">
                {[
                  { key: 'dados', label: 'Dados' },
                  { key: 'eventos', label: `Eventos · ${(selectedCollab.wedding_ids || []).length}` },
                  { key: 'permissoes', label: 'Permissões' },
                  { key: 'convite', label: 'Link de Acesso' },
                ].map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setCollabDrawerTab(t.key)}
                    className={`flex-1 px-2.5 py-1.5 rounded-lg text-[11.5px] font-semibold transition-all ${collabDrawerTab === t.key ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-600 hover:text-zinc-900'}`}
                    data-testid={`collab-drawer-tab-${t.key}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Conteúdo */}
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {/* DADOS */}
              {collabDrawerTab === 'dados' && (
                <div className="space-y-4" data-testid="collab-drawer-dados">
                  <div>
                    <p className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-zinc-400 mb-1">Nome</p>
                    <p className="text-[14px] text-zinc-900 font-medium">{selectedCollab.name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-zinc-400 mb-1">Email</p>
                    <p className="text-[14px] text-zinc-700 break-all">{selectedCollab.email}</p>
                  </div>
                  <div>
                    <p className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-zinc-400 mb-1">Função</p>
                    <p className="text-[14px] text-zinc-900 font-medium capitalize">{selectedCollab.role || 'viewer'}</p>
                  </div>
                  <div>
                    <p className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-zinc-400 mb-1">Último acesso</p>
                    <p className="text-[14px] text-zinc-700">
                      {selectedCollab.accepted_at ? formatRelative(selectedCollab.accepted_at) : 'Aguardando aceitar convite'}
                    </p>
                  </div>
                </div>
              )}

              {/* EVENTOS ATRIBUÍDOS — checklist */}
              {collabDrawerTab === 'eventos' && (
                <div className="space-y-3" data-testid="collab-drawer-eventos">
                  <p className="text-[12px] text-zinc-500">
                    Selecione os eventos do seu espaço aos quais este usuário terá acesso.
                  </p>
                  <div className="space-y-1.5">
                    {(weddings || []).length === 0 && (
                      <p className="text-[12px] text-zinc-500 text-center py-4">Sem eventos no seu espaço.</p>
                    )}
                    {(weddings || []).map(ev => {
                      const checked = drawerSelectedEvents.has(ev.id);
                      return (
                        <label
                          key={ev.id}
                          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-colors ${checked ? 'bg-violet-50 ring-1 ring-violet-200' : 'bg-zinc-50/60 hover:bg-zinc-100/60'}`}
                          data-testid={`drawer-event-toggle-${ev.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleDrawerEvent(ev.id)}
                            className="w-4 h-4 rounded border-zinc-300 text-violet-600 focus:ring-violet-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-zinc-900 truncate">
                              {ev.event_name || ev.partner1_name || 'Evento'}
                            </p>
                            <p className="text-[10.5px] text-zinc-500">
                              {EVENT_TYPE_LABELS?.[ev.event_type] || ev.event_type || 'Evento'}
                              {ev.event_date && ` · ${formatDateBR(ev.event_date)}`}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {drawerEventsDirty && (
                    <button
                      type="button"
                      onClick={saveDrawerEvents}
                      className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-white text-[13px] font-semibold"
                      style={{ backgroundImage: 'linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)' }}
                      data-testid="drawer-save-events"
                    >
                      <Check size={14} /> Salvar alterações
                    </button>
                  )}
                </div>
              )}

              {/* PERMISSÕES — perfil + editor inline */}
              {collabDrawerTab === 'permissoes' && (
                <div className="space-y-4" data-testid="collab-drawer-permissoes">
                  <p className="text-[12px] text-zinc-500">
                    Selecione um perfil pronto ou personalize permissões por módulo.
                  </p>

                  {/* Cards de perfil rápido */}
                  <div>
                    <p className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-zinc-400 mb-2">Perfil de acesso</p>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'admin', label: 'Administrador', desc: 'Acesso total', cls: 'violet' },
                        { key: 'editor', label: 'Operacional', desc: 'Edita exceto $', cls: 'sky' },
                        { key: 'viewer', label: 'Visualizador', desc: 'Somente leitura', cls: 'zinc' },
                        { key: 'custom', label: 'Personalizado', desc: 'Por módulo', cls: 'amber' },
                      ].map(opt => {
                        const isActive = (selectedCollab.role || 'viewer') === opt.key;
                        const ringCls = isActive
                          ? (opt.cls === 'violet' ? 'ring-violet-300 bg-violet-50'
                            : opt.cls === 'sky' ? 'ring-sky-300 bg-sky-50'
                            : opt.cls === 'amber' ? 'ring-amber-300 bg-amber-50'
                            : 'ring-zinc-400 bg-zinc-100')
                          : 'ring-zinc-200 bg-white hover:bg-zinc-50';
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={async () => {
                              try {
                                if (selectedCollab?.user_id) {
                                  await axios.post(`${API}/collab/${selectedCollab.user_id}/assign`, {
                                    add_wedding_ids: [],
                                    remove_wedding_ids: [],
                                    role: opt.key,
                                  });
                                } else if (selectedCollab?.invite_token) {
                                  await axios.post(`${API}/collab/by-invite/${selectedCollab.invite_token}/assign`, {
                                    add_wedding_ids: [],
                                    remove_wedding_ids: [],
                                    role: opt.key,
                                  });
                                }
                                toast.success(`Perfil alterado para ${opt.label}.`);
                                setSelectedCollab(prev => prev ? { ...prev, role: opt.key } : prev);
                                await fetchCollabList();
                              } catch (err) {
                                toast.error(getErrorMessage(err, "Erro ao alterar perfil"));
                              }
                            }}
                            className={`text-left p-3 rounded-xl ring-1 transition-colors ${ringCls}`}
                            data-testid={`drawer-role-${opt.key}`}
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-[12.5px] font-semibold text-zinc-900">{opt.label}</p>
                              {isActive && <Check size={13} className="text-zinc-700" strokeWidth={2.5} />}
                            </div>
                            <p className="text-[10.5px] text-zinc-500 mt-0.5">{opt.desc}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Editor granular */}
                  <div>
                    <p className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-zinc-400 mb-2 mt-1">Granular por módulo</p>
                    <button
                      type="button"
                      onClick={() => {
                        const wuId = (selectedCollab.wedding_user_ids || [])[0];
                        const target = {
                          id: wuId,
                          user_id: selectedCollab.user_id,
                          invite_token: selectedCollab.invite_token,
                          permissions: selectedCollab.permissions || {},
                          name: selectedCollab.name,
                          email: selectedCollab.email,
                        };
                        openPermissionsModal(target);
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-zinc-200 text-zinc-700 hover:bg-zinc-50 text-[13px] font-semibold"
                      data-testid="drawer-edit-permissions"
                    >
                      <Settings size={14} /> Editar permissões avançadas
                    </button>
                  </div>
                </div>
              )}

              {/* LINK DE ACESSO — único botão "Copiar Link de Acesso" */}
              {collabDrawerTab === 'convite' && (
                <div className="space-y-4" data-testid="collab-drawer-convite">
                  {selectedCollab.invite_token && selectedCollab.status === 'pending' ? (
                    <>
                      <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-pink-50/40 ring-1 ring-violet-100 p-4">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="w-7 h-7 rounded-lg bg-white/70 flex items-center justify-center">
                            <Sparkles size={14} className="text-violet-600" />
                          </div>
                          <p className="text-[12.5px] font-bold text-violet-900">Acesso pendente</p>
                        </div>
                        <p className="text-[11.5px] text-violet-800/80 leading-relaxed">
                          Copie o link abaixo e compartilhe com o usuário por WhatsApp, Telegram, email ou qualquer canal de sua preferência.
                        </p>
                      </div>

                      {/* Preview do link */}
                      <div className="rounded-xl bg-zinc-50 ring-1 ring-zinc-200/60 p-3">
                        <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-zinc-400 mb-1.5">Link de acesso</p>
                        <p className="text-[11.5px] font-mono text-zinc-700 break-all leading-relaxed">
                          {`${window.location.origin}/invite/staff/${selectedCollab.invite_token}`}
                        </p>
                      </div>

                      {/* Único botão grande */}
                      <button
                        type="button"
                        onClick={async () => {
                          const url = `${window.location.origin}/invite/staff/${selectedCollab.invite_token}`;
                          const ok = await robustCopy(url);
                          if (ok) toast.success("Link copiado");
                          else toast.error("Não foi possível copiar — selecione e copie manualmente");
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-white text-[14px] font-bold transition-transform active:scale-[0.98]"
                        style={{
                          backgroundImage: 'linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)',
                          boxShadow: '0 12px 28px -10px rgba(107,61,246,0.40)'
                        }}
                        data-testid="drawer-copy-access-link"
                      >
                        <Copy size={15} />
                        Copiar Link de Acesso
                      </button>
                    </>
                  ) : (
                    <div className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-100 p-4">
                      <div className="flex items-start gap-2.5">
                        <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[13px] font-bold text-emerald-900">Acesso ativo</p>
                          <p className="text-[11.5px] text-emerald-800/85 mt-0.5">Este usuário já tem acesso ativo aos eventos atribuídos.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-zinc-100/80">
                    <button
                      type="button"
                      onClick={async () => {
                        if (!(await confirmDialog({
                          title: "Revogar todo o acesso",
                          message: "Tem certeza que deseja revogar TODO o acesso deste usuário a todos os eventos?",
                          confirmLabel: "Revogar tudo",
                          variant: "danger",
                        }))) return;
                        const ids = selectedCollab.wedding_ids || [];
                        const uid = selectedCollab.user_id;
                        try {
                          if (uid) {
                            await axios.post(`${API}/collab/${uid}/assign`, {
                              remove_wedding_ids: ids,
                            });
                          } else {
                            // Pending invite: revogar cada wedding_user diretamente
                            const wuids = selectedCollab.wedding_user_ids || [];
                            for (let i = 0; i < ids.length; i++) {
                              try {
                                await axios.delete(`${API}/weddings/${ids[i]}/users/${wuids[i]}`);
                              } catch {}
                            }
                          }
                          toast.success("Acesso revogado.");
                          closeCollabDrawer();
                          await fetchCollabList();
                        } catch (err) {
                          toast.error(getErrorMessage(err, "Erro ao revogar acesso"));
                        }
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-rose-200 text-rose-700 hover:bg-rose-50 text-[13px] font-semibold"
                      data-testid="drawer-revoke-access"
                    >
                      <Trash2 size={14} /> Revogar todo o acesso
                    </button>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

// ==================== PÁGINA PÚBLICA DE CHECK-IN AUTOMÁTICO ====================
// Quando a recepcionista escaneia o QR Code, esta página abre e faz o check-in AUTOMATICAMENTE
function PublicCheckinPage() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, ready, success, already, error
  const [data, setData] = useState(null);
  const [presences, setPresences] = useState({}); // { index: bool } — default todos presentes
  const [errorMessage, setErrorMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  // Lookup DINÂMICO do convite ao escanear o QR (token = credencial, sem login).
  // Reflete a MESA ATUAL e os participantes em tempo real, sem reenviar o QR.
  useEffect(() => {
    const loadInvite = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage('Token inválido');
        return;
      }
      try {
        const res = await axios.get(`${API}/rsvp/${token.toUpperCase()}/checkin-lookup`);
        const d = res.data;

        if (d.status === 'BLOQUEADO') {
          setStatus('error');
          setErrorMessage(d.message || 'Entrada bloqueada');
          return;
        }

        setData(d);
        // Checkboxes: todos marcados por padrão (present=true)
        const init = {};
        (d.participants || []).forEach((p) => { init[p.index] = p.present !== false; });
        setPresences(init);

        if (d.status === 'JA_REALIZADO' || d.already_checked_in) {
          setStatus('already');
          setErrorMessage(`Check-in já realizado para ${d.invite_name}`);
          return;
        }
        setStatus('ready');
      } catch (err) {
        setStatus('error');
        setErrorMessage(err.response?.data?.detail || 'Erro ao carregar convite');
      }
    };
    loadInvite();
  }, [token]);

  const togglePresence = (index) => {
    setPresences((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  // Confirma o check-in por participante (marca ausentes, libera assentos).
  const handleCheckin = async () => {
    setProcessing(true);
    try {
      const presencesArr = (data?.participants || []).map((p) => ({
        index: p.index,
        present: presences[p.index] !== false,
      }));
      const res = await axios.post(`${API}/rsvp/${token.toUpperCase()}/checkin-confirm`, {
        presences: presencesArr,
      });

      if (res.data.status === 'LIBERADO') {
        setData(res.data);
        setStatus('success');
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
          oscillator.connect(audioContext.destination);
          oscillator.start();
          oscillator.stop(audioContext.currentTime + 0.2);
        } catch (e) {}
      } else if (res.data.status === 'JA_REALIZADO') {
        setStatus('already');
        setErrorMessage(res.data.message || 'Check-in já realizado');
      } else {
        setStatus('error');
        setErrorMessage(res.data.message || 'Erro ao fazer check-in');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('já') || detail.toLowerCase().includes('already')) {
        setStatus('already');
        setErrorMessage(detail);
      } else {
        setStatus('error');
        setErrorMessage(detail || 'Erro ao fazer check-in');
      }
    } finally {
      setProcessing(false);
    }
  };

  // Tela de LOADING
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-white mx-auto mb-4" size={64} />
          <p className="text-white text-xl">Carregando...</p>
        </div>
      </div>
    );
  }

  // Tela PRONTA PARA CHECK-IN (checkboxes por convidado + mesa dinâmica)
  if (status === 'ready') {
    const participants = data?.participants || [];
    const selectedCount = participants.filter((p) => presences[p.index] !== false).length;
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-white">
          <div className="text-center mb-5">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <UserCheck className="text-white" size={32} />
            </div>
            <h1 className="text-2xl font-bold">Check-in</h1>
            <p className="text-lg opacity-90 mt-1" data-testid="public-checkin-invite-name">{data?.invite_name}</p>
            {data?.table_name && (
              <span className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-white/20 text-sm font-medium" data-testid="public-checkin-table">
                🪑 {data.table_name}
              </span>
            )}
          </div>

          <div className="bg-white rounded-2xl p-4 mb-4 text-zinc-900">
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-wide mb-3">
              Quem está entrando? <span className="text-zinc-400 normal-case font-normal">(desmarque quem faltou)</span>
            </p>
            <div className="space-y-2">
              {participants.map((p) => {
                const checked = presences[p.index] !== false;
                return (
                  <button
                    key={p.index}
                    type="button"
                    onClick={() => togglePresence(p.index)}
                    data-testid={`public-checkin-participant-${p.index}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${checked ? 'border-emerald-500 bg-emerald-50' : 'border-zinc-200 bg-zinc-50'}`}
                  >
                    <span className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${checked ? 'bg-emerald-500' : 'bg-white border-2 border-zinc-300'}`}>
                      {checked && <Check className="text-white" size={16} />}
                    </span>
                    <span className="flex-1">
                      <span className={`block text-[15px] font-semibold ${checked ? 'text-zinc-900' : 'text-zinc-400 line-through'}`}>{p.name}</span>
                      {p.type_label && <span className="block text-[11px] text-zinc-400">{p.type_label}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
            {participants.length === 0 && (
              <p className="text-sm text-zinc-500 text-center py-2">Convite sem participantes detalhados.</p>
            )}
          </div>

          <button
            onClick={handleCheckin}
            disabled={processing}
            data-testid="public-checkin-confirm-btn"
            className="w-full py-4 px-8 bg-emerald-500 hover:bg-emerald-600 text-white text-lg font-bold rounded-2xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            {processing ? (
              <><Loader2 className="animate-spin" size={22} /> Processando...</>
            ) : (
              <><Check size={22} /> CONFIRMAR ENTRADA ({selectedCount})</>
            )}
          </button>
          <p className="text-sm opacity-60 mt-3 text-center">
            A mesa e a lista são atualizadas em tempo real a cada leitura do QR.
          </p>
        </div>
      </div>
    );
  }

  // Tela de SUCESSO ✅ (verde)
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-emerald-500 flex items-center justify-center p-4">
        <div className="text-center text-white max-w-md">
          <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <Check className="text-emerald-500" size={80} />
          </div>
          <h1 className="text-4xl font-bold mb-4">CHECK-IN REALIZADO!</h1>
          <p className="text-2xl mb-6">{data?.invite_name}</p>
          <div className="bg-white/20 rounded-2xl p-6 mb-6">
            <p className="text-xl mb-2">✅ {data?.present_count ?? 1} presente(s)</p>
            {data?.mesa && <p className="text-xl">🪑 Mesa: {data.mesa}</p>}
            {Array.isArray(data?.absent_names) && data.absent_names.length > 0 && (
              <p className="text-base mt-2 opacity-90">Ausentes: {data.absent_names.join(', ')}</p>
            )}
          </div>
          <p className="text-lg opacity-80">
            ✅ Entrada liberada
          </p>
          <p className="text-sm mt-4 opacity-60">
            {new Date().toLocaleTimeString('pt-BR')}
          </p>
        </div>
      </div>
    );
  }

  // Tela de JÁ FEZ CHECK-IN ⚠️ (amarelo)
  if (status === 'already') {
    return (
      <div className="min-h-screen bg-yellow-500 flex items-center justify-center p-4">
        <div className="text-center text-white max-w-md">
          <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="text-yellow-500" size={80} />
          </div>
          <h1 className="text-4xl font-bold mb-4">JÁ FEZ CHECK-IN</h1>
          <p className="text-xl mb-6">{errorMessage}</p>
          <p className="text-lg opacity-80">
            Este convidado já entrou no evento
          </p>
        </div>
      </div>
    );
  }

  // Tela de ERRO ❌ (vermelho)
  return (
    <div className="min-h-screen bg-red-500 flex items-center justify-center p-4">
      <div className="text-center text-white max-w-md">
        <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
          <X className="text-red-500" size={80} />
        </div>
        <h1 className="text-4xl font-bold mb-4">ENTRADA BLOQUEADA</h1>
        <p className="text-xl mb-6">{errorMessage}</p>
        <p className="text-lg opacity-80">
          Verifique com o organizador
        </p>
      </div>
    </div>
  );
}


// ==================== PÁGINA WHATSAPP ====================
function WhatsAppPage() {
  const [waStatus, setWaStatus] = useState({ connected: false, state: 'loading' });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testSending, setTestSending] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/wa/status`, { headers: { Authorization: `Bearer ${token}` } });
      setWaStatus(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Auto-refresh status every 5s when waiting for QR scan
  useEffect(() => {
    if (!qrCode || waStatus.connected) return;
    const interval = setInterval(() => {
      fetchStatus().then(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [qrCode, waStatus.connected, fetchStatus]);

  // Clear QR when connected
  useEffect(() => {
    if (waStatus.connected && qrCode) setQrCode(null);
  }, [waStatus.connected, qrCode]);

  const handleConnect = async () => {
    setConnecting(true);
    setQrCode(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/wa/connect`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.qr_code && res.data.qr_code !== 'MOCK_QR_CODE_BASE64_DATA') {
        setQrCode(res.data.qr_code);
        setWaStatus(prev => ({ ...prev, connected: false, state: 'qr_ready' }));
        toast.success('QR Code gerado! Escaneie com seu WhatsApp.');
      } else if (res.data.mock) {
        toast.info('Modo demonstração — conecte a Evolution API para WhatsApp real');
        fetchStatus();
      } else {
        fetchStatus();
      }
    } catch (err) { toast.error('Erro ao gerar QR Code'); }
    finally { setConnecting(false); }
  };

  const handleDisconnect = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/wa/disconnect`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setWaStatus({ connected: false, state: 'disconnected' });
      toast.success('WhatsApp desconectado');
    } catch (err) { toast.error('Erro ao desconectar'); }
  };

  const handleTestSend = async () => {
    if (!testPhone || testPhone.replace(/\D/g,'').length < 10) {
      toast.error('Informe um numero valido');
      return;
    }
    setTestSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/wa/test-send`, { phone: testPhone }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Mensagem de teste enviada!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao enviar teste'); }
    finally { setTestSending(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-green-500" size={32} /></div>;

  return (
    <div className="space-y-6" data-testid="whatsapp-page" style={{fontFamily:"'Poppins', sans-serif"}}>
      <div>
        <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">WhatsApp</h1>
        <p className="text-sm text-zinc-500">Conecte seu WhatsApp para enviar convites e confirmações</p>
      </div>

      {/* Status da Conexão */}
      <div className="bg-white rounded-2xl p-5 sm:p-6 border border-zinc-200/60" data-testid="wa-status-card">
        <div className="flex items-center gap-3 sm:gap-4 mb-5">
          <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${waStatus.connected ? 'bg-green-100' : 'bg-slate-100'}`}>
            <MessageCircle size={24} className={waStatus.connected ? 'text-green-600' : 'text-slate-400'} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-[16px] sm:text-[17px] text-zinc-900 tracking-tight">Status da Conexão</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`w-2 h-2 rounded-full ${waStatus.connected ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
              <span className={`text-[12.5px] font-medium ${waStatus.connected ? 'text-green-600' : 'text-slate-500'}`}>
                {waStatus.connected ? 'Conectado' : waStatus.state === 'qr_ready' ? 'Aguardando QR Code' : 'Desconectado'}
              </span>
              {waStatus.mock && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">DEMO</span>}
            </div>
          </div>
        </div>

        {waStatus.connected && (
          <div className="bg-green-50 rounded-xl p-3.5 mb-5 border border-green-100">
            <div className="flex items-center gap-3">
              {waStatus.picture ? <img src={waStatus.picture} alt="" className="w-11 h-11 rounded-full flex-shrink-0" /> : <div className="w-11 h-11 rounded-full bg-green-200 flex items-center justify-center flex-shrink-0"><Phone size={18} className="text-green-700" /></div>}
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-green-800 truncate">{waStatus.name || 'WhatsApp Conectado'}</p>
                <p className="text-[12px] text-green-600">{waStatus.phone ? `+${waStatus.phone}` : ''}</p>
                {waStatus.connected_at && <p className="text-[10.5px] text-green-500 mt-0.5">Conectado desde {new Date(waStatus.connected_at).toLocaleDateString('pt-BR')}</p>}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
          {!waStatus.connected ? (
            <button onClick={handleConnect} disabled={connecting} className="w-full sm:w-auto h-12 sm:h-10 px-5 inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 text-white text-[14px] font-semibold transition-colors disabled:opacity-60" data-testid="wa-connect-btn">
              {connecting ? <Loader2 className="animate-spin" size={16} /> : <QrCode size={16} />}
              Conectar WhatsApp
            </button>
          ) : (
            <>
              <button onClick={handleConnect} disabled={connecting} className="flex-1 sm:flex-none h-11 sm:h-9 px-4 inline-flex items-center justify-center gap-1.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-[13px] font-semibold transition-colors" data-testid="wa-reconnect-btn">
                <QrCode size={14} /> Reconectar
              </button>
              <button onClick={handleDisconnect} className="flex-1 sm:flex-none h-11 sm:h-9 px-4 inline-flex items-center justify-center gap-1.5 rounded-xl bg-white border border-red-200 text-red-600 hover:bg-red-50 text-[13px] font-semibold transition-colors" data-testid="wa-disconnect-btn">
                <X size={14} /> Desconectar
              </button>
            </>
          )}
        </div>

          {/* Test Send with phone input */}
          {waStatus.connected && (
            <div className="mt-5 p-4 bg-zinc-50 rounded-xl border border-zinc-200/60">
              <p className="text-[10.5px] font-semibold text-zinc-400 uppercase tracking-[0.14em] mb-2.5">Testar envio real</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input type="text" placeholder="Número com DDD (ex: 5511999999999)" value={testPhone} onChange={e => setTestPhone(e.target.value)}
                  className="flex-1 px-3.5 py-3 sm:py-2 bg-white border border-zinc-200 rounded-xl text-[14px] focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300 transition-all" data-testid="wa-test-phone" />
                <button onClick={handleTestSend} disabled={testSending} className="h-11 sm:h-10 px-4 inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 text-white text-[13.5px] font-semibold transition-colors disabled:opacity-60" data-testid="wa-test-btn">
                  {testSending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
                  Enviar teste
                </button>
              </div>
              <p className="text-[11px] text-zinc-400 mt-2">Envia uma mensagem de teste real para validar a conexão</p>
            </div>
          )}

          {/* QR Code Display */}
          {qrCode && !waStatus.connected && (
            <div className="mt-5 p-6 sm:p-8 bg-white rounded-2xl border border-zinc-200/60 text-center" data-testid="wa-qrcode-display">
              <p className="text-[13.5px] font-semibold text-zinc-900 mb-5">Escaneie o QR Code com seu WhatsApp</p>
              <div className="inline-block p-3 sm:p-4 rounded-2xl bg-white ring-1 ring-zinc-200/60 shadow-[0_8px_32px_-12px_rgba(24,24,27,0.18)]">
                <img
                  src={qrCode}
                  alt="QR Code WhatsApp"
                  className="w-72 h-72 sm:w-80 sm:h-80 mx-auto rounded-lg block"
                  style={{ imageRendering: 'pixelated' }}
                />
              </div>
              <p className="text-[12px] text-zinc-500 mt-5">Abra o WhatsApp &gt; Menu &gt; Dispositivos conectados &gt; Conectar dispositivo</p>
              <button
                type="button"
                onClick={handleConnect}
                disabled={connecting}
                className="tap-scale mt-4 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-white border border-zinc-200 text-zinc-700 text-[12.5px] font-medium hover:bg-zinc-50 transition-all disabled:opacity-50"
                data-testid="wa-regen-qr"
              >
                {connecting ? <Loader2 className="animate-spin" size={13} /> : <QrCode size={13} />}
                Gerar novo QR Code
              </button>
            </div>
          )}
      </div>

      {/* Boas Práticas */}
      <div className="bg-white rounded-xl p-5 border border-zinc-200/60" data-testid="wa-best-practices">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
            <Shield size={18} className="text-zinc-500" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-zinc-900 mb-2">Boas práticas de uso</h3>
            <p className="text-xs text-zinc-500 leading-relaxed mb-3">
              O liventy utiliza o seu próprio número de WhatsApp para envio de convites, confirmações e QR Codes do evento. Para maior estabilidade recomendamos:
            </p>
            <ul className="text-xs text-zinc-500 space-y-1.5">
              <li className="flex items-center gap-2"><Check size={12} className="text-zinc-400 flex-shrink-0" /> Utilizar contatos reais do evento</li>
              <li className="flex items-center gap-2"><Check size={12} className="text-zinc-400 flex-shrink-0" /> Evitar grandes volumes instantaneamente</li>
              <li className="flex items-center gap-2"><Check size={12} className="text-zinc-400 flex-shrink-0" /> Utilizar um número profissional/comercial</li>
              <li className="flex items-center gap-2"><Check size={12} className="text-zinc-400 flex-shrink-0" /> Manter interação natural com convidados</li>
              <li className="flex items-center gap-2"><Check size={12} className="text-zinc-400 flex-shrink-0" /> Evitar mensagens repetitivas excessivas</li>
            </ul>
            <p className="text-xs text-zinc-400 mt-3 leading-relaxed">
              O envio de RSVP possui risco reduzido por se tratar de comunicação legítima de eventos.
            </p>
            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-zinc-100 rounded-full">
              <MessageCircle size={12} className="text-zinc-500" />
              <span className="text-[10px] font-medium text-zinc-600">Canal principal de RSVP e check-in</span>
            </div>
          </div>
        </div>
      </div>

      {/* Info: envios ficam em Convidados */}
      <div className="bg-white rounded-xl p-4 border border-zinc-200/60">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
            <Users size={15} className="text-zinc-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-zinc-700">Envio de convites e confirmações</p>
            <p className="text-xs text-zinc-500">Os disparos são feitos na página <strong>Convidados</strong>.</p>
          </div>
        </div>
      </div>
    </div>
  );
}


// ==================== PÁGINA DE SUPORTE FAQ ====================
function SupportPage() {
  const { user } = useAuth();
  const { wedding } = useWedding();
  const location = useLocation();
  const firstName = user?.name?.split(' ')[0] || '';
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `${firstName ? `Olá, ${firstName}! ` : 'Olá! '}Sou o assistente do **liventy**. Posso te orientar passo a passo em qualquer parte do sistema — convidados, RSVP, WhatsApp, QR Code, mesas, check-in, orçamentos e muito mais.\n\nComo posso ajudar?`,
      suggestions: [
        'Como envio mensagens em massa via WhatsApp?',
        'Como envio QR Code apenas para confirmados?',
        'Como organizo as mesas?',
        'Como funciona o check-in no dia do evento?'
      ]
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/support`, {
        message: text,
        session_id: sessionId,
        context: {
          path: location.pathname,
          wedding_id: wedding?.id || null,
        }
      });
      if (!sessionId) setSessionId(res.data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        suggestions: res.data.suggestions || []
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Desculpe, tive um problema momentâneo. Tente novamente em instantes.',
        suggestions: []
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const sendMessage = (e) => { e.preventDefault(); send(); };

  const newSession = () => {
    if (sessionId) axios.delete(`${API}/ai/support/session/${sessionId}`).catch(() => {});
    setSessionId(null);
    setMessages([{
      role: 'assistant',
      content: `${firstName ? `Olá, ${firstName}! ` : 'Olá! '}Sou o assistente do **liventy**. Como posso ajudar?`,
      suggestions: [
        'Como envio mensagens em massa via WhatsApp?',
        'Como envio QR Code apenas para confirmados?',
        'Como organizo as mesas?',
        'Como funciona o check-in no dia do evento?'
      ]
    }]);
  };

  // Inline markdown bold
  const renderText = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      const isBullet = line.startsWith('- ') || line.startsWith('• ');
      const cleanLine = isBullet ? line.replace(/^[-•]\s/, '') : line;
      const parts = cleanLine.split(/(\*\*[^*]+\*\*)/g).map(p => (
        p.startsWith('**') && p.endsWith('**')
          ? { type: 'bold', text: p.slice(2, -2) }
          : { type: 'text', text: p }
      ));
      return (
        <React.Fragment key={i}>
          {isBullet && <span className="text-zinc-400 mr-1.5">•</span>}
          {parts.map((p, k) => (
            p.type === 'bold'
              ? <strong key={k} className="font-semibold text-zinc-900">{p.text}</strong>
              : <React.Fragment key={k}>{p.text}</React.Fragment>
          ))}
          {i < text.split('\n').length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="space-y-5" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{backgroundImage:'linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)'}}>
            <Sparkles className="text-blue-400" size={18} />
          </div>
          <div>
            <h1 className="text-[18px] sm:text-[20px] font-bold text-zinc-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.025em' }}>Central de Ajuda</h1>
            <p className="text-[12px] text-zinc-500">Assistente IA + guia completo de tudo o que o liventy faz</p>
          </div>
        </div>
        <button
          onClick={newSession}
          className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white border border-zinc-200 text-zinc-700 text-[12.5px] font-medium hover:bg-zinc-50 transition-colors"
          data-testid="support-new-session"
        >
          <RefreshCw size={13} />
          <span className="hidden sm:inline">Nova conversa</span>
        </button>
      </div>

      {/* Chat */}
      <div className="h-[calc(100vh-280px)] sm:h-[600px] flex flex-col bg-white rounded-2xl border border-zinc-200/70 shadow-[0_1px_2px_rgba(24,24,27,0.04)] overflow-hidden" data-testid="support-chat">
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 sm:py-5 space-y-3.5 bg-[#FAFAFA]">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3.5 py-2.5 text-[13.5px] leading-[1.55] tracking-[-0.005em] ${
                msg.role === 'user'
                  ? 'bg-brand-deep text-white rounded-2xl rounded-br-md'
                  : 'bg-white border border-zinc-200/70 rounded-2xl rounded-bl-md text-zinc-800'
              }`} data-testid={`support-msg-${msg.role}-${idx}`}>
                <div>{renderText(msg.content)}</div>
                {msg.role === 'assistant' && msg.suggestions && msg.suggestions.length > 0 && (
                  <div className="mt-3 -mb-1 flex flex-col items-start gap-1.5">
                    {msg.suggestions.map((s, j) => (
                      <button
                        key={j}
                        onClick={() => send(s)}
                        disabled={loading}
                        className="tap-scale text-[12px] text-left px-2.5 py-1.5 rounded-lg border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 hover:border-zinc-300 text-zinc-700 transition-all disabled:opacity-50"
                        data-testid={`support-suggestion-${idx}-${j}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border border-zinc-200/70 rounded-2xl rounded-bl-md px-3.5 py-3 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" style={{ animationDelay: '120ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-pulse" style={{ animationDelay: '240ms' }} />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={sendMessage} className="p-3 sm:p-4 border-t border-zinc-100 bg-white">
          <div className="flex items-end gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pergunte qualquer coisa sobre o liventy..."
              className="flex-1 h-11 px-3.5 rounded-xl border border-zinc-200 bg-zinc-50 text-[14px] text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white focus:ring-4 focus:ring-zinc-900/5 transition-all"
              disabled={loading}
              data-testid="support-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="tap-scale w-11 h-11 rounded-xl bg-brand-deep text-white hover:bg-brand active:scale-95 transition-all flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              data-testid="support-send"
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : <ArrowUp size={16} />}
            </button>
          </div>
        </form>
      </div>

      {/* Catálogo completo — Tudo o que o liventy faz */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-[17px] font-bold text-zinc-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '-0.02em' }}>Tudo o que o liventy faz</h2>
            <p className="text-[12px] text-zinc-500">Toque em qualquer item para o assistente te guiar passo a passo.</p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-700 bg-violet-50 ring-1 ring-violet-100 px-2.5 py-1 rounded-full">
            <Sparkles size={12} /> Itens marcados com “Novo” são recém-lançados
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[
            {
              icon: Users, grad: 'linear-gradient(135deg,#F97316,#FB7185)', label: 'Convidados & RSVP',
              items: [
                { t: 'Adicionar convidados (individual ou importar CSV)', q: 'Como adiciono convidados em massa por CSV?' },
                { t: 'Link público de confirmação (RSVP) por convite', q: 'Como funciona o link de RSVP dos convidados?' },
                { t: 'Confirmação por participante (adultos, crianças e bebês)', q: 'Como funciona a confirmação por participante?' },
                { t: 'Bebês não ocupam lugar e aparecem separadamente', q: 'Como o sistema trata bebês nas mesas?' },
                { t: 'Lista completa e visão por convite/família', q: 'Qual a diferença entre Lista Completa e Por Convite?' },
                { t: 'Restrições alimentares e previsão de buffet', q: 'Como vejo a previsão de buffet das confirmações?' },
              ],
            },
            {
              icon: MessageCircle, grad: 'linear-gradient(135deg,#22C55E,#10B981)', label: 'WhatsApp & Comunicação',
              items: [
                { t: 'Conectar o WhatsApp (Evolution API)', q: 'Como conecto o WhatsApp ao liventy?' },
                { t: 'Envio de mensagens em massa', q: 'Como envio mensagens em massa via WhatsApp?' },
                { t: 'Automações e Smart Rules', q: 'O que são as Smart Rules de WhatsApp?' },
                { t: 'Modelos de mensagem personalizados', q: 'Como crio modelos de mensagem?' },
                { t: 'Enviar QR Code apenas para confirmados', q: 'Como envio QR Code apenas para confirmados?' },
              ],
            },
            {
              icon: LayoutGrid, grad: 'linear-gradient(135deg,#6B3DF6,#FF4D8D)', label: 'Mesas & Mapa do Evento',
              items: [
                { t: 'Criar mesas (redonda, quadrada, retangular)', q: 'Como crio e configuro as mesas?' },
                { t: 'Arrastar convidados para as mesas', q: 'Como aloco convidados nas mesas?' },
                { t: 'Mapa do Evento: arrastar mesas no salão', q: 'Como uso o Mapa do Evento para montar o salão?', isNew: true },
                { t: 'Elementos: palco, bar, buffet, cozinha, banheiro, lounge…', q: 'Quais elementos posso adicionar no Mapa do Evento?', isNew: true },
                { t: 'Girar e redimensionar elementos', q: 'Como giro e redimensiono os elementos do salão?', isNew: true },
                { t: 'Elemento Personalizado e Caixa de texto (ambientes)', q: 'Como uso o elemento Personalizado e a Caixa de texto para ambientes?', isNew: true },
                { t: 'Exportar Organização em PDF (com logo e mapa real)', q: 'O que sai no PDF de Exportar Organização?', isNew: true },
              ],
            },
            {
              icon: UserCheck, grad: 'linear-gradient(135deg,#8B5CF6,#6366F1)', label: 'Check-in no dia',
              items: [
                { t: 'Scanner de QR Code em tempo real', q: 'Como funciona o check-in por QR Code no dia do evento?' },
                { t: 'Check-in manual (card clicável)', q: 'Como faço check-in manual sem QR Code?', isNew: true },
                { t: 'Acompanhamento de chegadas ao vivo', q: 'Como acompanho as chegadas em tempo real?' },
                { t: 'Controle de pessoas por convite (parcial)', q: 'Como registro a entrada parcial das pessoas de um convite?' },
              ],
            },
            {
              icon: Receipt, grad: 'linear-gradient(135deg,#0EA5E9,#6366F1)', label: 'Financeiro',
              items: [
                { t: 'Minhas Receitas com parcelamento', q: 'Como cadastro e edito receitas com parcelamento?' },
                { t: 'Fornecedores e orçamentos', q: 'Como gerencio fornecedores e orçamentos?' },
                { t: 'Despesas por categoria', q: 'Como controlo despesas por categoria?' },
                { t: 'Fluxo de caixa', q: 'Como funciona o fluxo de caixa?' },
                { t: 'Relatório financeiro em PDF (com logo oficial)', q: 'O que contém o relatório financeiro em PDF?', isNew: true },
              ],
            },
            {
              icon: Calendar, grad: 'linear-gradient(135deg,#EC4899,#F43F5E)', label: 'Tarefas & Organização',
              items: [
                { t: 'Visões: Lista, Calendário e Checklist (Kanban)', q: 'Quais visões de tarefas existem?' },
                { t: 'Arrastar cards entre status (Kanban)', q: 'Como arrasto tarefas entre os status?', isNew: true },
                { t: 'Responsável, prioridade e prazo', q: 'Como defino responsável, prioridade e prazo de uma tarefa?' },
                { t: 'Anexos e comentários por tarefa', q: 'Como adiciono anexos e comentários nas tarefas?' },
              ],
            },
            {
              icon: Sparkles, grad: 'linear-gradient(135deg,#A855F7,#EC4899)', label: 'Inspirações & Eventos',
              items: [
                { t: 'Galeria de inspirações do evento', q: 'Como uso as Inspirações?' },
                { t: 'Gerenciar múltiplos eventos', q: 'Como crio e alterno entre vários eventos?' },
                { t: 'Painel com indicadores do evento', q: 'O que aparece no Painel?' },
              ],
            },
            {
              icon: Settings, grad: 'linear-gradient(135deg,#71717A,#3F3F46)', label: 'Conta, Equipe & Plano',
              items: [
                { t: 'Perfil, marca e dados do evento', q: 'Onde edito os dados e a marca do evento?' },
                { t: 'Equipe e permissões de colaboradores', q: 'Como convido colaboradores e defino permissões?' },
                { t: 'Plano PRO e recursos premium', q: 'Quais recursos são exclusivos do plano PRO?' },
                { t: 'Assistente IA contextual', q: 'Como o Assistente IA pode me ajudar?' },
              ],
            },
          ].map((cat, ci) => (
            <div key={ci} className="bg-white rounded-2xl border border-zinc-200/70 p-4 shadow-[0_1px_2px_rgba(24,24,27,0.04)]" data-testid={`support-cat-${ci}`}>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundImage: cat.grad }}>
                  <cat.icon size={16} className="text-white" />
                </div>
                <h3 className="text-[13.5px] font-bold text-zinc-900 tracking-tight">{cat.label}</h3>
              </div>
              <ul className="space-y-0.5">
                {cat.items.map((it, ii) => (
                  <li key={ii}>
                    <button
                      onClick={() => send(it.q)}
                      disabled={loading}
                      className="group w-full text-left flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-zinc-50 transition-colors disabled:opacity-50"
                      data-testid={`support-cap-${ci}-${ii}`}
                    >
                      <ChevronRight size={14} className="text-zinc-300 group-hover:text-violet-500 mt-0.5 flex-shrink-0 transition-colors" />
                      <span className="text-[12.5px] text-zinc-700 leading-snug flex-1">{it.t}</span>
                      {it.isNew && (
                        <span className="text-[9.5px] font-bold uppercase tracking-wider text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-md flex-shrink-0">Novo</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contato humano */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-zinc-50 rounded-2xl border border-zinc-200/60 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white border border-zinc-200 flex items-center justify-center flex-shrink-0">
              <HelpCircle size={18} className="text-zinc-600" />
            </div>
            <div>
              <p className="text-[13.5px] font-semibold text-zinc-900">Não encontrou o que precisa?</p>
              <p className="text-[12px] text-zinc-500">Fale com nosso time — respondemos por e-mail.</p>
            </div>
          </div>
          <a
            href="mailto:contato@liventy.com.br"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-zinc-900 text-white text-[13px] font-semibold hover:bg-zinc-800 transition-colors"
            data-testid="support-contact-email"
          >
            <Send size={14} /> Falar com o suporte
          </a>
        </div>
      </div>
    </div>
  );
}

// RSVP Page - Página Pública de Confirmação de Presença
function RSVPPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [confirmedCount, setConfirmedCount] = useState(0);
  const [form, setForm] = useState({
    status: "confirmed",
    adults_count: 1,
    children_count: 0,
    dietary_restrictions: "",
    message: ""
  });

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const res = await axios.get(`${API}/rsvp/${token}`);
        setInviteData(res.data);
        // Inicializa participantes (V1 RSVP individual). Default: todos marcados (vão).
        const inc = (res.data.participants || []).map(p => ({
          name: p.name,
          type: p.type || "adult",
          age: p.age,
          going: p.rsvp === "declined" ? false : true,
          previousRsvp: p.rsvp || "pending",
        }));
        setParticipants(inc);
        setForm(prev => ({
          ...prev,
          adults_count: res.data.adults_count || 1,
          children_count: res.data.children_count || 0,
          dietary_restrictions: res.data.dietary_restrictions || ""
        }));
        // Se já confirmou, mostrar status (backend usa português: confirmado, nao_ira)
        if (res.data.status === "confirmed" || res.data.status === "confirmado" || 
            res.data.status === "declined" || res.data.status === "nao_ira") {
          setConfirmed(true);
          setConfirmedCount(res.data.confirmed_people ?? inc.filter(p => p.going).length);
          // Set form status for display
          if (res.data.status === "confirmado" || res.data.status === "confirmed") {
            setForm(prev => ({ ...prev, status: 'confirmed' }));
          } else {
            setForm(prev => ({ ...prev, status: 'declined' }));
          }
        }
      } catch (err) {
        setError("Convite não encontrado ou link inválido.");
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  const usePerParticipant = inviteData?.participants_named && participants.length > 0;

  const toggleParticipant = (idx) => {
    setParticipants(prev => prev.map((p, i) => i === idx ? { ...p, going: !p.going } : p));
  };

  const handleSubmit = async (status) => {
    setSubmitting(true);
    try {
      let payload = { ...form, status };
      let effectiveStatus = status;

      if (usePerParticipant) {
        // "Não poderei ir" => todos recusam. "Confirmar" => respeita os toggles.
        const decided = participants.map(p => ({
          name: p.name,
          type: p.type,
          rsvp: (status === "declined" ? false : p.going) ? "confirmed" : "declined",
        }));
        const nConfirmed = decided.filter(d => d.rsvp === "confirmed").length;
        effectiveStatus = nConfirmed > 0 ? "confirmed" : "declined";
        payload = { ...form, status: effectiveStatus, participants: decided };
        setConfirmedCount(nConfirmed);
      }

      await axios.post(`${API}/rsvp/${token}/confirm`, payload);
      setConfirmed(true);
      setForm(prev => ({ ...prev, status: effectiveStatus }));
      if (effectiveStatus === 'confirmed') {
        toast.success('Presença confirmada com sucesso!');
      } else {
        toast.info('Resposta registrada.');
      }
    } catch (err) {
      toast.error('Não foi possível registrar sua resposta. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center" style={{fontFamily:"'Poppins', sans-serif"}}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin text-zinc-400" size={32} />
          <p className="text-sm text-zinc-500">Carregando convite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center p-4" style={{fontFamily:"'Poppins', sans-serif"}}>
        <div className="bg-white rounded-2xl border border-zinc-200/60 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="text-zinc-400" size={26} />
          </div>
          <h1 className="text-xl font-semibold text-zinc-900 mb-1.5">Convite não encontrado</h1>
          <p className="text-sm text-zinc-500">{error}</p>
        </div>
      </div>
    );
  }

  const eventName = inviteData.event_name || 
    (inviteData.wedding_info?.event_name) ||
    (inviteData.wedding_info?.partner1_name && inviteData.wedding_info?.partner2_name 
      ? `${inviteData.wedding_info.partner1_name} & ${inviteData.wedding_info.partner2_name}` 
      : 'Evento');

  const eventDate = inviteData.event_date || inviteData.wedding_info?.event_date || inviteData.wedding_info?.wedding_date;
  const eventDateFormatted = eventDate 
    ? new Date(eventDate + (eventDate.includes('T') ? '' : 'T12:00:00')).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const eventTime = inviteData.event_time || inviteData.wedding_info?.event_time || '';
  const eventLocation = inviteData.event_location || inviteData.wedding_info?.venue_name || '';
  const eventAddress = inviteData.event_address || inviteData.wedding_info?.venue_address || '';

  return (
    <div className="min-h-screen bg-[#F8F8F8] py-8 px-4 flex items-center justify-center" style={{fontFamily:"'Poppins', sans-serif"}}>
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mx-auto mb-3">
            <LiventyLogoMark size={56} />
          </div>
          <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">{eventName}</h1>
          <p className="text-sm text-zinc-500 mt-1">Confirmação de Presença</p>
        </div>

        {/* Card Principal */}
        <div className="bg-white rounded-2xl border border-zinc-200/60 overflow-hidden">
          {/* Convidado */}
          <div className="px-6 py-5 border-b border-zinc-100">
            <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Convidado</p>
            <h2 className="text-lg font-semibold text-zinc-900">{inviteData.invite_name}</h2>
          </div>

          {/* Detalhes do Evento */}
          {(eventDateFormatted || eventLocation) && (
            <div className="px-6 py-4 border-b border-zinc-100 space-y-3">
              {eventDateFormatted && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-zinc-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="text-zinc-500" size={16} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-900 capitalize">{eventDateFormatted}</p>
                    {eventTime && <p className="text-xs text-zinc-500">às {eventTime.replace(':', 'h')}</p>}
                  </div>
                </div>
              )}
              {eventLocation && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-zinc-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-zinc-500" size={16} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-900">{eventLocation}</p>
                    {eventAddress && <p className="text-xs text-zinc-400">{eventAddress}</p>}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="px-6 py-5">
            {confirmed ? (
              <div className="text-center py-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  form.status === 'confirmed' ? 'bg-emerald-100' : 'bg-zinc-100'
                }`}>
                  {form.status === 'confirmed' ? <Check className="text-emerald-500" size={32} /> : <X className="text-zinc-400" size={32} />}
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-1.5">
                  {form.status === 'confirmed' ? 'Presença confirmada com sucesso 😊' : 'Resposta registrada'}
                </h3>
                <p className="text-sm text-zinc-500 leading-relaxed">
                  {form.status === 'confirmed' 
                    ? `Obrigado por confirmar! ${confirmedCount > 0 ? `${confirmedCount} ${confirmedCount === 1 ? 'pessoa confirmada' : 'pessoas confirmadas'}. ` : ''}Em breve você receberá seu QR Code de acesso.`
                    : 'Que pena que você não poderá participar 😔. Agradecemos pelo retorno.'
                  }
                </p>
                {form.status === 'confirmed' && (
                  <div className="mt-5 p-3 bg-zinc-50 rounded-xl border border-zinc-200/60">
                    <div className="flex items-center justify-center gap-2 text-zinc-500">
                      <QrCode size={16} />
                      <p className="text-xs font-medium">QR Code será enviado pelo organizador</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Formulário de Confirmação */
              <div className="space-y-4">
                {usePerParticipant ? (
                  <div data-testid="rsvp-participants">
                    <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider block mb-2">Quem vai comparecer?</label>
                    <div className="space-y-2">
                      {participants.map((p, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => toggleParticipant(idx)}
                          data-testid={`rsvp-participant-${idx}`}
                          className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-all active:scale-[0.99] ${
                            p.going ? 'border-emerald-300 bg-emerald-50/60' : 'border-zinc-200 bg-white'
                          }`}
                        >
                          <span className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border ${
                            p.going ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-zinc-300'
                          }`}>
                            {p.going && <Check size={13} className="text-white" />}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-zinc-900 truncate">{p.name}</span>
                            <span className="block text-[11px] text-zinc-400">
                              {p.type === 'adult' ? 'Adulto' : p.type === 'child' ? 'Criança' : 'Bebê'}
                              {(p.type === 'child' || p.type === 'baby') && (p.age || p.age === 0) ? ` · ${p.age} ${Number(p.age) === 1 ? 'ano' : 'anos'}` : ''}
                            </span>
                          </span>
                          <span className={`text-[11px] font-medium ${p.going ? 'text-emerald-600' : 'text-zinc-400'}`}>
                            {p.going ? 'Vai' : 'Não vai'}
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-2">
                      {participants.filter(p => p.going).length} de {participants.length} confirmado(s). Toque para alternar.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider block mb-1.5">Adultos</label>
                      <div className="flex items-center border border-zinc-200 rounded-lg overflow-hidden">
                        <button type="button" onClick={() => setForm(prev => ({ ...prev, adults_count: Math.max(1, prev.adults_count - 1) }))} className="px-3 py-2.5 bg-zinc-50 hover:bg-zinc-100 transition-colors"><Minus size={15} className="text-zinc-500" /></button>
                        <span className="flex-1 text-center text-lg font-semibold text-zinc-900">{form.adults_count}</span>
                        <button type="button" onClick={() => setForm(prev => ({ ...prev, adults_count: prev.adults_count + 1 }))} className="px-3 py-2.5 bg-zinc-50 hover:bg-zinc-100 transition-colors"><Plus size={15} className="text-zinc-500" /></button>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider block mb-1.5">Crianças</label>
                      <div className="flex items-center border border-zinc-200 rounded-lg overflow-hidden">
                        <button type="button" onClick={() => setForm(prev => ({ ...prev, children_count: Math.max(0, prev.children_count - 1) }))} className="px-3 py-2.5 bg-zinc-50 hover:bg-zinc-100 transition-colors"><Minus size={15} className="text-zinc-500" /></button>
                        <span className="flex-1 text-center text-lg font-semibold text-zinc-900">{form.children_count}</span>
                        <button type="button" onClick={() => setForm(prev => ({ ...prev, children_count: prev.children_count + 1 }))} className="px-3 py-2.5 bg-zinc-50 hover:bg-zinc-100 transition-colors"><Plus size={15} className="text-zinc-500" /></button>
                      </div>
                    </div>
                  </div>
                )}

                <input type="text" value={form.dietary_restrictions} onChange={e => setForm(prev => ({ ...prev, dietary_restrictions: e.target.value }))} placeholder="Restrições alimentares (opcional)"
                  className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300 transition-all" />

                <textarea value={form.message} onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))} placeholder="Mensagem para o organizador (opcional)" rows={2}
                  className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300 transition-all resize-none" />

                <div className="space-y-2 pt-2">
                  <button onClick={() => handleSubmit('confirmed')} disabled={submitting}
                    className="w-full py-3 bg-brand-deep text-white rounded-xl font-medium text-sm hover:bg-brand transition-all disabled:opacity-50 flex items-center justify-center gap-2 active:scale-[0.98]" data-testid="rsvp-confirm-btn">
                    {submitting ? <><Loader2 className="animate-spin" size={16} /> Confirmando...</> : <><Check size={16} /> {usePerParticipant ? 'Confirmar presença' : 'Confirmar presença'}</>}
                  </button>
                  <button onClick={() => handleSubmit('declined')} disabled={submitting}
                    className="w-full py-2.5 bg-zinc-100 text-zinc-500 rounded-xl text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50" data-testid="rsvp-decline-btn">
                    {usePerParticipant ? 'Ninguém poderá ir' : 'Não poderei ir'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-zinc-400 mt-5">Powered by liventy</p>
      </div>
    </div>
  );
}

// Upgrade Success — handles Stripe redirect with session_id polling
function UpgradeSuccessPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | pending | failed
  const [planName, setPlanName] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 8;
    const POLL_INTERVAL = 2000;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        const ps = res.data?.payment_status;
        const sStatus = res.data?.status;
        const activated = res.data?.subscription_activated;
        const trial = res.data?.trial_active;
        if (res.data?.plan_key) setPlanName(res.data.plan_key);
        if (activated || ps === "paid" || ps === "no_payment_required") {
          setStatus("paid");
          await refreshUser();
          toast.success(trial ? "Trial de 7 dias ativado!" : "Assinatura ativada com sucesso!");
          return;
        }
        if (sStatus === "expired" || ps === "failed") {
          setStatus("failed");
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        if (cancelled) return;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-white">
      <div className="max-w-md w-full text-center" data-testid="upgrade-success-page" style={{ fontFamily: "'Poppins', sans-serif" }}>
        {status === "checking" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-zinc-100 flex items-center justify-center mb-5">
              <Loader2 className="animate-spin text-zinc-600" size={26} />
            </div>
            <h1 className="text-xl font-semibold text-zinc-900 tracking-tight">Confirmando seu pagamento</h1>
            <p className="text-sm text-zinc-500 mt-2">Estamos validando sua assinatura com a Stripe — isso leva alguns segundos.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-5">
              <Check className="text-emerald-600" size={26} strokeWidth={2.5} />
            </div>
            <h1 className="text-xl font-semibold text-zinc-900 tracking-tight">Tudo pronto!</h1>
            <p className="text-sm text-zinc-500 mt-2">Seu acesso Premium está ativo. Aproveite os 7 dias gratuitos — você só será cobrado depois desse período.</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-6 inline-flex h-11 px-6 items-center rounded-xl bg-brand-deep text-white text-sm font-medium hover:bg-brand transition-colors"
              data-testid="upgrade-success-continue-btn"
            >
              Continuar para o painel
            </button>
          </>
        )}
        {status === "pending" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-amber-50 flex items-center justify-center mb-5">
              <Loader2 className="text-amber-600 animate-spin" size={26} />
            </div>
            <h1 className="text-xl font-semibold text-zinc-900 tracking-tight">Pagamento em processamento</h1>
            <p className="text-sm text-zinc-500 mt-2">Isso pode levar alguns minutos. Você receberá um email assim que a Stripe confirmar.</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-6 inline-flex h-11 px-6 items-center rounded-xl bg-zinc-100 text-zinc-700 text-sm font-medium hover:bg-zinc-200 transition-colors"
            >
              Voltar ao painel
            </button>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-5">
              <X className="text-red-600" size={26} />
            </div>
            <h1 className="text-xl font-semibold text-zinc-900 tracking-tight">Não foi possível confirmar o pagamento</h1>
            <p className="text-sm text-zinc-500 mt-2">Sua sessão expirou ou foi cancelada. Você pode tentar novamente quando quiser.</p>
            <button
              onClick={() => navigate("/upgrade")}
              className="mt-6 inline-flex h-11 px-6 items-center rounded-xl bg-brand-deep text-white text-sm font-medium hover:bg-brand transition-colors"
              data-testid="upgrade-failed-retry-btn"
            >
              Tentar novamente
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Upgrade Page — comparação completa Premium vs Pro
function UpgradePage() {
  const { user } = useAuth();
  const access = useAccessStatus();
  const capabilities = useCapabilities();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");

  const trialEnded = !access.loading && !access.hasAccess && !access.hasSubscription;
  const hasSub = !!capabilities.has_active_subscription;
  const currentTier = capabilities.plan_tier;

  const PLANS_UI = [
    {
      tier: "essencial",
      name: "Essencial",
      tagline: "Para organizar o seu evento com o essencial",
      monthly: 39.90,
      yearly: 430.92,
      monthly_equiv_yearly: 35.91,
      saveYearly: 47.88,
      popular: false,
      dark: false,
      features: [
        "1 evento ativo por vez",
        "Convidados ilimitados",
        "Fornecedores, Despesas e Fluxo de caixa",
        "Tarefas e agenda",
        "Confirmação RSVP",
        "Suporte por e-mail",
      ],
    },
    {
      tier: "plus",
      name: "Plus",
      tagline: "Tudo liberado — profissionais e múltiplos eventos",
      monthly: 99.90,
      yearly: 1078.92,
      monthly_equiv_yearly: 89.91,
      saveYearly: 119.88,
      popular: true,
      dark: true,
      features: [
        "Até 12 eventos ativos",
        "Minhas Receitas (financeiro completo)",
        "Meus Eventos (multi-evento)",
        "Inspirações (galeria colaborativa)",
        "Mesas & Assentos + Mapa do evento",
        "Check-in com QR Code em tempo real",
        "Envio WhatsApp + Assistente IA",
        "Suporte prioritário",
      ],
    },
  ];

  const handleSubscribe = async (tier, cycle) => {
    if (loading) return;
    const planKey = `${tier}_${cycle === "annual" ? "yearly" : "monthly"}`;
    setLoading(true);
    setLoadingKey(`${tier}_${cycle}`);
    try {
      if (hasSub) {
        // Option A — upgrade/downgrade na MESMA assinatura Stripe (proration)
        const res = await axios.post(`${API}/payments/change-plan`, { plan: planKey });
        if (res.data?.updated) {
          toast.success(res.data?.no_change ? "Este já é o seu plano atual." : "Plano atualizado com sucesso! 🎉");
          await new Promise((r) => setTimeout(r, 900));
          window.location.href = "/dashboard";
          return;
        }
        throw new Error("Não foi possível alterar o plano");
      }
      const res = await axios.post(`${API}/payments/checkout/session`, {
        plan: planKey,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        redirectToCheckout(res.data.url);
        return;
      }
      throw new Error("URL de pagamento não recebida");
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao processar"));
      setLoading(false);
      setLoadingKey(null);
    }
  };

  const fmt = (v) => `R$ ${Number(v).toFixed(2).replace(".", ",")}`;

  return (
    <div className="space-y-10 max-w-6xl mx-auto" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div className="text-center">
        {trialEnded ? (
          <>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11.5px] font-semibold text-white mb-4"
              style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)" }}>
              <Lock size={12} /> Período gratuito encerrado
            </div>
            <h1 className="text-[34px] sm:text-[44px] font-semibold tracking-tight text-zinc-900 leading-tight">
              Seu período premium terminou
            </h1>
            <p className="text-[14.5px] text-zinc-500 mt-3 max-w-lg mx-auto">
              Assine para continuar utilizando todos os recursos da Liventy — eventos ilimitados, automações WhatsApp, check-in, financeiro e muito mais.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-medium text-zinc-600 border border-zinc-200 hover:bg-zinc-50 transition-colors"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft size={14} /> Voltar ao painel
            </button>
          </>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 text-white text-[11px] font-medium mb-4">
              <Sparkles size={12} /> 7 dias grátis · Cancele quando quiser
            </div>
            <h1 className="text-[34px] sm:text-[40px] font-semibold tracking-tight text-zinc-900">Escolha seu plano</h1>
            <p className="text-[14px] text-zinc-500 mt-2 max-w-xl mx-auto">
              Sem limites artificiais. Pagamento seguro via Stripe. Comece sem cartão — só pedimos o pagamento se decidir continuar após o teste.
            </p>
          </>
        )}
      </div>

      {/* Toggle Mensal / Anual */}
      <div className="flex justify-center">
        <div className="inline-flex bg-zinc-100 rounded-full p-1 ring-1 ring-zinc-200/60">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all ${
              billingCycle === "monthly" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-900"
            }`}
            data-testid="billing-monthly"
          >
            Mensal
          </button>
          <button
            onClick={() => setBillingCycle("annual")}
            className={`px-5 py-2 rounded-full text-[13px] font-medium transition-all flex items-center gap-2 ${
              billingCycle === "annual" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-900"
            }`}
            data-testid="billing-annual"
          >
            Anual
          </button>
        </div>
      </div>

      {/* Plans grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {PLANS_UI.map((p) => {
          const price = billingCycle === "annual" ? p.yearly : p.monthly;
          const priceLabel = billingCycle === "annual" ? "/ano" : "/mês";
          const monthlyHint = billingCycle === "annual" ? p.monthly_equiv_yearly : null;
          const saveHint = billingCycle === "annual" ? p.saveYearly : null;
          const isLoading = loadingKey === `${p.tier}_${billingCycle}`;
          const bg = p.dark ? "bg-zinc-950 text-white border-zinc-800" : "bg-white text-zinc-900 border-zinc-200";

          return (
            <div
              key={p.tier}
              className={`relative rounded-3xl border ${bg} p-7 sm:p-8 transition-all hover:scale-[1.01]`}
              style={p.dark ? { boxShadow: "0 30px 80px -30px rgba(0,0,0,0.55)" } : { boxShadow: "0 12px 36px -16px rgba(0,0,0,0.08)" }}
              data-testid={`plan-card-${p.tier}`}
            >
              {p.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-semibold tracking-wider uppercase"
                  style={{ backgroundImage: "linear-gradient(135deg, #FFB347, #FF4D8D)", color: "#fff" }}>
                  Mais popular
                </div>
              )}
              <div className="mb-5">
                <p className={`text-[11px] uppercase tracking-[0.18em] font-medium ${p.dark ? "text-zinc-400" : "text-zinc-400"}`}>
                  {p.tier === "plus" ? "Plus" : "Essencial"}
                </p>
                <h3 className="text-[28px] font-semibold tracking-tight mt-1">{p.name}</h3>
                <p className={`text-[13px] mt-1.5 ${p.dark ? "text-zinc-400" : "text-zinc-500"}`}>{p.tagline}</p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[40px] font-semibold tracking-tight">{fmt(price)}</span>
                  <span className={`text-[14px] ${p.dark ? "text-zinc-400" : "text-zinc-500"}`}>{priceLabel}</span>
                </div>
                {monthlyHint && (
                  <p className={`text-[12.5px] mt-1.5 ${p.dark ? "text-emerald-300" : "text-emerald-600"} font-medium`}>
                    ≈ {fmt(monthlyHint)}/mês{saveHint > 0 ? ` · economize ${fmt(saveHint)}` : ' · cobrança única anual'}
                  </p>
                )}
              </div>

              <button
                onClick={() => handleSubscribe(p.tier, billingCycle)}
                disabled={loading || (hasSub && currentTier === p.tier)}
                className={`w-full py-3.5 rounded-xl text-[14px] font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${
                  p.dark
                    ? "bg-white text-zinc-900 hover:bg-zinc-100"
                    : "text-white"
                }`}
                style={p.dark ? {} : { backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 55%, #FFB347 100%)", boxShadow: "0 10px 30px -10px rgba(107,61,246,0.55)" }}
                data-testid={`subscribe-${p.tier}-${billingCycle}-btn`}
              >
                {isLoading ? <Loader2 className="animate-spin" size={16} /> : <Crown size={16} />}
                <span>
                  {hasSub && currentTier === p.tier
                    ? "Plano atual"
                    : hasSub
                      ? `Mudar para ${p.name}`
                      : `Quero o ${p.name}`}
                </span>
              </button>

              <ul className="mt-6 space-y-2.5">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[13px]">
                    <Check size={15} className={`mt-0.5 flex-shrink-0 ${p.dark ? "text-emerald-400" : "text-emerald-600"}`} />
                    <span className={p.dark ? "text-zinc-200" : "text-zinc-700"}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Trust strip */}
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[11.5px] text-zinc-500">
        <div className="inline-flex items-center gap-1.5"><Shield size={14} /> Pagamento seguro Stripe</div>
        <div className="inline-flex items-center gap-1.5"><Lock size={14} /> Checkout criptografado</div>
        <div className="inline-flex items-center gap-1.5"><Check size={14} /> Cancele quando quiser</div>
        <div className="inline-flex items-center gap-1.5"><Sparkles size={14} /> 7 dias grátis sem cartão cobrado</div>
      </div>
    </div>
  );
}

// Dashboard Layout
function DashboardLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const { wedding, weddings } = useWedding();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { startTour, tourCompleted, initializeTourStatus } = useTour();
  const access = useAccessStatus();

  // Permite que componentes filhos (ex.: MobileDashboard hero) abram o drawer "Mais"
  useEffect(() => {
    const handler = () => setMoreOpen(true);
    window.addEventListener("liventy:open-mobile-menu", handler);
    return () => window.removeEventListener("liventy:open-mobile-menu", handler);
  }, []);

  // Inicializar status do tour baseado no usuário
  useEffect(() => {
    if (user) {
      initializeTourStatus(user.tour_completed);
    }
  }, [user, initializeTourStatus]);

  useEffect(() => {
    // Redirecionar para onboarding APENAS se onboarding não completado
    if (user && user.onboarding_step && user.onboarding_step !== "completed") {
      navigate('/onboarding');
    }
  }, [navigate, user]);
  
  // Mostrar tour no primeiro acesso
  useEffect(() => {
    if (wedding && !tourCompleted && user) {
      const hasSeenTour = localStorage.getItem('nosly_tour_completed') || localStorage.getItem('nosly_tour_skipped') || user.tour_completed;
      if (!hasSeenTour) {
        const timer = setTimeout(() => startTour(), 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [wedding, tourCompleted, startTour, user]);

  // ─── Trial expired: banner não-bloqueante + redirect só em 402 ───
  // Sem overlay full-screen. O paywall agora é a página /upgrade dedicada.
  // O backend retorna 402 em ações premium → interceptor axios redireciona para /upgrade.
  const accessExpired = !access.loading && !access.hasAccess;

  // PERFORMANCE: Renderizar layout IMEDIATAMENTE, mesmo sem dados
  // O conteúdo interno (children) mostrará skeleton se necessário
  return (
    <div className="min-h-screen bg-[#F8F8F8] flex" style={{fontFamily:"'Poppins', sans-serif"}}>
      <MemoizedSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header — escondido no Dashboard (que tem seu próprio hero roxo full-bleed) */}
        {!(location.pathname === '/dashboard' || location.pathname === '/') && (
          <header className="lg:hidden bg-white/85 backdrop-blur-xl border-b border-zinc-200/60 sticky top-0 z-30 pt-safe">
            <div className="px-4 h-12 flex items-center justify-between">
              <button type="button" onClick={() => navigate('/dashboard')} className="flex items-center gap-2 active:scale-95 transition-transform" aria-label="Ir para o Painel" data-testid="mobile-header-brand-home">
                <LiventyLogoMark size={28} />
                <LiventyWordmark height={18} variant="dark" />
              </button>
              <button
                onClick={() => setMoreOpen(true)}
                className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-700 tap-scale"
                data-testid="open-more-header"
                aria-label="Abrir menu"
              >
                <Menu size={17} />
              </button>
            </div>
          </header>
        )}
        {access.trialActive && !access.hasSubscription && (
          <TrialCountdownBanner days={access.trialDaysLeft} expiresAt={access.trialExpiresAt} />
        )}
        {accessExpired && (
          <TrialEndedBanner />
        )}
        <main className="flex-1 px-4 py-4 md:p-6 lg:p-8 pb-safe-nav">
          <Suspense fallback={<PageLoadingFallback />}>
            {children || <Outlet />}
          </Suspense>
        </main>
      </div>
      <MobileMoreDrawer open={moreOpen} onClose={() => setMoreOpen(false)} />
      <MobileBottomNav onOpenMore={() => setMoreOpen(true)} />
      {/* AISupportAssistant FAB removido — IA agora é contextual (dentro do card Insights) */}
    </div>
  );
}

// Banner não-bloqueante quando o trial expirou (mantém usuário com controle total)
function TrialEndedBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  // Não mostra na própria página de upgrade (evita duplicação visual)
  if (location.pathname.startsWith("/upgrade")) return null;
  return (
    <div
      className="px-4 py-2.5 flex items-center justify-between gap-3 text-[12.5px] border-b"
      style={{ backgroundImage: "linear-gradient(90deg, rgba(255,77,141,0.14), rgba(255,179,71,0.14))", borderColor: "rgba(255,77,141,0.30)" }}
      data-testid="trial-ended-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Lock size={14} className="text-rose-600 flex-shrink-0" />
        <span className="truncate text-zinc-900 font-medium">
          Seu período premium gratuito acabou
        </span>
      </div>
      <button
        onClick={() => navigate("/upgrade")}
        className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11.5px] font-semibold text-white"
        style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)" }}
        data-testid="trial-ended-cta"
      >
        Assinar agora
      </button>
    </div>
  );
}

// Banner discreto com contador regressivo do trial
function TrialCountdownBanner({ days, expiresAt }) {
  const navigate = useNavigate();
  const isUrgent = days <= 2;
  return (
    <div
      className="px-4 py-2 flex items-center justify-between gap-3 text-[12.5px] border-b"
      style={
        isUrgent
          ? { backgroundImage: "linear-gradient(90deg, rgba(255,77,141,0.12), rgba(255,179,71,0.12))", borderColor: "rgba(255,77,141,0.25)" }
          : { backgroundImage: "linear-gradient(90deg, rgba(107,61,246,0.08), rgba(255,77,141,0.06))", borderColor: "rgba(107,61,246,0.18)" }
      }
      data-testid="trial-countdown-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Sparkles size={14} className={isUrgent ? "text-rose-600" : "text-violet-600"} />
        <span className="truncate text-zinc-800">
          Você está no período premium gratuito · <strong>{days === 1 ? "1 dia restante" : `${days} dias restantes`}</strong>
        </span>
      </div>
      <button
        onClick={() => navigate("/upgrade")}
        className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11.5px] font-semibold text-white"
        style={{ backgroundImage: "linear-gradient(135deg, #6B3DF6 0%, #FF4D8D 100%)" }}
        data-testid="trial-cta-upgrade"
      >
        Assinar
      </button>
    </div>
  );
}

// Protected Route
function ProtectedRoute({ children }) {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return <React.Fragment>{children}</React.Fragment>;
}

// Premium Toaster — responsivo (top no mobile, top-right no desktop), swipe-to-dismiss nativo, glassmorphism sutil
function PremiumToaster() {
  const isMobile = useIsMobile();
  return (
    <Toaster
      position={isMobile ? "top-center" : "top-right"}
      richColors
      closeButton
      duration={3500}
      visibleToasts={4}
      swipeDirections={isMobile ? ["top", "right", "left"] : ["right"]}
      offset={isMobile ? 12 : 24}
      toastOptions={{
        style: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: isMobile ? '14px' : '13.5px',
          borderRadius: '14px',
          padding: '12px 14px',
          boxShadow: '0 10px 40px -12px rgba(24,24,27,0.18), 0 0 0 1px rgba(24,24,27,0.06)',
          backdropFilter: 'saturate(180%) blur(8px)',
          WebkitBackdropFilter: 'saturate(180%) blur(8px)',
        },
        className: 'liventy-toast',
      }}
    />
  );
}

// Main App


function VipAccessAdminLayout() {
  // Wrapper minimalista: usa AuthContext pra checar admin email; se não for, mostra "Acesso restrito".
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="text-sm text-zinc-500">Carregando…</div></div>;
  }
  const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "adm.liventy@gmail.com").split(",").map(e => e.trim().toLowerCase());
  const isAdmin = user && ADMIN_EMAILS.includes((user.email || "").toLowerCase());
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <div className="max-w-md bg-white rounded-2xl border border-rose-100 p-8 text-center shadow-sm">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-50 flex items-center justify-center mb-4">
            <Shield size={24} className="text-rose-500" />
          </div>
          <h2 className="text-[18px] font-semibold text-zinc-900">Acesso restrito</h2>
          <p className="text-[13px] text-zinc-500 mt-1">Esta área é exclusiva para administradores.</p>
          <button onClick={() => navigate("/dashboard")} className="mt-5 px-4 h-10 rounded-lg bg-zinc-900 text-white text-[13px] font-medium hover:bg-zinc-800">Voltar ao painel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="px-5 sm:px-8 py-4 bg-white border-b border-zinc-200 flex items-center gap-3">
        <button onClick={() => navigate("/admin")} className="flex items-center gap-1.5 text-[12.5px] text-zinc-600 hover:text-zinc-900 font-medium" data-testid="vip-back-to-admin">
          <ArrowLeft size={14} />
          <span>Admin</span>
        </button>
        <span className="text-zinc-300">·</span>
        <span className="text-[12.5px] text-zinc-900 font-semibold">Convites VIP</span>
      </header>
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-6">
        <VipAccessAdminPage />
      </main>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// EmailLogsAdminLayout — wrapper protegido pra /admin/email-logs
// ════════════════════════════════════════════════════════════════════════
function EmailLogsAdminLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="text-sm text-zinc-500">Carregando…</div></div>;
  }
  const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "adm.liventy@gmail.com").split(",").map(e => e.trim().toLowerCase());
  const isAdmin = user && ADMIN_EMAILS.includes((user.email || "").toLowerCase());
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <div className="max-w-md bg-white rounded-2xl border border-rose-100 p-8 text-center shadow-sm">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-50 flex items-center justify-center mb-4">
            <Shield size={24} className="text-rose-500" />
          </div>
          <h2 className="text-[18px] font-semibold text-zinc-900">Acesso restrito</h2>
          <p className="text-[13px] text-zinc-500 mt-1">Esta área é exclusiva para administradores.</p>
          <button onClick={() => navigate("/dashboard")} className="mt-5 px-4 h-10 rounded-lg bg-zinc-900 text-white text-[13px] font-medium hover:bg-zinc-800">Voltar ao painel</button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="px-5 sm:px-8 py-4 bg-white border-b border-zinc-200 flex items-center gap-3">
        <button onClick={() => navigate("/admin")} className="flex items-center gap-1.5 text-[12.5px] text-zinc-600 hover:text-zinc-900 font-medium" data-testid="email-back-to-admin">
          <ArrowLeft size={14} />
          <span>Admin</span>
        </button>
        <span className="text-zinc-300">·</span>
        <span className="text-[12.5px] text-zinc-900 font-semibold">Logs de Email</span>
      </header>
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-6">
        <EmailLogsPage />
      </main>
    </div>
  );
}

// =====================================================================
// MINHAS RECEITAS v2 (Plano Pro) — gráfico misto, parcelamento,
// pagamentos parciais, formas de pagamento, filtros e recebimentos rápidos.
// =====================================================================

const PAYMENT_METHOD_OPTIONS = [
  { value: "pix",           label: "Pix" },
  { value: "dinheiro",      label: "Dinheiro" },
  { value: "cartao",        label: "Cartão" },
  { value: "transferencia", label: "Transferência" },
  { value: "boleto",        label: "Boleto" },
  { value: "outro",         label: "Outro" },
];

const PAYMENT_METHOD_LABEL = Object.fromEntries(PAYMENT_METHOD_OPTIONS.map(o => [o.value, o.label]));

const STATUS_META = {
  paid:    { label: "Recebido",   bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  partial: { label: "Parcial",    bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-500" },
  overdue: { label: "Atrasado",   bg: "bg-red-50",     text: "text-red-700",     dot: "bg-red-500" },
  pending: { label: "Pendente",   bg: "bg-zinc-100",   text: "text-zinc-700",    dot: "bg-zinc-400" },
};

function fmtBRL_revenue(n) {
  return (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function fmtBRLShort_revenue(n) {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000) return `R$ ${(v / 1000).toFixed(1)}k`;
  return `R$ ${v.toFixed(0)}`;
}
function fmtDate_revenue(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length <= 10 ? iso + "T12:00:00" : iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
function monthLabel_revenue(ym) {
  if (!ym || ym.length < 7) return ym || "";
  const [y, m] = ym.split("-");
  const names = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${names[parseInt(m, 10) - 1] || ""}/${y.slice(2)}`;
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${meta.bg} ${meta.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

// DateField — seletor de data premium estilo Apple iOS (popover com grid mensal).
// Substitui o <input type="date"> nativo (feio) nos formulários de Receitas.
function DateField({ label, value, onChange, placeholder = "Selecionar data", testId, minToday = false }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const monthNames = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const dayNames = ["D","S","T","Q","Q","S","S"];
  const parse = (iso) => {
    if (!iso || iso.length < 10) return null;
    const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };
  const selected = parse(value);
  const [cursor, setCursor] = useState(() => {
    const s = parse(value) || new Date();
    return { year: s.getFullYear(), month: s.getMonth() };
  });
  useEffect(() => {
    if (open) {
      const s = parse(value) || new Date();
      setCursor({ year: s.getFullYear(), month: s.getMonth() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const firstDow = new Date(cursor.year, cursor.month, 1).getDay();
  const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const toISO = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const fmt = selected ? selected.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }) : "";

  return (
    <div ref={wrapRef} className="relative">
      {label && <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl border bg-white text-left text-[14px] transition-all duration-200 ${open ? "border-violet-400 ring-4 ring-violet-500/10" : "border-zinc-200 hover:border-zinc-300"}`}
      >
        <span className={fmt ? "text-zinc-900" : "text-zinc-400"}>{fmt || placeholder}</span>
        <Calendar size={16} className="text-zinc-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-[10060] mt-2 left-0 w-[300px] max-w-[calc(100vw-3rem)] rounded-2xl bg-white shadow-[0_20px_50px_-12px_rgba(24,24,27,0.28)] ring-1 ring-zinc-200/70 p-3 animate-slide-up" data-testid={testId ? `${testId}-popover` : undefined}>
          <div className="flex items-center justify-between mb-3 px-1">
            <div>
              <p className="text-[16px] font-bold text-zinc-900 leading-none">{monthNames[cursor.month]}</p>
              <p className="text-[12px] text-zinc-400 mt-1">{cursor.year}</p>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setCursor((c) => c.month === 0 ? { year: c.year - 1, month: 11 } : { ...c, month: c.month - 1 })} className="w-8 h-8 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-500 active:scale-95 transition-colors"><ChevronLeft size={16} /></button>
              <button type="button" onClick={() => setCursor((c) => c.month === 11 ? { year: c.year + 1, month: 0 } : { ...c, month: c.month + 1 })} className="w-8 h-8 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-500 active:scale-95 transition-colors"><ChevronRight size={16} /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {dayNames.map((d, i) => <div key={i} className="text-center text-[10px] font-semibold uppercase tracking-wider text-zinc-300 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (d === null) return <div key={i} className="h-9" />;
              const cellDate = new Date(cursor.year, cursor.month, d); cellDate.setHours(0, 0, 0, 0);
              const isToday = cellDate.getTime() === today.getTime();
              const isSel = selected && selected.getFullYear() === cursor.year && selected.getMonth() === cursor.month && selected.getDate() === d;
              const disabled = minToday && cellDate < today;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabled}
                  onClick={() => { onChange(toISO(cursor.year, cursor.month, d)); setOpen(false); }}
                  className={`h-9 rounded-full text-[13px] font-medium tabular-nums flex items-center justify-center transition-all duration-150 ${
                    isSel ? "bg-gradient-to-br from-violet-500 to-pink-500 text-white shadow-[0_2px_8px_-2px_rgba(139,92,246,0.55)]"
                    : isToday ? "ring-1 ring-violet-300 text-violet-700"
                    : disabled ? "text-zinc-300 cursor-not-allowed"
                    : "text-zinc-700 hover:bg-zinc-100"
                  }`}
                >
                  {d}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-2 pt-2.5 border-t border-zinc-100 px-1">
            <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="text-[12px] font-medium text-zinc-400 hover:text-zinc-600 transition-colors">Limpar</button>
            <button type="button" onClick={() => { const t = new Date(); onChange(toISO(t.getFullYear(), t.getMonth(), t.getDate())); setOpen(false); }} className="text-[12px] font-semibold text-violet-600 hover:text-violet-700 transition-colors">Hoje</button>
          </div>
        </div>
      )}
    </div>
  );
}

// EditRevenueForm — editar dados + PARCELAMENTO e datas de vencimento (PUT /revenues/{id}).
function EditRevenueForm({ open, onClose, revenue, onSave, saving }) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const [form, setForm] = useState({ client_name: "", event_name: "", event_date: "", total_amount: 0, notes: "" });
  const [installments, setInstallments] = useState([]); // {id?, amount, due_date, paid}
  useEffect(() => {
    if (open && revenue) {
      setForm({
        client_name: revenue.client_name || "",
        event_name: revenue.event_name || "",
        event_date: (revenue.event_date || "").slice(0, 10),
        total_amount: revenue.total_amount || 0,
        notes: revenue.notes || "",
      });
      setInstallments((revenue.installments || []).map((i) => ({
        id: i.id,
        amount: Number(i.amount) || 0,
        due_date: (i.due_date || "").slice(0, 10) || (revenue.event_date || todayIso).slice(0, 10),
        paid: Number(i.total_paid) || 0,
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, revenue]);
  if (!revenue) return null;

  const hasInstallments = installments.length > 0;
  const sumInstallments = round2(installments.reduce((s, i) => s + (Number(i.amount) || 0), 0));
  const totalToSend = hasInstallments ? sumInstallments : Number(form.total_amount);

  const updateInstallment = (idx, key, val) => {
    setInstallments((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  };
  const addInstallment = () => {
    const last = installments[installments.length - 1];
    setInstallments((prev) => [...prev, { amount: 0, due_date: last?.due_date || form.event_date || todayIso, paid: 0 }]);
  };
  const removeInstallment = (idx) => {
    const it = installments[idx];
    if (it.paid > 0) { toast.error("Não é possível remover uma parcela que já possui recebimentos."); return; }
    setInstallments((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) { toast.error("Informe o nome do cliente"); return; }
    if (!totalToSend || totalToSend <= 0) { toast.error("Informe o valor total cobrado"); return; }
    for (let i = 0; i < installments.length; i++) {
      const it = installments[i];
      if (!it.amount || it.amount <= 0) { toast.error(`Parcela ${i + 1}: informe um valor maior que zero`); return; }
      if (it.amount + 0.01 < (it.paid || 0)) { toast.error(`Parcela ${i + 1}: o valor é menor que o já recebido (${fmtBRL_revenue(it.paid)})`); return; }
      if (!it.due_date) { toast.error(`Parcela ${i + 1}: informe a data de vencimento`); return; }
    }
    onSave({
      client_name: form.client_name.trim(),
      event_name: form.event_name.trim(),
      event_date: form.event_date,
      total_amount: round2(totalToSend),
      notes: form.notes.trim(),
      installments: installments.map((i) => ({ id: i.id, amount: round2(i.amount), due_date: i.due_date })),
    });
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Editar receita" fullScreen>
      <form onSubmit={submit} className="space-y-4" data-testid="revenue-edit-form">
        <Input
          label="Nome do cliente *"
          value={form.client_name}
          onChange={(e) => setForm({ ...form, client_name: e.target.value })}
          placeholder="Ex: Família Silva"
          required
          data-testid="edit-revenue-client"
        />
        <Input
          label="Nome do evento"
          value={form.event_name}
          onChange={(e) => setForm({ ...form, event_name: e.target.value })}
          placeholder="Ex: Casamento Maria & João"
        />
        <DateField
          label="Data do evento"
          value={form.event_date}
          onChange={(iso) => setForm({ ...form, event_date: iso })}
          testId="edit-revenue-date"
        />

        {hasInstallments ? (
          <div className="rounded-2xl border border-zinc-200/70 bg-zinc-50/40 p-3.5" data-testid="edit-installments-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[13px] font-semibold text-zinc-900">Parcelamento</p>
                <p className="text-[11px] text-zinc-400">{installments.length} parcela(s) · total {fmtBRL_revenue(sumInstallments)}</p>
              </div>
              <button type="button" onClick={addInstallment} className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-[12px] font-semibold transition-colors active:scale-95" data-testid="edit-add-installment">
                <Plus size={14} /> Parcela
              </button>
            </div>
            <div className="space-y-2.5">
              {installments.map((it, i) => (
                <div key={i} className="bg-white rounded-xl border border-zinc-200/70 p-2.5" data-testid={`edit-installment-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-zinc-100 text-zinc-500 text-[11px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <MoneyInput value={it.amount || 0} onChange={(v) => updateInstallment(i, "amount", v)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <DateField value={it.due_date} onChange={(iso) => updateInstallment(i, "due_date", iso)} testId={`edit-installment-date-${i}`} />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeInstallment(i)}
                      disabled={it.paid > 0}
                      title={it.paid > 0 ? "Parcela com recebimento — não removível" : "Remover parcela"}
                      className={`p-2 rounded-lg flex-shrink-0 transition-colors ${it.paid > 0 ? "text-zinc-200 cursor-not-allowed" : "text-zinc-400 hover:text-red-500 hover:bg-red-50"}`}
                      data-testid={`edit-remove-installment-${i}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                  {it.paid > 0 && (
                    <p className="text-[10.5px] font-medium text-emerald-600 mt-1.5 pl-8">Recebido {fmtBRL_revenue(it.paid)} · não pode ser removida</p>
                  )}
                </div>
              ))}
            </div>
            {Math.abs(sumInstallments - (Number(form.total_amount) || sumInstallments)) > 0.05 && (
              <p className="text-[11px] text-amber-600 mt-2.5">O total cobrado foi ajustado para a soma das parcelas ({fmtBRL_revenue(sumInstallments)}).</p>
            )}
          </div>
        ) : (
          <MoneyInput
            label="Valor total cobrado *"
            value={form.total_amount || 0}
            onChange={(v) => setForm({ ...form, total_amount: v })}
          />
        )}

        <div>
          <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">Observações</label>
          <textarea
            className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 focus:border-zinc-400 focus:ring-4 focus:ring-zinc-900/5 outline-none transition-all duration-200 bg-white text-zinc-900 placeholder-zinc-400 text-[14px] min-h-[80px]"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Anotações internas (opcional)"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="w-full px-4 py-3 rounded-xl text-white text-[14px] font-semibold shadow-[0_8px_24px_-8px_rgba(107,61,246,0.5)] disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)" }}
          data-testid="revenue-edit-submit"
        >
          {saving ? "Salvando…" : "Salvar alterações"}
        </button>
      </form>
    </Modal>
  );
}

function RevenueChart({ data }) {
  const chartData = useMemo(() => data.map(d => ({
    ...d,
    monthLabel: monthLabel_revenue(d.month),
  })), [data]);
  const hasAny = chartData.some(d => d.contracted > 0 || d.received > 0);
  return (
    <Card className="p-5 sm:p-6" animate>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-zinc-900 tracking-tight">Evolução financeira</p>
          <p className="text-[12px] text-zinc-500 mt-0.5">Últimos 12 meses · valor contratado vs recebido</p>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-[11px] font-medium">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-violet-300" /> Contratado</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Recebido</span>
        </div>
      </div>
      {!hasAny ? (
        <div className="h-[220px] flex items-center justify-center text-[13px] text-zinc-400" data-testid="revenue-chart-empty">
          Sem dados financeiros nos últimos 12 meses
        </div>
      ) : (
        <div className="h-[260px] -mx-2" data-testid="revenue-chart">
          <Suspense fallback={
            <div className="w-full h-full flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-zinc-300 animate-spin" />
            </div>
          }>
            <LazyRevenueComposedChart
              chartData={chartData}
              fmtBRL={fmtBRL_revenue}
              fmtBRLShort={fmtBRLShort_revenue}
            />
          </Suspense>
        </div>
      )}
    </Card>
  );
}

function QuickPaymentSheet({ open, onClose, installment, onSubmit, saving }) {
  const [amount, setAmount] = useState(0);
  const [method, setMethod] = useState("pix");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open && installment) {
      setAmount(installment.remaining || installment.amount || 0);
      setMethod("pix");
      setDate(new Date().toISOString().slice(0, 10));
      setNotes("");
    }
  }, [open, installment]);

  if (!installment) return null;

  return (
    <Modal isOpen={open} onClose={onClose} title="Registrar recebimento">
      <div className="space-y-4" data-testid="quick-payment-form">
        <div className="p-3 rounded-xl bg-zinc-50 border border-zinc-100">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Parcela {installment.number}</p>
          <p className="text-[15px] font-semibold text-zinc-900 tabular-nums mt-0.5">{fmtBRL_revenue(installment.amount)}</p>
          <p className="text-[11.5px] text-zinc-500 mt-0.5">
            Falta receber: <span className="font-semibold text-amber-700">{fmtBRL_revenue(installment.remaining)}</span>
          </p>
        </div>
        <MoneyInput label="Valor recebido *" value={amount} onChange={setAmount} />
        <Select
          label="Forma de pagamento *"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          options={PAYMENT_METHOD_OPTIONS}
        />
        <DateField
          label="Data do recebimento *"
          value={date}
          onChange={setDate}
          testId="quick-payment-date"
        />
        <Input
          label="Observações (opcional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ex: Pagamento via PIX código..."
        />
        <button
          type="button"
          disabled={saving || !amount || amount <= 0}
          onClick={() => onSubmit({ amount, method, date, notes })}
          className="w-full px-4 py-3 rounded-xl text-white text-[14px] font-semibold shadow-[0_8px_24px_-8px_rgba(107,61,246,0.5)] disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)" }}
          data-testid="quick-payment-submit"
        >
          {saving ? "Registrando…" : `Confirmar recebimento de ${fmtBRL_revenue(amount)}`}
        </button>
      </div>
    </Modal>
  );
}

function NewRevenueForm({ open, onClose, weddings, onCreate, saving }) {
  const today = new Date().toISOString().slice(0, 10);
  // Modes: "existing" (vincular evento), "new" (criar evento + receita), "standalone" (avulsa)
  const [mode, setMode] = useState("existing");
  const [weddingId, setWeddingId] = useState("");
  const [form, setForm] = useState({
    client_name: "",
    event_name: "",
    event_date: today,
    total_amount: 0,
    notes: "",
  });
  // Para modo "new" — campos do evento a ser criado
  const [newEvent, setNewEvent] = useState({
    name: "",
    type: "casamento",
    event_date: today,
    budget: 0,
    guest_count: 0,
  });
  const [hasEntry, setHasEntry] = useState(false);
  const [entry, setEntry] = useState({ amount: 0, method: "pix", date: today });
  const [installmentsCount, setInstallmentsCount] = useState(0);
  const [installments, setInstallments] = useState([]);

  useEffect(() => {
    if (!open) return;
    // Quando abrir: se houver eventos, default "existing"; caso contrário, "new"
    setMode(weddings.length > 0 ? "existing" : "new");
    const first = weddings[0];
    setWeddingId(first?.id || "");
    setForm({
      client_name: "",
      event_name: first?.event_name || first?.partner1_name || "",
      event_date: first?.event_date || today,
      total_amount: 0,
      notes: "",
    });
    setNewEvent({
      name: "",
      type: "casamento",
      event_date: today,
      budget: 0,
      guest_count: 0,
    });
    setHasEntry(false);
    setEntry({ amount: 0, method: "pix", date: today });
    setInstallmentsCount(0);
    setInstallments([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (mode !== "existing") return;
    const w = weddings.find(x => x.id === weddingId);
    if (w) {
      setForm(f => ({
        ...f,
        event_name: f.event_name || w.event_name || w.partner1_name || "",
        event_date: f.event_date || w.event_date || today,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weddingId, mode]);

  useEffect(() => {
    if (installmentsCount <= 0) { setInstallments([]); return; }
    const total = Number(form.total_amount || 0);
    const entryAmt = hasEntry ? Number(entry.amount || 0) : 0;
    const remaining = Math.max(0, total - entryAmt);
    const per = installmentsCount > 0 ? +(remaining / installmentsCount).toFixed(2) : 0;
    const diff = +(remaining - per * installmentsCount).toFixed(2);
    const baseDate = mode === "new" ? newEvent.event_date : (mode === "standalone" ? today : form.event_date);
    const base = new Date(baseDate || today);
    const out = [];
    for (let i = 0; i < installmentsCount; i++) {
      const d = new Date(base);
      d.setMonth(d.getMonth() + i + 1);
      out.push({
        amount: i === installmentsCount - 1 ? +(per + diff).toFixed(2) : per,
        due_date: d.toISOString().slice(0, 10),
      });
    }
    setInstallments(out);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installmentsCount, form.total_amount, hasEntry, entry.amount, newEvent.event_date, form.event_date, mode]);

  const updateInstallment = (i, key, val) => {
    setInstallments(arr => arr.map((it, idx) => idx === i ? { ...it, [key]: key === "amount" ? Number(val) : val } : it));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validações por modo
    if (mode === "existing") {
      if (!weddingId) { toast.error("Selecione um evento"); return; }
    } else if (mode === "new") {
      if (!newEvent.name.trim()) { toast.error("Informe o nome do evento"); return; }
      if (!newEvent.event_date) { toast.error("Informe a data do evento"); return; }
    }
    if (!form.client_name.trim()) { toast.error("Informe o nome do cliente"); return; }
    if (!form.total_amount || form.total_amount <= 0) { toast.error("Informe o valor total cobrado"); return; }
    let payloadInstallments = null;
    if (hasEntry || installments.length > 0) {
      const out = [];
      if (hasEntry && entry.amount > 0) {
        out.push({
          amount: Number(entry.amount),
          due_date: entry.date,
          payments: [{ amount: Number(entry.amount), date: entry.date, method: entry.method, notes: "Entrada" }],
        });
      }
      for (const it of installments) {
        out.push({ amount: Number(it.amount), due_date: it.due_date, payments: [] });
      }
      payloadInstallments = out;
    }
    const basePayload = {
      mode,
      client_name: form.client_name.trim(),
      total_amount: Number(form.total_amount),
      notes: form.notes.trim(),
      installments: payloadInstallments,
    };
    if (mode === "existing") {
      onCreate({
        ...basePayload,
        wedding_id: weddingId,
        event_name: form.event_name.trim(),
        event_date: form.event_date,
      });
    } else if (mode === "new") {
      onCreate({
        ...basePayload,
        event: {
          name: newEvent.name.trim(),
          type: newEvent.type,
          event_date: newEvent.event_date,
          budget: Number(newEvent.budget || 0),
          guest_count: Number(newEvent.guest_count || 0),
          client_name: form.client_name.trim(),
        },
      });
    } else {
      // standalone
      onCreate({
        ...basePayload,
        event: { event_date: today },
      });
    }
  };

  // Componente: aba premium do segmented control
  const TabBtn = ({ value, icon: Icon, title, subtitle, testid }) => {
    const active = mode === value;
    return (
      <button
        type="button"
        onClick={() => setMode(value)}
        className={`flex-1 group relative text-left px-3.5 py-3 rounded-xl transition-all ${active ? "bg-white shadow-[0_4px_14px_-6px_rgba(107,61,246,0.30)] ring-1 ring-violet-200" : "hover:bg-zinc-100/60"}`}
        data-testid={testid}
        aria-pressed={active}
      >
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center mb-1.5 transition-colors ${active ? "bg-violet-100 text-violet-700" : "bg-zinc-100 text-zinc-500"}`}>
          <Icon size={14} strokeWidth={2.2} />
        </div>
        <p className={`text-[12.5px] font-semibold leading-tight ${active ? "text-zinc-900" : "text-zinc-700"}`}>{title}</p>
        <p className="text-[10.5px] text-zinc-500 mt-0.5 leading-tight">{subtitle}</p>
      </button>
    );
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Nova receita" fullScreen>
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="revenue-create-form">
        {/* Segmented Control — 3 fluxos */}
        <div className="bg-zinc-50/60 ring-1 ring-zinc-200/60 rounded-2xl p-1.5 flex gap-1.5" data-testid="revenue-mode-tabs">
          <TabBtn
            value="existing"
            icon={Calendar}
            title="Vincular evento"
            subtitle={weddings.length === 0 ? "Sem eventos ainda" : `${weddings.length} disponível${weddings.length === 1 ? "" : "is"}`}
            testid="revenue-mode-existing"
          />
          <TabBtn
            value="new"
            icon={Plus}
            title="Criar evento"
            subtitle="Evento + receita juntos"
            testid="revenue-mode-new"
          />
          <TabBtn
            value="standalone"
            icon={DollarSign}
            title="Avulsa"
            subtitle="Sem evento"
            testid="revenue-mode-standalone"
          />
        </div>

        {/* Bloco condicional por fluxo */}
        {mode === "existing" && (
          weddings.length === 0 ? (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 text-[13px] text-amber-800">
              Você ainda não tem eventos cadastrados. Use a aba <strong>Criar evento</strong> para criar o evento e a receita em uma única ação.
            </div>
          ) : (
            <div className="space-y-4 p-4 rounded-2xl bg-violet-50/40 ring-1 ring-violet-100/70">
              <div className="flex items-center gap-2">
                <Calendar size={14} className="text-violet-600" />
                <p className="text-[12px] font-semibold text-violet-900">Vincular receita a um evento existente</p>
              </div>
              <Select
                label="Evento *"
                value={weddingId}
                onChange={(e) => setWeddingId(e.target.value)}
                options={weddings.map(w => ({ value: w.id, label: w.event_name || w.partner1_name || "Evento" }))}
              />
              <Input
                label="Nome do evento"
                value={form.event_name}
                onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                placeholder="Ex: Casamento Maria & João"
              />
              <DateField
                label="Data do evento *"
                value={form.event_date}
                onChange={(iso) => setForm({ ...form, event_date: iso })}
                testId="revenue-existing-event-date"
              />
            </div>
          )
        )}

        {mode === "new" && (
          <div className="space-y-4 p-4 rounded-2xl bg-violet-50/40 ring-1 ring-violet-100/70">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-violet-600" />
              <p className="text-[12px] font-semibold text-violet-900">Criar evento + receita em uma única ação</p>
            </div>
            <Input
              label="Nome do evento *"
              value={newEvent.name}
              onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
              placeholder="Ex: Casamento Maria & João"
              required
              data-testid="new-event-name"
            />
            <Select
              label="Tipo *"
              value={newEvent.type}
              onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}
              options={[
                { value: "casamento", label: "Casamento" },
                { value: "aniversario", label: "Aniversário" },
                { value: "corporativo", label: "Corporativo" },
                { value: "formatura", label: "Formatura" },
                { value: "evento", label: "Outro tipo" },
              ]}
            />
            <DateField
              label="Data do evento *"
              value={newEvent.event_date}
              onChange={(iso) => setNewEvent({ ...newEvent, event_date: iso })}
              testId="new-event-date"
            />
            <div className="grid grid-cols-2 gap-3">
              <MoneyInput
                label="Orçamento estimado"
                value={newEvent.budget || 0}
                onChange={(v) => setNewEvent({ ...newEvent, budget: v })}
              />
              <Input
                label="Convidados estimados"
                type="number"
                value={newEvent.guest_count}
                onChange={(e) => setNewEvent({ ...newEvent, guest_count: e.target.value })}
                placeholder="Ex: 120"
              />
            </div>
          </div>
        )}

        {mode === "standalone" && (
          <div className="p-4 rounded-2xl bg-emerald-50/40 ring-1 ring-emerald-100/70 text-[12.5px] text-emerald-900 flex items-start gap-2.5">
            <DollarSign size={16} className="text-emerald-700 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-emerald-900">Entrada financeira sem evento</p>
              <p className="text-[11.5px] text-emerald-800/80 mt-1">Use para consultorias, comissões, royalties e outros recebimentos que não estão vinculados a um evento da agenda.</p>
            </div>
          </div>
        )}

        {/* Bloco comum: cliente + valor */}
        <Input
          label="Nome do cliente *"
          value={form.client_name}
          onChange={(e) => setForm({ ...form, client_name: e.target.value })}
          placeholder="Ex: Família Silva"
          required
          data-testid="revenue-form-client"
        />

        <MoneyInput
          label="Valor total cobrado *"
          value={form.total_amount || 0}
          onChange={(val) => setForm({ ...form, total_amount: val })}
        />

        <div className="rounded-xl border border-zinc-200/70 overflow-hidden">
          <button
            type="button"
            onClick={() => setHasEntry(v => !v)}
            className="w-full px-4 py-3 flex items-center justify-between text-left bg-zinc-50 hover:bg-zinc-100 transition-colors"
            data-testid="toggle-entry"
          >
            <div>
              <p className="text-[13px] font-semibold text-zinc-900">Já recebeu uma entrada?</p>
              <p className="text-[11.5px] text-zinc-500 mt-0.5">{hasEntry ? "Sim — informe abaixo" : "Toque para registrar"}</p>
            </div>
            <span className={`w-9 h-5 rounded-full transition-colors ${hasEntry ? "bg-violet-500" : "bg-zinc-300"} relative`}>
              <span className={`absolute top-0.5 ${hasEntry ? "right-0.5" : "left-0.5"} w-4 h-4 rounded-full bg-white shadow transition-all`} />
            </span>
          </button>
          {hasEntry && (
            <div className="p-4 space-y-3 bg-white">
              <div className="grid grid-cols-2 gap-3">
                <MoneyInput label="Valor da entrada" value={entry.amount || 0} onChange={(v) => setEntry({ ...entry, amount: v })} />
                <Select
                  label="Forma"
                  value={entry.method}
                  onChange={(e) => setEntry({ ...entry, method: e.target.value })}
                  options={PAYMENT_METHOD_OPTIONS}
                />
              </div>
              <DateField label="Data" value={entry.date} onChange={(iso) => setEntry({ ...entry, date: iso })} />
            </div>
          )}
        </div>

        <div className="rounded-xl border border-zinc-200/70 overflow-hidden">
          <div className="px-4 py-3 bg-zinc-50 flex items-center justify-between">
            <div>
              <p className="text-[13px] font-semibold text-zinc-900">Parcelas restantes</p>
              <p className="text-[11.5px] text-zinc-500 mt-0.5">Configure o saldo em parcelas (opcional)</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setInstallmentsCount(Math.max(0, installmentsCount - 1))}
                className="w-8 h-8 rounded-lg bg-white border border-zinc-200 flex items-center justify-center hover:bg-zinc-50"
                data-testid="installments-decrement"
              ><Minus size={14} /></button>
              <span className="w-6 text-center text-[14px] font-semibold tabular-nums" data-testid="installments-count">{installmentsCount}</span>
              <button
                type="button"
                onClick={() => setInstallmentsCount(Math.min(36, installmentsCount + 1))}
                className="w-8 h-8 rounded-lg bg-white border border-zinc-200 flex items-center justify-center hover:bg-zinc-50"
                data-testid="installments-increment"
              ><Plus size={14} /></button>
            </div>
          </div>
          {installments.length > 0 && (
            <div className="p-3 space-y-2 bg-white">
              {installments.map((it, i) => (
                <div key={i} className="grid grid-cols-[40px_1fr_1fr] gap-2 items-center">
                  <span className="text-[12px] font-bold text-zinc-500 tabular-nums">#{i + 1}</span>
                  <MoneyInput value={it.amount || 0} onChange={(v) => updateInstallment(i, "amount", v)} />
                  <DateField value={it.due_date} onChange={(iso) => updateInstallment(i, "due_date", iso)} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-[12px] font-medium text-zinc-700 mb-1.5">Observações</label>
          <textarea
            className="w-full px-3.5 py-2.5 rounded-xl border border-zinc-200 focus:border-zinc-400 focus:ring-4 focus:ring-zinc-900/5 outline-none transition-all duration-200 bg-white text-zinc-900 placeholder-zinc-400 text-[14px] min-h-[80px]"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Anotações internas (opcional)"
          />
        </div>

        <button
          type="submit"
          disabled={saving || (mode === "existing" && weddings.length === 0)}
          className="w-full px-4 py-3 rounded-xl text-white text-[14px] font-semibold shadow-[0_8px_24px_-8px_rgba(107,61,246,0.5)] disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)" }}
          data-testid="revenue-form-submit"
        >
          {saving
            ? "Salvando…"
            : mode === "new"
            ? "Criar evento e receita"
            : mode === "standalone"
            ? "Salvar receita avulsa"
            : "Salvar receita"}
        </button>
      </form>
    </Modal>
  );
}

function RevenueContractRow({ item, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
    if (!isPdf) { toast.error("Envie um arquivo PDF"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Arquivo muito grande. Limite 10MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/weddings/${item.wedding_id}/revenues/${item.id}/contract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Contrato anexado!");
      onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao anexar contrato"));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!(await confirmDialog({
      title: "Remover contrato",
      message: "Deseja remover o contrato anexado a esta receita?",
      confirmLabel: "Remover",
      variant: "danger",
    }))) return;
    try {
      await axios.delete(`${API}/weddings/${item.wedding_id}/revenues/${item.id}/contract`);
      toast.success("Contrato removido");
      onChanged?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Erro ao remover contrato"));
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-zinc-100" data-testid={`revenue-contract-${item.id}`}>
      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`revenue-contract-input-${item.id}`}
      />
      {item.contract_pdf_url ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <a
            href={`${BACKEND_URL}${item.contract_pdf_url}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 min-w-0 text-[12.5px] font-medium text-violet-700 hover:text-violet-900 transition-colors"
            data-testid={`revenue-contract-view-${item.id}`}
          >
            <span className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
              <FileText size={15} className="text-violet-600" />
            </span>
            <span className="truncate max-w-[220px]">{item.contract_filename || "Contrato.pdf"}</span>
            <ChevronRight size={13} className="text-violet-400 flex-shrink-0" />
          </a>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md text-zinc-600 hover:bg-zinc-100 transition-colors disabled:opacity-50"
              data-testid={`revenue-contract-replace-${item.id}`}
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : "Substituir"}
            </button>
            <button
              onClick={remove}
              className="text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md text-red-500 hover:bg-red-50 transition-colors"
              data-testid={`revenue-contract-remove-${item.id}`}
            >
              Remover
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-zinc-300 text-[12.5px] font-medium text-zinc-500 hover:border-violet-400 hover:text-violet-600 hover:bg-violet-50/40 transition-colors disabled:opacity-50"
          data-testid={`revenue-contract-attach-${item.id}`}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
          {busy ? "Enviando contrato…" : "Anexar contrato de prestação de serviço (PDF)"}
        </button>
      )}
    </div>
  );
}

function RevenuesPage() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const capabilities = useCapabilities();
  const { wedding, weddings: allWeddings } = useWedding() || {};
  const { isOwner, can, loading: permsLoading } = useWeddingPermissions(wedding?.id);

  const [summary, setSummary] = useState({
    items: [],
    totals: { total_revenue: 0, total_received: 0, total_remaining: 0, total_overdue: 0, revenues_count: 0 },
    monthly_breakdown: [],
  });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quickInst, setQuickInst] = useState(null);
  const [quickSaving, setQuickSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const [filterMonth, setFilterMonth] = useState("all");
  const [filterEvent, setFilterEvent] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    if (!user) return;
    // Gate centralizado via /api/me/capabilities (single source of truth).
    // Aguarda capabilities carregarem antes de decidir o redirect.
    if (capabilities._loaded && !capabilities.can_see_revenues) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, capabilities._loaded, capabilities.can_see_revenues, navigate]);

  // OBS: removido o segundo useEffect que verificava !isOwner && !can("receitas","view").
  // Motivo: capabilities.can_see_revenues já é a fonte da verdade; o backend
  // continua aplicando _wedding_has_revenue_access em cada endpoint, garantindo
  // que colaborador sem permissão receba 403 ao tentar manipular receitas.

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/revenues/summary`);
      setSummary(res.data || { items: [], totals: {}, monthly_breakdown: [] });
    } catch (err) {
      console.error("Erro ao carregar receitas:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const weddingsForCreate = useMemo(() => {
    return Array.isArray(allWeddings) && allWeddings.length > 0 ? allWeddings : (wedding ? [wedding] : []);
  }, [allWeddings, wedding]);

  const filteredItems = useMemo(() => {
    return (summary.items || []).filter(it => {
      if (filterEvent !== "all" && it.wedding_id !== filterEvent) return false;
      if (filterStatus !== "all" && it.status !== filterStatus) return false;
      if (filterMonth !== "all") {
        const m = (it.event_date || "").slice(0, 7);
        if (m !== filterMonth) return false;
      }
      return true;
    });
  }, [summary.items, filterMonth, filterEvent, filterStatus]);

  const eventOptions = useMemo(() => {
    const map = new Map();
    (summary.items || []).forEach(it => {
      if (!map.has(it.wedding_id)) map.set(it.wedding_id, it.event_name || "Evento");
    });
    return [{ value: "all", label: "Todos os eventos" }, ...Array.from(map, ([v, l]) => ({ value: v, label: l }))];
  }, [summary.items]);

  const monthOptions = useMemo(() => {
    const set = new Set();
    (summary.items || []).forEach(it => { if (it.event_date) set.add(it.event_date.slice(0, 7)); });
    const arr = Array.from(set).sort();
    return [{ value: "all", label: "Todos os meses" }, ...arr.map(m => ({ value: m, label: monthLabel_revenue(m) }))];
  }, [summary.items]);

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      const mode = payload.mode || "existing";
      if (mode === "existing") {
        await axios.post(`${API}/weddings/${payload.wedding_id}/revenues`, {
          client_name: payload.client_name,
          event_name: payload.event_name,
          event_date: payload.event_date,
          total_amount: payload.total_amount,
          notes: payload.notes,
          installments: payload.installments,
        });
      } else {
        // mode === "new" || "standalone"  → endpoint atômico
        await axios.post(`${API}/events-from-revenue`, {
          standalone: mode === "standalone",
          event: payload.event || {},
          revenue: {
            client_name: payload.client_name,
            total_amount: payload.total_amount,
            notes: payload.notes,
            installments: payload.installments,
          },
        });
      }
      toast.success(
        mode === "new"
          ? "Evento e receita criados!"
          : mode === "standalone"
          ? "Receita avulsa cadastrada"
          : "Receita cadastrada"
      );
      setShowNew(false);
      await loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar receita");
    } finally {
      setSaving(false);
    }
  };

  const handleQuickPayment = async ({ amount, method, date, notes }) => {
    if (!quickInst) return;
    setQuickSaving(true);
    try {
      await axios.post(
        `${API}/weddings/${quickInst.weddingId}/revenues/${quickInst.revenueId}/installments/${quickInst.installment.id}/payments`,
        { amount, method, date, notes }
      );
      toast.success("Recebimento registrado");
      setQuickInst(null);
      await loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao registrar recebimento");
    } finally {
      setQuickSaving(false);
    }
  };

  const handleDeletePayment = async (revenueId, weddingId, installmentId, paymentId) => {
    if (!(await confirmDialog({
      title: "Estornar recebimento",
      message: "Tem certeza que deseja estornar este recebimento?",
      confirmLabel: "Estornar",
      variant: "danger",
    }))) return;
    try {
      await axios.delete(`${API}/weddings/${weddingId}/revenues/${revenueId}/installments/${installmentId}/payments/${paymentId}`);
      toast.success("Recebimento estornado");
      await loadSummary();
    } catch (err) {
      toast.error("Erro ao estornar");
    }
  };

  const handleDeleteRevenue = async (revenueId, weddingId) => {
    if (!(await confirmDialog({
      title: "Excluir receita",
      message: "Tem certeza que deseja excluir esta receita? Esta ação não pode ser desfeita.",
      confirmLabel: "Excluir",
      variant: "danger",
    }))) return;
    try {
      await axios.delete(`${API}/weddings/${weddingId}/revenues/${revenueId}`);
      toast.success("Receita removida");
      await loadSummary();
    } catch (err) {
      toast.error("Erro ao remover receita");
    }
  };

  const handleUpdateRevenue = async (payload) => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await axios.put(`${API}/weddings/${editTarget.wedding_id}/revenues/${editTarget.id}`, payload);
      toast.success("Receita atualizada");
      setEditTarget(null);
      await loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar receita");
    } finally {
      setEditSaving(false);
    }
  };

  const totals = summary.totals || {};

  return (
    <div className="space-y-6" data-testid="revenues-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-semibold text-zinc-900 tracking-tight">Minhas Receitas</h1>
          <p className="text-[13px] text-zinc-500 mt-1">Acompanhe o que foi contratado, recebido e o que ainda falta receber.</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-[13.5px] font-semibold shadow-[0_8px_24px_-8px_rgba(107,61,246,0.5)]"
          style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)" }}
          data-testid="revenue-new-btn"
        >
          <Plus size={16} strokeWidth={2.4} />
          Nova receita
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-5" animate>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center"><Wallet size={14} className="text-violet-500" /></div>
            <p className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">Receita Total</p>
          </div>
          <p className="text-2xl font-semibold text-zinc-900 tracking-tight tabular-nums" data-testid="revenue-total-contracted">
            {fmtBRL_revenue(totals.total_revenue)}
          </p>
          <p className="text-[11.5px] text-zinc-400 mt-1">{totals.revenues_count || 0} {totals.revenues_count === 1 ? "receita" : "receitas"}</p>
        </Card>
        <Card className="p-5" animate>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center"><TrendingUp size={14} className="text-emerald-500" /></div>
            <p className="text-[11px] font-medium text-emerald-600 uppercase tracking-wider">Total Recebido</p>
          </div>
          <p className="text-2xl font-semibold text-emerald-700 tracking-tight tabular-nums" data-testid="revenue-total-received">
            {fmtBRL_revenue(totals.total_received)}
          </p>
          <p className="text-[11.5px] text-zinc-400 mt-1">
            {totals.total_revenue > 0 ? `${Math.round((totals.total_received / totals.total_revenue) * 100)}% do total` : "—"}
          </p>
        </Card>
        <Card className="p-5" animate>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center"><Clock size={14} className="text-amber-500" /></div>
            <p className="text-[11px] font-medium text-amber-600 uppercase tracking-wider">Valor Pendente</p>
          </div>
          <p className="text-2xl font-semibold text-amber-700 tracking-tight tabular-nums" data-testid="revenue-total-remaining">
            {fmtBRL_revenue(totals.total_remaining)}
          </p>
          {totals.total_overdue > 0 && (
            <p className="text-[11.5px] text-red-600 mt-1 font-medium" data-testid="revenue-total-overdue">
              {fmtBRL_revenue(totals.total_overdue)} atrasado
            </p>
          )}
        </Card>
      </div>

      <RevenueChart data={summary.monthly_breakdown || []} />

      {(summary.items || []).length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-zinc-500 text-[12px] font-medium pr-1 border-r border-zinc-200/70 mr-1">
              <Filter size={14} /> Filtrar
            </div>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-zinc-200 text-[12.5px] bg-white text-zinc-900"
              data-testid="filter-month"
            >
              {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select
              value={filterEvent}
              onChange={(e) => setFilterEvent(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-zinc-200 text-[12.5px] bg-white text-zinc-900"
              data-testid="filter-event"
            >
              {eventOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-zinc-200 text-[12.5px] bg-white text-zinc-900"
              data-testid="filter-status"
            >
              <option value="all">Todos os status</option>
              <option value="paid">Recebido</option>
              <option value="partial">Parcial</option>
              <option value="pending">Pendente</option>
              <option value="overdue">Atrasado</option>
            </select>
          </div>
        </Card>
      )}

      {loading ? (
        <Card className="p-8 text-center text-zinc-500 text-sm">Carregando receitas…</Card>
      ) : filteredItems.length === 0 ? (
        <Card className="p-10 text-center" data-testid="revenue-empty">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center mb-4">
            <Wallet size={22} className="text-violet-500" />
          </div>
          <p className="text-zinc-900 font-semibold">
            {(summary.items || []).length === 0 ? "Nenhuma receita cadastrada" : "Nenhuma receita encontrada com esses filtros"}
          </p>
          <p className="text-[13px] text-zinc-500 mt-1.5 max-w-md mx-auto">
            {(summary.items || []).length === 0
              ? "Registre o valor cobrado de cada cliente para acompanhar parcelas, recebimentos e o que ainda falta receber."
              : "Tente limpar os filtros acima."}
          </p>
          {(summary.items || []).length === 0 && (
            <button
              onClick={() => setShowNew(true)}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-[13.5px] font-semibold"
              style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)" }}
              data-testid="revenue-empty-cta"
            >
              <Plus size={16} strokeWidth={2.4} />
              Cadastrar primeira receita
            </button>
          )}
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item) => {
            const pct = item.total_amount > 0 ? Math.min(100, Math.round((item.total_received / item.total_amount) * 100)) : 0;
            return (
              <Card key={item.id} className="p-5" data-testid={`revenue-item-${item.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[15px] font-semibold text-zinc-900 truncate">{item.event_name}</p>
                      <StatusPill status={item.status} />
                    </div>
                    <p className="text-[12.5px] text-zinc-500 mt-0.5 truncate">
                      {item.client_name ? `Cliente: ${item.client_name}` : "Sem cliente"}
                      {item.event_date ? ` · ${fmtDate_revenue(item.event_date)}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 -mr-1 shrink-0">
                    <button
                      onClick={() => setEditTarget(item)}
                      className="p-2 rounded-lg hover:bg-violet-50 text-zinc-400 hover:text-violet-600 transition-colors"
                      title="Editar receita"
                      data-testid={`revenue-edit-${item.id}`}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteRevenue(item.id, item.wedding_id)}
                      className="p-2 rounded-lg hover:bg-red-50 text-zinc-400 hover:text-red-500 transition-colors"
                      title="Excluir receita"
                      data-testid={`revenue-delete-${item.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between text-[11.5px] text-zinc-500 mb-1.5">
                    <span>Recebido</span>
                    <span className="font-semibold text-zinc-900">{pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-zinc-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${pct}%`, backgroundImage: "linear-gradient(90deg, #10B981, #34D399)" }}
                    />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 font-medium">Contratado</p>
                    <p className="mt-1 text-[14px] font-semibold text-zinc-900 tabular-nums">{fmtBRL_revenue(item.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-[10.5px] uppercase tracking-wider text-emerald-600 font-medium">Recebido</p>
                    <p className="mt-1 text-[14px] font-semibold text-emerald-700 tabular-nums">{fmtBRL_revenue(item.total_received)}</p>
                  </div>
                  <div>
                    <p className="text-[10.5px] uppercase tracking-wider text-amber-600 font-medium">A receber</p>
                    <p className="mt-1 text-[14px] font-semibold text-amber-700 tabular-nums">{fmtBRL_revenue(item.total_remaining)}</p>
                  </div>
                </div>

                <RevenueContractRow item={item} onChanged={loadSummary} />

                {Array.isArray(item.installments) && item.installments.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2.5">Parcelas</p>
                    <div className="space-y-2">
                      {item.installments.map((inst) => {
                        const meta = STATUS_META[inst.status] || STATUS_META.pending;
                        return (
                          <div
                            key={inst.id}
                            className="px-3 py-2.5 rounded-lg bg-zinc-50"
                            data-testid={`revenue-installment-${inst.id}`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold ${meta.bg} ${meta.text}`}>
                                  {inst.number || "E"}
                                </span>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-[13px] font-medium text-zinc-900 tabular-nums">{fmtBRL_revenue(inst.amount)}</p>
                                    <StatusPill status={inst.status} />
                                  </div>
                                  <p className="text-[11px] text-zinc-500">
                                    {inst.status === "partial"
                                      ? <>Recebido <span className="font-semibold text-emerald-700">{fmtBRL_revenue(inst.total_paid)}</span> · falta <span className="font-semibold text-amber-700">{fmtBRL_revenue(inst.remaining)}</span></>
                                      : <>Vence em {fmtDate_revenue(inst.due_date)}</>
                                    }
                                  </p>
                                </div>
                              </div>
                              {inst.status !== "paid" && (
                                <button
                                  onClick={() => setQuickInst({ revenueId: item.id, weddingId: item.wedding_id, installment: inst })}
                                  className="shrink-0 inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-lg text-white shadow-[0_4px_12px_-4px_rgba(107,61,246,0.45)] hover:shadow-[0_6px_16px_-4px_rgba(107,61,246,0.55)] active:scale-[0.97] transition-all"
                                  style={{ backgroundImage: "linear-gradient(95deg, #6B3DF6 0%, #8B5CF6 50%, #A855F7 100%)" }}
                                  data-testid={`revenue-register-payment-${inst.id}`}
                                >
                                  <Plus size={12} strokeWidth={2.4} />
                                  Registrar recebimento
                                </button>
                              )}
                            </div>
                            {Array.isArray(inst.payments) && inst.payments.length > 0 && (
                              <div className="mt-2 pl-9 space-y-1">
                                {inst.payments.map((p) => (
                                  <div key={p.id} className="flex items-center gap-2 text-[11.5px] text-zinc-500">
                                    <Check size={12} className="text-emerald-500 shrink-0" />
                                    <span className="tabular-nums font-medium text-zinc-700">{fmtBRL_revenue(p.amount)}</span>
                                    <span>· {PAYMENT_METHOD_LABEL[p.method] || p.method}</span>
                                    <span>· {fmtDate_revenue(p.date)}</span>
                                    <button
                                      onClick={() => handleDeletePayment(item.id, item.wedding_id, inst.id, p.id)}
                                      className="ml-auto text-zinc-300 hover:text-red-500 transition-colors"
                                      title="Estornar"
                                      data-testid={`revenue-payment-delete-${p.id}`}
                                    >
                                      <X size={12} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {item.notes && (
                  <p className="mt-3 text-[12px] text-zinc-500 italic">&ldquo;{item.notes}&rdquo;</p>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <NewRevenueForm
        open={showNew}
        onClose={() => setShowNew(false)}
        weddings={weddingsForCreate}
        onCreate={handleCreate}
        saving={saving}
      />

      <QuickPaymentSheet
        open={!!quickInst}
        onClose={() => setQuickInst(null)}
        installment={quickInst?.installment || null}
        onSubmit={handleQuickPayment}
        saving={quickSaving}
      />

      <EditRevenueForm
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        revenue={editTarget}
        onSave={handleUpdateRevenue}
        saving={editSaving}
      />
    </div>
  );
}





function App() {
  // 🟢 Keep-alive ping anti cold-start
  // Bate em /api/health a cada 4 min para evitar que o container hiberne
  // por inatividade. Endpoint super leve (sem DB, sem auth) no backend.
  React.useEffect(() => {
    const ping = () => {
      fetch(`${API}/health`, { method: 'GET', cache: 'no-store' }).catch(() => {});
    };
    ping(); // dispara imediatamente no mount
    const interval = setInterval(ping, 240000); // 4 minutos
    return () => clearInterval(interval);
  }, []);

  // 📲 Service Worker — habilita install prompt do PWA (Chrome/Edge)
  React.useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    const onLoad = () => {
      navigator.serviceWorker.register("/sw.js").catch(() => {
        // Falha silenciosa: PWA continua funcionando, só perde o install prompt
      });
    };
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad, { once: true });
  }, []);

  return (
    <div className="notranslate" translate="no">
    <BrowserRouter>
      <AuthProvider>
        <PremiumGateProvider>
        <PlanUpgradeProvider>
        <TourProvider>
        <PremiumToaster />
        <ConfirmDialogRoot />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/criar-conta" element={<RegisterPage />} />
          {/* Páginas legais públicas (LGPD) */}
          <Route path="/privacidade" element={<PrivacidadePage />} />
          <Route path="/termos" element={<TermosPage />} />
          {/* Rota Pública de RSVP - Sem autenticação */}
          <Route path="/rsvp/:token" element={<RSVPPage />} />
          <Route path="/confirmar/:token" element={<RSVPPage />} />
          {/* Check-out seguro de crianças — deep-link do QR (equipe autenticada) */}
          <Route path="/retirada/:weddingId/:token" element={<ChildRetiradaPage />} />
          {/* Rota Pública de CONVITE DIGITAL - Convidado acessa link do convite */}
          <Route path="/convite/:token" element={<PublicInvitePage />} />
          {/* Rota Pública de CONVITE PERSONALIZADO - Link público do convite uploadado */}
          <Route path="/convite-personalizado/:token" element={<PublicCustomInvitePage />} />
          {/* Rota Pública de CHECK-IN - Convidado escaneia QR e faz check-in */}
          <Route path="/checkin/:token" element={<PublicCheckinPage />} />
          {/* Rota Pública do SITE DO CASAL - sem autenticação */}
          <Route path="/casal/:slug" element={<PublicCasalPage />} />

          {/* 🎁 VIP Access — resgate público de convite intransferível */}
          <Route path="/invite/:code" element={<VipRedeemPage />} />

          {/* 👔 Convite de COLABORADOR (staff) — tela premium dedicada */}
          <Route path="/invite/staff/:token" element={<StaffInvitePage />} />
          
          {/* ROTAS PROTEGIDAS - WeddingProvider ÚNICO para todas */}
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          } />
          
          {/* Rotas com DashboardLayout - WeddingProvider compartilhado */}
          <Route element={
            <ProtectedRoute>
              <WeddingProvider>
                <DashboardLayout />
              </WeddingProvider>
            </ProtectedRoute>
          }>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/my-events" element={<MyEventsPage />} />
            <Route path="/ai-assistant" element={<AIAssistantPage />} />
            <Route path="/guests" element={<GuestsPage />} />
            <Route path="/organizar-mesas" element={<SeatingRoute />} />
            <Route path="/whatsapp" element={<WhatsAppPage />} />
            <Route path="/checkin-live" element={<CheckinLivePage />} />
            <Route path="/child-checkout" element={<ChildCheckoutPage />} />
            <Route path="/budgets" element={<BudgetsPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/cashflow" element={<CashflowPage />} />
            <Route path="/revenue" element={<RevenuesPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/inspirations" element={<InspirationsRoute />} />
            <Route path="/reports" element={<ReportsRoute />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/upgrade" element={<UpgradePage />} />
            <Route path="/upgrade/success" element={<UpgradeSuccessPage />} />
          </Route>
          
          <Route path="/checkin-reception" element={
            <ProtectedRoute>
              <WeddingProvider>
                <CheckinReceptionPage />
              </WeddingProvider>
            </ProtectedRoute>
          } />

          {/* /admin tem seu próprio gate (AdminGate) — NÃO usa ProtectedRoute
              para permitir que admin não logado veja o form de login direto. */}
          <Route path="/admin" element={
            <Suspense fallback={<PageLoadingFallback />}>
              <AdminDashboard />
            </Suspense>
          } />
          <Route path="/admin/vip-access" element={
            <ProtectedRoute>
              <VipAccessAdminLayout />
            </ProtectedRoute>
          } />
          <Route path="/admin/email-logs" element={
            <ProtectedRoute>
              <EmailLogsAdminLayout />
            </ProtectedRoute>
          } />
          
          {/* Fallback - redireciona para home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <StaffUpgradeGate />
        </TourProvider>
        </PlanUpgradeProvider>
        </PremiumGateProvider>
      </AuthProvider>
    </BrowserRouter>
    </div>
  );
}

export default App;
